/**
 * English Translations
 *
 * Master translation file for English language (SOURCE OF TRUTH).
 * All UI text should be defined here for consistency across portals.
 *
 * Types are auto-derived from this file - no need to update types.ts.
 * Other language files must implement the same keys (enforced by TypeScript).
 */

export const en = {
  // ============================================================================
  // COMMON
  // ============================================================================
  'common.case': 'Loan',
  'common.loading': 'Loading...',
  'common.saving': 'Saving...',
  'common.deleting': 'Deleting...',
  'common.saved': 'Saved',
  'common.savedSuccessfully': 'Saved successfully',
  'common.error': 'Error',
  'common.success': 'Success',
  'common.warning': 'Warning',
  'common.info': 'Info',
  'common.confirm': 'Confirm',
  'common.confirmAction': 'Are you sure? This action cannot be undone.',
  'common.done': 'Done',
  'common.cancel': 'Cancel',
  'common.hide': 'Hide',
  'common.showPassword': 'Show password',
  'common.hidePassword': 'Hide password',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.backToDashboard': 'Back to Dashboard',
  'common.backToLogin': 'Back to Login',
  'common.goBack': 'Go Back',
  'common.favorite': 'Add to favorites',
  'common.unfavorite': 'Remove from favorites',
  'common.checking': 'Checking...',
  'common.comingSoon': 'Coming soon',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.search': 'Search',
  'common.tryAdjustingSearch': 'Try adjusting your search criteria',
  'common.lastActivity': 'Last Activity',
  'common.contactInfo': 'Contact Information',
  'common.dates': 'Dates',
  'common.sendMessage': 'Send Message',
  'common.filter': 'Filter',
  'common.filterByStatus': 'Filter by Status',
  'common.filters': 'Filters',
  'common.clearFilters': 'Clear Filters',
  'common.sort': 'Sort',
  'common.clear': 'Clear',
  'common.apply': 'Apply',
  'common.reset': 'Reset',
  'common.skip': 'Skip',
  'common.skipped': 'Skipped',
  'common.refresh': 'Refresh',
  'common.retry': 'Retry',
  'common.you': 'You',
  'common.submit': 'Submit',

  // Citation primitives (Issue #1749 — Mark / Cite / CitedHeading)
  'designSystem.citations.markerAriaLabel': 'Citation {{n}}',

  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.deletedSuccessfully': 'Deleted successfully',
  'common.deleteStage': 'Delete stage {{name}}',
  'common.confirmDelete': 'Confirm Delete',
  'common.systemProtected': 'System default — cannot be deleted. You can deactivate it instead.',
  'common.edit': 'Edit',
  'common.revise': 'Revise',
  'common.resend': 'Resend',
  'common.view': 'View',
  'common.viewAll': 'View All',
  'common.viewField': 'View field',
  'common.viewProfile': 'View Profile',
  'common.deactivate': 'Deactivate',
  'common.created': 'Created',
  'common.updated': 'Updated',
  'common.documents': 'Documents',
  'common.quickActions': 'Quick Actions',
  'common.actions': 'Actions',
  'common.moreActions': 'More actions',
  'common.create': 'Create',
  'common.update': 'Update',
  'common.duplicate': 'Duplicate',
  'common.remove': 'Remove',
  'common.export': 'Export',
  'common.import': 'Import',
  'common.download': 'Download',
  'common.downloadPdf': 'Download PDF',
  'common.print': 'Print',
  'common.upload': 'Upload',
  'common.uploadImage': 'Upload Image',
  'common.removeImage': 'Remove Image',
  'common.uploading': 'Uploading...',
  'common.copy': 'Copy',
  'common.paste': 'Paste',
  'common.select': 'Select',
  'common.selectAll': 'Select all',
  'common.deselectAll': 'Deselect all',
  'common.none': 'None',
  'common.unknown': 'Unknown',
  'common.unknownUser': 'Unknown User',
  'common.currentUser': 'Current User',
  'common.all': 'All',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.never': 'Never',
  'common.or': 'or',
  'common.and': 'and',
  'common.of': 'of',
  'common.to': 'to',
  'common.from': 'from',

  // Approval actions (Issue #261 - AI Data Chat)
  'common.accept': 'Accept',
  'common.accepted': 'Accepted',
  'common.approve': 'Approve',
  'common.reject': 'Reject',
  'common.rename': 'Rename',
  'common.preview': 'Preview',
  'common.settings': 'Settings',
  'common.archive': 'Archive',
  'common.open': 'Open',
  'common.play': 'Play',
  'common.star': 'Star',
  'common.more': 'More',
  'common.optional': 'Optional',
  'common.noContentForStep': 'No content for this step',
  'common.showMore': 'Show more',
  'common.showLess': 'Show less',
  'common.viewSource': 'View source',
  'common.regenerate': 'Regenerate',
  'common.copied': 'Copied!',
  'common.collapse': 'Collapse',
  'common.expand': 'Expand',

  // Pagination
  'common.page': 'Page',
  'common.perPage': 'Per page',
  'common.showing': 'Showing',
  'common.results': 'results',
  'common.record': 'Record',
  'common.notFound': 'Not found',
  'common.notSpecified': 'Not specified',
  'common.noResults': 'No results found',
  'common.noChanges': 'No changes to save',
  'common.loadMore': 'Load more',
  'common.selectedCount': '{{count}} selected',
  'common.allPriorities': 'All Priorities',
  'common.allStatuses': 'All Statuses',
  'common.viewFullDetails': 'View Full Details',
  'common.viewDetails': 'View Details',
  'common.priority': 'Priority',
  'common.assignee': 'Assignee',
  'common.dueDate': 'Due Date',
  'common.estimatedTime': 'Estimated Time',

  // Labels (for table headers, forms, etc.)
  'common.profile': 'Profile',
  'common.user': 'User',
  'common.role': 'Role',
  'common.status': 'Status',
  'common.connected': 'Connected',
  'common.disconnecting': 'Disconnecting...',
  'common.consumerEmail': 'Consumer Email',
  'common.email': 'Email',
  'common.notSet': 'Not set',
  'common.people': 'People',
  'common.slaStatus': 'SLA Status',
  'common.password': 'Password',
  'common.firstName': 'First Name',
  'common.lastName': 'Last Name',
  'common.confirmPassword': 'Confirm Password',
  'common.business': 'Business',
  'common.security': 'Security',
  'common.mfa': 'MFA',
  'common.lastLogin': 'Last Login',
  'common.createdAt': 'Created At',
  'common.resetPassword': 'Reset Password',

  // Time
  'common.now': 'Now',
  'common.today': 'Today',
  'common.yesterday': 'Yesterday',
  'common.tomorrow': 'Tomorrow',
  'common.thisWeek': 'This week',
  'common.lastWeek': 'Last week',
  'common.thisMonth': 'This month',
  'common.lastMonth': 'Last month',
  'common.ago': 'ago',
  'common.in': 'in',
  'common.justNow': 'just now',
  'common.minuteAgo': '1 minute ago',
  'common.minutesAgo': '{{count}} minutes ago',
  'common.hourAgo': '1 hour ago',
  'common.hoursAgo': '{{count}} hours ago',
  'common.dayAgo': '1 day ago',
  'common.daysAgo': '{{count}} days ago',
  'common.sources': 'Sources',
  'common.submitted': 'Submitted',
  'common.lastUpdated': 'Last Updated',

  // Generic field labels
  'common.title': 'Title',
  'common.description': 'Description',
  'common.name': 'Name',
  'common.type': 'Type',
  'common.na': 'N/A',
  'common.system': 'System',
  'common.loan': 'loan',
  'common.borrower': 'borrower',
  'common.partner': 'partner',

  // Form field labels
  'form.firstName': 'First Name',
  'form.lastName': 'Last Name',
  'form.email': 'Email',
  'form.phone': 'Phone',
  'form.companyName': 'Company Name',

  // Entity operations
  'common.createEntity': 'Create {{entity}}',
  'common.editEntity': 'Edit {{entity}}',
  'common.deleteEntity': 'Delete {{entity}}',
  'common.viewEntity': 'View {{entity}}',

  // Action states
  'common.creating': 'Creating...',
  'common.updating': 'Updating...',

  // Status indicators
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.paid': 'Paid',
  'common.pending': 'Pending',
  'common.completed': 'Completed',
  'common.failed': 'Failed',
  'common.processing': 'Processing...',
  'common.draft': 'Draft',
  'common.published': 'Published',
  'common.publish': 'Publish',
  'common.archived': 'Archived',

  // Attachments (Issue #497 - Universal Document Attachment System)
  'common.attachments': 'Attachments',
  'common.attachment': 'Attachment',
  'common.addAttachment': 'Add Attachment',
  'common.addFirstAttachment': 'Add your first attachment',
  'common.attachDocument': 'Attach Document',
  'common.selectDocuments': 'Select Documents',
  'common.noAttachments': 'No attachments',
  'common.noDocuments': 'No documents available',
  'common.documentAttached': 'Document attached',
  'common.uploadDocumentsFirst': 'Upload documents first to attach them',
  'common.documentType': 'Document Type',
  'common.verificationStatus': 'Verification Status',
  'common.verify': 'Verify',
  'common.verified': 'Verified',
  'common.rejected': 'Rejected',
  'common.needsReupload': 'Needs Re-upload',
  'common.expired': 'Expired',
  'common.requestReupload': 'Request Re-upload',
  'common.enterRejectionReason': 'Enter rejection reason...',

  // Entity labels (hierarchy)
  'common.tenant': 'Tenant',
  'common.tenants': 'Tenants',
  'common.realm': 'Realm',
  'common.realms': 'Realms',
  'common.businesses': 'Businesses',
  'common.domain': 'Domain',
  'common.slug': 'Slug',
  'common.subscription': 'Subscription',
  'common.configuration': 'Configuration',
  'common.overview': 'Overview',
  'common.canCreateTemplates': 'Can Create Templates',
  'common.customTemplates': 'Custom Templates',

  // Additional common keys
  'common.add': 'Add',
  'common.assign': 'Assign',
  'common.assignToMe': 'Assign to me',
  'common.assigning': 'Assigning...',
  'common.default': 'Default',
  'common.details': 'Details',
  'common.enable': 'Enable',
  'common.enabled': 'Enabled',
  'common.integrations': 'Integrations',
  'common.metadata': 'Metadata',
  'common.new': 'New',
  'common.noDescription': 'No description',
  'common.saveChanges': 'Save Changes',
  'common.saveDraft': 'Save Draft',
  'common.timezone': 'Timezone',
  'common.tryDifferentSearch': 'Try a different search term',
  'common.updatedAt': 'Updated At',
  'common.timestamp': 'Timestamp',
  'common.start': 'Start',
  'common.scheduled': 'Scheduled',

  // Additional common keys (i18n audit)
  'common.businessId': 'Business ID',
  'common.characters': 'characters',
  'common.converting': 'Converting...',
  'common.leaveEmptyToUseDefault': 'Leave empty to use default value',
  'common.noOptionsAvailable': 'No options available',
  'common.selectOption': 'Select...',
  'common.clickToExpand': 'Click to expand',
  'common.clone': 'Clone',
  'common.configurations': 'Configurations',
  'common.configure': 'Configure',
  'common.consumer': 'Consumer',
  'common.currentStatus': 'Current Status',
  'common.database': 'Database',
  'common.dbHost': 'Database Host',
  'common.dbName': 'Database Name',
  'common.dbSchema': 'Database Schema',
  'common.expires': 'Expires',
  'common.exportAsTemplate': 'Export as Template',
  'common.filtered': 'filtered',
  'common.generateEmergencyCode': 'Generate Emergency Code',
  'common.keep': 'Keep',
  'common.keyInfo': 'Key Info',
  'common.keyPrefix': 'Key Prefix',
  'common.lastUsed': 'Last Used',
  'common.limit': 'Limit',
  'common.max': 'Max',
  'common.min': 'Min',
  'common.notConfigured': 'Not Configured',
  'common.quickOverview': 'Quick Overview',
  'common.resetMfa': 'Reset MFA',
  'common.revoke': 'Revoke',
  'common.searchByName': 'Search by name',
  'common.send': 'Send',
  'common.sent': 'Sent',
  'common.sending': 'Sending...',
  'common.messageSupport': 'Message Support',
  'common.messages': 'Messages',
  'common.setup': 'Setup',
  'common.suspend': 'Suspend',
  'common.template': 'Template',
  'common.tenantId': 'Tenant ID',
  'common.usage': 'Usage',
  'common.tier': 'Tier',

  // Upgrade modal (Issue #383)
  'common.maybeLater': 'Maybe Later',

  // Partner detail page (Issue #383)
  'common.code': 'Code',
  'common.amount': 'Amount',
  'common.date': 'Date',
  'common.client': 'Client',
  'common.copyLink': 'Copy Link',
  'common.downloadPng': 'Download PNG',
  'common.editPartner': 'Edit Partner',
  'common.backToPartners': 'Back to Partners',
  'common.approving': 'Approving...',
  'common.rejecting': 'Rejecting...',
  'common.rejectionReason': 'Rejection Reason',
  'common.approveAll': 'Approve All ({{count}})',
  'common.exportCsv': 'Export CSV',

  // Additional common keys (Issue #400 - i18n audit)
  'common.company': 'Company',
  'common.exitFullscreen': 'Exit Fullscreen',
  'common.fullscreen': 'Fullscreen',
  'common.generating': 'Generating...',
  'common.hideFilters': 'Hide Filters',
  'common.noActivityYet': 'No activity yet',
  'common.noDescriptionProvided': 'No description provided.',
  'common.noSignatureRequestsYet': 'No signature requests yet',
  'common.noTimelineEventsYet': 'No timeline events yet',
  'common.requestSignature': 'Request Signature',
  'common.signingRequest': 'Signing Request',
  'common.noData': 'No data available',
  'common.noMatchingResults': 'No matching results',
  'common.columns': 'Columns',
  'common.errorLoadingData': 'Error loading data',
  'common.adjustSearchOrFilters': 'Try adjusting your search or filters',
  'common.addDataToStart': 'Add some data to get started',
  'common.rowsPerPage': 'Rows per page:',
  'common.noResultsCount': '0 results',
  'common.notAvailable': 'N/A',
  'common.openInNewTab': 'Open in New Tab',
  'common.permissions': 'Permissions',
  'common.selected': 'selected',
  'common.share': 'Share',
  'common.showFilters': 'Show Filters',
  'common.submitting': 'Submitting...',
  'common.skipToMainContent': 'Skip to main content',
  'common.clickMe': 'Click Me!',
  'common.apiCallsMade': 'API Calls Made:',
  'common.protectionFeatures': 'Protection Features:',
  'common.searching': 'Searching...',
  'common.deletePermanently': 'Delete Permanently',

  // Demo/showcase component strings
  'demo.protectedButtonTitle': 'Protected Button Demo',
  'demo.protectedButtonInstructions': 'Try clicking rapidly! Only 1 request will be sent.',
  'demo.protectedButtonError': 'Failed to process request',
  'demo.protectedButtonDebouncing': 'Debouncing (1 second delay)',
  'demo.protectedButtonDisabled': 'Button disabled during operation',
  'demo.protectedButtonLoading': 'Visual loading feedback',
  'demo.protectedButtonErrorHandling': 'Error handling',
  'demo.debouncedSearchTitle': 'Debounced Search Demo',
  'demo.debouncedSearchInstructions': 'Type fast! API calls wait 500ms after you stop typing.',
  'demo.debouncedSearchResults': 'Results:',
  'demo.debouncedSearchDebouncing': 'Debounced input (500ms)',
  'demo.debouncedSearchDeduplication': 'React Query deduplication',
  'demo.debouncedSearchCached': 'Cached results (prevents re-fetch)',
  'demo.debouncedSearchConditional': 'Conditional queries (only when input exists)',

  // ============================================================================
  // COPY LINK — reusable strings for "copy my personal link" actions
  // Used by the useCopyPersonalLink hook anywhere a URL is copied that carries
  // the current user's attribution (utm_content=user_<id>, b, tenant).
  // ============================================================================
  'copyLink.personalLinkCopied':
    'Your personal invite link was copied. Anyone who applies through it will be credited to you.',
  'copyLink.failed': 'Could not copy the link to your clipboard. Please try again.',
  'copyLink.notLoggedIn': 'You must be signed in to copy a personal invite link.',

  // ============================================================================
  // TABS - Reusable tab labels (Issue #383)
  // ============================================================================
  'tabs.details': 'Details',
  'tabs.overview': 'Overview',
  // Loan Case header — v3 design (#1758)
  'cases.header.loanLabel': 'Loan #',
  'cases.header.copyLink': 'Copy link',
  'cases.header.linkCopied': 'Link copied to clipboard',
  'cases.header.messageBorrower': 'Message borrower',
  'cases.header.submitForApproval': 'Submit for approval',
  'tabs.documents': 'Documents',
  'tabs.timeline': 'Timeline',
  'tabs.messages': 'Messages',
  'tabs.activity': 'Activity',
  'tabs.notes': 'Notes',
  'tabs.history': 'History',
  'tabs.settings': 'Settings',
  'tabs.analytics': 'Analytics',
  'tabs.members': 'Members',
  'tabs.permissions': 'Permissions',
  'tabs.audit': 'Audit',
  'tabs.integrations': 'Integrations',
  'tabs.notifications': 'Notifications',
  'tabs.billing': 'Billing',
  'tabs.security': 'Security',
  'tabs.general': 'General',
  'tabs.advanced': 'Advanced',
  'tabs.builder': 'Builder',
  'tabs.preview': 'Preview',
  'tabs.enrichmentHistory': 'Enrichment History',
  'tabs.comments': 'Comments',
  'tabs.tasks': 'Tasks',
  'tabs.modifications': 'Modifications',
  'tabs.dataRoom': 'Data Room',
  'tabs.closing': 'Closing',
  'tabs.releaseSchedule': 'Release Schedule',
  'tabs.parties': 'Parties',
  'tabs.termSheets': 'Term Sheets',
  'tabs.cases': 'Loans',
  'tabs.loans': 'Loans',
  'tabs.entities': 'Entities',
  'tabs.applications': 'Applications',
  'tabs.codes': 'Tracking Codes',
  'tabs.referrals': 'Referrals',
  'tabs.commissions': 'Commissions',
  'tabs.contacts': 'Contacts',
  'tabs.sequences': 'Sequences',
  'tabs.properties': 'Properties',
  'tabs.enrichment': 'Enrichment',
  'tabs.fields': 'Fields',
  'tabs.relationships': 'Relationships',
  'tabs.underwriting': 'Underwriting',
  // Issue #1466 — credit memo / loan submission artifact tab.
  'tabs.creditMemo': 'Credit Memo',
  'tabs.comparables': 'Comparables',
  'tabs.exitHistory': 'Exit History',

  // ============================================================================
  // UNDERWRITING / ASSESSMENT (Digital Twin Platform SP7)
  // ============================================================================
  'underwriting.event.score': 'Score',
  'underwriting.trigger.kind.system': 'System',
  'underwriting.trigger.kind.twin': 'Digital twin',
  'underwriting.trigger.kind.user': 'User',
  'underwriting.assessmentSummary': 'Assessment Summary',
  'underwriting.assessmentFactors': 'Assessment Factors',
  'underwriting.aiNarrative': 'AI Narrative',
  'underwriting.assessmentTimeline': 'Assessment Timeline',
  'underwriting.noAssessment': 'No Assessment Available',
  'underwriting.noAssessmentDescription':
    'Run a full assessment to generate an AI-powered underwriting analysis for this case.',
  'underwriting.noFactors': 'No assessment factors available yet.',
  'underwriting.noHistory': 'No assessment events recorded yet.',
  'underwriting.riskBand': 'Risk Band',
  'underwriting.recommendation': 'Recommendation',
  'underwriting.confidence': 'Confidence',
  'underwriting.completeness': 'Completeness',
  'underwriting.lastAssessed': 'Last Assessed',
  'underwriting.pullCredit': 'Pull Credit',
  'underwriting.runFullAssessment': 'Run Full Assessment',
  'underwriting.overrideScore': 'Override Score',
  'underwriting.creditPullDescription': 'Select the credit bureau to pull a credit report from.',
  'underwriting.creditPullTriggered': 'Credit pull has been triggered successfully.',
  'underwriting.fullAssessmentTriggered': 'Full assessment has been triggered. Results will appear shortly.',
  'underwriting.overrideSaved': 'Assessment override has been saved.',
  'underwriting.overrideDescription':
    'Override the AI recommendation with a manual decision. A reason is required for audit purposes.',
  'underwriting.overrideReason': 'Reason for Override',
  'underwriting.confirmOverride': 'Confirm Override',
  'underwriting.bureau': 'Credit Bureau',
  'underwriting.triggeredBy': 'Triggered by',
  'underwriting.recommendationApprove': 'Approve',
  'underwriting.recommendationReject': 'Reject',
  'underwriting.recommendationReview': 'Review',
  // Factor statuses
  'underwriting.factorStatus.pass': 'Pass',
  'underwriting.factorStatus.warn': 'Warn',
  'underwriting.factorStatus.fail': 'Fail',
  'underwriting.factorStatus.pending': 'Pending',
  'underwriting.factorStatus.not_available': 'N/A',
  // Assessment statuses
  'underwriting.status.in_progress': 'In Progress',
  'underwriting.status.complete': 'Complete',
  'underwriting.status.stale': 'Stale',
  'underwriting.status.error': 'Error',
  // Factor categories
  'underwriting.category.credit': 'Credit',
  'underwriting.category.property': 'Property',
  'underwriting.category.income': 'Income',
  'underwriting.category.experience': 'Experience',
  'underwriting.category.document': 'Documents',
  'underwriting.category.compliance': 'Compliance',
  'underwriting.category.collateral': 'Collateral',
  'underwriting.category.fraud': 'Fraud',
  // Source types
  'underwriting.source.bureau': 'Bureau',
  'underwriting.source.document': 'Document',
  'underwriting.source.calculation': 'Calculated',
  'underwriting.source.userInput': 'User Input',
  'underwriting.source.aiInference': 'AI',
  // Event types
  'underwriting.eventType.score_updated': 'Score Updated',
  'underwriting.eventType.factor_added': 'Factor Added',
  'underwriting.eventType.recommendation_changed': 'Recommendation Changed',
  'underwriting.eventType.threshold_breached': 'Threshold Breached',
  'underwriting.eventType.data_received': 'Data Received',
  'underwriting.eventType.human_override': 'Human Override',
  'underwriting.eventType.error': 'Error',

  // ============================================================================
  // ERROR MESSAGES (Issue #383)
  // ============================================================================
  'error.partnerNotFound': 'Partner not found. The requested partner does not exist.',
  'error.loadingAgreements': 'Failed to load agreements',
  'error.loadingFailed': 'Failed to load data. Please try again.',
  'error.loadFailed': 'Failed to load data. Please try again.',
  'error.uploadFailed': 'Upload failed',
  'error.attachFailed': 'Failed to attach documents: {{message}}',
  'error.generic': 'Something went wrong. Please try again.',
  'error.unknown': 'Unknown error',
  'error.title': 'Something went wrong',
  'error.description': 'An unexpected error occurred. Please try again.',
  'error.notFound.title': 'Page Not Found',
  'error.notFound.description': 'The page you are looking for does not exist or has been moved.',
  // Error boundary copy (PR #1543 review feedback — reload action + refresh-specific
  // fallback message for the React error boundary at common/ErrorBoundary.tsx).
  'error.boundary.refreshHint': 'An unexpected error occurred. Please try refreshing the page.',
  'error.boundary.reload': 'Reload Page',

  // ============================================================================
  // BILLING (Issue #400 - i18n audit)
  // ============================================================================
  'billing.createInvoice': 'Create Invoice',
  'billing.downloadPdf': 'Download PDF',
  'billing.invoices': 'Invoices',
  'billing.sendInvoice': 'Send Invoice',

  // ============================================================================
  // DIALOG TITLES AND CONTENT (Issue #383)
  // ============================================================================
  'dialog.approveCommission': 'Approve Commission',
  'dialog.approveCommissionConfirm': 'Are you sure you want to approve this commission?',
  'dialog.rejectCommission': 'Reject Commission',
  'dialog.rejectCommissionReason': 'Please provide a reason for rejecting this commission.',
  'dialog.bulkApproveCommissions': 'Bulk Approve Commissions',
  'dialog.bulkApproveConfirm': 'Are you sure you want to approve {{count}} commission(s)?',
  'dialog.createTitle': 'Create {{entity}}',
  'dialog.attachDocuments': 'Attach Documents',
  'dialog.enterRejectionReason': 'Enter rejection reason',
  'dialog.enterReuploadReason': 'Enter reason for re-upload request',

  // ============================================================================
  // CHECKLIST (Issue #497 - Document Checklists)
  // ============================================================================
  'checklist.fulfilled': 'Fulfilled',
  'checklist.requiredPending': 'Required - Pending',
  'checklist.optionalPending': 'Optional - Pending',

  // ============================================================================
  // PAGE DESCRIPTIONS
  // ============================================================================

  // ============================================================================
  // NAVIGATION
  // ============================================================================
  'navigation.platformName': 'Fiops',
  'navigation.dashboard': 'Dashboard',
  'navigation.home': 'Home',
  'navigation.pinSidebar': 'Pin sidebar open',
  'navigation.unpinSidebar': 'Unpin sidebar',
  'navigation.overview': 'Overview',

  // Organization
  'navigation.organization': 'Organization',
  'navigation.tenants': 'Tenants',
  'navigation.businesses': 'Businesses',
  'navigation.realms': 'Realms',
  'navigation.users': 'Users',
  'navigation.team': 'Team',
  'navigation.teams': 'Teams',
  'navigation.teamMembers': 'Team Members',
  'navigation.scorecard': 'Scorecard',

  // Operations
  'navigation.operations': 'Operations',
  'navigation.cases': 'Loans',
  'navigation.tasks': 'Tasks',
  'navigation.pipelines': 'Pipelines',
  'navigation.entities': 'Entities',
  'navigation.borrowingEntities': 'Borrowing Entities',
  'navigation.templates': 'Templates',
  'navigation.systemTemplates': 'System Templates',
  'navigation.documents': 'Documents',
  'navigation.myDocuments': 'My Documents',
  'navigation.retainedCopies': 'Retained Copies',

  // New nav items (Issue #596 — Baseline Parity Phase 2)
  'navigation.activity': 'Activity',
  'navigation.inbox': 'Inbox',
  'navigation.contacts': 'Relations',
  'navigation.contactDirectory': 'Directory',
  'navigation.workspace': 'Workspace',
  'navigation.finance': 'Finance',
  'navigation.payments': 'Payments',
  'navigation.funds': 'Funds',
  'navigation.fundsOverview': 'Overview',
  'navigation.payouts': 'Payouts',
  'navigation.offerings': 'Offerings',
  // Reports section sub-tabs (E-Sign History + Retained Copies + Document Approvals)
  // moved here from the legacy /documents top-level surface. The base
  // `navigation.reports` key already exists below in the Analytics block.
  'navigation.eSignatureHistory': 'E-Signature History',
  'navigation.documentApprovals': 'Document Approvals',
  // Inbox sub-tab — active e-signatures awaiting attention. Lives next to
  // Tasks under /inbox so chasing pending signatures matches the "needs my
  // attention" pattern users already use for tasks.
  'navigation.eSignQueue': 'E-Sign Queue',
  // Inbox sub-tab — BIC approver dashboard (Issue #1559 — Stream E / Phase 4).
  // Surfaces "things waiting for me to approve" + "things I've requested
  // approval for". Sibling to Tasks because both answer the same "what
  // needs my attention?" question, just for different work types.
  'navigation.approvals': 'Approvals',

  // Tools & Calculators
  'navigation.tools': 'Tools',
  'navigation.calculators': 'Calculators',
  'navigation.monthlyPayment': 'Monthly Payment',
  'navigation.dscrCalculator': 'DSCR',
  'navigation.cashToClose': 'Cash to Close',
  'navigation.pipelineAnalytics': 'Pipeline Analytics',
  'navigation.loanPortfolio': 'Loan Portfolio',
  'navigation.financial': 'Financial',
  'navigation.custom': 'Custom',

  // Astor
  'navigation.aiAutomation': 'Astor',
  'navigation.agents': 'Agents',
  'navigation.models': 'Models',
  'navigation.playground': 'Playground',
  'navigation.identity': 'Identity',
  'navigation.chat': 'Chat',
  'navigation.history': 'History',
  'navigation.astor': 'Astor',
  'navigation.watchlist': 'Watchlist',

  // CRM & Sales
  'navigation.crmSales': 'CRM & Sales',
  'navigation.discovery': 'Discovery',
  'navigation.leadsList': 'All Leads',
  'navigation.termSheets': 'Term Sheets',
  'navigation.proposals': 'Proposals',
  'navigation.quotes': 'Quotes',

  // Relationships
  'navigation.relationships': 'Relationships',
  'navigation.consumers': 'Consumers',
  'navigation.borrowers': 'Borrowers',
  'navigation.partners': 'Partners',
  'navigation.partnerDirectory': 'Directory',
  'navigation.partnerApplications': 'Partner Inquiries',
  'page.partnerApplicationsDescription':
    'Sign-up requests from people and companies applying through your public partner application form. Review, qualify, and approve to convert them into active partners.',

  // Page subtitles

  // Billing
  'navigation.billing': 'Billing',
  'navigation.billingOverview': 'Billing Overview',
  'navigation.invoices': 'Invoices',
  'navigation.plans': 'Plans',
  'navigation.pricing': 'Pricing',
  'navigation.pricingRules': 'Pricing Rules',
  'navigation.subscriptions': 'Subscriptions',

  // Analytics
  'navigation.analytics': 'Analytics',
  'navigation.monitoring': 'Monitoring',
  'navigation.reports': 'Reports',

  // System
  'navigation.system': 'System',
  'navigation.settings': 'Settings',
  'navigation.controlCenter': 'Control Center',
  'navigation.security': 'Security',
  'navigation.audit': 'Audit Logs',
  'navigation.apiKeys': 'API Keys',
  'navigation.notifications': 'Notifications',
  'navigation.notificationPreferences': 'Notification Preferences',
  'navigation.astorPreferences': 'Astor Preferences',
  'navigation.profile': 'Profile',

  // Unified Navigation System (Issue #330)
  'navigation.prospecting': 'Prospects', // Merged Pipeline + Campaigns
  'navigation.loans': 'Loans', // Was Origination — industry-standard terminology
  'navigation.applications': 'Applications', // Was Intake Submissions
  'navigation.pipeline': 'Pipeline', // Kept for backward compatibility
  'navigation.origination': 'Origination', // Kept for backward compatibility
  'navigation.guarantors': 'Guarantors', // Issue #636 - Guarantor dashboard
  'navigation.aiAssistant': 'Astor',
  'navigation.partnerships': 'Partnerships',
  'navigation.referrals': 'Referrals',
  'navigation.referralTracking': 'Referral Tracking', // Issue #368 - Partner referral link tracking
  'navigation.agreements': 'Agreements',
  'navigation.commissions': 'Commissions',
  'navigation.sequences': 'Sequences',
  'navigation.intakeSubmissions': 'Intake Submissions', // Issue #475 - Loan application submissions
  'navigation.intakeTemplates': 'Intake Templates', // Issue #475 - Intake flow templates (admin)
  'navigation.realmTemplates': 'Realm Templates', // Default templates tab in admin portal
  'navigation.campaigns': 'Campaigns',
  'navigation.workflows': 'Workflows',
  'navigation.allDocuments': 'All Documents',
  'navigation.pending': 'Pending',
  'navigation.orgChart': 'Org Chart',
  'navigation.sandboxes': 'Sandboxes',
  'navigation.controlTower': 'Control Tower',
  'navigation.incidents': 'Incidents',
  'navigation.metrics': 'Metrics',
  'navigation.signals': 'Signals',
  'navigation.sla': 'SLA Health',
  'navigation.scenarios': 'Scenarios',
  'navigation.testRuns': 'Test Runs',
  'navigation.networkLogs': 'Network Logs',
  'navigation.integrations': 'Integrations',
  'navigation.integrations.providers': 'Providers',
  'navigation.integrations.monitoring': 'Monitoring',
  'navigation.integrations.businesses': 'Businesses',
  'navigation.integrations.config': 'Config',
  'navigation.auditLogs': 'Audit Logs',
  'navigation.tracking': 'Tracking',
  'navigation.services': 'Services',
  'navigation.myApplications': 'My Applications',
  'navigation.myEntities': 'My Entities',
  'navigation.myGuarantees': 'My Guarantees',
  'navigation.myReferrals': 'My Referrals',
  'navigation.submitReferral': 'Submit Referral',
  'navigation.myCases': 'My Loans',
  'navigation.submitNew': 'Submit New',
  'navigation.teamManagement': 'Team Management',
  'navigation.signup': 'Signup',
  'navigation.compliance': 'Compliance',
  'navigation.records': 'Records',

  // Borrower portal nav (Fiops Borrower Portal redesign — Astor-first IA)
  // Sections appear in two groups: Work (active surfaces) and Reference (read-only history).
  // `navigation.astor` (line 691) + `navigation.loans` (line 740) already defined upstream.
  'navigation.offers': 'Offers',
  'navigation.signQueue': 'Sign queue',
  'navigation.guarantees': 'Guarantees',
  'navigation.work': 'Work',
  'navigation.reference': 'Reference',

  // Platform Guide
  'navigation.platform': 'Platform',
  'navigation.platformOverview': 'Overview',
  'navigation.platformPersonas': 'Personas',
  'navigation.platformWorkflows': 'Workflows',
  'navigation.platformStagePermissions': 'Stage Permissions',
  'navigation.platformFieldPermissions': 'Field Permissions',
  'navigation.platformArchitecture': 'Architecture',
  'navigation.platformCustomDomains': 'Custom Domains',

  // ============================================================================
  // SETTINGS (Issue #330 - Settings Hub)
  // ============================================================================
  'settings.searchPlaceholder': 'Search settings...',
  'settings.business': 'Business',
  'settings.businessDescription': 'Configure your business settings',
  'settings.organization': 'Organization',
  'settings.organizationDescription': 'Manage branches, roles, and permissions',
  'settings.workflowsAndPipelines': 'Workflows & Pipelines',
  'settings.workflowsAndPipelinesDescription': 'Configure process automation and sales pipelines',
  'settings.astorAi': 'Astor AI',
  'settings.astorAiDescription': 'Configure AI assistants and automation',
  'settings.aiAgents.title': 'AI Agents',
  'settings.aiAgents.description': 'Configure and manage AI-powered assistants',
  'settings.appearance.title': 'Appearance',
  'settings.appearance.description': 'Customize the look and feel of your portal',
  'settings.business.title': 'Business Settings',
  'settings.business.description': 'Configure your business information and preferences',
  'settings.integrations.title': 'Integrations',
  'settings.integrations.description': 'Connect with third-party services and APIs',
  'settings.integrations.pageDescription':
    'Connect the tools your team already uses — CRMs, communication platforms, credit bureaus, and property data providers. Configure credentials once and Fiops keeps them in sync.',
  'settings.integrations.byok.title': 'Integration API Keys',
  'settings.integrations.byok.description':
    'Configure third-party API keys for this business. These credentials are stored securely and used for email delivery, AI voice calling, and LLM processing.',
  'settings.integrations.byok.active': 'Active',
  'settings.integrations.byok.disabled': 'Disabled',
  'settings.integrations.byok.notConfigured': 'Not configured',
  'settings.integrations.byok.placeholder.new': 'Enter API key',
  'settings.integrations.byok.placeholder.update': 'Enter new key to update',
  'settings.integrations.byok.helperText.existing': 'A key is already configured. Enter a new key to replace it.',
  'settings.integrations.byok.helperText.new': 'Enter your {{provider}} API key to enable this integration',
  'settings.integrations.byok.save': 'Save',
  'settings.integrations.byok.saving': 'Saving...',
  'settings.integrations.byok.success.configured': '{{provider}} API key configured',
  'settings.integrations.byok.success.updated': '{{provider}} API key updated',
  'settings.integrations.byok.error.empty': 'Please enter a valid API key',
  'settings.integrations.byok.error.saveFailed': 'Failed to save API key',

  // ── HouseCanary integration panel (Issue #1629 — per-business BYOK) ──────
  // HouseCanary uses HTTP Basic auth (apiKey:apiSecret); both are required.
  // Configuring this connector lights up the cross-AVM second-opinion path
  // in property triage (digital-twin-service consumes via the gRPC bridge).
  'settings.integrations.housecanary.title': 'HouseCanary',
  'settings.integrations.housecanary.description':
    'Cross-AVM property valuation second-opinion. When configured, every loan-level property triage runs in parallel with HouseCanary and SFR Analytics so divergence flags the deal for underwriter review.',
  'settings.integrations.housecanary.docsLink': 'View HouseCanary developer docs',
  'settings.integrations.housecanary.apiKeyLabel': 'API Key',
  'settings.integrations.housecanary.apiKeyPlaceholder': 'Enter your HouseCanary API key',
  'settings.integrations.housecanary.apiKeyHelperExisting': 'API key is configured. Enter a new value to replace it.',
  'settings.integrations.housecanary.apiSecretLabel': 'API Secret',
  'settings.integrations.housecanary.apiSecretPlaceholder': 'Enter your HouseCanary API secret',
  'settings.integrations.housecanary.apiSecretHelperExisting':
    'API secret is configured. Enter a new value to replace it.',
  'settings.integrations.housecanary.apiSecretHelperNew':
    'HouseCanary uses HTTP Basic auth — both API key and secret are required.',
  'settings.integrations.housecanary.baseUrlLabel': 'API Base URL',
  'settings.integrations.housecanary.baseUrlHelper':
    'Defaults to https://api.housecanary.com/v2 — only override for sandbox or proxy environments.',
  'settings.integrations.housecanary.disconnect': 'Disconnect',
  'settings.integrations.housecanary.disconnecting': 'Disconnecting...',
  'settings.integrations.housecanary.connectedSince': 'Connected since {{date}}',
  'settings.integrations.housecanary.statusConnected': 'Connected',
  'settings.integrations.housecanary.statusNotConnected': 'Not connected',
  'settings.integrations.housecanary.success.saved': 'HouseCanary credentials saved.',
  'settings.integrations.housecanary.success.tested': 'Connection verified! HouseCanary API is accessible.',
  'settings.integrations.housecanary.success.disconnected': 'HouseCanary disconnected.',
  'settings.integrations.housecanary.error.missingCredentials':
    'Both API key and API secret are required for HouseCanary.',
  'settings.integrations.housecanary.error.testFailed': 'Connection failed. Please verify your credentials.',

  // ── Close.com integration page (Issue #1621 — Stream 4a) ─────────────────
  // Connection card
  'settings.integrations.close.title': 'Close.com',
  'settings.integrations.close.pageDescription':
    'Sync borrower contacts and lead activity from Close into the platform. Connect once, then run a backfill or wait for new leads tagged with your trigger value to flow through automatically.',
  'settings.integrations.close.connection.title': 'Connection',
  'settings.integrations.close.connection.description':
    'Paste your Close API key. We verify it against the Close /me endpoint before saving.',
  'settings.integrations.close.connection.apiKeyLabel': 'API key',
  'settings.integrations.close.connection.apiKeyPlaceholder': 'Enter your Close API key',
  'settings.integrations.close.connection.apiKeyHelperConfigured':
    'A key is already configured. Enter a new value to replace it.',
  'settings.integrations.close.connection.testButton': 'Test connection',
  'settings.integrations.close.connection.testing': 'Testing…',
  'settings.integrations.close.connection.statusConnected': 'Connected',
  'settings.integrations.close.connection.statusDisconnected': 'Not connected',
  'settings.integrations.close.connection.statusFailed': 'Connection failed',
  'settings.integrations.close.connection.organizationLabel': 'Organization',
  'settings.integrations.close.connection.disconnectButton': 'Disconnect',
  'settings.integrations.close.connection.disconnectConfirmTitle': 'Disconnect Close?',
  'settings.integrations.close.connection.disconnectConfirmBody':
    'This stops all sync activity. Synced borrowers stay in the platform, but no new updates flow in until you reconnect.',
  'settings.integrations.close.connection.disconnectConfirmConfirm': 'Disconnect',
  'settings.integrations.close.connection.disconnectSuccess': 'Close disconnected.',
  'settings.integrations.close.connection.testSuccess': 'Connection verified.',
  'settings.integrations.close.connection.testFailedDefault':
    'We could not reach Close with that key. Double-check the value and try again.',

  // Trigger card
  'settings.integrations.close.trigger.title': 'Trigger configuration',
  'settings.integrations.close.trigger.description':
    'Pick the Close custom field that determines whether a contact is in scope, and the value the field must have for sync to fire.',
  'settings.integrations.close.trigger.fieldLabel': 'Trigger custom field',
  'settings.integrations.close.trigger.fieldPlaceholder': 'Select a custom field',
  'settings.integrations.close.trigger.valueLabel': 'Trigger value',
  'settings.integrations.close.trigger.valuePlaceholder': 'e.g. Lead is borrower',
  'settings.integrations.close.trigger.entityTypeFieldLabel': 'Entity type custom field (optional)',
  'settings.integrations.close.trigger.einFieldLabel': 'EIN custom field (optional)',
  'settings.integrations.close.trigger.optionalFieldNone': 'None',
  'settings.integrations.close.trigger.saveButton': 'Save configuration',
  'settings.integrations.close.trigger.saving': 'Saving…',
  'settings.integrations.close.trigger.saveSuccess': 'Configuration saved.',
  'settings.integrations.close.trigger.schemaError':
    'Could not load Close custom fields. Verify your API key, then refresh.',
  'settings.integrations.close.trigger.fieldRequired': 'Choose a custom field.',
  'settings.integrations.close.trigger.valueRequired': 'Enter a trigger value.',

  // Field mapping card
  'settings.integrations.close.fieldMapping.title': 'Field mapping',
  'settings.integrations.close.fieldMapping.description':
    'Default contact and lead mappings. Per-tenant overrides are not editable in this release — let support know if you need a custom mapping.',
  'settings.integrations.close.fieldMapping.expandLabel': 'View default mappings',
  'settings.integrations.close.fieldMapping.closeFieldHeader': 'Close field',
  'settings.integrations.close.fieldMapping.platformFieldHeader': 'Platform field',
  'settings.integrations.close.fieldMapping.notesHeader': 'Notes',
  'settings.integrations.close.fieldMapping.contactSection': 'Contact',
  'settings.integrations.close.fieldMapping.leadSection': 'Lead',
  'settings.integrations.close.fieldMapping.contact.email.note': 'Lowercased and trimmed',
  'settings.integrations.close.fieldMapping.contact.phone.note': 'Normalized to E.164 (US default)',
  'settings.integrations.close.fieldMapping.contact.name.note': 'Trimmed',
  'settings.integrations.close.fieldMapping.lead.legalName.note': 'Trimmed',
  'settings.integrations.close.fieldMapping.lead.entityType.note': 'Normalized to platform entity types',
  'settings.integrations.close.fieldMapping.lead.ein.note': 'Optional — only when EIN field configured',

  // Field mappings — interactive table (Stream 4e)
  'settings.integrations.close.fieldMappings.title': 'Custom field mappings',
  'settings.integrations.close.fieldMappings.description':
    'Map Close.com custom fields to fiops fields so the values flow during sync.',
  'settings.integrations.close.fieldMappings.tabContact': 'Contact mappings',
  'settings.integrations.close.fieldMappings.tabLead': 'Lead mappings',
  'settings.integrations.close.fieldMappings.addButton': 'Add mapping',
  'settings.integrations.close.fieldMappings.readOnlyHint':
    "You don't have permission to edit field mappings — contact your administrator.",
  'settings.integrations.close.fieldMappings.empty': 'No custom field mappings yet for this object type.',
  'settings.integrations.close.fieldMappings.loadError': 'Could not load field mappings. Please try again.',
  'settings.integrations.close.fieldMappings.colCloseField': 'Close field',
  'settings.integrations.close.fieldMappings.colFiopsTarget': 'Fiops target',
  'settings.integrations.close.fieldMappings.colFieldType': 'Type',
  'settings.integrations.close.fieldMappings.colEnabled': 'Enabled',
  'settings.integrations.close.fieldMappings.colActions': 'Actions',
  'settings.integrations.close.fieldMappings.deleteAction': 'Delete mapping',

  // Add/edit form
  'settings.integrations.close.fieldMappings.form.createTitle': 'Add field mapping',
  'settings.integrations.close.fieldMappings.form.editTitle': 'Edit field mapping',
  'settings.integrations.close.fieldMappings.form.closeFieldLabel': 'Close field',
  'settings.integrations.close.fieldMappings.form.closeFieldHelper':
    'Pick the Close.com custom field whose value should flow to fiops.',
  'settings.integrations.close.fieldMappings.form.fiopsTargetLabel': 'Fiops target',
  'settings.integrations.close.fieldMappings.form.fiopsTargetHelper':
    'Choose where the value lands in fiops. Use “Custom (metadata)” to write into the metadata bag.',
  'settings.integrations.close.fieldMappings.form.customKeyLabel': 'Custom metadata key',
  'settings.integrations.close.fieldMappings.form.customKeyHelper':
    'Lowercase letters, numbers and underscores only (e.g. tax_year).',
  'settings.integrations.close.fieldMappings.form.customKeyInvalid':
    'Use lowercase letters, numbers and underscores only.',
  'settings.integrations.close.fieldMappings.form.customKeyRequired': 'Enter a metadata key.',
  'settings.integrations.close.fieldMappings.form.fieldTypeLabel': 'Field type',
  'settings.integrations.close.fieldMappings.form.fieldType.string': 'String',
  'settings.integrations.close.fieldMappings.form.fieldType.number': 'Number',
  'settings.integrations.close.fieldMappings.form.fieldType.date': 'Date',
  'settings.integrations.close.fieldMappings.form.fieldType.enum': 'Enum',
  'settings.integrations.close.fieldMappings.form.enabledLabel': 'Enabled',
  'settings.integrations.close.fieldMappings.form.enabledHelper': 'Disabled mappings are kept but skipped during sync.',
  'settings.integrations.close.fieldMappings.form.save': 'Save mapping',
  'settings.integrations.close.fieldMappings.form.cancel': 'Cancel',
  'settings.integrations.close.fieldMappings.form.closeFieldRequired': 'Pick a Close field.',
  'settings.integrations.close.fieldMappings.form.fiopsTargetRequired': 'Pick a fiops target.',
  'settings.integrations.close.fieldMappings.form.schemaLoadError':
    'Could not load Close custom fields. Verify your API key, then refresh.',
  'settings.integrations.close.fieldMappings.form.saveSuccess': 'Field mapping saved.',
  'settings.integrations.close.fieldMappings.form.saveError': 'Could not save field mapping.',

  // Delete confirmation
  'settings.integrations.close.fieldMappings.delete.title': 'Delete field mapping?',
  'settings.integrations.close.fieldMappings.delete.body':
    'This mapping will be removed and the value will no longer flow during sync. The default mappings are unaffected.',
  'settings.integrations.close.fieldMappings.delete.confirm': 'Delete',
  'settings.integrations.close.fieldMappings.delete.success': 'Field mapping deleted.',
  'settings.integrations.close.fieldMappings.delete.error': 'Could not delete field mapping.',

  // Defaults card (read-only listing of the 6 hardcoded mappings)
  'settings.integrations.close.fieldMappings.defaults.title': 'Default mappings (always active)',
  'settings.integrations.close.fieldMappings.defaults.description':
    'These six mappings ship with every tenant and run automatically. Custom mappings below add to them — they can’t override the defaults.',
  'settings.integrations.close.fieldMappings.defaults.contact.email': 'Contact.emails[0].email → Consumer.email',
  'settings.integrations.close.fieldMappings.defaults.contact.phone': 'Contact.phones[0].phone → Consumer.phone',
  'settings.integrations.close.fieldMappings.defaults.contact.name': 'Contact.name → Consumer.name',
  'settings.integrations.close.fieldMappings.defaults.lead.name': 'Lead.name → BorrowingEntity.legalName',
  'settings.integrations.close.fieldMappings.defaults.lead.entityType':
    'Lead.custom[entityTypeCustomFieldId] → BorrowingEntity.entityType',
  'settings.integrations.close.fieldMappings.defaults.lead.ein': 'Lead.custom[einCustomFieldId] → BorrowingEntity.ein',

  // Issue #1655 — direction picker, conflict resolution, choice value-map,
  // type-compat warnings, outbound-sync toggle, legacy-conversion banner.
  'settings.integrations.close.fieldMappings.direction.label': 'Sync direction',
  'settings.integrations.close.fieldMappings.direction.inbound': 'Close → Platform',
  'settings.integrations.close.fieldMappings.direction.outbound': 'Platform → Close',
  'settings.integrations.close.fieldMappings.direction.bidirectional': 'Both directions',
  'settings.integrations.close.fieldMappings.conflictResolution.label': 'Conflict resolution',
  'settings.integrations.close.fieldMappings.conflictResolution.lastWriteWins': 'Most recent edit wins',
  'settings.integrations.close.fieldMappings.conflictResolution.fiopsWins': 'Platform always wins',
  'settings.integrations.close.fieldMappings.conflictResolution.closeWins': 'Close always wins',
  'settings.integrations.close.fieldMappings.compatibility.incompatible':
    'These two field types cannot be mapped together. Pick a compatible target field or change the Close field type.',
  'settings.integrations.close.fieldMappings.compatibility.warning':
    'This combination requires a value map — set one before saving.',
  'settings.integrations.close.fieldMappings.choiceMap.title': 'Map Close options to platform options',
  'settings.integrations.close.fieldMappings.choiceMap.unmapped':
    '{{count}} Close option(s) are not mapped — values from those options will be skipped on sync.',
  'settings.integrations.close.fieldMappings.choiceMap.closeOption': 'Close option',
  'settings.integrations.close.fieldMappings.choiceMap.fiopsOption': 'Platform option',
  // Action label for the dropdown entry that removes a previously-set
  // mapping. Distinct from the column header `fiopsOption` so the picker
  // reads as an action ("skip / don't map") rather than the table header.
  'settings.integrations.close.fieldMappings.choiceMap.fiopsSkip': "Skip / don't map",
  'settings.integrations.close.fieldMappings.outboundSync.toggleLabel': 'Push platform edits back to Close',
  'settings.integrations.close.fieldMappings.outboundSync.toggleHelper':
    'When enabled, edits to mapped fields on the platform are sent back to the linked Close record. Mappings still need direction set to Outbound or Both.',
  'settings.integrations.close.fieldMappings.legacy.bannerTitle':
    '{{count}} Close mapping(s) still use the legacy "Custom (metadata)" target.',
  'settings.integrations.close.fieldMappings.legacy.bannerBody':
    'Convert each one to a typed platform field so the value lands on the entity record itself instead of an unstructured metadata blob.',
  'settings.integrations.close.fieldMappings.legacy.convertButton': 'Convert to typed field',

  // Backfill card
  'settings.integrations.close.backfill.title': 'Backfill',
  'settings.integrations.close.backfill.description':
    'Pull every Close contact whose trigger field matches your value into the platform. Safe to run repeatedly — already-synced contacts are skipped.',
  'settings.integrations.close.backfill.startButton': 'Start backfill',
  'settings.integrations.close.backfill.starting': 'Starting…',
  'settings.integrations.close.backfill.disabledHint': 'Save and verify your configuration before running a backfill.',
  'settings.integrations.close.backfill.statusPending': 'Pending',
  'settings.integrations.close.backfill.statusRunning': 'Running',
  'settings.integrations.close.backfill.statusCompleted': 'Completed',
  'settings.integrations.close.backfill.statusFailed': 'Failed',
  'settings.integrations.close.backfill.totalLabel': 'Total',
  'settings.integrations.close.backfill.processedLabel': 'Processed',
  'settings.integrations.close.backfill.createdLabel': 'Created',
  'settings.integrations.close.backfill.linkedLabel': 'Linked',
  'settings.integrations.close.backfill.flaggedDupLabel': 'Flagged duplicates',
  'settings.integrations.close.backfill.errorCountLabel': 'Errors',
  'settings.integrations.close.backfill.errorsTitle': 'Sync errors',
  'settings.integrations.close.backfill.startedAtLabel': 'Started',
  'settings.integrations.close.backfill.completedAtLabel': 'Completed',
  'settings.integrations.close.backfill.startFailedDefault': 'Could not start backfill. Try again in a moment.',

  // Activity log card — Issue #1670 Phase 2.3 replaced the stub with a real
  // paginated list backed by closeIntegration.listSyncJobs.
  'settings.integrations.close.activityLog.title': 'Sync activity log',
  'settings.integrations.close.activityLog.description':
    'Recent Close sync jobs (backfills, refreshes, webhook syncs).',
  'settings.integrations.close.activityLog.empty':
    'No sync activity yet. Start a backfill or wait for a webhook to fire.',
  'settings.integrations.close.activityLog.loadError': 'Failed to load sync activity log',
  'settings.integrations.close.activityLog.loadMore': 'Load more',
  'settings.integrations.close.activityLog.columns.jobType': 'Type',
  'settings.integrations.close.activityLog.columns.status': 'Status',
  'settings.integrations.close.activityLog.columns.startedAt': 'Started',
  'settings.integrations.close.activityLog.columns.processed': 'Processed',
  'settings.integrations.close.activityLog.columns.created': 'Created',
  'settings.integrations.close.activityLog.columns.linked': 'Linked',
  'settings.integrations.close.activityLog.columns.errors': 'Errors',
  // Issue #1707 — jobType labels rendered as a colored chip in each row.
  // NOTE: production data stores `'initial_backfill'` for the one-shot
  // connect-time pull (see CloseBackfillController.StartCloseBackfill);
  // `'backfill'` is the same kind but used by some test fixtures. Both map
  // to the same label + color so the chip stays consistent across
  // historical and freshly-created rows.
  'settings.integrations.close.activityLog.types.backfill': 'Backfill',
  'settings.integrations.close.activityLog.types.initial_backfill': 'Backfill',
  'settings.integrations.close.activityLog.types.webhook': 'Webhook',
  'settings.integrations.close.activityLog.types.refresh': 'Refresh',
  'settings.integrations.close.activityLog.types.delta': 'Delta sync',
  'settings.integrations.close.activityLog.triggerDeltaSync': 'Sync now',
  // Surfaced when the gateway's direction-filter over-fetch cap binds — the
  // page may be a lower bound of matching events. Operators can narrow the
  // window or clear the filter to see the full count.
  'settings.integrations.close.activityLog.truncatedHint':
    'Results may be incomplete — too many events to scan in one batch. Narrow the filter or clear it to see the full list.',
  // Legacy key kept for back-compat with any reference still in the tree.
  'settings.integrations.close.activityLog.notImplemented':
    'No activity log yet — implementation pending in #1621 follow-up.',

  // Health strip — Issue #1670 Phase 4c
  'settings.integrations.close.health.title': 'Integration health',
  'settings.integrations.close.health.connected': 'Connected',
  'settings.integrations.close.health.disconnected': 'Disconnected',
  'settings.integrations.close.health.lastSyncNever': 'No sync yet',
  'settings.integrations.close.health.lastSync': 'Last sync',
  'settings.integrations.close.health.errorRate': 'Error rate (last 50)',
  'settings.integrations.close.health.commsLast24h': 'Comms (24h)',
  'settings.integrations.close.health.totalJobs': 'Total syncs',
  'settings.integrations.close.health.statusSuccess': 'success',
  'settings.integrations.close.health.statusPartial': 'partial',
  'settings.integrations.close.health.statusFailed': 'failed',
  'settings.integrations.close.health.statusNone': 'none',
  'settings.integrations.close.health.loadError': 'Failed to load health snapshot',
  // Compact relative-time labels for the health strip's "last sync" cell.
  'settings.integrations.close.health.justNow': 'just now',
  'settings.integrations.close.health.minutesAgo': '{{count}}m ago',
  'settings.integrations.close.health.hoursAgo': '{{count}}h ago',
  'settings.integrations.close.health.daysAgo': '{{count}}d ago',

  // Borrower-URL backlink writeback — Issue #1670 Phase 4a
  'settings.integrations.close.writeback.title': 'Borrower URL writeback (optional)',
  'settings.integrations.close.writeback.description':
    'Push the fiops borrower-portal URL into a Close custom field after each borrower record change so sales reps can click straight into fiops without leaving Close.',
  'settings.integrations.close.writeback.borrowerIdFieldLabel': 'Close field — fiops borrower URL',

  // Dry-run validate-mapping panel — Issue #1670 Phase 4b
  'settings.integrations.close.validateLead.title': 'Test with Close lead',
  'settings.integrations.close.validateLead.description':
    'Paste a Close lead ID to preview what fiops would receive from your current mappings — read-only, no writes.',
  'settings.integrations.close.validateLead.input': 'Close lead ID (lead_xxx)',
  'settings.integrations.close.validateLead.run': 'Run dry-run',
  'settings.integrations.close.validateLead.running': 'Running…',
  'settings.integrations.close.validateLead.error': 'Dry-run failed',
  'settings.integrations.close.validateLead.leadHeader': 'Lead',
  'settings.integrations.close.validateLead.leadResultsHeader': 'Lead-level mappings',
  'settings.integrations.close.validateLead.contactResultsHeader': 'Contact-level mappings',
  'settings.integrations.close.validateLead.noMappings': 'No mappings configured for this object type.',
  'settings.integrations.close.validateLead.statusOk': 'ok',
  'settings.integrations.close.validateLead.statusSkip': 'skip',
  'settings.integrations.close.validateLead.statusIncompatible': 'incompatible',
  'settings.integrations.close.validateLead.statusError': 'error',

  // Page-level
  'settings.integrations.close.permissionRequired':
    'You need the Close integration administrator permission to access this page.',

  // ── Opportunity → Loan sync (Issue #1734) ─────────────────────────────
  'settings.integrations.close.opportunitySync.title': 'Opportunity → Loan sync',
  'settings.integrations.close.opportunitySync.description':
    'Create a loan in the platform when a Close opportunity reaches the trigger stage. Optionally sync subsequent stage advances in both directions.',
  'settings.integrations.close.opportunitySync.triggerStage.label': 'Trigger stage',
  'settings.integrations.close.opportunitySync.triggerStage.help':
    'When an opportunity moves into this Close stage, create a new loan in the platform with the mapped fields.',
  'settings.integrations.close.opportunitySync.triggerStage.placeholder': 'Select a Close opportunity stage…',
  'settings.integrations.close.opportunitySync.triggerStage.empty':
    'No opportunity stages found in Close. Add a stage in Close before configuring this sync.',
  'settings.integrations.close.opportunitySync.stageMapping.title': 'Stage mapping',
  'settings.integrations.close.opportunitySync.stageMapping.description':
    'Map each Close opportunity stage to a platform loan stage. Unmapped Close stages are ignored (no-op).',
  'settings.integrations.close.opportunitySync.stageMapping.colCloseStage': 'Close stage',
  'settings.integrations.close.opportunitySync.stageMapping.colFiopsStage': 'Platform loan stage',
  'settings.integrations.close.opportunitySync.stageMapping.unmappedPlaceholder': '— Not mapped (no-op) —',
  'settings.integrations.close.opportunitySync.stageMapping.empty':
    'No loan stages defined for this business yet. Configure loan stages in Control Center → Loan Stages first.',
  'settings.integrations.close.opportunitySync.writeback.toggle.label': 'Write platform stage back to Close',
  'settings.integrations.close.opportunitySync.writeback.toggle.description':
    'When a loan advances in the platform, update the matching Close opportunity to the inverse-mapped stage. Failures retry up to 3 times with backoff.',
  'settings.integrations.close.opportunitySync.writeback.toggle.disabledReauthorize':
    'Re-authorize Close with write scope to enable writeback.',
  'settings.integrations.close.opportunitySync.writeback.reauthorizeBanner':
    'Your Close API token does not have write permission on opportunities. Re-authorize the integration to enable writeback.',
  'settings.integrations.close.opportunitySync.fiopsLoanUrl.label':
    'Close field — platform loan URL backlink (optional)',
  'settings.integrations.close.opportunitySync.fiopsLoanUrl.description':
    'When a loan is created from a Close opportunity, stamp the loan-detail URL into this Close custom field. Leave unset to skip.',
  'settings.integrations.close.opportunitySync.fiopsLoanUrl.placeholder': 'Select a Close custom field…',
  'settings.integrations.close.opportunitySync.fieldMap.title': 'Field mapping',
  'settings.integrations.close.opportunitySync.fieldMap.description':
    'Map Close opportunity custom fields to platform loan fields. Standard fields (`display_name`, `value`) are mapped automatically; only configure overrides here.',
  'settings.integrations.close.opportunitySync.fieldMap.colFiopsField': 'Platform field',
  'settings.integrations.close.opportunitySync.fieldMap.colCloseField': 'Close custom field',
  'settings.integrations.close.opportunitySync.fieldMap.unmapped': '— Not mapped —',
  'settings.integrations.close.opportunitySync.backfill.title': 'Backfill existing opportunities',
  'settings.integrations.close.opportunitySync.backfill.description':
    'After enabling the sync, run a one-time backfill to import opportunities that are already in or past the trigger stage.',
  'settings.integrations.close.opportunitySync.backfill.button': 'Backfill opportunities',
  'settings.integrations.close.opportunitySync.backfill.buttonWithCount': 'Backfill {{count}} opportunities',
  'settings.integrations.close.opportunitySync.backfill.inProgress': 'Backfill in progress…',
  'settings.integrations.close.opportunitySync.backfill.progressFmt': '{{done}} / {{total}} opportunities',
  'settings.integrations.close.opportunitySync.backfill.completed': 'Backfill completed',
  'settings.integrations.close.opportunitySync.backfill.failed': 'Backfill failed',
  'settings.integrations.close.opportunitySync.backfill.confirmTitle': 'Backfill {{count}} opportunities?',
  'settings.integrations.close.opportunitySync.backfill.confirmBody':
    'This will fetch every Close opportunity at or past the trigger stage and create a platform loan for each. The job runs in the background and may take several minutes.',
  'settings.integrations.close.opportunitySync.saveButton': 'Save opportunity sync',
  'settings.integrations.close.opportunitySync.saved': 'Opportunity sync saved.',
  // PR-feedback #3246061543 — error-specific fallback. Previously the error
  // branch reused the success key, rendering "Saved" after a failure.
  'settings.integrations.close.opportunitySync.saveError': 'Could not save opportunity sync. Try again.',
  // PR-feedback #3243975522 — tooltip on the disabled Backfill button when the
  // form has unsaved changes (backfill reads only the persisted config).
  'settings.integrations.close.opportunitySync.backfill.dirtyHint':
    'Save the current changes before starting a backfill.',
  'settings.integrations.close.opportunitySync.disabledMissingConfig':
    'Configure a trigger stage to enable this section.',
  'settings.integrations.close.opportunitySync.statusEnabled': 'Enabled',
  'settings.integrations.close.opportunitySync.statusDisabled': 'Disabled',

  // ── Webhook-only sync health + DLQ surfaces (Issue #1738) ──────────────
  // Render after CloseSyncStatusSection on the Integration page so business
  // admins can see the sync mechanism at a glance and intervene when stuck.
  // Note: `settings.integrations.close.health.*` is already used by the
  // existing connection-health card — these go under `webhookHealth.*`.
  'settings.integrations.close.webhookHealth.title': 'Sync status',
  'settings.integrations.close.webhookHealth.description':
    'Real-time view of the Close ↔ fiops webhook pipeline. We sync via webhook-only — no polling.',
  'settings.integrations.close.webhookHealth.modeLabel': 'Mode',
  'settings.integrations.close.webhookHealth.modeWebhookOnly': 'Webhook-only',
  'settings.integrations.close.webhookHealth.lastEventLabel': 'Last event received',
  'settings.integrations.close.webhookHealth.lastEventNever': 'No events received yet',
  'settings.integrations.close.webhookHealth.eventsLast24hLabel': 'Events in last 24h',
  'settings.integrations.close.webhookHealth.byTypeHeading': 'By event type (24h)',
  'settings.integrations.close.webhookHealth.byTypeEmpty': 'No events received in the last 24 hours.',
  'settings.integrations.close.webhookHealth.refreshTooltip': 'Refresh now',
  'settings.integrations.close.webhookHealth.loadError':
    'Could not load webhook health. Check connection and try again.',
  'settings.integrations.close.dlq.title': 'Stuck events',
  'settings.integrations.close.dlq.description':
    'Events that downstream services rejected. They retry automatically; use Drain to retry immediately.',
  'settings.integrations.close.dlq.inboundTitle': 'Inbound (Close → fiops)',
  'settings.integrations.close.dlq.outboundTitle': 'Outbound (fiops → Close)',
  'settings.integrations.close.dlq.countLabel': 'Pending',
  'settings.integrations.close.dlq.deadLetterLabel': 'Dead letter',
  'settings.integrations.close.dlq.oldestLabel': 'Oldest entry',
  'settings.integrations.close.dlq.empty': 'No stuck events.',
  'settings.integrations.close.dlq.drainButton': 'Drain now',
  'settings.integrations.close.dlq.drainConfirmTitle': 'Drain inbound queue?',
  'settings.integrations.close.dlq.drainConfirmBody':
    'This will retry every pending and dead-letter event in the queue against downstream services. Continue?',
  'settings.integrations.close.dlq.drainConfirmConfirm': 'Drain now',
  'settings.integrations.close.dlq.drainSuccess': 'Drain started.',
  'settings.integrations.close.dlq.drainSuccessWithCount': 'Retried {{count}} stuck events.',
  'settings.integrations.close.dlq.drainFailed': 'Could not drain queue. Try again.',
  'settings.integrations.close.dlq.loadError': 'Could not load DLQ status. Refresh to retry.',
  'settings.integrations.close.dlq.pausedLabel': 'Paused (schema drift)',

  // ── Sync activity / audit diff (Issue #1738) ───────────────────────────
  // Surfaces the structured field diff in CloseSyncActivityLog + the audit
  // page so admins can answer "exactly what changed?" without leaving the
  // portal. Reuses existing `audit.diff.title` / `audit.diff.noChanges`
  // keys from the version-comparison surface — these are Close-sync-specific
  // additions only.
  'audit.direction.label': 'Direction',
  'audit.direction.all': 'All',
  'audit.direction.inbound': 'Inbound',
  'audit.direction.outbound': 'Outbound',
  // PR-feedback #3246061663 — Audit page entity-type filter labels. Resolved
  // via `t(...)` so the white-label deploys / locales translate them.
  'audit.entityTypes.case': 'Cases',
  'audit.entityTypes.consumer': 'Consumers',
  'audit.entityTypes.partner': 'Partners',
  'audit.entityTypes.team_member': 'Team Members',
  'audit.entityTypes.user': 'Users',
  'audit.entityTypes.close_sync_job': 'Close sync jobs',
  'audit.entityTypes.close_sync': 'Close sync contact events',
  'audit.entityTypes.close_opportunity': 'Close opportunity sync (outbound)',
  'audit.entityTypes.close_opportunity_link': 'Close opportunity links',
  'audit.entityTypes.close_inbound_dlq': 'Close inbound DLQ events',
  'audit.closeSync.toggleShow': 'Show field changes',
  'audit.closeSync.toggleHide': 'Hide field changes',
  'audit.closeSync.fieldHeading': 'Field',
  'audit.closeSync.beforeHeading': 'Before',
  'audit.closeSync.afterHeading': 'After',
  // Surfaced as the section header above the field/before/after table when
  // the audit row is expanded. The audit-page diff drawer reuses
  // `audit.diff.title`; the integration-page sync log uses this dedicated
  // key so the wording reads naturally inline ("Changes" rather than
  // "Version Comparison").
  'audit.closeSync.changesHeading': 'Changes',
  'audit.closeSync.actorLabel': 'Actor',
  // Fallback label when an audit row has no resolved actor (defensive — the
  // gateway resolver normally maps every `system:*` sentinel to a readable
  // label like "Close.com (webhook)").
  'audit.closeSync.systemActor': 'System',
  // Subscript on each row when the change came from Close.com (source =
  // Close) but a specific platform user triggered the pull
  // (e.g. clicked "Sync now"). Resolves to "via Rachel Martinez" so admins
  // can see both the data origin AND the operator who triggered it.
  'audit.closeSync.viaActor': 'via {{actor}}',
  'audit.closeSync.triggerLabel': 'Trigger',
  // Keys here mirror the camelCased triggerSource enum value (the audit
  // page composes the key as `triggerWebhookEvent` for the enum value
  // 'webhook_event', etc.). Earlier shorter names (triggerWebhook /
  // triggerUser) didn't match the composed key and rendered as raw key
  // strings in the diff drawer — caught in E2E this session.
  'audit.closeSync.triggerWebhookEvent': 'Webhook event',
  'audit.closeSync.triggerUserInitiated': 'User-initiated',
  'audit.closeSync.triggerDlqDrain': 'DLQ drain',
  'audit.closeSync.triggerCronDrain': 'Cron drain',
  'audit.closeSync.webhookEventIdLabel': 'Webhook event',
  'audit.closeSync.idempotencyKeyLabel': 'Idempotency key',
  'audit.closeSync.latencyLabel': 'Latency',
  'audit.closeSync.latencyMs': '{{ms}} ms',
  'audit.closeSync.bannerTitle': 'Close.com sync mechanism',
  'audit.closeSync.bannerModeWebhookOnly': 'Webhook-only — every event below was delivered by Close via webhook.',
  'audit.closeSync.bannerLastEvent': 'Last webhook event: {{timestamp}}',
  'audit.closeSync.bannerInboundDlqCount': '{{count}} inbound events queued for retry',
  'audit.closeSync.bannerOutboundDlqCount': '{{count}} outbound events queued for retry',

  // ── Connected Integrations Hub (Issue #1621 — Phase 2 follow-up) ─────────
  // The /settings/integrations index — a grid of GlassCards listing each
  // per-tenant integration (Close.com today; Salesforce / HubSpot / others
  // later). Distinct from /control-center/integrations (SFR Analytics, Slack,
  // Zillow) which is a single configuration page for those connectors.
  'settings.integrations.hub.title': 'Connected Integrations',
  'settings.integrations.hub.description':
    'Connect external systems your team relies on — CRMs, communication platforms, deal-management tools — so leads and contacts stay in sync with the platform.',
  'settings.integrations.hub.statusConnected': 'Connected',
  'settings.integrations.hub.statusNotConnected': 'Not connected',
  'settings.integrations.hub.configure': 'Configure',
  'settings.integrations.hub.permissionLocked':
    'You do not have permission to configure this integration. Ask a business administrator.',
  'settings.integrations.hub.close.name': 'Close.com',
  'settings.integrations.hub.close.description':
    'Sync borrower contacts and lead activity from Close. Trigger applications when a lead hits your configured stage.',
  'settings.profile.description': 'Manage your personal profile and preferences',
  'settings.team.title': 'Team Management',
  'settings.team.description': 'Manage team members, roles, and permissions',
  'settings.documentChecklists': 'Document Checklists',
  'settings.documentChecklistsDescription': 'Configure required documents per loan type',
  'settings.documentTemplates': 'Document Templates',
  'settings.documentTemplatesDescription': 'Manage document templates for proposals and agreements',
  'settings.complianceSettings': 'Compliance Settings',
  'settings.complianceSettingsDescription': 'Compliance settings and requirements',
  'settings.integrations': 'Integrations',
  'settings.integrationsDescription': 'Manage connected apps and services',
  'settings.website': 'Website',
  'settings.websiteDescription': 'Manage your website content, blog, and testimonials',
  'settings.ai': 'Astor',
  'settings.aiDescription': 'Configure assistants and automation',
  'settings.aiAgents': 'Agents',
  'settings.aiAgentsDescription': 'Create and manage agents',
  'astorAgents.pageTitle': 'AI Agents',
  'astorAgents.pageSubtitle':
    'Build AI agents that handle routine work for your team — like drafting follow-ups, summarizing loan files, or flagging missing documents. Agents run against your data, on your rules, inside Fiops.',
  'astorAgents.emptyTitle': 'No agents yet',
  'astorAgents.emptyDescription':
    'Agents are AI assistants you configure for specific tasks. Start with a simple one — like an intake reviewer or a document extractor — and iterate from there.',
  'astorAgents.emptyCreateButton': 'Create your first agent',
  // Astor Autonomy page (v3 design-pass-5 PR-2) — Issue #1806
  'astorAutonomy.pageTitle': 'Astor autonomy',
  'astorAutonomy.pageDescription': 'Configure how independently Astor acts per agent.',
  'astorAutonomy.viewAuditLog': 'View audit log',
  'astorAutonomy.placeholderNotice': 'Showing placeholder configuration. Backend procedures pending (Issue #1806).',
  'astorAutonomy.masterSelectorLabel': 'Master autonomy level',
  'astorAutonomy.auditLog': 'Audit log',
  // Level definitions
  'astorAutonomy.levels.L1.name': 'Observe',
  'astorAutonomy.levels.L1.sub': 'Suggest only · I act',
  'astorAutonomy.levels.L2.name': 'Supervised',
  'astorAutonomy.levels.L2.sub': 'Recommend · I approve',
  'astorAutonomy.levels.L3.name': 'Trusted',
  'astorAutonomy.levels.L3.sub': 'Acts within guardrails',
  'astorAutonomy.levels.L4.name': 'Full',
  'astorAutonomy.levels.L4.sub': 'Acts · escalates exceptions',
  // Overrides table
  'astorAutonomy.overrides.title': 'Per-agent overrides',
  'astorAutonomy.overrides.description': 'Set autonomy per agent. Falls back to master level when unset.',
  'astorAutonomy.overrides.col.agent': 'Agent',
  'astorAutonomy.overrides.col.description': 'Description',
  'astorAutonomy.overrides.col.level': 'Level',
  'astorAutonomy.overrides.col.calls7d': 'Calls (7d)',
  'astorAutonomy.row.tune': 'Tune',
  'astorAutonomy.row.viewMetrics': 'View metrics',
  // Agent descriptions (mono agent identifiers stay untranslated)
  'astorAutonomy.agents.intakePrescreener': 'Pre-screens new applications, recommends approve or decline.',
  'astorAutonomy.agents.sizerComputer': 'Computes loan sizing within policy gates.',
  'astorAutonomy.agents.compsPuller': 'Auto-pulls comparable property data.',
  'astorAutonomy.agents.memoDrafter': 'Drafts credit memos for underwriter review.',
  'astorAutonomy.agents.committeeRouter': 'Routes loans to the appropriate approval committee.',
  'astorAutonomy.agents.borrowerMessenger': 'Drafts borrower communication messages.',
  'astorAutonomy.agents.docVerifier': 'Auto-verifies uploaded documents against schema.',
  'astorAutonomy.agents.closingCoordinator': 'Coordinates closing-stage tasks.',
  'settings.aiAutonomy': 'Autonomy',
  'settings.aiAutonomyDescription': 'Configure autonomy levels',
  'settings.security': 'Security',
  'settings.securityDescription': 'Security and access controls',
  'settings.personal': 'Personal',
  'settings.personalDescription': 'Your personal preferences',
  // Control Center sidebar section headers
  'settings.sectionOrganization': 'Organization',
  'settings.sectionWorkflows': 'Workflows & Pipelines',
  'settings.sectionLoanLifecycle': 'Loan Lifecycle',
  'settings.sectionTemplates': 'Templates',
  // Loan Lifecycle Subsystem (Issue #1388)
  'settings.loanProducts': 'Loan Products',
  'settings.loanProductsDescription': 'Catalog of loan products your business offers',
  'settings.loanStages': 'Loan Stages',
  'settings.loanStagesDescription': 'Configure how loans move from lead to servicing',
  'settings.loanStageRequirements': 'Stage Requirements',
  'settings.loanStageRequirementsDescription':
    'Map data points to lifecycle stages — what must be collected before a loan can advance',
  'settings.loanDataPoints': 'Loan Data Points',
  'settings.loanDataPointsDescription': 'Catalog of data fields collected during the loan lifecycle',
  'settings.sizerPolicy': 'Sizer',
  'settings.sizerPolicyDescription': 'Tier caps, leverage haircuts, and conditional flags for the loan sizer',
  'settings.sectionAstorAI': 'Astor AI',
  'settings.sectionIntegrations': 'Integrations',
  'settings.sectionNotifications': 'Notifications & Finance',
  'settings.sectionWebsite': 'Website',
  'settings.sectionPersonal': 'Personal',
  // Secondary-nav section headers used in `contacts`, `prospecting`,
  // `reports`, and `workspace`. Same rendering pipeline as the Control Center
  // sections: sortItemsByTranslatedLabel groups by header alphabetically,
  // ungrouped items pin to the top, SidebarPanel renders the header once
  // per group. Names chosen so the alphabetical sort produces a sensible
  // reading order (Borrowers < Partnerships; Analytics < Audit Trails).
  'navigation.sectionBorrowers': 'Borrowers',
  'navigation.sectionPartnerships': 'Partnerships',
  'navigation.sectionPipeline': 'Pipeline',
  'navigation.sectionOutreach': 'Outreach',
  'navigation.sectionAnalytics': 'Analytics',
  'navigation.sectionAuditTrails': 'Audit Trails',
  'navigation.sectionTeam': 'Team',
  'settings.branches': 'Branches',
  'settings.branchesDescription': 'Manage business branches and locations',
  'settings.customRoles': 'Custom Roles',
  'settings.customRolesDescription': 'Create and manage custom roles',
  'settings.compliance': 'Compliance',
  'settings.complianceDescription': 'Compliance settings and requirements',
  'settings.onboarding': 'Getting Started',
  'settings.onboardingDescription': 'Your personal checklist to finish setting up your account',
  'settings.borrowingEntities': 'Borrowing Entities',
  'settings.borrowingEntitiesDescription': 'Manage LLCs, trusts, corporations, and ownership structures',
  'settings.entityDefinitions': 'Entity Definitions',
  'settings.entityDefinitionsDescription': 'Manage custom entity schemas and data models',
  'settings.pricing': 'Pricing',
  'settings.pricingDescription': 'Pricing rules and rate configurations',
  'settings.teamSettings': 'Team Settings',
  'settings.myTemplates': 'My Templates',
  'settings.prospectingTemplates': 'Prospecting Templates',
  'settings.prospectingTemplatesDescription': 'Reusable templates for sequences, campaigns, and saved searches',
  'settings.pipelineTemplates': 'Pipeline Templates',
  'settings.pipelineTemplatesDescription': 'Reusable loan pipeline templates',
  'settings.termsheetTemplates': 'Termsheet Templates',
  'settings.termsheetTemplatesDescription': 'Reusable termsheet templates for offers',
  'templates.pipelines.emptyDescription': 'Save a pipeline as a template to reuse its stages on future loans.',
  'templates.termsheets.emptyDescription':
    'Save a termsheet as a template to reuse its terms and signers on future offers.',
  'settings.caseManagement': 'Loans',
  // Sidebar override for the Loan Lifecycle entry that points at /control-center/case-management.
  // The page itself still renders "Loans" via settings.caseManagement; this key only retitles the
  // sidebar item so it stops colliding with the operational left-rail "Loans".
  'settings.loanSettings': 'Loan Settings',
  'settings.astorAnalytics': 'Astor Analytics',
  'settings.businessProfile': 'Business Profile',
  'settings.businessOnboarding': 'Business Setup',
  'settings.businessOnboardingDescription': 'Guided setup to configure your business on the platform',
  // Hub overview-card descriptions for entries that appear in the secondary sidebar
  // but were missing matching descriptions on the hub. Keep these aligned with the
  // sidebar items so the overview never drifts from the navigation.
  'settings.businessProfileDescription': 'Manage your business name, logo, and contact information',
  'settings.teamSettingsDescription': 'Manage team members, teams, and organizational structure',
  'settings.loanSettingsDescription': 'Configure loan types, statuses, and SLAs',
  'settings.astorAnalyticsDescription': 'Usage and performance metrics for your Astor agents',
  'settings.notificationsDescription': 'Configure notification channels and delivery preferences',
  'settings.commissionsDescription': 'Configure commission rules and partner payouts',
  // Section descriptions for hub groupings that mirror sidebar section headers
  'settings.loanLifecycleDescription': 'Configure loan stages, products, requirements, and data collection',
  'settings.notificationsAndFinanceDescription': 'Manage notifications, commissions, and pricing rules',
  // Pricing Settings Page (Configurable Rate Calculator)
  'pricing.title': 'Pricing Rules',
  'pricing.subtitle': 'Configure rate formulas and adjustment tiers for your loan products',
  'pricing.createRule': 'Create Pricing Rule',
  'pricing.editRule': 'Edit Pricing Rule',
  'pricing.deleteRule': 'Delete Pricing Rule',
  'pricing.deleteConfirmTitle': 'Delete Pricing Rule',
  'pricing.deleteConfirmMessage': 'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
  'pricing.noRules': 'No pricing rules configured',
  'pricing.noRulesDescription': 'Create your first pricing rule to customize how rates are calculated for your loans.',
  'pricing.setAsDefault': 'Set as Default',
  'pricing.default': 'Default',
  'pricing.active': 'Active',
  'pricing.inactive': 'Inactive',
  // Base Configuration Section
  'pricing.baseConfig': 'Base Configuration',
  'pricing.baseConfigDescription': 'Set the foundation for your rate calculations',
  'pricing.ruleName': 'Rule Name',
  'pricing.ruleNamePlaceholder': 'e.g., Standard Bridge Pricing',
  'pricing.description': 'Description',
  'pricing.descriptionPlaceholder': 'Describe when this rule applies',
  'pricing.baseRate': 'Base Rate',
  'pricing.baseRateHelp': 'Starting interest rate before adjustments',
  'pricing.minLoanAmount': 'Minimum Loan Amount',
  'pricing.maxLoanAmount': 'Maximum Loan Amount',
  'pricing.maxLTV': 'Maximum LTV',
  'pricing.maxLTVHelp': 'Loan-to-value ratio limit (e.g., 75% = 0.75)',
  'pricing.maxLTC': 'Maximum LTC',
  'pricing.maxLTCHelp': 'Loan-to-cost ratio limit for rehab loans',
  'pricing.termOptions': 'Available Terms',
  'pricing.termOptionsHelp': 'Loan term lengths in months',
  // Adjustment Tiers Section
  'pricing.adjustments': 'Rate Adjustments',
  'pricing.adjustmentsDescription': 'Configure how rates adjust based on loan characteristics',
  'pricing.ficoAdjustments': 'FICO Score Adjustments',
  'pricing.ficoAdjustmentsHelp': 'Rate changes based on borrower credit score',
  'pricing.ltvAdjustments': 'LTV Adjustments',
  'pricing.ltvAdjustmentsHelp': 'Rate changes based on loan-to-value ratio',
  'pricing.experienceAdjustments': 'Experience Adjustments',
  'pricing.experienceAdjustmentsHelp': 'Rate changes based on borrower experience level',
  'pricing.propertyAdjustments': 'Property Type Adjustments',
  'pricing.propertyAdjustmentsHelp': 'Rate changes based on property type',
  'pricing.loanAmountAdjustments': 'Loan Amount Adjustments',
  'pricing.loanAmountAdjustmentsHelp': 'Rate changes based on loan size',
  'pricing.tierMin': 'Min',
  'pricing.tierMax': 'Max',
  'pricing.tierAdjustment': 'Adjustment',
  'pricing.tierLabel': 'Label',
  'pricing.addTier': 'Add Tier',
  'pricing.removeTier': 'Remove',
  // Special Adjustments Section
  'pricing.specialAdjustments': 'Special Adjustments',
  'pricing.specialAdjustmentsDescription': 'Discounts and bonuses for specific scenarios',
  'pricing.repeatBorrower': 'Repeat Borrower Discount',
  'pricing.repeatBorrowerHelp': 'Rate reduction for returning customers',
  'pricing.crossCollateral': 'Cross-Collateral Discount',
  'pricing.crossCollateralHelp': 'Rate reduction when pledging additional properties',
  'pricing.earlyPayoff': 'Early Payoff Penalty',
  'pricing.earlyPayoffHelp': 'Minimum months before waiving prepayment penalty',
  // Eligibility Rules Section
  'pricing.eligibility': 'Eligibility Rules',
  'pricing.eligibilityDescription': 'Define hard stops and warnings for loan applications',
  'pricing.hardStop': 'Hard Stop',
  'pricing.hardStopHelp': 'Application will be rejected if condition fails',
  'pricing.warning': 'Warning',
  'pricing.warningHelp': 'Display warning but allow application to proceed',
  'pricing.ruleCondition': 'Condition',
  'pricing.ruleOperator': 'Operator',
  'pricing.ruleValue': 'Value',
  'pricing.ruleMessage': 'Message',
  'pricing.addRule': 'Add Rule',
  'pricing.removeRule': 'Remove',
  // Live Preview Section
  'pricing.preview': 'Rate Calculator Preview',
  'pricing.previewDescription': 'See how your pricing rule affects rate calculations',
  'pricing.previewLoanAmount': 'Loan Amount',
  'pricing.previewPropertyValue': 'Property Value',
  'pricing.previewFico': 'FICO Score',
  'pricing.previewExperience': 'Deals Completed',
  'pricing.previewPropertyType': 'Property Type',
  'pricing.previewIsRepeat': 'Repeat Borrower',
  'pricing.previewCalculate': 'Calculate Rate',
  'pricing.previewResult': 'Calculated Rate',
  'pricing.previewBreakdown': 'Rate Breakdown',
  'pricing.previewBaseRate': 'Base Rate',
  'pricing.previewFicoAdj': 'FICO Adjustment',
  'pricing.previewLtvAdj': 'LTV Adjustment',
  'pricing.previewExpAdj': 'Experience Adjustment',
  'pricing.previewPropAdj': 'Property Adjustment',
  'pricing.previewAmountAdj': 'Amount Adjustment',
  'pricing.previewSpecialAdj': 'Special Adjustments',
  'pricing.previewFinalRate': 'Final Rate',
  'pricing.previewEligible': 'Eligible',
  'pricing.previewIneligible': 'Ineligible',
  'pricing.previewWarnings': 'Warnings',
  // Property Types
  'pricing.propertyType.singleFamily': 'Single Family',
  'pricing.propertyType.multiFamily': 'Multi-Family (2-4 units)',
  'pricing.propertyType.condo': 'Condo',
  'pricing.propertyType.townhouse': 'Townhouse',
  'pricing.propertyType.commercial': 'Commercial',
  'pricing.propertyType.mixedUse': 'Mixed Use',
  'pricing.propertyType.land': 'Land',
  // Experience Levels
  'pricing.experience.firstTime': 'First-Time Investor',
  'pricing.experience.beginner': 'Beginner (1-2 deals)',
  'pricing.experience.intermediate': 'Intermediate (3-5 deals)',
  'pricing.experience.experienced': 'Experienced (6-10 deals)',
  'pricing.experience.expert': 'Expert (10+ deals)',
  // Validation Messages
  'pricing.validation.nameRequired': 'Rule name is required',
  'pricing.validation.baseRateRequired': 'Base rate is required',
  'pricing.validation.baseRateRange': 'Base rate must be between 0 and 100',
  'pricing.validation.minLoanRequired': 'Minimum loan amount is required',
  'pricing.validation.maxLoanRequired': 'Maximum loan amount is required',
  'pricing.validation.maxLTVRange': 'Max LTV must be between 0 and 1',
  'pricing.validation.tierOverlap': 'Tier ranges cannot overlap',
  // Success/Error Messages
  'pricing.saveSuccess': 'Pricing rule saved successfully',
  'pricing.saveError': 'Failed to save pricing rule',
  'pricing.deleteSuccess': 'Pricing rule deleted',
  'pricing.deleteError': 'Failed to delete pricing rule',
  // Table Column Headers
  'pricing.loanRange': 'Loan Range',
  'pricing.status': 'Status',
  // Preview Calculator
  'pricing.monthlyPaymentInterestOnly': 'Monthly Payment (Interest-Only)',
  'pricing.ltvLabel': 'LTV',
  'pricing.ltcLabel': 'LTC',
  'settings.pipelines': 'Pipelines',
  'settings.pipelinesDescription': 'Configure pipeline stages and workflows',
  'settings.workflows': 'Workflows',
  'settings.workflowsDescription': 'Automate actions with workflow rules',
  'settings.templates': 'Document Templates',
  'settings.templatesDescription': 'Manage document templates for proposals and agreements',
  'settings.enrichment': 'Enrichment',
  'settings.enrichmentDescription': 'Data enrichment integrations',
  'settings.connectedApps': 'Connected Apps',
  // Retired in favor of the unified `settings.integrations` entry (Issue #1344).
  // Kept for backwards-compat with any cached nav configs until a clean break.
  'settings.connectedWorkspaces': 'Connected Workspaces',

  // -- Slack connector tile + primer (business portal) --
  'integrations.connectors.slack.description':
    'Connect your workspace for the /loan-size slash command and inbound events',
  'integrations.connections.primer.title': 'How the Slack integration works',
  'integrations.connections.primer.body':
    "Connecting Slack takes one click — we redirect you to Slack, you pick a workspace you're an admin of, and Slack brings you back. No configuration, no app install from your side.",
  'integrations.connections.primer.whatYouGet.title': 'What your team gets',
  'integrations.connections.primer.whatYouGet.body':
    'Once connected, anyone in the workspace can run {{cmd}} to get an instant sizing without leaving Slack.',
  'integrations.connections.primer.whatYouGet.footnote':
    "Slack replies in-thread with the target loan, binding constraint, leverage metrics, and a link back to the full sizing in the business portal. Every sizing is saved to this business's sizing history — you'll see Slack as the surface.",
  'integrations.connections.primer.whoCanUse.title': 'Who can use it',
  'integrations.connections.primer.whoCanUse.body':
    'Anyone in the connected Slack workspace. Sizings are stamped with the Slack user id so you can trace who ran each one from the sizing history. Disconnecting a workspace stops {{cmd}} from it immediately — saved sizings stay put.',
  'integrations.connections.platformNotConfigured':
    "Slack isn't configured on this platform yet. Ask your platform admin to set {{vars}} on the integration service, then try again.",
  'integrations.connections.connected.success':
    '{{provider}} workspace connected — any member of that Slack workspace can now run {{cmd}}.',
  'integrations.connections.connected.successNamed':
    '{{name}} is connected — any member of that Slack workspace can now run {{cmd}}.',
  'integrations.connections.connected.error': "Couldn't finish connecting {{provider}}: {{message}}",
  'integrations.connections.workspaceCount': '{{count}} workspace connected',
  'integrations.connections.workspaceCount_other': '{{count}} workspaces connected',

  // -- Admin Integrations (Slack platform setup) page --
  // Renamed from admin.platformIntegrations.* — page moved from
  // /platform-integrations/slack to /integrations/slack and the
  // sidebar entries were unified.
  'admin.integrations.slack.restricted': 'Platform integration setup is restricted to Fiops platform admins.',
  'admin.integrations.slack.openSlack': 'Open api.slack.com',
  'admin.integrations.slack.infoBanner':
    'This is a one-time platform setup. After you build the Fiops Slack App and set {{vars}} on the integration service, every business on the platform can connect their own Slack workspace from the business portal with one click.',
  'admin.integrations.slack.manifest.title': 'Recommended: paste the Slack App manifest',
  'admin.integrations.slack.manifest.body':
    "At api.slack.com/apps, click Create New App → From a manifest and paste the JSON below. This preconfigures the slash command (with the right description and usage hint so end users see helpful autocomplete text in Slack), bot scopes, and OAuth redirect URL in a single step — you don't have to click through each setting by hand.",
  'admin.integrations.slack.manifest.copy': 'Copy manifest JSON',
  'admin.integrations.slack.manifest.copied': 'Copied',
  'admin.integrations.slack.manual.title': "Or, if you're configuring the app by hand",
  'admin.integrations.slack.manual.body':
    'These are the raw values the manifest sets — copy them individually if you prefer the Slack UI to the manifest flow.',
  'admin.integrations.slack.manual.slashCommand': 'Slash command URL',
  'admin.integrations.slack.manual.slashCommandHint':
    'In the Slack App config, under Slash Commands → /loan-size, set Request URL to the value above.',
  'admin.integrations.slack.manual.callback': 'OAuth redirect URL',
  'admin.integrations.slack.manual.callbackHint':
    'Under OAuth & Permissions → Redirect URLs, add the value above. This is where Slack sends users back after they authorize a workspace.',
  'admin.integrations.slack.icon.title': 'App icon',
  'admin.integrations.slack.icon.body':
    "Slack requires a 512×512 PNG and doesn't accept icons via manifest. After creating the app, upload this file under Basic Information → Display Information → App Icon. This is what users see next to bot messages in-channel and in Slack's app directory.",
  'admin.integrations.slack.icon.download': 'Download app icon (512×512 PNG)',
  'admin.integrations.slack.scopes.title': 'Required bot scopes',
  'admin.integrations.slack.scopes.body':
    "Add these under OAuth & Permissions → Scopes → Bot Token Scopes. Business admins will see these in Slack's consent screen when they connect their workspace.",
  'admin.integrations.slack.steps.title': 'Setup steps',
  'admin.integrations.slack.steps.manifest.openSlack':
    'Go to https://api.slack.com/apps and click "Create New App → From a manifest".',
  'admin.integrations.slack.steps.manifest.pasteManifest':
    'Pick the workspace you want to own the app (usually your internal workspace), then paste the manifest JSON shown below.',
  'admin.integrations.slack.steps.manifest.create':
    'Click "Create" — Slack will scaffold the slash command, scopes, and OAuth redirect for you.',
  'admin.integrations.slack.steps.manifest.uploadIcon':
    'Upload the Fiops app icon (downloadable below) under "Basic Information → Display Information → App Icon". Slack requires a 512×512 PNG.',
  'admin.integrations.slack.steps.manifest.copySecrets':
    'On the same "Basic Information" page, copy the Client ID, Client Secret, and Signing Secret.',
  'admin.integrations.slack.icon.alt': 'Fiops Slack app icon',
  'admin.integrations.slack.steps.secrets':
    "Copy the app's Client ID, Client Secret, and Signing Secret from the app's Basic Information page. Set them on the integration service as SLACK_CLIENT_ID, SLACK_CLIENT_SECRET, and SLACK_SIGNING_SECRET (via Secret Manager in production; .env.local in dev). The service refuses to boot in production if the signing secret is missing.",
  'admin.integrations.slack.steps.restart': 'Restart the integration service so it picks up the new env vars.',
  'admin.integrations.slack.steps.verify':
    "Verify by clicking Connect Slack as a business admin on any tenant's business portal Integrations page — it should redirect to Slack, show your app's consent screen, and bring you back with a new IntegrationConnection row keyed on the workspace's team.id.",
  'admin.integrations.slack.copy': 'Copy',
  'admin.integrations.slack.copied': 'Copied',

  // -- Admin Integrations picker (providers list) --
  'admin.integrations.viewMonitoring': 'View monitoring',
  'admin.integrations.status.configured': 'Configured',
  'admin.integrations.status.available': 'Available',
  'admin.integrations.status.comingSoon': 'Coming soon',
  'admin.integrations.actions.manage': 'Manage',
  'admin.integrations.actions.configure': 'Configure',
  'admin.integrations.providers.slack.description':
    'Build the Fiops Slack App so every business can connect their workspace and run /loan-size.',
  'admin.integrations.providers.sfrAnalytics.description':
    'Single-family rental enrichment — property comps, rent estimates, market signals.',
  'admin.integrations.providers.resend.description':
    'Transactional email delivery for invites, notifications, and signing flows.',
  'admin.integrations.providers.retell.description':
    'Voice AI for outbound calls, lead qualification, and follow-up scheduling.',
  'admin.integrations.providers.housecanary.description': 'Property AVM cross-validation for triage second-opinion.',
  // -- Admin Integrations: HouseCanary detail page (Issue #1629 Phase 2.5C) --
  'admin.integrations.actions.back': 'Back to integrations',
  'admin.integrations.housecanary.statusHeading': 'Status',
  'admin.integrations.housecanary.enabled': 'Enabled',
  'admin.integrations.housecanary.notConfigured': 'Not configured',
  'admin.integrations.housecanary.description':
    'HouseCanary provides a second-opinion AVM that runs in parallel with SFR Analytics during property triage. Cross-AVM agreement raises the ARV_CONFIDENCE factor; divergence flags the deal for underwriter review.',
  'admin.integrations.housecanary.toggleHelp':
    'To enable HouseCanary for a business, create a connector with type "housecanary" via the per-business integrations page. Status=CONNECTED enables the cross-AVM call in property triage.',
  'admin.integrations.housecanary.openBusinesses': 'Open per-business integrations',
  'settings.connectedAppsDescription': 'Manage third-party integrations',
  'settings.auditLogs': 'Audit Logs',
  'settings.auditLogsDescription': 'View activity and audit trail',
  'settings.permissions': 'Permissions',
  'settings.permissionsDescription': 'Manage user permissions and access',
  'settings.preferences': 'Preferences',
  'settings.preferencesDescription': 'Your personal preferences',
  'settings.myDevices': 'My Devices',
  'settings.myDevicesDescription': 'Manage your connected devices',
  'settings.gettingStarted': 'Getting Started',
  'settings.gettingStartedDescription': 'Onboarding guide and setup wizard',
  'settings.platform': 'Platform',
  'settings.platformDescription': 'Platform-wide settings',
  'settings.general': 'General',
  'settings.generalDescription': 'General platform settings',
  'settings.apiKeys': 'API Keys',
  'settings.apiKeysDescription': 'Manage API keys and tokens',
  'settings.webhooks': 'Webhooks',
  'settings.webhooksDescription': 'Configure webhook endpoints',
  // Email Connections - Issue #372
  'settings.emailConnections': 'Email Connections',
  'settings.emailConnectionsDescription': 'Connect your personal email accounts for outreach',
  'settings.emailConnections.title': 'Email Connections',
  'settings.emailConnections.description':
    'Connect your personal email accounts to send emails from your inbox for sequences and outreach.',
  'settings.emailConnections.infoMessage':
    'Email connections allow you to send personalized emails from your own inbox. Replies will appear in your email client and automatically unenroll leads from sequences.',
  'settings.emailConnections.gmailConnected': 'Connected as {{email}}',
  'settings.emailConnections.gmailNotConnected': 'Connect your Gmail account to send emails from your personal inbox.',
  'settings.emailConnections.connectGmail': 'Connect Gmail',
  'settings.emailConnections.connecting': 'Connecting...',
  'settings.emailConnections.disconnect': 'Disconnect',
  'settings.emailConnections.connectedEmail': 'Connected Email',
  'settings.emailConnections.usageTitle': 'What you can do with connected email',
  'settings.emailConnections.sequencesFeature': 'Sequences',
  'settings.emailConnections.sequencesDescription': 'Send automated drip campaigns from your inbox',
  'settings.emailConnections.replyDetectionFeature': 'Reply Detection',
  'settings.emailConnections.replyDetectionDescription': 'Auto-unenroll leads when they reply',
  'settings.emailConnections.threadingFeature': 'Threading',
  'settings.emailConnections.threadingDescription': 'Keep all emails in the same thread',

  // E-Signature Settings (Issue #400 - i18n audit)
  'settings.esign': 'E-Signature',
  'settings.esign.title': 'E-Signature Settings',
  'settings.esign.description': 'Configure e-signature templates and workflows',

  // ============================================================================
  // ENTITIES (singular forms for interpolation)
  // ============================================================================
  'entities.case': 'Loan',
  'entities.consumer': 'Borrower',
  'entities.partner': 'Partner',
  'entities.user': 'User',
  'entities.business': 'Business',
  'entities.realm': 'Realm',
  'entities.tenant': 'Tenant',
  'entities.document': 'Document',
  'entities.agent': 'Agent',
  'entities.model': 'Model',
  'entities.lead': 'Lead',
  'entities.leads': 'Leads',
  'entities.termSheet': 'Term Sheet',
  'entities.pipeline': 'Pipeline',
  'entities.template': 'Template',
  'entities.viewRecords': 'View Records',
  // @see PR #263 - Added missing terminology override keys
  'entities.loan': 'Loan',
  'entities.loans': 'Loans',
  'entities.property': 'Property',
  'entities.properties': 'Properties',
  'entities.branch': 'Branch',
  'entities.branches': 'Branches',
  'entities.cases': 'Loans',
  'entities.consumers': 'Borrowers',
  'entities.partners': 'Partners',
  'entities.documents': 'Documents',
  'entities.borrowingEntity': 'Borrowing Entity',
  'entities.borrowingEntities': 'Borrowing Entities',
  'entities.guarantor': 'Guarantor',
  'entities.guarantors': 'Guarantors',
  'entities.team': 'Team',
  'entities.teams': 'Teams',
  'entities.searchPlaceholder': 'Search entities...',
  'entities.newEntity': 'New Entity',
  'entities.empty.title': 'No entities found',
  'entities.empty.description': 'Create your first entity to get started',
  'entities.empty.createEntity': 'Create Entity',

  // ============================================================================
  // ENTITY DETAIL PAGE — Tabs (Fields / Relationships / Settings) — Issue #1653
  // ============================================================================
  'entities.tab.fields': 'Fields',
  'entities.tab.relationships': 'Relationships',
  'entities.tab.settings': 'Settings',
  'entities.detail.fieldsCount': 'Fields',
  'entities.detail.recordsCount': 'Records',
  'entities.detail.relationshipsCount': 'Relationships',

  // Fields tab
  'entities.fields.title': 'Fields',
  'entities.fields.addButton': 'Add Field',
  'entities.fields.showArchived': 'Show archived',
  'entities.fields.archivedBadge': 'Archived',
  'entities.fields.empty': 'No fields yet — click Add Field to define one.',
  'entities.fields.deleteConfirmTitle': 'Delete field?',
  'entities.fields.deleteConfirmBody':
    'This entity has no records, so the field can be deleted permanently. This cannot be undone.',
  'entities.fields.archiveConfirmTitle': 'Archive field?',
  'entities.fields.archiveConfirmBody':
    'This entity has records that may reference this field, so we will archive it instead of deleting. Archived fields stay hidden but their data is preserved.',
  'entities.fields.toast.created': 'Field created',
  'entities.fields.toast.updated': 'Field saved',
  'entities.fields.toast.deleted': 'Field deleted',
  'entities.fields.toast.archived': 'Field archived',
  'entities.fields.toast.reordered': 'Fields reordered',
  'entities.fields.toast.error': 'Failed to save: {message}',

  // Custom fields section (Issue #1654 — inline custom fields on loan/borrower/application/borrowing_entity detail pages)
  'entities.customFields.sectionTitle': 'Custom fields',
  'entities.customFields.sectionTitleWithCount': 'Custom fields ({{count}})',
  'entities.customFields.tabLabel': 'Custom',
  'entities.customFields.addField': 'Add field',
  'entities.customFields.addFieldEmpty': 'Add your first custom field',
  'entities.customFields.emptyAdmin':
    'Add fields like "Origination Source" or "Risk Tier" — they show up on every {{pluralLabel}} so you can track what matters to your team.',
  'entities.customFields.emptyViewer': 'No custom fields yet.',
  'entities.customFields.addFieldConfirmTitle': 'Add field to all {{pluralLabel}}?',
  'entities.customFields.addFieldConfirmBody':
    'Adding "{{fieldLabel}}" makes it available on every {{pluralLabel}} (empty until filled in). {{recordCount}} {{pluralLabel}} already have custom-field data.',
  'entities.customFields.addFieldConfirmCta': 'Add field',
  'entities.customFields.archiveField': 'Archive',
  'entities.customFields.deleteFieldBlocked': 'Has data — archive instead',
  'entities.customFields.recordCount': '{{count}} {{pluralLabel}} with custom-field data',
  'entities.customFields.toast.saved': 'Saved',
  'entities.customFields.toast.error': 'Could not save: {{message}}',
  'entities.customFields.placeholder': 'Empty',

  // Field editor (SplitPanel)
  'entities.fieldEditor.createTitle': 'New field',
  'entities.fieldEditor.editTitle': 'Edit field',
  'entities.fieldEditor.sectionBasic': 'Basic',
  'entities.fieldEditor.sectionType': 'Type configuration',
  'entities.fieldEditor.sectionValidation': 'Validation',
  'entities.fieldEditor.sectionDefault': 'Default value',
  'entities.fieldEditor.sectionDisplay': 'Display',
  'entities.fieldEditor.fieldName': 'API name',
  'entities.fieldEditor.fieldNameHelper':
    'Snake-case identifier used by integrations and AI agents. Cannot be changed once set.',
  'entities.fieldEditor.fieldNameInvalid': 'Use lowercase letters, numbers, and underscores only.',
  'entities.fieldEditor.fieldLabel': 'Label',
  'entities.fieldEditor.fieldDescription': 'Description',
  'entities.fieldEditor.fieldType': 'Field type',
  'entities.fieldEditor.required': 'Required',
  'entities.fieldEditor.unique': 'Unique',
  'entities.fieldEditor.placeholder': 'Placeholder',
  'entities.fieldEditor.helpText': 'Help text',
  'entities.fieldEditor.section': 'Section',
  'entities.fieldEditor.showInList': 'Show in list view',
  'entities.fieldEditor.showInCreate': 'Show in create form',
  'entities.fieldEditor.showInDetail': 'Show in detail view',
  'entities.fieldEditor.showInSearch': 'Searchable',
  'entities.fieldEditor.options': 'Options',
  'entities.fieldEditor.addOption': 'Add option',
  'entities.fieldEditor.optionValue': 'Value',
  'entities.fieldEditor.optionLabel': 'Label',
  'entities.fieldEditor.optionColor': 'Color',
  'entities.fieldEditor.lookupEntity': 'Lookup entity',
  'entities.fieldEditor.lookupDisplayField': 'Display field',
  'entities.fieldEditor.lookupEntityHelper': 'Records of this field will reference rows in the chosen entity.',
  'entities.fieldEditor.minLength': 'Min length',
  'entities.fieldEditor.maxLength': 'Max length',
  'entities.fieldEditor.pattern': 'Regex pattern',
  'entities.fieldEditor.minValue': 'Min value',
  'entities.fieldEditor.maxValue': 'Max value',
  'entities.fieldEditor.formula': 'Formula',
  'entities.fieldEditor.formulaHelper': 'Expression evaluated when the record is saved.',
  'entities.fieldEditor.advancedNotice': 'Advanced configuration for this field type is not yet available in the UI.',
  'entities.fieldEditor.defaultValue': 'Default value',
  'entities.fieldEditor.validateLabel': 'Label is required',
  'entities.fieldEditor.validateName': 'API name is required',
  'entities.fieldEditor.defaultValueInvalidNumber': 'Default value must be a valid number for this field type.',
  'entities.fieldEditor.defaultValueInvalidBoolean': 'Default value must be "true" or "false" for boolean fields.',
  'entities.fieldEditor.defaultValueInvalidJson':
    'Default value must be valid JSON for JSON fields (e.g. {"a":1} or [1,2]).',
  'entities.fieldEditor.minValueInvalid': 'Min value must be a finite number.',
  'entities.fieldEditor.maxValueInvalid': 'Max value must be a finite number.',
  'entities.fieldEditor.minLengthInvalid': 'Min length must be a non-negative whole number.',
  'entities.fieldEditor.maxLengthInvalid': 'Max length must be a non-negative whole number.',
  'entities.fieldEditor.deleteOption': 'Remove option',
  'entities.fieldEditor.deleteRelationship': 'Delete relationship',
  'entities.fields.editAction': 'Edit field',
  'entities.fields.archiveAction': 'Archive field',
  'entities.fields.deleteAction': 'Delete field',

  // Relationships tab
  'entities.relationships.title': 'Relationships',
  'entities.relationships.subtitle': 'Configure relationships with other entities',
  'entities.relationships.addButton': 'Add Relationship',
  'entities.relationships.empty': 'No relationships defined yet.',
  'entities.relationships.editorTitle': 'New relationship',
  'entities.relationships.fieldName': 'API name',
  'entities.relationships.fieldFromEntity': 'From entity',
  'entities.relationships.fieldFromFieldName': 'From field name',
  'entities.relationships.fieldFromLabel': 'From label',
  'entities.relationships.fieldToEntity': 'To entity',
  'entities.relationships.fieldToFieldName': 'To field name',
  'entities.relationships.fieldToLabel': 'To label',
  'entities.relationships.fieldRelationshipType': 'Relationship type',
  'entities.relationships.fieldCascadeDelete': 'Cascade delete',
  'entities.relationships.fieldCascadeDeleteHelper':
    'When the source record is deleted, also delete the linked target records.',
  'entities.relationships.fieldRequired': 'Required',
  'entities.relationships.deleteConfirmTitle': 'Delete relationship?',
  'entities.relationships.deleteConfirmBody': 'Existing links between records will be removed. This cannot be undone.',
  'entities.relationships.toast.created': 'Relationship created',
  'entities.relationships.toast.deleted': 'Relationship deleted',
  'entities.relationships.toast.error': 'Failed to save: {message}',
  'entities.relationships.nameInvalid': 'API name must use lowercase letters, numbers, and underscores only.',
  'entities.relationships.toEntityRequired': 'Please select the entity this relationship points to.',
  'entities.relationships.type.ONE_TO_ONE': 'One-to-one',
  'entities.relationships.type.ONE_TO_MANY': 'One-to-many',
  'entities.relationships.type.MANY_TO_MANY': 'Many-to-many',

  // Settings tab
  'entities.settings.title': 'Entity settings',
  'entities.settings.systemReadOnly':
    'This is a system entity. Settings are managed by the platform and cannot be edited here.',
  'entities.settings.sectionDisplay': 'Display',
  'entities.settings.sectionBehavior': 'Behavior',
  'entities.settings.sectionPipeline': 'Pipeline',
  'entities.settings.sectionDangerZone': 'Danger zone',
  'entities.settings.fieldLabel': 'Label',
  'entities.settings.fieldPluralLabel': 'Plural label',
  'entities.settings.fieldDescription': 'Description',
  'entities.settings.fieldIcon': 'Icon',
  'entities.settings.fieldIconHelper': 'Material icon name (e.g. "ViewModule").',
  'entities.settings.fieldColor': 'Color',
  'entities.settings.fieldPrimaryField': 'Primary field',
  'entities.settings.fieldSecondaryField': 'Secondary field',
  'entities.settings.fieldAllowCreate': 'Allow create',
  'entities.settings.fieldAllowEdit': 'Allow edit',
  'entities.settings.fieldAllowDelete': 'Allow delete',
  'entities.settings.fieldAllowImport': 'Allow import',
  'entities.settings.fieldAllowExport': 'Allow export',
  'entities.settings.fieldPipelineEnabled': 'Pipeline enabled',
  'entities.settings.fieldDefaultPipelineId': 'Default pipeline ID',
  'entities.settings.fieldIsActive': 'Active',
  'entities.settings.deleteEntityButton': 'Delete entity',
  'entities.settings.deleteDisabledRecords': 'Cannot delete — entity has records. Archive it instead.',
  'entities.settings.deleteDisabledSystem': 'System entities cannot be deleted.',
  'entities.settings.deleteConfirmTitle': 'Delete entity?',
  'entities.settings.deleteConfirmBody':
    'This deletes the entity definition and all its fields. The action cannot be undone.',
  'entities.settings.toast.updated': 'Entity saved',
  'entities.settings.toast.deleted': 'Entity deleted',
  'entities.settings.toast.error': 'Failed to save: {message}',

  // Field-type display labels (shared across the editor and field list chips)
  'enums.FieldType.TEXT': 'Text',
  'enums.FieldType.TEXTAREA': 'Long text',
  'enums.FieldType.RICH_TEXT': 'Rich text',
  'enums.FieldType.NUMBER': 'Number',
  'enums.FieldType.CURRENCY': 'Currency',
  'enums.FieldType.PERCENTAGE': 'Percentage',
  'enums.FieldType.DATE': 'Date',
  'enums.FieldType.DATETIME': 'Date & time',
  'enums.FieldType.TIME': 'Time',
  'enums.FieldType.BOOLEAN': 'Checkbox',
  'enums.FieldType.SELECT': 'Dropdown',
  'enums.FieldType.MULTI_SELECT': 'Multi-select',
  'enums.FieldType.LOOKUP': 'Lookup',
  'enums.FieldType.MULTI_LOOKUP': 'Multi-lookup',
  'enums.FieldType.FILE': 'File',
  'enums.FieldType.IMAGE': 'Image',
  'enums.FieldType.PHONE': 'Phone',
  'enums.FieldType.EMAIL': 'Email',
  'enums.FieldType.URL': 'URL',
  'enums.FieldType.ADDRESS': 'Address',
  'enums.FieldType.JSON': 'JSON',
  'enums.FieldType.CALCULATED': 'Calculated',
  'enums.FieldType.ROLLUP': 'Rollup',
  'enums.FieldType.AUTO_NUMBER': 'Auto number',
  'enums.FieldType.RATING': 'Rating',
  'enums.FieldType.SIGNATURE': 'Signature',

  // ============================================================================
  // BORROWING ENTITIES (Issue #636)
  // ============================================================================
  'borrowingEntities.title': 'Borrowing Entities',
  'borrowingEntities.createTitle': 'Create Borrowing Entity',
  'borrowingEntities.editTitle': 'Edit Borrowing Entity',
  'borrowingEntities.detailTitle': 'Entity Details',
  'borrowingEntities.emptyState': 'No borrowing entities yet',
  'borrowingEntities.emptyDescription':
    'Create your first borrowing entity to start tracking multi-entity loan structures.',
  // Entity fields
  'borrowingEntities.legalName': 'Legal Name',
  'borrowingEntities.dbaName': 'DBA / Trade Name',
  'borrowingEntities.entityType': 'Entity Type',
  'borrowingEntities.ein': 'EIN',
  'borrowingEntities.stateOfFormation': 'State of Formation',
  'borrowingEntities.formationDate': 'Formation Date',
  'borrowingEntities.registeredAgent': 'Registered Agent',
  'borrowingEntities.registeredAddress': 'Registered Address',
  'borrowingEntities.status': 'Status',
  'borrowingEntities.activeLoans': 'Active Loans',
  'borrowingEntities.activeCasesCount': '{{count}} loans',
  'borrowingEntities.viewCases': 'View Loans',
  'borrowingEntities.totalOwners': 'Owners',
  // Entity types
  'borrowingEntities.type.individual': 'Individual',
  'borrowingEntities.type.llc': 'LLC',
  'borrowingEntities.type.corporation': 'Corporation',
  'borrowingEntities.type.trust': 'Trust',
  'borrowingEntities.type.partnership': 'Partnership',
  // Entity statuses
  'borrowingEntities.status.active': 'Active',
  'borrowingEntities.status.inactive': 'Inactive',
  'borrowingEntities.status.dissolved': 'Dissolved',
  // Ownership
  'borrowingEntities.ownership': 'Ownership',
  'borrowingEntities.ownershipTitle': 'Entity Ownership',
  'borrowingEntities.addOwner': 'Add Owner',
  'borrowingEntities.removeOwner': 'Remove Owner',
  'borrowingEntities.ownerName': 'Owner Name',
  'borrowingEntities.ownerType': 'Owner Type',
  'borrowingEntities.ownerType.individual': 'Individual',
  'borrowingEntities.ownerType.entity': 'Entity',
  'borrowingEntities.ownershipPercentage': 'Ownership %',
  'borrowingEntities.ownerRole': 'Role',
  'borrowingEntities.signingAuthority': 'Signing Authority',
  'borrowingEntities.effectiveDate': 'Effective Date',
  'borrowingEntities.totalOwnership': 'Total Ownership',
  'borrowingEntities.ownershipWarning': 'Total ownership exceeds 100%',
  // Owner roles
  'borrowingEntities.role.member': 'Member',
  'borrowingEntities.role.managing_member': 'Managing Member',
  'borrowingEntities.role.partner': 'Partner',
  'borrowingEntities.role.general_partner': 'General Partner',
  'borrowingEntities.role.limited_partner': 'Limited Partner',
  'borrowingEntities.role.trustee': 'Trustee',
  'borrowingEntities.role.beneficiary': 'Beneficiary',
  'borrowingEntities.role.shareholder': 'Shareholder',
  'borrowingEntities.role.officer': 'Officer',

  'borrowingEntities.ownershipTree': 'Ownership Tree',
  'borrowingEntities.ownershipTreeDescription': 'Visual hierarchy of entity ownership structure',
  'borrowingEntities.linkedGuarantees': 'Linked Guarantees',
  'borrowingEntities.linkedGuaranteesDescription': 'Guarantees associated with this entity',
  'borrowingEntities.noOwners': 'No owners recorded',
  'borrowingEntities.noGuarantees': 'No guarantees linked to this entity',
  'borrowingEntities.ownerEmail': 'Email',
  'borrowingEntities.numberOfOwners': 'Number of Owners',
  // Consumer entity management (Issue #809)
  'borrowingEntities.createSuccess': 'Entity created successfully',
  'borrowingEntities.updateSuccess': 'Entity updated successfully',
  'borrowingEntities.deleteSuccess': 'Entity deleted successfully',
  'borrowingEntities.deleteConfirm': 'Are you sure you want to delete this entity? This action cannot be undone.',
  'borrowingEntities.deleteConfirmTitle': 'Delete Entity',
  'borrowingEntities.ownerAddSuccess': 'Owner added successfully',
  'borrowingEntities.ownerUpdateSuccess': 'Owner updated successfully',
  'borrowingEntities.ownerRemoveSuccess': 'Owner removed successfully',
  'borrowingEntities.ownerRemoveConfirm': 'Are you sure you want to remove this owner?',
  'borrowingEntities.ownerRemoveConfirmTitle': 'Remove Owner',
  'borrowingEntities.linkToApplication': 'Link to Application',
  'borrowingEntities.linkToApplicationDescription': 'Associate this entity with one of your active loan applications.',
  'borrowingEntities.noActiveApplications': 'No active applications available to link.',
  'borrowingEntities.linkedToApplication': 'Linked to Application',
  'borrowingEntities.entityLinkedSuccess': 'Entity linked to application successfully',
  'borrowingEntities.einPlaceholder': 'XX-XXXXXXX',

  // Entity Portfolio Hub (Issue #1196)
  'entityHub.title': 'Entity Portfolio',
  'entityHub.description': 'Complete view of loans, guarantees, and ownership for this entity',
  'entityHub.tabs.loans': 'Loans',
  'entityHub.tabs.guarantees': 'Guarantees',
  'entityHub.tabs.ownership': 'Ownership',
  'entityHub.tabs.financials': 'Financials',
  'entityHub.tabs.documents': 'Documents',
  'entityHub.tabs.activity': 'Activity',
  'entityHub.tabs.applications': 'Applications',
  'entityHub.applications.empty': 'No application data available',
  'entityHub.applications.emptyDescription':
    'Application data will appear when intake submissions are linked to this entity.',
  'entityHub.kpi.activeLoans': 'Active Loans',
  'entityHub.kpi.totalLoans': 'Total Loans',
  'entityHub.kpi.activeGuarantees': 'Active Guarantees',
  'entityHub.kpi.totalGuarantees': 'Total Guarantees',
  'entityHub.kpi.totalExposure': 'Total Guarantee Exposure',
  'entityHub.kpi.ownerCount': 'Owners',
  'entityHub.kpi.outstandingBalance': 'Outstanding Balance',
  'entityHub.kpi.riskRating': 'Risk Rating',
  'entityHub.loans.caseNumber': 'Loan #',
  'entityHub.loans.title': 'Loan Title',
  'entityHub.loans.role': 'Role',
  'entityHub.loans.priority': 'Priority',
  'entityHub.loans.status': 'Status',
  'entityHub.loans.dueDate': 'Due Date',
  'entityHub.loans.committedAmount': 'Committed Amount',
  'entityHub.loans.outstandingBalance': 'Outstanding Balance',
  'entityHub.loans.maturityDate': 'Maturity Date',
  'entityHub.loans.empty': 'No loans associated with this entity',
  'entityHub.loans.emptyDescription': 'When this entity is added as a borrower on a loan, it will appear here.',
  'entityHub.loans.role.primary_borrower': 'Primary Borrower',
  'entityHub.loans.role.co_borrower': 'Co-Borrower',
  'entityHub.loans.role.sponsor': 'Sponsor',
  'entityHub.loans.addedDate': 'Added Date',
  'entityHub.loans.stageUnknown': 'Stage pending',
  // Borrower Loans tab v3 (#1749) — card-row list of lifetime loans for a borrowing entity.
  'borrowers.loansTab.listAriaLabel': 'Lifetime loans',
  'borrowers.loansTab.rowAriaLabel': 'Loan {{loanNumber}}: {{title}}',
  'borrowers.loansTab.stageAriaLabel': 'Current stage: {{stage}}',
  'borrowers.loansTab.outstanding': '{{amount}} outstanding',
  'borrowers.loansTab.maturesOn': 'matures {{date}}',
  'borrowers.loansTab.addedDate': 'Added to loan',
  'borrowers.loansTab.aggregate.count': 'Loans',
  'borrowers.loansTab.aggregate.committed': 'Total Committed',
  'borrowers.loansTab.aggregate.outstanding': 'Total Outstanding',
  'entityHub.guarantees.caseNumber': 'Loan #',
  'entityHub.guarantees.type': 'Guarantee Type',
  'entityHub.guarantees.amount': 'Amount',
  'entityHub.guarantees.status': 'Status',
  'entityHub.guarantees.expirationDate': 'Expiration',
  'entityHub.guarantees.percentage': 'Percentage',
  'entityHub.guarantees.effectiveDate': 'Effective Date',
  'entityHub.guarantees.guarantorName': 'Guarantor',
  'entityHub.guarantees.borrowerName': 'Borrower',
  'entityHub.guarantees.empty': 'No guarantees for this entity',
  'entityHub.guarantees.emptyDescription': 'When this entity guarantees a loan, it will appear here.',
  'entityHub.ownership.empty': 'No ownership records',
  'entityHub.ownership.emptyDescription': 'Add owners to establish the ownership structure for this entity.',
  'entityHub.ownership.ownedBy': 'Owned By',
  'entityHub.ownership.owns': 'Entities Owned',
  'entityHub.ownership.ownsEmpty': 'This entity does not own any other entities.',
  'entityHub.ownership.percentage': 'Ownership %',
  'entityHub.ownership.role': 'Role',
  'entityHub.ownership.signingAuth': 'Signing Authority',
  'entityHub.ownership.totalOwnership': 'Total Ownership',
  'entityHub.ownership.ownershipWarning': 'Total ownership exceeds 100%. Please review the ownership structure.',
  'entityHub.ownership.individual': 'Individual',
  'entityHub.ownership.entity': 'Entity',
  'entityHub.ownership.unknownOwner': 'Unknown Owner',
  'entityHub.ownership.effectiveDate': 'Effective Date',
  'entityHub.ownership.endDate': 'End Date',
  'entityHub.ownership.email': 'Email',
  'entityHub.ownership.yes': 'Yes',
  'entityHub.ownership.no': 'No',
  'entityHub.ownership.activeLoanCount': 'Active Loans',
  'entityHub.ownership.entityType': 'Type',
  'entityHub.ownership.status': 'Status',
  'entityHub.ownership.unknownEntity': 'Unknown Entity',
  'entityHub.ownership.viewCards': 'Cards',
  'entityHub.ownership.viewTree': 'Tree',
  'entityHub.ownership.viewGraph': 'Graph',
  'entityHub.ownership.loanParticipation': 'Loan Participation',
  'entityHub.ownership.guaranteeObligations': 'Guarantee Obligations',
  'entityHub.comingSoon': 'Coming Soon',
  'entityHub.comingSoonDescription': 'This feature is under development.',

  // Financials Tab (Issue #1196)
  'entityHub.financials.summary': 'Exposure Summary',
  'entityHub.financials.totalCommitted': 'Total Committed',
  'entityHub.financials.totalOutstanding': 'Total Outstanding',
  'entityHub.financials.totalGuaranteeExposure': 'Guarantee Exposure',
  'entityHub.financials.netExposure': 'Net Exposure',
  'entityHub.financials.loanBreakdown': 'Loan Breakdown',
  'entityHub.financials.guaranteeBreakdown': 'Guarantee Breakdown',
  'entityHub.financials.allocation': 'Commitment Allocation',
  'entityHub.financials.empty': 'No financial data available',
  'entityHub.financials.emptyDescription': 'Financial data will appear when this entity is linked to loans.',
  'entityHub.financials.caseNumber': 'Loan #',
  'entityHub.financials.role': 'Role',
  'entityHub.financials.committed': 'Committed',
  'entityHub.financials.outstanding': 'Outstanding',
  'entityHub.financials.maturity': 'Maturity',
  'entityHub.financials.status': 'Status',
  'entityHub.financials.type': 'Type',
  'entityHub.financials.amount': 'Amount',
  'entityHub.financials.expiration': 'Expiration',

  // Guarantor Analysis Tab (Issue #1197)
  'entityHub.tabs.guarantorAnalysis': 'Guarantor Analysis',
  'entityHub.guarantorAnalysis.empty': 'No guarantor data for this entity',
  'entityHub.guarantorAnalysis.emptyDescription':
    'Guarantor analysis will appear when this entity has guarantee obligations.',
  'entityHub.guarantorAnalysis.financialSnapshot': 'Financial Snapshot',
  'entityHub.guarantorAnalysis.exposure': 'Total Exposure',
  'entityHub.guarantorAnalysis.netWorth': 'Net Worth',
  'entityHub.guarantorAnalysis.liquidity': 'Liquidity',
  'entityHub.guarantorAnalysis.exposureRatio': 'Exposure / NW Ratio',
  'entityHub.guarantorAnalysis.expirationTimeline': 'Active Guarantees & Expiration',
  'entityHub.guarantorAnalysis.daysRemaining': 'Days Remaining',
  'entityHub.guarantorAnalysis.history': 'Guarantee History',
  'entityHub.guarantorAnalysis.caseNumber': 'Loan #',
  'entityHub.guarantorAnalysis.type': 'Type',
  'entityHub.guarantorAnalysis.amount': 'Amount',
  'entityHub.guarantorAnalysis.status': 'Status',
  'entityHub.guarantorAnalysis.effective': 'Effective',
  'entityHub.guarantorAnalysis.expiration': 'Expiration',
  'entityHub.guarantorAnalysis.releasedDate': 'Released Date',
  'entityHub.guarantorAnalysis.creditScore': 'Credit Score',
  'entityHub.guarantorAnalysis.exposureVsNetWorth': 'Exposure vs Net Worth',
  'entityHub.guarantorAnalysis.concentration': 'Concentration by Guarantee Type',

  // Guarantor Alert Messages (Issue #1197)
  'guarantorAlert.exposure_over_90':
    'Exposure exceeds 90% of net worth — committee approval required for new guarantees',
  'guarantorAlert.exposure_over_80': 'Exposure exceeds 80% of net worth',
  'guarantorAlert.expiring_soon': '{{count}} guarantee(s) expiring within 90 days',
  'guarantorAlert.expired': '{{count}} guarantee(s) have expired',
  'guarantorAlert.annual_review': 'Annual financial review recommended for active guarantor',

  // Documents Tab (Issue #1196)
  'entityHub.documents.title': 'Entity Documents',
  'entityHub.documents.caseDocuments': 'Loan Documents',
  'entityHub.documents.name': 'Document Name',
  'entityHub.documents.type': 'Type',
  'entityHub.documents.status': 'Status',
  'entityHub.documents.size': 'Size',
  'entityHub.documents.uploaded': 'Uploaded',
  'entityHub.documents.case': 'Loan',
  'entityHub.documents.empty': 'No documents found',
  'entityHub.documents.emptyDescription': 'Documents attached to this entity or its linked loans will appear here.',
  'entityHub.documents.truncatedNotice':
    'Showing documents for {{shown}} of {{total}} linked cases. Visit individual loans for complete document lists.',

  // Entity Hub — Documents tab v3 list shell (#1749, #1855)
  'entityHub.documentsTab.title': 'Documents',
  'entityHub.documentsTab.onFile': '{{count}} on file',
  'entityHub.documentsTab.expiringBanner': '{{count}} document(s) expiring soon — review and request fresh copies.',
  'entityHub.documentsTab.astorVerify': 'Astor verify',
  'entityHub.documentsTab.upload': 'Upload',
  'entityHub.documentsTab.rowActions': 'Row actions',
  'entityHub.documentsTab.column.document': 'Document',
  'entityHub.documentsTab.column.category': 'Category',
  'entityHub.documentsTab.column.status': 'Status',
  'entityHub.documentsTab.column.uploaded': 'Uploaded',
  'entityHub.documentsTab.column.expires': 'Expires',

  // Application Tab truncation notice
  'entityHub.applications.truncatedNotice': 'Showing application data for {{shown}} of {{total}} linked loans.',

  // Activity Tab (Issue #1196)
  'entityHub.activity.title': 'Entity Activity',
  'entityHub.activity.empty': 'No activity recorded for this entity',

  'entityHub.viewCase': 'View Loan',
  'entityHub.backToEntities': 'Back to Entities',

  // ============================================================================
  // ENTITY DETAIL v3 (Issue #1769 — v3 rail pattern · borrowing-entity detail)
  // ----------------------------------------------------------------------------
  // Keys for the borrowing-entity detail page rewrite that mirrors
  // `docs/ux-v3-1769/project/pages/details.jsx :: EntityDetail`. Existing
  // `entityHub.*` keys above remain in place — the v3 page additively uses
  // `entityDetail.*` for header copy, KPI strip, Details/Loans/Ownership/
  // Financials tab labels, UBO sub-card, right-rail labels, and Astor inline
  // notes. (Coordinated with sibling v3 rewrites of applications/partners
  // detail pages — keep these contiguous in this block.)
  // ============================================================================
  'entityDetail.header.subtitle': 'Complete view of loans, guarantees, and ownership for this entity',
  'entityDetail.header.einPrefix': 'EIN',
  'entityDetail.header.formedYear': 'Formed {{year}}',
  'entityDetail.actions.edit': 'Edit',
  'entityDetail.actions.export': 'Export',
  'entityDetail.actions.newLoan': 'New loan',
  'entityDetail.actions.backToEntities': 'Back to entities',
  'entityDetail.actions.backToBorrower': 'Back to borrower',
  'entityDetail.status.active': 'active',
  'entityDetail.status.inactive': 'inactive',
  'entityDetail.status.dissolved': 'dissolved',
  // KPI strip — 5 StatCard tiles
  'entityDetail.kpi.totalGuaranteeExposure': 'Total guarantee expos.',
  'entityDetail.kpi.totalGuaranteeExposureSub': 'across {{count}} loans',
  'entityDetail.kpi.outstandingBalance': 'Outstanding balance',
  'entityDetail.kpi.outstandingBalanceSub': '{{count}} active loans',
  'entityDetail.kpi.activeLoans': 'Active loans',
  'entityDetail.kpi.activeLoansSub': 'of {{count}} lifetime',
  'entityDetail.kpi.activeGuarantees': 'Active guarantees',
  'entityDetail.kpi.activeGuaranteesSub': 'primary + spouse',
  'entityDetail.kpi.riskRating': 'Risk rating',
  'entityDetail.kpi.riskRatingSub': 'Astor model · v2',
  // Tabs
  'entityDetail.tabs.details': 'Details',
  'entityDetail.tabs.applications': 'Applications',
  'entityDetail.tabs.loans': 'Loans',
  'entityDetail.tabs.guarantees': 'Guarantees',
  'entityDetail.tabs.guarantorAnalysis': 'Guarantor Analysis',
  'entityDetail.tabs.ownership': 'Ownership',
  'entityDetail.tabs.financials': 'Financials',
  'entityDetail.tabs.documents': 'Documents',
  // Details tab — Legal & formation
  'entityDetail.details.legalFormation.title': 'Legal & formation',
  'entityDetail.details.legalName': 'Legal name',
  'entityDetail.details.entityType': 'Entity type',
  'entityDetail.details.ein': 'EIN',
  'entityDetail.details.stateOfFormation': 'State of formation',
  'entityDetail.details.dba': 'DBA / Trade name',
  'entityDetail.details.formationDate': 'Formation date',
  'entityDetail.details.registeredAgent': 'Registered agent',
  'entityDetail.details.goodStanding': 'Good standing',
  'entityDetail.details.goodStandingVerified': 'verified',
  'entityDetail.details.goodStandingPending': 'pending verification',
  // Details tab — Risk & exposure
  'entityDetail.details.riskExposure.title': 'Risk & exposure',
  'entityDetail.details.riskRating': 'Risk rating',
  'entityDetail.details.astorConfidence': 'Astor confidence',
  'entityDetail.details.activeLoans': 'Active loans',
  'entityDetail.details.lifetimeLoans': 'Lifetime loans',
  'entityDetail.details.totalApproved': 'Total approved',
  'entityDetail.details.outstanding': 'Outstanding',
  'entityDetail.details.astorNote.prefix': 'Astor:',
  'entityDetail.details.astorNote.body':
    'Entity exposure is at {{current}} of single-entity cap. New deal would push to {{projected}}.',
  'entityDetail.details.astorNote.unavailable': 'Astor exposure cap projection is not yet wired (#1776).',
  'entityDetail.details.confidence.high': 'high',
  'entityDetail.details.confidence.medium': 'medium',
  'entityDetail.details.confidence.low': 'low',
  'entityDetail.details.confidence.unknown': 'pending',
  // Loans tab — table columns + states
  'entityDetail.loans.col.loanNumber': 'Loan #',
  'entityDetail.loans.col.property': 'Property',
  'entityDetail.loans.col.amount': 'Amount',
  'entityDetail.loans.col.stage': 'Stage',
  'entityDetail.loans.col.originated': 'Originated',
  'entityDetail.loans.col.status': 'Status',
  'entityDetail.loans.empty': 'No loans linked to this entity yet',
  'entityDetail.loans.emptyDescription': 'When this entity is added as a borrower on a loan, it will appear here.',
  'entityDetail.loans.statusActive': 'active',
  'entityDetail.loans.statusClosed': 'closed',
  'entityDetail.loans.untitledProperty': '(no property on file)',
  // Ownership tab — Ownership structure (left) + UBO (right)
  'entityDetail.ownership.structure.title': 'Ownership structure',
  'entityDetail.ownership.structure.totalAccounted': 'Total accounted',
  'entityDetail.ownership.structure.addOwner': 'Add owner',
  'entityDetail.ownership.structure.empty': 'No owners on file',
  'entityDetail.ownership.structure.emptyDescription':
    'Add owners to establish the ownership structure for this entity.',
  'entityDetail.ownership.structure.ownership': 'ownership',
  'entityDetail.ownership.ubo.title': 'Beneficial owners (UBO ≥ 25%)',
  'entityDetail.ownership.ubo.cipCleared': 'CIP cleared',
  'entityDetail.ownership.ubo.cipPending': 'CIP pending',
  'entityDetail.ownership.ubo.cipFlagged': 'CIP flagged',
  'entityDetail.ownership.ubo.ofac': 'OFAC',
  'entityDetail.ownership.ubo.pep': 'PEP screen',
  'entityDetail.ownership.ubo.adverseMedia': 'Adverse media',
  'entityDetail.ownership.ubo.lastRefresh': 'Last refresh',
  'entityDetail.ownership.ubo.clear': 'clear',
  'entityDetail.ownership.ubo.pending': 'pending',
  'entityDetail.ownership.ubo.flagged': 'flagged',
  'entityDetail.ownership.ubo.empty': 'No beneficial owners ≥ 25%',
  'entityDetail.ownership.ubo.emptyDescription':
    'CIP / OFAC / PEP / adverse-media screens are not yet wired (#1777). When an owner crosses the 25% threshold, their AML status will appear here.',
  // Financials tab — Balance snapshot (left) + Cash flow (right)
  'entityDetail.financials.balance.title': 'Balance snapshot',
  'entityDetail.financials.balance.liquidReserves': 'Liquid reserves',
  'entityDetail.financials.balance.totalAssets': 'Total assets (est.)',
  'entityDetail.financials.balance.totalLiabilities': 'Total liabilities',
  'entityDetail.financials.balance.netWorth': 'Net worth (est.)',
  'entityDetail.financials.balance.statementsAsOf': 'Bank stmts as of',
  'entityDetail.financials.balance.monthsOfReserves': 'Months of reserves',
  'entityDetail.financials.cashFlow.title': 'Cash flow · last 12mo',
  'entityDetail.financials.cashFlow.avgInflow': 'Avg monthly inflow',
  'entityDetail.financials.cashFlow.avgOutflow': 'Avg monthly outflow',
  'entityDetail.financials.cashFlow.trend': 'Trend (3mo)',
  'entityDetail.financials.cashFlow.nsfs': 'NSFs (12mo)',
  'entityDetail.financials.empty': 'Bank statements unavailable',
  'entityDetail.financials.emptyDescription':
    'Connect Plaid or upload bank statements to enable cash-flow analysis. Aggregation not yet wired (#1779).',
  // Right rail
  'entityDetail.rail.activity': 'Activity',
  'entityDetail.rail.messages': 'Messages',
  'entityDetail.rail.requests': 'Requests',
  'entityDetail.rail.astor': 'Astor',
  'entityDetail.rail.activityEmpty': 'No activity recorded for this entity yet.',
  'entityDetail.rail.messagesEmpty':
    'Entity-scoped messaging is not yet wired. Messages on individual loans show up there.',
  'entityDetail.rail.requestsLinkedOwners': 'Linked owners',
  'entityDetail.rail.requestsLinkedLoans': 'Linked loans',
  'entityDetail.rail.requestsLinkedGuarantees': 'Linked guarantees',
  'entityDetail.rail.requestsEmpty': 'No linked records',
  'entityDetail.rail.astorSummaryLabel': 'Astor — Entity context',
  'entityDetail.rail.astorSummary':
    'This entity carries {{outstanding}} outstanding across {{active}} active loans. Risk rating is {{rating}}. Review the Ownership tab for UBO clearance status before adding a new deal.',
  'entityDetail.rail.astorSuggestedActions': 'Suggested actions',
  'entityDetail.rail.astorActionNewLoan': 'Start a new loan for this entity',
  'entityDetail.rail.astorActionRefreshUBO': 'Refresh UBO CIP/AML screens',
  'entityDetail.rail.astorActionExport': 'Export entity portfolio summary',

  // ============================================================================
  // GUARANTORS (Issue #636)
  // ============================================================================
  'guarantors.title': 'Guarantors',
  'guarantors.name': 'Guarantor Name',
  'guarantors.add': 'Add Guarantor',
  'guarantors.description': 'View your guarantee obligations and exposure.',
  'guarantors.addTitle': 'Add Guarantor',
  'guarantors.editTitle': 'Edit Guarantor',
  'guarantors.detailTitle': 'Guarantor Details',
  'guarantors.emptyState': 'No guarantors on this loan',
  'guarantors.emptyDescription': 'Add guarantors to track personal and corporate guarantees.',
  'guarantors.dashboard': 'Guarantor Dashboard',
  'guarantors.exposure': 'Guarantee Exposure',
  'guarantors.totalExposure': 'Total Exposure',
  'guarantors.activeGuarantees': 'Active Guarantees',
  // Guarantor fields
  'guarantors.guarantorType': 'Guarantor Type',
  'guarantors.guarantorType.individual': 'Individual',
  'guarantors.guarantorType.entity': 'Entity',
  'guarantors.guaranteeType': 'Guarantee Type',
  'guarantors.guaranteeType.full_recourse': 'Full Recourse',
  'guarantors.guaranteeType.partial_recourse': 'Partial Recourse',
  'guarantors.guaranteeType.payment': 'Payment Guarantee',
  'guarantors.guaranteeType.completion': 'Completion Guarantee',
  'guarantors.guaranteeType.carve_out': 'Carve-Out',
  'guarantors.guaranteeAmount': 'Guarantee Amount',
  'guarantors.guaranteePercentage': 'Guarantee %',
  // Financial profile
  'guarantors.financialProfile': 'Financial Profile',
  'guarantors.netWorth': 'Net Worth',
  'guarantors.liquidity': 'Liquidity',
  'guarantors.creditScore': 'Credit Score',
  'guarantors.existingExposure': 'Existing Exposure',
  // Guarantor statuses
  'guarantors.status': 'Status',
  'guarantors.status.pending': 'Pending',
  'guarantors.status.active': 'Active',
  'guarantors.status.released': 'Released',
  'guarantors.status.defaulted': 'Defaulted',
  // Dates
  'guarantors.effectiveDate': 'Effective Date',
  'guarantors.expirationDate': 'Expiration Date',
  'guarantors.expiringSoon': 'Expiring Soon',
  'guarantors.caseNumber': 'Loan #',
  'guarantors.numberOfGuarantors': 'Number of Guarantors',
  'guarantors.noGuarantors': 'No guarantors found',
  'guarantors.noGuarantorsDescription': 'Guarantors added to loans will appear here.',
  'guarantors.totalGuarantors': 'Total Guarantors',
  // Guarantor profile panel (Issue #1197)
  'guarantors.profile': 'Guarantor Profile',
  'guarantors.viewFullProfile': 'View Full Profile',
  'guarantors.exposureRatio': 'Exposure / Net Worth',
  'guarantors.activeObligations': 'Active Obligations',
  'guarantors.alerts': 'Alerts',
  'guarantors.noProfile': 'Select a guarantor to view profile',
  'guarantors.exposureSummary': 'Exposure Summary',
  // v3 Detail page (Design Pass 4 — PR-1, Issues #1794-#1798)
  'guarantors.detail.back': 'Back to guarantors',
  'guarantors.detail.chip.guarantor': 'Guarantor',
  'guarantors.detail.chip.active': 'active',
  'guarantors.detail.relationship.spouseJoint': 'Spouse · joint sign',
  'guarantors.detail.linkedTo': 'Linked to',
  'guarantors.detail.actions.export': 'Export',
  'guarantors.detail.actions.edit': 'Edit',
  'guarantors.detail.actions.message': 'Message',
  'guarantors.detail.actions.addToLoan': 'Add to loan',
  'guarantors.detail.kpi.totalExposure': 'Total guarantee exposure',
  'guarantors.detail.kpi.totalExposureSub_one': 'across {{count}} active loan',
  'guarantors.detail.kpi.totalExposureSub_other': 'across {{count}} active loans',
  // Legacy alias retained for existing callers / e2e snapshots.
  'guarantors.detail.kpi.totalExposureSub': 'across {{count}} active loans',
  'guarantors.detail.kpi.activeGuarantees': 'Active guarantees',
  'guarantors.detail.kpi.activeGuaranteesSub': 'full recourse · joint',
  'guarantors.detail.kpi.ficoMid': 'FICO mid',
  'guarantors.detail.kpi.ficoMidSub': 'band: {{band}} · pulled {{days}}d ago',
  'guarantors.detail.kpi.netWorth': 'Net worth (claimed)',
  'guarantors.detail.kpi.netWorthSub': 'PFS verified {{days}}d ago',
  'guarantors.detail.kpi.liquidity': 'Liquidity',
  'guarantors.detail.kpi.liquiditySub': '{{months}}mo coverage',
  'guarantors.detail.personal.title': 'Personal information',
  'guarantors.detail.personal.fullName': 'Full name',
  'guarantors.detail.personal.email': 'Email',
  'guarantors.detail.personal.phone': 'Phone',
  'guarantors.detail.personal.dob': 'DOB',
  'guarantors.detail.personal.address': 'Address',
  'guarantors.detail.personal.ssn': 'SSN last 4',
  'guarantors.detail.personal.idVerified': 'ID verified',
  'guarantors.detail.personal.idVerifiedValue': '✓ {{provider}} · {{date}}',
  'guarantors.detail.personal.relationship': 'Relationship',
  'guarantors.detail.credit.title': 'Credit profile',
  'guarantors.detail.credit.ficoMidLabel': 'FICO MID',
  'guarantors.detail.credit.bandLine': 'band: {{band}} · pulled {{days}}d ago · {{bureau}}',
  'guarantors.detail.credit.tradelines': 'Tradelines',
  'guarantors.detail.credit.tradelinesValue': '{{total}} · {{derog}} derog',
  'guarantors.detail.credit.util': 'Util',
  'guarantors.detail.credit.oldestLine': 'Oldest line',
  'guarantors.detail.credit.oldestLineValue': '{{years}}y',
  'guarantors.detail.credit.inquiries': 'Inquiries 12m',
  'guarantors.detail.guarantees.title': 'Active guarantees',
  'guarantors.detail.guarantees.activeCount': '{{count}} active',
  'guarantors.detail.guarantees.col.loan': 'Loan',
  'guarantors.detail.guarantees.col.propertyBorrower': 'Property / Borrower',
  'guarantors.detail.guarantees.col.exposure': 'Exposure',
  'guarantors.detail.guarantees.col.guaranteeType': 'Guarantee type',
  'guarantors.detail.guarantees.col.signed': 'Signed',
  'guarantors.detail.guarantees.col.status': 'Status',
  'guarantors.detail.guarantees.statusActive': 'active',
  'guarantors.detail.guarantees.statusReleased': 'released',
  'guarantors.detail.pfs.title': 'Financial snapshot · PFS',
  'guarantors.detail.pfs.totalAssets': 'Total assets',
  'guarantors.detail.pfs.totalLiabilities': 'Total liabilities',
  'guarantors.detail.pfs.netWorth': 'Net worth',
  'guarantors.detail.pfs.liquid': 'Liquid (cash + securities)',
  'guarantors.detail.pfs.realEstate': 'Real estate owned',
  'guarantors.detail.pfs.realEstateValue_one': '{{count}} property',
  'guarantors.detail.pfs.realEstateValue_other': '{{count}} properties',
  // Legacy alias.
  'guarantors.detail.pfs.realEstateValue': '{{count}} properties',
  'guarantors.detail.pfs.monthlyIncome': 'Monthly income',
  'guarantors.detail.pfs.monthlyDebt': 'Monthly debt service',
  'guarantors.detail.pfs.dti': 'DTI',
  'guarantors.detail.pfs.verifiedLine': 'PFS dated {{date}} · auto-verified by {{verifier}}',
  'guarantors.detail.docs.title': 'Guarantor documents',
  'guarantors.detail.docs.statusVerified': 'verified',
  'guarantors.detail.docs.statusSigned': 'signed',
  'guarantors.detail.docs.statusPending': 'pending',
  'guarantors.detail.docs.statusExpired': 'expired',
  // Live guarantee status chip (placeholder fallback handles pending/defaulted)
  'guarantors.detail.guarantees.statusPending': 'pending',
  'guarantors.detail.guarantees.statusDefaulted': 'defaulted',
  // FicoBand accessible label (rendered by screen readers only)
  'guarantors.detail.credit.ficoAriaLabel': 'FICO score {{score}} in band {{band}}',
  // Placeholder banner shown when no backend procedure has shipped yet
  // (Issues #1794-#1798).
  'guarantors.detail.placeholderBanner':
    'Showing placeholder profile. Live backend procedures pending (Issues #1794-#1798).',
  // Disabled-action explainers — buttons render but are disabled until the
  // backing flows ship.
  'guarantors.detail.actions.export.unavailable': 'Export not available yet',
  'guarantors.detail.actions.edit.unavailable': 'Edit not available yet',
  'guarantors.detail.actions.message.unavailable': 'Messaging not available yet',
  'guarantors.detail.actions.addToLoan.unavailable': 'Add-to-loan flow not available yet',
  // KPI subtitle pluralization (active-loans count)
  'guarantors.detail.kpi.netWorthVerifiedAge_zero': 'PFS just verified',
  'guarantors.detail.kpi.netWorthVerifiedAge_one': 'PFS verified {{days}}d ago',
  'guarantors.detail.kpi.netWorthVerifiedAge_other': 'PFS verified {{days}}d ago',
  'guarantors.detail.kpi.liquidityCoverage': '{{months}}mo coverage',
  'guarantors.detail.kpi.liquidityCoverageMissing': 'coverage —',

  // ============================================================================
  // CASE BORROWERS / PARTIES (Issue #636)
  // ============================================================================
  'caseParties.title': 'Loan Parties',
  'caseParties.borrowers': 'Borrowers',
  'caseParties.borrowingEntities': 'Borrowing Entities',
  'caseParties.partners': 'Partners',
  'caseParties.guarantors': 'Guarantors',
  'caseParties.owners': 'Entity Owners',
  'caseParties.addBorrower': 'Add Borrower',
  'caseParties.addBorrowingEntity': 'Add Borrowing Entity',
  'caseParties.removeBorrower': 'Remove Borrower',
  'caseParties.role': 'Borrower Role',
  'caseParties.role.primary_borrower': 'Primary Borrower',
  'caseParties.role.co_borrower': 'Co-Borrower',
  'caseParties.role.sponsor': 'Sponsor',
  'caseParties.partnerRole': 'Channel Partner',
  'caseParties.ownershipInterest': 'Ownership Interest',
  'caseParties.noBorrowers': 'No individual borrowers linked',
  'caseParties.noBorrowingEntities': 'No borrowing entities linked',
  'caseParties.noPartners': 'No partner linked to this loan',
  'caseParties.linkPartner': 'Link Partner',
  'caseParties.unlinkPartner': 'Unlink Partner',
  'caseParties.unlinkPartnerConfirm':
    'Are you sure you want to unlink this partner? Pending commissions will be cancelled.',
  'caseParties.searchPartners': 'Search active partners...',
  'caseParties.selectPartner': 'Select a partner to link to this loan',
  'caseParties.partnerLinked': 'Partner linked successfully',
  'caseParties.partnerUnlinked': 'Partner unlinked and pending commissions cancelled',
  'caseParties.noActivePartners': 'No active partners found',
  'caseParties.borrowerName': 'Name',
  'caseParties.borrowerEmail': 'Email',
  'caseParties.borrowerPhone': 'Phone',
  'caseParties.partnerName': 'Name',
  'caseParties.partnerEmail': 'Email',
  'caseParties.partnerPhone': 'Phone',
  'caseParties.entity': 'Borrowing Entity',
  'caseParties.consumer': 'Consumer',
  'caseParties.pointOfContact': 'Point of Contact',
  'caseParties.makePointOfContact': 'Make Point of Contact',
  'caseParties.pointOfContactTooltip': 'The Point of Contact receives all communications for this loan',
  'caseParties.crossLoanTitle': 'Cross-Loan References',
  'caseParties.crossLoanDescription': 'Other loans involving the same parties',

  // Unified parties grid
  'caseParties.column.name': 'Name',
  'caseParties.column.type': 'Type',
  'caseParties.column.roleSubtype': 'Role',
  'caseParties.column.contactDetail': 'Contact',
  'caseParties.column.actions': 'Actions',
  'caseParties.typeBorrower': 'Borrower',
  'caseParties.typePartner': 'Partner',
  'caseParties.typeEntity': 'Entity',
  'caseParties.typeGuarantor': 'Guarantor',
  'caseParties.menu.addBorrower': 'Add Borrower',
  'caseParties.menu.linkPartner': 'Link Partner',
  'caseParties.menu.addEntity': 'Add Borrowing Entity',
  'caseParties.menu.addGuarantor': 'Add Guarantor',
  'caseParties.action.makePoc': 'Make Point of Contact',
  'caseParties.action.unlinkPartner': 'Unlink Partner',
  'caseParties.action.removeEntity': 'Remove Borrowing Entity',
  'caseParties.action.removeGuarantor': 'Remove Guarantor',
  'caseParties.noPartiesYet': 'No parties have been added to this loan yet',
  'caseParties.addMenuTitle': 'Add a party',
  'caseParties.singleBorrowerTooltip': 'A loan must have at least one borrower',

  'coBorrower.title': 'Co-Borrowers',
  'coBorrower.stepTitle': 'Co-Borrower Information',
  'coBorrower.hasCoBorrower': 'Will there be a co-borrower on this loan?',
  'coBorrower.entityName': 'Co-Borrower Entity Name',
  'coBorrower.role': 'Co-Borrower Role',
  'coBorrower.ownershipInterest': 'Ownership Interest %',

  // ============================================================================
  // ACTIONS
  // ============================================================================
  'actions.newLead': 'New Lead',
  'actions.newCase': 'New Loan',
  'actions.createCase': 'Create Loan',
  'actions.newConsumer': 'New Consumer',
  'actions.newDocument': 'New Document',
  'actions.newAgent': 'New Agent',
  'actions.newUser': 'New User',
  'actions.newTeamMember': 'New Team Member',
  'actions.newBusiness': 'New Business',
  'actions.createBusiness': 'Create Business',
  'actions.newRealm': 'New Realm',
  'actions.newTenant': 'New Tenant',

  // Issue #400 - i18n audit
  'actions.addBorrower': 'Add Borrower',
  'actions.createTermSheet': 'Create Term Sheet',
  'actions.sendInvites': 'Send Invites',

  'actions.reviewCases': 'Review Loans',
  'actions.manageConsumers': 'Manage Consumers',
  'actions.viewDetails': 'View Details',
  'actions.viewDocuments': 'View Documents',
  'actions.editDetails': 'Edit Details',
  'actions.deleteItem': 'Delete',
  'actions.archiveItem': 'Archive',
  'actions.restoreItem': 'Restore',

  'actions.sendInvite': 'Send Invite',
  'actions.sendMessage': 'Send Message',
  'actions.resendInvite': 'Resend Invite',
  'actions.revokeAccess': 'Revoke Access',
  'actions.grantAccess': 'Grant Access',

  'actions.runTest': 'Run Test',
  'actions.simulate': 'Simulate',
  'actions.preview': 'Preview',
  'actions.publish': 'Publish',
  'actions.unpublish': 'Unpublish',

  'actions.exportPdf': 'Export as PDF',
  'actions.exportCsv': 'Export as CSV',
  'actions.exportExcel': 'Export as Excel',
  'actions.downloadReport': 'Download Report',

  'actions.startChat': 'Start Chat',
  'actions.endChat': 'End Chat',
  'actions.clearHistory': 'Clear History',

  'actions.suspend': 'Suspend',
  'actions.reactivate': 'Reactivate',
  'actions.exportTemplate': 'Export as Template',
  'actions.duplicate': 'Duplicate',
  'actions.download': 'Download',
  'actions.testRule': 'Test Rule',

  // ============================================================================
  // STATUS
  // ============================================================================
  // Generic statuses (reusable across features)
  'status.pending': 'Pending',
  'status.running': 'Running',
  'status.inProgress': 'In Progress',
  'status.completed': 'Completed',
  'status.complete': 'Complete',
  'status.failed': 'Failed',
  'status.cancelled': 'Cancelled',
  'status.success': 'Success',
  'status.error': 'Error',
  'status.active': 'Active',
  'status.inactive': 'Inactive',
  'status.draft': 'Draft',
  'status.paused': 'Paused',
  'status.scheduled': 'Scheduled',
  'status.revoked': 'Revoked',
  'status.suspended': 'Suspended',
  'status.required': 'Required',
  'status.optional': 'Optional',
  'status.fulfilled': 'Fulfilled',
  'status.unfulfilled': 'Unfulfilled',
  'status.uploading': 'Uploading...',
  'status.uploadingFiles': 'Uploading files...',
  'status.expired': 'Expired',
  'status.needsReupload': 'Needs Re-upload',

  // Deal statuses

  // Case statuses
  'status.case.new': 'New',
  'status.case.open': 'Open',
  'status.case.inProgress': 'In Progress',
  'status.case.inReview': 'In Review',
  'status.case.pending': 'Pending',
  'status.case.pendingReview': 'Pending Review',
  'status.case.approved': 'Approved',
  'status.case.rejected': 'Rejected',
  'status.case.resolved': 'Resolved',
  'status.case.closed': 'Closed',
  'status.case.onHold': 'On Hold',
  'status.case.escalated': 'Escalated',
  'status.case.in_progress': 'In Progress',
  'status.case.pending_review': 'Pending Review',
  'status.case.under_review': 'Under Review',
  'status.case.pending_approval': 'Pending Approval',
  'status.case.cancelled': 'Cancelled',
  'status.case.archived': 'Archived',

  // Loan lifecycle stages (platform-canonical — stepper positions on the
  // business-portal loan detail page). See `loan-stage.enums.ts`.
  'loan.stage.to_review': 'To Review',
  'loan.stage.in_progress': 'In Progress',
  'loan.stage.term_sheet': 'Term Sheet',
  'loan.stage.underwriting': 'Underwriting',
  'loan.stage.approval': 'Approval',
  'loan.stage.closing': 'Closing',
  'loan.stage.funded': 'Funded',
  'loan.stage.servicing': 'Servicing',
  // Terminal stages (off-stepper)
  'loan.stage.paid_off': 'Paid Off',
  'loan.stage.declined': 'Declined',
  'loan.stage.withdrawn': 'Withdrawn',
  'loan.stage.expired': 'Expired',
  'loan.stage.cancelled': 'Cancelled',
  'loan.stage.charged_off': 'Charged Off',

  // Legacy stage aliases — pre-rename DB rows that escape the
  // 0010_rename_loan_stages_lead_application migration still resolve correctly.
  'loan.stage.lead': 'To Review',
  'loan.stage.application': 'In Progress',
  'loan.substage.application_started': 'Started',
  'loan.substage.application_submitted': 'Submitted',
  'loan.substage.application_under_review': 'Intake review',
  'loan.substage.application_returned': 'Sent back to borrower',

  // Loan substages (shown as a chip next to the active stage name)
  'loan.substage.to_review_new': 'New',
  'loan.substage.to_review_assigned': 'Pre-qualified',
  'loan.substage.to_review_escalated': 'Disqualified',
  'loan.substage.in_progress_sizing': 'Started',
  'loan.substage.in_progress_structuring': 'Submitted',
  'loan.substage.in_progress_awaiting_info': 'Intake review',
  'loan.substage.term_sheet_issued': 'Issued',
  'loan.substage.term_sheet_signed': 'Signed',
  'loan.substage.term_sheet_countered': 'Counter-offered',
  'loan.substage.underwriting_processing': 'Processing',
  'loan.substage.underwriting_in_review': 'In review',
  'loan.substage.underwriting_awaiting_appraisal': 'Awaiting appraisal',
  'loan.substage.underwriting_awaiting_title': 'Awaiting title',
  'loan.substage.underwriting_credit_memo_drafted': 'Credit memo drafted',
  'loan.substage.underwriting_suspended': 'Suspended',
  'loan.substage.approval_conditional': 'Conditional',
  'loan.substage.approval_committee_review': 'Committee review',
  'loan.substage.approval_committed': 'Committed',
  'loan.substage.closing_clear_to_close': 'Clear to close',
  'loan.substage.closing_docs_out': 'Docs out',
  'loan.substage.closing_docs_signed': 'Docs signed',
  'loan.substage.closing_funding_review': 'Funding review',
  'loan.substage.funded_initial': 'Initial funding',
  'loan.substage.funded_post_closing_qc': 'Post-closing QC',
  'loan.substage.servicing_current': 'Current',
  'loan.substage.servicing_grace': 'Grace period',
  'loan.substage.servicing_delinquent_30': '30 days past due',
  'loan.substage.servicing_delinquent_60': '60 days past due',
  'loan.substage.servicing_delinquent_90': '90 days past due',
  'loan.substage.servicing_default': 'Default',
  'loan.substage.servicing_matured': 'Matured',
  'loan.substage.servicing_extension_granted': 'Extension granted',

  // Terminal reasons (attached when entering DECLINED / WITHDRAWN / etc.)
  'loan.terminalReason.declined_credit': 'Credit',
  'loan.terminalReason.declined_collateral': 'Collateral',
  'loan.terminalReason.declined_income_or_dscr': 'Income / DSCR',
  'loan.terminalReason.declined_policy': 'Policy',
  'loan.terminalReason.declined_incomplete': 'Incomplete application',
  'loan.terminalReason.declined_other': 'Other',
  'loan.terminalReason.withdrawn_borrower_choice': "Borrower's choice",
  'loan.terminalReason.withdrawn_lost_to_competitor': 'Lost to competitor',
  'loan.terminalReason.withdrawn_deal_died': 'Deal died',
  'loan.terminalReason.expired_term_sheet': 'Term sheet expired',
  'loan.terminalReason.expired_commitment': 'Commitment expired',
  'loan.terminalReason.expired_rate_lock': 'Rate lock expired',
  'loan.terminalReason.cancelled_duplicate': 'Duplicate',
  'loan.terminalReason.cancelled_admin': 'Administrative',
  'loan.terminalReason.paid_off_sale': 'Property sale',
  'loan.terminalReason.paid_off_refinance': 'Refinanced',
  'loan.terminalReason.paid_off_maturity': 'Matured',
  'loan.terminalReason.charged_off_default': 'Default',
  'loan.terminalReason.charged_off_settlement': 'Settlement',

  // Consumer-portal simplified stage labels (borrower-facing — hides
  // operational nuance like credit-memo drafts, committee reviews, etc.).
  'loan.consumerStage.applied': 'Applied',
  'loan.consumerStage.reviewing': 'Under review',
  'loan.consumerStage.offer': 'Offer received',
  'loan.consumerStage.accepted': 'Offer accepted',
  'loan.consumerStage.closing': 'Closing',
  'loan.consumerStage.funded': 'Funded',
  'loan.consumerStage.active': 'Active',
  'loan.consumerStage.terminal': 'Closed',

  // Stage action blocks on the Overview panel (what the user does next at this stage)
  'loan.action.to_review': 'Send pre-qualification letter',
  'loan.action.in_progress': 'Complete intake and verify documents',
  'loan.action.term_sheet': 'Issue term sheet for borrower signature',
  'loan.action.underwriting': 'Complete underwriting conditions',
  'loan.action.approval': 'Issue commitment letter',
  'loan.action.closing': 'Draw closing docs and fund',
  'loan.action.funded': 'Complete post-closing QC',
  'loan.action.servicing': 'Monitor payments and maturity',
  // Issue #1388 — Phase 5: capability-driven action keys (LoanStageActionCard).
  // Selected by `LoanStageCapability` rather than the legacy LoanStage enum,
  // so a tenant who renames their internal stages still gets the right copy.
  'loan.action.toReview': 'Send pre-qualification letter',
  'loan.action.intake': 'Complete intake and verify documents',
  'loan.action.draftTermSheet': 'Draft and send term sheet for signature',
  'loan.action.termSheetSigned': 'Term sheet signed — kick off underwriting',
  'loan.action.orderCreditPull': 'Pull credit and clear findings',
  'loan.action.orderAppraisal': 'Order appraisal and review valuation',
  'loan.action.orderTitle': 'Order title and review preliminary report',
  'loan.action.requiresCommittee': 'Schedule committee review',
  'loan.action.requiresQc': 'Complete post-close QC review',
  'loan.action.coordinateClosing': 'Coordinate closing checklist and signing',
  'loan.action.docsSigned': 'Coordinate funding wire',
  'loan.action.changeStage': 'Change stage',

  // ============================================================================
  // PROVISIONAL — LO team to review (Bug 6 / Issue #1615)
  //
  // Per-capability stage guidance copy (purpose statement + step-by-step
  // checklist) surfaced by `LoanStageActionCard`'s collapsible "What
  // happens in this stage" accordion. Keys are addressed by
  // `LoanStageCapability` so a tenant who renames their internal stages
  // still gets the right guidance as long as the stage carries the
  // matching capability.
  //
  // Step keys use a flat numeric-index suffix (.steps.0, .steps.1, ...)
  // because the en.ts dictionary is `Record<string, string>` — array
  // values aren't supported by the existing useI18n() loader. The card
  // probes indices 0..MAX_GUIDANCE_STEPS and stops at the first missing
  // key, so each capability can declare any number of steps here.
  //
  // All copy below is provisional; LO team will review and tighten the
  // language before this surface is rolled out.
  // ============================================================================
  'loan.stage.guidance.title': 'What happens in this stage',

  'loan.stage.guidance.TRIGGERS_INTAKE.purpose': "Collect the borrower's application and initial documents.",
  'loan.stage.guidance.TRIGGERS_INTAKE.steps.0': 'Verify borrower identity and contact info',
  'loan.stage.guidance.TRIGGERS_INTAKE.steps.1': 'Collect signed authorization form',
  'loan.stage.guidance.TRIGGERS_INTAKE.steps.2': 'Upload initial documents (ID, bank statements)',
  'loan.stage.guidance.TRIGGERS_INTAKE.steps.3': 'Confirm intake checklist before advancing',

  'loan.stage.guidance.TRIGGERS_REVIEW.purpose': 'Pre-qualify the borrower and confirm the deal is worth working up.',
  'loan.stage.guidance.TRIGGERS_REVIEW.steps.0': 'Review intake package for completeness',
  'loan.stage.guidance.TRIGGERS_REVIEW.steps.1': 'Run preliminary pricing and eligibility checks',
  'loan.stage.guidance.TRIGGERS_REVIEW.steps.2': 'Send pre-qualification letter to the borrower',

  'loan.stage.guidance.TRIGGERS_TERM_SHEET.purpose': 'Issue and execute the term sheet with the borrower.',
  'loan.stage.guidance.TRIGGERS_TERM_SHEET.steps.0': 'Draft term sheet from the deal sizing',
  'loan.stage.guidance.TRIGGERS_TERM_SHEET.steps.1': 'Send via DocuSign for borrower signature',
  'loan.stage.guidance.TRIGGERS_TERM_SHEET.steps.2': 'Confirm signed term sheet before advancing',

  'loan.stage.guidance.TRIGGERS_TERM_SHEET_SIGNED.purpose':
    'Term sheet is signed — kick off third-party orders and underwriting.',
  'loan.stage.guidance.TRIGGERS_TERM_SHEET_SIGNED.steps.0': 'Confirm signed term sheet is filed in the data room',
  'loan.stage.guidance.TRIGGERS_TERM_SHEET_SIGNED.steps.1': 'Open underwriting file and assign processor',
  'loan.stage.guidance.TRIGGERS_TERM_SHEET_SIGNED.steps.2': 'Queue up third-party orders (credit, appraisal, title)',

  'loan.stage.guidance.TRIGGERS_CREDIT_PULL.purpose': "Pull the borrower's credit and clear any preliminary findings.",
  'loan.stage.guidance.TRIGGERS_CREDIT_PULL.steps.0': 'Confirm credit authorization is on file',
  'loan.stage.guidance.TRIGGERS_CREDIT_PULL.steps.1': 'Pull tri-merge credit report',
  'loan.stage.guidance.TRIGGERS_CREDIT_PULL.steps.2': 'Surface tradelines, derogatories, and inquiries to UW',
  'loan.stage.guidance.TRIGGERS_CREDIT_PULL.steps.3': 'Document any letters of explanation requested',

  'loan.stage.guidance.TRIGGERS_APPRAISAL.purpose':
    'Order the appraisal and review the valuation against the deal sizing.',
  'loan.stage.guidance.TRIGGERS_APPRAISAL.steps.0': 'Order appraisal from the approved AMC',
  'loan.stage.guidance.TRIGGERS_APPRAISAL.steps.1': 'Track inspection scheduling and report ETA',
  'loan.stage.guidance.TRIGGERS_APPRAISAL.steps.2': 'Review final report against deal value and condition',
  'loan.stage.guidance.TRIGGERS_APPRAISAL.steps.3': 'Flag valuation gaps to underwriting',

  'loan.stage.guidance.TRIGGERS_TITLE_ORDER.purpose': 'Order title and review the preliminary report for any clouds.',
  'loan.stage.guidance.TRIGGERS_TITLE_ORDER.steps.0': 'Open title order with the closing agent',
  'loan.stage.guidance.TRIGGERS_TITLE_ORDER.steps.1': 'Review preliminary title report and exceptions',
  'loan.stage.guidance.TRIGGERS_TITLE_ORDER.steps.2': 'Resolve any liens, judgments, or vesting issues',

  'loan.stage.guidance.TRIGGERS_UNDERWRITING.purpose': "Underwrite the loan against the lender's guidelines.",
  'loan.stage.guidance.TRIGGERS_UNDERWRITING.steps.0': 'Review credit, income, and asset documents',
  'loan.stage.guidance.TRIGGERS_UNDERWRITING.steps.1': 'Order any outstanding 3rd-party reports',
  'loan.stage.guidance.TRIGGERS_UNDERWRITING.steps.2': 'Surface conditions to the borrower',
  'loan.stage.guidance.TRIGGERS_UNDERWRITING.steps.3': 'Issue conditional approval when ready',

  'loan.stage.guidance.REQUIRES_COMMITTEE.purpose': 'Schedule and present the loan to credit committee for approval.',
  'loan.stage.guidance.REQUIRES_COMMITTEE.steps.0': 'Prepare the credit memo and supporting exhibits',
  'loan.stage.guidance.REQUIRES_COMMITTEE.steps.1': 'Schedule the committee meeting and circulate materials',
  'loan.stage.guidance.REQUIRES_COMMITTEE.steps.2': 'Capture committee decision and any conditions',

  'loan.stage.guidance.REQUIRES_QC.purpose': 'Complete the post-close (or pre-close) QC review against guidelines.',
  'loan.stage.guidance.REQUIRES_QC.steps.0': 'Run the QC checklist for this loan program',
  'loan.stage.guidance.REQUIRES_QC.steps.1': 'Resolve any QC findings with the originator',
  'loan.stage.guidance.REQUIRES_QC.steps.2': 'Sign off on the QC report and file it',

  'loan.stage.guidance.TRIGGERS_CLOSING.purpose': 'Coordinate the closing checklist and signing.',
  'loan.stage.guidance.TRIGGERS_CLOSING.steps.0': 'Send closing instructions to the closing agent',
  'loan.stage.guidance.TRIGGERS_CLOSING.steps.1': 'Confirm closing disclosure and figures with the borrower',
  'loan.stage.guidance.TRIGGERS_CLOSING.steps.2': 'Schedule the signing and confirm signing logistics',
  'loan.stage.guidance.TRIGGERS_CLOSING.steps.3': 'Track signed package return',

  'loan.stage.guidance.TRIGGERS_DOCS_SIGNED.purpose': 'Docs are signed — coordinate the funding wire.',
  'loan.stage.guidance.TRIGGERS_DOCS_SIGNED.steps.0': 'Confirm signed package is complete and recorded',
  'loan.stage.guidance.TRIGGERS_DOCS_SIGNED.steps.1': 'Send wire instructions to treasury/funding',
  'loan.stage.guidance.TRIGGERS_DOCS_SIGNED.steps.2': 'Confirm wire was sent and capture the reference',

  'loan.stage.guidance.IS_FUNDED.purpose': 'Loan is funded — handle post-close QC and servicing handoff.',
  'loan.stage.guidance.IS_FUNDED.steps.0': 'Complete post-close QC review',
  'loan.stage.guidance.IS_FUNDED.steps.1': 'Hand off to servicing',
  'loan.stage.guidance.IS_FUNDED.steps.2': 'Confirm wire receipt',

  'loan.stage.guidance.IS_SERVICING.purpose': 'Loan is in servicing — monitor payments and maturity.',
  'loan.stage.guidance.IS_SERVICING.steps.0': 'Track payment performance against the schedule',
  'loan.stage.guidance.IS_SERVICING.steps.1': 'Watch for delinquency, modification, or extension triggers',
  'loan.stage.guidance.IS_SERVICING.steps.2': 'Coordinate payoff or maturity workout when due',

  // Stage transition dialog (loan stage change flow)
  'loan.stageTransition.title': 'Change loan stage',
  'loan.stageTransition.currentLabel': 'Current stage',
  'loan.stageTransition.targetLabel': 'Target stage',
  'loan.stageTransition.substageLabel': 'Substage',
  'loan.stageTransition.reasonLabel': 'Reason',
  'loan.stageTransition.reasonRequired': 'Reason is required for this stage',
  'loan.stageTransition.noteLabel': 'Note (optional)',
  'loan.stageTransition.submit': 'Apply',

  // Terminal pill label template (e.g., "Declined · Credit")
  'loan.terminal.pillLabel': '{{stage}} · {{reason}}',

  // Pre-existing UI keys surfaced by the i18n audit (Issue #1438 CI cleanup).
  'cases.confirmApproveTitle': 'Approve case',
  'cases.confirmApproveBody': 'Approve case {{caseNumber}}? This decision is final.',
  'consumers.sendInvite.buttonLabel': 'Send invite',
  'consumers.sendInvite.menuLabel': 'Send invite',
  'consumers.sendInvite.sending': 'Sending…',
  'status.new': 'New',
  'status.approved': 'Approved',
  'status.rejected': 'Rejected',
  'status.pendingReview': 'Pending Review',
  'status.in_progress': 'In Progress',
  'status.pending_review': 'Pending Review',
  'status.under_review': 'Under Review',
  'status.pending_approval': 'Pending Approval',
  'status.closed': 'Closed',
  'status.archived': 'Archived',

  // Lead statuses
  'status.lead.new': 'New',
  'status.lead.contacted': 'Contacted',
  'status.lead.qualified': 'Qualified',
  'status.lead.proposal': 'Proposal',
  'status.lead.negotiation': 'Negotiation',
  'status.lead.won': 'Won',
  'status.lead.lost': 'Lost',
  'status.lead.nurturing': 'Nurturing',
  'status.lead.converted': 'Converted',
  'status.lead.archived': 'Archived',

  // Lead scoring/qualification
  'leads.qualification': 'Qualification',
  'leads.score': 'Lead Score',
  'leads.minScore': 'Minimum Score',
  'leads.maxScore': 'Maximum Score',
  'leads.notFound': 'Lead not found',
  'leads.conversionTitle': 'Convert Lead to Loan',
  'leads.qualifications.hot': 'Hot',
  'leads.qualifications.warm': 'Warm',
  'leads.qualifications.cold': 'Cold',
  'leads.scoring.mql': 'MQL',
  'leads.scoring.sql': 'SQL',
  'leads.scoring.unqualified': 'Unqualified',

  // Document statuses
  'status.document.pending': 'Pending',
  'status.document.processing': 'Processing',
  'status.document.approved': 'Approved',
  'status.document.rejected': 'Rejected',
  'status.document.needsReview': 'Needs Review',

  // Consumer statuses
  'status.consumer.pending': 'Pending',
  'status.consumer.active': 'Active',
  'status.consumer.inactive': 'Inactive',
  'status.consumer.suspended': 'Suspended',

  // Entity fields
  'entity.legalName': 'Legal Name',
  'entity.type': 'Type',
  'entity.ein': 'EIN',
  'entity.borrowingEntities': 'Borrowing Entities',
  'entity.status': 'Status',
  'entity.stateOfFormation': 'State of Formation',
  'entity.dbaName': 'DBA Name',
  'entity.noEntities': 'No borrowing entities found',

  // Priority levels
  'status.priority.critical': 'Critical',
  'status.priority.urgent': 'Urgent',
  'status.priority.high': 'High',
  'status.priority.medium': 'Medium',
  'status.priority.low': 'Low',
  'status.priority.info': 'Info',

  // Priority (short form - Issue #400 i18n audit)
  'priority.high': 'High',
  'priority.low': 'Low',
  'priority.medium': 'Medium',
  'priority.urgent': 'Urgent',

  // Agent statuses
  'status.agent.active': 'Active',
  'status.agent.inactive': 'Inactive',
  'status.agent.training': 'Training',
  'status.agent.error': 'Error',

  // Subscription statuses
  'status.subscription.trial': 'Trial',
  'status.subscription.active': 'Active',
  'status.subscription.suspended': 'Suspended',
  'status.subscription.cancelled': 'Cancelled',

  // ============================================================================
  // CONFIDENCE BADGES (Issue #475 - i18n migration)
  // ============================================================================
  'confidence.high': 'High Confidence',
  'confidence.medium': 'Medium Confidence',
  'confidence.low': 'Low Confidence',
  'confidence.high.explanation': 'The AI is highly confident in this extraction. The information is likely accurate.',
  'confidence.medium.explanation': 'The AI has moderate confidence in this extraction. Please review for accuracy.',
  'confidence.low.explanation':
    'The AI has low confidence in this extraction. Please verify this information carefully.',
  'confidence.tooltip': '{{level}} ({{percentage}}%)\n\n{{explanation}}',

  // ============================================================================
  // ROLES (Issue #400 - i18n audit)
  // ============================================================================
  'roles.businessAdmin': 'Business Admin',
  'roles.businessUser': 'Business User',
  'roles.superAdmin': 'Super Admin',

  // ============================================================================
  // USERS (Issue #400 - i18n audit)
  // ============================================================================
  'users.addUser': 'Add User',

  // ============================================================================
  // VALIDATION
  // ============================================================================
  'validation.required': 'This field is required',
  'validation.email': 'Please enter a valid email address',
  'validation.phone': 'Please enter a valid phone number',
  'validation.url': 'Please enter a valid URL',
  'validation.minLength': 'Must be at least {{min}} characters',
  'validation.maxLength': 'Must be no more than {{max}} characters',
  'validation.min': 'Must be at least {{min}}',
  'validation.max': 'Must be no more than {{max}}',
  'validation.pattern': 'Invalid format',
  'validation.unique': 'This value already exists',
  'validation.match': 'Values do not match',
  'validation.invalid': 'Invalid value',
  'validation.tooShort': 'Too short',
  'validation.tooLong': 'Too long',
  'validation.passwordWeak': 'Password is too weak',
  'validation.passwordMismatch': 'Passwords do not match',
  'validation.invalidEmail': 'Invalid email address',
  'validation.invalidPhone': 'Invalid phone number',
  'validation.invalidUrl': 'Invalid URL (must start with http:// or https://)',
  'validation.range': '{{field}} must be between {{min}} and {{max}}',
  'validation.range.money': 'Must be between {{min}} and {{max}}',
  'validation.range.percent': 'Must be between {{min}}% and {{max}}%',
  'validation.range.number': 'Must be between {{min}} and {{max}}',
  'validation.emailRequired': 'Email address is required',
  'validation.mustBeNumber': 'Must be a valid number',
  'validation.percentRange': 'Must be between 0 and 100',
  'validation.positiveNumber': 'Must be a positive number',

  // ============================================================================
  // ERRORS
  // ============================================================================
  'errors.generic': 'Something went wrong. Please try again.',
  'errors.network': 'Network error. Please check your connection.',
  'errors.timeout': 'Request timed out. Please try again.',
  'errors.notFound': 'The requested resource was not found.',
  'errors.unauthorized': 'You are not authorized to perform this action.',
  'errors.forbidden': 'Access denied.',
  'errors.serverError': 'Server error. Please try again later.',
  'errors.serviceUnavailable': 'Service temporarily unavailable.',
  'errors.rateLimited': 'Too many requests. Please wait a moment.',
  'errors.validationFailed': 'Please check your input and try again.',
  'errors.operationFailed': 'Operation failed. Please try again.',
  'errors.saveFailed': 'Failed to save. Please try again.',
  'errors.createFailed': 'Failed to create {{entity}}. Please try again.',
  'errors.loadFailed': 'Failed to load data. Please refresh the page.',
  'errors.deleteFailed': 'Failed to delete. Please try again.',
  'errors.uploadFailed': 'Failed to upload file. Please try again.',
  'errors.downloadFailed': 'Failed to download file. Please try again.',
  'errors.sessionExpired': 'Your session has expired. Please log in again.',
  'errors.tryAgain': 'Please try again',
  'errors.contactSupport': 'If the problem persists, please contact support.',
  'errors.accessDenied': 'Access Denied',
  'errors.adminRequired': 'You need administrator privileges to access this page.',
  // Route-level RBAC (Issue #428)
  'error.accessDenied': 'Access Denied',
  'error.insufficientPermissions': "You don't have permission to access this page.",
  'error.permissionCheckFailed': 'Unable to verify permissions. Please try signing out and back in.',
  'error.contactAdminForAccess': 'Please contact your administrator if you believe this is an error.',
  'error.missingCaseStatuses':
    'No loan statuses are configured. Please contact your administrator to set up loan statuses before creating loans.',

  // ============================================================================
  // DASHBOARD
  // ============================================================================
  'dashboard.welcome': 'Welcome',
  'dashboard.welcomeBack': 'Welcome back, {{name}}',
  'dashboard.consumerIntroBanner':
    'Track your loan applications, upload required documents, and respond to signature requests from this page. New here? Click Submit New above to start your first application.',
  'dashboard.overview': 'Overview',
  'dashboard.quickActions': 'Quick Actions',
  'dashboard.recentActivity': 'Recent Activity',

  // Section titles
  'dashboard.sections.pipeline': 'Loan Origination Pipeline',
  'dashboard.sections.pipelineDescription':
    'Track leads through your sales funnel from initial inquiry to closing. Monitor active leads, pending reviews, total loans, and consumer engagement.',
  'dashboard.sections.performance': 'Loan Performance Metrics',
  'dashboard.sections.performanceDescription':
    'Key metrics for team efficiency. Resolution time measures how quickly loans are fully resolved. Response time tracks initial acknowledgment speed.',
  'dashboard.sections.quickActions': 'Quick Actions',
  'dashboard.sections.quickActionsDescription':
    'Common actions to speed up your workflow. Create new leads, review pending loans, or manage consumers with one click.',
  'dashboard.sections.aiDocuments': 'Astor & Documents',
  'dashboard.sections.aiDocumentsDescription':
    'Create agents for automated workflows and chat assistance. Upload and manage documents that agents can reference for context-aware responses.',

  // KPI labels
  'dashboard.kpi.activeLeads': 'Active Leads',
  'dashboard.kpi.pendingReview': 'Pending Review',
  'dashboard.kpi.totalCases': 'Total Loans',
  'dashboard.kpi.consumers': 'Consumers',
  'dashboard.kpi.avgResolutionTime': 'Avg Resolution Time',
  'dashboard.kpi.avgResponseTime': 'Avg Response Time',
  'dashboard.kpi.agents': 'Agents',
  'dashboard.kpi.documents': 'Documents',
  'dashboard.summary.clickToManage': 'Click to manage',
  'dashboard.summary.clickToView': 'Click to view',

  // Pipeline funnel (Issue #599)
  'dashboard.sections.pipelineFunnel': 'Loan Pipeline',
  'dashboard.sections.pipelineFunnelDescription':
    'Visual breakdown of loans by stage. Click any stage to view filtered loans. Conversion rates show stage-to-stage progression.',
  'dashboard.funnel.leads': 'Leads',
  'dashboard.funnel.applications': 'Applications',
  'dashboard.funnel.underwriting': 'Underwriting',
  'dashboard.funnel.approved': 'Approved',
  'dashboard.funnel.funded': 'Funded',
  'dashboard.funnel.conversionRate': '{{rate}}% conversion',
  'dashboard.funnel.bottleneck': 'Bottleneck',
  'dashboard.funnel.dateRange': 'Date Range',
  'dashboard.funnel.days7': '7 Days',
  'dashboard.funnel.days30': '30 Days',
  'dashboard.funnel.days90': '90 Days',
  'dashboard.funnel.days365': '1 Year',
  'dashboard.funnel.stageCount': '{{count}} loans',
  'dashboard.funnel.noData': 'No pipeline data available',

  // Revenue metrics (Issue #600)
  'dashboard.sections.revenue': 'Revenue & Financial Metrics',
  'dashboard.sections.revenueDescription':
    'Key financial KPIs for your lending portfolio including total volume, revenue, average loan size, and funding activity.',
  'dashboard.revenue.totalVolume': 'Active Pipeline',
  'dashboard.revenue.revenueThisMonth': 'New Applications',
  'dashboard.revenue.avgLoanSize': 'Pending Review',
  'dashboard.revenue.fundedThisMonth': 'Funded This Month',
  'dashboard.revenue.fundedTotal': '{{count}} total',
  'dashboard.revenue.vsLastMonth': 'vs last month',
  'dashboard.revenue.activeCases': 'Active loans in pipeline',
  'dashboard.revenue.newApplications': 'New & in-progress',
  'dashboard.revenue.pendingReview': 'Awaiting review',

  // Loan volume chart (Issue #600)
  'dashboard.sections.loanVolume': 'Loan Volume Over Time',
  'dashboard.sections.loanVolumeDescription': 'Monthly loan volume breakdown by loan type over the past 12 months.',
  'dashboard.loanVolume.bridge': 'Bridge',
  'dashboard.loanVolume.dscr': 'DSCR',
  'dashboard.loanVolume.construction': 'Construction',
  'dashboard.loanVolume.conventional': 'Conventional',
  'dashboard.loanVolume.noData': 'No loan volume data available',

  // Enhanced activity feed (Issue #601)
  'dashboard.sections.activityFeed': 'Activity Feed',
  'dashboard.sections.activityFeedDescription':
    'Recent activity across your organization including loans, payments, documents, and tasks.',
  'dashboard.activity.today': 'Today',
  'dashboard.activity.yesterday': 'Yesterday',
  'dashboard.activity.thisWeek': 'This Week',
  'dashboard.activity.viewAll': 'View All',
  'dashboard.activity.noActivity': 'No recent activity',

  // Deadlines widget (Issue #601)
  'dashboard.sections.deadlines': 'Upcoming Deadlines',
  'dashboard.sections.deadlinesDescription':
    'Tasks and items approaching their due dates. Stay ahead of SLA requirements and upcoming deadlines.',
  'dashboard.deadlines.tasksDue': 'Tasks Due',
  'dashboard.deadlines.slaAtRisk': 'SLA At-Risk',
  'dashboard.deadlines.loanMaturity': 'Loan Maturity',
  'dashboard.deadlines.overdue': 'Overdue',
  'dashboard.deadlines.dueToday': 'Due Today',
  'dashboard.deadlines.dueSoon': 'Due Soon',
  'dashboard.deadlines.noDeadlines': 'No upcoming deadlines',
  'dashboard.deadlines.daysLeft': '{{days}}d left',
  'dashboard.deadlines.daysOverdue': '{{days}}d overdue',
  'dashboard.deadlines.untitledTask': 'Untitled Task',

  // Dashboard tasks section
  'dashboard.sections.myTasks': 'My Tasks',
  'dashboard.sections.myTasksDescription':
    'View and manage tasks assigned to you. Stay on top of follow-ups, document requests, and other action items.',
  'dashboard.tasks.viewAll': 'View All Tasks',
  'dashboard.tasks.noTasks': 'No tasks assigned to you',
  'dashboard.tasks.noTasksDescription': 'Tasks will appear here when they are assigned to you.',

  // Dashboard quick actions
  'dashboard.actions.chatWithAstor': 'Chat with Astor',
  'dashboard.actions.uploadDocument': 'Upload Document',

  // Dashboard hint titles
  'dashboard.hints.pipelineOverview': 'Pipeline Overview',
  'dashboard.hints.performanceAnalytics': 'Performance Analytics',
  'dashboard.hints.taskManagement': 'Task Management',

  // Dashboard Data Room widget (Issue #873)
  'dashboard.dataRoom.title': 'Data Room Status',
  'dashboard.dataRoom.complete': 'All documents collected',
  'dashboard.dataRoom.pending': '{{count}} documents pending',
  'dashboard.dataRoom.documentsLinked': '{{count}} linked',
  'dashboard.dataRoom.noActiveCases': 'No active loans with data room documents',
  'dashboard.dataRoom.viewCase': 'View Loan',
  'dashboard.dataRoom.viewAll': 'View All Loans',

  // Consumer portal dashboard
  'dashboard.activeCases': 'Active Loans',
  'dashboard.pendingActions': 'Pending Actions',
  'dashboard.recentCases': 'Recent Loans',

  // Loans (display as "Loans" per baseline parity — backend model stays "Case")
  'cases.submitNew': 'Submit New Loan',
  'cases.empty': 'No loans yet. Submit a new request to get started.',
  'cases.caseNumber': 'Loan Number',
  'cases.deleteCase': 'Delete Loan',
  'cases.deleteConfirmation': 'Are you sure you want to delete this loan? This action cannot be undone.',
  'cases.editCase': 'Edit Loan',
  'cases.notFound': 'Loan not found',
  'cases.column.sla': 'SLA',
  'cases.unassigned': 'Unassigned',
  'cases.viewBorrower': 'View Borrower',
  'cases.loanAmount': 'Loan Amount',
  'cases.property': 'Property',

  // Loans — ContextRail consolidated activity feed (Issue #1647)
  // Action-slug registry. Slugs are normalised lower-snake — see
  // `normaliseActionSlug` in `consolidatedActivity.ts`. Helpers fall back
  // to title-cased English when a key isn't in the dictionary, so this
  // registry is *additive*: only the actions that benefit from a custom
  // phrasing (or non-English locales) need entries here. Missing keys do
  // NOT break the UI.
  // Slugs are sourced from `event.actionName` (PR #1650 review thread
  // #3213086620). The gateway emits `case.create`/`case.update`/
  // `case.delete` (present-tense, dot-separated) → `normaliseActionSlug`
  // collapses dots to underscores → lookup key is the present-tense
  // form below. The past-tense `case_created`/`case_updated`/
  // `case_deleted` keys were dead code; no emitter ever produced them.
  'cases.activity.action.case_create': 'Loan created',
  'cases.activity.action.case_update': 'Loan updated',
  'cases.activity.action.case_delete': 'Loan deleted',
  'cases.activity.action.case_status_change': 'Status changed',
  // `entity.*` slugs come from `createCrudAuditService` running on the
  // entity-framework service.
  'cases.activity.action.entity_create': 'Entity created',
  'cases.activity.action.entity_update': 'Entity updated',
  'cases.activity.action.entity_delete': 'Entity removed',
  // `document.*` / `consumer.*` / `apikey.*` slugs come from per-feature
  // emitters in the API gateway business router (`document.delete`,
  // `document.rename`, `document.approve`, `document.reject`,
  // `consumer.create`, `consumer.invite_sent`, `apikey.create`,
  // `apikey.revoke`, `apikey.rotate`, `invite.resend`).
  'cases.activity.action.document_create': 'Document added',
  'cases.activity.action.document_delete': 'Document deleted',
  'cases.activity.action.document_rename': 'Document renamed',
  'cases.activity.action.document_approve': 'Document approved',
  'cases.activity.action.document_reject': 'Document rejected',
  'cases.activity.action.consumer_create': 'Borrower added',
  'cases.activity.action.consumer_invite_sent': 'Borrower invited',
  'cases.activity.action.apikey_create': 'API key created',
  'cases.activity.action.apikey_revoke': 'API key revoked',
  'cases.activity.action.apikey_rotate': 'API key rotated',
  'cases.activity.action.invite_resend': 'Invite resent',
  'cases.activity.action.case_stage_changed': 'Stage changed',
  'cases.activity.action.case_status_changed': 'Status changed',
  'cases.activity.action.stage_changed': 'Stage changed',
  'cases.activity.action.status_changed': 'Status changed',
  'cases.activity.action.loan_status_changed': 'Loan status changed',
  'cases.activity.action.transitioned': 'Stage transitioned',
  'cases.activity.action.assigned': 'Assigned',
  'cases.activity.action.unassigned': 'Unassigned',
  'cases.activity.action.case_assigned': 'Loan assigned',
  'cases.activity.action.case_unassigned': 'Loan unassigned',
  'cases.activity.action.borrower_assigned': 'Borrower assigned',
  'cases.activity.action.borrower_unassigned': 'Borrower unassigned',
  'cases.activity.action.partner_assigned': 'Partner assigned',
  'cases.activity.action.partner_unassigned': 'Partner unassigned',
  'cases.activity.action.commented': 'Commented',
  'cases.activity.action.note_added': 'Note added',
  'cases.activity.action.message_sent': 'Message sent',
  'cases.activity.action.case_commented': 'Comment added',
  'cases.activity.action.case_note_added': 'Note added',
  'cases.activity.action.document_uploaded': 'Document uploaded',
  'cases.activity.action.document_deleted': 'Document deleted',
  'cases.activity.action.file_uploaded': 'File uploaded',
  'cases.activity.action.case_document_uploaded': 'Document uploaded',
  'cases.activity.action.borrower_created': 'Borrower added',
  'cases.activity.action.borrower_added': 'Borrower added',
  'cases.activity.action.borrower_removed': 'Borrower removed',
  'cases.activity.action.borrower_deleted': 'Borrower deleted',
  'cases.activity.action.borrower_updated': 'Borrower updated',
  'cases.activity.action.loan_stage_transition_created': 'Stage transition created',
  'cases.activity.action.loan_stage_transition_deleted': 'Stage transition removed',
  'cases.activity.action.stage_permission_created': 'Stage permission added',
  'cases.activity.action.stage_permission_updated': 'Stage permission updated',
  'cases.activity.action.digital_twin_auto_bootstrapped': 'Digital twin auto-bootstrapped',
  'cases.activity.action.property_evaluator_run': 'Property evaluator ran',
  'cases.activity.action.watchlist_added': 'Added to watchlist',
  'cases.activity.action.watchlist_removed': 'Removed from watchlist',
  'cases.activity.action.created': 'Created',
  'cases.activity.action.updated': 'Updated',
  'cases.activity.action.deleted': 'Deleted',
  'cases.activity.action.edit': 'Edited',
  // Field labels in the diff renderer (PR #1650 review thread #3213120013).
  // Used by the status-shorthand and oldValue/newValue branches in
  // `extractDiffFromMetadata` when the audit metadata doesn't carry an
  // explicit field name.
  'cases.activity.field.status': 'Status',
  'cases.activity.field.value': 'Value',

  // Loans — Property analysis tab (Issue #1617)
  'cases.propertyAnalysis.tabLabel': 'Property Analysis',
  'cases.propertyAnalysis.photos.title': 'Property photos',
  // Placeholder alt-text for the four scaffolding tiles rendered before the
  // photo-upload flow lands (#3237461119).
  'cases.propertyAnalysis.photos.placeholder.front': 'Front exterior',
  'cases.propertyAnalysis.photos.placeholder.street': 'Street view',
  'cases.propertyAnalysis.photos.placeholder.kitchen': 'Kitchen',
  'cases.propertyAnalysis.photos.placeholder.bathroom': 'Bathroom',
  // Astor loan-recommendation banner meta line (#3237461098).
  'cases.astorBanner.meta': '{{done}}/{{total}} tools complete · reasoned {{age}}',
  'cases.astorBanner.age.seconds': '{{count}}s ago',
  'cases.astorBanner.age.minutes': '{{count}}m ago',
  'cases.astorBanner.age.hours': '{{count}}h ago',
  // Empty-state heading (used both as the tab's "no evaluation yet" headline
  // and as the InsightsRail empty-factor placeholder).
  'cases.propertyAnalysis.triagePending': 'No property analysis yet',
  'cases.propertyAnalysis.emptyDescription': 'Run triage to pull AVM, comparables, and risk flags for this property.',
  'cases.propertyAnalysis.runningTriage': 'Running triage…',
  'cases.propertyAnalysis.triageInsufficient': 'Not enough data — verify the address',
  // Verdict labels
  'cases.propertyAnalysis.verdict.STRONG_FIT': 'Strong fit',
  'cases.propertyAnalysis.verdict.CONDITIONAL': 'Conditional',
  'cases.propertyAnalysis.verdict.POOR_FIT': 'Poor fit',
  'cases.propertyAnalysis.verdict.INSUFFICIENT_DATA': 'Insufficient data',
  // Snapshot card
  'cases.propertyAnalysis.snapshot.title': 'Snapshot',
  'cases.propertyAnalysis.snapshot.purchasePrice': 'Purchase price',
  'cases.propertyAnalysis.snapshot.avmRange': 'AVM range',
  'cases.propertyAnalysis.snapshot.arv': 'ARV',
  'cases.propertyAnalysis.snapshot.projectedRoi': 'Projected ROI',
  'cases.propertyAnalysis.snapshot.confidence': 'confidence {{percent}}%',
  'cases.propertyAnalysis.snapshot.lastRun': 'last run {{timestamp}}',
  'cases.propertyAnalysis.snapshot.phase2Pending': '— (Phase 2)',
  'cases.propertyAnalysis.snapshot.empty': '—',
  // Snapshot redesign — full lender-grade vision (#1623 Phase 2 visuals).
  // Confidence pill (replaces the verdict chip — verdict is now derived
  // from the recommendation banner). Low/medium/high/insufficient mirror
  // the model-1 confidence bucket.
  'cases.propertyAnalysis.snapshot.confidencePill.high': 'High confidence',
  'cases.propertyAnalysis.snapshot.confidencePill.medium': 'Medium confidence',
  'cases.propertyAnalysis.snapshot.confidencePill.low': 'Low confidence',
  'cases.propertyAnalysis.snapshot.confidencePill.insufficient': 'Insufficient data',
  'cases.propertyAnalysis.snapshot.reportId': 'report {{id}}',
  'cases.propertyAnalysis.snapshot.exportPdf': 'Export PDF',
  'cases.propertyAnalysis.snapshot.rerunAnalysis': 'Re-run analysis',
  // Stat strip — labels + sub captions for the 5-stat snapshot bar.
  'cases.propertyAnalysis.snapshot.statAsIsValue': 'As-is value',
  'cases.propertyAnalysis.snapshot.statArvAtTarget': 'ARV at target',
  'cases.propertyAnalysis.snapshot.statRecommendedList': 'Recommended list',
  'cases.propertyAnalysis.snapshot.statNetRoi': 'Net ROI potential',
  'cases.propertyAnalysis.snapshot.statSubject': 'Subject',
  'cases.propertyAnalysis.snapshot.statAsIsValueSub': '{{lo}} – {{hi}} · ±{{pct}}%',
  'cases.propertyAnalysis.snapshot.statArvSub': '{{condition}} · +{{liftPct}}% lift',
  'cases.propertyAnalysis.snapshot.statListSub': 'ARV +{{pct}}% · {{dom}} day P50 DOM',
  'cases.propertyAnalysis.snapshot.statNetRoiSub': 'if acquired {{pct}}% below as-is',
  'cases.propertyAnalysis.snapshot.statSubjectSub': 'ceiling {{ceiling}} · target {{target}}',
  'cases.propertyAnalysis.snapshot.modelsComplete': '{{n}} of {{total}} models complete',
  'cases.propertyAnalysis.snapshot.fnmCompliant': 'FNM 1004 / UAD compliant',
  // Comparables card
  'cases.propertyAnalysis.comparables.title': 'Comparables',
  'cases.propertyAnalysis.comparables.autoPulled': 'Auto-pulled comps',
  'cases.propertyAnalysis.comparables.manualEntry': 'Manual comparables',
  'cases.propertyAnalysis.comparables.saleTab': 'Sale',
  'cases.propertyAnalysis.comparables.rentTab': 'Rent',
  'cases.propertyAnalysis.comparables.noResults': 'No comparables found',
  'cases.propertyAnalysis.comparables.modelTag': 'Model 2 · weighted reconciled',
  'cases.propertyAnalysis.comparables.reconciledLabel': 'Weighted reconciled · As-is',
  'cases.propertyAnalysis.comparables.reconciledBand': '80% band · {{lo}} — {{hi}}',
  'cases.propertyAnalysis.comparables.col.comp': 'Comp',
  'cases.propertyAnalysis.comparables.col.distSold': 'Dist · sold',
  'cases.propertyAnalysis.comparables.col.bdBa': 'Bd / ba',
  'cases.propertyAnalysis.comparables.col.sqft': 'SqFt',
  'cases.propertyAnalysis.comparables.col.cq': 'C/Q',
  'cases.propertyAnalysis.comparables.col.adjusted': 'Adjusted',
  'cases.propertyAnalysis.comparables.col.weight': 'Weight',
  'cases.propertyAnalysis.comparables.adj.feature': 'Feature',
  'cases.propertyAnalysis.comparables.adj.subject': 'Subject',
  'cases.propertyAnalysis.comparables.adj.comp': 'Comp',
  'cases.propertyAnalysis.comparables.adj.adjustment': 'Adjustment',
  'cases.propertyAnalysis.comparables.adj.adjustedSalePrice': 'Adjusted sale price',
  'cases.propertyAnalysis.comparables.meta.source': 'Source',
  'cases.propertyAnalysis.comparables.meta.shapTopDriver': 'SHAP top driver',
  'cases.propertyAnalysis.comparables.meta.fnmCompliant': 'Within FNM 1004 caps',
  'cases.propertyAnalysis.comparables.meta.yes': 'Yes',
  'cases.propertyAnalysis.comparables.meta.no': 'No',
  // Risk flags card
  'cases.propertyAnalysis.riskFlags.title': 'Risk flags & recommendation',
  'cases.propertyAnalysis.riskFlags.resolve': 'Resolve before committing',
  'cases.propertyAnalysis.riskFlags.negotiate': 'Negotiate or price into offer',
  'cases.propertyAnalysis.riskFlags.accept': 'Accept',
  'cases.propertyAnalysis.riskFlags.linkedTask': 'View task',
  'cases.propertyAnalysis.riskFlags.signalsHeader': '{{count}} signals · {{unresolved}} unresolved',
  'cases.propertyAnalysis.riskFlags.allSignals': 'All signals',
  'cases.propertyAnalysis.recommendation.label': 'Recommendation',
  'cases.propertyAnalysis.recommendation.acceptHeadline': 'Accept — at {{price}} or below',
  'cases.propertyAnalysis.recommendation.negotiateHeadline': 'Negotiate — close at {{price}} or below',
  'cases.propertyAnalysis.recommendation.rejectHeadline': 'Reject — pass at the current acquisition price',
  'cases.propertyAnalysis.recommendation.breakdownArv': 'ARV at target',
  'cases.propertyAnalysis.recommendation.breakdownAcquisition': 'Acquisition',
  'cases.propertyAnalysis.recommendation.breakdownRehab': 'Rehab cost',
  'cases.propertyAnalysis.recommendation.breakdownHolding': 'Holding · 60d',
  'cases.propertyAnalysis.recommendation.breakdownSelling': 'Selling · 6% + closing',
  'cases.propertyAnalysis.recommendation.breakdownNet': 'Net profit',
  // Market snapshot card
  'cases.propertyAnalysis.marketSnapshot.title': 'Market snapshot',
  'cases.propertyAnalysis.marketSnapshot.medianSalePrice': 'Median sale price',
  'cases.propertyAnalysis.marketSnapshot.medianDaysOnMarket': 'Median days on market',
  'cases.propertyAnalysis.marketSnapshot.sixMonthChange': '6-month price change',
  'cases.propertyAnalysis.marketSnapshot.activeListings': 'Active listings',
  'cases.propertyAnalysis.marketSnapshot.saleToList': 'Sale-to-list ratio',
  'cases.propertyAnalysis.marketSnapshot.monthsOfSupply': 'Months of supply',
  'cases.propertyAnalysis.marketSnapshot.distributionTitle': 'Price distribution',
  'cases.propertyAnalysis.marketSnapshot.distributionSubject': 'Subject',
  'cases.propertyAnalysis.marketSnapshot.distributionCaption':
    'Subject ARV {{arv}} sits in the {{lowerK}}–{{upperK}} bin.',
  // Subject facts header — beds/baths/sqft/year/lot under the page title.
  // Each value renders only when the SFR provider returned it; missing
  // facts surface as the muted empty marker.
  'cases.propertyAnalysis.subject.beds': '{{count}} bed',
  'cases.propertyAnalysis.subject.baths': '{{count}} bath',
  'cases.propertyAnalysis.subject.sqft': '{{sqft}} sqft',
  'cases.propertyAnalysis.subject.builtYear': 'Built {{year}}',
  'cases.propertyAnalysis.subject.lotAcres': '{{acres}} acre lot',
  'cases.propertyAnalysis.subject.neighborhoodPill': 'Neighborhood',
  'cases.propertyAnalysis.subject.conditionPill': 'Current condition',
  'cases.propertyAnalysis.subject.qualityPill': 'Current quality',

  // UAD condition + quality scale section (Model 1).
  'cases.propertyAnalysis.uad.title': 'UAD condition & quality rating',
  'cases.propertyAnalysis.uad.subtitle':
    'Subject property rating per FNM 1004 / FHLMC UAD standards. C and Q scores are the foundation for adjustment modeling and ARV targeting.',
  'cases.propertyAnalysis.uad.modelConfidence': 'Model 1 confidence',
  'cases.propertyAnalysis.uad.conditionAxis': 'Condition',
  'cases.propertyAnalysis.uad.qualityAxis': 'Quality',
  'cases.propertyAnalysis.uad.subjectMarker': 'Subject',
  'cases.propertyAnalysis.uad.ceilingMarker': 'Ceiling',
  'cases.propertyAnalysis.uad.targetMarker': 'Target',
  'cases.propertyAnalysis.uad.combinedCeilingTargetMarker': 'Ceiling · Target',
  'cases.propertyAnalysis.uad.modelTag': 'Model 1',
  'cases.propertyAnalysis.uad.summary':
    'Subject scored {{subject}}. Neighborhood supports up to {{ceiling}}. ROI-optimal renovation target is {{target}} — condition lift captures most of the available margin without overbuilding the neighborhood.',
  // C1-C6 / Q1-Q6 step descriptions — mirror FNM 1004 verbatim so the
  // labels read as the canonical UAD scale, not a paraphrase.
  'cases.propertyAnalysis.uad.condition.C1': 'New / never occupied',
  'cases.propertyAnalysis.uad.condition.C2': 'No deferred maintenance',
  'cases.propertyAnalysis.uad.condition.C3': 'Well maintained',
  'cases.propertyAnalysis.uad.condition.C4': 'Adequate, some wear',
  'cases.propertyAnalysis.uad.condition.C5': 'Obvious deferred maintenance',
  'cases.propertyAnalysis.uad.condition.C6': 'Substantial repair needed',
  'cases.propertyAnalysis.uad.quality.Q1': 'Luxury, custom',
  'cases.propertyAnalysis.uad.quality.Q2': 'High-end finishes',
  'cases.propertyAnalysis.uad.quality.Q3': 'Above-average finishes',
  'cases.propertyAnalysis.uad.quality.Q4': 'Standard builder-grade',
  'cases.propertyAnalysis.uad.quality.Q5': 'Low-end, economy',
  'cases.propertyAnalysis.uad.quality.Q6': 'Substandard',

  // ROI breakdown table (page 3 of the AVM report).
  'cases.propertyAnalysis.roi.title': 'ROI breakdown',
  'cases.propertyAnalysis.roi.arvAtTarget': 'ARV at target',
  'cases.propertyAnalysis.roi.acquisition': 'Acquisition (at as-is fair value)',
  'cases.propertyAnalysis.roi.rehabCost': 'Rehab cost',
  'cases.propertyAnalysis.roi.holdingCost': 'Holding cost · 60 days',
  'cases.propertyAnalysis.roi.sellingCost': 'Selling cost (6% + closing)',
  'cases.propertyAnalysis.roi.netProfitFair': 'Net profit (if acquired at fair value)',
  // Discount-acquisition row label is dynamic — uses the discount fraction
  // returned by the backend so future changes (e.g. 8% discount) flow
  // through without a translation update.
  'cases.propertyAnalysis.roi.netProfitDiscount': 'If acquired at {{percent}}% below as-is',

  // ROI Strategy section (Models 3 + 4 — renovation marginal-value curve).
  'cases.propertyAnalysis.renovationStrategy.title': 'Renovation strategy',
  'cases.propertyAnalysis.renovationStrategy.modelTag': 'Models 3 + 4',
  'cases.propertyAnalysis.renovationStrategy.subtitle':
    'Marginal value per C/Q step, net of rehab cost. ROI peaks at the target scenario; pushing past the ceiling burns margin.',
  'cases.propertyAnalysis.renovationStrategy.legendValueAdded': 'Value added',
  'cases.propertyAnalysis.renovationStrategy.legendRehabCost': 'Rehab cost',
  'cases.propertyAnalysis.renovationStrategy.legendNet': 'Net',
  'cases.propertyAnalysis.renovationStrategy.targetBadge': 'Target',
  'cases.propertyAnalysis.renovationStrategy.ceilingBadge': 'Ceiling',
  'cases.propertyAnalysis.renovationStrategy.overbuildBadge': 'Overbuild',
  'cases.propertyAnalysis.renovationStrategy.peakBadge': 'ROI peak',
  'cases.propertyAnalysis.renovationStrategy.targetPanel.title': 'Recommended target',
  'cases.propertyAnalysis.renovationStrategy.targetPanel.subtitle':
    'Condition lift + selective Q upgrade — capture margin without overbuilding the neighborhood.',
  'cases.propertyAnalysis.renovationStrategy.targetPanel.deltaLabel': 'vs. pushing to ceiling',
  'cases.propertyAnalysis.renovationStrategy.targetPanel.deltaValue': '+{{amount}} better net',
  'cases.propertyAnalysis.renovationStrategy.targetPanel.deltaExplanation':
    'Pushing past the target adds value but costs more — the marginal return goes negative at the ceiling.',
  'cases.propertyAnalysis.renovationStrategy.empty':
    'Renovation scenarios are not available — run a full memo to compute the marginal-value curve.',
  'cases.propertyAnalysis.renovationStrategy.netPill': 'Net {{amount}}',
  'cases.propertyAnalysis.renovationStrategy.scopeHeader': 'Rehab scope · {{total}}',
  // DOM analysis section (page 3 — Model 5).
  'cases.propertyAnalysis.dom.title': 'Time-to-sale analysis',
  'cases.propertyAnalysis.dom.modelTag': 'Model 5',
  'cases.propertyAnalysis.dom.subtitle':
    'Recommended list price + expected days on market. Conditional on the list price being fair relative to our valuation.',
  'cases.propertyAnalysis.dom.recommendedListPrice': 'Recommended list',
  'cases.propertyAnalysis.dom.medianDom': 'Median DOM',
  'cases.propertyAnalysis.dom.p25p75': 'P25 / P75',
  'cases.propertyAnalysis.dom.byDayLabel': 'contract by day {{days}}',
  'cases.propertyAnalysis.dom.curveAxisLabel': 'Price ↑ vs expected DOM',
  'cases.propertyAnalysis.dom.velocityCollapseLabel': 'Velocity collapse →',
  'cases.propertyAnalysis.dom.daysValue': '{{count}} days',
  'cases.propertyAnalysis.dom.p25p75Value': '{{p25}} / {{p75}} days',
  'cases.propertyAnalysis.dom.p50AtRecommended': 'P50 DOM at recommended list',
  'cases.propertyAnalysis.dom.percentileSummary': 'P25 {{p25}} · P75 {{p75}} · P90 {{p90}}',
  'cases.propertyAnalysis.dom.disclosure':
    'DOM predictions are conditional on the list price being fair relative to our valuation. Each $10k of valuation error shifts expected DOM by ~5 days in backtest.',

  // Actions
  'cases.propertyAnalysis.actions.runFullAnalysis': 'Run full analysis',
  // First-run CTA shown when no evaluation row exists for the case yet.
  // Distinct from `runTriage` (re-run on an existing row) so the empty
  // state isn't mislabeled as "Re-run".
  'cases.propertyAnalysis.actions.runTriageFirst': 'Run triage',
  'cases.propertyAnalysis.actions.runTriage': 'Re-run triage',
  'cases.propertyAnalysis.actions.editAssumptions': 'Edit assumptions',
  // Strategies card
  'cases.propertyAnalysis.strategies.title': 'Strategies',
  'cases.propertyAnalysis.strategies.flipTab': 'Fix & flip',
  'cases.propertyAnalysis.strategies.rentalHoldTab': 'Rental hold',
  'cases.propertyAnalysis.strategies.profit': 'Projected profit',
  'cases.propertyAnalysis.strategies.cashFlow': 'Monthly cash flow',
  'cases.propertyAnalysis.strategies.maxOffer': 'Max allowable offer',
  'cases.propertyAnalysis.strategies.capRate': 'Cap rate',
  'cases.propertyAnalysis.strategies.dscr': 'DSCR',
  // Rehab card
  'cases.propertyAnalysis.rehab.title': 'Rehab scope',
  'cases.propertyAnalysis.rehab.category': 'Category',
  'cases.propertyAnalysis.rehab.scope': 'Scope',
  'cases.propertyAnalysis.rehab.low': 'Low',
  'cases.propertyAnalysis.rehab.mid': 'Mid',
  'cases.propertyAnalysis.rehab.high': 'High',
  'cases.propertyAnalysis.rehab.contingency': 'Contingency',
  'cases.propertyAnalysis.rehab.total': 'Total',
  // Narrative card
  'cases.propertyAnalysis.narrative.title': 'Executive summary',
  // Errors
  'cases.propertyAnalysis.errors.triageFailed': 'Triage failed — re-run to retry',
  // Surfaced inline in the empty-state card when the runTriage mutation
  // itself errors (e.g. missing PROPERTY_EVALUATOR twin, downstream RPC
  // outage). Distinct from `triageFailed` which is the full-page banner
  // shown when `byCase` itself fails.
  'cases.propertyAnalysis.errors.triageRunFailed': 'Triage failed: {{message}}',
  'cases.propertyAnalysis.errors.fullMemoFailed': 'Full analysis failed — re-run to retry',

  // Phase 2 (#1623) — strategies tabs (Flip / Rental hold)
  'cases.propertyAnalysis.strategies.tabs.flip': 'Flip',
  'cases.propertyAnalysis.strategies.tabs.rentalHold': 'Rental hold',
  'cases.propertyAnalysis.strategies.flip.roi': 'ROI',
  'cases.propertyAnalysis.strategies.flip.profit': 'Profit',
  'cases.propertyAnalysis.strategies.flip.holdMonths': 'Hold (months)',
  'cases.propertyAnalysis.strategies.flip.mao': 'MAO',
  'cases.propertyAnalysis.strategies.rentalHold.cashFlowMonthly': 'Cash flow / mo',
  'cases.propertyAnalysis.strategies.rentalHold.capRate': 'Cap rate',
  'cases.propertyAnalysis.strategies.rentalHold.cashOnCash': 'Cash-on-cash',
  'cases.propertyAnalysis.strategies.rentalHold.dscr': 'DSCR',
  'cases.propertyAnalysis.strategies.rentalHold.maxPurchase': 'Max purchase',
  'cases.propertyAnalysis.strategies.emptyState': 'Run full analysis to see strategies + rehab scope',
  // Phase 2 (#1623) — rehab scope (editable Mid via MoneyField)
  'cases.propertyAnalysis.rehabScope.title': 'Rehab scope',
  'cases.propertyAnalysis.rehabScope.column.category': 'Category',
  'cases.propertyAnalysis.rehabScope.column.scope': 'Scope',
  'cases.propertyAnalysis.rehabScope.column.low': 'Low',
  'cases.propertyAnalysis.rehabScope.column.mid': 'Mid',
  'cases.propertyAnalysis.rehabScope.column.high': 'High',
  'cases.propertyAnalysis.rehabScope.contingency': 'Contingency',
  'cases.propertyAnalysis.rehabScope.total': 'Total',
  'cases.propertyAnalysis.rehabScope.category.GENERAL': 'General',
  'cases.propertyAnalysis.rehabScope.category.PAINT_DRYWALL': 'Paint / drywall',
  'cases.propertyAnalysis.rehabScope.category.FLOORING': 'Flooring',
  'cases.propertyAnalysis.rehabScope.category.KITCHEN': 'Kitchen',
  'cases.propertyAnalysis.rehabScope.category.BATHROOM': 'Bathroom',
  'cases.propertyAnalysis.rehabScope.category.HVAC': 'HVAC',
  'cases.propertyAnalysis.rehabScope.category.EXTERIOR': 'Exterior',
  // Phase 2 (#1623) — Run-full-analysis button surface on snapshot
  'cases.propertyAnalysis.snapshot.runFullAnalysis': 'Run full analysis',
  'cases.propertyAnalysis.snapshot.runFullAnalysisRunning': 'Running…',

  // #1628 item 5 — InsightsRail dedicated keys (Phase 2J follow-up)
  'cases.propertyAnalysis.insightsRail.groupTitle': 'Property snapshot',
  'cases.propertyAnalysis.insightsRail.emptyState': 'No property factors yet — run triage to populate',
  'cases.propertyAnalysis.factor.ARV_CONFIDENCE': 'ARV confidence',
  'cases.propertyAnalysis.factor.COMP_QUALITY': 'Comp quality',
  'cases.propertyAnalysis.factor.MARKET_TREND': 'Market trend',
  'cases.propertyAnalysis.factor.PRICE_VS_AVM': 'Price vs AVM',
  'cases.propertyAnalysis.factor.REHAB_RISK_BUDGET': 'Rehab budget risk',
  'cases.propertyAnalysis.factor.CONDITION_RISK': 'Condition risk',
  'cases.propertyAnalysis.factor.OCCUPANCY_RISK': 'Occupancy risk',
  'cases.propertyAnalysis.factor.ZONING_FIT': 'Zoning fit',
  'cases.propertyAnalysis.factor.FLOOD_RISK': 'Flood risk',
  'cases.propertyAnalysis.factor.DOM_RISK': 'Days-on-market risk',

  // Phase 2 (#1623) — Consumer portal: borrower's read-only property view
  'consumer.propertyAnalysis.tabLabel': 'My property',
  'consumer.propertyAnalysis.avmRange.title': 'Estimated value range',
  'consumer.propertyAnalysis.avmRange.purchasePrice': 'Your purchase price',
  'consumer.propertyAnalysis.comparables.title': 'Recent comparable sales',
  'consumer.propertyAnalysis.comparables.empty': 'No comparable sales available yet',
  'consumer.propertyAnalysis.comparables.address': 'Address',
  'consumer.propertyAnalysis.comparables.bedsBaths': 'Beds / Baths',
  'consumer.propertyAnalysis.comparables.sqft': 'Sq ft',
  'consumer.propertyAnalysis.comparables.price': 'Sale price',
  'consumer.propertyAnalysis.comparables.distance': 'Distance',
  'consumer.propertyAnalysis.market.title': 'Local market',
  'consumer.propertyAnalysis.market.medianSalePrice': 'Median sale price',
  'consumer.propertyAnalysis.market.medianDaysOnMarket': 'Median days on market',
  'consumer.propertyAnalysis.market.sixMonthChange': '6-month price change',
  'consumer.propertyAnalysis.itemsToAddress.title': 'Items to address',
  'consumer.propertyAnalysis.itemsToAddress.empty': 'No items to address',
  'consumer.propertyAnalysis.errors.loadFailed': "We couldn't load your property analysis right now.",

  // Documents
  'documents.title': 'Documents',
  'documents.upload': 'Upload Documents',
  'documents.dragDrop': 'Drag & drop files here',
  'documents.browseFiles': 'or click to browse (PDF, DOC, Images)',
  'documents.maxSize': 'Maximum 10MB per file',
  'documents.category': 'Category',
  'documents.yourDocuments': 'Your Documents ({{count}})',
  'documents.uploadMore': 'Upload More',
  'documents.selectCase': 'Select Loan',
  'documents.caseLabel': '{{caseNumber}} — {{title}}',
  'documents.linkToCase.optional': 'Link to loan (optional)',
  'documents.linkToCase.select': 'Select a loan to link this document',
  'documents.linkToCase.action': 'Link to Loan',
  'documents.linkedToCases': 'Linked to {{count}} loan(s)',
  'documents.classification.documentType': 'Document Type',
  'documents.noCase.info': 'You can upload documents anytime. Link them to a loan application when ready.',
  'documents.caseLink.verified': 'Verified',
  'documents.caseLink.pending': 'Pending Review',
  'documents.caseLink.rejected': 'Rejected',
  'documents.caseLink.needsReupload': 'Needs Re-upload',

  // Document Detail Page (Issue #872 - Link to Case)
  'documents.linkToCase': 'Link to Loan',
  'documents.linkedToCase': 'Linked to loan',
  'documents.linkSuccess': 'Document linked to loan successfully',
  'documents.untitled': 'Untitled Document',
  'documents.documentInfo': 'Document Information',
  'documents.fileName': 'File Name',
  'documents.fileType': 'File Type',
  'documents.fileSize': 'File Size',
  'documents.uploaded': 'Uploaded',
  'documents.processingStatus': 'Processing Status',
  'documents.currentStatus': 'Current Status',
  'documents.statusProcessing': 'Your document is being processed. This may take a few minutes.',
  'documents.statusVerified': 'Your document has been verified successfully.',
  'documents.statusRejected': 'Your document was not accepted. Please upload a new version.',
  'documents.statusFailed': 'There was a problem processing your document. Please try uploading again.',

  // Document Categories (Issue #475 - i18n migration)
  'documents.category.identity': 'Identity Document',
  'documents.category.income': 'Proof of Income',
  'documents.category.address': 'Proof of Address',
  'documents.category.bankStatement': 'Bank Statement',
  'documents.category.other': 'Other',
  'documents.category.personal': 'Personal Documents',
  'documents.category.property': 'Property Documents',
  'documents.category.financial': 'Financial Documents',
  'documents.category.legal': 'Legal Documents',
  'documents.category.loan': 'Loan Documents',
  'documents.category.authorization': 'Authorization Documents',

  // Consumer Document Redesign — case-independent uploads
  'documents.documentType': 'Document Type',
  'documents.caseLinks': 'Linked Loans',
  'documents.noCasesInfo':
    'You have documents uploaded but no active cases yet. Your documents will be available when a case is created.',
  'documents.uploadDialog.linkToCase': 'Link to loan (optional)',

  // Document Status Options (Issue #475 - i18n migration)
  'documents.status.allStatuses': 'All Statuses',
  'documents.status.pendingReview': 'Pending Review',
  'documents.status.verified': 'Verified',
  'documents.status.rejected': 'Rejected',
  'documents.status.processing': 'Processing',
  'documents.status.uploaded': 'Uploaded',
  'documents.status.approved': 'Approved',
  'documents.status.notRequired': 'Not Required',
  'documents.status.missing': 'Missing',
  'documents.status.pending': 'Pending Review',

  // Document Requirement Types (Consumer Portal)
  'documents.requirement.identity': 'Government ID',
  'documents.requirement.bankStatement': 'Bank Statements',
  'documents.requirement.income': 'Proof of Income',
  'documents.requirement.creditReport': 'Credit Report',
  'documents.requirement.taxReturns': 'Tax Returns',

  // Document Upload Dialog (Issue #475 - i18n migration)
  'documents.uploadDialog.title': 'Upload Documents',
  'documents.uploadDialog.filesSelected': '{{count}} file(s) selected',
  'documents.uploadDialog.selectCategory': 'Document Category',
  'documents.uploadDialog.uploading': 'Uploading...',
  'documents.uploadDialog.uploadFiles': 'Upload {{count}} File(s)',
  'documents.uploadCategoryDescription': 'Category: {{category}}',

  // Document Upload Card (PR #1543 review feedback — i18n for the inline
  // GlassCard upload surface at components/documents/DocumentUpload.tsx).
  // Distinct from documents.uploadDialog.* because the card is a different
  // surface with its own copy (single-file, AI-processing-specific hint).
  'documents.uploadCard.title': 'Upload Document',
  'documents.uploadCard.description': 'Upload PDFs, text files, or documents for AI processing',
  'documents.uploadCard.uploading': 'Uploading and processing document...',
  'documents.uploadCard.submit': 'Upload Document',

  // Document Empty States (Issue #475 - i18n migration)
  'documents.empty.noMatch': 'No documents match your filters.',
  'documents.empty.noDocuments': 'No documents uploaded yet.',
  'documents.empty.noRequired':
    'No documents required yet. Document requirements will appear once your loan type is determined.',
  'documents.empty.noExtracted': 'No extracted fields available for this document.',
  'documents.checklist.required': '{{uploaded}}/{{required}} required',

  // Document Preview (Issue #475 - i18n migration)
  'documents.preview.title': 'Document Preview',
  'documents.preview.loading': 'Loading preview...',
  'documents.preview.notAvailable': 'Preview not available for this file type.',
  'documents.preview.downloadToView': 'Download to View',
  // Document View Page (#1163)
  'documents.view.title': 'Document View',
  'documents.view.noContent': 'Document content unavailable.',
  'documents.view.unsupported': 'This file type cannot be previewed inline.',
  'documents.view.downloadToView': 'Download to View',
  'documents.view.tabDocument': 'Document',
  'documents.view.tabDetails': 'Details',
  'documents.view.download': 'Download',
  'documents.view.delete': 'Delete',
  'documents.view.approve': 'Approve',
  'documents.view.reject': 'Reject',
  'documents.view.rename': 'Rename',
  'documents.view.reprocess': 'Reprocess',
  'documents.view.linkToCase': 'Link to Loan',
  'documents.view.linkSuccess': 'Document linked to loan successfully',
  'documents.view.reupload': 'Upload New Version',
  'documents.view.noCases': 'No loans available to link',
  'documents.view.backToDocuments': 'Documents',

  // Document Attributes Panel (#1163)
  'documents.attributes.title': 'Details',
  'documents.attributes.name': 'File Name',
  'documents.attributes.type': 'MIME Type',
  'documents.attributes.size': 'File Size',
  'documents.attributes.uploaded': 'Uploaded',
  'documents.attributes.lastUpdated': 'Last Updated',
  'documents.attributes.category': 'Category',
  'documents.attributes.documentType': 'Document Type',
  'documents.attributes.gcsPath': 'Storage Path',
  'documents.attributes.chunkCount': 'Chunk Count',
  'documents.attributes.documentId': 'Document ID',
  'documents.attributes.rawMetadata': 'Raw Metadata',

  // Document Sections (#1163)
  'documents.sections.fileInfo': 'File Information',
  'documents.sections.status': 'Status',
  'documents.sections.aiFindings': 'AI Findings',
  'documents.sections.linkedCases': 'Linked Loans',
  'documents.sections.tags': 'Tags',
  'documents.sections.storage': 'Storage & Metadata',
  'documents.findings.none': 'No extraction findings available.',
  'documents.findings.defaultTitle': 'Finding',
  'documents.caseLinks.count': 'Used in {{count}} loan(s)',

  'documents.preview.extractedFields': 'Extracted Fields',
  'documents.preview.extractedFieldsDesc':
    'AI-extracted information from this document. Confidence indicators show reliability of each extraction.',
  'documents.preview.verifyInfo':
    'Please verify all extracted information is correct. You can edit any fields that need correction.',

  // Rejection Banner (Issue #475 - i18n migration)
  'documents.rejection.reason': 'Reason: {{reason}}',
  'documents.rejection.uploadCorrected': 'Please upload a corrected document.',
  'documents.rejection.reupload': 'Re-upload Document',

  // Re-upload Request Banner (data room verification lifecycle)
  'documents.reuploadRequest.reason': 'Re-upload requested: {{reason}}',
  'documents.reuploadRequest.uploadNewVersion': 'Your team has requested an updated version of this document.',
  'documents.reuploadRequest.reupload': 'Upload New Version',

  // Document Management (Issue #948 - doc-mgmt gaps)
  'document.renameTitle': 'Rename Document',
  'document.newName': 'New Name',
  'document.deleteWarningLinked':
    'This document is linked to {{count}} entity/entities. Deleting it will remove all links.',
  'document.caseLinkCount': 'Used in {{count}} loan(s)',
  'document.rejectionReason': 'Rejection Reason',
  'document.deleteTitle': 'Delete Document',
  'document.deleteConfirmMessage': 'Are you sure you want to delete "{{name}}"? This cannot be undone.',
  'document.rejectTitle': 'Reject Document',
  'document.rejectMessage': 'Please provide a reason for rejecting this document.',
  'document.rejectReasonLabel': 'Rejection Reason',
  'document.renameLabel': 'New name',

  // Data Room Linkage (Issue #875)
  'documents.usedInCases': 'Used in {{count}} loan(s)',

  // Admin Documents List (Issue #1163 - i18n gaps)
  'documents.columns.name': 'Document',
  'documents.columns.size': 'Size',
  'documents.columns.chunks': 'Chunks',
  'documents.columns.status': 'Status',
  'documents.columns.uploaded': 'Uploaded',
  'documents.columns.actions': 'Actions',
  'documents.stats.total': 'Total Documents',
  'documents.stats.processed': 'Processed',
  'documents.stats.totalChunks': 'Total Chunks',
  'documents.stats.totalSize': 'Total Size',
  'documents.actions.viewDetails': 'View Details',
  'documents.actions.reprocess': 'Reprocess',
  'documents.actions.delete': 'Delete',
  'documents.actions.deleteSuccess': 'Document deleted successfully',
  'documents.actions.reprocessStarted': 'Document reprocessing started',
  'documents.empty.noDocumentsFound': 'No documents found',
  'documents.empty.noDocumentsUploaded': 'No documents uploaded yet',
  'documents.empty.uploadFirst': 'Upload Your First Document',
  'documents.errors.loadFailed': 'Failed to load data',
  'documents.errors.deleteFailed': 'Failed to delete document',
  'documents.errors.reprocessFailed': 'Failed to reprocess document',
  'documents.errors.approveFailed': 'Failed to approve document',
  'documents.errors.rejectFailed': 'Failed to reject document',
  'documents.errors.reuploadFailed': 'Failed to re-upload document',
  'documents.errors.linkFailed': 'Failed to link document to loan',
  'documents.errors.renameFailed': 'Failed to rename document',

  // Retained Copies (Issue #400 - i18n audit)
  'documents.retainedCopies.comingSoon': 'Coming Soon',
  'documents.retainedCopies.description': 'Manage retained copies of signed documents',
  'documents.retainedCopies.placeholder': 'No retained copies yet',
  'documents.retainedCopies.title': 'Retained Copies',
  'documents.retainedCopies.accessedRecently': 'Accessed (30 days)',
  'documents.retainedCopies.columns.accessCount': 'Accessed',
  'documents.retainedCopies.accessCountValue': '{{count}} time{{plural}}',
  'documents.retainedCopies.columns.actions': 'Actions',
  'documents.retainedCopies.columns.completedAt': 'Completed',
  'documents.retainedCopies.columns.document': 'Document',
  'documents.retainedCopies.columns.envelope': 'Envelope',
  'documents.retainedCopies.columns.size': 'Size',
  'documents.retainedCopies.columns.status': 'Status',
  // Issue #1631 M2 — warehouse-line attribution column (FundCell).
  'documents.retainedCopies.columns.warehouseLine': 'Warehouse line',
  'documents.retainedCopies.loadError': 'Failed to load retained copies',
  'documents.retainedCopies.noDocuments': 'No Retained Copies',
  'documents.retainedCopies.noDocumentsHint': 'Retained copies will appear here when signing is completed',
  'documents.retainedCopies.searchPlaceholder': 'Search documents...',
  'documents.retainedCopies.totalCopies': 'Total Copies',

  // ============================================================================
  // CASE (Issue #400 - i18n audit)
  // ============================================================================
  'case.create': 'Create Loan',
  'case.yourSubmission': 'Your Submission',
  'case.noSubmissionData': 'No submission data available',

  // ============================================================================
  // LEAD (Issue #400 - i18n audit)
  // ============================================================================
  'lead.convertToCase': 'Convert to Loan',

  // ============================================================================
  // E-SIGNATURE (Issue #400 - i18n audit)
  // ============================================================================
  'esign.columns.completed': 'Completed',
  'esign.history.empty.description': 'Completed e-signature submissions will appear here.',
  'esign.history.empty.title': 'No signing history yet',
  'esign.queue.empty.description': 'Active e-signature requests will appear here.',
  'esign.queue.empty.title': 'No envelopes in the queue',
  'esign.stats.awaitingFirst': 'Awaiting first signer',
  'esign.stats.partiallySigned': 'Partially signed',
  'esign.stats.voided': 'Voided',
  'esign.builder.createTitle': 'Create Template',
  'esign.builder.description': 'Design e-signature templates with drag and drop',
  'esign.builder.editTitle': 'Edit Template',
  'esign.envelopes.title': 'E-Signature Envelopes',

  // Envelope Expiration Extension (Issue #1531 Phase 6 - admin-only deadline extension UX)
  'esign.envelopes.extend.button': 'Extend expiration',
  'esign.envelopes.extend.title': 'Extend Signing Deadline',
  'esign.envelopes.extend.dateLabel': 'New deadline',
  'esign.envelopes.extend.reasonLabel': 'Reason for extension',
  'esign.envelopes.extend.maxThirtyDays': 'Maximum 30 days from today',
  'esign.envelopes.extend.minReasonLength': 'Reason must be at least 20 characters',
  'esign.envelopes.extend.confirm': 'Confirm extension',
  'esign.envelopes.extend.cancel': 'Cancel',
  'esign.envelopes.extend.success': 'Deadline extended to {{date}}',
  'esign.envelopes.extend.error': 'Could not extend deadline: {{message}}',
  'esign.envelopes.extend.notExtendable': 'This envelope cannot be extended (status: {{status}})',

  // Document Signing Modal (Issue #475 - i18n migration)
  'esign.signing.title': 'Sign Document',
  'esign.signing.complete': 'Signing Complete',
  'esign.signing.documentLabel': 'Document: {{name}}',
  'esign.signing.signerLabel': 'Signer: {{name}}',
  'esign.signing.done': 'Done',
  'esign.signing.loading': 'Loading document...',
  'esign.signing.failed': 'Failed to load document',
  'esign.signing.failedHelp': 'Please try again or contact support.',
  'esign.signing.success': 'Document Signed Successfully',
  'esign.signing.successHelp': 'Thank you for signing. You can now close this window.',
  'esign.signing.iframeTitle': 'Document Signing',
  'esign.signing.openedInNewTab':
    'The document has been opened in a new tab. Complete signing there, then return here.',
  'esign.signing.reopenTab': 'Reopen Signing Tab',
  'esign.signingRequest': 'Signing Request #{{id}}',
  'esign.requested': 'Requested {{date}}',
  'esign.signNow': 'Sign Now',
  'esign.moreDocuments': 'and {{count}} more documents requiring signature',

  // Term Sheet actions (per-row + bulk)
  'termsheet.actions.sendToBorrower': 'Send to Borrower',

  // Term Sheet E-Sign Modal (Issue #56)
  'esign.termSheet.sendViaEmail': 'Send via Email',
  'esign.termSheet.requestEsign': 'Request E-Signature',
  'esign.termSheet.recipientEmail': 'Recipient Email',
  'esign.termSheet.recipientName': 'Recipient Name',
  'esign.termSheet.expiresInDays': 'Expires In (Days)',
  'esign.termSheet.sendForSigning': 'Send for Signing',
  'esign.termSheet.signingRequested': 'E-signature requested',
  'esign.termSheet.signingRequestedHelp': 'The recipient will receive an email with a link to sign.',
  'esign.termSheet.signingRequestFailed': 'Failed to send for e-signature',

  // Consumer Signing Page (Issue #56)
  'esign.consumer.pageTitle': 'Sign Document',
  'esign.consumer.loadingDocument': 'Loading your document...',
  'esign.consumer.signedSuccess': 'Document Signed Successfully',
  'esign.consumer.signedSuccessHelp': 'Thank you for signing. A copy has been sent to your email.',
  'esign.consumer.backToDashboard': 'Back to Dashboard',
  'esign.consumer.documentExpired': 'This signing link has expired',
  'esign.consumer.documentExpiredHelp': 'Please contact the sender to request a new signing link.',

  // Consumer Portal — Partnership Envelope Display (Issue #1531 Phase 6 - cross-business partnership signing)
  'esign.consumer.partnershipEnvelope.title': 'Partnership Agreement',
  'esign.consumer.partnershipEnvelope.fromBusiness': 'From {{businessName}}',
  'esign.consumer.partnershipEnvelope.agreementType': '{{type}}',
  'esign.consumer.partnershipEnvelope.deadline': 'Sign by {{date}}',
  'esign.consumer.partnershipEnvelope.signNow': 'Sign now',
  'esign.consumer.partnershipEnvelope.signed': 'Signed on {{date}}',
  'esign.consumer.partnershipEnvelope.expired': 'Expired',
  'esign.consumer.partnershipEnvelope.extended': 'Deadline extended to {{date}}',

  // Business E-Sign Management (Issue #56)
  'esign.management.title': 'E-Signatures',
  'esign.management.description': 'Manage document signing envelopes',
  'esign.management.totalEnvelopes': 'Total',
  'esign.management.pendingEnvelopes': 'Pending',
  'esign.management.completedEnvelopes': 'Completed',
  'esign.management.voidedEnvelopes': 'Voided',
  'esign.management.tabAll': 'All',
  'esign.management.tabDraft': 'Draft',
  'esign.management.tabSent': 'Sent',
  'esign.management.tabCompleted': 'Completed',
  'esign.management.tabVoided': 'Voided',
  'esign.management.noEnvelopes': 'No envelopes found',
  'esign.management.noEnvelopesHelp': 'Send a document for signature to get started.',
  'esign.management.voidEnvelope': 'Void Envelope',
  'esign.management.resendNotification': 'Resend Notification',
  'esign.management.downloadCopy': 'Download Signed Copy',
  'esign.management.recipientName': 'Recipient',
  'esign.management.documentName': 'Document',
  'esign.management.sentDate': 'Sent',
  'esign.management.completedDate': 'Completed',
  'esign.management.timeline': 'Timeline',

  // E-Signature columns and stats (documents/signing page)
  'esign.columns.id': 'ID',
  'esign.columns.template': 'Template',
  'esign.columns.status': 'Status',
  'esign.columns.signers': 'Signers',
  'esign.columns.created': 'Created',
  'esign.columns.actions': 'Actions',
  // Issue #1631 M2 — warehouse-line attribution column (FundCell).
  'esign.columns.warehouseLine': 'Warehouse line',
  'esign.stats.total': 'Total',
  'esign.stats.pending': 'Pending',
  'esign.stats.completed': 'Completed',
  'esign.loadError': 'Failed to load e-signature submissions',
  'esign.noSubmissions': 'No e-signature submissions yet',
  'esign.noSubmissionsHint': 'Signing requests will appear here once documents are sent out for signature.',
  'esign.resend': 'Resend',
  'esign.void': 'Void',
  'esign.notification.resent': 'Reminder sent to signer',
  'esign.notification.resendFailed': 'Failed to resend reminder',
  'esign.submission.voided': 'Submission voided',
  'esign.submission.voidFailed': 'Failed to void submission',

  // Enrichment Review Panel (Issue #525)
  'enrichment.review.title': 'Review Enrichment Data',
  'enrichment.review.description': 'Compare current data with enrichment results',
  'enrichment.review.currentValue': 'Current',
  'enrichment.review.enrichedValue': 'Enriched',
  'enrichment.review.selectFields': 'Select fields to apply',
  'enrichment.review.selectAll': 'Select All',
  'enrichment.review.deselectAll': 'Deselect All',
  'enrichment.review.applySelected': 'Apply Selected',
  'enrichment.review.noChanges': 'No new data found from enrichment',
  'enrichment.review.applying': 'Applying enrichment data...',
  'enrichment.review.applied': 'Enrichment data applied successfully',
  'enrichment.review.applyFailed': 'Failed to apply enrichment data',
  'enrichment.review.previewing': 'Previewing enrichment data...',
  'enrichment.review.previewFailed': 'Failed to preview enrichment data',

  // ============================================================================
  // AGREEMENTS (Issue #400 - i18n audit)
  // ============================================================================
  'agreements.send.comingSoon': 'Coming Soon',
  'agreements.send.description': 'Send agreements for signature',
  'agreements.send.title': 'Send Agreement',
  'agreements.send.agreement': 'Agreement',
  'agreements.send.configureSigners': 'Configure Signers',
  'agreements.send.configureSignersDesc': 'Add and configure the signers for this agreement',
  'agreements.send.noTemplates': 'No templates available',
  'agreements.send.notFound': 'Agreement not found',
  'agreements.send.reviewSend': 'Review & Send',
  'agreements.send.reviewSendDesc': 'Review the agreement details and send for signature',
  'agreements.send.selectedTemplate': 'Selected Template',
  'agreements.send.selectTemplate': 'Select Template',
  'agreements.send.selectTemplateDesc': 'Choose a template for your agreement',
  'agreements.send.sendForSignature': 'Send for Signature',
  'agreements.send.signers': 'Signers',
  'agreements.send.template': 'Template',
  'agreements.send.untitled': 'Untitled Agreement',

  // ============================================================================
  // PAGE HEADERS (Issue #400 - i18n audit)
  // ============================================================================

  // ============================================================================
  // DISCOVERY (Issue #556 - Business Portal Next.js)
  // ============================================================================
  'discovery.addToLeads': 'Add to Leads',
  'discovery.estimatedValue': 'Estimated Value',
  'discovery.highMatches': 'High Matches',
  'discovery.investorType': 'Investor Type',
  'discovery.matchesFound': 'Matches Found',
  'discovery.matchScore': 'Match Score',
  'discovery.portfolioSize': 'Portfolio Size',
  'discovery.properties': 'Properties',
  'discovery.saveSearch': 'Save Search',
  'discovery.searchPlaceholder': 'Search investors...',
  'discovery.startSearch': 'Start Search',
  'discovery.startSearchHint': 'Enter search criteria to find potential investors',
  'discovery.totalProperties': 'Total Properties',
  'discovery.totalValue': 'Total Value',

  // Issue #850: Progressive Enrichment & Market Context
  'discovery.dataQuality.high': 'Rich data',
  'discovery.dataQuality.low': 'Limited data',
  'discovery.dataQuality.medium': 'Partial data',
  'discovery.dataQuality.tooltip': 'Data quality score: {{score}}/100',
  'discovery.enableRadar': 'Connect Radar in Settings \u2192 Integrations to enable analytics enrichment',
  'discovery.enriching': 'Enriching investor profiles...',
  'discovery.enrichmentComplete': 'Profiles enriched',
  'discovery.enrichmentPartial': '{{count}} of {{total}} profiles enriched',
  'discovery.marketContext.activityScore': 'Investor Activity',
  'discovery.marketContext.avgPropertyValue': 'Avg Property Value',
  'discovery.marketContext.flipRate': 'Flip Rate',
  'discovery.marketContext.institutionalShare': 'Institutional Share',
  'discovery.marketContext.loading': 'Loading market data...',
  'discovery.marketContext.title': 'Market Intelligence',
  'discovery.marketContext.unavailable': 'Market data unavailable for this geography',
  'discovery.marketContext.activityScoreTooltip':
    'Composite score measuring investor buying activity in this market over the last 12 months',
  'discovery.marketContext.avgPropertyValueTooltip':
    'Average current assessed value of investor-owned properties in this market',
  'discovery.marketContext.institutionalShareTooltip':
    'Percentage of properties owned by institutional investors (50+ properties) vs individual investors',
  'discovery.marketContext.flipRateTooltip':
    'Percentage of properties sold within 12 months of purchase (fix-and-flip activity)',
  'discovery.radarAvailable': 'Radar enrichment enabled',
  'discovery.radarUnavailable': 'Radar not configured',

  // Issue #850: Prospect contact & transaction details
  'prospect.email': 'Email',
  'prospect.phone': 'Phone',
  'prospect.transactions.avmValue': 'AVM Value',
  'prospect.transactions.empty': 'No transaction history available',
  'prospect.transactions.propertyType': 'Property Type',
  'prospect.transactions.recordingDate': 'Recording Date',
  'prospect.transactions.saleAmount': 'Sale Amount',
  'prospect.transactions.title': 'Recent Transactions',

  'pageHeader.esignSettings.description': 'Configure e-signature templates and workflows',
  'pageHeader.esignSettings.title': 'E-Signature Settings',
  'pageHeader.permissions.description': 'Manage user permissions and access controls',
  'pageHeader.permissions.title': 'Permissions',
  'pageHeader.discovery.description': 'Find and connect with potential investors',
  'pageHeader.discovery.title': 'Investor Discovery',
  'pageHeader.prospectDetail.title': 'Prospect Detail',
  'pageHeader.prospectDetail.description': 'View complete investor profile and portfolio',
  'pageHeader.settingsHub.description': 'Manage your business settings and preferences',
  'pageHeader.settingsHub.title': 'Control Center',

  // ============================================================================
  // PERMISSIONS (Issue #400 - i18n audit)
  'pageHeader.applicationAnalytics.title': 'Application Analytics',
  'pageHeader.applicationAnalytics.description': 'Track and analyze application performance metrics',
  'pageHeader.applicationsDashboard.title': 'Applications Dashboard',
  'pageHeader.applicationsDashboard.description': 'Monitor and manage all applications',
  'pageHeader.guarantorHub.title': 'Guarantor Profile',
  'pageHeader.guarantorHub.description': 'Complete view of guarantee obligations, exposure, and financial analysis',
  // ============================================================================
  'permissions.description': 'Control what users can access and modify',
  'permissions.pageDescription':
    'See what each role can do across your business, check your own access, and on higher plans, create custom roles. Borrower-facing permissions flagged with a license requirement (NMLS, state) apply per-user based on their credentials.',
  'permissions.matrixTitle': 'Role Permission Matrix',
  'permissions.tabs.matrix': 'Role Matrix',
  'permissions.tabs.myAccess': 'My Access',
  'permissions.tabs.customRoles': 'Custom Roles',
  'permissions.tabs.audit': 'Access Audit',
  'permissions.roles.admin': 'Admin',
  'permissions.roles.manager': 'Manager',
  'permissions.roles.user': 'User',
  'permissions.roles.viewer': 'Viewer',
  'permissions.customRoles.info':
    "Custom roles let you create fine-grained permission sets tailored to your organization's needs.",
  'permissions.customRoles.availability':
    'Create and manage custom roles with specific permission combinations. Available on Professional and Enterprise plans.',
  'permissions.customRoles.createButton': 'Create Custom Role',
  'permissions.audit.info':
    'Access audit logs show every permission check and access attempt across your organization.',
  'permissions.audit.availability':
    'This feature requires the Enterprise plan and the audit:read permission. Contact your administrator for access.',

  // ============================================================================
  // REFERRAL (Issue #400 - i18n audit)
  // ============================================================================
  'referral.shareText': 'Check out this referral link!',
  'referral.shareTitle': 'Share Referral Link',
  'referral.referredByFromCompany': 'Referred by: {{firstName}} from {{companyName}}',
  'referral.referredByName': 'Referred by: {{name}}',
  'referral.referredToCompany': 'Referred to: {{companyName}}',
  'referral.expired': 'Referral link has expired',
  'referral.invalid': 'Invalid referral code',

  // ============================================================================
  // SETTINGS
  // ============================================================================
  'settings.title': 'Control Center',
  'settings.description': 'Configure your business, integrations, and security settings',

  // Tabs
  'settings.tabs.profile': 'Profile',
  'settings.tabs.preferences': 'Preferences',
  'settings.tabs.notifications': 'Notifications',
  'settings.tabs.security': 'Security',
  'settings.tabs.terminology': 'Terminology',

  // Settings Hub
  'settings.hub.allBusinessSettings': 'All Business Settings',

  // Profile
  'settings.profile.title': 'Account Information',
  'settings.profile.email': 'Email',
  'settings.profile.name': 'Name',
  'settings.profile.avatar': 'Avatar',
  'settings.profile.contactAdmin': 'Contact your administrator to update your email address',

  // Preferences
  'settings.preferences.title': 'User Preferences',
  'settings.preferences.theme': 'Theme',
  'settings.preferences.themeLight': 'Light',
  'settings.preferences.themeDark': 'Dark',
  'settings.preferences.themeSystem': 'System Default',
  'settings.preferences.language': 'Language',
  'settings.preferences.timezone': 'Timezone',
  'settings.preferences.communicationTitle': 'Communication Preferences',
  'settings.preferences.emailNotifications': 'Receive email notifications',
  'settings.preferences.pushNotifications': 'Receive push notifications',
  'settings.preferences.errorLoading': 'Failed to load preferences',
  'settings.preferences.errorSaving': 'Failed to save preferences',
  'settings.preferences.successSaving': 'Preferences saved successfully',
  'settings.preferences.loading': 'Loading preferences...',
  'settings.preferences.saveButton': 'Save Preferences',

  // Notifications
  'settings.notifications.title': 'Notification Channels',
  'settings.notifications.description': 'Configure which channels you want to receive notifications through',
  'settings.notifications.emailTitle': 'Email Notifications',
  'settings.notifications.emailDescription': 'Receive notifications via email',
  'settings.notifications.smsTitle': 'SMS Notifications',
  'settings.notifications.smsDescription': 'Receive notifications via SMS (requires verified phone number)',
  'settings.notifications.pushTitle': 'Push Notifications',
  'settings.notifications.pushDescription': 'Receive browser push notifications',
  'settings.notifications.pushUnsupported': 'Push notifications are not supported in this browser',
  'settings.notifications.pushDenied': 'Push notifications were blocked. Enable them in your browser settings.',
  'settings.notifications.pushError': 'Failed to enable push notifications',
  'settings.notifications.pushEnabling': 'Enabling push notifications...',
  'settings.notifications.inAppTitle': 'In-App Notifications',
  'settings.notifications.inAppDescription': "See notifications in the app's notification center",
  'settings.notifications.errorLoading': 'Failed to load notification settings',
  'settings.notifications.errorSaving': 'Failed to save notification settings',
  'settings.notifications.successSaving': 'Notification settings saved successfully',
  'settings.notifications.loading': 'Loading notification settings...',
  'settings.notifications.saveButton': 'Save Notification Settings',

  // Security
  'settings.security.title': 'Two-Factor Authentication (MFA)',
  'settings.security.description': 'Add an extra layer of security to your account',
  'settings.security.mfa': 'MFA',
  'settings.security.mfaEnabled': 'MFA Enabled',
  'settings.security.mfaDisabled': 'MFA Not Enabled',
  'settings.security.enableMfa': 'Enable Two-Factor Authentication',
  'settings.security.disableMfa': 'Disable MFA',
  'settings.security.resetMfa': 'Reset MFA',
  'settings.security.enrolledFactors': 'Enrolled Factors:',
  'settings.security.backupCodes': 'Backup Codes',
  'settings.security.backupCodesDescription':
    'Backup codes can be used to access your account if you lose access to your authenticator app. Each code can only be used once.',
  'settings.security.backupCodesRemaining': 'Backup codes remaining:',
  'settings.security.generateBackupCodes': 'Generate Backup Codes',
  'settings.security.regenerateBackupCodes': 'Regenerate Backup Codes',
  'settings.security.backupCodesWarning': "Save these codes now! You won't be able to see them again.",
  'settings.security.errorLoading': 'Failed to load MFA status',
  'settings.security.errorUpdating': 'Failed to update MFA',
  'settings.security.successUpdating': 'MFA updated successfully',
  'settings.security.errorGeneratingCodes': 'Failed to generate backup codes',
  'settings.security.loading': 'Loading MFA status...',

  // Terminology Settings (White-Label)
  'settings.terminology.title': 'White-Label Terminology',
  'settings.terminology.description':
    'Customize terminology to match your business branding. Changes apply across all portal pages.',
  'settings.terminology.resetTooltip': 'Reset all terminology to defaults',
  'settings.terminology.resetAll': 'Reset All',
  'settings.terminology.customized': 'Customized',
  'settings.terminology.custom': 'Custom',
  'settings.terminology.editTerm': 'Edit term',
  'settings.terminology.default': 'Default',
  'settings.terminology.errorLoading': 'Failed to load terminology',
  'settings.terminology.errorSaving': 'Failed to save terminology',
  'settings.terminology.successSaving': 'Terminology saved successfully',
  'settings.terminology.successReset': 'Terminology reset to defaults',

  // Preferences Page
  'settings.preferences.pageTitle': 'Personal Preferences',
  'settings.preferences.description': 'Manage your personal settings and preferences',

  // Admin Auth Settings (Issue #215 - i18n gaps)
  'settings.auth.title': 'Authentication Settings',
  'settings.auth.description': 'Configure session management, password policies, and authentication methods.',
  'settings.auth.loadError': 'Failed to load auth settings',
  'settings.auth.yourMfaSettings': 'Your MFA Settings',
  'settings.auth.yourMfaDescription': 'Manage your personal two-factor authentication settings',
  'settings.auth.backupCodesDescription':
    'Backup codes can be used to access your account if you lose access to your authenticator app.',
  'settings.auth.backupCodesRemaining': 'Backup codes remaining:',
  'settings.auth.regenerateBackupCodes': 'Regenerate Backup Codes',
  'settings.auth.systemWideSettings': 'System-Wide Settings',
  'settings.auth.systemWideDescription': 'Configure authentication settings for all users in the platform',
  'settings.auth.sessionManagement': 'Session Management',
  'settings.auth.sessionTimeout': 'Session Timeout:',
  'settings.auth.sessionTimeoutOption': '{{hours}} hours',
  'settings.auth.maxLoginAttempts': 'Max Login Attempts',
  'settings.auth.passwordPolicy': 'Password Policy',
  'settings.auth.minLength': 'Minimum Length',
  'settings.auth.requireUppercase': 'Require uppercase letters',
  'settings.auth.requireNumbers': 'Require numbers',
  'settings.auth.requireSpecialChars': 'Require special characters',
  'settings.auth.mfaEnforcement': 'Multi-Factor Authentication',
  'settings.auth.mfaOff': 'Off',
  'settings.auth.mfaOptional': 'Optional',
  'settings.auth.mfaRequired': 'Required for all users',
  'settings.auth.allowedMethods': 'Allowed Authentication Methods',
  'settings.auth.method.email': 'Email/Password',
  'settings.auth.method.google': 'Google OAuth',
  'settings.auth.method.sso': 'SSO (SAML/OIDC)',
  'settings.auth.method.github': 'GitHub OAuth',
  'settings.auth.method.microsoft': 'Microsoft OAuth',
  'settings.auth.mfaUpdateSuccess': 'MFA updated successfully',
  'settings.auth.mfaLoadError': 'Failed to load MFA status',
  'settings.auth.mfaUpdateError': 'Failed to update MFA',
  'settings.auth.backupCodesError': 'Failed to generate backup codes',
  'settings.auth.confirmResetMfa': 'Are you sure you want to reset your MFA? You will need to set it up again.',
  'settings.auth.confirmDisableMfa': 'Are you sure you want to disable MFA? This will reduce your account security.',
  'settings.auth.enterPassword': 'Enter your password to confirm:',

  // Settings Hub
  'settings.hub.title': 'Settings',
  'settings.hub.description': 'Manage your business settings and preferences',

  // Business Profile Settings
  'settings.businessProfile.title': 'Business Profile',
  'settings.businessProfile.description': 'Manage your business information and branding',
  'settings.businessProfile.companyInfo': 'Company Information',
  'settings.businessProfile.companyInfoDescription': 'Basic information about your company',
  'settings.businessProfile.businessName': 'Business Name',
  'settings.businessProfile.businessNameHint': 'The name displayed to your customers',
  'settings.businessProfile.companyLegalName': 'Company Legal Name',
  'settings.businessProfile.companyLegalNameHint': 'The official registered name of your company',
  'settings.businessProfile.legalName': 'Legal Name',
  'settings.businessProfile.legalNameHint': 'Your official registered business name',
  'settings.businessProfile.industry': 'Industry',
  'settings.businessProfile.taxId': 'Tax ID',
  'settings.businessProfile.website': 'Website',
  'settings.businessProfile.contactInfo': 'Contact Information',
  'settings.businessProfile.contactInfoDescription': 'How customers can reach you',
  'settings.businessProfile.email': 'Email',
  'settings.businessProfile.phone': 'Phone',
  'settings.businessProfile.address': 'Address',
  'settings.businessProfile.addressDescription': 'Your business location',
  'settings.businessProfile.street': 'Street Address',
  'settings.businessProfile.city': 'City',
  'settings.businessProfile.state': 'State',
  'settings.businessProfile.zipCode': 'ZIP Code',
  'settings.businessProfile.branding': 'Branding',
  'settings.businessProfile.brandingDescription': 'Customize your business appearance',
  'settings.businessProfile.logoHint': 'Upload your company logo',
  'settings.businessProfile.logoRequirements': 'PNG or JPG, max 2MB, minimum 200x200px',
  'settings.businessProfile.regionalSettings': 'Regional Settings',
  'settings.businessProfile.regionalSettingsDescription': 'Configure timezone and locale preferences',
  'settings.businessProfile.timezone': 'Timezone',
  'settings.businessProfile.unsavedChanges': 'You have unsaved changes',
  'settings.businessProfile.companySize': 'Company Size',
  'settings.businessProfile.validationErrors': 'Please fix the validation errors below before saving.',

  // Pipeline Settings
  'settings.pipelines.title': 'Pipeline Configuration',
  'settings.pipelines.description': 'Configure deal stages and pipeline workflows',
  'settings.pipelines.pipelines': 'Pipelines',
  'settings.pipelines.stages': 'Stages',
  'settings.pipelines.noPipelines': 'No pipelines configured',
  'settings.pipelines.noStages': 'No stages in this pipeline',
  'settings.pipelines.createFirst': 'Create your first pipeline to get started',
  'settings.pipelines.createPipeline': 'Create Pipeline',
  'settings.pipelines.editPipeline': 'Edit Pipeline',
  'settings.pipelines.selectPipeline': 'Select a pipeline',
  'settings.pipelines.namePlaceholder': 'Enter pipeline name',
  'settings.pipelines.setAsDefault': 'Set as Default',
  'settings.pipelines.addStage': 'Add Stage',
  'settings.pipelines.addFirstStage': 'Add your first stage',
  'settings.pipelines.editStage': 'Edit Stage',
  'settings.pipelines.stageNamePlaceholder': 'Enter stage name',
  'settings.pipelines.stageType': 'Stage Type',
  'settings.pipelines.stageColor': 'Stage Color',
  'settings.pipelines.stageOrderInfo': 'Drag stages to reorder them',
  'settings.pipelines.slaHours': 'SLA (Hours)',
  'settings.pipelines.slaHint': 'Maximum time a deal should stay in this stage',
  'settings.pipelines.wonStage': 'Won Stage',
  'settings.pipelines.lostStage': 'Lost Stage',
  'settings.pipelines.finalStage': 'Final Stage',
  'settings.pipelines.approvalStage': 'Approval Stage',
  'settings.pipelines.failureStage': 'Failure Stage',
  'settings.pipelines.initialStage': 'Initial Stage',
  'settings.pipelines.normalStage': 'Normal Stage',
  'settings.pipelines.successStage': 'Success Stage',
  'settings.pipelines.waitingStage': 'Waiting Stage',

  // Webhook Settings
  'settings.webhooks.title': 'Webhooks',
  'settings.webhooks.description':
    'Send real-time notifications from Fiops to your systems when events happen — a new case is created, a loan is funded, a document is uploaded. Useful for alerts in Slack, Zapier automations, or syncing back to your CRM.',
  'settings.webhooks.endpoints': 'Endpoints',
  'settings.webhooks.noEndpoints': 'No webhook endpoints configured',
  'settings.webhooks.createFirst': 'Create your first webhook endpoint',
  'settings.webhooks.createEndpoint': 'Create Endpoint',
  'settings.webhooks.editEndpoint': 'Edit Endpoint',
  'settings.webhooks.deleteEndpoint': 'Delete Endpoint',
  'settings.webhooks.endpointName': 'Endpoint Name',
  'settings.webhooks.endpointUrl': 'Endpoint URL',
  'settings.webhooks.endpointUrlPlaceholder': 'https://your-server.com/webhook',
  'settings.webhooks.secret': 'Webhook Secret',
  'settings.webhooks.secretHint': 'Used to verify webhook signatures',
  'settings.webhooks.generateSecret': 'Generate Secret',
  'settings.webhooks.copySecret': 'Copy Secret',
  'settings.webhooks.events': 'Events',
  'settings.webhooks.selectEvents': 'Select events to receive',
  'settings.webhooks.allEvents': 'All Events',
  'settings.webhooks.customEvents': 'Custom Events',
  'settings.webhooks.eventCategories': 'Event Categories',
  'settings.webhooks.caseEvents': 'Loan Events',
  'settings.webhooks.consumerEvents': 'Consumer Events',
  'settings.webhooks.documentEvents': 'Document Events',
  'settings.webhooks.paymentEvents': 'Payment Events',
  'settings.webhooks.userEvents': 'User Events',
  'settings.webhooks.testEndpoint': 'Test Endpoint',
  'settings.webhooks.testSuccess': 'Test webhook sent successfully',
  'settings.webhooks.testFailed': 'Failed to send test webhook',
  'settings.webhooks.deliveryHistory': 'Delivery History',
  'settings.webhooks.noDeliveries': 'No webhook deliveries yet',
  'settings.webhooks.deliveryStatus': 'Delivery Status',
  'settings.webhooks.deliveryTime': 'Delivery Time',
  'settings.webhooks.responseCode': 'Response Code',
  'settings.webhooks.responseTime': 'Response Time',
  'settings.webhooks.payload': 'Payload',
  'settings.webhooks.retry': 'Retry',
  'settings.webhooks.retrying': 'Retrying...',
  'settings.webhooks.enabled': 'Enabled',
  'settings.webhooks.disabled': 'Disabled',
  'settings.webhooks.status.active': 'Active',
  'settings.webhooks.status.inactive': 'Inactive',
  'settings.webhooks.status.failed': 'Failed',
  'settings.webhooks.confirmDelete': 'Are you sure you want to delete this endpoint?',
  'settings.webhooks.headers': 'Custom Headers',
  'settings.webhooks.addHeader': 'Add Header',
  'settings.webhooks.headerName': 'Header Name',
  'settings.webhooks.headerValue': 'Header Value',
  'settings.webhooks.authentication': 'Authentication',
  'settings.webhooks.authNone': 'None',
  'settings.webhooks.authBasic': 'Basic Auth',
  'settings.webhooks.authBearer': 'Bearer Token',
  'settings.webhooks.authApiKey': 'API Key',
  'settings.webhooks.username': 'Username',
  'settings.webhooks.password': 'Password',
  'settings.webhooks.token': 'Token',
  'settings.webhooks.apiKey': 'API Key',
  'settings.webhooks.apiKeyHeader': 'API Key Header',
  'settings.webhooks.addEndpoint': 'Add Endpoint',
  'settings.webhooks.addFirstEndpoint': 'Add your first endpoint to start receiving webhooks',
  'settings.webhooks.attempts': 'Attempts',
  'settings.webhooks.deliveriesFor': 'Deliveries for {{endpoint}}',
  'settings.webhooks.deliveryDetails': 'Delivery Details',
  'settings.webhooks.duration': 'Duration',
  'settings.webhooks.event': 'Event',
  'settings.webhooks.eventsDescription': 'Select which events should trigger this webhook',
  'settings.webhooks.failed': 'Failed',
  'settings.webhooks.lastTriggered': 'Last Triggered',
  'settings.webhooks.actionsMenu': 'Webhook actions',
  'settings.webhooks.namePlaceholder': 'Enter endpoint name',
  'settings.webhooks.noDeliveriesDescription': 'No deliveries recorded for this endpoint yet',
  'settings.webhooks.noEndpointsDescription': 'Create a webhook endpoint to receive real-time event notifications',
  'settings.webhooks.regenerateSecret': 'Regenerate Secret',
  'settings.webhooks.requestBody': 'Request Body',
  'settings.webhooks.responseBody': 'Response Body',
  'settings.webhooks.secretHelperText': 'This secret is used to sign webhook payloads',
  'settings.webhooks.securityNote': 'Always verify webhook signatures in production',
  'settings.webhooks.selectEndpoint': 'Select an endpoint to view deliveries',
  'settings.webhooks.sendTest': 'Send Test',
  'settings.webhooks.signingSecret': 'Signing Secret',
  'settings.webhooks.statusCode': 'Status Code',
  'settings.webhooks.successRate': 'Success Rate',
  'settings.webhooks.tabs.deliveries': 'Deliveries',
  'settings.webhooks.tabs.endpoints': 'Endpoints',
  'settings.webhooks.testDescription': 'Send a test event to verify your endpoint is configured correctly',
  'settings.webhooks.testWebhook': 'Test Webhook',
  'settings.webhooks.testingEndpoint': 'Testing endpoint...',
  'settings.webhooks.totalDeliveries': 'Total Deliveries',
  'settings.webhooks.urlHelperText': 'The URL that will receive webhook events',
  'settings.webhooks.viewDeliveries': 'View Deliveries',
  'settings.webhooks.viewEndpoints': 'View Endpoints',

  // Developer API Settings
  'settings.developerApi': 'Developer API',
  'settings.developerApiDescription': 'API keys, documentation, and MCP integration',

  // Developer API Page
  'developerApi.title': 'Developer API',
  'developerApi.description':
    'Connect your CRM, loan origination system, or any other tool to Fiops. Create API keys with scoped access, read the API reference, or enable AI assistants to take actions for you via MCP.',

  // Developer API Tabs
  'developerApi.tabs.apiKeys': 'API Keys',
  'developerApi.tabs.apiDocs': 'API Documentation',
  'developerApi.tabs.mcp': 'MCP & Claude Cowork',

  // Developer API - API Keys tab
  'developerApi.apiKeys.title': 'API Keys',
  'developerApi.apiKeys.description': 'Create and manage API keys for programmatic access',
  'developerApi.apiKeys.createKey': 'Create API Key',
  'developerApi.apiKeys.name': 'Key Name',
  'developerApi.apiKeys.namePlaceholder': 'e.g., Claude Integration, CI Pipeline',
  'developerApi.apiKeys.prefix': 'Key',
  'developerApi.apiKeys.status': 'Status',
  'developerApi.apiKeys.scopes': 'Scopes',
  'developerApi.apiKeys.lastUsed': 'Last Used',
  'developerApi.apiKeys.createdAt': 'Created',
  'developerApi.apiKeys.expiresAt': 'Expires',
  'developerApi.apiKeys.revoke': 'Revoke',
  'developerApi.apiKeys.revokeConfirm': 'Are you sure you want to revoke this API key? This action cannot be undone.',
  'developerApi.apiKeys.noKeys': 'No API keys yet',
  'developerApi.apiKeys.noKeysDescription': 'Create an API key to start integrating with Fiops',
  'developerApi.apiKeys.copyWarning': "Copy this key now. You won't be able to see it again.",
  'developerApi.apiKeys.copied': 'API key copied to clipboard',
  'developerApi.apiKeys.never': 'Never',
  'developerApi.apiKeys.active': 'Active',
  'developerApi.apiKeys.revoked': 'Revoked',
  'developerApi.apiKeys.expired': 'Expired',
  'developerApi.apiKeys.scopeCount': '{{count}} scopes',

  // Developer API - Scope presets
  'developerApi.scopes.title': 'Permissions',
  'developerApi.scopes.preset': 'Scope Preset',
  'developerApi.scopes.readOnly': 'Read Only',
  'developerApi.scopes.readOnlyDescription': 'View loans, consumers, documents, and reports',
  'developerApi.scopes.standard': 'Standard',
  'developerApi.scopes.standardDescription': 'Read and write loans, consumers, documents, and tasks',
  'developerApi.scopes.fullAccess': 'Full Access',
  'developerApi.scopes.fullAccessDescription': 'All available API permissions',
  'developerApi.scopes.custom': 'Custom',
  'developerApi.scopes.customizeScopes': 'Customize scopes',

  // Developer API - Expiration
  'developerApi.expiration.title': 'Expiration',
  'developerApi.expiration.30days': '30 days',
  'developerApi.expiration.90days': '90 days',
  'developerApi.expiration.1year': '1 year',
  'developerApi.expiration.custom': 'Custom date',
  'developerApi.expiration.never': 'Never',

  // Developer API - API Docs tab
  'developerApi.docs.title': 'API Documentation',
  'developerApi.docs.description': 'Reference for available API endpoints',
  'developerApi.docs.authentication': 'Authentication',
  'developerApi.docs.authDescription': 'Include your API key in the Authorization header',
  'developerApi.docs.endpoints': 'Endpoints',
  'developerApi.docs.requiredScope': 'Required scope',
  'developerApi.docs.rateLimits': 'Rate Limits',
  'developerApi.docs.fullDocs': 'View Full Documentation',
  'developerApi.docs.codeExamples': 'Code Examples',

  // Developer API - MCP tab
  'developerApi.mcp.title': 'MCP & Claude Cowork',
  'developerApi.mcp.description': 'Connect Claude and other MCP clients to your Fiops data',
  'developerApi.mcp.quickStart': 'Quick Start',
  'developerApi.mcp.connectToClaude': 'Connect to Claude',
  'developerApi.mcp.selectApiKey': 'Select API Key',
  'developerApi.mcp.noKeysPrompt': 'Create an API key first to generate your MCP configuration',
  'developerApi.mcp.createKeyFirst': 'Create API Key',
  'developerApi.mcp.configCopied': 'MCP configuration copied to clipboard',
  'developerApi.mcp.copyConfig': 'Copy Configuration',
  'developerApi.mcp.availableTools': 'Available Tools',
  'developerApi.mcp.toolName': 'Tool',
  'developerApi.mcp.toolDescription': 'Description',
  'developerApi.mcp.toolScope': 'Required Scope',
  'developerApi.mcp.enabled': 'Enabled',
  'developerApi.mcp.disabled': 'Upgrade key scopes to enable',
  'developerApi.mcp.testConnection': 'Test Connection',
  'developerApi.mcp.testSuccess': 'Connected successfully',
  'developerApi.mcp.testFailed': 'Connection failed',
  'developerApi.mcp.toolsAvailable': '{{count}} tools available',
  'developerApi.mcp.setupInstructions': 'Setup Instructions',
  'developerApi.mcp.claudeDesktop': 'Claude Desktop',
  'developerApi.mcp.claudeCode': 'Claude Code',
  'developerApi.mcp.otherClients': 'Other MCP Clients',
  'developerApi.mcp.connectTitle': 'Connect to Claude',
  'developerApi.mcp.connectDescription':
    'Select an API key to generate your MCP configuration. Paste it into your Claude client to get started.',
  'developerApi.mcp.noKeys': 'Create an API key first to connect via MCP.',
  'developerApi.mcp.selectKey': 'Select an API key...',
  'developerApi.mcp.configLabel': 'MCP Configuration',
  'developerApi.mcp.setupTitle': 'Setup Instructions',
  'developerApi.mcp.claudeDesktopInstructions':
    'Open Claude Desktop settings, navigate to the MCP section, and paste the configuration above into your claude_desktop_config.json file. Restart Claude Desktop to apply changes.',
  'developerApi.mcp.claudeCodeInstructions':
    "Add the configuration to your project's .claude/mcp.json file. Claude Code will automatically detect and connect to the MCP server on next launch.",
  'developerApi.mcp.otherClientsInstructions':
    'Use the MCP server URL from the configuration above. Any MCP-compatible client can connect using the SSE transport with your API key for authentication.',
  'developerApi.mcp.noTools': 'No tools available for this API key.',
  'developerApi.mcp.requiredScope': 'Required Scope',
  'developerApi.mcp.scopeRequired': 'API key does not have the required scope',
  'developerApi.mcp.testDescription':
    'Verify that your MCP server is reachable and your API key has the correct permissions.',
  'developerApi.mcp.testing': 'Testing...',
  'developerApi.docs.authTitle': 'Authentication',
  'developerApi.docs.endpointReference': 'Endpoint Reference',
  'developerApi.docs.rateLimitsDescription':
    'Rate limits vary by subscription tier. Contact your administrator for details.',
  'developerApi.docs.viewFullDocs': 'View Full Documentation',

  // ============================================================================
  // AUTH
  // ============================================================================
  'auth.login': 'Log In',
  'auth.logout': 'Log Out',
  'auth.signup': 'Sign Up',
  'auth.forgotPassword': 'Forgot Password?',
  'auth.resetPassword': 'Reset Password',
  'auth.changePassword': 'Change Password',
  'auth.verifyEmail': 'Verify Email',
  'auth.checkingAuth': 'Checking authentication...',
  'auth.redirectingToLogin': 'Redirecting to login...',
  'auth.sessionExpired': 'Your session has expired',
  'auth.loginRequired': 'Please log in to continue',
  'auth.signOut': 'Sign Out',
  'auth.signingOut': 'Signing out...',
  'auth.loggedInAs': 'Logged in as {{email}}',
  'auth.signInTitle': 'Sign in to your account',
  'auth.enterEmail': 'Enter your email to continue',
  'auth.enterPassword': 'Enter your password',
  'auth.signingInAs': 'Signing in as {{email}}',
  'auth.completingSignIn': 'Completing sign-in...',
  'auth.completingSignInSubtext': 'One moment while we redirect you to your portal.',
  'auth.continue': 'Continue',
  'auth.continueWithGoogle': 'Continue with Google',
  'auth.signInWithSso': 'Sign in with SSO',
  'auth.signInWithMagicLink': 'Sign in with Magic Link',
  'auth.signIn': 'Sign In',
  'auth.noAccount': "Don't have an account?",
  'auth.rememberDevice': 'Remember this device',
  'auth.or': 'or',
  'auth.back': 'Back',
  'auth.invalidCredentials': 'Invalid email or password',
  'auth.tooManyAttempts': 'Too many failed attempts. Please try again later.',
  'auth.genericError': 'An error occurred. Please try again.',
  'auth.googleSignInFailed': 'Google sign-in failed. Please try again.',
  'auth.accountExistsLinkGoogle':
    'An account already exists for {{email}}. Sign in with your password and we\u2019ll link your Google account so you can use either method next time.',
  'auth.googleLinkFailed':
    'Signed in, but couldn\u2019t link your Google account automatically. You can link it from your profile.',
  'auth.googleCredentialInUse':
    'That Google account is already linked to a different user. Sign in with the other account, or use your password.',
  'auth.ssoSignInFailed': 'SSO sign-in failed. Please contact your administrator or try another method.',
  'auth.tokenExchangeFailed': 'Failed to complete authentication',
  'auth.portalAccessDenied': 'Access denied. You do not have permission to access this portal.',
  'auth.adminPortalAccessDenied': 'Access denied. Admin portal requires platform administrator privileges.',
  'auth.allRightsReserved': '{{company}}. All rights reserved.',
  'auth.privacyTerms': 'Privacy & terms',
  'auth.contact': 'Contact',

  // Sign-In page v3 (Issue #1749 follow-up)
  'auth.signIn.title': 'Welcome back',
  'auth.signIn.subtitle': 'Sign in to your {{platformName}} workspace.',
  'auth.signIn.workEmailLabel': 'Work email',
  'auth.signIn.rememberDevice': 'Remember this device for 30 days',
  'auth.signIn.orContinueWith': 'OR CONTINUE WITH',
  'auth.signIn.googleSsoLabel': 'Continue with Google',
  'auth.signIn.magicLinkLabel': 'Magic link to your inbox',
  'auth.signIn.samlLabel': 'SSO / SAML',
  'auth.signIn.footerNoAccount': "Don't have an account?",
  'auth.signIn.footerRequestAccess': 'Request access',
  'auth.signIn.submitLabel': 'Sign in',

  // Sign-Up (Request Access) page v3 (Issue #1749 follow-up)
  'auth.signUp.title': 'Request access',
  'auth.signUp.subtitle':
    "{{platformName}} is invite-only while we onboard new lenders. Tell us about your shop and we'll be in touch within 24h.",
  'auth.signUp.consumerTitle': 'Create your account',
  'auth.signUp.consumerSubtitle': 'Sign up to access your portal.',
  'auth.signUp.partnerTitle': 'Complete partner registration',
  'auth.signUp.partnerSubtitle': "We've pre-filled your email — pick a password and you're in.",
  'auth.signUp.firstNameLabel': 'First name',
  'auth.signUp.lastNameLabel': 'Last name',
  'auth.signUp.workEmailLabel': 'Work email',
  'auth.signUp.workEmailHelper': 'Use your company domain so we can route to the right workspace.',
  'auth.signUp.companyLabel': 'Company',
  'auth.signUp.annualOriginationsLabel': 'Annual originations',
  'auth.signUp.annualOriginations.under50M': '< $50M',
  'auth.signUp.annualOriginations.50to250M': '$50–250M',
  'auth.signUp.annualOriginations.250Mto1B': '$250M–1B',
  'auth.signUp.annualOriginations.over1B': '$1B+',
  'auth.signUp.productsLabel': 'Products you originate',
  'auth.signUp.products.bridge': 'Bridge / F&F',
  'auth.signUp.products.dscr': 'DSCR Rental',
  'auth.signUp.products.construction': 'Construction',
  'auth.signUp.products.commercial': 'Commercial',
  'auth.signUp.submitLabel': 'Request access',
  'auth.signUp.passwordLabel': 'Password',
  'auth.signUp.confirmPasswordLabel': 'Confirm password',
  'auth.signUp.termsAgreement':
    'By requesting access you agree to our <terms>Terms</terms> and <privacy>Privacy Policy</privacy>.',
  // Split-segment versions of the terms callout — t() only does {{param}}
  // interpolation, so the inline <terms>/<privacy> tags in the legacy
  // `termsAgreement` key cannot render as MUI <Link>s at runtime. The page
  // composes these three segments around two <Link> components to preserve
  // both translatability and visual styling.
  'auth.signUp.termsPrefix': 'By requesting access you agree to our',
  'auth.signUp.termsConjunction': 'and',
  'auth.signUp.termsLabel': 'Terms',
  'auth.signUp.privacyLabel': 'Privacy Policy',
  'auth.signUp.footerHaveAccount': 'Already have an account?',
  'auth.signUp.footerSignIn': 'Sign in',
  // Aria labels for the chip-group fieldsets (originations / products).
  'auth.signUp.annualOriginationsAriaLabel': 'Annual originations volume',
  'auth.signUp.productsAriaLabel': 'Products you originate',
  'auth.signUp.passwordHelper': 'At least 8 characters.',
  'auth.signUp.continueWithGoogle': 'Continue with Google',
  'auth.signUp.orContinueWith': 'OR CONTINUE WITH',

  // Magic Link Sent state v3 (Issue #1749 follow-up)
  'auth.magicLink.sentTitle': 'Check your inbox',
  'auth.magicLink.sentSubtitle':
    'We sent a sign-in link to your email. Click it to land back in your workspace — no password needed.',
  'auth.magicLink.sentToLabel': 'Sent to',
  'auth.magicLink.expiryNote': 'The link expires in <expiry>15 minutes</expiry>.',
  // Split companion keys for the v3 sent-state card so the duration ("15
  // minutes") can render in mono+bold without needing a Trans component or
  // tag parsing. `expiryNote` is kept for future <Trans>-based callers.
  'auth.magicLink.expiryPrefix': 'The link expires in',
  'auth.magicLink.expiryDuration': '15 minutes',
  'auth.magicLink.expirySecondary': 'It only works once, only on this device.',
  // `seconds` is a pre-formatted m:ss string (e.g. "0:47"), not a raw count,
  // so the translation should NOT append a trailing "s" unit suffix.
  'auth.magicLink.resendIn': 'Resend in {{seconds}}',
  'auth.magicLink.resendNow': 'Resend now',
  'auth.magicLink.whyHeading': 'WHY MAGIC LINKS?',
  'auth.magicLink.whyBody':
    'No password to phish. No password to forget. Stronger than 2FA-on-password for most attack paths.',
  'auth.magicLink.wrongEmailPrompt': 'Wrong email?',
  'auth.magicLink.goBack': 'Go back',

  // Forgot Password v3 additions (Issue #1749 follow-up)
  'auth.forgotPassword.useMagicLinkInstead': 'Use a magic link instead',
  'auth.forgotPassword.adminRecoveryNote':
    "If you can't access your email, ask your <admin>workspace admin</admin> to reset it from Control Center → Permissions. Account recovery isn't email-based for security reasons.",
  'auth.forgotPassword.adminLabel': 'workspace admin',
  'auth.forgotPassword.orDivider': 'OR',
  'auth.forgotPassword.workEmailLabel': 'Work email',
  // Split-segment versions of the admin-recovery callout — t() only does
  // {{param}} interpolation, so the inline <admin>…</admin> emphasis tag in
  // `adminRecoveryNote` cannot be substituted at render-time. The page renders
  // these three segments around an inline emphasised <Typography> so the
  // "workspace admin" phrase gets the highlight without losing translatability.
  // TODO(#1749): "Control Center" should also be sourced from useTerminology
  // when a control-tower terminology key is added; hardcoded here for now to
  // match the platform-default copy.
  'auth.forgotPassword.adminRecoveryBefore': "If you can't access your email, ask your ",
  'auth.forgotPassword.adminRecoveryAfter':
    " to reset it from Control Center → Permissions. Account recovery isn't email-based for security reasons.",
  // v3 page strings — distinct from the legacy keys further down so we keep
  // the existing surface unaffected and can compare A/B if needed.
  'auth.forgotPassword.titleV3': 'Reset your password',
  'auth.forgotPassword.subtitleV3':
    "Enter the email associated with your {{platformName}} account. We'll send a one-time reset link.",
  'auth.forgotPassword.rememberItPrompt': 'Remember it?',

  // Auth shell (v3, Issue #1749 follow-up) — split-pane marketing-side strings.
  // Brand identity (label, initials, logo) is sourced from BusinessContext at
  // runtime per §11d white-label rules; the marketing copy below is the
  // platform-default surface and is overridable per-tenant via the existing
  // i18n override path.
  'auth.shell.adminTitle': 'Admin Portal',
  'auth.shell.poweredBy': 'Powered by {{platformName}}',
  'auth.shell.taglineLine1': 'The AI ops layer',
  'auth.shell.taglineLine2': 'for private lenders.',
  'auth.shell.taglineSubtitle':
    'Astor reads your policy, sizes deals against your warehouse lines, drafts memos with citations, and tells you the three things that need a human call today.',
  'auth.shell.astorCard.recommendation': 'Astor recommends',
  'auth.shell.astorCard.confidence': 'conf 82%',
  'auth.shell.astorCard.headline': 'Approve at $725,000 with experience exception.',
  'auth.shell.astorCard.detail': 'Comps support ARV at $1.18M. LTARV 61.4% — 8.6pts below cap.',
  'auth.shell.trust.compliance': 'SOC 2 Type II',
  'auth.shell.trust.funded': '$2.4B funded',
  'auth.shell.trust.tenants': '14 lenders',
  'auth.shell.switchToLight': 'Switch to light mode',
  'auth.shell.switchToDark': 'Switch to dark mode',
  'auth.shell.toggleTheme': 'Toggle theme',

  // Forgot Password (Issue #992)
  'auth.forgotPassword.title': 'Forgot your password?',
  'auth.forgotPassword.subtitle': "Enter your email and we'll send you a link to reset your password.",
  'auth.forgotPassword.sendResetLink': 'Send Reset Link',
  'auth.forgotPassword.checkEmail': 'Check your email',
  'auth.forgotPassword.checkEmailDescription':
    "If an account exists with that email, we've sent instructions to reset your password.",
  'auth.forgotPassword.backToSignIn': 'Back to Sign In',
  'auth.forgotPassword.rememberPassword': 'Remember your password?',
  'auth.forgotPassword.error': 'Failed to send reset email. Please try again.',

  // Reset Password (Issue #992)
  'auth.resetPassword.title': 'Reset your password',
  'auth.resetPassword.subtitle': 'Enter a new password for {{email}}',
  'auth.resetPassword.newPassword': 'New Password',
  'auth.resetPassword.confirmNewPassword': 'Confirm New Password',
  'auth.resetPassword.resetButton': 'Reset Password',
  'auth.resetPassword.successTitle': 'Password reset successful',
  'auth.resetPassword.successDescription': 'Your password has been reset. You can now sign in with your new password.',
  'auth.resetPassword.verifying': 'Verifying reset link...',
  'auth.resetPassword.invalidLink': 'Invalid password reset link',
  'auth.resetPassword.expiredLink': 'This password reset link has expired or is invalid',
  'auth.resetPassword.expiredError': 'This reset link has expired. Please request a new one.',
  'auth.resetPassword.weakPassword': 'Password is too weak. Please use a stronger password.',
  'auth.resetPassword.error': 'Failed to reset password. Please try again.',
  'auth.resetPassword.requestNewLink': 'Request New Reset Link',
  'auth.resetPassword.backToSignIn': 'Back to Sign In',

  // Magic Link (Issue #992; v3 entry-state copy refreshed in #1749 follow-up
  // to match the split-pane AuthLayout — title softened, subtitle now takes
  // a {{platformName}} so the white-label tenant brand renders in the prose).
  'auth.magicLink.title': 'Sign in with a magic link',
  'auth.magicLink.subtitle':
    "Enter your {{platformName}} email and we'll send you a one-time sign-in link. No password required.",
  'auth.magicLink.sendButton': 'Send Magic Link',
  'auth.magicLink.checkEmail': 'Check your email',
  'auth.magicLink.checkEmailDescription': "We've sent a magic link to your email address. Click the link to sign in.",
  'auth.magicLink.tryAgain': "Didn't receive it? Try again",
  'auth.magicLink.verifying': 'Verifying your magic link...',
  'auth.magicLink.enterEmail': 'Please enter your email to complete sign-in',
  'auth.magicLink.backToSignIn': 'Back to sign in',
  'auth.magicLink.sendError': 'Failed to send magic link. Please try again.',
  'auth.magicLink.invalidLink': 'Invalid or expired magic link. Please request a new one.',
  'auth.magicLink.businessIdMissing':
    'Could not determine which business to sign you in to. Open this page from your portal link or contact support.',

  // MFA Setup (Issue #992)
  'auth.mfa.setup.title': 'Set Up Two-Factor Authentication',
  'auth.mfa.setup.subtitle': 'Add an extra layer of security to your account',
  'auth.mfa.setup.stepDownloadApp': 'Download App',
  'auth.mfa.setup.stepScanQr': 'Scan QR Code',
  'auth.mfa.setup.stepVerifyCode': 'Verify Code',
  'auth.mfa.setup.downloadInstruction': 'Download an authenticator app on your phone:',
  'auth.mfa.setup.googleAuthenticator': 'Google Authenticator',
  'auth.mfa.setup.microsoftAuthenticator': 'Microsoft Authenticator',
  'auth.mfa.setup.authy': 'Authy',
  'auth.mfa.setup.onePassword': '1Password',
  'auth.mfa.setup.totpCompatible': 'Any TOTP-compatible authenticator will work.',
  'auth.mfa.setup.scanInstruction': 'Scan this QR code with your authenticator app:',
  'auth.mfa.setup.manualEntryKey': 'Manual entry key:',
  'auth.mfa.setup.clickToCopy': "Click to copy \u2022 Use this if you can't scan the QR code",
  'auth.mfa.setup.scanOrManual': 'Scan the QR code or use the manual entry key above',
  'auth.mfa.setup.loadingQr': 'Loading QR code...',
  'auth.mfa.setup.enterCode': 'Enter the 6-digit code from your authenticator app:',
  'auth.mfa.setup.codeChanges': 'The code changes every 30 seconds.',
  'auth.mfa.setup.completeSetup': 'Complete Setup',
  'auth.mfa.setup.skipForNow': 'Skip for now',
  'auth.mfa.setup.noUserError': 'No user logged in. Please log in first.',
  'auth.mfa.setup.initError': 'Failed to initialize MFA setup. Please try again.',
  'auth.mfa.setup.enterSixDigit': 'Please enter a 6-digit code',
  'auth.mfa.setup.notInitialized': 'MFA setup not initialized. Please refresh and try again.',
  'auth.mfa.setup.invalidCode': 'Invalid code. Please check and try again.',
  'auth.mfa.setup.setupError': 'Failed to complete MFA setup. Please try again.',

  // MFA Verify (Issue #992)
  'auth.mfa.verify.title': 'Two-Factor Authentication',
  'auth.mfa.verify.subtitle': 'Enter the 6-digit code from your {{method}}',
  'auth.mfa.verify.initializing': 'Initializing MFA verification...',
  'auth.mfa.verify.backupCodeLabel': 'Backup Code',
  'auth.mfa.verify.verifyButton': 'Verify',
  'auth.mfa.verify.verifyBackupCode': 'Verify Backup Code',
  'auth.mfa.verify.useAuthenticator': 'Use authenticator code instead',
  'auth.mfa.verify.useBackupCode': 'Use backup code instead',
  'auth.mfa.verify.useDifferentAccount': 'Use a different account',
  'auth.mfa.verify.codeChanges': 'The code changes every 30 seconds',
  'auth.mfa.verify.enterAllDigits': 'Please enter all 6 digits',
  'auth.mfa.verify.notInitialized': 'MFA verification not initialized. Please log in again.',
  'auth.mfa.verify.notInitializedFirst': 'MFA verification not initialized. Please log in first.',
  'auth.mfa.verify.invalidCode': 'Invalid code. Please check and try again.',
  'auth.mfa.verify.verifyError': 'Verification failed. Please try again.',
  'auth.mfa.verify.backupCodeLength': 'Backup code must be 8 characters',
  'auth.mfa.verify.emailNotFound': 'Email not found. Please log in again.',
  'auth.mfa.verify.invalidBackupCode': 'Invalid backup code',

  // Accept Invite (Issue #992)
  'auth.acceptInvite.loadingInvite': 'Loading invite information...',
  'auth.acceptInvite.invalidInvite': 'Invalid Invite',
  'auth.acceptInvite.goToLogin': 'Go to Login',
  'auth.acceptInvite.successTitle': 'Account Activated!',
  'auth.acceptInvite.successDescription':
    'Your account has been set up successfully. You will be redirected to login...',
  'auth.acceptInvite.welcomeBusiness': 'Welcome to {{businessName}}!',
  'auth.acceptInvite.welcomeTeam': 'Welcome to the Team!',
  'auth.acceptInvite.invitedBy': '{{inviterName}} has invited you to join {{teamName}} as a {{role}}.',
  'auth.acceptInvite.invitedByTeam': '{{inviterName}} has invited you to join the team as a {{role}}.',
  'auth.acceptInvite.profileInfoOptional': 'Profile Information (Optional)',
  'auth.acceptInvite.activateAccount': 'Activate Account',
  'auth.acceptInvite.passwordMust': 'Password must:',
  'auth.acceptInvite.passwordMinLength': 'Be at least 8 characters long',
  'auth.acceptInvite.passwordUppercase': 'Contain at least one uppercase letter',
  'auth.acceptInvite.passwordLowercase': 'Contain at least one lowercase letter',
  'auth.acceptInvite.passwordNumber': 'Contain at least one number',
  'auth.acceptInvite.invalidLinkError': 'Invalid invite link. Please check your email for the correct link.',
  'auth.acceptInvite.invalidOrExpired': 'Invalid or expired invite',
  'auth.acceptInvite.failedToLoad': 'Failed to load invite information',
  'auth.acceptInvite.failedToAccept': 'Failed to accept invite',
  'auth.acceptInvite.roleAdmin': 'Administrator',
  'auth.acceptInvite.roleUser': 'Team Member',
  'auth.acceptInvite.roleViewer': 'Viewer',
  'auth.acceptInvite.roleManager': 'Manager',

  // Register (Issue #992)
  'auth.register.createBusinessAccount': 'Create a business account',
  'auth.register.createYourAccount': 'Create your account',
  'auth.register.completePartnerRegistration': 'Complete Partner Registration',
  'auth.register.getStarted': 'Get started with your organization',
  'auth.register.signUpToAccess': 'Sign up to access your portal',
  'auth.register.partnerInviteSubtitle':
    "You've been invited as a partner. Complete your registration to get your referral code.",
  'auth.register.fullName': 'Full Name',
  'auth.register.referralCode': 'Referral Code (Optional)',
  'auth.register.referralCodeHelperText': 'Have a referral code from a partner? Enter it here to get credit.',
  'auth.register.confirmPassword': 'Confirm Password',
  'auth.register.createAccount': 'Create Account',
  'auth.register.continueWithGoogle': 'Continue with Google',
  'auth.register.alreadyHaveAccount': 'Already have an account?',
  'auth.register.emailAlreadyExists': 'An account with this email already exists',
  'auth.register.weakPassword': 'Password is too weak',
  'auth.register.registrationError': 'An error occurred. Please try again.',
  'auth.register.googleSignUpFailed': 'Google sign-up failed. Please try again.',
  'auth.register.registrationFailed': 'Failed to complete registration',
  'auth.register.invalidInvitationLink': 'Invalid invitation link',

  // Partner Onboarding (Issue #992)
  'auth.partnerOnboarding.title': 'Partner Onboarding',
  'auth.partnerOnboarding.invitedBy': "You've been invited by {{companyName}}",
  'auth.partnerOnboarding.invitedByWithContracts':
    "You've been invited by {{companyName}} \u2014 {{count}} contract(s) to review and sign",

  // ============================================================================
  // TEAM MANAGEMENT
  // ============================================================================
  'teamManagement.description': 'Manage internal team members and their portal access',

  // ============================================================================
  // PALETTE (Command Palette)
  // ============================================================================
  'palette.placeholder': 'Search commands...',
  'palette.empty': 'No commands available',
  'palette.noResults': 'No results found',
  'palette.recent': 'Recent',
  'palette.tryDifferent': 'Try a different search term',
  'palette.searchOrJump': 'Search or jump to...',
  'palette.search': 'Search loans, borrowers, and more…',
  'palette.openCommandPalette': 'Open command palette',

  // Mode toggle (Issue #236 - Enhanced Command Palette)
  'palette.mode.search': 'Search',
  'palette.mode.chat': 'Chat',
  'palette.mode.toggle': 'Toggle mode',
  'palette.mode.switchToChat': 'to chat',
  'palette.mode.switchToSearch': 'to search',

  // Preview panel
  'palette.preview.loading': 'Loading preview...',
  'palette.preview.askAi': 'Ask AI',
  'palette.preview.viewDetails': 'View Details',
  'palette.preview.noPreview': 'No preview available',
  'palette.preview.selectHint': 'Select an item to see details',
  'palette.preview.lastUpdated': 'Updated {{time}}',

  // Unified search + chat (redesigned UX)
  'palette.askAi': 'Ask AI: "{{query}}"',
  'palette.askAiEmpty': 'Ask AI anything...',
  'palette.askAiHint': 'Enter to ask AI',
  'palette.askAiDescription': 'Get AI assistance with your question',

  // Chat panel
  'palette.chat.placeholder': 'Ask AI anything...',
  'palette.chat.thinking': 'Thinking...',
  'palette.chat.error': 'Failed to get response',
  'palette.chat.retry': 'Retry',
  'palette.chat.clear': 'Clear chat',
  'palette.chat.contextLabel': 'Context',
  'palette.chat.clearContext': 'Clear context',
  'palette.chat.sources': 'Sources',
  'palette.chat.sourcesCount': '{{count}} sources',
  'palette.chat.noMessages': 'Ask me anything about your data',
  'palette.chat.contextHint': 'Ask about {{entity}}',
  'palette.chat.send': 'Send',
  'palette.chat.stopGenerating': 'Stop generating',

  // AI Data Chat Enhancement (Issue #261)
  // Tool Approval Dialog (Phase 5 - Human-in-the-Loop)
  'palette.chat.approvalRequired': 'Approval Required',
  'palette.chat.toolName': 'Tool',
  'palette.chat.parameters': 'Parameters',
  'palette.chat.riskFactors': 'Risk Factors',
  'palette.chat.expiresIn': 'Expires in {{seconds}}s',
  'palette.chat.approveEdited': 'Approve with edits',

  // Confidence Indicator (Phase 9)
  'palette.chat.confidenceFactors': 'Confidence Factors',
  'palette.chat.confidenceScore': 'Confidence Score',

  // Suggestions (Phase 9)
  'palette.chat.suggestions': 'Suggestions',
  'palette.chat.followUp': 'Follow-up questions',

  // Tool calls (Phase 5)
  'palette.chat.toolCall': 'Tool Call',
  'palette.chat.toolResult': 'Tool Result',
  'palette.chat.pendingApproval': 'Pending Approval',
  'palette.chat.approved': 'Approved',
  'palette.chat.rejected': 'Rejected',
  'palette.chat.executing': 'Executing...',

  // Message actions (Phase 9)
  'palette.chat.regenerate': 'Regenerate',
  'palette.chat.copyMessage': 'Copy message',
  'palette.chat.messageCopied': 'Message copied',

  // ============================================================================
  // GLOBAL SEARCH (Issue #236)
  // ============================================================================
  'search.placeholder': 'Search cases, consumers, users...',
  'search.loading': 'Searching...',
  'search.noResults': 'No results found for "{{query}}"',
  'search.tryDifferent': 'Try a different search term or check your filters',
  'search.error': 'Search failed. Please try again.',
  'search.resultCount': '{{count}} results in {{time}}ms',
  'search.viewAll': 'View all results',
  'search.clear': 'Clear search',
  // Entity type labels
  'search.results.cases': 'Cases',
  'search.results.consumers': 'Consumers',
  'search.results.users': 'Users',
  'search.results.leads': 'Leads',
  'search.results.businesses': 'Businesses',
  'search.results.documents': 'Documents',
  // Category headers
  'search.category.cases': 'Cases',
  'search.category.consumers': 'Consumers',
  'search.category.users': 'Users',
  'search.category.leads': 'Leads',
  'search.category.businesses': 'Businesses',
  'search.category.documents': 'Documents',

  // ============================================================================
  // KEYBOARD (Navigation hints)
  // ============================================================================
  'keyboard.navigate': 'Navigate',
  'keyboard.select': 'Select',
  'keyboard.close': 'Close',
  'keyboard.back': 'Back',
  'keyboard.skip': 'Skip',

  // ============================================================================
  // HELP (Help Panel)
  // ============================================================================
  'help.title': 'Help Center',
  'help.subtitle': 'Find answers and learn how to use the platform',
  'help.search': 'Search help articles...',
  'help.noResults': 'No articles match your search',
  'help.empty': 'No help articles available',
  'help.needHelp': 'Need more help?',
  'help.contactSupport': 'Contact our support team',

  // ============================================================================
  // HINT (Feature Hints)
  // ============================================================================
  'hint.learnMore': 'Learn more',
  'hint.moreInfo': 'More information',
  'hint.shortcut': 'Shortcut:',

  // ============================================================================
  // TOUR (Spotlight Tour)
  // ============================================================================
  'tour.skip': 'Skip tour',
  'tour.next': 'Next',
  'tour.back': 'Back',
  'tour.finish': 'Finish',
  'tour.step': 'Step {{current}} of {{total}}',

  // ============================================================================
  // COMMAND (Consumer Portal Commands)
  // ============================================================================
  'command.dashboard': 'Dashboard',
  'command.documents': 'My Documents',
  'command.profile': 'Profile Settings',
  'command.uploadDoc': 'Upload Document',
  'command.helpCenter': 'Help Center',
  'command.contactSupport': 'Contact Support',
  // Descriptions
  'command.caseStatus': 'View your case status and progress',
  'command.uploadFiles': 'View and upload documents',
  'command.updateContact': 'Update your contact information',
  'command.newDocument': 'Upload a new document for your case',
  'command.commonQuestions': 'Find answers to common questions',
  'command.supportHelp': 'Get help from our team',
  // Additional consumer navigation (Command Palette)
  'command.applications': 'My Applications',
  'command.viewApplications': 'View your loan applications',
  'command.proposals': 'Proposals',
  'command.viewProposals': 'View loan proposals',
  'command.quotes': 'Quotes',
  'command.viewQuotes': 'View price quotes',
  'command.portfolio': 'Portfolio',
  'command.viewPortfolio': 'View your loan portfolio',
  'command.commissions': 'Commissions',
  'command.viewCommissions': 'View your commissions',
  'command.referrals': 'Referrals',
  'command.viewReferrals': 'View your referrals',
  'command.reports': 'Reports',
  'command.viewReports': 'View reports',
  'command.records': 'Records',
  'command.viewRecords': 'View your records',

  // ============================================================================
  // CATEGORY (Command Palette Categories)
  // ============================================================================
  'category.goTo': 'Go to',
  'category.quickActions': 'Quick Actions',
  'category.helpSupport': 'Help & Support',
  'category.navigation': 'Navigation',
  'category.adminOperations': 'Admin Operations',
  'category.search': 'Search',

  // ============================================================================
  // ADMIN PORTAL COMMANDS
  // ============================================================================
  // Navigation labels
  'admin.goToDashboard': 'Go to Dashboard',
  'admin.goToTenants': 'Go to Tenants',
  'admin.goToBusinesses': 'Go to Businesses',
  'admin.goToUsers': 'Go to Users',
  'admin.goToPipelines': 'Go to Pipelines',
  'admin.goToAgents': 'Go to Agents',
  'admin.goToBilling': 'Go to Billing',
  'admin.goToAnalytics': 'Go to Analytics',
  'admin.goToSecurity': 'Go to Security',
  'admin.goToSettings': 'Go to Settings',
  // Action labels
  'admin.createRealm': 'Create New Realm',
  'admin.createTenant': 'Create New Tenant',
  'admin.createUser': 'Create New User',
  'admin.createBusiness': 'Create New Business',
  'admin.openControlTower': 'Open Control Tower',
  'admin.viewAuditLogs': 'View Audit Logs',
  'admin.manageApiKeys': 'Manage API Keys',
  // Descriptions
  'admin.dashboardDesc': 'View admin dashboard overview',
  'admin.tenantsDesc': 'Manage tenants',
  'admin.businessesDesc': 'Manage businesses',
  'admin.usersDesc': 'Manage user accounts',
  'admin.pipelinesDesc': 'Manage pipelines',
  'admin.agentsDesc': 'Manage agents',
  'admin.billingDesc': 'View billing and subscriptions',
  'admin.analyticsDesc': 'View platform analytics',
  'admin.securityDesc': 'Security settings and audit logs',
  'admin.settingsDesc': 'System settings',
  'admin.newTenantDesc': 'Add a new tenant organization',
  'admin.newUserDesc': 'Add a new user account',
  'admin.newBusinessDesc': 'Add a new business',
  'admin.controlTowerDesc': 'Monitor system health and incidents',
  'admin.auditLogsDesc': 'Review system audit logs',
  // Delete business dialog
  'admin.deleteBusinessTitle': 'Delete Business Account',
  'admin.deleteBusinessWarning': 'This action cannot be undone!',
  'admin.deleteBusinessDescription': 'You are about to permanently delete the following business:',
  'admin.deleteBusinessThisWill': 'This will:',
  'admin.deleteBusinessDeleteData': 'Permanently delete all business data',
  'admin.deleteBusinessRemoveUsers': 'Remove all associated users',
  'admin.deleteBusinessCancelSubs': 'Cancel active subscriptions',
  'admin.deleteBusinessDeleteHistory': 'Delete all usage history',
  'admin.deleteBusinessExportLabel': 'Export business data before deletion (GDPR compliance)',
  'admin.deleteBusinessConfirmLabel': 'I understand this action is permanent and cannot be undone',
  'admin.deleteBusinessFailed': 'Failed to delete business: {{error}}',
  'admin.apiKeysDesc': 'View and manage API keys',
  // Additional navigation (Command Palette)
  'admin.goToConsumers': 'Go to Consumers',
  'admin.consumersDesc': 'Manage consumer accounts',
  'admin.goToDocuments': 'Go to Documents',
  'admin.documentsDesc': 'Manage platform documents',
  'admin.goToEntities': 'Go to Entities',
  'admin.entitiesDesc': 'Manage custom entities',
  'admin.goToIntegrations': 'Go to Integrations',
  'admin.integrationsDesc': 'Manage integrations and APIs',
  'admin.goToModels': 'Go to Models',
  'admin.modelsDesc': 'Manage AI models and providers',
  'admin.goToPricing': 'Go to Pricing',
  'admin.pricingDesc': 'Manage platform pricing',
  'admin.goToRealms': 'Go to Realms',
  'admin.realmsDesc': 'Manage tenant realms',
  'admin.goToSandboxes': 'Go to Sandboxes',
  'admin.sandboxesDesc': 'Manage sandbox environments',
  'admin.goToIdentity': 'Go to Identity',
  'admin.identityDesc': 'Manage identity verification',
  'admin.goToCompliance': 'Go to Compliance',
  'admin.complianceDesc': 'Manage compliance rules',
  'admin.goToAgentPlayground': 'Go to Agent Playground',
  'admin.agentPlaygroundDesc': 'Test and debug AI agents',
  'admin.goToSignup': 'Go to Signup Management',
  'admin.signupDesc': 'Manage signup and onboarding',
  'admin.goToTemplates': 'Go to Templates',
  'admin.templatesDesc': 'Manage intake and form templates',
  // Placeholder
  'admin.searchPlaceholder': 'Search commands, navigate, or perform admin actions...',
  // Impersonation
  'admin.impersonation.loggingInfo': 'All actions during impersonation are logged for security purposes',
  'admin.impersonation.targetConsumer': 'Target Consumer',
  'admin.impersonation.targetUser': 'Target User',

  // ============================================================================
  // CONTROL CENTER (Business Portal)
  // ============================================================================
  'controlCenter.createAgent': 'Create Agent',
  'controlCenter.permissions.roleMatrix': 'Role Matrix',
  'controlCenter.permissions.myAccess': 'My Access',
  'controlCenter.permissions.customRoles': 'Custom Roles',
  'controlCenter.permissions.accessAudit': 'Access Audit',
  // Control Center overview hub — system health tiles + section list (#1758)
  'controlCenter.overview.systemHealth': 'System health',
  'controlCenter.overview.tiles.activeStages.label': 'Active stages',
  'controlCenter.overview.tiles.activeStages.sub': '{{total}} configured',
  'controlCenter.overview.tiles.activePolicies.label': 'Active policies',
  'controlCenter.overview.tiles.activePolicies.sub': '{{drafts}} draft',
  'controlCenter.overview.tiles.loanProducts.label': 'Loan products',
  'controlCenter.overview.tiles.loanProducts.sub': '{{active}} of {{total}} active',
  'controlCenter.overview.tiles.loanProducts.subAllActive': 'All active',
  'controlCenter.overview.tiles.autonomy.label': 'Astor autonomy',
  // Autonomy short label for the KPI tile value
  'controlCenter.overview.autonomy.L1': 'L1 · observe',
  'controlCenter.overview.autonomy.L2': 'L2 · supervised',
  'controlCenter.overview.autonomy.L3': 'L3 · trusted',
  'controlCenter.overview.autonomy.L4': 'L4 · full',
  // Autonomy subtitle for the KPI tile sub-text
  'controlCenter.overview.autonomySub.L1': 'Astor suggests; you act',
  'controlCenter.overview.autonomySub.L2': 'Astor recommends; you approve',
  'controlCenter.overview.autonomySub.L3': 'Astor acts within guardrails',
  'controlCenter.overview.autonomySub.L4': 'Astor acts; escalates exceptions',
  // PageTabBar tab labels (t(tab.label) uses raw label text as key)
  'Role Matrix': 'Role Matrix',
  'My Access': 'My Access',
  'Custom Roles': 'Custom Roles',
  'Access Audit': 'Access Audit',

  // ============================================================================
  // CONTROL TOWER
  // ============================================================================
  // Navigation
  'controlTower.title': 'Control Tower',
  'controlTower.overview': 'Overview',
  'controlTower.scenarios': 'Canary Scenarios',
  'controlTower.slaHealth': 'SLA Health',
  'controlTower.signalHealth': 'Signal Health',
  'controlTower.reports': 'Reports',
  'controlTower.metrics': 'Metrics',
  'controlTower.incidents': 'Incidents',
  'controlTower.testRuns': 'Test Runs',
  'controlTower.networkLogs': 'Network Logs',
  // Descriptions
  'controlTower.overviewDesc': 'Platform monitoring dashboard with summary cards and service health',
  'controlTower.scenariosDesc': 'Canary test scenarios for validating platform health and critical workflows',
  'controlTower.slaHealthDesc': 'Monitor service level agreement compliance and tracking',
  'controlTower.signalHealthDesc': 'Monitor signal collection status and manage baselines',
  'controlTower.reportsDesc': 'Generate and export signal reports and sandbox summaries',
  'controlTower.metricsDesc': 'Platform performance metrics and time series data',
  'controlTower.incidentsDesc': 'Active and resolved incidents across services',
  'controlTower.testRunsDesc': 'Historical test execution results',
  'controlTower.networkLogsDesc': 'API request and response logs across services',
  // Actions
  'controlTower.refresh': 'Refresh',
  'controlTower.refreshAll': 'Refresh All',
  'controlTower.runAll': 'Run All',
  'controlTower.runScenario': 'Run Scenario',
  'controlTower.viewDetails': 'View Details',
  'controlTower.exportJson': 'Export JSON',
  'controlTower.rerun': 'Re-run',
  'controlTower.acknowledge': 'Acknowledge',
  'controlTower.resolve': 'Resolve',
  // Status
  'controlTower.passed': 'Passed',
  'controlTower.failed': 'Failed',
  'controlTower.running': 'Running',
  'controlTower.pending': 'Pending',
  'controlTower.timeout': 'Timeout',
  // Scenarios page
  'controlTower.totalScenarios': 'Total Scenarios',
  'controlTower.passRate': 'Pass Rate',
  'controlTower.avgDuration': 'Avg Duration',
  'controlTower.lastRun': 'Last Run',
  'controlTower.searchScenarios': 'Search scenarios...',
  'controlTower.noScenarios': 'No Canary scenarios found. Initialize the predefined scenarios to get started.',
  'controlTower.initializeScenarios': 'Initialize Scenarios',
  'controlTower.initializing': 'Initializing...',
  // Execution detail page
  'controlTower.backToScenarios': 'Back to Scenarios',
  'controlTower.runId': 'Run ID',
  'controlTower.traceId': 'Trace ID',
  'controlTower.duration': 'Duration',
  'controlTower.startedAt': 'Started At',
  'controlTower.thresholdBreaches': 'Threshold Breaches',
  'controlTower.anomaliesDetected': 'Anomalies Detected',
  'controlTower.whyDidThisFail': 'Why Did This Fail?',
  'controlTower.scenarioFailedDueTo': 'The scenario failed due to:',
  'controlTower.serviceFlowResults': 'Service Flow Results',
  'controlTower.anomalyDetection': 'Anomaly Detection',
  'controlTower.correlationAnalysis': 'Correlation Analysis',
  'controlTower.noAnomalies': 'No anomalies detected',
  'controlTower.noCorrelations': 'No significant correlations detected',
  'controlTower.noBreaches': 'No threshold breaches detected',
  'controlTower.accessDeniedDescription': 'You do not have permission to access Control Tower',
  'controlTower.correlationInsight': 'Correlation Insight',
  'controlTower.detection': 'Detection',
  'controlTower.noAnomaliesDetected': 'No anomalies detected in this time range',
  'controlTower.noSignificantCorrelations': 'No significant correlations found',
  'controlTower.noThresholdBreaches': 'All metrics are within acceptable thresholds',
  'controlTower.trace': 'Trace',
  // Filters
  'controlTower.filterByStatus': 'Status',
  'controlTower.filterByPriority': 'Priority',
  'controlTower.filterBySeverity': 'Severity',
  'controlTower.clearFilters': 'Clear filters',
  'controlTower.filterByTags': 'Tags',
  'controlTower.filterByTagsPlaceholder': 'Filter by tags',
  // Filter options
  'controlTower.all': 'All',
  'controlTower.passing': 'Passing',
  'controlTower.failing': 'Failing',
  'controlTower.neverRun': 'Never Run',
  'controlTower.critical': 'Critical',
  'controlTower.high': 'High',
  'controlTower.medium': 'Medium',
  'controlTower.low': 'Low',
  // Scenario details
  'controlTower.scenarioDetails': 'Scenario Details',
  'controlTower.scenarioCode': 'Scenario Code',
  'controlTower.lastExecution': 'Last Execution',
  'controlTower.runAt': 'Run At',
  'controlTower.viewExecutionDetails': 'View Execution Details',
  'controlTower.serviceFlow': 'Service Flow',
  'controlTower.signalThresholds': 'Signal Thresholds',
  'controlTower.recentExecutions': 'Recent Executions',
  'controlTower.noExecutions': 'No executions yet',
  'controlTower.quickRun': 'Quick Run',
  // Execution detail
  'controlTower.copyTraceId': 'Copy Trace ID',
  'controlTower.traceIdCopied': 'Trace ID copied',
  'controlTower.noServiceResults': 'No service results available',
  'controlTower.signalTrace': 'Signal Trace',
  'controlTower.signalRelationships': 'Signal relationships that may indicate root cause',
  'controlTower.expected': 'Expected',
  'controlTower.actual': 'Actual',
  'controlTower.deviation': 'Deviation',
  'controlTower.correlation': 'Correlation',
  'controlTower.lag': 'Lag',
  'controlTower.signalType': 'Signal Type',
  'controlTower.service': 'Service',
  'controlTower.threshold': 'Threshold',
  'controlTower.severity': 'Severity',
  'controlTower.noDescription': 'No description',
  'controlTower.enabled': 'Enabled',
  'controlTower.disabled': 'Disabled',
  'controlTower.active': 'Active',
  'controlTower.none': 'None',
  'controlTower.normal': 'Normal',
  'controlTower.accessDenied': 'Access Denied',
  'controlTower.businessIdNotFound':
    'Business ID not found. Please ensure you are properly authenticated with a valid business account.',
  'controlTower.missingExecutionId': 'Missing Execution ID',
  'controlTower.noExecutionIdProvided': 'No execution ID was provided in the URL.',
  'controlTower.errorLoadingExecution': 'Error Loading Execution',
  'controlTower.executionNotFound': 'Execution not found',
  'controlTower.goBack': 'Go Back',
  'controlTower.close': 'Close',
  'controlTower.scenarioPassed': 'Scenario passed',
  'controlTower.scenarioFailed': 'Scenario failed',
  'controlTower.scenarioStarted': 'Scenario started',
  'controlTower.failedToRunScenario': 'Failed to run scenario',
  'controlTower.exportedToJson': 'Exported to JSON',
  'controlTower.failedToLoadExecution': 'Failed to load execution details',
  'controlTower.unknownError': 'Unknown error',
  'controlTower.status': 'Status',
  'controlTower.tags': 'Tags',
  'controlTower.priority': 'Priority',
  'controlTower.noExecutionsYet': 'No executions yet',
  // Header bar
  'controlTower.needAttention': '{{count}} Need Attention',
  'controlTower.pendingCount': '{{count}} pending',
  'controlTower.failedCount': '{{count}} failed',
  'controlTower.loadingScenarios': 'Loading scenarios...',
  'controlTower.noDataLabel': 'No Data',
  'controlTower.byPriority': 'By Priority',
  'controlTower.bySection': 'By Section',
  'controlTower.avgTime': 'Avg Time',
  'controlTower.failedLabel': '{{count}} failed',
  'controlTower.p0Critical': 'P0 Critical',
  'controlTower.p0Description': 'Security & must-pass',
  'controlTower.p1High': 'P1 High',
  'controlTower.p1Description': 'High priority',
  'controlTower.p2Medium': 'P2 Medium',
  'controlTower.p2Description': 'Medium priority',
  'controlTower.p3Low': 'P3 Low',
  'controlTower.p3Description': 'Low priority',

  // ============================================================================
  // BUSINESS PORTAL COMMANDS
  // ============================================================================
  // Navigation labels (Issue #330 - Unified Navigation)
  'business.goToDashboard': 'Go to Dashboard',
  'business.goToDiscovery': 'Go to Discovery',
  'business.goToLeads': 'Go to Leads',
  'business.goToTermSheets': 'Go to Term Sheets',
  'business.goToCases': 'Go to Loans',
  'business.goToBorrowers': 'Go to Borrowers',
  'business.goToPartners': 'Go to Partners',
  'business.goToDocuments': 'Go to Document Approvals',
  'business.goToAgents': 'Go to Agents',
  'business.goToChat': 'Go to Chat',
  'business.goToAIHistory': 'Go to History',
  'business.goToSettings': 'Go to Settings',
  'business.goToAstor': 'Go to Astor',
  'business.goToAstorAgents': 'Go to Agents',
  // Action labels
  'business.createLead': 'Create New Lead',
  'business.createCase': 'Create New Loan',
  'business.uploadDocument': 'Upload Document',
  // Descriptions (Issue #330 - Unified Navigation)
  'business.dashboardDesc': 'View your dashboard overview',
  'business.discoveryDesc': 'Search and discover prospects',
  'business.leadsDesc': 'Manage your sales leads',
  'business.termSheetsDesc': 'View and manage term sheets',
  'business.casesDesc': 'View and manage loans',
  'business.borrowersDesc': 'View borrower profiles',
  'business.partnersDesc': 'Manage partner relationships',
  'business.documentsDesc': 'Review documents pending approval',
  'business.agentsDesc': 'Manage agents',
  'business.chatDesc': 'Open Astor chat',
  'business.aiHistoryDesc': 'View conversation history',
  'business.settingsDesc': 'Configure your settings',
  'business.astorDesc': 'Chat with Astor',
  'business.astorAgentsDesc': 'Manage agents',
  'business.newLeadDesc': 'Add a new sales lead',
  'business.newCaseDesc': 'Open a new loan',
  'business.uploadDocDesc': 'Upload a new document',
  // Additional navigation (Command Palette)
  'business.goToTasks': 'Go to Tasks',
  'business.tasksDesc': 'View and manage tasks',
  'business.goToTeamMembers': 'Go to Team Members',
  'business.teamMembersDesc': 'View team members',
  'business.goToTeamAnalytics': 'Go to Team Analytics',
  'business.teamAnalyticsDesc': 'View team performance analytics',
  'business.goToOrgChart': 'Go to Org Chart',
  'business.orgChartDesc': 'View organization structure',
  'business.goToEmailCampaigns': 'Go to Email Campaigns',
  'business.emailCampaignsDesc': 'Manage email marketing campaigns',
  'business.goToVoiceCampaigns': 'Go to Voice Campaigns',
  'business.voiceCampaignsDesc': 'Manage voice outreach campaigns',
  'business.goToOriginationCases': 'Go to Origination Loans',
  'business.originationCasesDesc': 'View loan origination loans',
  'business.goToOriginationBorrowers': 'Go to Origination Borrowers',
  'business.originationBorrowersDesc': 'View origination borrowers',
  'business.goToOriginationTermSheets': 'Go to Origination Term Sheets',
  'business.originationTermSheetsDesc': 'View origination term sheets',
  'business.goToIntakeSubmissions': 'Go to Intake Submissions',
  'business.intakeSubmissionsDesc': 'Review intake form submissions',
  'business.goToCrmLeads': 'Go to CRM Leads',
  'business.crmLeadsDesc': 'Manage CRM leads',
  'business.goToLeadScoring': 'Go to Lead Scoring',
  'business.leadScoringDesc': 'Configure lead scoring rules',
  'business.goToEntities': 'Go to Entities',
  'business.entitiesDesc': 'Manage custom entities',
  'business.goToPricingRules': 'Go to Pricing Rules',
  'business.pricingRulesDesc': 'Configure pricing rules',
  'business.goToPricingQuotes': 'Go to Pricing Quotes',
  'business.pricingQuotesDesc': 'View and manage quotes',
  'business.goToPricingMatrices': 'Go to Pricing Matrices',
  'business.pricingMatricesDesc': 'Configure pricing matrices',
  'business.goToEnrichment': 'Go to Enrichment',
  'business.enrichmentDesc': 'View data enrichment history',
  'business.goToAnalytics': 'Go to Analytics',
  'business.analyticsDesc': 'View application analytics',
  'business.goToNotificationTemplates': 'Go to Notification Templates',
  'business.notificationTemplatesDesc': 'Manage notification templates',
  'business.goToNotificationTriggers': 'Go to Notification Triggers',
  'business.notificationTriggersDesc': 'Configure notification triggers',
  // Additional navigation entries (command palette coverage)
  'business.goToInbox': 'Go to Inbox',
  'business.inboxDesc': 'View messages and communications',
  'business.goToContacts': 'Go to Contacts',
  'business.contactsDesc': 'Browse the unified contacts directory',
  'business.goToGuarantors': 'Go to Guarantors',
  'business.guarantorsDesc': 'Manage loan guarantors',
  'business.goToLoanDocuments': 'Go to Loan Documents',
  'business.loanDocumentsDesc': 'Manage loan-specific documents',
  'business.goToProspectingLeads': 'Go to Leads',
  'business.prospectingLeadsDesc': 'View and manage prospect leads',
  'business.goToSequences': 'Go to Sequences',
  'business.sequencesDesc': 'Manage email drip sequences',
  'business.goToCalculators': 'Go to Calculators',
  'business.calculatorsDesc': 'Financial calculator tools',
  'business.goToDscrCalculator': 'DSCR Calculator',
  'business.dscrCalculatorDesc': 'Calculate debt service coverage ratio',
  'business.goToMonthlyPaymentCalculator': 'Monthly Payment Calculator',
  'business.monthlyPaymentCalculatorDesc': 'Calculate monthly loan payments',
  'business.goToCashToCloseCalculator': 'Cash to Close Calculator',
  'business.cashToCloseCalculatorDesc': 'Estimate cash needed at closing',
  'business.goToReports': 'Go to Reports',
  'business.reportsDesc': 'View pipeline and financial reports',
  'business.goToPerformanceReports': 'Go to Performance Reports',
  'business.performanceReportsDesc': 'View team and loan performance metrics',
  'business.goToCustomReports': 'Go to Custom Reports',
  'business.customReportsDesc': 'Build and view custom reports',
  'business.goToNotifications': 'Go to Notifications',
  'business.notificationsDesc': 'View notification center',
  'business.goToNotificationAnalytics': 'Go to Notification Analytics',
  'business.notificationAnalyticsDesc': 'View notification delivery and engagement metrics',
  'business.goToReferrals': 'Go to Referrals',
  'business.referralsDesc': 'Track inbound and outbound referrals',
  'business.goToPartnershipAgreements': 'Go to Partnership Agreements',
  'business.partnershipAgreementsDesc': 'Manage partner contracts and terms',
  'business.goToCommissions': 'Go to Commissions',
  'business.commissionsDesc': 'View partnership commission payouts',
  'business.goToPartnerApplications': 'Go to Partner Applications',
  'business.partnerApplicationsDesc': 'Review pending partner applications',
  'business.goToInvitePartner': 'Invite a Partner',
  'business.invitePartnerDesc': 'Send a partner invitation',
  'business.goToEnrichmentHistory': 'Go to Enrichment History',
  'business.enrichmentHistoryDesc': 'View data enrichment run history',
  'business.goToSubTeams': 'Go to Sub-Teams',
  'business.subTeamsDesc': 'Manage team groups and departments',
  'business.goToDirectReports': 'Go to Direct Reports',
  'business.directReportsDesc': 'View your direct reports',
  'business.goToTeamOnboarding': 'Go to Team Onboarding',
  'business.teamOnboardingDesc': 'Manage new team member onboarding',
  'business.goToPipelines': 'Go to Pipelines',
  'business.pipelinesDesc': 'View and manage pipeline boards',
  'business.goToMarketing': 'Go to Marketing',
  'business.marketingDesc': 'Manage marketing campaigns and promotions',
  'business.goToOfferings': 'Go to Offerings',
  'business.offeringsDesc': 'Manage loan product offerings',
  // Placeholder
  'business.searchPlaceholder': 'Search commands, navigate, or take action...',

  // ============================================================================
  // TEAM (Team Management - Issue #233)
  // ============================================================================
  // Page titles
  'team.title': 'Team',
  'team.members': 'Team Members',
  'team.settings': 'Team Settings',
  'team.management': 'Team Management',
  'team.team': 'Team',

  // Table headers
  'team.name': 'Name',
  'team.email': 'Email',
  'team.role': 'Role',
  'team.status': 'Status',
  'team.joinedAt': 'Joined',
  'team.lastActive': 'Last Active',

  // Roles
  'team.roles.admin': 'Admin',
  'team.roles.manager': 'Manager',
  'team.roles.member': 'Member',
  'team.roles.viewer': 'Viewer',
  'team.roles.partner': 'Partner',
  'team.roles.borrower': 'Borrower',
  'team.roles.business_user': 'Business User',
  'team.roles.business_admin': 'Business Admin',
  'team.roles.business_viewer': 'Business Viewer',
  'team.roles.super_admin': 'Super Admin',
  'team.roles.platform_admin': 'Platform Admin',
  'team.roles.analyst': 'Analyst',
  'team.roles.underwriter': 'Underwriter',
  'team.roles.closer': 'Closer',
  'team.roles.ops_manager': 'Ops Manager',
  'team.roles.processor': 'Processor',
  'team.roles.originator': 'Originator',
  'team.roles.compliance': 'Compliance',

  // Role descriptions
  'team.roleDescriptions.admin': 'Full access to all features, can manage team members and settings.',
  'team.roleDescriptions.manager': 'Can manage loans, documents, and view reports.',
  'team.roleDescriptions.member': 'Standard access to assigned loans and documents.',
  'team.roleDescriptions.viewer': 'Read-only access to loans and documents.',
  'team.roleDescriptions.partner': 'External partner with limited access',
  'team.roleDescriptions.borrower': 'Loan applicant with borrower portal access',
  'team.roleDescriptions.business_user': 'Standard access to assigned loans and documents.',
  'team.roleDescriptions.business_admin': 'Full access to all features, can manage team members and settings.',
  'team.roleDescriptions.business_viewer': 'Read-only access to loans and documents.',
  'team.roleDescriptions.super_admin': 'Platform super administrator with unrestricted access',
  'team.roleDescriptions.platform_admin': 'Platform administrator with system-wide access',

  // Status
  'team.statuses.active': 'Active',
  'team.statuses.pending': 'Pending Invite',
  'team.statuses.invited': 'Invited',
  'team.statuses.inactive': 'Inactive',
  'team.statuses.suspended': 'Suspended',
  'team.statuses.deleted': 'Deleted',
  'team.statuses.deactivated': 'Deactivated',

  // Actions
  'team.actions.invite': 'Add Member',
  'team.actions.inviteMultiple': 'Add Multiple',
  'team.actions.remove': 'Deactivate Member',
  'team.actions.updateRole': 'Update Role',
  'team.actions.resendInvite': 'Resend Invite',
  'team.actions.cancelInvite': 'Cancel Invite',
  'team.actions.export': 'Export Team',
  'team.actions.viewProfile': 'View Profile',
  'team.actions.permanentlyDelete': 'Permanently Delete',

  // Permanent delete dialog
  'team.permanentlyDelete.title': 'Permanently Delete Team Member',
  'team.permanentlyDelete.irreversible': 'This action cannot be undone!',
  'team.permanentlyDelete.description': 'You are about to permanently delete the following team member:',
  'team.permanentlyDelete.consequences': 'This will permanently:',
  'team.permanentlyDelete.consequence1': 'Delete the user account and all associated data',
  'team.permanentlyDelete.consequence2': 'Remove all team memberships and role assignments',
  'team.permanentlyDelete.consequence3': 'Delete the authentication record (login will be impossible)',
  'team.permanentlyDelete.consequence4': 'Cancel any pending invitations',
  'team.permanentlyDelete.checkboxLabel': 'I understand this action is permanent and cannot be undone',
  'team.permanentlyDelete.confirm': 'Delete Permanently',
  'team.permanentlyDelete.deleting': 'Deleting...',
  'team.permanentlyDelete.success': 'User permanently deleted',
  'team.permanentlyDelete.warning': 'Warning:',
  'team.permanentlyDelete.errorPrefix': 'Failed to delete user:',

  // Invite modal
  'team.invite.title': 'Add Team Member',
  'team.invite.titleMultiple': 'Add Team Members',
  'team.invite.emailLabel': 'Email Address',
  'team.invite.emailPlaceholder': 'colleague@company.com',
  'team.invite.nameLabel': 'Full Name',
  'team.invite.namePlaceholder': 'John Smith',
  'team.invite.roleLabel': 'Role',
  'team.invite.rolePlaceholder': 'Select a role',
  'team.invite.messageLabel': 'Personal Message (Optional)',
  'team.invite.messagePlaceholder': 'Add a welcome message...',
  'team.invite.submit': 'Create Account',
  'team.invite.submitMultiple': 'Create Accounts',
  'team.invite.cancel': 'Cancel',
  // Extended fields (Issue #296)
  'team.invite.phoneLabel': 'Phone Number',
  'team.invite.phonePlaceholder': '+1 (555) 123-4567',
  'team.invite.phoneHelper': 'Optional - for contact purposes',
  'team.invite.timezoneLabel': 'Timezone',
  'team.invite.managerLabel': 'Direct Manager',
  'team.invite.managerPlaceholder': 'Select a manager',
  'team.invite.teamLabel': 'Team Assignment',
  'team.invite.teamPlaceholder': 'Select a team',
  'team.invite.processing': 'Creating account...',
  'team.invite.sendEmail': 'Send invite email',
  'team.invite.sendEmailHelper': 'Send an email notification with login instructions',
  'team.invite.inviteExpires': 'Invite expires in 7 days',

  // Messages
  'team.messages.inviteSuccess': 'Team member created successfully',
  'team.messages.inviteSuccessMultiple': '{{count}} team members created successfully',
  'team.messages.removeSuccess': 'Team member removed',
  'team.messages.updateSuccess': 'Role updated successfully',
  'team.messages.exportSuccess': 'Team data exported',
  'team.messages.inviteResent': 'Invitation resent',
  'team.messages.inviteCancelled': 'Invitation cancelled',

  // Errors
  'team.errors.cannotChangeOwnRole': 'You cannot change your own role',
  'team.errors.cannotRemoveYourself': 'You cannot remove yourself from the team',
  'team.errors.memberNotFound': 'Team member not found',
  'team.errors.inviteFailed': 'Failed to send invitation',
  'team.errors.emailAlreadyExists': 'A user with this email already exists',
  'team.errors.alreadyTeamMember': 'This user is already a member of your team',
  'team.errors.existsOnPlatform':
    'This email belongs to a user on another organization. Contact them directly or reach out to support.',
  'team.errors.invalidEmail': 'Please enter a valid email address',
  'team.emailCheck.available': 'Email available',
  'team.errors.rateLimitExceeded': 'Too many invites. Please try again later.',
  'team.errors.exportFailed': 'Failed to export team data',

  // Empty states
  'team.empty.title': 'No team members yet',
  'team.empty.description': 'Invite your first team member to get started',
  'team.empty.action': 'Invite Member',
  'team.empty.searchNoResults': 'No members match your search',
  'team.empty.filterNoResults': 'No members match the selected filters',
  'team.empty.noMembers': 'No team members yet',
  'team.empty.filterNoResultsDescription': 'Try adjusting your search or filter criteria',

  // Confirmations
  'team.confirm.removeTitle': 'Deactivate Team Member',
  'team.confirm.removeMessage':
    'Are you sure you want to deactivate {{name}}? They will lose access but their data will be preserved. You can reactivate them later.',
  'team.confirm.removeConfirm': 'Deactivate Member',
  'team.confirm.removeCancel': 'Cancel',

  // Offboarding (Issue #233 - P1)
  'team.offboarding.title': 'Deactivate Team Member',
  'team.offboarding.warning':
    'This will revoke all active sessions and remove access to all business resources. The member can be reactivated later.',
  'team.offboarding.sessionsRevoked': '{{count}} active sessions revoked',
  'team.offboarding.success': 'Team member has been deactivated and offboarded successfully',
  'team.offboarding.processing': 'Deactivating member...',
  // Issue #273 - Manager reassignment on offboarding
  'team.offboarding.reassignReports': 'This member has {{count}} direct report(s) that need to be reassigned:',
  'team.offboarding.newManager': 'New Manager',
  'team.offboarding.noManager': 'No manager (remove assignment)',
  'team.offboarding.reassignNote':
    'Direct reports will be reassigned to the selected manager when this member is removed.',

  // Licensing (Issue #1223 GAP 8)
  'team.licensing.nmlsId': 'NMLS ID',
  'team.licensing.nmlsIdDescription': 'Nationwide Multistate Licensing System identifier for mortgage licensing',
  'team.licensing.notSet': 'Not set',
  'team.licensing.licensedStates': 'Licensed States',
  'team.licensing.noLicensedStates': 'No licensed states on file',

  // Activity
  'team.activity.title': 'Activity',
  'team.activity.lastLogin': 'Last login',
  'team.activity.casesWorked': 'Loans worked',
  'team.activity.documentsUploaded': 'Documents uploaded',
  'team.activity.neverLoggedIn': 'Never logged in',
  'team.activity.assignedCases': 'Assigned Loans',
  'team.activity.resolved': 'Resolved',
  'team.activity.inProgress': 'In Progress',

  // Filters
  'team.filters.all': 'All Members',
  'team.filters.allTeams': 'All Teams',
  'team.filters.byRole': 'Filter by Role',
  'team.filters.byStatus': 'Filter by Status',
  'team.filters.byTeam': 'Filter by Team',
  'team.filters.search': 'Search members...',

  // Bulk Invite (Issue #233 - P0)
  'team.bulk.title': 'Add Multiple Members',
  'team.bulk.description': 'Enter email addresses separated by commas or new lines (2-10 emails)',
  'team.bulk.emailsLabel': 'Email Addresses',
  'team.bulk.emailsPlaceholder': 'john@example.com, jane@example.com',
  'team.bulk.roleLabel': 'Role for all invites',
  'team.bulk.submit': 'Create {{count}} Accounts',
  'team.bulk.processing': 'Creating accounts...',
  'team.bulk.successCount': '{{success}} of {{total}} accounts created successfully',
  'team.bulk.partialSuccess': 'Some invites failed. Check the details below.',
  'team.bulk.validation.minEmails': 'Enter at least 2 email addresses',
  'team.bulk.validation.maxEmails': 'Maximum 10 email addresses allowed',
  'team.bulk.validation.duplicateEmail': 'Duplicate email: {{email}}',
  'team.bulk.validation.invalidEmails': 'Invalid email addresses: {{emails}}',
  'team.bulk.switchToSingle': 'One person',
  'team.bulk.switchToBulk': 'Multiple people',
  'team.bulk.sendEmail': 'Send invite emails',
  'team.bulk.sendEmailHelper': 'Send email notifications with login instructions to all members',

  // Export (Issue #233 - P0)
  'team.export.button': 'Export',
  'team.export.title': 'Export Team Members',
  'team.export.description': 'Download a CSV file with team member data',
  'team.export.formatLabel': 'Format',
  'team.export.formatCSV': 'CSV (Excel compatible)',
  'team.export.includeLabel': 'Include',
  'team.export.includeAll': 'All members',
  'team.export.includeActive': 'Active members only',
  'team.export.includeInvited': 'Pending invites only',
  'team.export.columnsLabel': 'Columns',
  'team.export.columnName': 'Name',
  'team.export.columnEmail': 'Email',
  'team.export.columnRole': 'Role',
  'team.export.columnStatus': 'Status',
  'team.export.columnCreatedAt': 'Joined Date',
  'team.export.columnLastLogin': 'Last Login',
  'team.export.download': 'Download CSV',
  'team.export.downloading': 'Preparing download...',

  // My Direct Reports (Issue #273 - Manager Assignment)
  'team.myDirectReports.title': 'My Direct Reports',
  'team.myDirectReports.subtitle': 'View and manage your team members',
  'team.myDirectReports.count': '{{count}} direct reports',
  'team.myDirectReports.viewOrgChart': 'View Org Chart',
  'team.myDirectReports.noReports': 'No direct reports',
  'team.myDirectReports.noReportsDescription': 'Team members assigned to you will appear here',
  'team.bulk.subtitle': 'Add multiple team members to your organization at once',
  'team.invite.subtitle': 'Add a new team member to your organization',

  // Personal Referral Codes (Issue #369)
  'team.personalReferralCode': 'Personal Referral Code',
  'team.noReferralCode': 'You do not have a personal referral code yet. Generate one to start earning commissions.',
  'team.generateReferralCode': 'Generate Code',
  'team.referralClicks': 'Clicks',
  'team.referralLeads': 'Leads',
  'team.referralConversions': 'Conversions',
  'team.referralCodeLabel': 'Code',
  'team.referralEarnings': 'Earnings',
  'team.referralPending': 'Pending',

  'team.members.noAssignedCases': 'No loans currently assigned to this team member',

  // Invite
  'invite.title': 'Send Invitation',
  'invite.subtitle': 'Invite someone to join',
  'invite.email': 'Email',
  'invite.sms': 'SMS',
  'invite.both': 'Email & SMS',
  'invite.deliveryChannel': 'Delivery Channel',
  'invite.template': 'Template',
  'invite.templateAndMessage': 'Template & Message',
  'invite.customMessage': 'Custom Message',
  'invite.customMessagePlaceholder': 'Add a personal note to the invitation...',
  'invite.expiresIn': 'Expires In',
  'invite.noTemplates': 'No templates available',
  'invite.send': 'Send Invitation',
  'invite.sendFailed': 'Failed to send invitation',
  'invite.sendingTo': 'Sending invitation to {{recipient}}...',

  // Teams / Departments (Issue #233 - P1)
  'teams.title': 'Teams',
  'teams.subtitle': 'Organize your team members into departments',
  'teams.create': 'Create Team',
  'teams.edit': 'Edit Team',
  'teams.delete': 'Delete Team',
  'teams.addMember': 'Add Member',
  'teams.removeMember': 'Deactivate Member',
  'teams.viewMembers': 'View Members',
  'teams.noTeams': 'No teams created yet',
  'teams.noTeamsDescription': 'Create your first team to organize your members',

  // Team form
  'teams.form.name': 'Team Name',
  'teams.form.namePlaceholder': 'e.g., Engineering, Sales, Support',
  'teams.form.description': 'Description',
  'teams.form.descriptionPlaceholder': 'What does this team do?',
  'teams.form.color': 'Team Color',
  'teams.form.icon': 'Team Icon',
  'teams.form.manager': 'Team Manager',
  'teams.form.managerPlaceholder': 'Select a manager',

  // Team member roles
  'teams.memberRoles.member': 'Member',
  'teams.memberRoles.lead': 'Lead',

  // Team errors
  'teams.errors.notFound': 'Team not found',
  'teams.errors.nameExists': 'A team with this name already exists',
  'teams.errors.alreadyMember': 'This user is already a member of this team',
  'teams.errors.notMember': 'This user is not a member of this team',
  'teams.errors.createFailed': 'Failed to create team',
  'teams.errors.updateFailed': 'Failed to update team',
  'teams.errors.deleteFailed': 'Failed to delete team',

  // Team messages
  'teams.messages.created': 'Team created successfully',
  'teams.messages.updated': 'Team updated successfully',
  'teams.messages.deleted': 'Team deleted successfully',
  'teams.messages.memberAdded': 'Member added to team',
  'teams.messages.memberRemoved': 'Member removed from team',
  'teams.messages.roleUpdated': 'Member role updated',

  // Team confirmations
  'teams.confirm.deleteTitle': 'Delete Team',
  'teams.confirm.deleteMessage':
    'Are you sure you want to delete "{{name}}"? All members will be removed from this team.',
  'teams.confirm.deleteConfirm': 'Delete Team',
  'teams.confirm.removeMemberTitle': 'Deactivate Member',
  'teams.confirm.removeMemberMessage':
    'Are you sure you want to deactivate {{name}}? They will lose access but their data will be preserved.',
  'teams.confirm.removeMemberConfirm': 'Deactivate',
  'teams.confirm.cancelInviteTitle': 'Cancel Invitation',
  'teams.confirm.cancelInviteMessage': 'Are you sure you want to cancel the invitation for {{name}}?',
  'teams.confirm.cancelInviteConfirm': 'Cancel Invite',
  'teams.createTeam': 'Create Team',
  'teams.empty': 'No teams yet',
  'teams.emptyDescription': 'Create your first team to organize members',

  // ============================================================================
  // ORG CHART (Issue #273 - Manager Assignment & Org Structure)
  // ============================================================================
  'orgChart.title': 'Organization Chart',
  'orgChart.subtitle': 'Showing {{count}} team members',
  'orgChart.empty': 'No organizational structure defined',
  'orgChart.emptyDescription': 'Assign managers to team members to build the org chart',
  'orgChart.goToTeam': 'Go to Team',
  'orgChart.directReports': 'Direct Reports',
  'orgChart.manager': 'Manager',
  'orgChart.noManager': 'No manager assigned',
  'orgChart.assignManager': 'Assign Manager',
  'orgChart.removeManager': 'Remove Manager',
  'orgChart.selectManager': 'Select Manager',
  'orgChart.managerAssigned': 'Manager assigned successfully',
  'orgChart.managerRemoved': 'Manager removed successfully',
  'orgChart.circularReference': 'Cannot create circular manager reference',
  'orgChart.viewFullChart': 'View Full Org Chart',
  'orgChart.myTeam': 'My Team',
  'orgChart.reportingTo': 'Reporting to',
  'orgChart.teamMembersCount_one': '{{count}} team member',
  'orgChart.teamMembersCount_other': '{{count}} team members',
  // Legacy alias retained for callers that haven't migrated to the
  // _one / _other split (no automatic plural selection — pick the suffix at
  // the call site).
  'orgChart.teamMembersCount': '{{count}} team members',
  'orgChart.panel.email': 'Email',

  // ============================================================================
  // CUSTOM ROLES (Issue #273 - User Management Phase 2)
  // ============================================================================
  'customRoles.title': 'Custom Roles',
  'customRoles.subtitle': 'Define custom roles with granular permissions',
  'customRoles.create': 'Create Role',
  'customRoles.edit': 'Edit Role',
  'customRoles.noRoles': 'No custom roles defined',
  'customRoles.noRolesDescription': 'Create custom roles to grant specific permissions to team members',
  'customRoles.createFirst': 'Create Your First Role',

  // Custom Roles Form
  'customRoles.form.name': 'Role Name',
  'customRoles.form.description': 'Description',
  'customRoles.form.baseRole': 'Base Role',
  'customRoles.form.permissions': 'Permissions',
  'customRoles.form.permissionsHelp': 'Select the permissions this role should have',
  'customRoles.form.active': 'Active',

  // Custom Roles Confirmation
  'customRoles.confirm.deleteTitle': 'Delete Custom Role',
  'customRoles.confirm.deleteMessage':
    'Are you sure you want to delete this role? Users with this role will lose these permissions.',
  'customRoles.confirm.deleteConfirm': 'Delete Role',

  // Custom Roles Messages
  'customRoles.messages.created': 'Custom role created successfully',
  'customRoles.messages.updated': 'Custom role updated successfully',
  'customRoles.messages.deleted': 'Custom role deleted successfully',
  'customRoles.messages.assigned': 'Role assigned successfully',
  'customRoles.messages.removed': 'Role removed successfully',

  // Custom Roles Errors
  'customRoles.errors.notFound': 'Role not found',
  'customRoles.errors.nameExists': 'A role with this name already exists',
  'customRoles.errors.systemRole': 'Cannot modify system roles',
  'customRoles.errors.createFailed': 'Failed to create role',
  'customRoles.errors.updateFailed': 'Failed to update role',
  'customRoles.errors.deleteFailed': 'Failed to delete role',

  // ============================================================================
  // ROLE ACCESS CONFIGURATION
  // ============================================================================
  'roleAccess.title': 'Role Access Configuration',
  'roleAccess.subtitle': 'Control what each role can do within your organization',
  'roleAccess.adminSubtitle': 'Platform default access levels for each role',
  'roleAccess.adminInfo': 'These are platform defaults. Individual businesses may override these.',
  'roleAccess.customOverridesActive':
    'Custom overrides are active for this business. Some roles have permissions different from platform defaults.',
  'roleAccess.search': 'Search permission groups...',
  'roleAccess.save': 'Save Changes',
  'roleAccess.saving': 'Saving...',
  'roleAccess.resetToDefaults': 'Reset to Defaults',
  'roleAccess.resetConfirmTitle': 'Reset Role Access?',
  'roleAccess.resetConfirmMessage':
    'This will remove all custom role access overrides and restore platform defaults. This action cannot be undone.',
  'roleAccess.resetConfirm': 'Reset',
  'roleAccess.saved': 'Role access configuration saved successfully',
  'roleAccess.resetSuccess': 'Role access configuration reset to defaults',
  'roleAccess.saveFailed': 'Failed to save role access configuration',
  'roleAccess.resetFailed': 'Failed to reset role access configuration',
  'roleAccess.modified': 'Modified',
  'roleAccess.default': 'Default',
  'roleAccess.locked': 'Locked',
  'roleAccess.lockedTooltip': 'Admin roles always have full access and cannot be modified',
  'roleAccess.permissionCount': '{{count}} permissions',
  'roleAccess.legend': 'Access Level Legend',
  'roleAccess.unsavedChanges': 'You have unsaved changes',
  'roleAccess.roles.admin': 'Admin',
  'roleAccess.roles.user': 'User',
  'roleAccess.roles.viewer': 'Viewer',

  // Access Level Labels
  'accessLevel.none': 'No Access',
  'accessLevel.none.description': 'Cannot access this area',
  'accessLevel.view': 'View',
  'accessLevel.view.description': 'Read-only access',
  'accessLevel.edit': 'Edit',
  'accessLevel.edit.description': 'Can create and modify',
  'accessLevel.admin': 'Admin',
  'accessLevel.admin.description': 'Full access including delete and manage',

  // Settings navigation
  'settings.roleAccess': 'Role Access',
  'settings.roleAccessDescription': 'Configure what each role can access',
  'settings.roleAccessDefaults': 'Role Access Defaults',
  'settings.roleAccessDefaultsDescription': 'Platform default access levels for each role',

  // ============================================================================
  // MY DEVICES (Issue #273 - Self-Service Session Management)
  // ============================================================================
  'devices.title': 'My Devices',
  'devices.subtitle': 'Manage devices that have access to your account',
  'devices.securityInfo':
    "Review your devices regularly. Remove any devices you don't recognize to keep your account secure.",
  'devices.noDevices': 'No devices found',
  'devices.noDevicesDescription': 'Devices will appear here after you sign in from different browsers or devices',
  'devices.trusted': 'Trusted',
  'devices.lastSeen': 'Last seen',
  'devices.ipAddress': 'IP',
  'devices.trust': 'Trust',
  'devices.rename': 'Rename Device',
  'devices.revoke': 'Revoke Access',
  'devices.deviceName': 'Device Name',

  // Device Confirmation
  'devices.confirm.revokeTitle': 'Revoke Device Access',
  'devices.confirm.revokeMessage':
    'This will sign out the device and remove it from your account. You can sign in again from that device.',
  'devices.confirm.revokeConfirm': 'Revoke Access',

  // Revoke All Devices
  'devices.revokeAll': 'Sign Out All Other Devices',
  'devices.revokeAllDescription':
    'This will sign you out from all other browsers and devices. Only this current session will remain active.',
  'devices.confirm.revokeAllTitle': 'Sign Out All Other Devices?',
  'devices.confirm.revokeAllMessage':
    'You will be signed out from all other devices. You will need to sign in again on those devices.',
  'devices.confirm.revokeAllConfirm': 'Sign Out All Others',
  'devices.revokeAllSuccess': 'Successfully signed out from {{count}} device(s)',

  // ============================================================================
  // TEAM ANALYTICS (Issue #273 - Team Analytics Dashboard)
  // ============================================================================
  'teamAnalytics.title': 'Team Analytics',
  'teamAnalytics.subtitle': 'Overview of your team metrics and performance',
  'teamAnalytics.totalMembers': 'Total Members',
  'teamAnalytics.activeMembers': 'Active Members',
  'teamAnalytics.last30Days': 'Active in last 30 days',
  'teamAnalytics.totalTeams': 'Total Teams',
  'teamAnalytics.customRoles': 'Custom Roles',
  'teamAnalytics.roleDistribution': 'Role Distribution',
  'teamAnalytics.roleDistributionDescription': 'Breakdown of team members by their assigned roles',
  'teamAnalytics.customRolesOverview': 'Custom Roles Overview',
  'teamAnalytics.customRolesDescription': 'Custom roles defined for your organization',
  'teamAnalytics.noCustomRoles': 'No custom roles defined yet',
  'teamAnalytics.permissions': 'permissions',
  'teamAnalytics.teamsOverview': 'Teams Overview',
  'teamAnalytics.teamsDescription': 'Organizational teams and their member counts',
  'teamAnalytics.noTeams': 'No teams created yet',
  'teamAnalytics.members': 'members',
  'teamAnalytics.roles.admin': 'Admin',
  'teamAnalytics.roles.business_admin': 'Business Admin',
  'teamAnalytics.roles.manager': 'Manager',
  'teamAnalytics.roles.business_user': 'Business User',
  'teamAnalytics.roles.member': 'Member',
  'teamAnalytics.roles.viewer': 'Viewer',

  // Performance Metrics (Issue #273 - Phase E)
  'teamAnalytics.overviewStats': 'Overview Statistics',
  'teamAnalytics.performanceMetrics': 'Performance Metrics',
  'teamAnalytics.casesHandled': 'Loans Handled',
  'teamAnalytics.casesResolved': 'Loans Resolved',
  'teamAnalytics.resolutionRate': 'Resolution Rate',
  'teamAnalytics.avgResponseTime': 'Avg Response Time',

  // Team Leaderboard (Issue #273 - Phase E)
  'teamAnalytics.teamLeaderboard': 'Team Leaderboard',
  'teamAnalytics.teamLeaderboardDescription': 'Performance comparison across all teams',
  'teamAnalytics.team': 'Team',
  'teamAnalytics.performance': 'Performance',
  'teamAnalytics.noTeamData': 'No team performance data available',

  // Activity Heatmap (Issue #273 - Phase E)
  'teamAnalytics.activityHeatmap': 'Activity Heatmap',
  'teamAnalytics.activityHeatmapDescription': 'Team activity patterns by day and hour',
  'teamAnalytics.filterByTeam': 'Filter by Team',
  'teamAnalytics.allTeams': 'All Teams',
  'teamAnalytics.lessActivity': 'Less',
  'teamAnalytics.moreActivity': 'More',

  // ============================================================================
  // ONBOARDING (Issue #273 - Onboarding Checklist)
  // ============================================================================
  'onboarding.title': 'Getting Started',
  'onboarding.subtitle': 'Complete your account setup to get the most out of the platform',
  'onboarding.progress': 'Setup Progress',
  'onboarding.completed': 'completed',
  'onboarding.allComplete': 'All setup steps completed!',
  'onboarding.allCompleteDescription':
    'You have completed all the setup steps. You are ready to start using the platform.',
  'onboarding.quickActions': 'Recommended Next Steps',

  // Onboarding Steps
  'onboarding.steps.profile.label': 'Complete Your Profile',
  'onboarding.steps.profile.description': 'Add your name and profile picture so your team can recognize you.',
  'onboarding.steps.profile.action': 'Update Profile',

  'onboarding.steps.security.label': 'Enable Two-Factor Authentication',
  'onboarding.steps.security.description': 'Add an extra layer of security to protect your account.',
  'onboarding.steps.security.action': 'Set Up 2FA',

  'onboarding.steps.devices.label': 'Review Your Devices',
  'onboarding.steps.devices.description': 'Trust the devices you use frequently for faster access.',
  'onboarding.steps.devices.action': 'Manage Devices',

  'onboarding.steps.preferences.label': 'Customize Your Experience',
  'onboarding.steps.preferences.description': 'Set your preferred theme and notification settings.',
  'onboarding.steps.preferences.action': 'Set Preferences',

  // Admin Onboarding View (Issue #273 - Phase F)
  'onboarding.admin.title': 'Team Onboarding Progress',
  'onboarding.admin.subtitle': 'Monitor and manage team member onboarding status',
  'onboarding.admin.totalUsers': 'Total Users',
  'onboarding.admin.started': 'Started',
  'onboarding.admin.completed': 'Completed',
  'onboarding.admin.completionRate': 'Completion Rate',
  'onboarding.admin.userProgress': 'User Progress',
  'onboarding.admin.sendAllReminders': 'Send All Reminders',
  'onboarding.admin.sendReminder': 'Send Reminder',
  'onboarding.admin.complete': 'Complete',
  'onboarding.admin.inProgress': 'In Progress',
  'onboarding.admin.notStarted': 'Not Started',
  'onboarding.admin.reminderSent': 'Reminder sent successfully',
  'onboarding.admin.remindersAllSent': 'Reminders sent to {{count}} users',
  'onboarding.admin.reminderFailed': 'Failed to send reminder',

  // Business Onboarding Wizard (Issue #435)
  'onboarding.welcome.title': 'Welcome to Fiops',
  'onboarding.welcome.subtitle':
    "Let's set up your business profile. This helps us customize the platform for your needs.",
  'onboarding.completeSetup': 'Complete Setup',
  'onboarding.skipMessage': 'Not ready to complete setup? You can skip and finish later.',
  'onboarding.skipAndContinue': 'Skip and Go to Portal',

  // Business Setup Wizard — Settings Page
  'businessOnboarding.settings.title': 'Business Setup',
  'businessOnboarding.settings.subtitle':
    'A short guided setup to tailor the platform to your business — what you do, who works with you, and how borrowers see you.',
  'businessOnboarding.settings.whatsCoveredTitle': 'What this covers',
  'businessOnboarding.settings.whatsCoveredItem.profile': 'Business profile — name, logo, and contact details',
  'businessOnboarding.settings.whatsCoveredItem.products': 'Loan products — the types of loans you offer',
  'businessOnboarding.settings.whatsCoveredItem.team': 'Team — invite teammates and set roles',
  'businessOnboarding.settings.whatsCoveredItem.branding': 'Borrower-facing branding — logo, colors, and domain',
  'businessOnboarding.settings.whatsCoveredItem.integrations': 'Integrations — connect the tools you already use',
  'businessOnboarding.settings.estimatedTime': 'Takes about 5–10 minutes. Your progress is saved automatically.',
  'businessOnboarding.settings.status.label': 'Setup status',
  'businessOnboarding.settings.status.notStarted': 'Not started yet',
  'businessOnboarding.settings.status.notStartedDescription':
    "You haven't started setup. You'll see the wizard on your next login, or start it now.",
  'businessOnboarding.settings.status.inProgress': 'In progress',
  'businessOnboarding.settings.status.inProgressDescription':
    "You're partway through setup. Pick up where you left off — nothing is lost.",
  'businessOnboarding.settings.status.completed': 'Setup complete',
  'businessOnboarding.settings.status.completedDescription':
    'Your business is configured and ready to go. You can revisit setup anytime to update your answers.',
  'businessOnboarding.settings.status.completedAt': 'Completed {{date}}',
  'businessOnboarding.settings.status.skipped': 'Setup is hidden',
  'businessOnboarding.settings.status.skippedDescription':
    "You've chosen to hide the setup wizard. You can bring it back anytime from here.",
  'businessOnboarding.settings.actions.start': 'Start setup',
  'businessOnboarding.settings.actions.continue': 'Continue setup',
  'businessOnboarding.settings.actions.revisit': 'Revisit setup',
  'businessOnboarding.settings.actions.reset': 'Reset setup',
  'businessOnboarding.settings.actions.resetHelp': 'Start over from the beginning. Your saved answers will be cleared.',
  'businessOnboarding.settings.actions.hide': 'Hide setup wizard',
  'businessOnboarding.settings.actions.hideHelp':
    'Stop showing the wizard on login. You can always restart it from this page.',
  'businessOnboarding.settings.actions.unhide': 'Show setup wizard again',
  'businessOnboarding.settings.advanced': 'Advanced',
  'businessOnboarding.settings.resetDialog.title': 'Reset Business Setup?',
  'businessOnboarding.settings.resetDialog.body':
    'This clears all saved answers. Your business profile stays intact — only the setup flow resets. This action cannot be undone.',
  'businessOnboarding.settings.resetDialog.confirm': 'Reset setup',
  'businessOnboarding.settings.resetDialog.resetting': 'Resetting…',
  'businessOnboarding.settings.hideDialog.title': 'Hide the setup wizard?',
  'businessOnboarding.settings.hideDialog.body':
    "The wizard won't appear on login anymore. You can unhide it from this page anytime.",
  'businessOnboarding.settings.hideDialog.confirm': 'Hide wizard',
  'businessOnboarding.settings.hideDialog.hiding': 'Hiding…',

  // Common skip translations
  'common.skipForNow': 'Skip for now',
  'common.continue': 'Continue',

  // ============================================================================
  // AUDIT (Issue #234 - Entity Audit Activity Logs)
  // ============================================================================
  // Panel & Navigation
  'audit.title': 'Audit Log',
  'audit.description': 'View activity history and changes',
  'audit.tabs.timeline': 'Timeline',
  'audit.tabs.table': 'Table View',
  'audit.tabs.versions': 'Version History',

  // Filters (Issue #400 - i18n audit)
  'audit.action': 'Action',
  'audit.endDate': 'End Date',
  'audit.entity': 'Entity',
  'audit.entityType': 'Entity Type',
  'audit.exportCsv': 'Export CSV',
  'audit.ipAddress': 'IP Address',
  'audit.noLogsFound': 'No audit logs found',
  'audit.searchPlaceholder': 'Search audit logs...',
  'audit.startDate': 'Start Date',
  'audit.status': 'Status',
  'audit.timestamp': 'Timestamp',
  'audit.user': 'User',
  'audit.totalLogs30d': 'Total Logs (30d)',
  'audit.successRate': 'Success Rate',
  'audit.topAction': 'Top Action',
  'audit.topUser': 'Top User',

  // Timeline
  'audit.timeline.title': 'Activity Timeline',
  'audit.timeline.empty': 'No activity recorded',
  'audit.timeline.emptyDescription': 'Activity will appear here as changes are made',
  'audit.timeline.loadMore': 'Load more activity',
  'audit.timeline.loading': 'Loading activity...',
  'audit.timeline.error': 'Failed to load activity timeline',
  'audit.timeline.showFieldChanges': 'Show field changes',
  'audit.timeline.hideFieldChanges': 'Hide field changes',
  'audit.timeline.viewDiff': 'View changes',
  'audit.timeline.today': 'Today',
  'audit.timeline.yesterday': 'Yesterday',
  'audit.timeline.earlier': 'Earlier',

  // Entity Activity Timeline (time-of-day grouping)
  'timeline.activity': 'activity',
  'timeline.activities': 'activities',
  'timeline.noActivity': 'No activity recorded yet',
  'timeline.loadMore': 'Load more',
  'timeline.showingCount': 'Showing {{shown}} of {{total}} activities',
  'timeline.period.morning': 'Morning',
  'timeline.period.noon': 'Noon',
  'timeline.period.afternoon': 'Afternoon',
  'timeline.period.evening': 'Evening',
  'timeline.period.morningRange': '6:00 AM - 12:00 PM',
  'timeline.period.noonRange': '12:00 PM - 2:00 PM',
  'timeline.period.afternoonRange': '2:00 PM - 6:00 PM',
  'timeline.period.eveningRange': '6:00 PM - 6:00 AM',

  // Entity Activity Timeline — detail panel labels & expand/collapse a11y
  'entityActivityTimeline.action': 'Action',
  'entityActivityTimeline.resource': 'Resource',
  'entityActivityTimeline.when': 'When',
  'entityActivityTimeline.actor': 'Actor',
  'entityActivityTimeline.ipAddress': 'IP address',
  'entityActivityTimeline.device': 'Device',
  'entityActivityTimeline.request': 'Request',
  'entityActivityTimeline.status': 'Status',
  'entityActivityTimeline.severity': 'Severity',
  'entityActivityTimeline.duration': 'Duration',
  'entityActivityTimeline.tags': 'Tags',
  'entityActivityTimeline.error': 'Error',
  'entityActivityTimeline.location': 'Location',
  'entityActivityTimeline.expandDetails': 'Expand details',
  'entityActivityTimeline.collapseDetails': 'Collapse details',

  // Table
  'audit.table.title': 'Audit Logs',
  'audit.table.empty': 'No audit logs found',
  'audit.table.emptyDescription': 'Adjust your filters or date range',
  'audit.table.export': 'Export',
  'audit.table.exportCsv': 'Export as CSV',
  'audit.table.exportJson': 'Export as JSON',
  'audit.table.exportPdf': 'Export as PDF',
  'audit.table.exportSuccess': 'Activity log exported successfully',
  'audit.table.exportError': 'Failed to export activity log',
  'audit.table.columns.timestamp': 'Timestamp',
  'audit.table.columns.user': 'User',
  'audit.table.columns.action': 'Action',
  'audit.table.columns.resource': 'Resource',
  'audit.table.columns.status': 'Status',
  'audit.table.columns.severity': 'Severity',
  'audit.table.columns.ipAddress': 'IP Address',
  'audit.table.columns.duration': 'Duration',

  // Diff Viewer
  'audit.diff.title': 'Version Comparison',
  'audit.diff.from': 'From',
  'audit.diff.to': 'To',
  'audit.diff.version': 'Version {{number}}',
  'audit.diff.modifiedBy': 'Modified by {{name}}',
  'audit.diff.noChanges': 'No changes detected',
  'audit.diff.summary': '{{added}} added, {{removed}} removed, {{modified}} modified',
  'audit.diff.fieldAdded': 'Added',
  'audit.diff.fieldRemoved': 'Removed',
  'audit.diff.fieldModified': 'Modified',
  'audit.diff.fieldUnchanged': 'Unchanged',
  'audit.diff.showUnchanged': 'Show unchanged fields',
  'audit.diff.hideUnchanged': 'Hide unchanged fields',
  'audit.diff.sideBySide': 'Side by side',
  'audit.diff.inline': 'Inline',
  'audit.diff.selectVersions': 'Select versions to compare',
  'audit.diff.added': 'Added',
  'audit.diff.modified': 'Modified',
  'audit.diff.removed': 'Removed',

  // Panel
  'audit.panel.title': 'Audit Trail',

  // Stats
  'audit.stats.lastEditor': 'Last Editor',
  'audit.stats.totalChanges': 'Total Changes',
  'audit.stats.uniqueEditors': 'Unique Editors',

  // Compliance
  'audit.compliance.businessFilter': 'Filter by Business',
  'audit.compliance.dateRange': 'Date Range',
  'audit.compliance.exportReport': 'Export Report',
  'audit.compliance.summary.complianceScore': 'Compliance Score',
  'audit.compliance.summary.securityEvents': 'Security Events',
  'audit.compliance.summary.totalEvents': 'Total Events',
  'audit.compliance.summary.uniqueUsers': 'Unique Users',

  // Filters
  'audit.filter.dateRange': 'Date range',
  'audit.filter.user': 'User',
  'audit.filter.action': 'Action',
  'audit.filter.resource': 'Resource',
  'audit.filter.severity': 'Severity',
  'audit.filter.status': 'Status',
  'audit.filter.search': 'Search audit logs...',
  'audit.filter.clear': 'Clear filters',
  'audit.filter.apply': 'Apply filters',
  'audit.filter.entityType': 'Entity Type',

  // Actions
  'audit.action.create': 'Created',
  'audit.action.read': 'Viewed',
  'audit.action.update': 'Updated',
  'audit.action.delete': 'Deleted',
  'audit.action.login': 'Logged in',
  'audit.action.logout': 'Logged out',
  'audit.action.access': 'Accessed',
  'audit.action.export': 'Exported',
  'audit.action.import': 'Imported',
  'audit.action.config': 'Configured',
  'audit.action.permission': 'Permission changed',
  'audit.action.assign': 'Assigned',
  'audit.action.unassign': 'Unassigned',
  'audit.action.approve': 'Approved',
  'audit.action.reject': 'Rejected',
  'audit.action.suspend': 'Suspended',
  'audit.action.activate': 'Activated',
  'audit.action.upload': 'Uploaded',
  'audit.action.download': 'Downloaded',
  'audit.action.comment': 'Commented',
  'audit.action.send': 'Sent',

  // Severity
  'audit.severity.info': 'Info',
  'audit.severity.warning': 'Warning',
  'audit.severity.error': 'Error',
  'audit.severity.critical': 'Critical',

  // Status
  'audit.status.success': 'Success',
  'audit.status.failure': 'Failed',
  'audit.status.pending': 'Pending',

  // Detail Panel
  'audit.detail.title': 'Audit Log Details',
  'audit.detail.userAgent': 'User Agent',
  'audit.detail.requestId': 'Request ID',
  'audit.detail.errorMessage': 'Error Message',
  'audit.detail.changes': 'Changes',
  'audit.detail.before': 'Before',
  'audit.detail.after': 'After',
  // AuditActivityTab click-to-expand panel labels (Issue #1414)
  'audit.detail.action': 'Action',
  'audit.detail.resource': 'Resource',
  'audit.detail.when': 'When',
  'audit.detail.location': 'Location',
  'audit.detail.ipAddress': 'IP address',
  'audit.detail.device': 'Device',
  'audit.detail.request': 'Request',
  'audit.detail.status': 'Status',
  'audit.detail.severity': 'Severity',
  'audit.detail.duration': 'Duration',
  'audit.detail.tags': 'Tags',
  'audit.detail.error': 'Error',

  // Resource labels for AuditActivityTab terminology rewrite (Issue #1414)
  'audit.resourceLabel.loan': 'loan',
  'audit.resourceLabel.lead': 'lead',
  'audit.resourceLabel.partner': 'partner',
  'audit.resourceLabel.borrower': 'borrower',
  'audit.resourceLabel.entity': 'entity',
  'audit.resourceLabel.teamMember': 'team member',

  // Field Changes
  'audit.changes.from': 'From',
  'audit.changes.to': 'To',
  'audit.changes.empty': '(empty)',
  'audit.changes.fieldCount': '{{count}} field changed',
  'audit.changes.fieldCountPlural': '{{count}} fields changed',

  // Consumer Activity (Privacy-focused)
  'audit.consumer.title': 'Account Activity',
  'audit.consumer.description': 'See what happened on your account',
  'audit.consumer.caseActivity': 'Loan Activity',
  'audit.consumer.viewedBy': 'Viewed by {{name}}',
  'audit.consumer.modifiedBy': 'Modified by {{name}}',
  'audit.consumer.noActivity': 'No recent activity',
  'audit.consumer.empty': 'No activity to display',
  'audit.consumer.error': 'Failed to load account activity',
  'audit.consumer.privacy': 'Your activity is private and only visible to you',

  // Compliance
  'audit.compliance.title': 'Compliance Dashboard',
  'audit.compliance.description': 'Monitor compliance and security events',
  'audit.compliance.gdpr': 'GDPR',
  'audit.compliance.hipaa': 'HIPAA',
  'audit.compliance.pii': 'PII Access',
  'audit.compliance.highRisk': 'High Risk Actions',

  // ============================================================================
  // PARTNER / B2B (Issue #238 - Partner Companies B2B Model)
  // ============================================================================
  // Navigation
  'partner.title': 'Partners',
  'partner.portal': 'Partner Portal',
  'partner.dashboard': 'Dashboard',
  'partner.description': 'Manage partner relationships and agreements',
  'partner.createSubtitle': 'Add a new referral or white-label partner',
  'partner.type': 'Partner Type',
  'partner.type.referral': 'Referral Partner',
  'partner.type.white_label': 'White-Label Partner',
  'partner.type.affiliate': 'Affiliate Partner',
  'partner.type.reseller': 'Reseller Partner',
  'partner.typeDescription.referral': 'Partner refers clients to you; the borrower sees your brand.',
  'partner.typeDescription.white_label':
    'White-labeled: partner keeps borrowers on their own account and branding. You process behind the scenes.',
  'partner.commissionRate': 'Commission Rate',
  'partner.commissionRateHelp': 'Percentage of loan amount',
  'partner.autoVerify': 'Auto-verify partner',
  'partner.autoVerifyHelp': 'Partner will be active immediately without manual verification',
  'partner.copyInviteLink': 'Copy Invite Link',
  'partner.details': 'Partner Details',

  // Partner Invitations
  'partner.invitation.details': 'Invitation',
  'partner.invitation.sent': 'Sent',
  'partner.invitation.expires': 'Expires',
  'partner.invitation.defaultName': 'Invited partner',
  'partner.invitation.resent': 'Invitation resent to {{name}}',
  'partner.invitation.updated': 'Invitation updated',
  'partner.invitation.updatedDescription':
    'The partner will see the revised details the next time they open the invite link.',
  'partner.invitation.resendFailed': 'Failed to resend invitation',
  'partner.invitation.cancelFailed': 'Failed to cancel invitation',
  'partner.invitation.pendingStatus': 'Pending Invite',
  'partner.invitation.contactName': 'Contact Name',
  'partner.invitation.invitedAt': 'Invited',
  'partner.invitation.expiresAt': 'Expires',
  'partner.invitation.cancelConfirmTitle': 'Cancel Invitation',
  'partner.invitation.cancelConfirmBody':
    'The invite link for {{name}} will stop working. You can send a new invite later.',
  'partner.invitation.cancelConfirmAction': 'Cancel Invite',
  'partner.invitation.cancelConfirmKeep': 'Keep Invite',
  'partner.invitation.cancelled': 'Invitation cancelled',
  'partner.invitation.linkCopied': 'Invite link copied — valid until {{expiresAt}}',
  'partner.invitation.linkCopiedNoExpiry': 'Invite link copied',
  'partner.invitation.linkRefreshedAndCopied': 'Invitation refreshed (valid until {{expiresAt}}) and link copied',
  'partner.actions.editInvite': 'Edit Invite',
  'partner.actions.resendInvite': 'Resend Invite',
  'partner.actions.copyInviteLink': 'Copy Invite Link',
  'partner.actions.cancelInvite': 'Cancel Invite',
  // Partner Contract Terms
  'partner.contractTerms': 'Contract Terms',
  'partner.contractTerms.rate': 'Rate',
  'partner.contractTerms.flatFee': 'Flat fee',
  'partner.contractTerms.trigger': 'Trigger',

  // Business Capabilities
  'partner.capabilities.title': 'Business Capabilities',
  'partner.capabilities.canLend': 'Can Lend',
  'partner.capabilities.canBroker': 'Can Broker',
  'partner.capabilities.canReceiveReferrals': 'Can Receive Referrals',
  'partner.capabilities.lendingProducts': 'Lending Products',
  'partner.capabilities.lendingStates': 'Lending States',

  // Referral Agreements
  'partner.agreement.title': 'Referral Agreement',
  'partner.agreement.titlePlural': 'Referral Agreements',
  'partner.agreement.notFoundTitle': 'Agreement not found',
  'partner.agreement.notFoundDescription': 'This agreement may have been deleted or is no longer accessible to you.',
  'partner.agreement.create': 'Create Agreement',
  'partner.agreement.edit': 'Edit Agreement',
  'partner.agreement.view': 'View Agreement',
  'partner.agreement.sign': 'Sign Agreement',
  'partner.agreement.suspend': 'Suspend Agreement',
  'partner.agreement.terminate': 'Terminate Agreement',
  'partner.agreement.renew': 'Renew Agreement',
  'partner.agreement.referrer': 'Referrer',
  'partner.agreement.receiver': 'Receiver',
  'partner.agreement.effectiveDate': 'Effective Date',
  'partner.agreement.expiresAt': 'Expires At',
  'partner.agreement.autoRenew': 'Auto Renew',
  'partner.exclusivity.none': 'None',
  'partner.exclusivity.geographic': 'Geographic',
  'partner.exclusivity.product_type': 'Product Type',
  'partner.exclusivity.full': 'Full',
  'partner.visibility.none': 'None',
  'partner.visibility.stats_only': 'Stats Only',
  'partner.visibility.case_summary': 'Case Summary',
  'partner.visibility.full_details': 'Full Details',
  'partner.renewal.manual': 'Manual',
  'partner.agreement.exclusivity': 'Exclusivity',
  'partner.agreement.territories': 'Territories',
  'partner.agreement.productTypes': 'Product Types',
  'partner.agreement.createSubtitle': 'Create a new referral agreement with a partner',
  'partner.agreement.exclusivityHelp': 'Exclusive agreements prevent the partner from working with competitors',
  'partner.agreement.expiresAtHelp': 'Agreement will automatically terminate on this date',
  'partner.agreement.receiverHelp': 'The business that will receive referrals',
  'partner.agreement.type': 'Agreement Type',
  'partner.agreement.type.oneWay': 'One-Way',
  'partner.agreement.type.twoWay': 'Two-Way',

  // Agreement Lifecycle Settings (Issue #1223 PR4)
  'partner.agreement.lifecycle': 'Lifecycle Settings',
  'partner.agreement.lifecycle.honorInflightReferrals': 'Honor In-Flight Referrals',
  'partner.agreement.lifecycle.honorInflightReferralsHelp':
    'If enabled, referrals already in progress will continue to be honored after the agreement expires or is terminated.',
  'partner.agreement.lifecycle.clawbackEnabled': 'Clawback Enabled',
  'partner.agreement.lifecycle.clawbackEnabledHelp':
    'If enabled, commissions can be reclaimed if the referred loan defaults within the clawback period.',
  'partner.agreement.lifecycle.clawbackDays': 'Clawback Period (Days)',
  'partner.agreement.lifecycle.clawbackDaysHelp':
    'Number of days after funding during which commissions can be clawed back if the loan defaults.',

  // Agreement Status (enum: draft | pending_approval | active | suspended | terminated | expired)
  'partner.agreement.status.draft': 'Draft',
  'partner.agreement.status.pending_approval': 'Pending Approval',
  'partner.agreement.status.active': 'Active',
  'partner.agreement.status.suspended': 'Suspended',
  'partner.agreement.status.terminated': 'Terminated',
  'partner.agreement.status.expired': 'Expired',

  // Partner Onboarding
  'partner.onboarding.validating': 'Validating invitation...',
  'partner.onboarding.invalidInvitation': 'Invalid Invitation',
  'partner.onboarding.goToLogin': 'Go to Login',
  'partner.onboarding.completeFailed': 'Failed to complete onboarding',
  'partner.onboarding.complete': 'Onboarding Complete!',
  'partner.onboarding.activatedWithContracts':
    'Your partnership has been activated with {{count}} contract(s). Redirecting to login...',
  'partner.onboarding.activated': 'Your partnership has been activated. Redirecting to login...',
  'partner.onboarding.creatingAccount': 'Creating your account...',
  'partner.onboarding.invitationExpired':
    'This invitation has expired. Please contact the inviting partner to request a new invitation.',
  'partner.onboarding.invitationCancelled':
    'This invitation has been cancelled. Please contact the inviting partner for more information.',
  'partner.onboarding.yourPartner': 'Your Partner',

  // Agreement Types
  'partner.agreement.type.referral': 'Referral',
  'partner.agreement.type.correspondent': 'Correspondent',
  'partner.agreement.type.wholesale': 'Wholesale',
  'partner.agreement.type.white_label': 'White Label',

  // Commissions
  'partner.commission.title': 'Commission',
  'partner.commission.titlePlural': 'Commissions',
  'partner.commission.track': 'Track B2B commissions from referral agreements',
  'partner.commission.export': 'Export Report',
  'partner.commission.earned': 'Earned',
  'partner.commission.owed': 'Owed',
  'partner.commission.totalEarned': 'Total Earned',
  'partner.commission.pendingEarned': 'Pending',
  'partner.commission.totalOwed': 'Total Owed',
  'partner.commission.noCapabilities': 'No Commission Capabilities',
  'partner.commission.noCapabilitiesDescription':
    'Your business does not have commission capabilities enabled yet. Enable broker or referral receiving permissions in your business profile to start tracking commissions.',
  'partner.commission.loanAmount': 'Loan Amount',
  'partner.commission.paidDate': 'Paid',
  'partner.commission.dueDate': 'Due',
  'partner.commission.internal': 'Internal Commissions',
  'partner.commission.receipts': 'Commission Receipts',
  'partner.commission.splits': 'Commission Splits',
  'partner.commission.splitConfig': 'Split Configuration',
  'partner.commission.rate': 'Commission Rate',
  'partner.commission.amount': 'Amount',
  'partner.commission.grossAmount': 'Gross Amount',
  'partner.commission.calculate': 'Calculate Commission',
  'partner.commission.approve': 'Approve Commission',
  'partner.commission.markPaid': 'Mark as Paid',
  'partner.commission.dispute': 'Dispute',
  'partner.commission.trigger': 'Commission Trigger',
  'partner.payment.terms': 'Payment Terms',
  'partner.branch.createSubtitle': 'Add a new branch location',

  // Commission Status
  'partner.commission.status.pending': 'Pending',
  'partner.commission.status.approved': 'Approved',
  'partner.commission.status.processing': 'Processing',
  'partner.commission.status.paid': 'Paid',
  'partner.commission.status.cancelled': 'Cancelled',
  'partner.commission.status.disputed': 'Disputed',
  'partner.commission.status.calculated': 'Calculated',
  'partner.commission.status.voided': 'Voided',
  'partner.commission.status.expected': 'Expected',
  'partner.commission.status.received': 'Received',
  'partner.commission.status.adjusted': 'Adjusted',
  'partner.commission.status.written_off': 'Written Off',
  'partner.commission.status.held': 'On Hold',

  // Commission Payout Status
  'partner.commission.payoutStatus': 'Payout Status',
  'partner.commission.payoutStatus.pending': 'Pending',
  'partner.commission.payoutStatus.scheduled': 'Scheduled',
  'partner.commission.payoutStatus.processing': 'Processing',
  'partner.commission.payoutStatus.paid': 'Paid',
  'partner.commission.payoutStatus.failed': 'Failed',

  // Commission Calculation
  'partner.commission.calculation.percentage': 'Percentage',
  'partner.commission.calculation.flat_fee': 'Flat Fee',
  'partner.commission.calculation.points': 'Points',
  'partner.commission.calculation.basis_points': 'Basis Points',
  'partner.commission.calculation.tiered': 'Tiered',

  // Commission Triggers
  'partner.commission.trigger.funding': 'On Funding',
  'partner.commission.trigger.closing': 'On Closing',
  'partner.commission.trigger.first_payment': 'After First Payment',
  'partner.commission.trigger.application': 'On Application',

  // Payment Terms
  'partner.payment.terms.net_15': 'Net 15',
  'partner.payment.terms.net_30': 'Net 30',
  'partner.payment.terms.net_45': 'Net 45',
  'partner.payment.terms.net_60': 'Net 60',
  'partner.payment.terms.immediate': 'Immediate',

  // Branches
  'partner.branch.title': 'Branch',
  'partner.branch.titlePlural': 'Branches',
  'partner.branch.create': 'Add Branch',
  'partner.branch.edit': 'Edit Branch',
  'partner.branch.name': 'Branch Name',
  'partner.branch.code': 'Branch Code',
  'partner.branch.address': 'Address',
  'partner.branch.phone': 'Phone',
  'partner.branch.manager': 'Branch Manager',
  'partner.branch.nmlsBranchId': 'NMLS Branch ID',
  'partner.branch.licenseStates': 'Licensed States',

  // Address fields
  'partner.address.street': 'Street',
  'partner.address.city': 'City',
  'partner.address.state': 'State',
  'partner.address.zipCode': 'ZIP Code',
  'partner.address.country': 'Country',

  // Branch Status
  'partner.branch.status.active': 'Active',
  'partner.branch.status.inactive': 'Inactive',
  'partner.branch.status.pending': 'Pending',
  'partner.branch.status.suspended': 'Suspended',

  // Licenses
  'partner.license.title': 'License',
  'partner.license.titlePlural': 'Licenses',
  'partner.license.business': 'Business Licenses',
  'partner.license.individual': 'Individual Licenses',
  'partner.license.create': 'Add License',
  'partner.license.edit': 'Edit License',
  'partner.license.verify': 'Verify License',
  'partner.license.type': 'License Type',
  'partner.license.number': 'License Number',
  'partner.license.issuingAuthority': 'Issuing Authority',
  'partner.license.issuedAt': 'Issue Date',
  'partner.license.expiresAt': 'Expiration Date',
  'partner.license.nmlsId': 'NMLS ID',
  'partner.license.sponsoredBy': 'Sponsored By',

  // License Types
  'partner.license.type.nmls_company': 'NMLS Company License',
  'partner.license.type.state_lender': 'State Lender License',
  'partner.license.type.state_broker': 'State Broker License',
  'partner.license.type.state_servicer': 'State Servicer License',
  'partner.license.type.nmls_individual': 'NMLS Individual License',
  'partner.license.type.state_loan_officer': 'State Loan Officer License',

  // License Status
  'partner.license.status.active': 'Active',
  'partner.license.status.expired': 'Expired',
  'partner.license.status.pending_renewal': 'Pending Renewal',
  'partner.license.status.suspended': 'Suspended',
  'partner.license.status.revoked': 'Revoked',
  'partner.license.status.pending_verification': 'Pending Verification',

  // Partner Roles
  'partner.roles.owner': 'Owner',
  'partner.roles.branch_manager': 'Branch Manager',
  'partner.roles.loan_officer': 'Loan Officer',
  'partner.roles.processor': 'Processor',
  'partner.roles.admin': 'Admin',
  'partner.roles.viewer': 'Viewer',

  // Partner Role Descriptions
  'partner.roleDescriptions.owner': 'Full access to all features and company settings',
  'partner.roleDescriptions.branch_manager': 'Manage branch team and view branch performance',
  'partner.roleDescriptions.loan_officer': 'Submit referrals and view own commissions',
  'partner.roleDescriptions.processor': 'View deals and provide support',
  'partner.roleDescriptions.admin': 'Manage team members, no financial access',
  'partner.roleDescriptions.viewer': 'Read-only access to dashboard',

  // Referrals
  'partner.referral.title': 'Referral',
  'partner.referral.titlePlural': 'Referrals',
  'partner.referral.submit': 'Submit Referral',
  'partner.referral.track': 'Track Referrals',
  'partner.referral.outgoing': 'Outgoing Referrals',
  'partner.referral.incoming': 'Incoming Referrals',
  'partner.referral.pendingAction': 'Pending Action',
  'partner.referral.noCapabilities': 'No Referral Capabilities',
  'partner.referral.noCapabilitiesDescription':
    'Your business does not have referral capabilities enabled yet. Contact your administrator to enable broker or referral receiving permissions in your business profile.',
  'partner.referral.clientName': 'Client Name',
  'partner.referral.clientEmail': 'Client Email',
  'partner.referral.type': 'Referral Type',
  'partner.referral.notes': 'Notes',

  // Referral Types
  'partner.referral.type.buyer': 'Buyer',
  'partner.referral.type.seller': 'Seller',
  'partner.referral.type.investor': 'Investor',

  // Referral Status
  'partner.referral.status.pending': 'Pending',
  'partner.referral.status.contacted': 'Contacted',
  'partner.referral.status.qualified': 'Qualified',
  'partner.referral.status.converted': 'Converted',
  'partner.referral.status.lost': 'Lost',

  // Case-level referral view (Issue #1369 — replaces the legacy Referral model view)
  'partner.referral.cases.title': 'Cases You Referred',
  'partner.referral.cases.description': 'Loan cases that lenders received from your referral links.',
  'partner.referral.cases.empty': 'No referred cases yet. Share your referral link to get started.',
  'partner.referral.cases.statsOnlyNotice':
    'Your agreement restricts visibility to aggregate stats only. Contact the lender to request broader access.',
  'partner.referral.cases.caseNumber': 'Case',
  'partner.referral.cases.lender': 'Lender',
  'partner.referral.cases.status': 'Status',
  'partner.referral.cases.stage': 'Stage',
  'partner.referral.cases.referralCode': 'Referral Code',
  'partner.referral.cases.createdAt': 'Referred',
  'partner.referral.cases.commission': 'Commission',
  'partner.referral.cases.total': 'Total Referrals',
  'partner.referral.cases.funded': 'Funded',
  'partner.referral.cases.inProgress': 'In Progress',
  'partner.referral.cases.declined': 'Declined',
  'partner.referral.cases.conversionRate': 'Conversion Rate',
  'partner.referral.cases.totalCommission': 'Total Commission',
  'partner.referral.cases.viewDetails': 'View Details',
  'partner.referral.cases.maskedTitle': 'Case details restricted',
  'partner.referral.cases.type': 'Type',
  // Case status filter labels — partner-view-specific wording
  'partner.referral.cases.filter.inProgress': 'In Progress',
  'partner.referral.cases.filter.termsheetSigned': 'Term Sheet Signed',
  'partner.referral.cases.filter.funded': 'Funded',
  'partner.referral.cases.filter.declined': 'Declined',

  // Deals

  // Deal Status

  // Loan Product Types
  'partner.loanProduct.fix_flip': 'Fix & Flip',
  'partner.loanProduct.bridge': 'Bridge',
  'partner.loanProduct.construction': 'Construction',
  'partner.loanProduct.dscr': 'DSCR',
  'partner.loanProduct.rental': 'Rental',
  'partner.loanProduct.ground_up': 'Ground Up',
  'partner.loanProduct.commercial': 'Commercial',
  'partner.loanProduct.mixed_use': 'Mixed Use',

  // Bonuses
  'partner.bonus.title': 'Bonus',
  'partner.bonus.titlePlural': 'Bonuses',
  'partner.bonus.type.signing': 'Signing Bonus',
  'partner.bonus.type.milestone': 'Milestone Bonus',
  'partner.bonus.type.retention': 'Retention Bonus',
  'partner.bonus.type.exclusivity': 'Exclusivity Bonus',
  'partner.bonus.type.performance': 'Performance Bonus',
  'partner.bonus.status.pending': 'Pending',
  'partner.bonus.status.earned': 'Earned',
  'partner.bonus.status.paid': 'Paid',
  'partner.bonus.status.forfeited': 'Forfeited',

  // Reports
  'partner.reports.title': 'Reports',
  'partner.reports.commissionStatement': 'Commission Statement',
  'partner.reports.performance': 'Performance Report',
  'partner.reports.loPerformance': 'LO Performance',
  'partner.reports.branchPerformance': 'Branch Performance',
  'partner.reports.pipeline': 'Pipeline Report',
  'partner.reports.taxDocuments': '1099 Documents',

  // Compliance
  'partner.compliance.title': 'Compliance',
  'partner.compliance.description': 'Manage licenses and compliance documents',
  'partner.compliance.score': 'Compliance Score',
  'partner.compliance.businessLicenses': 'Business Licenses',
  'partner.compliance.teamLicenses': 'Team Licenses',
  'partner.compliance.validDocuments': 'Valid Documents',
  'partner.compliance.documents': 'Documents',
  'partner.compliance.expires': 'Expires',
  'partner.compliance.daysLeft': 'days left',
  'partner.compliance.uploaded': 'Uploaded',
  'partner.compliance.noExpiration': 'No expiration',
  'partner.compliance.licenseExpiring': 'License Expiring',
  'partner.compliance.licenseExpired': 'License Expired',
  'partner.compliance.uploadDocument': 'Upload Document',
  'partner.compliance.expirationAlert': '{{count}} license(s) expiring within {{days}} days',
  'partner.compliance.noBusinessLicenses': 'No business licenses configured',
  'partner.compliance.noTeamLicenses': 'No team licenses found',

  // Compliance Status
  'partner.compliance.status.valid': 'Valid',
  'partner.compliance.status.expiringSoon': 'Expiring Soon',
  'partner.compliance.status.expired': 'Expired',
  'partner.compliance.status.pendingVerification': 'Pending Verification',

  // License Warnings (Issue #1223 AC8.5)
  'partner.compliance.licenseWarnings': 'License Warnings',
  'partner.compliance.licenseWarningsDescription': 'Referrals with state license mismatch warnings',
  'partner.compliance.noLicenseWarnings': 'No license warnings found',

  // Partner Portal Pages
  'partner.pages.dashboard': 'Dashboard',
  'partner.pages.referrals': 'Referrals',
  'partner.pages.commissions': 'Commissions',
  'partner.pages.team': 'Team',
  'partner.pages.branches': 'Branches',
  'partner.pages.reports': 'Reports',
  'partner.pages.compliance': 'Compliance',
  'partner.pages.settings': 'Settings',
  'partner.pages.agreements': 'Agreements',

  // Partnership Actions (suspend/reactivate/terminate)
  'partner.actions.suspend': 'Suspend Partnership',
  'partner.actions.reactivate': 'Reactivate Partnership',
  'partner.actions.terminate': 'Terminate Partnership',
  'partner.actions.suspend.confirm': 'Are you sure you want to suspend this partnership? New referrals will be paused.',
  'partner.actions.reactivate.confirm': 'Reactivate this partnership? Referral tracking will resume.',
  'partner.actions.terminate.confirm':
    'Are you sure you want to terminate this partnership? This action cannot be undone.',
  'partner.actions.reason': 'Reason (optional)',
  'partner.actions.reason.placeholder': 'Enter reason for this action...',
  'partner.contracts': 'Contracts',
  'partner.signContract': 'Sign',
  'partner.awaitingOtherParty': 'Awaiting other party',
  'partner.status.terminated': 'Terminated',
  'partner.status.expired': 'Expired',
  'common.signed': 'Signed',
  'intake.validation.fieldsMustMatch': 'Fields must match',
  'intake.validation.minLength': 'Minimum {{min}} characters',

  // Partner Errors
  'partner.errors.agreementNotFound': 'Agreement not found',
  'partner.errors.cannotSignOwnAgreement': 'Cannot sign your own agreement',
  'partner.errors.invalidCommissionRate': 'Invalid commission rate',
  'partner.errors.licenseExpired': 'License has expired',
  'partner.errors.branchNotFound': 'Branch not found',
  'partner.errors.branchCodeExists': 'A branch with this code already exists',
  'partner.errors.cannotDeleteActiveBranch': 'Cannot delete active branch with members',
  'partner.errors.invalidTerritory': 'Invalid territory selection',
  'partner.errors.commissionNotFound': 'Commission record not found',
  'partner.errors.splitsMustEqual100': 'Commission splits must equal 100%',

  // Partner Messages
  'partner.messages.agreementCreated': 'Agreement created successfully',
  'partner.messages.agreementSigned': 'Agreement signed successfully',
  'partner.messages.agreementSuspended': 'Agreement suspended',
  'partner.messages.agreementTerminated': 'Agreement terminated',
  'partner.messages.commissionApproved': 'Commission approved',
  'partner.messages.commissionPaid': 'Commission marked as paid',
  'partner.messages.branchCreated': 'Branch created successfully',
  'partner.messages.branchUpdated': 'Branch updated successfully',
  'partner.messages.licenseVerified': 'License verified successfully',
  'partner.messages.referralSubmitted': 'Referral submitted successfully',

  // Partner Empty States
  'partner.empty.agreements': 'No agreements yet',
  'partner.empty.agreementsDescription': 'Create your first referral agreement to start tracking partner relationships',
  'partner.empty.commissions': 'No commissions yet',
  'partner.empty.commissionsDescription': 'Commissions will appear here as deals are funded',
  'partner.empty.branches': 'No branches yet',
  'partner.empty.branchesDescription': 'Add your first branch to organize your team',
  'partner.empty.branchesHint': 'Create a branch to get started',
  'partner.empty.referrals': 'No referrals yet',
  'partner.empty.referralsDescription': 'Submit your first referral to get started',
  'partner.empty.outgoingReferrals': 'No outgoing referrals yet',
  'partner.empty.incomingReferrals': 'No incoming referrals yet',
  'partner.empty.earnedCommissions': 'No earned commissions yet',
  'partner.empty.owedCommissions': 'No owed commissions yet',

  // Partner Dashboard
  'partner.dashboard.welcome': 'Welcome, {{name}}',
  'partner.dashboard.overview': 'Partner Overview',
  'partner.dashboard.pendingReferrals': 'Pending Referrals',
  'partner.dashboard.activeDeals': 'Active Deals',
  'partner.dashboard.pendingCommissions': 'Pending Commissions',
  'partner.dashboard.mtdEarnings': 'MTD Earnings',
  'partner.dashboard.ytdEarnings': 'YTD Earnings',
  'partner.dashboard.teamLeaderboard': 'Team Leaderboard',
  'partner.dashboard.recentActivity': 'Recent Activity',
  'partner.dashboard.upcomingPayouts': 'Upcoming Payouts',
  'partner.dashboard.licenseAlerts': 'License Alerts',

  // Partner Detail Page (Issue #383)
  'partner.information': 'Partner Information',
  'partner.joined': 'Joined',
  'partner.totalReferrals': 'Total Referrals',
  'partner.conversions': 'Conversions',
  'partner.conversionRate': 'Conversion Rate',
  'partner.totalEarnings': 'Total Earnings',
  // Header subtitle + tenure pill (v3 design #1762)
  'partner.partnerSince': 'Partner since {{date}}',
  'partner.partnerSinceWithType': '{{type}} · partner since {{date}}',
  'partner.tenureMonths': '{{count}}mo partner',
  'partner.tenureYearsSingular': '1yr partner',
  'partner.tenureYears': '{{count}}yr partner',
  // KPI tile subtitle copy (v3 design #1762)
  'partner.kpi.lifetime': 'lifetime',
  'partner.kpi.fundedAndConverted': 'funded & converted',
  'partner.kpi.lastThirtyDays': 'last 30 days',
  'partner.kpi.paidOut': 'paid out',
  'partner.pendingCommissionsMessage': 'in pending commissions awaiting payment',
  'partner.referralCodes': 'Referral Codes',
  'partner.generateNewCode': 'Generate New Code',
  'partner.noTrackingCodes': 'No tracking codes yet',
  'partner.noPartnersHint': 'Create a partner to get started',
  'partner.createFirstCode': 'Create First Code',
  'partner.clicks': 'Clicks',
  'partner.leads': 'Leads',
  'partner.viewingDataFor': 'Viewing data for',
  'partner.myReferrals': 'My Referrals',
  'partner.allTeamReferrals': 'All Team Referrals',
  'partner.recentReferrals': 'Recent Referrals',
  'partner.yourReferrals': 'Your referrals',
  'partner.noReferralsYet': 'No referrals yet',
  'partner.loanAmount': 'Loan Amount',
  'partner.commission': 'Commission',
  'partner.commissions': 'Commissions',
  'partner.yourCommissions': 'Your commissions',
  'partner.approveSelected': 'Approve Selected',
  'partner.noCommissionsYet': 'No commissions yet',
  'partner.referralQrCode': 'Referral QR Code',
  // Partner detail page 4-card layout
  'partner.commissionTerms': 'Commission Terms',
  'partner.agreementTerms': 'Agreement Terms',
  'partner.invitationLifecycle': 'Invitation Lifecycle',
  'partner.commissionType': 'Commission Type',
  'partner.trigger': 'Trigger',
  'partner.paymentTerms': 'Payment Terms',
  'partner.exclusivity': 'Exclusivity',
  'partner.territory': 'Territory',
  'partner.effectiveDate': 'Effective Date',
  'partner.expirationDate': 'Expiration Date',
  'partner.autoRenew': 'Auto-Renew',
  'partner.exclusivityScope': 'Exclusivity Scope',
  'partner.productRestrictions': 'Product Restrictions',
  'partner.partnerVisibility': 'Partner Visibility',
  'partner.invitationStatus': 'Invitation Status',
  'partner.sentAt': 'Sent',
  'partner.viewedAt': 'Viewed',
  'partner.onboardingStartedAt': 'Onboarding Started',
  'partner.acceptedAt': 'Accepted',
  'partner.invitedBy': 'Invited By',
  'partner.company': 'Company',
  'partner.contact': 'Contact',
  'partner.agreementType': 'Agreement Type',
  // Enhanced tracking code features (Issue #369)
  'partner.editTrackingCode': 'Edit Tracking Code',
  'partner.codeName': 'Code Name',
  'partner.codeNamePlaceholder': 'e.g., Q1 Campaign, Summer Promo',
  'partner.codeNameHelper': 'Give your code a memorable name for easy identification',
  'partner.codeDescriptionPlaceholder': 'Optional notes about this code',
  'partner.attributionWindow': 'Attribution Window',
  'partner.attributionWindowPlaceholder': 'e.g., 30, 60, 90',
  'partner.attributionWindowHelper': 'Days to track conversions (leave empty for unlimited)',
  'partner.unnamedCode': 'Unnamed Code',
  'partner.exportQrCodes': 'Export QR Codes',
  'partner.showArchived': 'Show Archived',
  'partner.hideArchived': 'Hide Archived',
  // Partner Applications page
  'partner.applications.searchPlaceholder': 'Search by name, email, or submission number...',
  'partner.applications.empty.title': 'No partnership requests yet',
  'partner.applications.empty.description':
    'When someone applies to become a referral partner for your business, their application will appear here for review.',

  // ============================================================================
  // INTEGRATIONS (Issue #259 - Outbound Sales Motion)
  // ============================================================================

  // Platform Config
  'integrations.platformConfig.title': 'Platform Integrations',
  'integrations.platformConfig.description': 'Configure platform-level API keys and defaults',
  'integrations.platformConfig.provider': 'Provider',
  'integrations.platformConfig.apiKey': 'API Key',
  'integrations.platformConfig.webhookSecret': 'Webhook Secret',
  'integrations.platformConfig.defaultConfig': 'Default Configuration',
  'integrations.platformConfig.allowByok': 'Allow BYOK',
  'integrations.platformConfig.allowByokDescription': 'Allow businesses to use their own API keys',
  'integrations.platformConfig.usageThisMonth': 'Usage This Month',
  'integrations.platformConfig.usageLimit': 'Usage Limit',
  'integrations.usage.title': 'Usage Statistics',
  'integrations.usage.apiCallsToday': 'API Calls Today',
  'integrations.usage.successRate': 'Success Rate',
  'integrations.usage.rateLimitUsed': 'Rate Limit Used',
  'integrations.platformConfig.testConnection': 'Test Connection',
  'integrations.platformConfig.testSuccess': 'Connection successful',
  'integrations.platformConfig.testFailed': 'Connection failed',
  'integrations.platformConfig.noProviders': 'No providers configured',
  'integrations.platformConfig.addProvider': 'Add Provider',
  'integrations.platformConfig.editProvider': 'Edit Provider',

  // Business Config
  'integrations.businessConfig.title': 'Business Integrations',
  'integrations.businessConfig.description': 'Manage per-business integration settings',
  'integrations.businessConfig.managedBy': 'Managed By',
  'integrations.businessConfig.platform': 'Platform',
  'integrations.businessConfig.business': 'Business (BYOK)',
  'integrations.businessConfig.verified': 'Verified',
  'integrations.businessConfig.pending': 'Pending Verification',
  'integrations.businessConfig.usingPlatformKey': 'Using platform credentials',
  'integrations.businessConfig.usingBusinessKey': 'Using business credentials',
  'integrations.businessConfig.noConfigs': 'No business configurations',
  'integrations.businessConfig.apiKeyHelper': 'Enter your API key from the provider dashboard',
  'integrations.businessConfig.deleteConfirm': 'Are you sure you want to delete this integration?',
  'integrations.businessConfig.editTitle': 'Edit Integration',
  'integrations.businessConfig.empty': 'No integrations configured for this business',

  // Providers
  'integrations.providers.resend': 'Resend',
  'integrations.providers.resendDescription': 'Email delivery and marketing campaigns',
  'integrations.providers.retell': 'Retell AI',
  'integrations.providers.retellDescription': 'AI-powered voice calls and conversations',
  'integrations.providers.openai': 'OpenAI',
  'integrations.providers.openaiDescription': 'AI models for intelligence extraction',

  // Buttons
  'integrations.button.testConnection': 'Test Connection',
  'integrations.button.testing': 'Testing...',
  'integrations.button.verified': 'Verified',
  'integrations.button.saving': 'Saving...',

  // Status
  'integrations.status.connected': 'Connected',
  'integrations.status.failed': 'Failed',

  // Success messages
  'integrations.success.connectionVerified': 'Connection verified! API is accessible.',

  // Error messages
  'integrations.error.connectionFailed': 'Connection failed. Please check your credentials.',
  'integrations.error.apiKeyTooShort': 'API key must be at least 10 characters.',

  // ============================================================================
  // CAMPAIGNS (Issue #259 - Outbound Sales Motion)
  // ============================================================================

  // Campaign List (generic)
  'campaigns.title': 'Campaigns',
  'campaigns.description': 'Manage your email and voice outreach campaigns',
  'campaigns.empty': 'No campaigns yet',
  'campaigns.emptyDescription': 'Create your first campaign to start reaching out to leads',

  // Email Campaigns
  'campaigns.email.title': 'Email Campaigns',
  'campaigns.email.create': 'Create Campaign',
  'campaigns.email.edit': 'Edit Campaign',
  'campaigns.email.schedule': 'Schedule',
  'campaigns.email.send': 'Send Now',
  'campaigns.email.pause': 'Pause',
  'campaigns.email.resume': 'Resume',
  'campaigns.email.duplicate': 'Duplicate',
  'campaigns.email.noCampaigns': 'No email campaigns yet',
  'campaigns.email.createFirst': 'Create your first email campaign',
  'campaigns.email.stats.sent': 'Sent',
  'campaigns.email.stats.opened': 'Opened',
  'campaigns.email.stats.clicked': 'Clicked',
  'campaigns.email.stats.bounced': 'Bounced',
  'campaigns.email.stats.unsubscribed': 'Unsubscribed',
  'campaigns.email.stats.openRate': 'Open Rate',
  'campaigns.email.stats.clickRate': 'Click Rate',
  'campaigns.email.form.name': 'Campaign Name',
  'campaigns.email.form.subject': 'Subject Line',
  'campaigns.email.form.fromName': 'From Name',
  'campaigns.email.form.fromEmail': 'From Email',
  'campaigns.email.form.replyTo': 'Reply To',
  'campaigns.email.form.content': 'Email Content',
  'campaigns.email.form.recipients': 'Recipients',
  'campaigns.email.form.selectRecipients': 'Select recipients for this campaign',
  'campaigns.email.form.scheduleFor': 'Schedule For',
  'campaigns.email.additionalStats': 'Additional Statistics',
  'campaigns.email.campaignName': 'Campaign Name',
  'campaigns.email.details': 'Campaign Details',
  'campaigns.email.filterLeads': 'Filter Leads',
  'campaigns.email.from': 'From',
  'campaigns.email.fromEmail': 'From Email',
  'campaigns.email.fromName': 'From Name',
  'campaigns.email.htmlContent': 'HTML Content',
  'campaigns.email.htmlContentHelper': 'The HTML version of your email',
  'campaigns.email.performance': 'Performance',
  'campaigns.email.preview': 'Preview',
  'campaigns.email.recipientType': 'Recipient Type',
  'campaigns.email.replyTo': 'Reply To',
  'campaigns.email.review': 'Review',
  'campaigns.email.scheduleForLater': 'Schedule for Later',
  'campaigns.email.scheduledAt': 'Scheduled At',
  'campaigns.email.sentAt': 'Sent At',
  'campaigns.email.stats.complained': 'Complained',
  'campaigns.email.subject': 'Subject',
  'campaigns.email.textContent': 'Text Content',
  'campaigns.email.type': 'Campaign Type',

  // Voice Campaigns
  'campaigns.voice.title': 'Voice Campaigns',
  'campaigns.voice.create': 'Create Campaign',
  'campaigns.voice.edit': 'Edit Campaign',
  'campaigns.voice.start': 'Start Calls',
  'campaigns.voice.pause': 'Pause',
  'campaigns.voice.resume': 'Resume',
  'campaigns.voice.noCampaigns': 'No voice campaigns yet',
  'campaigns.voice.createFirst': 'Create your first voice campaign',
  'campaigns.voice.stats.totalCalls': 'Total Calls',
  'campaigns.voice.stats.connected': 'Connected',
  'campaigns.voice.stats.transferred': 'Transferred',
  'campaigns.voice.stats.voicemails': 'Voicemails',
  'campaigns.voice.stats.avgDuration': 'Avg Duration',
  'campaigns.voice.stats.connectRate': 'Connect Rate',
  'campaigns.voice.form.name': 'Campaign Name',
  'campaigns.voice.form.voiceId': 'Voice',
  'campaigns.voice.form.fromNumber': 'Caller ID',
  'campaigns.voice.form.greeting': 'Agent Greeting',
  'campaigns.voice.form.prompt': 'Agent Prompt',
  'campaigns.voice.form.callWindow': 'Call Window',
  'campaigns.voice.form.autonomyLevel': 'Astor Autonomy',
  'campaigns.voice.agentGreeting': 'Agent Greeting',
  'campaigns.voice.agentPrompt': 'Agent Prompt',
  'campaigns.voice.callWindowEnd': 'Call Window End',
  'campaigns.voice.callWindowStart': 'Call Window Start',
  'campaigns.voice.campaignName': 'Campaign Name',
  'campaigns.voice.filterLeads': 'Filter Leads',
  'campaigns.voice.fromNumber': 'From Number',
  'campaigns.voice.maxCallDuration': 'Max Call Duration',
  'campaigns.voice.respectDnc': 'Respect Do Not Call List',
  'campaigns.voice.retellAgentId': 'Retell Agent ID',
  'campaigns.voice.retellConfig': 'Retell Configuration',
  'campaigns.voice.review': 'Review',
  'campaigns.voice.schedule': 'Schedule',
  'campaigns.voice.scheduleForLater': 'Schedule for Later',
  'campaigns.voice.tcpaCompliance': 'TCPA Compliance',
  'campaigns.voice.voiceId': 'Voice ID',

  // Campaign Status
  'campaigns.status.draft': 'Draft',
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.status.active': 'Active',
  'campaigns.status.paused': 'Paused',
  'campaigns.status.completed': 'Completed',

  // Campaign Types
  'campaigns.types.product_announcement': 'Product Announcement',
  'campaigns.types.nurture': 'Lead Nurture',
  'campaigns.types.re_engagement': 'Re-engagement',
  'campaigns.types.referral_partner': 'Partner Outreach',

  // Campaign Errors
  'campaigns.error.notFound': 'Campaign not found',

  // Common campaign keys
  'common.pause': 'Pause',
  'common.resume': 'Resume',
  'common.phone': 'Phone',
  'common.time': 'Time',
  'common.recipientCount': 'Recipients',

  // Voice Campaign Detail
  'campaigns.voice.recentCalls': 'Recent Calls',
  'campaigns.voice.noCalls': 'No calls yet',
  'campaigns.voice.details': 'Campaign Details',
  'campaigns.voice.recipientType': 'Recipient Type',
  'campaigns.voice.avgDuration': 'Avg Call Duration',
  'campaigns.voice.callWindow': 'Call Window',
  'campaigns.voice.scheduledAt': 'Scheduled For',
  'campaigns.voice.startedAt': 'Started At',
  'campaigns.voice.performance': 'Performance',
  'campaigns.voice.outcome': 'Outcome',
  'campaigns.voice.duration': 'Duration',
  'campaigns.voice.sentiment': 'Sentiment',
  'campaigns.voice.emptyState.title': 'No voice campaigns yet',
  'campaigns.voice.emptyState.description': 'Create your first AI-powered voice campaign',

  // ============================================================================
  // ASTOR AUTONOMY (Issue #259 - Outbound Sales Motion)
  // ============================================================================

  'autonomy.title': 'Astor Autonomy Settings',
  'autonomy.description': 'Configure how Astor handles calls and when to escalate to humans',
  'autonomy.level': 'Autonomy Level',
  'autonomy.defaultSettings': 'Default Settings',
  'autonomy.enableLearning': 'Enable Learning',
  'autonomy.fallbackBehavior': 'Fallback Behavior',
  'autonomy.saved': 'Settings saved successfully',
  'autonomy.stats': 'Statistics',
  'autonomy.activeRules': 'Active Rules',
  'autonomy.totalEscalations': 'Total Escalations',
  'autonomy.fallbackWhenNoHumans': 'Fallback when no humans available',
  'autonomy.error.load': 'Failed to load autonomy settings',
  'autonomy.levels.full_ai': 'Full Astor',
  'autonomy.levels.full_aiDescription': 'Astor handles entire conversation without human involvement',
  'autonomy.levels.hybrid': 'Hybrid',
  'autonomy.levels.hybridDescription': 'Astor handles calls but escalates based on rules',
  'autonomy.levels.ai_assist': 'Astor Assist',
  'autonomy.levels.ai_assistDescription': 'Astor assists human agents with suggestions',
  'autonomy.levels.human_only': 'Human Only',
  'autonomy.levels.human_onlyDescription': 'All calls handled by human agents',
  'autonomy.escalation.title': 'Escalation Rules',
  'autonomy.escalation.addRule': 'Add Rule',
  'autonomy.escalation.editRule': 'Edit Rule',
  'autonomy.escalation.testRule': 'Test Rule',
  'autonomy.escalation.noRules': 'No escalation rules configured',
  'autonomy.escalation.empty': 'No escalation history',
  'autonomy.escalation.history': 'Escalation History',
  'autonomy.escalation.action': 'Action',
  'autonomy.escalation.type': 'Type',
  'autonomy.escalation.value': 'Value',
  'autonomy.escalation.priority': 'Priority',
  'autonomy.escalation.types.keyword': 'Keyword Detection',
  'autonomy.escalation.types.loan_amount': 'Loan Amount Threshold',
  'autonomy.escalation.types.sentiment': 'Negative Sentiment',
  'autonomy.escalation.types.confidence': 'Low Confidence',
  'autonomy.escalation.types.objection_count': 'Objection Count',
  'autonomy.fallback.title': 'Fallback Behavior',
  'autonomy.fallback.voicemail': 'Leave Voicemail',
  'autonomy.fallback.callback': 'Schedule Callback',
  'autonomy.fallback.end_call': 'End Call',

  // ============================================================================
  // PLACEHOLDER - Form input placeholders (Issue #215)
  // ============================================================================
  'placeholder.search': 'Search...',
  'placeholder.searchEntity': 'Search {{entity}}...',
  'placeholder.searchByName': 'Search by name...',
  'placeholder.searchByNameEmail': 'Search by name or email...',
  'placeholder.searchByNameOrEmail': 'Search by name or email...',
  'placeholder.searchByDetails': 'Search by name, email, or address...',
  'placeholder.addComment': 'Add a note or comment...',
  'placeholder.personalNote': 'Add a personal note to include...',
  'placeholder.rejectionReason': 'Enter the reason for rejection...',
  'placeholder.suspensionReason': 'e.g., Payment failure, Terms violation, etc.',
  'placeholder.enterDescription': 'Enter a description...',
  'placeholder.enterTitle': 'Enter a title...',
  'placeholder.example': 'e.g., {{value}}',
  'placeholder.commaSeparated': 'Comma-separated',
  'placeholder.selectBusiness': 'Select a business',
  'placeholder.unlimitedValue': 'Unlimited',

  // Entity/field examples
  'placeholder.entityName': 'e.g., Customer Contact',
  'placeholder.entityApiName': 'e.g., customer_contact',
  'placeholder.companyName': 'e.g., Acme Corporation',
  'placeholder.domain': 'e.g., acme.com',
  'placeholder.fullName': 'e.g., John Doe',
  'placeholder.emailExample': 'e.g., you@example.com',
  'placeholder.passwordHint': 'At least 8 characters',
  'placeholder.phone': 'e.g., (555) 123-4567',
  'placeholder.address': 'e.g., 123 Main Street',
  'placeholder.city': 'e.g., New York',
  'placeholder.state': 'e.g., NY',
  'placeholder.zipCode': 'e.g., 10001',
  'placeholder.branchName': 'e.g., Main Office',
  'placeholder.branchCode': 'e.g., HQ01',
  'placeholder.amount': 'e.g., $100,000',
  'placeholder.ssnFormat': 'XX-XXXXXXX',
  'placeholder.years': 'e.g., 5',
  'placeholder.institution': 'e.g., Wells Fargo, Capital One',
  'placeholder.assetExample': 'e.g., Chase Bank, Vanguard, Property Address',
  'placeholder.assetDescription': 'e.g., Joint account, Primary residence',

  // Template/rule examples
  'placeholder.templateName': 'e.g., Enterprise CRM',
  'placeholder.templateDescription': 'Describe this template...',
  'placeholder.ruleName': 'e.g., Volume Discount',
  'placeholder.ruleDescription': 'Describe this rule...',
  'placeholder.pipelineTemplate': 'e.g., Enterprise Sales Pipeline',
  'placeholder.pipelineDescription': 'Describe this pipeline template...',
  'placeholder.dimensionExample': 'e.g., Region, Product, Tier',
  'placeholder.modelExample': 'gpt-4, gpt-3.5-turbo...',
  'placeholder.systemPrompt': 'You are a helpful assistant...',
  'placeholder.testPrompt': 'Enter your test prompt here...',
  'placeholder.shareUrl': 'Share URL will be generated here',

  // URLs
  'placeholder.termsUrl': 'https://example.com/terms',
  'placeholder.slackWebhook': 'https://hooks.slack.com/...',
  'placeholder.webhookUrl': 'https://your-domain.com/webhook',
  'placeholder.customCss': '/* Add custom CSS here */',
  'placeholder.version': '1.0.0',

  // Business context
  'placeholder.caseTitle': 'Enter a descriptive title for this loan',
  'placeholder.caseDescription': 'Provide additional details about this loan...',
  'placeholder.caseDetails': 'Provide additional details about this loan...',
  'placeholder.fundUse': 'Describe how the funds will be used...',
  'placeholder.itemDescription': 'Enter item description',
  'placeholder.invoiceNotes': 'Add any notes...',
  'placeholder.trackingSource': 'e.g., LinkedIn Campaign, Email Newsletter',
  'placeholder.qualifierName': 'e.g., Industry',
  'placeholder.qualifierCode': 'e.g., industry',
  'placeholder.qualifierHelp': 'Help text for users',
  'placeholder.ruleContext': 'e.g., Tech Startups - Solar Design',
  'placeholder.auditReason': 'e.g., Scheduled rotation, Security incident',
  'placeholder.termsheetSubject': 'Your Term Sheet from [Company Name]',

  // ============================================================================
  // ACCESSIBILITY - ARIA labels (Issue #215)
  // ============================================================================
  'a11y.openMenu': 'Open navigation menu',
  'a11y.toggleSidebar': 'Toggle sidebar',
  'a11y.closeMenu': 'Close menu',
  'a11y.toggleTheme': 'Toggle theme',
  'a11y.openUserMenu': 'Open user menu',
  'a11y.openThemeMenu': 'Open theme menu',
  'a11y.breadcrumbNavigation': 'Breadcrumb navigation',
  'a11y.tableView': 'Table view',
  'a11y.kanbanView': 'Kanban view',
  'a11y.cardView': 'Card view',
  'a11y.listView': 'List view',
  'a11y.gridView': 'Grid view',
  'a11y.openSearch': 'Open search',
  'a11y.closeSearch': 'Close search',
  'a11y.openHelp': 'Open help panel',
  'a11y.closeHelp': 'Close help panel',
  'a11y.addTag': 'Add tag',
  'a11y.removeTag': 'Remove tag',
  'a11y.approve': 'Approve',
  'a11y.reject': 'Reject',
  'a11y.delete': 'Delete',
  'a11y.edit': 'Edit',
  'a11y.preview': 'Preview',
  'a11y.download': 'Download',
  'a11y.settings': 'Settings',
  'a11y.notifications': 'Notifications',
  'a11y.modelAnalyticsTabs': 'Model analytics tabs',
  'a11y.costAnalyticsTabs': 'Cost analytics tabs',
  'a11y.settingsTabs': 'Settings tabs',
  'a11y.moreInformation': 'More information',
  'a11y.copyToClipboard': 'Copy to clipboard',
  'a11y.removeItem': 'Remove item',
  'a11y.expandSection': 'Expand section',
  'a11y.collapseSection': 'Collapse section',
  'a11y.entityDetails': '{{entity}} details',
  'a11y.openActionsMenu': 'Open actions menu',
  'a11y.closeAstor': 'Close Astor assistant',
  'a11y.openAstor': 'Open Astor assistant',
  'a11y.closeChat': 'Close chat',
  'a11y.sendMessage': 'Send message',
  'a11y.dismissNudge': 'Dismiss nudge',
  'a11y.firstPage': 'First page',
  'a11y.previousPage': 'Previous page',
  'a11y.nextPage': 'Next page',
  'a11y.lastPage': 'Last page',
  'a11y.clearSearch': 'Clear search',

  // ============================================================================
  // TOOLTIP - UI tooltips (Issue #215)
  // ============================================================================
  'tooltip.edit': 'Edit',
  'tooltip.delete': 'Delete',
  'tooltip.approve': 'Approve',
  'tooltip.reject': 'Reject',
  'tooltip.duplicate': 'Duplicate',
  'tooltip.archive': 'Archive',
  'tooltip.restore': 'Restore',
  'tooltip.refresh': 'Refresh',
  'tooltip.close': 'Close',
  'tooltip.copy': 'Copy',
  'tooltip.download': 'Download',
  'tooltip.upload': 'Upload',
  'tooltip.preview': 'Preview',
  'tooltip.quickView': 'Quick View',
  'tooltip.editEntity': 'Edit {{entity}}',
  'tooltip.deleteEntity': 'Delete {{entity}}',
  'tooltip.viewEntity': 'View {{entity}}',
  'tooltip.import': 'Import',
  'tooltip.export': 'Export',
  'tooltip.comingSoon': 'Coming soon',
  'tooltip.editPartnerAssignment': 'Edit Partner Assignment',
  'tooltip.removePartner': 'Remove Partner',
  'tooltip.copyTrackingUrl': 'Copy tracking URL',
  'tooltip.removeAsset': 'Remove asset',
  'tooltip.removeLiability': 'Remove liability',
  'tooltip.sendMessage': 'Send Message',
  'tooltip.visibleToConsumer': 'This message is visible to the consumer/applicant',
  'tooltip.privateNotVisibleToConsumer': 'This message is private and NOT visible to the consumer/applicant',
  'tooltip.showConsumerVisibleMessages': 'Show messages visible to consumers',
  'tooltip.showPrivateMessagesOnly': 'Show private messages (not visible to consumers)',
  'tooltip.consumerWillSeeThisMessage': 'Consumer will see this message in their portal',
  'tooltip.consumerWillNotSeeThisMessage': 'Consumer will NOT see this message - private to business team only',

  // ============================================================================
  // SECTION - DetailSection titles (Issue #215)
  // ============================================================================
  'section.caseInformation': 'Loan Information',
  'section.caseSettings': 'Loan Settings',
  'section.status': 'Status',
  'section.overview': 'Overview',
  'section.contact': 'Contact',
  'section.activity': 'Activity',
  'section.performance': 'Performance',
  'section.earnings': 'Earnings',
  'section.timeline': 'Timeline',
  'section.details': 'Details',
  'section.people': 'People',
  'section.borrower': 'Borrower',
  'section.loanTerms': 'Loan Terms',
  'section.property': 'Property',
  'section.propertyDetails': 'Property Details',
  'section.activityTimeline': 'Activity Timeline',
  'section.history': 'History',
  'section.healthMetrics': 'Health Metrics',
  'section.diagnostics': 'Diagnostics',
  'section.trafficMetrics': 'Traffic Metrics',
  'section.dependencies': 'Dependencies',
  'section.totalAssets': 'Total Assets',
  'section.totalDebt': 'Total Debt',
  'section.monthlyPayments': 'Monthly Payments',
  'section.netWorth': 'Net Worth',
  'section.conversionRate': 'Conversion Rate',
  'section.lifetimeEarnings': 'Lifetime Earnings',
  'section.pendingActions': 'Pending Actions',
  'section.basicInfo': 'Basic Information',
  'section.partnerConfig': 'Partner Configuration',
  'section.additionalInfo': 'Additional Information',
  'section.agreementTerms': 'Agreement Terms',
  'section.commissionTerms': 'Commission Terms',
  'section.partnerInfo': 'Partner Information',
  'section.requiredInfo': 'Required Information',
  'section.roleAssignment': 'Role Assignment',
  // Issue #368 - Partner Referral Tracking
  'section.referralAttribution': 'Referral Attribution',

  // ============================================================================
  // LABEL - Form labels (Issue #368 - Partner Referral Tracking)
  // ============================================================================
  'label.referralCode': 'Referral Code',
  'label.referralPartner': 'Referring Partner',
  'label.referralLink': 'Referral Link',
  'label.commissionStatus': 'Commission Status',
  'label.casesReferred': 'Loans Referred',
  'label.commissionsEarned': 'Commissions Earned',
  'label.pendingCommissions': 'Pending Commissions',
  'label.copyReferralLink': 'Copy Referral Link',

  // ============================================================================
  // STATUS - Status labels (Issue #368 - Partner Referral Tracking)
  // ============================================================================
  'status.referred': 'Referred',
  'status.attributed': 'Attributed',
  'status.commissionPending': 'Commission Pending',
  'status.commissionPaid': 'Commission Paid',

  // ============================================================================
  // DIALOG - Dialog/modal titles (Issue #215 - additional keys)
  // Note: Base dialog keys (createTitle, editTitle, etc.) are defined earlier in the file
  // ============================================================================
  'dialog.exportPortfolio': 'Export Portfolio',
  'dialog.createAgent': 'Create New Agent',
  'dialog.editAgent': 'Edit Agent',

  // ============================================================================
  // MESSAGES (Consumer Portal - Issue #215)
  // ============================================================================
  'messages.noConversation': 'No messages in this conversation',
  'messages.send': 'Send',

  // ============================================================================
  // PORTFOLIO (Consumer Portal - Issue #215)
  // ============================================================================
  'portfolio.title': 'My Portfolio',
  'portfolio.description': 'Track and manage all your submitted deals.',
  'portfolio.totalDeals': 'Total Deals',
  'portfolio.totalVolume': 'Total Volume',
  'portfolio.inPipeline': 'In Pipeline',
  'portfolio.approved': 'Approved',
  'portfolio.submitDeal': 'Submit Deal',
  'portfolio.loanDetails': 'Loan Details',

  // ============================================================================
  // AGENTS (Admin Portal - Issue #215)
  // ============================================================================
  'agents.title': 'Agents',
  'agents.description': 'Manage and test your agents with streaming chat capabilities',
  'agents.searchPlaceholder': 'Search agents by name or description...',
  'agents.createAgent': 'Create Agent',
  'agents.empty': 'No agents found. Create your first agent to get started.',
  'agents.table.name': 'Name',
  'agents.table.description': 'Description',
  'agents.table.model': 'Model',
  'agents.table.status': 'Status',
  'agents.table.created': 'Created',
  'agents.table.actions': 'Actions',
  'agents.config.title': 'Configuration',
  'agents.config.model': 'Model',
  'agents.config.temperature': 'Temperature',
  'agents.config.temperatureHelp': 'Controls randomness (0 = focused, 2 = creative)',
  'agents.config.maxTokens': 'Max Tokens',
  'agents.config.systemPrompt': 'System Prompt',
  'agents.tooltip.test': 'Test Agent',
  'agents.tooltip.edit': 'Edit Agent',
  'agents.tooltip.delete': 'Delete Agent',
  'agents.showingCount': 'Showing {{shown}} of {{total}} agents',
  'agents.error.loadFailed': 'Failed to load agents. Please try again.',

  // ============================================================================
  // CONTRACT PACKAGE (Contract-Driven Partner Invitation System)
  // ============================================================================
  'contract.title': 'Contracts',
  'contract.package': 'Contract Package',
  'contract.selectContracts': 'Select Contracts',
  'contract.selectContractsHint': 'Choose one or more contract types for this invitation',
  'contract.details': 'Contract Details',
  'contract.detailsHint': 'Complete the terms for each selected contract',
  'contract.review': 'Review Contracts',
  'contract.reviewHint': 'Review all contracts in the invitation package',
  'contract.signerAssignment': 'Assign Signers',
  'contract.signerAssignmentHint': 'Designate who will sign each contract',
  'contract.signing': 'Sign Contracts',
  'contract.signingHint': 'Sign each contract electronically',
  'contract.signingProgress': '{{signed}} of {{total}} contracts signed',
  'contract.status.draft': 'Draft',
  'contract.status.pendingSignature': 'Pending Signature',
  'contract.status.b1Signed': 'Signed by Sender',
  'contract.status.b2Signed': 'Signed by Recipient',
  'contract.status.completed': 'Completed',
  'contract.status.voided': 'Voided',
  'contract.type.referralBroker': 'Referral Broker Agreement',
  'contract.type.correspondentLending': 'Correspondent Lending Agreement',
  'contract.type.wholesale': 'Wholesale Agreement',
  'contract.type.channelPartner': 'Channel Partner Agreement',
  'contract.type.nda': 'Non-Disclosure Agreement',
  'contract.type.dpa': 'Data Processing Agreement',
  'contract.type.msa': 'Master Service Agreement',
  'contract.type.sla': 'Service Level Agreement',
  'contract.signer.email': 'Signer Email',
  'contract.signer.name': 'Signer Name',
  'contract.signer.role': 'Signer Title',
  'contract.signer.default': 'Use default signer for all contracts',
  'contract.empty.noTypes': 'No contract types configured',
  'contract.empty.noTypesHint': 'Configure contract types in your business settings first',
  'contract.empty.noContracts': 'No contracts in this invitation',

  // ============================================================================
  // PARTNER DETAIL (Business Portal - Issue #215)
  // ============================================================================
  'partnerDetail.partnerInfo': 'Partner Information',
  'partnerDetail.recentReferrals': 'Recent Referrals',
  'partnerDetail.commissions': 'Commissions',
  'partnerDetail.editPartner': 'Edit Partner',
  'partnerDetail.saveChanges': 'Save Changes',
  'partnerDetail.approveSelected': 'Approve Selected',
  'partnerDetail.exportCsv': 'Export CSV',
  'partnerDetail.confirmApprove': 'Are you sure you want to approve this commission?',
  'partnerDetail.confirmBulkApprove': 'Are you sure you want to approve {{count}} commission(s)?',
  'partnerDetail.approveAll': 'Approve All ({{count}})',
  'partnerDetail.noReferrals': 'No referrals yet',
  'partnerDetail.noCommissions': 'No commissions yet',
  'partnerDetail.rejectionReason': 'Rejection Reason',
  'partnerDetail.rejectionPlaceholder': 'Enter the reason for rejection...',
  'partnerDetail.commissionRate': 'Commission Rate',
  'partnerDetail.joined': 'Joined',
  'partnerDetail.backToPartners': 'Back to Partners',
  'partnerDetail.table.client': 'Client',
  'partnerDetail.table.loanAmount': 'Loan Amount',
  'partnerDetail.table.commission': 'Commission',
  'partnerDetail.table.date': 'Date',
  'partnerDetail.table.amount': 'Amount',

  // ============================================================================
  // THEME (UI theme switching - Issue #215)
  // ============================================================================
  'theme.label': 'Theme',
  'theme.switchToLight': 'Switch to light mode',
  'theme.switchToDark': 'Switch to dark mode',

  // ============================================================================
  // ENRICHMENT (Issue #271 - SFR Analytics CRM Integration)
  // ============================================================================
  'enrichment.enrichFromSfr': 'Enrich from SFR',
  'enrichment.batchEnrichment': 'Batch Enrichment',
  'enrichment.enriching': 'Enriching...',
  'enrichment.enrichmentComplete': 'Enrichment complete',
  'enrichment.enrichmentFailed': 'Enrichment failed',
  'enrichment.selectLeadsToEnrich': 'Select leads to enrich',
  'enrichment.enrichmentHistory': 'Enrichment History',
  'enrichment.noEnrichmentHistory': 'No enrichment history',
  'enrichment.enrichSelected': 'Enrich Selected',
  'enrichment.enrichingCount': 'Enriching {{count}} leads...',
  'enrichment.enrichmentProgress': '{{completed}} of {{total}} completed',
  'enrichment.enrichmentSuccessCount': '{{count}} leads enriched successfully',
  'enrichment.enrichmentFailedCount': '{{count}} leads failed to enrich',
  'enrichment.selectEnrichmentTypes': 'Select enrichment types',
  'enrichment.buyerProfile': 'Buyer Profile',
  'enrichment.propertyHistory': 'Property History',
  'enrichment.ownershipHistory': 'Ownership History',
  'enrichment.transactionHistory': 'Transaction History',
  'enrichment.marketAnalytics': 'Market Analytics',
  'enrichment.contactInfo': 'Contact Info',
  'enrichment.lastEnriched': 'Last enriched',
  'enrichment.enrichmentSource': 'Source',
  'enrichment.enrichmentType': 'Type',
  'enrichment.enrichmentStatus': 'Status',
  'enrichment.cancelEnrichment': 'Cancel Enrichment',
  'enrichment.viewDetails': 'View Details',
  'enrichment.dataUpdated': 'Data Updated',
  'enrichment.noChanges': 'No changes detected',

  // Batch enrichment dialog
  'enrichment.selectedCount': '{{count}} leads selected',
  'enrichment.confirmBatchEnrich': 'Are you sure you want to enrich {{count}} leads from SFR Analytics?',
  'enrichment.batchEnrichDescription':
    'This will fetch and update property data, ownership records, and transaction history for all selected leads.',
  'enrichment.startingBatch': 'Starting batch enrichment...',
  'enrichment.processingLeads': 'Processing {{processed}} of {{total}} leads...',
  'enrichment.batchComplete': 'Batch Enrichment Complete',
  'enrichment.batchResults': '{{success}} leads enriched successfully, {{failed}} failed',
  'enrichment.batchFailed': 'Batch Enrichment Failed',
  'enrichment.tryAgainLater': 'Please try again later or contact support if the issue persists.',
  'enrichment.batchCancelled': 'Batch Enrichment Cancelled',
  'enrichment.batchCancelledResults': 'Processed {{processed}} of {{total}} leads before cancellation.',
  'enrichment.startEnrichment': 'Start Enrichment',
  'enrichment.cancelBatch': 'Cancel Batch',
  'enrichment.enrichFromSfrAnalytics': 'Enrich leads from SFR Analytics data',
  'enrichment.selectAll': 'Select all leads',
  'enrichment.selectLead': 'Select {{name}}',

  // Multi-Value Contacts (Issue #525)
  'contacts.emails': 'Email Addresses',
  'contacts.phones': 'Phone Numbers',
  'contacts.addresses': 'Addresses',
  'contacts.noEmails': 'No email addresses added',
  'contacts.noPhones': 'No phone numbers added',
  'contacts.noAddresses': 'No addresses added',
  'contacts.primary': 'Primary',
  'contacts.setAsPrimary': 'Set as primary',
  'contacts.verified': 'Verified',
  'contacts.verifiedOn': 'Verified on',
  'contacts.unverified': 'Unverified',
  'contacts.street1': 'Street Address',
  'contacts.street2': 'Apt, Suite, etc.',
  'contacts.city': 'City',
  'contacts.state': 'State',
  'contacts.postalCode': 'ZIP Code',
  'contacts.types.work': 'Work',
  'contacts.types.personal': 'Personal',
  'contacts.types.mobile': 'Mobile',
  'contacts.types.home': 'Home',
  'contacts.types.fax': 'Fax',
  'contacts.types.other': 'Other',
  'contacts.types.business': 'Business',
  'contacts.types.mailing': 'Mailing',
  'contacts.types.property': 'Property',

  // Enrichment History Page (Issue #271)
  'enrichment.totalJobs': 'Total Jobs',
  'enrichment.completedJobs': 'Completed',
  'enrichment.runningJobs': 'Running',
  'enrichment.failedJobs': 'Failed',
  'enrichment.searchJobs': 'Search jobs...',
  'enrichment.jobId': 'Job ID',
  'enrichment.targetType': 'Target Type',
  'enrichment.enrichmentTypes': 'Enrichment Types',
  'enrichment.status': 'Status',
  'enrichment.progress': 'Progress',
  'enrichment.startedAt': 'Started',
  'enrichment.completedAt': 'Completed',
  'enrichment.noJobsFound': 'No enrichment jobs found',
  'enrichment.loadError': 'Failed to load enrichment history',

  // Enrichment Status Badge (Issue #271)
  'enrichment.statusEnriched': 'Enriched',
  'enrichment.statusPartial': 'Partially Enriched',
  'enrichment.statusPending': 'Enrichment Pending',
  'enrichment.statusFailed': 'Enrichment Failed',
  'enrichment.statusNotEnriched': 'Not Enriched',
  'enrichment.enrichmentColumn': 'Enrichment',

  // Enrichment Settings (Issue #271)
  'enrichment.settings': 'Enrichment Settings',
  'enrichment.settingsDescription': 'Configure automatic and manual enrichment options',
  'enrichment.autoEnrich': 'Auto-Enrich',
  'enrichment.autoEnrichDescription': 'Automatically enrich new leads when created',
  'enrichment.autoEnrichOnImport': 'Enrich on Import',
  'enrichment.autoEnrichOnImportDescription': 'Automatically enrich leads imported from external sources',
  'enrichment.defaultEnrichmentTypes': 'Default Enrichment Types',
  'enrichment.defaultEnrichmentTypesDescription': 'Select which data types to enrich by default',
  'enrichment.refreshInterval': 'Refresh Interval',
  'enrichment.refreshIntervalDescription': 'How often to re-enrich stale data (days)',
  'enrichment.settingsSaved': 'Enrichment settings saved',
  'enrichment.settingsSaveFailed': 'Failed to save enrichment settings',

  // Enrichment Review Panel (Issue #525 - SFR Side-by-Side Review)
  'enrichment.reviewTitle': 'Review SFR Enrichment',
  'enrichment.fetchSFRData': 'Fetch SFR Analytics Data',
  'enrichment.fetchDescription':
    'Click below to fetch enrichment data from SFR Analytics. You can then review and select which fields to import.',
  'enrichment.fetchData': 'Fetch Data',
  'enrichment.fetchingData': 'Fetching data from SFR Analytics...',
  'enrichment.fetchFailed': 'Failed to Fetch Data',
  'enrichment.fieldsWithChanges': 'fields with changes',
  'enrichment.selectedForImport': 'selected for import',
  'enrichment.currentValue': 'Current',
  'enrichment.sfrValue': 'SFR Value',
  'enrichment.noValue': '(not set)',
  'enrichment.changed': 'Changed',
  'enrichment.noChange': 'No Change',
  'enrichment.applySelected': 'Apply Selected',

  // ============================================================================
  // INTEGRATION MONITORING (Issue #271 - Platform Integration Health)
  // ============================================================================
  'integrationMonitoring.title': 'Integration Monitoring',
  'integrationMonitoring.description': 'Platform-wide integration health, quality metrics, and performance monitoring',

  // Overview
  'integrationMonitoring.healthOverview': 'Integration Health Overview',
  'integrationMonitoring.recentEvents': 'Recent Integration Events',
  'integrationMonitoring.successRate': 'Success Rate',
  'integrationMonitoring.avgLatency': 'Avg Latency',
  'integrationMonitoring.totalRequests': 'Total Requests',
  'integrationMonitoring.failedRequests': 'Failed Requests',
  'integrationMonitoring.lastChecked': 'Last Checked',

  // Status
  'integrationMonitoring.status.healthy': 'Healthy',
  'integrationMonitoring.status.degraded': 'Degraded',
  'integrationMonitoring.status.down': 'Down',

  // Providers
  'integrationMonitoring.provider.sfrAnalytics': 'SFR Analytics',
  'integrationMonitoring.provider.resend': 'Resend',
  'integrationMonitoring.provider.retell': 'RetellAI',

  // SFR Analytics
  'integrationMonitoring.sfr.dataQualityScore': 'Data Quality Score',
  'integrationMonitoring.sfr.accuracyValidation': 'Based on accuracy validation',
  'integrationMonitoring.sfr.activeJobs': 'Active Jobs',
  'integrationMonitoring.sfr.runningPending': '{{running}} running, {{pending}} pending',

  // Resend (Email)
  'integrationMonitoring.resend.deliveryRate': 'Delivery Rate',
  'integrationMonitoring.resend.openRate': 'Open Rate',
  'integrationMonitoring.resend.bounceRate': 'Bounce Rate',
  'integrationMonitoring.resend.clickRate': 'Click Rate',
  'integrationMonitoring.resend.delivered': '{{count}} delivered',
  'integrationMonitoring.resend.opened': '{{count}} opened',
  'integrationMonitoring.resend.bounced': '{{count}} bounced',
  'integrationMonitoring.resend.clicked': '{{count}} clicks',

  // RetellAI (Voice)
  'integrationMonitoring.retell.connectionRate': 'Connection Rate',
  'integrationMonitoring.retell.avgSentiment': 'Avg Sentiment',
  'integrationMonitoring.retell.callQuality': 'Call Quality',
  'integrationMonitoring.retell.transferRate': 'Transfer Rate',
  'integrationMonitoring.retell.connected': '{{count}} connected',
  'integrationMonitoring.retell.transferred': '{{count}} transferred',
  'integrationMonitoring.retell.audioQuality': 'Audio quality rating',
  'integrationMonitoring.retell.overallPositive': 'Overall positive',

  // Events
  'integrationMonitoring.eventType': 'Event Type',
  'integrationMonitoring.provider': 'Provider',
  'integrationMonitoring.latency': 'Latency',
  'integrationMonitoring.time': 'Time',
  'integrationMonitoring.details': 'Details',

  // ============================================================================
  // WORKFLOWS
  // ============================================================================
  'workflows.title': 'Workflow Automation',
  'workflows.description': 'Create rules to automate business processes',
  'workflows.newRule': 'New Rule',
  'workflows.trigger': 'Trigger',
  'workflows.entityType': 'Entity Type',
  'workflows.entityTypes': 'Entity Types',
  'workflows.conditions': 'Conditions',
  'workflows.actions': 'Actions',
  'workflows.executions': 'Executions',
  'workflows.status': 'Status',
  'workflows.rule': 'Rule',
  'workflows.ruleName': 'Rule Name',
  'workflows.ruleNamePlaceholder': 'e.g., Auto-assign high-value leads',
  'workflows.ruleDescription': 'Description',
  'workflows.basicInfo': 'Basic Information',
  'workflows.noConditions': 'No conditions - rule will trigger for all records',
  'workflows.addAtLeastOneAction': 'Add at least one action',
  'workflows.addCondition': 'Add Condition',
  'workflows.addAction': 'Add Action',
  'workflows.field': 'Field',
  'workflows.operator': 'Operator',
  'workflows.value': 'Value',
  'workflows.actionType': 'Action Type',
  'workflows.createRule': 'Create Rule',
  'workflows.creating': 'Creating...',
  'workflows.testRule': 'Test Rule',
  'workflows.testDescription': 'Test this rule with sample data to verify it works as expected.',
  'workflows.sampleRecordId': 'Sample Record ID',
  'workflows.sampleRecordPlaceholder': 'Enter a record ID to test against',
  'workflows.runTest': 'Run Test',
  'workflows.testing': 'Testing...',
  'workflows.testSuccess': 'Test completed successfully! Check execution logs for details.',
  'workflows.deleteConfirm': 'Delete rule "{{name}}"?',
  'workflows.configureDetailed': 'Configure in detailed view',
  'workflows.emailTemplate': 'Email Template',
  'workflows.selectTemplate': 'Select template...',
  'workflows.webhookUrl': 'Webhook URL',

  // Stats
  'workflows.stats.totalRules': 'Total Rules',
  'workflows.stats.active': 'Active',
  'workflows.stats.totalExecutions': 'Total Executions',
  // Action Types - Issue #372
  'workflows.actions.send_email': 'Send Email',
  'workflows.actions.send_email_gmail': 'Send Email (Gmail)',
  'workflows.actions.send_sms': 'Send SMS',
  'workflows.actions.update_field': 'Update Field',
  'workflows.actions.create_task': 'Create Task',
  'workflows.actions.assign_user': 'Assign User',
  'workflows.actions.move_stage': 'Move Stage',
  'workflows.actions.add_tag': 'Add Tag',
  'workflows.actions.remove_tag': 'Remove Tag',
  'workflows.actions.webhook': 'Webhook',
  'workflows.actions.delay': 'Delay',
  'workflows.actions.notify_user': 'Notify User',
  'workflows.actions.create_case': 'Create Loan',
  'workflows.actions.update_case_status': 'Update Loan Status',
  'workflows.actions.enroll_sequence': 'Enroll in Sequence',
  'workflows.actions.unenroll_sequence': 'Unenroll from Sequence',

  // ============================================================================
  // CONSUMERS (Issue #311 - Portal UX Gaps)
  // ============================================================================
  'consumers.title': 'Consumers',
  'consumers.description': 'Manage your consumer relationships',
  'consumers.sendMessage.title': 'Send Message',
  'consumers.sendMessage.recipient': 'Sending message to:',
  'consumers.sendMessage.channelLabel': 'Delivery Channel',
  'consumers.sendMessage.email': 'Email',
  'consumers.sendMessage.sms': 'SMS',
  'consumers.sendMessage.push': 'Push Notification',
  'consumers.sendMessage.noChannels': 'No contact information available for this consumer.',
  'consumers.sendMessage.subject': 'Subject',
  'consumers.sendMessage.subjectPlaceholder': 'Message subject...',
  'consumers.sendMessage.message': 'Message',
  'consumers.sendMessage.messagePlaceholder': 'Type your message here...',
  'consumers.sendMessage.characters': 'characters',
  'consumers.sendMessage.priority': 'Priority',
  'consumers.sendMessage.priorityLow': 'Low',
  'consumers.sendMessage.priorityNormal': 'Normal',
  'consumers.sendMessage.priorityHigh': 'High',
  'consumers.sendMessage.send': 'Send Message',
  'consumers.sendMessage.sending': 'Sending...',
  'consumers.sendMessage.success': 'Message sent successfully!',
  'consumers.sendMessage.error': 'Failed to send message. Please try again.',
  'consumers.viewAsConsumer': 'View as Consumer',

  // Send Application Invite panel
  'consumers.sendInvite.title': 'Send application invite',
  'consumers.sendInvite.description':
    'Email this borrower a magic link to start their loan application on the consumer portal.',
  'consumers.sendInvite.recipientLabel': 'Sending to',
  'consumers.sendInvite.subjectLabel': 'Subject',
  'consumers.sendInvite.subjectDefault': "You're invited to apply — {{lender}}",
  'consumers.sendInvite.defaultLenderName': 'your lender',
  'consumers.sendInvite.noteLabel': 'Note (optional)',
  'consumers.sendInvite.notePlaceholder': 'Add a personal message that will appear in the email…',
  'consumers.sendInvite.characters': 'characters',
  'consumers.sendInvite.sendButton': 'Send invite',
  'consumers.sendInvite.successMessage': 'Invite sent successfully.',
  'consumers.sendInvite.error': 'Failed to send invite. Please try again.',
  'consumers.sendInvite.clipboardCopiedMessage':
    'Email service is unavailable — we copied the invite link to your clipboard so you can share it manually.',
  'consumers.sendInvite.stubbedMessage': 'Email service is unavailable. The invite link is ready to share manually.',
  'consumers.sendInvite.footerText': 'The invite link points to {{url}}',

  // Application Invite history
  'consumers.invites.sectionTitle': 'Application Invites',
  // Button label inside the "Application Invites" card — "application" is
  // implicit from the section title, so the CTA stays one line in narrow
  // sidebar cards (PR #1438 follow-up; layout fix in InviteHistorySection).
  'consumers.invites.sendCta': 'Send invite',
  'consumers.invites.empty': 'No invites sent yet.',
  'consumers.invites.column.sent': 'Sent',
  'consumers.invites.column.channel': 'Channel',
  'consumers.invites.column.subject': 'Subject',
  'consumers.invites.column.expires': 'Expires',
  'consumers.invites.column.status': 'Status',
  'consumers.invites.status.outstanding': 'Outstanding',
  'consumers.invites.status.accepted': 'Accepted',
  'consumers.invites.status.revoked': 'Revoked',
  'consumers.invites.status.expired': 'Expired',
  'consumers.invites.revokeTooltip': 'Revoke invite',
  'consumers.invites.loadFailed': 'Failed to load invites: {{message}}',

  // ============================================================================
  // IMPERSONATION (Issue #321 - User Impersonation)
  // ============================================================================
  // Business Portal Banner (shown during impersonation)
  'impersonation.banner.message': 'Impersonating {{userName}} - Actions logged as {{actorEmail}} ({{duration}})',
  'impersonation.banner.reason': 'Reason',
  'impersonation.banner.endSession': 'End Session',
  'impersonation.banner.ending': 'Ending session...',

  // Consumer Portal Banner (softer language for consumers)
  'impersonation.consumerBanner.message': 'Support session active - {{actorEmail}} is assisting with your account',
  'impersonation.consumerBanner.reason': 'Reason',
  'impersonation.consumerBanner.info': 'All actions are logged for your protection',

  // Admin Portal Impersonation Actions
  'admin.impersonate': 'Impersonate',
  'admin.impersonateConsumer': 'Impersonate Consumer',

  // Impersonation Modal
  'admin.impersonation.title': 'Impersonate User',
  'admin.impersonation.titleConsumer': 'Impersonate Consumer',
  'admin.impersonation.warning':
    'You are about to impersonate {{name}}. All actions will be logged and attributed to you.',
  'admin.impersonation.warningConsumer':
    'You are about to view the Consumer Portal as {{name}}. All actions will be logged and attributed to you.',
  'admin.impersonation.targetLabel': 'Target',
  'admin.impersonation.reasonLabel': 'Reason for Impersonation',
  'admin.impersonation.reasonPlaceholder': 'e.g., Investigating support ticket #1234',
  'admin.impersonation.reasonHelp': 'Required for audit compliance (min 10 characters)',
  'admin.impersonation.durationLabel': 'Session Duration',
  'admin.impersonation.duration5min': '5 minutes',
  'admin.impersonation.duration15min': '15 minutes',
  'admin.impersonation.duration30min': '30 minutes (default)',
  'admin.impersonation.startButton': 'Start Impersonation',
  'admin.impersonation.starting': 'Starting session...',
  'admin.impersonation.success': 'Impersonation session started. Opening portal in new tab...',
  'admin.impersonation.error': 'Failed to start impersonation session',
  'admin.impersonation.cannotImpersonateSuperAdmin': 'Cannot impersonate other super admins',
  'admin.impersonation.cannotImpersonateSelf': 'Cannot impersonate yourself',

  // Active Sessions (Admin view)
  'admin.impersonation.activeSessions': 'Active Impersonation Sessions',
  'admin.impersonation.noActiveSessions': 'No active impersonation sessions',
  'admin.impersonation.revokeSession': 'Revoke Session',
  'admin.impersonation.revokeConfirm': 'Are you sure you want to revoke this impersonation session?',
  'admin.impersonation.revokeReasonLabel': 'Revocation Reason',
  'admin.impersonation.revokeSuccess': 'Session revoked successfully',
  'admin.impersonation.revokeError': 'Failed to revoke session',

  // ============================================================================
  // PIPELINE GENERATION (Issue #360 - Juicebox-Style Pipeline)
  // ============================================================================

  // Prospect Search Page
  'pipeline.search.title': 'Prospect Search',
  'pipeline.search.subtitle': 'Find and connect with real estate investors using AI-powered search',

  // AI Search
  'pipeline.aiSearch.mode.ai': 'AI Search',
  'pipeline.aiSearch.placeholder': 'Investors with 5+ properties in California, purchased in last year...',
  'pipeline.aiSearch.searching': 'Analyzing your search...',
  'pipeline.lead.leadScore': 'Lead Score',
  'pipeline.lead.noActivity': 'No activity yet',
  'pipeline.aiSearch.generating': 'Generating filters...',
  'pipeline.aiSearch.noResults': 'No matching prospects found',
  'pipeline.aiSearch.resultsCount': '{{count}} matches',
  'pipeline.aiSearch.editFilters': 'Edit Filters',
  'pipeline.aiSearch.saveSearch': 'Save Search',
  'pipeline.aiSearch.importProspects': 'Import Prospects',
  'pipeline.aiSearch.findSimilar': 'Find Similar',
  'pipeline.aiSearch.boolean': 'Boolean',
  'pipeline.aiSearch.manual': 'Select Manually',

  // AI Filter Summary
  'pipeline.aiSearch.filterSummary.title': 'AI interpreted your search as:',
  'pipeline.aiSearch.filterSummary.titleFallback': 'Basic search interpretation:',
  'pipeline.aiSearch.filterSummary.filterCount': '{{count}} filters',
  'pipeline.aiSearch.filterSummary.editFilters': 'Edit Filters',
  'pipeline.aiSearch.filterSummary.editHint': 'Click to refine these filters',
  'pipeline.aiSearch.filterSummary.noFilters': 'No specific filters detected. Click Edit Filters to add criteria.',
  'pipeline.aiSearch.filterSummary.fallbackMode': 'Basic Mode',
  'pipeline.aiSearch.filterSummary.fallbackHint': 'Configure API keys for smarter AI-powered search',
  'pipeline.aiSearch.filterSummary.toggleLabel': 'Toggle filter summary',

  // AI Fallback Warning
  'pipeline.aiSearch.fallbackWarning.title': 'Using Basic Search Parser',
  'pipeline.aiSearch.fallbackWarning.message':
    'AI-powered search is currently unavailable. Your search was processed using basic pattern matching, which may be less accurate.',
  'pipeline.aiSearch.fallbackWarning.hint':
    'Contact your administrator to configure OpenAI or Anthropic API keys for better AI-powered search results.',

  // Boolean Search
  'pipeline.boolean.title': 'Boolean Search',
  'pipeline.boolean.subtitle': 'Build precise queries using boolean operators',
  'pipeline.boolean.fields': 'Available Fields',
  'pipeline.boolean.operators': 'Operators',
  'pipeline.boolean.examples': 'Example Queries',
  'pipeline.boolean.helpTitle': 'Boolean Search Syntax',
  'pipeline.boolean.helpDescription':
    'Use field:value pairs combined with AND, OR, NOT operators to build precise search queries.',
  'pipeline.boolean.availableFields': 'Available Fields:',
  'pipeline.boolean.operatorHelp': 'Operators:',

  // Find Similar
  'pipeline.findSimilar.title': 'Find Similar Investors',
  'pipeline.findSimilar.subtitle': 'Search for investors matching similar attributes',
  'pipeline.findSimilar.selectInvestor': 'Select Reference Investor',
  'pipeline.findSimilar.searchPlaceholder': 'Search by name...',
  'pipeline.findSimilar.matchBy': 'Match By',
  'pipeline.findSimilar.matchByDescription': 'Select which attributes to use for finding similar investors',
  'pipeline.findSimilar.search': 'Find Similar',

  // Advanced Filters
  'pipeline.filters.title': 'Edit Your Search Filters',
  'pipeline.filters.matchCount': '{{count}}+ matches',
  'pipeline.filters.saveChanges': 'Save Changes',
  'pipeline.filters.general': 'General',
  'pipeline.filters.locations': 'Locations',
  'pipeline.filters.investorType': 'Investor Type',
  'pipeline.filters.propertyType': 'Property Type',
  'pipeline.filters.activity': 'Activity',
  'pipeline.filters.portfolio': 'Portfolio',
  'pipeline.filters.keywords': 'Keywords',
  'pipeline.filters.minProperties': 'Min Properties Owned',
  'pipeline.filters.maxProperties': 'Max Properties Owned',
  'pipeline.filters.minPortfolioValue': 'Min Portfolio Value',
  'pipeline.filters.maxPortfolioValue': 'Max Portfolio Value',
  'pipeline.filters.minInvestorScore': 'Min Investor Score',
  'pipeline.filters.lastActivityDays': 'Active in last (days)',
  'pipeline.filters.states': 'States',
  'pipeline.filters.cities': 'Cities',
  'pipeline.filters.zipCodes': 'Zip Codes',
  'pipeline.filters.selectPreset': 'Select Preset',
  'pipeline.filters.noLocations': 'No locations added',
  'pipeline.filters.addLocation': 'Add location',
  'pipeline.filters.hideInactive': 'Hide inactive filters',
  'pipeline.filters.clearAll': 'Clear All',
  'pipeline.filters.apply': 'Apply Filters',
  'pipeline.filters.activeCount': '{{count}} active filters',
  'pipeline.filters.anyTime': 'Any Time',
  'pipeline.filters.last30Days': 'Last 30 Days',
  'pipeline.filters.last6Months': 'Last 6 Months',
  'pipeline.filters.last7Days': 'Last 7 Days',
  'pipeline.filters.last90Days': 'Last 90 Days',
  'pipeline.filters.lastYear': 'Last Year',
  'pipeline.filters.name': 'Filter Name',
  'pipeline.filters.namePlaceholder': 'Enter filter name',
  'pipeline.filters.portfolioValue': 'Portfolio Value',
  'pipeline.filters.propertiesOwned': 'Properties Owned',
  'pipeline.filters.zipCodesHelp': 'Enter zip codes separated by commas',
  'pipeline.filters.zipCodesPlaceholder': 'e.g., 90210, 90211',
  'pipeline.filters.scoring': 'Scoring',
  'pipeline.filters.search': 'Search',
  'pipeline.filters.investorScore': 'Investor Score',
  'pipeline.filters.creditIndicator': 'Credit Indicator',
  'pipeline.filters.companyName': 'Company Name',
  'pipeline.filters.counties': 'Counties',
  'pipeline.filters.lastPurchaseDays': 'Last Purchase',
  'pipeline.filters.lastSaleDays': 'Last Sale',
  'pipeline.filters.last2Years': 'Last 2 Years',
  'pipeline.filters.matches': 'matches',

  // SFR Analytics Extended Filters
  'pipeline.filters.msas': 'Metro Areas (MSA)',
  'pipeline.filters.transactions': 'Transactions',
  'pipeline.filters.buyerProfile': 'Buyer Profile',
  'pipeline.filters.buyerClassification': 'Buyer Classification',
  'pipeline.filters.purchaseType': 'Purchase Type',
  'pipeline.filters.entityType': 'Entity Type',
  'pipeline.filters.transactionCount': 'Transaction Count',
  'pipeline.filters.saleAmount': 'Sale Amount',
  'pipeline.filters.equityPercentage': 'Equity Percentage',
  'pipeline.filters.avgPropertyValue': 'Avg Property Value',
  'pipeline.filters.growthPercentage': 'Portfolio Growth',
  'pipeline.filters.loanFinancing': 'Loan & Financing',
  'pipeline.filters.loanTypes': 'Loan Types',
  'pipeline.filters.lenderName': 'Lender Name',
  'pipeline.filters.lenderChipLabel': 'Lender: {{name}}',
  'pipeline.filters.lenderNamePlaceholder': 'e.g. Bank of America, Kiavi...',
  'pipeline.filters.loanType.bridge': 'Bridge',
  'pipeline.filters.loanType.dscr': 'DSCR',
  'pipeline.filters.loanType.fixAndFlip': 'Fix & Flip',
  'pipeline.filters.loanType.construction': 'Construction',
  'pipeline.filters.loanType.hardMoney': 'Hard Money',
  'pipeline.filters.loanType.conventional': 'Conventional',
  'pipeline.filters.dateRange': 'Recording Date Range',
  'pipeline.filters.fromDate': 'From',
  'pipeline.filters.toDate': 'To',

  // Prospect Search Results
  'pipeline.prospects.title': 'Search Results',
  'pipeline.prospects.nextPage': 'Next Page',
  'pipeline.prospects.previousPage': 'Previous Page',
  'pipeline.prospects.summarize': 'Summarize',
  'pipeline.prospects.addToShortlist': 'Add to Shortlist',
  'pipeline.prospects.addToSequence': 'Add to Sequence',
  'pipeline.prospects.manageContact': 'Manage Contact',
  'pipeline.prospects.lastContacted': 'Last contacted on {{date}}',
  'pipeline.prospects.addTags': 'Add Tags',
  'pipeline.prospects.aiSpotlight': 'AI-Powered Spotlight',
  'pipeline.prospects.highlights': 'Highlights',
  'pipeline.prospects.portfolio': 'Portfolio',
  'pipeline.prospects.properties': 'Properties',
  'pipeline.prospects.totalValue': 'Total Value',
  'pipeline.prospects.investorScore': 'Investor Score',
  'pipeline.prospects.existsAsLead': 'Already in CRM',

  // Prospect Profile Tabs
  'pipeline.profile.overview': 'Overview',
  'pipeline.profile.activity': 'Activity',
  'pipeline.profile.sequences': 'Sequences',
  'pipeline.profile.properties': 'Properties',
  'pipeline.profile.notes': 'Notes',

  // Highlight Types
  'pipeline.highlight.likelyToSwitch': 'Likely to Switch',
  'pipeline.highlight.highValueInvestor': 'High Value Investor',
  'pipeline.highlight.portfolioOwner': 'Portfolio Owner',
  'pipeline.highlight.repeatBuyer': 'Repeat Buyer',
  'pipeline.highlight.recentActivity': 'Recent Activity',
  'pipeline.highlight.marketTiming': 'Market Timing',
  'pipeline.highlight.geographicFocus': 'Geographic Focus',

  // Activity Timeline
  'pipeline.activity.title': 'Activity',
  'pipeline.activity.addActivity': 'Add Activity',
  'pipeline.activity.viewed': 'Viewed',
  'pipeline.activity.statusChanged': 'Status changed',
  'pipeline.activity.qualificationChanged': 'Qualification changed',
  'pipeline.activity.emailSent': 'Email sent',
  'pipeline.activity.emailOpened': 'Email opened',
  'pipeline.activity.emailClicked': 'Email clicked',
  'pipeline.activity.emailReplied': 'Email replied',
  'pipeline.activity.sequenceEnrolled': 'Enrolled in sequence',
  'pipeline.activity.sequenceCompleted': 'Completed sequence',
  'pipeline.activity.sequenceExited': 'Exited sequence',
  'pipeline.activity.enriched': 'Data enriched',
  'pipeline.activity.noteAdded': 'Note added',
  'pipeline.activity.callMade': 'Call made',
  'pipeline.activity.meetingScheduled': 'Meeting scheduled',
  'pipeline.activity.shortlisted': 'Added to shortlist',
  'pipeline.activity.tagged': 'Tagged',
  'pipeline.activity.assigned': 'Assigned',
  'pipeline.activity.imported': 'Imported',
  'pipeline.activity.noActivities': 'No activities yet',
  'pipeline.activity.showAll': 'Show all activities',

  // Sequences
  'pipeline.sequences.title': 'Email Sequences',
  'pipeline.sequences.newSequence': 'New Sequence',
  'pipeline.sequences.createSequence': 'Create Sequence',
  'pipeline.sequences.editSequence': 'Edit Sequence',
  'pipeline.sequences.deleteSequence': 'Delete Sequence',
  'pipeline.sequences.deleteConfirm': 'Are you sure you want to delete this sequence? This will unenroll all leads.',
  'pipeline.sequences.noSequences': 'No sequences yet',
  'pipeline.sequences.noSequencesDescription': 'Create your first email sequence to automate lead nurturing.',
  'pipeline.sequences.status.draft': 'Draft',
  'pipeline.sequences.status.active': 'Active',
  'pipeline.sequences.status.paused': 'Paused',
  'pipeline.sequences.status.archived': 'Archived',
  'pipeline.sequences.activate': 'Activate',
  'pipeline.sequences.pause': 'Pause',
  'pipeline.sequences.archive': 'Archive',
  'pipeline.sequences.duplicate': 'Duplicate',
  'pipeline.sequences.enrolled': '{{count}} enrolled',
  'pipeline.sequences.completed': '{{count}} completed',
  'pipeline.sequences.steps': '{{count}} steps',
  'pipeline.sequences.openRate': 'Open Rate',
  'pipeline.sequences.clickRate': 'Click Rate',
  'pipeline.sequences.replyRate': 'Reply Rate',

  // Sequence Builder
  'pipeline.sequenceBuilder.title': 'Sequence Builder',
  'pipeline.sequenceBuilder.settings': 'Sequence Settings',
  'pipeline.sequenceBuilder.steps': 'Steps',
  'pipeline.sequenceBuilder.enrollmentSettings': 'Enrollment Settings',
  'pipeline.sequenceBuilder.exitConditions': 'Exit Conditions',
  'pipeline.sequenceBuilder.name': 'Sequence Name',
  'pipeline.sequenceBuilder.namePlaceholder': 'e.g., New Investor Welcome Series',
  'pipeline.sequenceBuilder.description': 'Description',
  'pipeline.sequenceBuilder.sendingEmail': 'From Email',
  'pipeline.sequenceBuilder.sendingName': 'From Name',
  'pipeline.sequenceBuilder.replyTo': 'Reply-To Email',
  'pipeline.sequenceBuilder.timezone': 'Timezone',
  'pipeline.sequenceBuilder.addStep': 'Add Step',
  'pipeline.sequenceBuilder.stepNumber': 'Step {{number}}',
  'pipeline.sequenceBuilder.delay': 'Delay',
  'pipeline.sequenceBuilder.delayDays': '{{days}} days',
  'pipeline.sequenceBuilder.delayHours': '{{hours}} hours',
  'pipeline.sequenceBuilder.delayDescription': 'Wait {{days}} days, {{hours}} hours after previous step',
  'pipeline.sequenceBuilder.sendTime': 'Send Time',
  'pipeline.sequenceBuilder.sendTimeImmediate': 'Immediately when ready',
  'pipeline.sequenceBuilder.sendTimeSpecific': 'At specific time',
  'pipeline.sequenceBuilder.sendTimeBusinessHours': 'During business hours',
  'pipeline.sequenceBuilder.sendDays': 'Send on days',
  'pipeline.sequenceBuilder.subject': 'Subject Line',
  'pipeline.sequenceBuilder.emailContent': 'Email Content',
  'pipeline.sequenceBuilder.preview': 'Preview',
  'pipeline.sequenceBuilder.autoEnroll': 'Auto-enroll matching leads',
  'pipeline.sequenceBuilder.enrollmentCriteria': 'Enrollment criteria',
  'pipeline.sequenceBuilder.maxEnrollments': 'Max active enrollments',
  'pipeline.sequenceBuilder.exitOnReply': 'Exit when lead replies',
  'pipeline.sequenceBuilder.exitOnClick': 'Exit when lead clicks a link',
  'pipeline.sequenceBuilder.exitOnBounce': 'Exit on email bounce',
  'pipeline.sequenceBuilder.removeStep': 'Remove Step',
  'pipeline.sequenceBuilder.moveUp': 'Move Up',
  'pipeline.sequenceBuilder.moveDown': 'Move Down',

  // Sequence Enrollments
  'pipeline.enrollments.title': 'Enrollments',
  'pipeline.enrollments.enroll': 'Enroll Leads',
  'pipeline.enrollments.unenroll': 'Unenroll',
  'pipeline.enrollments.pause': 'Pause',
  'pipeline.enrollments.resume': 'Resume',
  'pipeline.enrollments.status.active': 'Active',
  'pipeline.enrollments.status.paused': 'Paused',
  'pipeline.enrollments.status.completed': 'Completed',
  'pipeline.enrollments.status.exited': 'Exited',
  'pipeline.enrollments.status.failed': 'Failed',
  'pipeline.enrollments.currentStep': 'Step {{step}}',
  'pipeline.enrollments.nextStepAt': 'Next step: {{date}}',
  'pipeline.enrollments.exitReason': 'Exit reason: {{reason}}',
  'pipeline.enrollments.noEnrollments': 'No leads enrolled in this sequence',
  'pipeline.enrollments.enrollSuccess': '{{count}} leads enrolled successfully',
  'pipeline.enrollments.enrollSkipped': '{{count}} leads skipped (already enrolled or opted out)',

  // Saved Searches
  'pipeline.savedSearches.title': 'Saved Searches',
  'pipeline.savedSearches.noSearches': 'No saved searches yet',
  'pipeline.savedSearches.noSearchesDescription': 'Save your search criteria to quickly find prospects later.',
  'pipeline.savedSearches.lastRun': 'Last Run',
  'pipeline.savedSearches.resultCount': '{{count}} results',
  'pipeline.savedSearches.runSearch': 'Run Search',
  'pipeline.savedSearches.editSearch': 'Edit',
  'pipeline.savedSearches.deleteSearch': 'Delete',
  'pipeline.savedSearches.scheduled': 'Scheduled',
  'pipeline.savedSearches.autoImport': 'Auto-import enabled',

  // Import Jobs
  'pipeline.import.title': 'Import Jobs',
  'pipeline.import.startImport': 'Start Import',
  'pipeline.import.importing': 'Importing prospects...',
  'pipeline.import.status.pending': 'Pending',
  'pipeline.import.status.searching': 'Searching',
  'pipeline.import.status.importing': 'Importing',
  'pipeline.import.status.completed': 'Completed',
  'pipeline.import.status.failed': 'Failed',
  'pipeline.import.status.cancelled': 'Cancelled',
  'pipeline.import.progress': '{{imported}} of {{total}} imported',
  'pipeline.import.duplicates': '{{count}} duplicates skipped',
  'pipeline.import.errors': '{{count}} errors',
  'pipeline.import.cancelImport': 'Cancel',
  'pipeline.import.assignTo': 'Assign imported leads to',
  'pipeline.import.applyTags': 'Apply tags',
  'pipeline.import.enrollInSequence': 'Enroll in sequence',

  // Shortlists
  'pipeline.shortlists.title': 'Shortlists',
  'pipeline.shortlists.newShortlist': 'New Shortlist',
  'pipeline.shortlists.noShortlists': 'No shortlists yet',
  'pipeline.shortlists.leadCount': '{{count}} leads',
  'pipeline.shortlists.addToShortlist': 'Add to Shortlist',
  'pipeline.shortlists.removeFromShortlist': 'Remove',
  'pipeline.shortlists.createShortlist': 'Create Shortlist',
  'pipeline.shortlists.shortlistName': 'Shortlist Name',
  'pipeline.shortlists.shortlistDescription': 'Description (optional)',

  // ============================================================================
  // Issue #360: Additional Pipeline Translations (Juicebox-Style)
  // ============================================================================

  // Prospect Search Page
  'pipeline.prospectSearch.title': 'Prospect Discovery',
  'pipeline.prospectSearch.subtitle': 'Find and connect with real estate investors using AI-powered search',
  'pipeline.prospectSearch.selected': '{{count}} selected',
  'pipeline.prospectSearch.addToSequence': 'Add to Sequence',
  'pipeline.prospectSearch.convertToLeads': 'Convert to Leads',
  'pipeline.prospectSearch.export': 'Export',
  'pipeline.prospectSearch.exportSuccess': 'Prospects exported to CSV',
  'pipeline.prospect.addToLeads': 'Add to Leads',
  'pipeline.prospect.addToLeadsSuccess': 'Prospect added to leads successfully',
  'pipeline.prospect.existsAsLead': 'Already exists as a lead',
  'pipeline.prospectSearch.resultsFound': '{{count}} prospects found',
  'pipeline.prospectSearch.filters': 'Filters',
  'pipeline.prospectSearch.saveSearch': 'Save Search',
  'pipeline.prospectSearch.noResults': 'No prospects match your criteria',
  'pipeline.prospectSearch.startSearch': 'Start your search',
  'pipeline.prospectSearch.locationRequired': 'Location filter required',
  'pipeline.prospectSearch.noResultsHint': 'Try adjusting your filters or searching for different criteria',
  'pipeline.prospectSearch.startSearchHint': 'Enter a natural language query or use filters to find prospects',
  'pipeline.prospectSearch.locationRequiredHint':
    'Please add at least one location filter (state, city, zip code, or MSA) to search for investors',
  'pipeline.prospectSearch.addLocationFilter': 'Add Location Filter',
  'pipeline.prospectSearch.searchError': 'Search Failed',
  'pipeline.prospectSearch.searchErrorDefault': 'Unable to complete your search. Please try again.',
  'pipeline.prospectSearch.errorLocationRequired':
    'Please specify a location (state, city, or zip code) to search for investors.',
  'pipeline.prospectSearch.errorAuthFailed':
    'Unable to connect to the data provider. Please check your integration settings.',
  'pipeline.prospectSearch.errorRateLimit': 'Too many requests. Please wait a moment and try again.',
  'pipeline.prospectSearch.errorServiceUnavailable':
    'The search service is temporarily unavailable. Please try again later.',
  'pipeline.prospectSearch.errorNotConfiguredTitle': 'SFR Analytics Not Configured',
  'pipeline.prospectSearch.errorNotConfigured':
    'Investor Discovery requires an SFR Analytics connector. Set it up in Settings to start finding investors.',
  'pipeline.prospectSearch.goToIntegrations': 'Set Up Integration',
  'pipeline.prospectSearch.savedSearches': 'Saved',
  'pipeline.prospectSearch.saveCurrentSearch': 'Save current search',
  'pipeline.prospectSearch.viewSavedSearches': 'View saved searches',

  // Save Search Dialog
  'pipeline.savedSearch.presets': 'Quick Filters',
  'pipeline.savedSearch.saveTitle': 'Save Search',
  'pipeline.savedSearch.queryLabel': 'Search Query',
  'pipeline.savedSearch.filtersLabel': 'Active Filters',
  'pipeline.savedSearch.noFilters': 'No filters applied',
  'pipeline.savedSearch.nameLabel': 'Search Name',
  'pipeline.savedSearch.namePlaceholder': 'e.g., High-value California investors',
  'pipeline.savedSearch.descriptionLabel': 'Description (optional)',
  'pipeline.savedSearch.descriptionPlaceholder': 'Describe what this search is for...',

  // Sequences Page (additional)
  'pipeline.sequences.subtitle': 'Create and manage automated email sequences for prospect outreach',
  'pipeline.sequences.createNew': 'New Sequence',
  'pipeline.sequences.searchPlaceholder': 'Search sequences...',
  'pipeline.sequences.noSequencesHint': 'Create your first sequence to automate prospect outreach',
  'pipeline.sequences.createFirst': 'Create Your First Sequence',
  'pipeline.sequences.name': 'Name',
  'pipeline.sequences.status': 'Status',
  'pipeline.sequences.stepsHeader': 'Steps',
  'pipeline.sequences.enrollments': 'Enrollments',
  'pipeline.sequences.sent': 'Sent',
  'pipeline.sequences.opened': 'Opened',
  'pipeline.sequences.replied': 'Replied',

  // Sequence Builder (additional)
  'pipeline.sequence.status.draft': 'Draft',
  'pipeline.sequence.status.active': 'Active',
  'pipeline.sequence.status.paused': 'Paused',
  'pipeline.sequence.status.archived': 'Archived',
  'pipeline.sequence.step': 'Step {{current}} of {{total}}',
  'pipeline.sequence.emailStep': 'Email',
  'pipeline.sequence.delayStep': 'Delay',
  'pipeline.sequence.noSubject': '(No subject)',
  'pipeline.sequence.noContent': '(No content)',
  'pipeline.sequence.waitBefore': 'Wait {{value}} {{unit}} before sending',
  'pipeline.sequence.sendImmediately': 'Send immediately',
  'pipeline.sequence.addStep': 'Add Step',
  'pipeline.sequence.noSteps': 'No steps added yet',
  'pipeline.sequence.addFirstStep': 'Add Your First Step',
  'pipeline.sequence.editStep': 'Edit Step {{number}}',
  'pipeline.sequence.delay': 'Wait Time',
  'pipeline.sequence.sendTime': 'Send Time',
  'pipeline.sequence.sendImmediate': 'Send immediately when ready',
  'pipeline.sequence.sendBusinessHours': 'During business hours (9am-5pm)',
  'pipeline.sequence.sendScheduled': 'At specific time',
  'pipeline.sequence.subject': 'Subject Line',
  'pipeline.sequence.subjectPlaceholder': 'Enter email subject...',
  'pipeline.sequence.body': 'Email Body',
  'pipeline.sequence.bodyPlaceholder': 'Write your email content here...',
  'pipeline.sequence.bodyHelper': 'Use merge fields like {{first_name}} to personalize',
  'pipeline.sequence.mergeFieldsTitle': 'Click to insert merge fields:',
  'pipeline.sequence.namePlaceholder': 'Enter sequence name...',
  'pipeline.sequence.description': 'Description',
  'pipeline.sequence.descriptionPlaceholder': 'Describe what this sequence does...',
  'pipeline.sequence.tabs.builder': 'Builder',
  'pipeline.sequence.tabs.analytics': 'Analytics',
  'pipeline.sequence.tabs.enrollments': 'Enrollments',
  'pipeline.sequence.tabs.settings': 'Settings',
  'pipeline.sequence.settings.exitConditions': 'Exit Conditions',
  'pipeline.sequence.settings.exitConditionsHint': 'Configure when leads should automatically exit the sequence',
  'pipeline.sequence.stepsCount': '{{count}} steps',
  'pipeline.sequence.quickStats': 'Quick Stats',
  'pipeline.sequence.openRate': 'Open Rate',
  'pipeline.sequence.clickRate': 'Click Rate',
  'pipeline.sequence.replyRate': 'Reply Rate',
  'pipeline.sequence.analyticsComingSoon': 'Analytics Coming Soon',
  'pipeline.sequence.analyticsComingSoonHint':
    'Detailed analytics will be available once the sequence has been running',
  'pipeline.sequence.noEnrollments': 'No leads enrolled yet',
  'pipeline.sequence.noEnrollmentsHint': 'Start enrolling leads to see their progress',

  // Email Provider (Issue #372 - HubSpot Three-Tier Model)
  'pipeline.sequence.emailProvider': 'Email Provider',
  'pipeline.sequence.emailProviderDescription':
    'Choose how emails are sent. Resend is best for marketing emails, Gmail for personal outreach.',
  'pipeline.sequence.providerResend': 'Resend (Marketing)',
  'pipeline.sequence.providerResendTag': 'Recommended',
  'pipeline.sequence.providerResendDescription':
    'Send emails via Resend for bulk marketing campaigns with tracking and compliance features.',
  'pipeline.sequence.providerGmail': 'Gmail (Personal Inbox)',
  'pipeline.sequence.providerGmailTag': 'Sales',
  'pipeline.sequence.providerGmailDescription':
    'Send emails from your personal Gmail account for 1:1 sales outreach with reply detection.',
  'pipeline.sequence.featureTracking': '✓ Open/click tracking',
  'pipeline.sequence.featureUnsubscribe': '✓ Unsubscribe handling',
  'pipeline.sequence.featureBulkSend': '✓ Bulk sending',
  'pipeline.sequence.featurePersonal': '✓ Personal inbox',
  'pipeline.sequence.featureReplyDetection': '✓ Reply detection',
  'pipeline.sequence.featureThreading': '✓ Email threading',
  'pipeline.sequence.gmailConnected': 'Connected as {{email}}',
  'pipeline.sequence.gmailNotConnected': 'Connect your Gmail account to send emails from your personal inbox.',
  'pipeline.sequence.gmailConnect': 'Connect Gmail',
  'pipeline.sequence.gmailConnecting': 'Connecting...',
  'pipeline.sequence.gmailThreadMode': 'Enable email threading',
  'pipeline.sequence.gmailThreadModeDescription':
    'Thread follow-up emails together so replies appear in the same conversation.',

  // Exit Conditions Settings (Issue #372)
  'pipeline.sequence.settings.exitOnReply': 'Exit on reply',
  'pipeline.sequence.settings.exitOnReplyDescription':
    'Automatically remove leads who reply to any email in the sequence.',
  'pipeline.sequence.settings.exitOnClick': 'Exit on link click',
  'pipeline.sequence.settings.exitOnClickDescription':
    'Automatically remove leads who click any link in the sequence emails.',
  'pipeline.sequence.settings.exitOnBounce': 'Exit on bounce',
  'pipeline.sequence.settings.exitOnBounceDescription':
    'Automatically remove leads whose emails bounce (invalid address).',
  'pipeline.sequence.settings.senderInfo': 'Sender Information',
  'pipeline.sequence.settings.sendingEmail': 'From Email',
  'pipeline.sequence.settings.sendingName': 'From Name',
  'pipeline.sequence.settings.gmailSendAsHint': 'Gmail sequences send from your connected Gmail address.',

  // Saved Searches Page (additional)
  'pipeline.savedSearches.subtitle': 'Quickly access your saved search criteria',
  'pipeline.savedSearches.createNew': 'New Search',
  'pipeline.savedSearches.searchPlaceholder': 'Search saved searches...',
  'pipeline.savedSearches.noSearchesHint': 'Save a search to quickly find prospects later',
  'pipeline.savedSearches.createFirst': 'Create Your First Saved Search',
  'pipeline.savedSearches.name': 'Name',
  'pipeline.savedSearches.filters': 'Filters',
  'pipeline.savedSearches.lastResults': 'Results',
  'pipeline.savedSearches.autoRun': 'Auto-Run',
  'pipeline.savedSearches.neverRun': 'Never run',
  'pipeline.savedSearches.runNow': 'Run Now',
  'pipeline.savedSearches.confirmDelete': 'Are you sure you want to delete this saved search?',
  'pipeline.sequences.confirmDelete': 'Are you sure you want to delete this sequence?',

  // Lead Highlights
  'pipeline.highlights.aiGenerated': 'AI-generated insights',
  'pipeline.highlights.spotlight': 'Spotlight',
  'pipeline.highlights.confidence': '{{value}}% confidence',
  'pipeline.highlights.more': '+{{count}} more',

  // Lead Activity
  'pipeline.activity.noActivity': 'No activity recorded yet',

  // Lead Detail
  'pipeline.lead.loading': 'Loading lead...',
  'pipeline.lead.score': 'Score: {{score}}',
  'pipeline.lead.properties': '{{count}} properties',
  'pipeline.lead.viewFull': 'View full profile',
  'pipeline.lead.tabs.details': 'Details',
  'pipeline.lead.tabs.activity': 'Activity',
  'pipeline.lead.tabs.sequences': 'Sequences',
  'pipeline.lead.tabs.properties': 'Properties',
  'pipeline.lead.tabs.notes': 'Notes',
  'pipeline.lead.tabs.contacts': 'Contacts',
  'pipeline.lead.tabs.enrichment': 'Enrichment',
  'pipeline.lead.contacts.title': 'Lead Contacts',
  'pipeline.lead.contacts.save': 'Save Contacts',
  'pipeline.lead.contacts.saving': 'Saving...',
  'pipeline.lead.contacts.saved': 'Contacts saved successfully',
  'pipeline.lead.contacts.saveFailed': 'Failed to save contacts',
  'pipeline.lead.contacts.cancel': 'Cancel',
  'pipeline.lead.contacts.edit': 'Edit Contacts',
  'pipeline.lead.contacts.noContacts': 'No contact information available',
  'pipeline.lead.contactInfo': 'Contact Information',
  'pipeline.lead.investorProfile': 'Investor Profile',
  'pipeline.lead.investorType': 'Investor Type',
  'pipeline.lead.scoreLabel': 'Score',
  'pipeline.lead.totalProperties': 'Total Properties',
  'pipeline.lead.portfolioValue': 'Portfolio Value',
  'pipeline.lead.avgPurchasePrice': 'Avg. Purchase Price',
  'pipeline.lead.geographicFocus': 'Geographic Focus',
  'pipeline.lead.yoyGrowth': 'YoY Growth',
  'pipeline.lead.totalLoans': 'Total Loans',
  'pipeline.lead.lastLender': 'Last Lender',
  'pipeline.lead.lendersUsed': 'Lenders Used',
  'pipeline.lead.secondaryPhone': 'Secondary Phone',
  'pipeline.lead.secondaryEmail': 'Secondary Email',
  'pipeline.lead.thirdPhone': 'Phone 3',
  'pipeline.lead.thirdEmail': 'Email 3',
  'pipeline.lead.verifiedContact': 'Verified',
  'pipeline.prospectSearch.contactStats.withPhone': '{{count}} with phone',
  'pipeline.prospectSearch.contactStats.withEmail': '{{count}} with email',
  'pipeline.lead.recentProperties': 'Recent Properties',
  'pipeline.lead.source': 'Source',
  'pipeline.lead.sourceRadar': 'SFR Analytics (Radar)',
  'pipeline.lead.totalActivities': 'Total Activities',
  'pipeline.lead.activeSequences': 'Active Sequences',
  'pipeline.lead.noSequences': 'Not enrolled in any sequences',
  'pipeline.lead.enrollInSequence': 'Enroll in Sequence',
  'pipeline.lead.noProperties': 'No properties found',
  'pipeline.lead.purchased': 'Purchased {{date}}',
  'pipeline.lead.noNotes': 'No notes added yet',
  'pipeline.lead.addToSequence': 'Add to Sequence',
  'pipeline.lead.sendEmail': 'Send Email',
  'pipeline.lead.status.new': 'New',
  'pipeline.lead.status.contacted': 'Contacted',
  'pipeline.lead.status.qualified': 'Qualified',
  'pipeline.lead.status.nurturing': 'Nurturing',
  'pipeline.lead.status.converted': 'Converted',
  'pipeline.lead.status.lost': 'Lost',
  'pipeline.lead.status.archived': 'Archived',
  'pipeline.lead.lastAcquisition': 'Last Acquisition',
  'pipeline.lead.dataQuality': 'Data Quality',
  'pipeline.lead.purchasePrice': 'Purchase Price',
  'pipeline.lead.currentValue': 'Current Value',
  'pipeline.lead.showingProperties': 'Showing {{shown}} of {{total}} properties',

  // Prospect Detail Page
  'pipeline.prospectDetail.loadError': 'Failed to load prospect details',

  // Prospect Card
  'pipeline.prospect.properties': 'properties',
  'pipeline.prospect.portfolio': 'Portfolio',
  'pipeline.prospect.score': 'Score',

  // Hot Lead Indicators (Issue #542)
  'pipeline.hotLeads.activeLabel': 'Active {{days}}d ago',
  'pipeline.hotLeads.activeDescription': 'Last purchase was {{days}} days ago',
  'pipeline.hotLeads.growthLabel': '+{{rate}}% growth',
  'pipeline.hotLeads.growthDescription': 'Portfolio grew {{rate}}% year-over-year',
  // biome-ignore lint/suspicious/noTemplateCurlyInString: i18n interpolation with currency prefix
  'pipeline.hotLeads.highValueLabel': '${{value}}M portfolio',
  // biome-ignore lint/suspicious/noTemplateCurlyInString: i18n interpolation with currency prefix
  'pipeline.hotLeads.highValueDescription': 'Total portfolio value: ${{formattedValue}}',
  'pipeline.hotLeads.portfolioOwnerLabel': '{{count}} properties',
  'pipeline.hotLeads.portfolioOwnerDescription': 'Owns {{count}} properties',

  // Quick Filter Chips (Issue #542)
  'pipeline.filters.active30d': 'Active 30d',
  'pipeline.filters.growing': 'Growing',
  'pipeline.filters.highValue10m': '$10M+',
  'pipeline.filters.corporate': 'Corporate',

  // Portfolio Filter Hints (Issue #542)
  'pipeline.filters.propertiesOwnedHint': 'Filter for repeat buyers with verified data',
  'pipeline.filters.minPropertiesHelp': 'Min 2 ensures quality data',

  // Search Mode Tabs (Issue #542 - SFR Analytics APIs)
  'pipeline.prospectSearch.tabs.discovery': 'Buyers Activity',
  'pipeline.prospectSearch.tabs.topBuyers': 'Top Buyers',
  'pipeline.prospectSearch.tabs.profiles': 'Investor Profiles',
  'pipeline.prospectSearch.tabs.discoveryDesc': 'Search individual buyer transactions',
  'pipeline.prospectSearch.tabs.topBuyersDesc': 'Ranked list of most active investors (50 max)',
  'pipeline.prospectSearch.tabs.profilesDesc': 'Portfolio aggregates for major investors',

  // Data Quality Indicator (Issue #542)
  'pipeline.dataQuality.high': 'High Quality',
  'pipeline.dataQuality.medium': 'Medium Quality',
  'pipeline.dataQuality.low': 'Low Quality',
  'pipeline.dataQuality.highDesc': 'Complete buyer profile with verified data',
  'pipeline.dataQuality.mediumDesc': 'Partial buyer data available',
  'pipeline.dataQuality.lowDesc': 'Limited data - consider additional research',
  'pipeline.dataQuality.tooltip': 'Data quality based on buyer name availability and property count',

  // Investor Profile Card (Issue #542)
  'pipeline.investor.properties': 'Properties',
  'pipeline.investor.acquisitions': 'Acquisitions',
  'pipeline.investor.portfolio': 'Portfolio',
  'pipeline.investor.portfolioValue': 'Portfolio Value',
  'pipeline.investor.avg': 'Avg',
  'pipeline.investor.avgPurchase': 'Avg. Purchase',
  'pipeline.investor.geographicFocus': 'Geographic Focus',
  'pipeline.investor.propertyTypes': 'Property Types',
  'pipeline.investor.growthMetrics': 'Growth Metrics',
  'pipeline.investor.yoyProperties': 'YoY properties',
  'pipeline.investor.yoyValue': 'YoY value',
  'pipeline.investor.equity': 'Equity',
  'pipeline.investor.lastAcquisition': 'Last Acquisition',
  'pipeline.investor.hasContact': 'Has Contact',
  'pipeline.investor.verifiedPhone': 'Verified Phone',
  'pipeline.investor.loans': 'Loans',
  'pipeline.dataQuality.label': 'Data Quality',

  // Top Buyers specific
  'pipeline.topBuyers.acquisitions': '{{count}} acquisitions',
  'pipeline.topBuyers.hasContactInfo': 'Contact info available',
  'pipeline.topBuyers.noContactInfo': 'No contact info',
  'pipeline.topBuyers.viewProfile': 'View Profile',

  // Top Buyers Mode UI (Issue #542)
  'pipeline.prospectSearch.topBuyers.title': 'Find Top Buyers in Your Market',
  'pipeline.prospectSearch.topBuyers.description':
    'Select a location to see the most active real estate investors ranked by acquisition volume',
  'pipeline.filters.selectLocation': 'Select Location',
  'pipeline.filters.citiesPlaceholder': 'Enter city names...',
  'pipeline.filters.companyNamePlaceholder': 'Enter company name...',
  'pipeline.filters.countiesPlaceholder': 'Enter county names...',
  'pipeline.filters.growthHint': 'Year-over-year portfolio growth rate',
  'pipeline.filters.keywordsHelper': 'Separate multiple keywords with commas',
  'pipeline.filters.keywordsPlaceholder': 'Enter keywords...',
  'pipeline.filters.msasHelper': 'Metropolitan Statistical Areas for targeted searches',
  'pipeline.filters.msasPlaceholder': 'Enter MSA names...',
  'pipeline.savedSearch.saveSubtitle': 'Save your current search criteria for quick access later',

  // Accessibility
  'a11y.prospectDetails': 'Prospect details panel',

  // Common (additional)
  'common.soon': 'Soon',
  'common.minutes': 'minutes',
  'common.hours': 'hours',
  'common.days': 'days',
  'common.months': 'months',
  'common.disabled': 'Disabled',
  'common.current': 'Current',
  'common.id': 'ID',
  'common.startTyping': 'Start typing…',
  'common.brandCaption.poweredBy': 'Powered by {{brand}}',
  'common.brandCaption.withEnv': '{{caption}} · {{env}}',
  'cases.lookup.searchBorrowers': 'Search borrowers by name or email…',
  'cases.lookup.searchTeamMembers': 'Search team members…',
  'cases.lookup.unassign': 'Unassign',
  'form.company': 'Company',
  'form.location': 'Location',
  'form.address': 'Address',

  // ============================================================================
  // PAGE HEADERS - Titles and descriptions for page headers in top navbar
  // ============================================================================

  // Pipeline Section
  'pageHeader.prospectSearch.title': 'Prospect Discovery',
  'pageHeader.prospectSearch.description': 'Find and connect with real estate investors using AI-powered search',
  'pageHeader.savedSearches.title': 'Saved Searches',
  'pageHeader.savedSearches.description': 'Manage and run your saved prospect searches',
  'pageHeader.sequences.title': 'Sequences',
  'pageHeader.sequences.description': 'Manage email sequences and drip campaigns',
  'pageHeader.sequenceBuilder.title': 'Sequence Builder',
  'pageHeader.sequenceBuilder.description': 'Create and edit email sequences',
  'pageHeader.newSequence.title': 'New Sequence',
  'pageHeader.leads.title': 'Leads',
  'pageHeader.leads.description': 'Manage and qualify your sales leads',
  'pageHeader.termSheets.title': 'Term Sheets',
  'pageHeader.termSheets.description': 'View and manage loan term sheets',
  'pageHeader.termSheetCreate.title': 'New Term Sheet',
  'pageHeader.termSheetCreate.description': 'Start from a template or build from scratch',

  // Term Sheets
  'termsheets.unknownBorrower': 'Unknown Borrower',
  'pageHeader.cases.title': 'Loans',
  'pageHeader.cases.description': 'Track and manage loans',
  'pageHeader.caseDetail.title': 'Loan Details',
  'pageHeader.caseDetail.description': 'View and manage loan information',
  'pageHeader.caseCreate.title': 'New Loan',
  'pageHeader.caseCreate.description': 'Create a new loan',
  'pageHeader.caseEdit.title': 'Edit Loan',
  'pageHeader.caseEdit.description': 'Modify loan information',

  // Team Section
  'pageHeader.teamMembers.title': 'Team Members',
  'pageHeader.teamMembers.description': 'Manage your team members',
  'pageHeader.teams.title': 'Teams',
  'pageHeader.teams.description': 'Organize your team into groups',
  'pageHeader.customRoles.title': 'Custom Roles',
  'pageHeader.customRoles.description': 'Manage custom roles and permissions',
  'pageHeader.myDirectReports.title': 'My Direct Reports',
  'pageHeader.myDirectReports.description': 'View and manage your team members',
  'pageHeader.teamAnalytics.title': 'Team Analytics',
  'pageHeader.teamAnalytics.description': 'Performance metrics and insights',
  'pageHeader.teamDetail.title': 'Team Details',
  'pageHeader.teamDetail.description': 'View and manage team information',
  'pageHeader.teamMember.title': 'Team Member',
  'pageHeader.teamMember.description': 'View member details and activity',
  'pageHeader.teamOnboarding.title': 'Team Onboarding',
  'pageHeader.teamOnboarding.description': 'Track onboarding progress',
  'pageHeader.orgChart.title': 'Organization Chart',
  'pageHeader.orgChart.description': 'View your organization structure',

  // Campaigns Section
  'pageHeader.campaigns.title': 'Campaigns',
  'pageHeader.campaigns.description': 'Manage email and voice campaigns',
  'pageHeader.emailCampaignCreate.title': 'Create Email Campaign',
  'pageHeader.emailCampaignCreate.description': 'Set up a new email campaign',
  'pageHeader.emailCampaignDetail.title': 'Email Campaign',
  'pageHeader.emailCampaignDetail.description': 'View and manage email campaign',
  'pageHeader.voiceCampaignCreate.title': 'Create Voice Campaign',
  'pageHeader.voiceCampaignCreate.description': 'Set up a new voice campaign',
  'pageHeader.voiceCampaignDetail.title': 'Voice Campaign',
  'pageHeader.voiceCampaignDetail.description': 'View and manage voice campaign',

  // Pricing Section
  'pageHeader.pricingMatrices.title': 'Pricing Matrices',
  'pageHeader.pricingMatrices.description': 'Configure pricing matrices',
  'pageHeader.quotes.title': 'Quotes',
  'pageHeader.quotes.description': 'Manage loan quotes',
  'pageHeader.pricingRules.title': 'Pricing Rules',
  'pageHeader.pricingRules.description': 'Configure pricing rules and conditions',
  'pageHeader.pricingRuleDetail.title': 'Pricing Rule',
  'pageHeader.pricingRuleDetail.description': 'Edit pricing rule configuration',

  // Relationships Section
  'pageHeader.borrowers.title': 'Borrowers',
  'pageHeader.borrowers.description': 'Manage your borrowers and customers',
  'pageHeader.borrowerDetail.title': 'Borrower Details',
  'pageHeader.borrowerDetail.description': 'View borrower information and history',

  // Borrower Form (drawer pattern - Issue #13)
  'borrower.name': 'Full Name',
  'borrower.email': 'Email Address',
  'borrower.phone': 'Phone Number',
  'borrower.status': 'Status',
  'borrower.statusHint': "Change the borrower's account status",
  'borrower.createTitle': 'Add Borrower',
  'borrower.editTitle': 'Edit Borrower',
  'borrower.createSubtitle': 'Create a new borrower profile',
  'borrower.editSubtitle': 'Update borrower information',
  'borrower.createAction': 'Add Borrower',
  'borrower.contactInfo': 'Contact Information',
  'borrower.memberSince': 'Member since',
  'borrower.lastActive': 'Last active',
  'borrower.statistics': 'Statistics',
  'borrower.totalCases': 'Total Loans',
  'borrower.activeCases': 'Active Loans',
  'borrower.approvedCases': 'Approved',
  'borrower.entities': 'Entities',
  'borrower.noCases': 'No cases found for this borrower',
  // KPI tile subtitle copy (v3 design)
  'borrower.kpi.lifetime': 'lifetime',
  'borrower.kpi.inFlight': 'in flight',
  'borrower.kpi.totalCredit': 'total credit',
  'borrower.kpi.entitiesMixed': '{{llcs}} · {{personal}} personal',
  'borrower.kpi.llcsCountSingular': '1 LLC',
  'borrower.kpi.llcsCount': '{{count}} LLCs',
  'borrower.kpi.personalOnly': '{{count}} personal',
  'borrower.kpi.entitiesFallback': '{{count}} on file',
  // Header subtitle + tenure pill (v3 design #1762 / PR #1757 review)
  'borrower.joinedWithEntity': '{{entity}} · joined {{date}}',
  'borrower.joinedShort': 'Joined {{date}}',
  'borrower.tenureMonths': '{{count}}mo customer',
  'borrower.tenureYearsSingular': '1yr customer',
  'borrower.tenureYears': '{{count}}yr customer',

  // Section Headers for Borrower Form
  'section.contactInfo': 'Contact Information',

  'pageHeader.partners.title': 'Partners',
  'pageHeader.partners.description': 'Manage your business partners',
  'pageHeader.partnerDetail.title': 'Partner Details',
  'pageHeader.partnerDetail.description': 'View partner information',

  // Astor Section
  'pageHeader.aiChat.title': 'Astor',
  'pageHeader.aiChat.description': 'Chat with Astor',
  'pageHeader.chatHome.title': 'Astor',
  'pageHeader.chatHome.description': 'Start a conversation with Astor',
  'pageHeader.agents.title': 'Agents',
  'pageHeader.agents.description': 'Manage your agents',
  'pageHeader.agentCreate.title': 'Create Agent',
  'pageHeader.agentCreate.description': "Configure your agent's behavior and capabilities",
  'pageHeader.agentEdit.title': 'Edit Agent',
  'pageHeader.agentEdit.description': "Modify your agent's configuration",
  'pageHeader.history.title': 'History',
  'pageHeader.history.description': 'View your past conversations',

  // Documents Section
  'pageHeader.documents.title': 'Documents',
  'pageHeader.documents.description': 'Manage your documents and files',
  'pageHeader.documents.signing.title': 'E-Signatures',
  'pageHeader.documents.signing.description': 'Track and manage document signing requests',
  'pageHeader.documentDetail.title': 'Document Details',
  'pageHeader.documentDetail.description': 'View and manage document',

  // Reports Section — historical/audit surfaces moved from legacy /documents.
  'pageHeader.reports.eSignatureHistory.title': 'E-Signature History',
  'pageHeader.reports.eSignatureHistory.description':
    'Completed and voided e-signature envelopes — archived for audit and reference.',
  'pageHeader.reports.retainedCopies.title': 'Retained Copies',
  'pageHeader.reports.retainedCopies.description': 'Signed PDF archive kept for compliance and record-keeping.',
  'pageHeader.reports.documentApprovals.title': 'Document Approvals',
  'pageHeader.reports.documentApprovals.description':
    'Review documents pending approval. Uploads happen contextually inside loans and data rooms.',

  // Inbox Section — active e-signature queue (chase / resend / void).
  'pageHeader.inbox.eSignQueue.title': 'E-Sign Queue',
  'pageHeader.inbox.eSignQueue.description':
    'Active e-signature envelopes awaiting recipient action. Resend reminders or void as needed.',

  // Settings Section
  'pageHeader.settings.title': 'Control Center',
  'pageHeader.settings.description': 'Configure your business, integrations, and security settings',
  'pageHeader.businessProfile.title': 'Business Profile',
  'pageHeader.businessProfile.description': 'Manage your business information',
  'pageHeader.branches.title': 'Branches',
  'pageHeader.branches.description': 'Manage your branch locations',
  'pageHeader.compliance.title': 'Compliance',
  'pageHeader.compliance.description': 'Manage compliance settings',
  'pageHeader.integrations.title': 'Integrations',
  'pageHeader.integrations.description': 'Connect third-party services',
  'pageHeader.webhooks.title': 'Webhooks',
  'pageHeader.webhooks.description': 'Configure webhook endpoints',
  'pageHeader.workflows.title': 'Workflow Rules',
  'pageHeader.workflows.description': 'Configure automation rules',
  'pageHeader.commissions.title': 'Commission Settings',
  'pageHeader.commissions.description': 'Configure commission structures',
  'pageHeader.commissions.earningsDescription': 'Track your commission earnings and payouts',
  'pageHeader.aiAutonomy.title': 'Astor Autonomy',
  'pageHeader.aiAutonomy.description': 'Configure Astor autonomy levels',
  'pageHeader.astorAnalytics.title': 'Astor Analytics',
  'pageHeader.astorAnalytics.description': 'Monitor Astor onboarding performance and engagement',
  'pageHeader.devices.title': 'My Devices',
  'pageHeader.devices.description': 'Manage your registered devices',
  'pageHeader.preferences.title': 'Personal Preferences',
  'pageHeader.preferences.description': 'Manage your account, security, and notification preferences',
  'pageHeader.audit.title': 'Audit Logs',
  'pageHeader.audit.description': 'View system activity and audit trail',
  'pageHeader.caseManagement.title': 'Loan Management',
  'pageHeader.caseManagement.description': 'Configure loan management settings',
  'pageHeader.pipelineConfig.title': 'Pipeline Configuration',
  'pageHeader.pipelineConfig.description': 'Configure your sales pipeline',
  'pageHeader.onboarding.title': 'Onboarding',
  'pageHeader.onboarding.description': 'Configure onboarding workflows',

  // Notifications Section
  'pageHeader.notificationTemplates.title': 'Notification Templates',
  'pageHeader.notificationTemplates.description': 'Manage notification templates',
  'pageHeader.notificationTriggers.title': 'Notification Triggers',
  'pageHeader.notificationTriggers.description': 'Configure when notifications are sent',
  'pageHeader.notificationAnalytics.title': 'Notification Analytics',
  'pageHeader.notificationAnalytics.description': 'View notification performance',
  'pageHeader.notifications.title': 'Notifications',
  'pageHeader.notifications.description': 'View and manage your notifications',

  // Partnerships Section
  'pageHeader.referrals.title': 'Referrals',
  'pageHeader.referrals.description': 'Manage B2B referrals',
  // Issue #368 - Partner Referral Tracking
  'pageHeader.referralTracking.title': 'Referral Tracking',
  'pageHeader.referralTracking.description': 'Track your referral links and commissions',
  'pageHeader.agreements.title': 'Agreements',
  'pageHeader.agreements.description': 'Manage partner agreements',
  'pageHeader.b2bCommissions.title': 'B2B Commissions',
  'pageHeader.b2bCommissions.description': 'Track partner commissions',

  // ============================================================================
  // REFERRAL TRACKING (Issue #368 - Partner Referral Tracking)
  // ============================================================================
  'referralTracking.subtitle': 'Share your referral links with borrowers and track your earnings.',
  'referralTracking.noAgreements': 'No Active Agreements',
  'referralTracking.noAgreementsDescription':
    'You need an active referral agreement to get a referral link. Contact your partner to set up an agreement.',
  'referralTracking.howItWorks': 'How It Works',
  'referralTracking.step1': 'Copy your referral link and share it with potential borrowers.',
  'referralTracking.step2': 'When borrowers sign up using your link, they get attributed to you.',
  'referralTracking.step3': 'You earn commissions based on your agreement terms.',
  // Partner Statistics (Issue #368)
  'referralTracking.activeAgreements': 'Active Agreements',
  'referralTracking.casesReferred': 'Loans Referred',
  'referralTracking.commissionsEarned': 'Commissions Earned',
  'referralTracking.pendingCommissions': 'Pending Commissions',
  // Commissions page empty states (Issue #1223 PR review round 3)
  'commissions.noDataDescription':
    'Commission records will appear here once referrals are converted and commissions are earned.',
  'commissions.noPayoutDataDescription':
    'Payout records will appear here once commissions are approved and scheduled for payment.',
  'column.case': 'Case',
  'column.date': 'Date',
  'column.agreement': 'Agreement',
  'column.partner': 'Partner',
  'column.commissionTrigger': 'Commission Trigger',
  'column.actions': 'Actions',
  'column.status': 'Status',
  'column.borrower': 'Borrower',
  'column.loanAmount': 'Loan Amount',
  'column.loanType': 'Loan Type',
  'column.stage': 'Stage',
  'column.estimatedClose': 'Estimated Close',
  'column.createdAt': 'Created At',
  'action.approve': 'Approve',
  'action.archive': 'Archive',
  'action.edit': 'Edit',
  'action.restore': 'Restore',
  'action.showQrCode': 'Show QR Code',
  'action.tryAgain': 'Try again',
  'action.goToDashboard': 'Go to Dashboard',
  'action.backToDashboard': 'Back to Dashboard',
  'toast.linkCopied': 'Referral link copied to clipboard',
  'toast.copyFailed': 'Failed to copy link',
  'toast.copied': 'Copied!',

  // Entities Section
  'pageHeader.entities.title': 'Entity Management',
  'pageHeader.entities.description': 'Manage custom entities',
  'pageHeader.entityDetail.title': 'Entity Details',
  'pageHeader.entityDetail.description': 'View and configure entity',
  'pageHeader.newEntity.title': 'New Entity',
  'pageHeader.newEntity.description': 'Create a new custom entity',
  'pageHeader.entityRecords.title': 'Entity Records',
  'pageHeader.entityRecords.description': 'View and manage entity records',
  'pageHeader.entityImport.title': 'Import Records',
  'pageHeader.entityImport.titleWithEntity': 'Import {{entity}} Records',
  'pageHeader.entityImport.description': 'Import records from external sources',
  'pageHeader.entityExport.title': 'Export Records',
  'pageHeader.entityExport.titleWithEntity': 'Export {{entity}} Records',
  'pageHeader.entityExport.description': 'Export records to external formats',
  'pageHeader.entityIdentity.title': 'Entity Identity',
  'pageHeader.entityIdentity.description': 'Configure entity identity settings',

  // CRM Section
  'pageHeader.scoring.title': 'Lead Scoring',
  'pageHeader.scoring.description': 'Configure lead scoring rules',
  'leads.scoringConfig': 'Scoring Config',
  'leads.discoverLeads': 'Discover Leads',
  // Lead page strings (Issue #364 PR feedback)
  'leads.totalLeads': 'Total Leads',
  'leads.salesQualified': 'Sales Qualified',
  'leads.marketingQualified': 'Marketing Qualified',
  'leads.avgScore': 'Avg Score',
  'leads.noLeadsFound': 'No leads found',
  'leads.addLead': 'Add Lead',
  'leads.searchPlaceholder': 'Search leads...',
  'leads.viewFullDetails': 'View Full Details',
  'leads.leadScore': 'Lead Score',
  'leads.contact': 'Contact',
  'leads.source': 'Source',
  'leads.leadId': 'Lead ID',
  'leads.created': 'Created',
  'leads.editLead': 'Edit Lead',
  'leads.addFirst': 'Add your first lead to get started',
  'leads.adjustSearch': 'Try adjusting your search query',
  'leads.failedToLoad': 'Failed to load leads',
  // Lead Filters (Issue #525)
  'leads.leadsFound': 'leads found',
  'leads.filters.title': 'Filter Leads',
  'leads.filters.status': 'Status',
  'leads.filters.qualification': 'Qualification',
  'leads.filters.scoreRange': 'Score Range',
  'leads.filters.source': 'Source',
  'leads.filters.dateRange': 'Date Range',
  'leads.filters.leadStatus': 'Lead Status',
  'leads.filters.qualificationStatus': 'Qualification Status',
  'leads.filters.scoreDescription': 'Filter by lead score (0-100). Higher scores indicate more qualified leads.',
  'leads.filters.leadSource': 'Lead Source',
  'leads.filters.selectSource': 'Select Source',
  'leads.filters.customSource': 'Custom Source',
  'leads.filters.enterCustomSource': 'Enter a custom source...',
  'leads.filters.createdDateRange': 'Created Date Range',
  'leads.filters.createdAfter': 'Created After',
  'leads.filters.createdBefore': 'Created Before',
  'leads.filters.dateRangeHelp': 'Filter leads by when they were created.',
  // Lead Lists (Issue #525)
  'leads.lists.title': 'Lead Lists',
  'leads.lists.listsAvailable': 'lists available',
  'leads.lists.createNew': 'Create List',
  'leads.lists.createNewList': 'Create New List',
  'leads.lists.listName': 'List Name',
  'leads.lists.description': 'Description (optional)',
  'leads.lists.staticList': 'Static List',
  'leads.lists.dynamicList': 'Dynamic List',
  'leads.lists.static': 'Static',
  'leads.lists.dynamic': 'Dynamic',
  'leads.lists.willUseCurrentFilter': 'This list will use the current filter criteria.',
  'leads.lists.noLists': 'No lead lists yet. Create one to organize your leads.',
  'leads.lists.allLeads': 'All Leads',
  'leads.lists.members': 'members',
  'leads.lists.refresh': 'Refresh List',
  'leads.lists.saveCurrentFilter': 'Save Filter as List',
  'leads.lists.savedFilter': 'Saved Filter',
  // Lead Conversion (Issue - Lead to Borrower)
  'leads.sendInvitation': 'Send Invitation',
  'leads.convertToBorrower': 'Convert to Borrower',
  'leads.alreadyConverted': 'Already Converted',
  'leads.conversionSuccess': 'Lead converted to borrower successfully',
  'leads.invitationSent': 'Invitation sent successfully',
  'leads.createCase': 'Create Loan',
  'leads.selectCaseType': 'Select Loan Type',
  'leads.caseTitle': 'Loan Title',
  'leads.caseTitlePlaceholder': 'e.g., Loan Application - John Doe',
  'leads.autoCreateCase': 'Auto-create loan on signup',
  'leads.consumerType': 'Consumer Type',
  'leads.conversionNotes': 'Notes',
  'leads.conversionNotesPlaceholder': 'Add any notes about this conversion...',
  'leads.consumerDetails': 'Consumer Details',
  'leads.caseConfiguration': 'Loan Configuration',
  'leads.leadInfoSummary': 'Lead Information',
  'leads.convertButton': 'Convert',
  'leads.sendInviteButton': 'Send Invitation',
  'leads.conversionFailed': 'Failed to convert lead',
  'leads.invitationFailed': 'Failed to send invitation',
  'leads.emailRequired': 'Lead must have an email to convert',
  'leads.caseTypeRequired': 'Please select a loan type',
  'leads.skipCaseCreation': 'Skip loan creation (create consumer only)',
  // Partner Referral
  'leads.partnerReferral': 'Partner Referral',
  'leads.referLead': 'Refer a Lead',
  'leads.referralCode': 'Referral Code',
  'leads.referralSuccess': 'Lead referred successfully',
  'leads.referralFailed': 'Failed to refer lead',
  'leads.referralAttribution': 'This lead will be attributed to your referral code',
  'leads.invalidReferralCode': 'Invalid or inactive referral code',
  'leads.referredBy': 'Referred by',
  'leads.afterSignupOptions': 'After Signup Options',
  'leads.inviteChannel': 'Delivery Channel',
  'leads.inviteTemplate': 'Email Template',
  'leads.customMessage': 'Custom Message',
  'leads.customMessagePlaceholder': 'Add a personal message to the invitation...',
  'leads.expiresIn': 'Invite Expires In',
  'leads.days': 'days',
  'leads.invitationTitle': 'Send signup invitation to this lead',
  // Public Invite Link (Issue - Copy Invite Link)
  'leads.copyInviteLink': 'Copy Invite Link',
  'leads.copyInviteLinkTooltip': 'Copy a shareable link for the lead to start an intake form',
  'leads.addNote': 'Add Note',
  'leads.addNoteFailed': 'Failed to add note. Please try again.',
  'leads.addNoteSubtitle': 'Add an internal note to this lead',
  'leads.noteContent': 'Note Content',
  'leads.noteContentPlaceholder': 'Enter your note here...',
  'leads.noteType': 'Note Type',
  'leads.changeStatus': 'Change Status',

  // Public Referral Page (Issue #369)
  'referral.pageTitle': 'Get Started Today',
  'referral.noCode': 'No referral code provided. Please use a valid referral link.',
  'referral.validationError': 'Unable to validate referral code. Please try again.',
  'referral.invalidCode': 'This referral code is invalid or has expired.',
  'referral.invalidTitle': 'Invalid Referral Link',
  'referral.invalidMessage':
    'The referral link you used is no longer valid. Please contact the person who shared it with you.',
  'referral.referredBy': 'Referred by {{name}}',
  'referral.successTitle': 'Thank You!',
  'referral.successMessage': 'Your information has been submitted successfully. We will be in touch soon.',
  'referral.referenceNumber': 'Reference Number',
  'referral.successNextSteps': 'Our team will contact you shortly to discuss your needs.',
  'referral.continueToApply': 'Continue to Apply',
  'referral.redirectingIn': 'Redirecting in {{seconds}}s...',
  'referral.loanAmount': 'Loan Amount (Optional)',
  'referral.propertyAddress': 'Property Address (Optional)',
  'referral.propertyAddressPlaceholder': 'e.g., 123 Main St, City, State',
  'referral.notes': 'Additional Notes',
  'referral.notesPlaceholder': 'Tell us about your needs...',
  'referral.submitButton': 'Submit',
  'referral.submitError': 'Failed to submit. Please try again.',
  'referral.privacyNote': 'Your information is secure and will only be used to contact you about your inquiry.',

  // Table headers
  'table.name': 'Name',
  'table.partner': 'Partner',
  'table.company': 'Company',
  'table.score': 'Score',
  'table.qualification': 'Qualification',
  'table.status': 'Status',
  'table.source': 'Source',
  'table.type': 'Type',
  'table.referrals': 'Referrals',
  'table.conversions': 'Conversions',
  'table.rate': 'Rate',
  'table.earnings': 'Earnings',
  'table.joined': 'Joined',
  'table.actions': 'Actions',
  'table.created': 'Created',
  // Entity records (Issue #364 PR feedback)
  'entityRecords.newRecord': 'New Record',
  'entityRecords.searchPlaceholder': 'Search records...',
  'entityRecords.noRecordsFound': 'No records found',
  'entityRecords.backTo': 'Back to {{entity}}',
  'entityRecords.createFirst': 'Create your first record or import from a file',
  'entityRecords.adjustSearch': 'Try adjusting your search query',
  'entityRecords.selectedCount': '{{count}} selected',
  'entityRecords.deleteCount': 'Delete {{count}} record(s)',
  'entityRecords.deletedSuccess': '{{count}} record(s) deleted successfully',
  'entityRecords.createdSuccess': 'Record created successfully',
  'entityRecords.updatedSuccess': 'Record updated successfully',
  'entityRecords.deleteFailed': 'Delete failed',
  'pageHeader.leadCreate.title': 'New Lead',
  'pageHeader.leadCreate.description': 'Create a new lead',
  'pageHeader.leadDetail.title': 'Lead Details',
  'pageHeader.leadDetail.description': 'View and manage lead information',

  // Integrations Section
  'pageHeader.enrichmentHistory.title': 'Enrichment History',
  'pageHeader.enrichmentHistory.description': 'View data enrichment history',
  'pageHeader.enrichmentSettings.title': 'Enrichment Settings',
  'pageHeader.enrichmentSettings.description': 'Configure data enrichment',

  // Pipelines Section
  'pageHeader.pipelines.title': 'Pipelines',
  'pageHeader.pipelines.description': 'Manage your sales pipelines',
  'pageHeader.pipelineDetail.title': 'Pipeline Details',
  'pageHeader.pipelineDetail.description': 'View and configure pipeline',
  'pageHeader.pipelineBoard.title': 'Pipeline Board',
  'pageHeader.pipelineBoard.titleWithPipeline': '{{pipeline}} - Board',
  'pageHeader.pipelineBoard.description': 'Kanban view of your pipeline',
  'pageHeader.pipelineAutomations.title': 'Pipeline Automations',
  'pageHeader.pipelineAutomations.description': 'Configure pipeline automations',

  // Other
  'pageHeader.dashboard.title': 'Home',
  'pageHeader.dashboard.description': 'Your Astor Dashboard',
  'pageHeader.reviewQueue.title': 'Review Queue',
  'pageHeader.reviewQueue.description': 'Items pending review',
  'pageHeader.notFound.title': 'Page Not Found',
  'pageHeader.notFound.description': 'The requested page could not be found',
  'pageHeader.unauthorized.title': 'Access Denied',
  'pageHeader.unauthorized.description': 'You do not have permission to view this page',
  'pageHeader.tasks.title': 'Tasks',
  'pageHeader.tasks.description': 'Manage tasks across your team',

  // Task Page Translations
  'tasks.tabs.all': 'All Tasks',
  'tasks.tabs.todo': 'To Do',
  'tasks.tabs.inProgress': 'In Progress',
  'tasks.tabs.completed': 'Completed',
  'tasks.loginRequired': 'Please log in to view tasks',
  'tasks.filters.assignee': 'Assignee',
  'tasks.filters.myTasks': 'My Tasks',
  'tasks.filters.allTasks': 'All Tasks',
  'tasks.filters.unassigned': 'Unassigned',
  'tasks.create': 'Create Task',
  'tasks.createTask': 'Create Task',
  'tasks.selectCase': 'Select Loan',
  'tasks.selectCasePlaceholder': 'Search and select a loan...',
  'tasks.noCaseSelected': 'Select a loan to associate this task',
  'tasks.entityType': 'Entity Type',
  'tasks.selectEntity': 'Select Entity',
  'tasks.selectEntityPlaceholder': 'Search and select an entity...',
  'tasks.noEntitySelected': 'Select an entity to associate this task',
  'tasks.entityTypes.case': 'Loan',
  'tasks.entityTypes.consumer': 'Consumer',
  'tasks.entityTypes.partner': 'Partner',
  'tasks.entityTypes.document': 'Document',
  'tasks.empty.noTasks': 'No tasks found',
  'tasks.empty.noTasksDescription': 'Create a task to get started',
  'tasks.empty.noAssignedTasks': 'No tasks assigned to you',
  'tasks.empty.noFilterMatches': 'Try adjusting your filters',
  'tasks.empty.createTaskForCase': 'Create a task to track work on this case',

  // TaskList inline editor (Issue #393 / PR #1236)
  'tasks.title': 'Tasks',
  'tasks.addTask': 'Add Task',
  'tasks.quickAddPlaceholder': 'Add task',
  'tasks.searchPlaceholder': 'Search tasks...',
  'tasks.moreFilters': 'More filters',
  'tasks.loadingTasks': 'Loading tasks...',
  'tasks.markAsPending': 'Mark as pending',
  'tasks.markAsCompleted': 'Mark as completed',
  'tasks.due': 'Due:',
  'tasks.overdueSuffix': '(Overdue)',
  'tasks.minutesAbbrev': '~{{minutes}} min',
  'tasks.addDescriptionPlaceholder': 'Add a description…',
  'tasks.estimatedMinutesShort': 'Estimated (min)',
  'tasks.inlineEditHelper': 'Changes save automatically. Press Esc or click the row again to collapse.',

  // ============================================================================
  // ADMIN PORTAL PAGE HEADERS
  // ============================================================================

  // Businesses Section
  'pageHeader.admin.businesses.title': 'Businesses',
  'pageHeader.admin.businesses.description': 'Manage platform businesses',
  'pageHeader.admin.businessDetail.title': 'Business Details',
  'pageHeader.admin.businessDetail.description': 'View and manage business',
  'pageHeader.admin.businessEdit.title': 'Edit Business',
  'pageHeader.admin.businessEdit.description': 'Modify business settings',
  'pageHeader.admin.newBusiness.title': 'New Business',
  'pageHeader.admin.newBusiness.description': 'Create a new business',

  // Identity Section
  'pageHeader.admin.identity.title': 'Identity Management',
  'pageHeader.admin.identity.description': 'Manage identity verification',
  'pageHeader.admin.identityRules.title': 'Identity Rules',
  'pageHeader.admin.identityRules.description': 'Configure identity verification rules',
  'pageHeader.admin.identityAnalytics.title': 'Identity Analytics',
  'pageHeader.admin.identityAnalytics.description': 'Identity verification metrics',

  // Signup Section
  'pageHeader.admin.signupConfig.title': 'Signup Configuration',
  'pageHeader.admin.signupConfig.description':
    'Control global signup settings, approval workflows, and sandbox defaults',
  'pageHeader.admin.provisioningRules.title': 'Provisioning Rules',
  'pageHeader.admin.provisioningRules.description': 'Auto-provisioning rules',
  'pageHeader.admin.qualifiers.title': 'Qualifiers',
  'pageHeader.admin.qualifiers.description': 'Manage signup qualifiers',
  'pageHeader.admin.approvals.title': 'Approvals',
  'pageHeader.admin.approvals.description': 'Review and approve or reject pending signup applications',

  // Platform Settings Section
  'pageHeader.admin.settings.title': 'Platform Settings',
  'pageHeader.admin.settings.description': 'Configure platform settings',
  'pageHeader.admin.sandboxes.title': 'Sandboxes',
  'pageHeader.admin.sandboxes.description': 'Manage test sandboxes',
  'pageHeader.admin.security.title': 'Security',
  'pageHeader.admin.security.description': 'Security settings and audit',
  'pageHeader.admin.agents.title': 'Agents',
  'pageHeader.admin.agents.description': 'Manage platform agents',
  'pageHeader.admin.apiKeys.title': 'API Keys',
  'pageHeader.admin.apiKeys.description': 'Manage API keys',
  'pageHeader.admin.realms.title': 'Realms',
  'pageHeader.admin.realms.description': 'Manage platform realms',
  'pageHeader.admin.tenants.title': 'Tenants',
  'pageHeader.admin.tenants.description': 'Manage tenant accounts and multi-realm access',
  'pageHeader.admin.dashboard.title': 'Dashboard',
  'pageHeader.admin.dashboard.description': 'Platform overview and metrics',
  'pageHeader.admin.entities.title': 'Entity Framework',
  'pageHeader.admin.entities.description': 'Manage entity types and templates',
  'pageHeader.admin.entityTemplates.title': 'Entity Templates',
  'pageHeader.admin.entityTemplates.description': 'Configure entity templates',
  'pageHeader.admin.entityMonitoring.title': 'Entity Monitoring',
  'pageHeader.admin.entityMonitoring.description': 'Monitor entity activity',
  'pageHeader.admin.pricing.title': 'Pricing Engine',
  'pageHeader.admin.pricing.description': 'Manage pricing rules and rates',
  'pageHeader.admin.pricingGlobalRules.title': 'Global Pricing Rules',
  'pageHeader.admin.pricingGlobalRules.description': 'Configure global pricing rules',
  'pageHeader.admin.pricingAudit.title': 'Pricing Audit',
  'pageHeader.admin.pricingAudit.description': 'Audit pricing decisions',
  'pageHeader.admin.templates.title': 'Templates',
  'pageHeader.admin.templates.description': 'Manage platform templates',
  'pageHeader.admin.systemTemplates.title': 'System Templates',
  'pageHeader.admin.systemTemplates.description': 'Manage system-wide templates',

  // Term Sheet Templates (Issue #1307)
  'pageHeader.admin.termsheetTemplates.title': 'Term Sheet Templates',
  'pageHeader.admin.termsheetTemplates.description':
    'Create, edit, and manage business-scoped term sheet templates with signer policies',
  'pageHeader.admin.termsheetTemplates.new.title': 'New Term Sheet Template',
  'pageHeader.admin.termsheetTemplates.new.description':
    'Define sections, branding, and the signer policy for a new template',
  'pageHeader.admin.termsheetTemplates.edit.title': 'Edit Term Sheet Template',
  'pageHeader.admin.termsheetTemplates.edit.description': 'Update sections, branding, and signer policy',

  'navigation.termsheetTemplates': 'Term Sheet Templates',
  'admin.nav.operations.termsheetTemplates': 'Term Sheet Templates',

  // Tabs + list view
  'admin.templates.termsheets.tabs.mine': 'My Templates',
  'admin.templates.termsheets.tabs.system': 'System Templates',
  'admin.templates.termsheets.tabs.ariaLabel': 'Template source — My Templates or System Templates',
  'admin.templates.termsheets.filter.category.label': 'Category',
  'admin.templates.termsheets.filter.category.all': 'All categories',
  'admin.templates.termsheets.filter.category.bridge': 'Bridge',
  'admin.templates.termsheets.filter.category.fixAndFlip': 'Fix & Flip',
  'admin.templates.termsheets.filter.category.dscr': 'DSCR',
  'admin.templates.termsheets.filter.category.construction': 'Construction',
  'admin.templates.termsheets.filter.category.groundUp': 'Ground-up',
  'admin.templates.termsheets.filter.category.custom': 'Custom',
  'admin.templates.termsheets.filter.status.label': 'Status',
  'admin.templates.termsheets.filter.status.all': 'All statuses',
  'admin.templates.termsheets.filter.status.draft': 'Draft',
  'admin.templates.termsheets.filter.status.active': 'Active',
  'admin.templates.termsheets.filter.searchPlaceholder': 'Search by name…',
  'admin.templates.termsheets.column.name': 'Name',
  'admin.templates.termsheets.column.category': 'Category',
  'admin.templates.termsheets.column.status': 'Status',
  'admin.templates.termsheets.column.signers': 'Signers',
  'admin.templates.termsheets.column.updated': 'Last updated',
  'admin.templates.termsheets.column.usage': 'Usage',
  'admin.templates.termsheets.column.actions': 'Actions',
  'admin.templates.termsheets.cta.create': 'Create from scratch',
  'admin.templates.termsheets.cta.copyFromSystem': 'Copy from system template',
  'admin.templates.termsheets.action.edit': 'Edit',
  'admin.templates.termsheets.action.duplicate': 'Duplicate',
  'admin.templates.termsheets.action.delete': 'Delete',
  'admin.templates.termsheets.empty.title': 'No term sheet templates yet',
  'admin.templates.termsheets.empty.description':
    'Start from scratch or copy one of the built-in system templates to get going.',
  'admin.templates.termsheets.empty.cta': 'Copy a system template',
  'admin.templates.termsheets.delete.title': 'Delete this term sheet template?',
  'admin.templates.termsheets.delete.description':
    'This template will be soft-deleted. Existing term sheets generated from it are not affected. Admins can restore it via the API.',
  'admin.templates.termsheets.delete.confirm': 'Delete template',
  'admin.templates.termsheets.copyFromSystem.title': 'Copy from system template',
  'admin.templates.termsheets.copyFromSystem.description': 'Pick a built-in template to start from.',
  'admin.templates.termsheets.copyFromSystem.selectLabel': 'System template',
  'admin.templates.termsheets.copyFromSystem.nameLabel': 'New template name',
  'admin.templates.termsheets.copyFromSystem.loading': 'Loading system templates…',
  'admin.templates.termsheets.copyFromSystem.loadError': 'Failed to load system templates. Close and try again.',
  'admin.templates.termsheets.copyFromSystem.empty': 'No system templates are available.',

  // Edit view — pane titles
  'admin.templates.termsheets.edit.sections.title': 'Sections',
  'admin.templates.termsheets.edit.sections.addButton': 'Add section',
  'admin.templates.termsheets.edit.sections.removeTooltip': 'Remove section',
  'admin.templates.termsheets.edit.sections.reorderUp': 'Move up',
  'admin.templates.termsheets.edit.sections.reorderDown': 'Move down',
  'admin.templates.termsheets.edit.sections.untitled': 'Untitled section',
  'admin.templates.termsheets.edit.sections.type.header': 'Header',
  'admin.templates.termsheets.edit.sections.type.borrower_info': 'Borrower info',
  'admin.templates.termsheets.edit.sections.type.property_info': 'Property info',
  'admin.templates.termsheets.edit.sections.type.loan_terms': 'Loan terms',
  'admin.templates.termsheets.edit.sections.type.acceptance': 'Acceptance',
  'admin.templates.termsheets.edit.sections.type.terms_and_conditions': 'Terms & conditions',
  'admin.templates.termsheets.edit.sections.type.custom': 'Custom',
  'admin.templates.termsheets.edit.metadata.name': 'Template name',
  'admin.templates.termsheets.edit.metadata.description': 'Description',
  'admin.templates.termsheets.edit.metadata.validityDays': 'Validity (days)',
  'admin.templates.termsheets.edit.metadata.supportedLoanTypes': 'Supported loan types',
  'admin.templates.termsheets.edit.metadata.isDefault': 'Use as default template',
  'admin.templates.termsheets.edit.tabs.content': 'Content',
  'admin.templates.termsheets.edit.tabs.branding': 'Branding',
  'admin.templates.termsheets.edit.tabs.signerPolicy': 'Signer policy',
  'admin.templates.termsheets.edit.tabs.preview': 'Preview',
  'admin.templates.termsheets.edit.save': 'Save',
  'admin.templates.termsheets.edit.saving': 'Saving…',
  'admin.templates.termsheets.edit.discard': 'Discard changes',
  'admin.templates.termsheets.edit.unsavedWarning': 'You have unsaved changes. Discard them?',

  // Section editor
  'admin.templates.termsheets.section.titleLabel': 'Section title',
  'admin.templates.termsheets.section.contentLabel': 'Content',
  'admin.templates.termsheets.section.contentPlaceholder':
    'Type content. Use {{borrower.legalName}}, {{loan.amount | currency}}, etc.',
  'admin.templates.termsheets.section.requiredLabel': 'Required',
  'admin.templates.termsheets.section.visibleLabel': 'Visible',
  'admin.templates.termsheets.section.picker.title': 'Insert variable',
  'admin.templates.termsheets.section.picker.search': 'Search variables…',
  'admin.templates.termsheets.section.picker.helpersTitle': 'Helpers',
  'admin.templates.termsheets.section.picker.noMatch': 'No variables match that search.',
  'admin.templates.termsheets.section.validation.run': 'Run validation',
  'admin.templates.termsheets.section.validation.running': 'Validating…',
  'admin.templates.termsheets.section.validation.valid': 'No issues',
  'admin.templates.termsheets.section.validation.errorsCount': '{{count}} issue(s)',
  'admin.templates.termsheets.section.validation.lineColumn': 'line {line}, col {column}',
  'admin.templates.termsheets.section.validation.unavailable':
    'Server validation is temporarily unavailable. Save is still allowed.',

  // Branding editor
  'admin.templates.termsheets.branding.companyName': 'Company name',
  'admin.templates.termsheets.branding.logoUrl': 'Logo URL',
  'admin.templates.termsheets.branding.logoUrlPlaceholder': 'https://…',
  'admin.templates.termsheets.branding.logoPreview': 'Logo preview',
  'admin.templates.termsheets.branding.primaryColor': 'Primary color',
  'admin.templates.termsheets.branding.secondaryColor': 'Secondary color',
  'admin.templates.termsheets.branding.contactEmail': 'Contact email',
  'admin.templates.termsheets.branding.contactPhone': 'Contact phone',
  'admin.templates.termsheets.branding.colorPicker.swatchLabel': '{{family}} {{shade}}',
  'admin.templates.termsheets.branding.previewLabel': 'Live preview',
  'admin.templates.termsheets.branding.errors.logoHttps': 'Logo URL must use HTTPS in production',

  // Signer policy builder
  'admin.templates.termsheets.signerPolicy.title': 'Required signers',
  'admin.templates.termsheets.signerPolicy.description':
    'Declare who must sign. The party resolver uses this to build the concrete signer list for each term sheet.',
  'admin.templates.termsheets.signerPolicy.addRow': 'Add signer',
  'admin.templates.termsheets.signerPolicy.column.role': 'Role',
  'admin.templates.termsheets.signerPolicy.column.cardinality': 'Cardinality',
  'admin.templates.termsheets.signerPolicy.column.source': 'Source',
  'admin.templates.termsheets.signerPolicy.column.signingOrder': 'Order',
  'admin.templates.termsheets.signerPolicy.column.counterSign': 'Counter-sign',
  'admin.templates.termsheets.signerPolicy.column.remove': 'Remove',
  'admin.templates.termsheets.signerPolicy.role.borrower': 'Borrower',
  'admin.templates.termsheets.signerPolicy.role.co_borrower': 'Co-borrower',
  'admin.templates.termsheets.signerPolicy.role.guarantor': 'Guarantor',
  'admin.templates.termsheets.signerPolicy.role.authorized_officer': 'Authorized officer',
  'admin.templates.termsheets.signerPolicy.role.lender_signer': 'Lender signer',
  'admin.templates.termsheets.signerPolicy.role.credit_manager': 'Credit manager',
  'admin.templates.termsheets.signerPolicy.role.witness': 'Witness',
  'admin.templates.termsheets.signerPolicy.role.notary': 'Notary',
  'admin.templates.termsheets.signerPolicy.role.cc': 'Carbon copy',
  'admin.templates.termsheets.signerPolicy.cardinality.one': 'One',
  'admin.templates.termsheets.signerPolicy.cardinality.many': 'Many',
  'admin.templates.termsheets.signerPolicy.cardinality.optional': 'Optional',
  'admin.templates.termsheets.signerPolicy.source.case.assignedTo': 'Assigned user',
  'admin.templates.termsheets.signerPolicy.source.case.creditManager': 'Credit manager',
  'admin.templates.termsheets.signerPolicy.source.case.borrowers': 'Case borrowers',
  'admin.templates.termsheets.signerPolicy.source.case.guarantors': 'Case guarantors',
  'admin.templates.termsheets.signerPolicy.source.case.entitySignatory': 'Entity signatory',
  'admin.templates.termsheets.signerPolicy.source.manual': 'Manual',
  'admin.templates.termsheets.signerPolicy.summary.empty': 'No signers configured yet.',
  'admin.templates.termsheets.signerPolicy.summary.single': 'This template requires {count} signer.',
  'admin.templates.termsheets.signerPolicy.summary.multiple': 'This template requires {count} signers.',
  'admin.templates.termsheets.signerPolicy.errors.missingBorrower':
    'Policy must include at least one borrower or authorized_officer row.',
  'admin.templates.termsheets.signerPolicy.errors.counterSignRole':
    'Counter-sign rows must use role "Lender signer" or "Credit manager".',
  'admin.templates.termsheets.signerPolicy.errors.counterSignOrder':
    'Counter-sign rows must have a higher signing order than any non-counter-sign row.',
  'admin.templates.termsheets.signerPolicy.errors.invalidOrder': 'Signing order must be a positive integer.',
  'admin.templates.termsheets.signerPolicy.errors.duplicateOrder':
    'Duplicate signing orders are not supported in the MVP (parallel signing is a future feature).',
  'admin.templates.termsheets.signerPolicy.errors.missingSource':
    'Rows with cardinality "one" or "many" must declare a source.',

  // Preview / misc
  'admin.templates.termsheets.preview.title': 'Preview',
  'admin.templates.termsheets.preview.description':
    'Render preview requires a saved template and a case. Use the Content tab to author and lint the template.',
  'admin.templates.termsheets.errors.loadFailed': 'Failed to load templates. Please try again.',
  'admin.templates.termsheets.errors.saveFailed': 'Failed to save template',
  'admin.templates.termsheets.errors.deleteFailed': 'Failed to delete template',
  'admin.templates.termsheets.errors.validateFailed': 'Failed to validate template',
  'admin.templates.termsheets.errors.featureDisabled':
    'Term sheet templates management is not enabled for this environment.',
  'admin.templates.termsheets.errors.notFound': 'This term sheet template could not be found.',
  'admin.templates.termsheets.errors.forbidden': 'You do not have permission to edit this template.',

  // Edit page
  'admin.templates.termsheets.edit.title': 'Edit term sheet template',
  'admin.templates.termsheets.edit.backToList': 'Back to templates',
  'admin.templates.termsheets.edit.loading': 'Loading template…',
  'admin.templates.termsheets.edit.savedAt': 'Saved {when}',
  'admin.templates.termsheets.edit.unsavedBadge': 'Unsaved changes',
  'admin.templates.termsheets.edit.sectionListTitle': 'Sections',
  'admin.templates.termsheets.edit.sectionListEmpty': 'No sections yet. Add one to get started.',
  'admin.templates.termsheets.edit.sectionPicker.placeholder': 'Select a section to edit',
  'admin.templates.termsheets.edit.preview.comingSoon':
    'PDF preview requires a saved template and a case. Save to enable preview.',
  'admin.templates.termsheets.edit.discardConfirm.title': 'Discard unsaved changes?',
  'admin.templates.termsheets.edit.discardConfirm.description':
    'You have unsaved changes that will be lost if you leave this page.',
  'admin.templates.termsheets.edit.discardConfirm.confirm': 'Discard changes',
  'admin.templates.termsheets.edit.discardConfirm.keep': 'Keep editing',

  // Create page
  'admin.templates.termsheets.new.title': 'New term sheet template',
  'admin.templates.termsheets.new.description':
    'Fill in template basics, add sections, and configure branding and signers.',
  'admin.templates.termsheets.new.defaultName': 'Untitled template',
  'admin.templates.termsheets.new.defaultSectionTitle': 'New section',

  // Shared
  'admin.templates.termsheets.metadataTab': 'Metadata',
  'admin.templates.termsheets.errors.validationFailed':
    'Validation failed. Review the issues in each section before saving.',
  'admin.templates.termsheets.errors.signerPolicyInvalid': 'Signer policy has errors. Fix them before saving.',
  'pageHeader.admin.agentPlayground.title': 'Agent Playground',
  'pageHeader.admin.agentPlayground.description': 'Test and debug agents',
  'pageHeader.admin.analytics.title': 'Analytics',
  'pageHeader.admin.analytics.description': 'Platform analytics and insights',
  'pageHeader.admin.apiAnalytics.title': 'API Analytics',
  'pageHeader.admin.apiAnalytics.description': 'Monitor API usage and performance',

  // Pipelines Section (Admin)
  'pageHeader.admin.pipelines.title': 'Pipelines',
  'pageHeader.admin.pipelines.description': 'Manage loan pipelines',
  'pageHeader.admin.pipelineTemplates.title': 'Pipeline Templates',
  'pageHeader.admin.pipelineTemplates.description': 'Configure pipeline templates',
  'pageHeader.admin.slaMonitoring.title': 'SLA Monitoring',
  'pageHeader.admin.slaMonitoring.description': 'Monitor pipeline SLAs',

  // Models Section
  'pageHeader.admin.models.title': 'AI Models',
  'pageHeader.admin.models.description': 'Manage AI models',
  'pageHeader.admin.modelDetail.title': 'Model Details',
  'pageHeader.admin.modelDetail.description': 'View model configuration',
  'pageHeader.admin.modelProviders.title': 'Model Providers',
  'pageHeader.admin.modelProviders.description': 'Configure AI providers',

  // Control Tower Section
  'pageHeader.admin.controlTower.title': 'Control Tower',
  'pageHeader.admin.controlTower.description': 'Monitor system health, incidents, and performance',
  'pageHeader.admin.controlTower.incidents.title': 'Incidents',
  'pageHeader.admin.controlTower.incidents.description': 'Track and manage platform incidents',
  'pageHeader.admin.controlTower.metrics.title': 'Metrics',
  'pageHeader.admin.controlTower.metrics.description': 'Monitor platform performance metrics',
  'pageHeader.admin.controlTower.networkLogs.title': 'Network Logs',
  'pageHeader.admin.controlTower.networkLogs.description': 'View network activity and API logs',
  'pageHeader.admin.controlTower.reports.title': 'Reports',
  'pageHeader.admin.controlTower.reports.description': 'Generate and view system reports',
  'pageHeader.admin.controlTower.signalHealth.title': 'Signal Health',
  'pageHeader.admin.controlTower.signalHealth.description': 'Monitor signal and service health status',
  'pageHeader.admin.controlTower.sla.title': 'SLA',
  'pageHeader.admin.controlTower.sla.description': 'Track service level agreement compliance',
  'pageHeader.admin.controlTower.testRuns.title': 'Test Runs',
  'pageHeader.admin.controlTower.testRuns.description': 'View canary and integration test results',
  'pageHeader.admin.scenarios.title': 'Scenarios',
  'pageHeader.admin.scenarios.description': 'Run end-to-end tests and canary scenarios',

  // Platform Guide Section
  'pageHeader.admin.platform.title': 'Platform Overview',
  'pageHeader.admin.platform.description': 'Fiops lending platform overview, capabilities, and tech stack',
  'pageHeader.admin.platformPersonas.title': 'Platform Personas',
  'pageHeader.admin.platformPersonas.description':
    'User roles and access levels across Admin, Business, and Consumer portals',
  'pageHeader.admin.platformWorkflows.title': 'Workflow Catalog',
  'pageHeader.admin.platformWorkflows.description':
    'Cross-portal workflow catalog with readiness and priority tracking',
  'pageHeader.admin.stagePermissions.title': 'Stage Permissions',
  'pageHeader.admin.stagePermissions.description':
    'Per-pipeline RBAC: who can move loans through each stage, dual-approval rules, single-actor caps, and team restrictions.',
  'pageHeader.admin.fieldPermissions.title': 'Field Permissions',
  'pageHeader.admin.fieldPermissions.description':
    'Override per-field role attribution for loan data points. Each field has separate viewable, editable, and hidden role lists, with optional per-stage refinements.',
  'pageHeader.admin.platformArchitecture.title': 'Architecture',
  'pageHeader.admin.platformArchitecture.description': 'System structure, services, and multi-tenancy model',

  // Billing Section (Admin)
  'pageHeader.admin.billing.title': 'Billing',
  'pageHeader.admin.billing.description': 'Manage platform billing',
  'pageHeader.admin.billing.plans.title': 'Billing Plans',
  'pageHeader.admin.billing.plans.description': 'Manage subscription plans and pricing tiers',
  'pageHeader.admin.invoices.title': 'Invoices',
  'pageHeader.admin.invoices.description': 'Manage billing invoices',
  'pageHeader.admin.subscriptions.title': 'Subscriptions',
  'pageHeader.admin.subscriptions.description': 'Manage platform subscriptions and billing tiers',

  // Users & Consumers Section (Admin)
  'pageHeader.admin.users.title': 'Users',
  'pageHeader.admin.users.description': 'Manage platform users and access',
  'pageHeader.admin.consumers.title': 'Consumers',
  'pageHeader.admin.consumers.description': 'Manage consumer accounts',

  // Integrations Section (Admin)
  'pageHeader.admin.integrations.title': 'Integrations',
  'pageHeader.admin.integrations.description': 'Manage platform integrations and connectors',
  'pageHeader.admin.integrations.businesses.title': 'Business Integrations',
  'pageHeader.admin.integrations.businesses.description': 'Manage business integration configurations',
  'pageHeader.admin.integrations.enrichment.title': 'Enrichment',
  'pageHeader.admin.integrations.enrichment.description': 'Configure data enrichment integrations',
  'pageHeader.admin.integrations.monitoring.title': 'Integration Monitoring',
  'pageHeader.admin.integrations.monitoring.description': 'Monitor integration health and activity',
  'pageHeader.admin.integrations.config.title': 'Integration Config',
  'pageHeader.admin.integrations.config.description': 'Configure integration settings',
  'pageHeader.admin.integrations.config.detail.title': 'Integration Details',
  'pageHeader.admin.integrations.slack.title': 'Slack — Platform Setup',
  'pageHeader.admin.integrations.slack.description':
    'Build the Fiops Slack App once so every tenant can connect their workspace with a single click.',
  'pageHeader.admin.integrations.housecanary.title': 'HouseCanary',
  'pageHeader.admin.integrations.housecanary.description':
    'Per-business HouseCanary opt-in for cross-AVM property triage validation.',
  'pageHeader.business.integrations.connections.title': 'Connected Workspaces',
  'pageHeader.business.integrations.connections.description':
    'OAuth-connected external workspaces (Slack, Salesforce, …) that can send inbound requests into this business.',

  // Integration Connections (Issue #1344) — per-business, surfaced in business portal
  'integrations.connections.empty.title': 'No connected workspaces yet',
  'integrations.connections.empty.description':
    'Connect a Slack workspace to enable the /loan-size slash command and other inbound integrations.',
  'integrations.connections.connectSlack': 'Connect Slack',
  'integrations.connections.connecting': 'Connecting…',
  'integrations.connections.disconnect': 'Disconnect',
  'integrations.connections.disconnect.confirmTitle': 'Disconnect {{provider}} workspace?',
  'integrations.connections.disconnect.confirmBody':
    'Inbound requests from "{{name}}" will immediately fail until reconnected. Saved data is preserved.',
  'integrations.connections.disconnecting': 'Disconnecting…',
  'integrations.connections.column.provider': 'Provider',
  'integrations.connections.column.workspace': 'Workspace',
  'integrations.connections.column.status': 'Status',
  'integrations.connections.column.connectedBy': 'Connected by',
  'integrations.connections.column.connectedAt': 'Connected',
  'integrations.connections.column.actions': 'Actions',
  'integrations.connections.status.ACTIVE': 'Active',
  'integrations.connections.status.REVOKED': 'Revoked',
  'integrations.connections.status.EXPIRED': 'Expired',
  'integrations.connections.slackSetup.title': 'Set up Slack',
  'integrations.connections.slackSetup.slashCommand': 'Slash command',
  'integrations.connections.slashCommandHint': 'Point this URL at /loan-size in your Slack App configuration.',
  'integrations.connections.slackSetup.scopes': 'Required scopes',
  'integrations.connections.slackSetup.callback': 'OAuth redirect URL',
  'integrations.connections.slackSetup.steps': 'Setup steps',

  // Audit
  'pageHeader.admin.audit.title': 'Audit Log',
  'pageHeader.admin.audit.description': 'View platform audit trail and activity logs',

  // Compliance
  'pageHeader.admin.compliance.title': 'Compliance',
  'pageHeader.admin.compliance.description': 'Manage compliance rules and monitoring',

  // Documents
  'pageHeader.admin.documents.title': 'Documents',
  'pageHeader.admin.documents.detail.title': 'Document Details',

  // Profile
  'pageHeader.admin.profile.title': 'Profile',
  'pageHeader.admin.profile.description': 'Manage your account profile',

  // Team Management
  'pageHeader.admin.teamManagement.title': 'Team Management',
  'pageHeader.admin.teamManagement.description': 'Manage team members and roles',

  // Model & Cost Analytics
  'pageHeader.admin.modelAnalytics.title': 'Model Analytics',
  'pageHeader.admin.modelAnalytics.description': 'Analyze AI model usage and performance',
  'pageHeader.admin.costAnalytics.title': 'Cost Analytics',
  'pageHeader.admin.costAnalytics.description': 'Monitor platform costs and spending',

  // Control Tower — Scenarios
  'pageHeader.admin.controlTower.scenarios.title': 'Scenarios',
  'pageHeader.admin.controlTower.scenarios.description': 'Run end-to-end tests and canary scenarios',
  'pageHeader.admin.controlTower.scenarios.detail.title': 'Scenario Execution',
  'pageHeader.admin.controlTower.scenarios.detail.description': 'View scenario execution details and results',

  // Invoices Detail / Edit / New
  'pageHeader.admin.invoiceDetail.title': 'Invoice Details',
  'pageHeader.admin.invoiceDetail.description': 'View invoice details and payment status',
  'pageHeader.admin.editInvoice.title': 'Edit Invoice',
  'pageHeader.admin.editInvoice.description': 'Modify invoice details',
  'pageHeader.admin.newInvoice.title': 'New Invoice',
  'pageHeader.admin.newInvoice.description': 'Create a new invoice',

  // User & Consumer Detail
  'pageHeader.admin.userDetail.title': 'User Details',
  'pageHeader.admin.userDetail.description': 'View and manage user account',
  'pageHeader.admin.consumerDetail.title': 'Consumer Details',
  'pageHeader.admin.consumerDetail.description': 'View and manage consumer profile',

  // Intake Templates
  'pageHeader.admin.intakeTemplates.title': 'Intake Templates',
  'pageHeader.admin.intakeTemplates.description': 'Configure intake form templates',

  // Entity Template Detail
  'pageHeader.admin.entities.templates.detail.title': 'Entity Template Details',

  // SLA Monitoring Tabs
  'sla.activeBreaches': 'Active Breaches',
  'sla.bottlenecks': 'Bottlenecks',

  // Identity Resolution
  'identity.matchRules': 'Match Rules',
  'identity.analytics': 'Analytics',
  'identity.analyticsDashboard': 'Analytics Dashboard',
  'identity.matchRulesDescription': 'Configure matching algorithms and thresholds',
  'identity.analyticsDescription': 'View match rates and quality metrics',

  // Pipelines
  'pipelines.templates': 'Templates',
  'pipelines.slaDashboard': 'SLA Dashboard',
  'pipelines.pipelineTemplates': 'Pipeline Templates',
  'pipelines.pipelineTemplatesDescription': 'Create and manage reusable pipeline configurations',
  'pipelines.slaMonitoring': 'SLA Monitoring',
  'pipelines.slaMonitoringDescription': 'Track SLA compliance and breach alerts',
  'pipelines.kanbanBoard': 'Kanban Board',
  'pipelines.activate': 'Activate',
  // Pipeline detail stats ribbon (#1855)
  'pipelines.stats.stages': 'Stages',
  'pipelines.stats.activeRecords': 'Active Records',
  'pipelines.stats.automations': 'Automations',
  'pipelines.stats.conversionRate': 'Conversion Rate',
  'pipelines.stats.totalAutomations': 'Total Automations',

  // Pricing
  'pricing.globalRules': 'Global Rules',
  'pricing.auditTrail': 'Audit Trail',
  'pricing.globalPricingRules': 'Global Pricing Rules',
  'pricing.globalPricingRulesDescription': 'Configure platform-wide base rates and fee structures',
  'pricing.quoteAuditTrail': 'Quote Audit Trail',
  'pricing.quoteAuditTrailDescription': 'Review quote history and rule change logs',

  // ============================================================================
  // RECORDS (Entity Framework)
  // ============================================================================
  'record.create': 'Create Record',
  'record.createSubtitle': 'Add a new record to this entity',
  'record.editSubtitle': 'Edit existing record details',
  'record.update': 'Update Record',

  // ============================================================================
  // TERM SHEETS
  // ============================================================================
  'termsheet.create': 'Create Term Sheet',
  'termsheet.createAction': 'Create Term Sheet',
  'termsheet.createSubtitle': 'Create a new term sheet for this deal',
  // Issue #1388 — Phase 5: per-business LoanProduct picker on the term-sheet builder
  'termsheet.field.loanProduct': 'Loan product',
  'termsheet.validation.loanProductRequired': 'Loan product is required',
  'termsheet.validation.loanProductCategoryUnsupported':
    "This product's category isn't supported by the term sheet engine. Pick a different product or update its category in Loan Products.",
  // Issue #1388 — Phase 5: shared hint for empty product catalogs
  'loanProducts.emptyHint': 'Configure loan products in Loan Products Control Center.',

  // ============================================================================
  // PLACEHOLDERS
  // ============================================================================
  'placeholder.businessId': 'Select a business',
  'placeholder.exclusivity': 'Select exclusivity level',

  // ============================================================================
  // PERMISSION UI (Issue #383)
  // ============================================================================

  // Lock Indicator
  'lock.tooltip.permission': 'This feature requires additional permissions',
  'lock.tooltip.tier': 'This feature requires {{tier}} tier or higher',
  'lock.tooltip.license': 'This feature requires a {{license}} license',
  'lock.contactAdmin': 'Contact your administrator for access',
  'lock.upgrade': 'Upgrade to unlock',
  'lock.viewPlans': 'View Plans',

  // Secure Button/Controls
  'secure.locked': 'Locked',
  'secure.requiresPermission': 'Requires {{permission}} permission',
  'secure.requiresTier': 'Requires {{tier}} subscription',
  'secure.requiresLicense': 'Requires {{license}} license',
  'secure.clickToRequest': 'Click to request access',
  'secure.clickToUpgrade': 'Click to upgrade',
  'secure.noAccess': 'You do not have access to this feature',

  // Upgrade Modal
  'upgrade.title': 'Upgrade Your Plan',
  'upgrade.subtitle': 'Unlock powerful features with a higher tier',
  'upgrade.currentPlan': 'Current Plan',
  'upgrade.recommendedPlan': 'Recommended',
  'upgrade.feature': 'Feature',
  'upgrade.featureDescription': 'Access {{feature}} and more with {{tier}}',
  'upgrade.featureComparison': 'Feature Comparison',
  'upgrade.included': 'Included',
  'upgrade.notIncluded': 'Not included',
  'upgrade.contactSales': 'Contact Sales',
  'upgrade.upgradeTo': 'Upgrade to {{tier}}',
  'upgrade.viewAllFeatures': 'View All Features',
  'upgrade.cancel': 'Maybe Later',
  'upgrade.tier.free': 'Free',
  'upgrade.tier.starter': 'Starter',
  'upgrade.tier.professional': 'Professional',
  'upgrade.tier.enterprise': 'Enterprise',
  'upgrade.tierDescription.free': 'Basic features to get started',
  'upgrade.tierDescription.starter': 'Essential features for small teams',
  'upgrade.tierDescription.professional': 'Advanced features for growing businesses',
  'upgrade.tierDescription.enterprise': 'Full platform access with premium support',
  'upgrade.cta': 'Upgrade to {{tier}}',
  'upgrade.generalDescription': 'Unlock this feature by upgrading to {{tier}}',
  'upgrade.requiredPlan': 'Required Plan',

  // Request Access Modal
  'requestAccess.title': 'Request Access',
  'requestAccess.featureDescription': 'You need additional permissions to access {{feature}}.',
  'requestAccess.generalDescription': 'You need additional permissions to access this feature.',
  'requestAccess.permissionRequired': 'Permission Required',
  'requestAccess.permissionExplanation':
    'This feature is restricted to users with specific permissions. Request access from your administrator.',
  'requestAccess.licenseRequired': 'License Required',
  'requestAccess.licenseExplanation':
    'This feature requires a {{license}} license. Contact your administrator to obtain the required license.',
  'requestAccess.reasonLabel': 'Reason for Request (Optional)',
  'requestAccess.reasonPlaceholder': 'Explain why you need access to this feature...',
  'requestAccess.submitRequest': 'Submit Request',
  'requestAccess.submitted': 'Request Submitted',
  'requestAccess.submittedDescription': 'Your access request has been submitted for review.',
  'requestAccess.adminWillReview': 'An administrator will review your request and get back to you.',
  'requestAccess.contactAdmin': 'You can also contact your administrator directly at {{email}}.',
  'requestAccess.orContactAdmin': 'Or contact your administrator at {{email}}.',

  // Permission Matrix
  'permissionMatrix.title': 'Permission Matrix',
  'permissionMatrix.subtitle': 'View and manage role permissions',
  'permissionMatrix.searchPlaceholder': 'Search permissions...',
  'permissionMatrix.permission': 'Permission',
  'permissionMatrix.roles': 'Roles',
  'permissionMatrix.granted': 'Granted',
  'permissionMatrix.denied': 'Denied',
  'permissionMatrix.inherited': 'Inherited',
  'permissionMatrix.custom': 'Custom',
  'permissionMatrix.requiresLicense': 'Requires license',
  'permissionMatrix.noPermissionsFound': 'No permissions found matching your search.',
  'permissionMatrix.expandAll': 'Expand All',
  'permissionMatrix.collapseAll': 'Collapse All',
  'permissionMatrix.group.loanOperations': 'Loan Operations',
  'permissionMatrix.group.consumerData': 'Consumer Data',
  'permissionMatrix.group.documents': 'Documents',
  'permissionMatrix.group.pipeline': 'Pipeline',
  'permissionMatrix.group.agents': 'Agents',
  'permissionMatrix.group.admin': 'Administration',
  'permissionMatrix.group.billing': 'Billing',
  'permissionMatrix.group.audit': 'Audit & Compliance',
  'permissionMatrix.searchPermissions': 'Search permissions...',
  'permissionMatrix.hasPermission': 'Has permission',
  'permissionMatrix.noPermission': 'No permission',

  // User Access View
  'userAccess.title': 'User Access',
  'userAccess.subtitle': 'View user permissions and licenses',
  'userAccess.licenses': 'Licenses',
  'userAccess.customRoles': 'Custom Roles',
  'userAccess.elevatedAccess': 'Elevated Access',
  'userAccess.permissions': 'Permissions',
  'userAccess.sourceRole': 'from role',
  'userAccess.sourceCustomRole': 'from custom role',
  'userAccess.sourceLicense': 'from license',
  'userAccess.sourceElevated': 'elevated access',
  'userAccess.expiresOn': 'Expires on {{date}}',
  'userAccess.grantedBy': 'Granted by {{name}}',
  'userAccess.noLicenses': 'No licenses assigned',
  'userAccess.noCustomRoles': 'No custom roles assigned',
  'userAccess.noElevatedAccess': 'No elevated access grants',

  // Subscription Tiers
  'tier.free': 'Free',
  'tier.starter': 'Starter',
  'tier.professional': 'Professional',
  'tier.enterprise': 'Enterprise',
  'tier.current': 'Current Plan',
  'tier.upgrade': 'Upgrade',
  'tier.downgrade': 'Downgrade',
  'tier.badge': '{{tier}} Plan',

  // License Types
  'license.nmls': 'NMLS',
  'license.nmlsIndividual': 'NMLS Individual',
  'license.nmlsCompany': 'NMLS Company',
  'license.stateSpecific': 'State License',
  'license.active': 'Active',
  'license.expired': 'Expired',
  'license.pending': 'Pending',
  'license.revoked': 'Revoked',

  // ============================================================================
  // ASTOR CHAT FLYOVER
  // ============================================================================

  'astorChat.title': 'Astor',
  'astorChat.tooltip': 'Open Astor Chat',
  'astorChat.emptyTitle': 'Start a conversation',
  'astorChat.emptySubtitle': 'Ask me anything about your data, documents, or workflows',
  'astorChat.placeholder': 'Ask Astor anything...',
  'astorChat.send': 'Send',
  'astorChat.sending': 'Sending',
  'astorChat.size.side': 'Side',
  'astorChat.size.wide': 'Wide',
  'astorChat.suggestions.title': 'Suggested prompts',
  'astorChat.suggestions.whatCanAstorDo': 'What can Astor do?',
  'astorChat.suggestions.createFormula': 'Create a formula based on conditions',
  'astorChat.suggestions.viewAll': 'View all suggestions',
  'astorChat.suggestions.analyzePipeline': 'Analyze my pipeline health',
  'astorChat.suggestions.summarizeCases': 'Summarize my open loans',
  'astorChat.suggestions.reviewDocuments': 'Review recent documents',
  'astorChat.suggestions.generateReport': 'Generate a weekly report',
  'astorChat.newConversation': 'New conversation',
  'astorChat.clearHistory': 'Clear history',
  'astorChat.generating': 'Generating...',
  'astorChat.error': 'Failed to get response. Please try again.',
  'astorChat.retry': 'Retry',
  'astorChat.disclaimer': 'Astor can make mistakes.',
  'astorChat.learnMore': 'Learn more',
  // Menu items
  'astorChat.menu.sendFeedback': 'Send feedback',
  'astorChat.menu.report': 'Report',
  'astorChat.menu.moreSuggestions': 'More suggestions',
  // Personalized greeting
  'astorChat.greeting': 'Hi {{name}}, how can I help?',
  'astorChat.greetingGeneric': 'How can I help you today?',
  // Portal-specific hints
  'astorChat.hint.business': 'I can help with loans, consumers, documents, leads, and pipeline management.',
  'astorChat.hint.consumer': 'I can help with your loans, documents, and application status.',
  'astorChat.hint.admin': 'I can help with user management, platform settings, and system monitoring.',
  // Portal-specific suggestions
  'astorChat.suggestions.consumer.checkStatus': 'Check my application status',
  'astorChat.suggestions.consumer.uploadDocument': 'Upload a document',
  'astorChat.suggestions.consumer.contactSupport': 'Contact my loan officer',
  'astorChat.suggestions.admin.viewUsers': 'View all users',
  'astorChat.suggestions.admin.systemHealth': 'Check system health',
  'astorChat.suggestions.admin.auditLogs': 'Review audit logs',

  // ============================================================================
  // CONSUMER CHAT PAGE (Issue #809 - Feature 7)
  // ============================================================================
  'chat.title': 'AI Assistant',
  'chat.description': 'Chat with your AI assistant',
  'chat.placeholder': 'Type your message...',
  'chat.send': 'Send',
  'chat.clearHistory': 'Clear history',
  'chat.suggestedActions': 'Suggested Actions',
  'chat.checkStatus': 'Check my application status',
  'chat.uploadDoc': 'Upload a document',
  'chat.whatDocsNeeded': 'What documents do I need?',
  'chat.contactLender': 'Contact my lender',
  'chat.typing': 'Generating...',
  'chat.errorMessage': 'Something went wrong. Please try again.',
  'chat.emptyState': 'Start a conversation with your AI assistant',
  'chat.welcome': 'Hi {{name}}, how can I help you today?',
  'chat.welcomeGeneric': 'How can I help you today?',
  'chat.disclaimer': 'AI responses may not always be accurate.',
  'chat.loadingHistory': 'Loading your conversation...',
  'chat.historyClearError': 'Failed to clear chat history. Please try again.',

  // ============================================================================
  // FILTERS
  // ============================================================================
  'filters.type': 'Type',
  'filters.white_label': 'White-Label',
  'filters.referral': 'Referral',
  'filters.status': 'Status',
  'filters.dateRange': 'Date Range',
  'filters.clearAll': 'Clear All',

  // ============================================================================
  // ACTIONS
  // ============================================================================
  'actions.addPartner': 'Add Partner',
  'actions.addConsumer': 'Add Consumer',
  'actions.addCase': 'Add Loan',
  'actions.addDocument': 'Add Document',
  'actions.addUser': 'Add User',
  'actions.addTeam': 'Add Team',

  // ============================================================================
  // DIALOG
  // ============================================================================
  'dialog.deleteTitle': 'Delete {{entity}}',
  'dialog.confirmDelete': 'Are you sure you want to delete {{entity}}?',
  'dialog.deleteWarning': 'This action cannot be undone.',
  'dialog.discardChanges': 'Discard Changes',
  'dialog.discardChangesMessage': 'You have unsaved changes. Are you sure you want to discard them?',
  'dialog.confirmAction': 'Confirm Action',

  // ============================================================================
  // SYSTEM TEMPLATES (Issue #400 - First-Time User Experience)
  // ============================================================================

  // Template Gallery UI
  'templates.title': 'Get started with a template',
  'templates.subtitle': 'Choose a template to customize or start from scratch',
  'templates.useTemplate': 'Use Template',
  'templates.preview': 'Preview',
  'templates.startFromScratch': 'Start from Scratch',
  'templates.or': 'or',
  'templates.searchPlaceholder': 'Search templates...',
  'templates.steps': '{{count}} steps',
  'templates.stepsPreview': 'Steps Preview',
  'templates.tags': 'Tags',
  'templates.delayDays': '{{days}} day delay',
  'templates.promptSenderName': 'Sender Name',
  'templates.promptSenderEmail': 'Sender Email',

  // Template complexity
  'templates.complexity.beginner': 'Beginner',
  'templates.complexity.intermediate': 'Intermediate',
  'templates.complexity.advanced': 'Advanced',

  // Empty states
  'templates.emptyState.title': 'No templates found',
  'templates.emptyState.noSearchResults': 'No templates match your search. Try different keywords.',
  'templates.emptyState.noTemplates': 'No templates available in this category.',

  // Template categories (generic)
  'templates.categories.all': 'All',
  'templates.categories.sales': 'Sales',
  'templates.categories.lending': 'Lending',
  'templates.categories.partnerships': 'Partnerships',
  'templates.categories.support': 'Support',
  'templates.categories.operations': 'Operations',

  // Sequence categories
  'templates.categories.lead-generation': 'Lead Generation',
  'templates.categories.nurture': 'Nurture',
  'templates.categories.follow-up': 'Follow-up',
  'templates.categories.re-engagement': 'Re-engagement',
  'templates.categories.referral': 'Referral',
  'templates.categories.onboarding': 'Onboarding',

  // Campaign categories
  'templates.categories.announcement': 'Announcement',
  'templates.categories.newsletter': 'Newsletter',
  'templates.categories.seasonal': 'Seasonal',
  'templates.categories.promotional': 'Promotional',
  'templates.categories.educational': 'Educational',

  // Saved search categories
  'templates.categories.pipeline': 'Pipeline',
  'templates.categories.engagement': 'Engagement',
  'templates.categories.performance': 'Performance',
  'templates.categories.compliance': 'Compliance',
  'templates.categories.reporting': 'Reporting',

  // Termsheet categories
  'templates.categories.short-term': 'Short-Term',
  'templates.categories.rental': 'Rental',
  'templates.categories.development': 'Development',
  'templates.categories.commercial': 'Commercial',

  // Sequence template names and descriptions
  'templates.sequence.newLeadNurture.name': 'New Lead Nurture',
  'templates.sequence.newLeadNurture.description': 'Welcome and educate new leads over 2 weeks with proven messaging',
  'templates.sequence.reEngagement.name': 'Re-engagement Campaign',
  'templates.sequence.reEngagement.description': 'Win back cold leads with a 3-email sequence',
  'templates.sequence.postCallFollowUp.name': 'Post-Call Follow-up',
  'templates.sequence.postCallFollowUp.description': 'Reinforce conversations after calls with timely touchpoints',
  'templates.sequence.applicationReminder.name': 'Application Reminder',
  'templates.sequence.applicationReminder.description': 'Nudge prospects to complete their applications',
  'templates.sequence.referralRequest.name': 'Referral Request',
  'templates.sequence.referralRequest.description': 'Ask satisfied clients for referrals after successful closings',

  // Campaign template names and descriptions
  'templates.campaign.productLaunch.name': 'Product Launch',
  'templates.campaign.productLaunch.description': 'Announce new products or features to your audience',
  'templates.campaign.newsletter.name': 'Monthly Newsletter',
  'templates.campaign.newsletter.description': 'Keep your audience informed with monthly updates',
  'templates.campaign.rateAnnouncement.name': 'Rate Announcement',
  'templates.campaign.rateAnnouncement.description': 'Notify clients about rate changes and opportunities',
  'templates.campaign.holiday.name': 'Holiday Greeting',
  'templates.campaign.holiday.description': 'Send warm seasonal greetings to your contacts',

  // Saved search template names and descriptions
  'templates.savedSearch.hotLeads.name': 'Hot Leads',
  'templates.savedSearch.hotLeads.description': 'High-intent leads ready for outreach',
  'templates.savedSearch.stalledApplications.name': 'Stalled Applications',
  'templates.savedSearch.stalledApplications.description': 'Applications stuck in review for over 48 hours',
  'templates.savedSearch.closingThisWeek.name': 'Closing This Week',
  'templates.savedSearch.closingThisWeek.description': 'Deals scheduled to close within 7 days',
  'templates.savedSearch.highValueDeals.name': 'High Value Deals',
  'templates.savedSearch.highValueDeals.description': 'Opportunities over $500K loan amount',
  'templates.savedSearch.needsFollowUp.name': 'Needs Follow-up',
  'templates.savedSearch.needsFollowUp.description': 'Consumers with no activity in the last 14 days',

  // Pipeline template names and descriptions
  'templates.pipeline.salesPipeline.name': 'Sales Pipeline',
  'templates.pipeline.salesPipeline.description': 'Classic sales pipeline from lead to closed deal',
  'templates.pipeline.loanOrigination.name': 'Loan Origination',
  'templates.pipeline.loanOrigination.description': 'Complete loan workflow from application to funding',
  'templates.pipeline.partnerOnboarding.name': 'Partner Onboarding',
  'templates.pipeline.partnerOnboarding.description': 'Streamlined partner onboarding workflow',

  // Termsheet template names and descriptions
  'templates.termsheet.bridge.name': 'Bridge Loan',
  'templates.termsheet.bridge.description': 'Short-term financing for acquisitions and transitions',
  'templates.termsheet.fixFlip.name': 'Fix & Flip',
  'templates.termsheet.fixFlip.description': 'Renovation financing with rehab budget draws',
  'templates.termsheet.dscr.name': 'DSCR Rental',
  'templates.termsheet.dscr.description': 'Long-term rental financing based on rental income',
  'templates.termsheet.construction.name': 'Construction',
  'templates.termsheet.construction.description': 'Ground-up or major renovation financing',
  'templates.termsheet.groundUp.name': 'Ground-Up Development',
  'templates.termsheet.groundUp.description': 'New construction financing for development projects',

  // ============================================================================
  // USER TEMPLATES (Issue #400 - Custom Template CRUD)
  // ============================================================================
  'templates.tabs.all': 'All',
  'templates.tabs.sequences': 'Sequences',
  'templates.tabs.campaigns': 'Campaigns',
  'templates.tabs.pipelines': 'Pipelines',
  'templates.tabs.savedSearches': 'Saved Searches',
  'templates.tabs.termsheets': 'Termsheets',

  'templates.columns.name': 'Name',
  'templates.columns.type': 'Type',
  'templates.columns.category': 'Category',
  'templates.columns.usage': 'Used',
  'templates.columns.source': 'Source',
  'templates.columns.updated': 'Updated',

  'templates.type.sequence': 'Sequence',
  'templates.type.campaign': 'Campaign',
  'templates.type.pipeline': 'Pipeline',
  'templates.type.saved_search': 'Saved Search',
  'templates.type.termsheet': 'Termsheet',

  'templates.fromSystem': 'From',
  'templates.custom': 'Custom',
  'templates.confirmDelete': 'Are you sure you want to delete "{{name}}"? This cannot be undone.',
  'templates.createFirst': 'Create Your First Template',

  'templates.emptyState.description':
    'Create your own templates by saving configurations from sequences, campaigns, and other features.',

  // Partnership Templates (Issue #1531 Phase 6 - partnership template picker + contract-type/signing-status labels)
  'templates.partnerships.picker.title': 'Select partnership template',
  'templates.partnerships.picker.empty': 'No configured templates for {{contractType}}. Configure one before sending.',
  'templates.partnerships.picker.configureNow': 'Configure now',
  'templates.partnerships.picker.setupBeforeUse': 'Set up before use',
  'templates.partnerships.picker.setupBeforeUseTooltip':
    'This template requires legal review before first use. Click Configure to fork-and-replace the PDF.',
  'templates.partnerships.contractType.referral': 'Referral Agreement',
  'templates.partnerships.contractType.correspondent': 'Correspondent Agreement',
  'templates.partnerships.contractType.wholesale': 'Wholesale Agreement',
  'templates.partnerships.contractType.white_label': 'White Label Agreement',
  'templates.partnerships.signingStatus.pending': 'Pending signatures',
  'templates.partnerships.signingStatus.partially_signed': 'Partially signed',
  'templates.partnerships.signingStatus.signed': 'Signed',
  'templates.partnerships.signingStatus.expired': 'Expired',

  'pageHeader.myTemplates.title': 'My Templates',
  'pageHeader.myTemplates.description': 'Manage your custom templates for sequences, campaigns, and more',

  // ============================================================================
  // INTAKE WIZARD (Issue #432 - Templated Loan Intake Wizard)
  // ============================================================================

  // Page Header
  'intake.title': 'Loan Application',
  'intake.subtitle': 'Complete your application in {{count}} easy steps',

  // Step Names
  'intake.step.investmentType': 'Investment Type',
  'intake.step.entityExperience': 'Entity & Experience',
  'intake.step.propertyAddress': 'Property Address',
  'intake.step.rateCalculator': 'Rate Calculator',
  'intake.step.compliance': 'Compliance',
  'intake.step.signingDate': 'Signing Date',
  'intake.step.guarantorDetails': 'Guarantor Details',
  'intake.step.coBorrower': 'Co-Borrower Information',
  'intake.step.ssnVerification': 'SSN Verification',

  // Field Labels
  'intake.field.investmentType': 'What type of investment is this?',
  'intake.field.entityType': 'Entity Type',
  'intake.field.experienceLevel': 'Real Estate Experience (flips in last 2 years)',
  'intake.field.loanPurpose': 'Loan Purpose',
  'intake.field.propertyAddress': 'Property Address',
  'intake.field.purchasePrice': 'Purchase Price',
  'intake.field.afterRepairValue': 'After Repair Value (ARV)',
  'intake.field.rehabBudget': 'Rehab Budget',
  'intake.field.loanAmount': 'Requested Loan Amount',
  'intake.field.preferredTerm': 'Preferred Term',
  'intake.field.preferredSigningDate': 'Preferred Signing Date',
  'intake.field.guarantorFirstName': 'Guarantor First Name',
  'intake.field.guarantorLastName': 'Guarantor Last Name',
  'intake.field.guarantorDob': 'Date of Birth',
  'intake.field.guarantorAddress': 'Guarantor Address',
  'intake.field.ssnLast4': 'Last 4 digits of SSN',
  'intake.field.fullSsn': 'Full SSN',
  'intake.field.ein': 'EIN',
  'intake.field.sensitiveData': 'Sensitive data',
  'intake.field.uploadFileAriaLabel': 'Upload file',
  'intake.field.numberOfOwners': 'Number of Entity Owners',
  'intake.field.ownerName': 'Owner {{number}} Name',
  'intake.field.ownerPercentage': 'Owner {{number}} Ownership %',
  'intake.field.ownerRole': 'Owner {{number}} Role',
  'intake.field.ownerSigningAuthority': 'Owner {{number}} Signing Authority',
  'intake.field.hasCoBorrower': 'Will there be a co-borrower?',
  'intake.field.coBorrowerEntityName': 'Co-Borrower {{number}} Entity Name',
  'intake.field.coBorrowerRole': 'Co-Borrower {{number}} Role',
  'intake.field.coBorrowerOwnershipInterest': 'Co-Borrower {{number}} Ownership Interest %',
  'intake.field.numberOfGuarantors': 'Number of Guarantors',
  'intake.field.guarantorGuaranteeType': 'Guarantor {{number}} Guarantee Type',
  'intake.field.guarantorNetWorth': 'Guarantor {{number}} Net Worth',
  'intake.field.guarantorLiquidity': 'Guarantor {{number}} Liquidity',

  // Rate Calculator
  'intake.calculator.title': 'Rate Calculator',
  'intake.calculator.estimatedRate': 'Estimated Rate',
  'intake.calculator.ltv': 'Loan-to-Value (LTV)',
  'intake.calculator.ltc': 'Loan-to-Cost (LTC)',
  'intake.calculator.monthlyPayment': 'Est. Monthly Payment',
  'intake.calculator.maxLtv': 'Max LTV',
  'intake.calculator.maxLtc': 'Max LTC',
  'intake.calculator.termMonths': '{{count}} months',

  // Compliance
  'intake.compliance.title': 'Compliance Questions',
  'intake.compliance.confirm': 'I confirm all information is accurate',
  'intake.compliance.creditAuth': 'I authorize a credit check',
  'intake.compliance.econsent': 'I agree to receive electronic communications',
  'intake.compliance.sidebarTitle': 'Terms & Compliance',
  'intake.compliance.sidebarDescription': 'Review and acknowledge requirements',
  'intake.sidebar.sections': 'Sections',

  // Validation messages (IntakeWizard)
  'intake.validation.required': '{{field}} is required',
  'intake.validation.minValue': 'Minimum value is {{min}}',
  'intake.validation.maxValue': 'Maximum value is {{max}}',
  'intake.validation.invalidFormat': 'Invalid format',

  // Auth CTA (inline prompt shown before authentication)
  'intake.authCta.title': 'Ready to continue?',
  'intake.authCta.description': 'Create an account to complete your application. Your progress has been saved.',
  'intake.authCta.createAccount': 'Create Account & Continue',
  'intake.authCta.signIn': 'Sign In',
  'intake.authCta.remainingSections': '{{count}} more sections remaining after sign in',

  // Auth Checkpoint
  'intake.auth.title': 'Secure Your Application',
  'intake.auth.description': 'Create an account to save your progress and protect your information.',
  'intake.auth.benefits.save': 'Save your progress',
  'intake.auth.benefits.resume': 'Resume anytime',
  'intake.auth.benefits.secure': 'Protect sensitive data',
  'intake.auth.benefits.track': 'Track application status',
  'intake.auth.createAccount': 'Create Account',
  'intake.auth.signIn': 'Sign In',
  'intake.auth.progressSaved': 'Your progress has been saved',

  // Wizard Navigation
  'intake.nav.continue': 'Continue',
  'intake.nav.back': 'Back',
  'intake.nav.submit': 'Submit Application',
  'intake.nav.submitting': 'Submitting...',
  'intake.nav.exit': 'Save & Exit',
  'intake.nav.step': 'Step {{current}} of {{total}}',

  // Success/Error Messages
  'intake.success.title': 'Application Submitted!',
  'intake.success.description': 'We will review your application and contact you soon.',
  'intake.error.loadTemplate': 'Unable to load the application form. Please try again.',
  'intake.error.submit': 'Failed to submit application. Please try again.',
  'intake.error.validation': 'Please correct the errors before continuing.',

  // Settings Hub (Business Portal)
  'settings.intakeTemplates': 'Intake Templates',
  'settings.intakeTemplatesDescription': 'Configure loan intake forms and application flows',
  'settings.leadScoring': 'Lead Scoring',
  'settings.leadScoringDescription': 'Configure scoring rules and qualification thresholds',

  // Intake Templates Page
  'intake.stats.activeTemplates': 'Active Templates',
  'intake.stats.outOf': 'of {{total}} available',
  'intake.stats.defaultTemplate': 'Default Template',
  'intake.stats.usedWhenNoType': 'Used when no type specified',
  'intake.stats.consumerLink': 'Consumer Application Link',
  'intake.searchPlaceholder': 'Search templates...',
  'intake.createTemplate': 'Create Template',
  'intake.edit': 'Edit',
  'intake.customize': 'Customize (creates copy)',

  'intake.columns.active': 'Active',
  'intake.columns.template': 'Template',
  'intake.columns.type': 'Type',
  'intake.columns.steps': 'Steps',
  'intake.columns.version': 'Version',
  'intake.columns.default': 'Default',
  'intake.columns.source': 'Source',

  'intake.currentDefault': 'Current default',
  'intake.setAsDefault': 'Set as default',
  'intake.preview': 'Preview',
  'intake.copyLink': 'Copy link',
  'intake.stepsOverview': 'Steps Overview',
  'intake.complianceQuestions': '{{count}} compliance questions',
  'intake.rateCalculator': 'Rate calculator',
  'intake.helpText':
    'Intake templates define the loan application wizard shown to borrowers in the Consumer Portal. Active templates are available for use; the default template is used when borrowers access /apply/loan without specifying a type.',

  'intake.editor.templateName': 'Template Name',
  'intake.editor.description': 'Description',
  'intake.editor.intakeType': 'Intake Type',

  'intake.emptyState.title': 'No templates found',
  'intake.emptyState.description': 'Intake templates define the loan application wizard flow for your borrowers.',

  // Template Types
  'intake.type.bridge': 'Bridge / Fix & Flip',
  'intake.type.construction': 'Construction',
  'intake.type.rental': 'Rental / DSCR',
  'intake.type.commercial': 'Commercial',
  'intake.type.general': 'General',
  'intake.type.partner_onboarding': 'Partner Application',

  // ========================================
  // Intake Submissions Management (Issue #475)
  // ========================================
  'intakeSubmissions.title': 'Intake Submissions',
  'intakeSubmissions.description': 'Review and process loan application submissions',
  'intakeSubmissions.searchPlaceholder': 'Search by name, email, or submission number...',
  'intakeSubmissions.viewDetails': 'View full details',

  // Filters
  'intakeSubmissions.filters.status': 'Status',
  'intakeSubmissions.filters.intakeType': 'Loan Type',

  // Status labels
  'intakeSubmissions.status.submitted': 'Submitted',
  'intakeSubmissions.status.reviewing': 'In Review',
  'intakeSubmissions.status.qualified': 'Qualified',
  'intakeSubmissions.status.converted': 'Converted',
  'intakeSubmissions.status.rejected': 'Rejected',

  // Table columns
  'intakeSubmissions.columns.number': 'Submission #',
  'intakeSubmissions.columns.applicant': 'Applicant',
  'intakeSubmissions.columns.type': 'Loan Type',
  'intakeSubmissions.columns.partnerType': 'Partner Type',
  'intakeSubmissions.columns.loanAmount': 'Loan Amount',
  'intakeSubmissions.columns.status': 'Status',
  'intakeSubmissions.columns.submitted': 'Submitted',

  // Detail panel sections
  'intakeSubmissions.sections.info': 'Submission Info',
  'intakeSubmissions.sections.status': 'Status & Assignment',
  'intakeSubmissions.sections.actions': 'Actions',

  // Fields
  'intakeSubmissions.fields.email': 'Email',
  'intakeSubmissions.fields.template': 'Template',
  'intakeSubmissions.fields.intakeType': 'Loan Type',
  'intakeSubmissions.fields.partnerType': 'Partner Type',
  'intakeSubmissions.fields.loanAmount': 'Loan Amount',
  'intakeSubmissions.fields.submittedAt': 'Submitted',
  'intakeSubmissions.fields.referralCode': 'Referral Code',
  'intakeSubmissions.fields.currentStatus': 'Current Status',
  'intakeSubmissions.fields.assignedTo': 'Assigned To',

  // Actions
  'intakeSubmissions.actions.startReview': 'Start Review',
  'intakeSubmissions.actions.approve': 'Approve',
  'intakeSubmissions.actions.markQualified': 'Mark as Qualified',
  'intakeSubmissions.actions.convertToCase': 'Convert to Loan',
  'intakeSubmissions.actions.approvePartner': 'Approve Partner',
  'intakeSubmissions.actions.reject': 'Reject',
  'intakeSubmissions.actions.assign': 'Assign',
  'intakeSubmissions.actions.copyInviteLink': 'Copy my invite link',

  // Reject Dialog
  'intakeSubmissions.rejectDialog.title': 'Reject Application',
  'intakeSubmissions.rejectDialog.reasonLabel': 'Rejection Reason',
  'intakeSubmissions.rejectDialog.reasonPlaceholder': 'Enter the reason for rejection...',
  'intakeSubmissions.rejectDialog.confirm': 'Confirm Rejection',

  // Empty state
  'intakeSubmissions.emptyState.title': 'No submissions found',
  'intakeSubmissions.emptyState.description': 'Submissions will appear here when borrowers submit loan applications.',

  // ==========================================================================
  // Empty Intake — bespoke first-day state for /loans/applications when
  // there are zero submissions AND no filters active. Mirrors the v3
  // design at pages/states-scenarios.jsx:142-205.
  // ==========================================================================
  'dashboard.emptyStates.intake.publicLink.title': 'Public application link',
  'dashboard.emptyStates.intake.publicLink.subtitle': 'Share with borrowers, partners, or embed on your website',
  'dashboard.emptyStates.intake.publicLink.copied': 'Copied',
  'dashboard.emptyStates.intake.publicLink.footnote': 'Auto-routes by loan type',
  'dashboard.emptyStates.intake.directInvite.title': 'Send a direct invite',
  'dashboard.emptyStates.intake.directInvite.subtitle': 'One borrower, attribution baked in',
  'dashboard.emptyStates.intake.directInvite.placeholder': 'borrower@email.com',
  'dashboard.emptyStates.intake.directInvite.ariaLabel': 'Borrower email address',
  'dashboard.emptyStates.intake.directInvite.send': 'Send invite',
  'dashboard.emptyStates.intake.emptyState.title': 'No submissions in the queue.',
  'dashboard.emptyStates.intake.emptyState.description':
    'When a borrower or partner submits an application — through the public link, a partner portal, or a direct invite — it lands here. Astor will pre-screen it within 30 seconds.',
  'dashboard.emptyStates.intake.emptyState.primaryCta': 'Send first invite',
  'dashboard.emptyStates.intake.emptyState.secondaryCta': 'View templates',
  'dashboard.emptyStates.intake.astorOffer.body':
    'Want me to send a test submission so you can see what the queue looks like?',
  'dashboard.emptyStates.intake.astorOffer.cta': 'Send test',

  // Detail page
  'intakeSubmissions.detail.backToList': 'Back to Applications',
  'intakeSubmissions.detail.untitled': 'Intake Submission',
  'intakeSubmissions.detail.applicantInfo': 'Applicant Information',
  'intakeSubmissions.detail.name': 'Name',
  'intakeSubmissions.detail.phone': 'Phone',
  'intakeSubmissions.detail.loanDetails': 'Loan Details',
  'intakeSubmissions.detail.propertyAddress': 'Property Address',
  'intakeSubmissions.detail.propertyState': 'Property State',
  'intakeSubmissions.detail.rate': 'Rate',
  'intakeSubmissions.detail.timeline': 'Timeline',
  'intakeSubmissions.detail.assignedAt': 'Assigned',
  'intakeSubmissions.detail.convertedAt': 'Converted',
  'intakeSubmissions.detail.convertedCase': 'Converted Case',
  'intakeSubmissions.detail.viewCase': 'View Case',
  'intakeSubmissions.detail.rejectedAt': 'Rejected',
  'intakeSubmissions.detail.rejectionReason': 'Rejection Reason',
  'intakeSubmissions.detail.completionTime': 'Form Completion Time',
  'intakeSubmissions.detail.formResponses': 'Form Responses',

  // Approval-gate actions (Dave's prod review 2026-05-15)
  'intakeSubmissions.actions.sendAgreement': 'Send Agreement',
  'intakeSubmissions.actions.markAgreementReceived': 'Mark Agreement Received',
  'intakeSubmissions.actions.configureTermsAndApprove': 'Configure Terms & Approve',
  'intakeSubmissions.statusHint':
    'Submitted → Agreement Sent → Agreement Received → Approved. Each step expects real work between clicks.',

  // Commission Terms form (qualified → convertToPartner gate)
  'intakeSubmissions.commissionTerms.title': 'Commission Terms',
  'intakeSubmissions.commissionTerms.flatFeeLabel': 'Flat fee per deal',
  'intakeSubmissions.commissionTerms.rateLabel': 'Commission rate (%)',
  'intakeSubmissions.commissionTerms.rateHelper': '2.5 = 2.5%. 10 bps = 0.10%.',
  'intakeSubmissions.commissionTerms.triggerLabel': 'Commission earned when',
  'intakeSubmissions.commissionTerms.paymentScheduleLabel': 'Payment terms',
  'intakeSubmissions.commissionTypeOptions.PERCENTAGE_LOAN': '% of Loan Amount (bps OK)',
  'intakeSubmissions.commissionTypeOptions.PERCENTAGE_OF_ORIGINATION': '% of Origination Fee',
  'intakeSubmissions.commissionTypeOptions.FLAT_AMOUNT': 'Flat Fee ($/Deal)',
  'intakeSubmissions.commissionTriggerOptions.SUBMISSION': 'Deal submitted',
  'intakeSubmissions.commissionTriggerOptions.APPROVAL': 'Deal approved',
  'intakeSubmissions.commissionTriggerOptions.FUNDING': 'Deal funded',
  'intakeSubmissions.commissionTriggerOptions.FIRST_PAYMENT': 'First payment received',
  'intakeSubmissions.paymentScheduleOptions.IMMEDIATE': 'Immediate',
  'intakeSubmissions.paymentScheduleOptions.NET_15': 'Net 15 days',
  'intakeSubmissions.paymentScheduleOptions.NET_30': 'Net 30 days',
  'intakeSubmissions.paymentScheduleOptions.NET_45': 'Net 45 days',
  'intakeSubmissions.paymentScheduleOptions.NET_60': 'Net 60 days',

  // Pending-applications banner on /partners landing
  // Callers must pick the suffix manually since translate() does not auto-resolve
  // plural forms — convention matches integrations.connections.workspaceCount_other et al.
  'partners.pendingApplications.banner': '{{count}} new partner application waiting for review',
  'partners.pendingApplications.banner_other': '{{count}} new partner applications waiting for review',
  'partners.pendingApplications.review': 'Review applications',

  // Errors
  'intakeSubmissions.error.loadFailed': 'Failed to load submissions. Please try again.',

  // ========================================
  // Application Detail v3 (Issue #1769)
  // Standalone full-page detail view for an intake submission. The page
  // shows the applicant, loan details, form responses, and a right-rail
  // for activity / messages / requests / Astor. Approval converts the
  // submission to a Loan; the page then shows a deep-link banner.
  // ========================================
  'applicationDetail.untitled': 'Intake Submission',
  'applicationDetail.backToList': 'Back to applications',
  'applicationDetail.meta.submittedAt': 'Submitted {{date}}',
  'applicationDetail.meta.viaPartner': 'via {{partnerName}} partner portal',

  // Status chips
  'applicationDetail.status.submitted': 'Submitted',
  'applicationDetail.status.reviewing': 'In review',
  'applicationDetail.status.qualified': 'Qualified',
  'applicationDetail.status.converted': 'Approved · Loan created',
  'applicationDetail.status.rejected': 'Rejected',
  'applicationDetail.status.submittedAwaitingReview': 'Submitted · awaiting review',

  // Header actions
  'applicationDetail.actions.message': 'Message',
  'applicationDetail.actions.openDocs': 'Open docs',
  'applicationDetail.actions.reject': 'Reject',
  'applicationDetail.actions.approveAndCreate': 'Approve & create loan',
  'applicationDetail.actions.auditLog': 'Audit log',
  'applicationDetail.actions.openLoan': 'Open loan {{caseNumber}}',
  'applicationDetail.actions.openLoanShort': 'Open loan',
  'applicationDetail.actions.reviewIndividually': 'Review individually',
  'applicationDetail.actions.approveCreateLoan': 'Approve · create loan',

  // Approved banner
  'applicationDetail.approvedBanner.title': 'Application approved · loan created',
  'applicationDetail.approvedBanner.body': '{{caseLink}} entered Lead stage on {{date}}.',

  // Astor pre-screen block
  'applicationDetail.astor.headline': 'Recommend approve — this is a textbook intake.',
  'applicationDetail.astor.fallbackBody':
    'Astor pre-screen has not run yet for this submission. Once the pre-screen proc is wired up, this card will show the recommendation, confidence, and tool list.',

  // Body panels
  'applicationDetail.panel.applicantInfo': 'Applicant information',
  'applicationDetail.panel.loanDetails': 'Loan details',
  'applicationDetail.panel.formResponses': 'Form responses',
  'applicationDetail.panel.customFields': 'Custom fields',
  'applicationDetail.panel.statusAssignment': 'Status & assignment',
  'applicationDetail.panel.timeline': 'Timeline',
  'applicationDetail.panel.linkedRecords': 'Linked records',

  // Field labels (uppercase mono in v3)
  'applicationDetail.field.fullName': 'Full name',
  'applicationDetail.field.email': 'Email',
  'applicationDetail.field.phone': 'Phone',
  'applicationDetail.field.address': 'Address',
  'applicationDetail.field.idVerified': 'ID verified',
  'applicationDetail.field.template': 'Template',
  'applicationDetail.field.loanType': 'Loan type',
  'applicationDetail.field.loanAmount': 'Loan amount',
  'applicationDetail.field.rate': 'Rate',
  'applicationDetail.field.ltv': 'LTV',
  'applicationDetail.field.ltc': 'LTC',
  'applicationDetail.field.property': 'Property',
  'applicationDetail.field.propertyState': 'Property state',
  'applicationDetail.field.source': 'Source',
  'applicationDetail.field.submittedTimestamp': 'Submitted timestamp',
  'applicationDetail.field.referralCode': 'Referral code',
  'applicationDetail.field.originationChannel': 'Origination channel',
  'applicationDetail.field.currentStatus': 'Current status',
  'applicationDetail.field.assignedTo': 'Assigned to',
  'applicationDetail.field.submittedBy': 'Submitted by',
  'applicationDetail.field.unassigned': 'Unassigned',

  // Submitted attachments
  'applicationDetail.attachments.heading_one': 'Submitted attachments · {{count}}',
  'applicationDetail.attachments.heading_other': 'Submitted attachments · {{count}}',
  'applicationDetail.attachments.none': 'No attachments uploaded.',

  // Timeline steps
  'applicationDetail.timeline.submitted': 'Submitted',
  'applicationDetail.timeline.astorPrescreen': 'Astor pre-screen',
  'applicationDetail.timeline.underReview': 'Under review',
  'applicationDetail.timeline.decision': 'Decision',
  'applicationDetail.timeline.approved': 'Approved · loan created',
  'applicationDetail.timeline.rejected': 'Rejected',
  'applicationDetail.timeline.enteredLeadStage': 'Loan enters Lead stage',
  'applicationDetail.timeline.pending': 'Pending',

  // Linked records
  'applicationDetail.linked.borrower': 'Borrower',
  'applicationDetail.linked.entity': 'Entity',
  'applicationDetail.linked.partner': 'Partner',
  'applicationDetail.linked.loan': 'Loan',
  'applicationDetail.linked.unknown': 'Not provided',
  'applicationDetail.linked.empty': 'No linked records yet.',

  // Context rail
  'applicationDetail.rail.activity': 'Activity',
  'applicationDetail.rail.messages': 'Messages',
  'applicationDetail.rail.requests': 'Requests',
  'applicationDetail.rail.astor': 'Astor',
  'applicationDetail.rail.activityEmpty': 'Audit activity for intake submissions is not wired up yet.',
  'applicationDetail.rail.messagesEmpty': 'No messages yet.',
  'applicationDetail.rail.requestsAssignedTo': 'Assigned to',
  'applicationDetail.rail.requestsBorrower': 'Borrower',
  'applicationDetail.rail.requestsEntities': 'Entities',
  'applicationDetail.rail.requestsGuarantors': 'Guarantors',
  'applicationDetail.rail.requestsCoBorrowers': 'Co-borrowers',
  'applicationDetail.rail.requestsEmpty': 'None on file.',
  'applicationDetail.rail.astorSummaryLabel': 'ASTOR · summary',
  'applicationDetail.rail.astorSuggestedActions': 'Suggested actions',
  'applicationDetail.rail.astorActionApprove': 'Approve & create loan',
  'applicationDetail.rail.astorActionReject': 'Reject application',
  'applicationDetail.rail.astorActionDraftReply': 'Draft reply to applicant',

  // ========================================
  // Partner Detail v3 (Issue #1769)
  // Standalone partner detail page with header, filter bar, tabs (Details,
  // Referral Codes, Recent Referrals, Commissions, Loans, Activity,
  // Attachments). Uses ContextRail for the right rail (Activity, Messages,
  // Requests, Astor).
  // ========================================

  // Header / meta strip
  'partnerDetail.backToList': 'Back to partners',
  'partnerDetail.meta.onboarded': 'Onboarded {{date}}',
  'partnerDetail.meta.agreementId': '{{id}}',
  'partnerDetail.actions.openPortal': 'Open partner portal',
  'partnerDetail.actions.viewAgreement': 'View agreement',
  'partnerDetail.actions.message': 'Message',
  'partnerDetail.actions.newReferral': 'New referral',

  // Viewing-data-for filter bar
  'partnerDetail.filter.label': 'Viewing data for',
  'partnerDetail.filter.allReferrals': 'All referrals',
  'partnerDetail.filter.range.ytd': 'YTD {{year}}',
  'partnerDetail.filter.range.last30': 'Last 30d',
  'partnerDetail.filter.range.thisWeek': 'This week',

  // Tabs
  'partnerDetail.tabs.details': 'Details',
  'partnerDetail.tabs.referralCodes': 'Referral Codes',
  'partnerDetail.tabs.recentReferrals': 'Recent Referrals',
  'partnerDetail.tabs.commissions': 'Commissions',
  'partnerDetail.tabs.loans': 'Loans',
  'partnerDetail.tabs.activity': 'Activity',
  'partnerDetail.tabs.attachments': 'Attachments',

  // KPI strip (Details tab)
  'partnerDetail.kpi.referrals.label': 'Referrals',
  'partnerDetail.kpi.referrals.sub': 'MTD · +{{count}} wow',
  'partnerDetail.kpi.referrals.empty': 'No referrals yet',
  'partnerDetail.kpi.conversions.label': 'Conversions',
  'partnerDetail.kpi.conversions.sub': '{{rate}}% rate',
  'partnerDetail.kpi.conversionRate.label': 'Conversion rate',
  'partnerDetail.kpi.conversionRate.sub': 'vs. {{avg}}% portfolio avg',
  'partnerDetail.kpi.conversionRate.subNoAvg': 'last 30 days',
  'partnerDetail.kpi.totalEarnings.label': 'Total earnings',
  'partnerDetail.kpi.totalEarnings.sub': 'MTD paid out',
  'partnerDetail.kpi.avgLoanSize.label': 'Avg loan size',
  'partnerDetail.kpi.avgLoanSize.sub': '+{{pct}}% yoy',
  'partnerDetail.kpi.avgLoanSize.empty': 'No funded loans yet',

  // Astor trend block (Details tab)
  'partnerDetail.astor.label': 'Astor recommends',
  'partnerDetail.astor.confidence': 'conf {{pct}}%',
  'partnerDetail.astor.headline': 'Top-quartile partner — and trending up.',
  'partnerDetail.astor.body':
    'Conversion is up MoM. Average loan size is climbing toward your tier-A pricing threshold. Their last few referrals were all the same loan type in the same MSA — well within the credit box.',
  'partnerDetail.astor.cta': 'Propose tier-A pricing',
  'partnerDetail.astor.ctaDisabledTooltip': 'Pricing tier proposal endpoint not yet wired up.',

  // Partner details panel
  'partnerDetail.panels.partnerDetails': 'Partner details',
  'partnerDetail.panels.commissionTerms': 'Commission terms',
  'partnerDetail.panels.warehouseLine': 'Warehouse line',
  'partnerDetail.panels.teamMembers': 'Team members',

  // Partner details fields
  'partnerDetail.field.status': 'Status',
  'partnerDetail.field.partnerType': 'Partner type',
  'partnerDetail.field.agreementType': 'Agreement type',
  'partnerDetail.field.company': 'Company',
  'partnerDetail.field.primaryContact': 'Primary contact',
  'partnerDetail.field.phone': 'Phone',
  'partnerDetail.field.joined': 'Joined',
  'partnerDetail.field.lastActivity': 'Last activity',
  'partnerDetail.field.commissionType': 'Commission type',
  'partnerDetail.field.commissionRate': 'Commission rate',
  'partnerDetail.field.trigger': 'Trigger',
  'partnerDetail.field.paymentTerms': 'Payment terms',
  'partnerDetail.field.exclusivity': 'Exclusivity',
  'partnerDetail.field.territory': 'Territory',
  'partnerDetail.field.tier': 'Tier',
  'partnerDetail.field.nextReview': 'Next review',
  'partnerDetail.field.empty': '—',

  // Warehouse line snapshot
  'partnerDetail.warehouse.utilizationFooter': 'capacity {{capacity}} · utilized {{utilized}} · headroom {{headroom}}',
  'partnerDetail.warehouse.creditBoxHeading': 'Credit box snapshot',
  'partnerDetail.warehouse.maxLtarv': 'Max LTARV',
  'partnerDetail.warehouse.maxLtc': 'Max LTC',
  'partnerDetail.warehouse.ficoFloor': 'FICO floor',
  'partnerDetail.warehouse.experienceFloor': 'Experience floor',
  'partnerDetail.warehouse.empty': 'No linked warehouse line.',

  // Referral codes tab
  'partnerDetail.codes.heading': 'Referral codes',
  'partnerDetail.codes.activeCount': '{{count}} active',
  'partnerDetail.codes.generate': 'Generate code',
  'partnerDetail.codes.col.code': 'Code',
  'partnerDetail.codes.col.campaign': 'Campaign',
  'partnerDetail.codes.col.tier': 'Tier',
  'partnerDetail.codes.col.uses': 'Uses',
  'partnerDetail.codes.col.conv': 'Conv',
  'partnerDetail.codes.col.earnings': 'Earnings',
  'partnerDetail.codes.col.status': 'Status',
  'partnerDetail.codes.tier': 'Tier {{tier}}',
  'partnerDetail.codes.statusOn': 'on',
  'partnerDetail.codes.statusOff': 'off',
  'partnerDetail.codes.empty': 'No tracking codes yet. Generate the first one.',
  'partnerDetail.codes.showArchived': 'Show archived',
  'partnerDetail.codes.hideArchived': 'Hide archived',
  'partnerDetail.codes.exportSelected': 'Export QR codes ({{count}})',
  'partnerDetail.codes.unnamed': 'Untitled code',

  // Recent referrals tab
  'partnerDetail.referrals.col.application': 'Application #',
  'partnerDetail.referrals.col.borrower': 'Borrower',
  'partnerDetail.referrals.col.loanType': 'Loan type',
  'partnerDetail.referrals.col.amount': 'Amount',
  'partnerDetail.referrals.col.status': 'Status',
  'partnerDetail.referrals.col.code': 'Code',
  'partnerDetail.referrals.col.date': 'Date',
  'partnerDetail.referrals.empty': 'No referrals yet from this partner.',

  // Commissions tab
  'partnerDetail.commissions.kpi.mtdPaid': 'MTD paid',
  'partnerDetail.commissions.kpi.mtdPaid.sub': '{{count}} payments',
  'partnerDetail.commissions.kpi.pending': 'Pending',
  'partnerDetail.commissions.kpi.pending.sub': '{{count}} funded · 30d clock',
  'partnerDetail.commissions.kpi.ytdPaid': 'YTD paid',
  'partnerDetail.commissions.kpi.ytdPaid.sub': '{{count}} payments',
  'partnerDetail.commissions.kpi.avgPerDeal': 'Avg per deal',
  'partnerDetail.commissions.kpi.avgPerDeal.sub': 'across all tiers',
  'partnerDetail.commissions.col.payment': 'Payment #',
  'partnerDetail.commissions.col.loan': 'Loan',
  'partnerDetail.commissions.col.fundedAmt': 'Funded amt',
  'partnerDetail.commissions.col.rate': 'Rate',
  'partnerDetail.commissions.col.earned': 'Earned',
  'partnerDetail.commissions.col.status': 'Status',
  'partnerDetail.commissions.empty': 'No commissions yet for this partner.',
  'partnerDetail.commissions.bulkApprove': 'Approve selected ({{count}})',
  'partnerDetail.commissions.exportCsv': 'Export CSV',

  // Loans tab (cases referred by this partner)
  'partnerDetail.loans.empty': 'No loans referred by this partner yet.',
  'partnerDetail.loans.gapNotice':
    'Partner-referred loan list is not wired up yet — backend gap filed (cases.list does not accept referralPartnerId).',
  'partnerDetail.loans.col.loan': 'Loan #',
  'partnerDetail.loans.col.borrower': 'Borrower',
  'partnerDetail.loans.col.stage': 'Stage',
  'partnerDetail.loans.col.amount': 'Amount',
  'partnerDetail.loans.col.date': 'Created',

  // Rail (ContextRail) — partner detail uses the same shape as application detail
  'partnerDetail.rail.activity': 'Activity',
  'partnerDetail.rail.messages': 'Messages',
  'partnerDetail.rail.requests': 'Requests',
  'partnerDetail.rail.astor': 'Astor',
  'partnerDetail.rail.activityEmpty': 'No recent activity for this partner.',
  'partnerDetail.rail.messagesEmpty': 'No messages yet.',
  'partnerDetail.rail.requestsAssignedTo': 'Owner',
  'partnerDetail.rail.requestsLinked': 'Linked',
  'partnerDetail.rail.requestsBorrowers': 'Borrowers',
  'partnerDetail.rail.requestsEntities': 'Entities',
  'partnerDetail.rail.requestsGuarantors': 'Guarantors',
  'partnerDetail.rail.requestsCoBorrowers': 'Co-borrowers',
  'partnerDetail.rail.requestsEmpty': 'None on file.',
  'partnerDetail.rail.astorSummaryLabel': 'ASTOR · summary',
  'partnerDetail.rail.astorSuggestedActions': 'Suggested actions',
  'partnerDetail.rail.astorActionPropose': 'Propose tier-A pricing',
  'partnerDetail.rail.astorActionMessage': 'Draft check-in to partner',
  'partnerDetail.rail.astorActionReview': 'Review scorecard',

  // ========================================
  // Partner Detail V2 (v3-design-pass-4 PR-1 — bidirectional, attribution-aware)
  // ========================================
  // Header chips & meta
  'partnerDetailV2.chip.bidirectional': 'White-Label · Bidirectional',
  'partnerDetailV2.chip.referralOnly': 'Referral · One-way',
  'partnerDetailV2.actions.openPortal': 'Open partner portal',
  'partnerDetailV2.actions.agreement': 'Agreement',
  'partnerDetailV2.actions.message': 'Message',
  'partnerDetailV2.actions.newReferral': 'New referral',

  // Tabs
  'partnerDetailV2.tabs.overview': 'Overview',
  'partnerDetailV2.tabs.referralLinks': 'Referral Links',
  'partnerDetailV2.tabs.commissions': 'Commissions',
  'partnerDetailV2.tabs.activity': 'Activity',
  'partnerDetailV2.tabs.agreement': 'Agreement',
  'partnerDetailV2.tabs.team': 'Team',

  // Overview — bidirectional explainer
  'partnerDetailV2.explainer.title': 'How this partnership works',
  'partnerDetailV2.explainer.chip': 'Bidirectional',
  'partnerDetailV2.explainer.outbound.eyebrow': 'OUTBOUND · YOU → {{name}}',
  'partnerDetailV2.explainer.outbound.bodyPrefix': 'You send your borrowers to',
  'partnerDetailV2.explainer.outbound.bodyMiddle': 'to apply. When their loan funds,',
  'partnerDetailV2.explainer.outbound.bodyHighlight': 'you earn',
  'partnerDetailV2.explainer.outbound.bodySuffix': 'a commission.',
  'partnerDetailV2.explainer.outbound.yourRate': 'Your rate:',
  'partnerDetailV2.explainer.outbound.mtdEarned': 'MTD earned:',
  'partnerDetailV2.explainer.inbound.eyebrow': 'INBOUND · {{name}} → YOU',
  'partnerDetailV2.explainer.inbound.bodyPrefix': '{{name}} sends their borrowers to',
  'partnerDetailV2.explainer.inbound.bodyMiddleSelf': 'you',
  'partnerDetailV2.explainer.inbound.bodyMiddle': 'to apply. When the loan funds,',
  'partnerDetailV2.explainer.inbound.bodyHighlight': 'you pay',
  'partnerDetailV2.explainer.inbound.bodySuffix': '{{name}} a commission.',
  'partnerDetailV2.explainer.inbound.theirRate': 'Their rate:',
  'partnerDetailV2.explainer.inbound.mtdPaid': 'MTD paid:',
  'partnerDetailV2.explainer.connector': 'and',
  'partnerDetailV2.explainer.unidirectionalNotice':
    'This is a one-way referral partnership. The inbound leg is not enabled.',

  // Overview — KPIs
  'partnerDetailV2.kpi.netMtd.label': 'NET position · MTD',
  'partnerDetailV2.kpi.netMtd.subPaidMore': 'paid > earned',
  'partnerDetailV2.kpi.netMtd.subEarnedMore': 'earned > paid',
  'partnerDetailV2.kpi.netMtd.subFlat': 'balanced',
  'partnerDetailV2.kpi.loansFunded.label': 'Loans funded · 90d',
  'partnerDetailV2.kpi.loansFunded.sub': '{{inbound}} inbound · {{outbound}} outbound',
  'partnerDetailV2.kpi.conversion.label': 'Conversion · 30d',
  'partnerDetailV2.kpi.conversion.subWithAvg': 'vs. {{avg}}% portfolio avg',
  'partnerDetailV2.kpi.conversion.subNoAvg': 'last 30 days',
  'partnerDetailV2.kpi.avgLoan.label': 'Avg loan size',
  'partnerDetailV2.kpi.avgLoan.sub': '{{delta}} yoy',
  'partnerDetailV2.kpi.avgLoan.subEmpty': 'No funded loans yet',
  'partnerDetailV2.kpi.activeEmployees.label': 'Active employees',
  'partnerDetailV2.kpi.activeEmployees.sub': 'with attribution',

  // Overview — Astor
  'partnerDetailV2.astor.headline': 'Strong partnership — and skewing inbound.',
  'partnerDetailV2.astor.body':
    'Conversion is up MoM, and inbound deals close faster than your team average. Worth proposing tier-A pricing on the inbound leg.',
  'partnerDetailV2.astor.cta': 'Propose tier-A pricing',
  'partnerDetailV2.astor.ctaDisabledTooltip': 'Pricing tier proposal endpoint not yet wired up.',
  'partnerDetailV2.astor.gapNotice': 'Astor analysis pending — backend gap to fill (partner.scorecard).',

  // Overview — Partner details panel
  'partnerDetailV2.panel.details.title': 'Partner details',
  'partnerDetailV2.panel.details.status': 'Status',
  'partnerDetailV2.panel.details.direction': 'Direction',
  'partnerDetailV2.panel.details.directionBi': 'Bidirectional · Two-way',
  'partnerDetailV2.panel.details.directionOne': 'Referral · One-way',
  'partnerDetailV2.panel.details.company': 'Company',
  'partnerDetailV2.panel.details.primaryContact': 'Primary contact',
  'partnerDetailV2.panel.details.phone': 'Phone',
  'partnerDetailV2.panel.details.territory': 'Territory',
  'partnerDetailV2.panel.details.tier': 'Tier',
  'partnerDetailV2.panel.details.nextReview': 'Next review',

  // Overview — Commission terms panel
  'partnerDetailV2.panel.terms.title': 'Commission terms',
  'partnerDetailV2.panel.terms.outboundRate': 'OUTBOUND rate (you earn)',
  'partnerDetailV2.panel.terms.inboundRate': 'INBOUND rate (you pay)',
  'partnerDetailV2.panel.terms.trigger': 'Trigger',
  'partnerDetailV2.panel.terms.paymentTerms': 'Payment terms',

  // Referral Links tab
  'partnerDetailV2.links.banner':
    'Each link includes company + employee attribution. When a borrower applies through a specific employee link, that employee earns the commission credit alongside their company.',
  'partnerDetailV2.links.copyDefaults': 'Copy company defaults',
  'partnerDetailV2.links.outbound.title': 'Outbound · Your team → {{name}}',
  'partnerDetailV2.links.outbound.rateChip': 'YOU EARN {{rate}}',
  'partnerDetailV2.links.outbound.description':
    "Share these with your borrowers. They will land on {{name}}'s intake form. A funded deal means your team earns commission with employee-level attribution.",
  'partnerDetailV2.links.outbound.cta': 'Generate link for employee',
  'partnerDetailV2.links.outbound.col.employee': 'Your employee',
  'partnerDetailV2.links.outbound.col.earned': 'You earned',
  'partnerDetailV2.links.inbound.title': "Inbound · {{name}}'s team → You",
  'partnerDetailV2.links.inbound.rateChip': 'YOU PAY {{rate}}',
  'partnerDetailV2.links.inbound.description':
    "{{name}}'s team shares these with their borrowers. They will land on your intake form. A funded deal means {{name}} earns commission from you, attributed to the specific employee who shared.",
  'partnerDetailV2.links.inbound.cta': 'View shareable for them',
  'partnerDetailV2.links.inbound.col.employee': '{{name}} employee',
  'partnerDetailV2.links.inbound.col.owe': 'You owe',
  'partnerDetailV2.links.col.link': 'Link',
  'partnerDetailV2.links.col.campaign': 'Campaign',
  'partnerDetailV2.links.col.sent': 'Sent',
  'partnerDetailV2.links.col.funded': 'Funded',
  'partnerDetailV2.links.companyDefault': 'Company default',
  'partnerDetailV2.links.companyDefaultRole': '(no employee attribution)',
  'partnerDetailV2.links.topOriginatorAria': 'Top originator',
  'partnerDetailV2.links.empty.outbound':
    'No outbound links yet. Generate a per-employee link to start tracking attribution.',
  'partnerDetailV2.links.empty.inbound':
    'No inbound links yet. {{name}} has not generated any links pointing at your intake form.',
  'partnerDetailV2.links.gapNotice':
    'Per-employee attribution is not wired up yet — backend gap filed (partners.listReferralLinks with direction).',
  'partnerDetailV2.links.copy': 'Copy link',
  'partnerDetailV2.links.more': 'More actions',
  // Disabled-action explainers (link/commission generation, copy-defaults,
  // filters and the row "more actions" menu are not wired yet).
  'partnerDetailV2.links.copyDefaults.unavailable':
    'Link generation flow not available yet (backend gap: partners.generateLink).',
  'partnerDetailV2.links.outbound.cta.unavailable': 'Generating a per-employee outbound link is not available yet.',
  'partnerDetailV2.links.inbound.cta.unavailable': 'Generating a per-employee inbound link is not available yet.',
  'partnerDetailV2.links.more.unavailable': 'No row actions available yet.',
  'partnerDetailV2.commissions.filter.unavailable': 'Commission filtering is not available yet.',
  // V1 lifecycle copy-failure surfaced via GlassAlert (i18n key referenced
  // from the partner detail page).
  'partnerDetailV2.copy.failed': 'Failed to copy to clipboard.',
  'partnerDetailV2.action.failed': 'Action failed: {{error}}',
  'partnerDetailV2.action.failed.unknown': 'Unknown error',
  // Header CTAs (Open portal / Agreement / Message / New referral) — flows
  // not implemented yet on the V2 page.
  'partnerDetailV2.actions.openPortal.unavailable': "Opening the partner's portal is not available yet.",
  'partnerDetailV2.actions.agreement.unavailable': 'Agreement viewer is not available yet.',
  'partnerDetailV2.actions.message.unavailable': 'Messaging is not available yet.',
  'partnerDetailV2.actions.newReferral.unavailable': 'New-referral flow is not available yet.',

  // Commissions tab (per-partner, both-directions)
  'partnerDetailV2.commissions.kpi.earnedMtd.label': 'You earned · MTD',
  'partnerDetailV2.commissions.kpi.earnedMtd.sub_one': '{{count}} funded loan',
  'partnerDetailV2.commissions.kpi.earnedMtd.sub_other': '{{count}} funded loans',
  'partnerDetailV2.commissions.kpi.earnedMtd.sub': '{{count}} funded loans',
  'partnerDetailV2.commissions.kpi.paidMtd.label': 'You paid · MTD',
  'partnerDetailV2.commissions.kpi.paidMtd.sub_one': '{{count}} funded loan',
  'partnerDetailV2.commissions.kpi.paidMtd.sub_other': '{{count}} funded loans',
  'partnerDetailV2.commissions.kpi.paidMtd.sub': '{{count}} funded loans',
  'partnerDetailV2.commissions.kpi.netPeriod.label': 'Net (this period)',
  'partnerDetailV2.commissions.kpi.netPeriod.sub.paid': 'paid > earned',
  'partnerDetailV2.commissions.kpi.netPeriod.sub.earned': 'earned > paid',
  'partnerDetailV2.commissions.kpi.netPeriod.sub.flat': 'balanced',
  'partnerDetailV2.commissions.kpi.pendingPayout.label': 'Pending payout',
  'partnerDetailV2.commissions.kpi.pendingPayout.sub_one': '{{count}} loan · 30d clock',
  'partnerDetailV2.commissions.kpi.pendingPayout.sub_other': '{{count}} loans · 30d clock',
  'partnerDetailV2.commissions.kpi.pendingPayout.sub': '{{count}} loans · 30d clock',
  'partnerDetailV2.commissions.tableTitle': 'All commission events',
  'partnerDetailV2.commissions.countChip_one': '{{count}} event · 90d',
  'partnerDetailV2.commissions.countChip_other': '{{count}} events · 90d',
  'partnerDetailV2.commissions.countChip': '{{count}} events · 90d',
  'partnerDetailV2.commissions.filter.direction': 'All directions',
  'partnerDetailV2.commissions.filter.status': 'All statuses',
  'partnerDetailV2.commissions.col.direction': 'Direction',
  'partnerDetailV2.commissions.col.loan': 'Loan',
  'partnerDetailV2.commissions.col.attribution': 'Attribution',
  'partnerDetailV2.commissions.col.funded': 'Funded',
  'partnerDetailV2.commissions.col.amount': 'Amount',
  'partnerDetailV2.commissions.col.status': 'Status',
  'partnerDetailV2.commissions.col.trigger': 'Trigger',
  'partnerDetailV2.commissions.direction.earned': '↗ EARNED',
  'partnerDetailV2.commissions.direction.paid': '↙ PAID',
  'partnerDetailV2.commissions.attribution.your': 'your:',
  'partnerDetailV2.commissions.attribution.their': 'their:',
  'partnerDetailV2.commissions.attribution.none': '—',
  'partnerDetailV2.commissions.empty': 'No commission events for this partner yet.',
  'partnerDetailV2.commissions.gapNotice':
    'Bidirectional commission flow is not wired up yet — backend gap filed (commissions.listByPartner with both-side attribution).',

  // Stub tabs — Activity / Agreement / Team
  'partnerDetailV2.stub.title': 'Coming in next design pass',
  'partnerDetailV2.stub.activity':
    'Per-partner activity timeline (referrals, commission events, agreement changes) will land in the v3 design pass #5.',
  'partnerDetailV2.stub.agreement':
    'Inline agreement viewer with signed-doc thumbnails and term diffs will land in the v3 design pass #5.',
  'partnerDetailV2.stub.team': 'Team roster with per-person attribution stats will land in the v3 design pass #5.',

  // ========================================
  // Template Purpose Configuration (Issue #475)
  // ========================================
  'intake.purposeConfig.title': 'Default Templates by Loan Type',
  'intake.purposeConfig.description': 'Set which template is used by default for each loan type',
  'intake.purposeConfig.purpose.bridge': 'Bridge / Fix & Flip',
  'intake.purposeConfig.purpose.construction': 'Construction',
  'intake.purposeConfig.purpose.rental': 'Rental / DSCR',
  'intake.purposeConfig.purpose.commercial': 'Commercial',
  'intake.purposeConfig.purpose.general': 'General',
  'intake.purposeConfig.setDefault': 'Set as default for {{purpose}}',
  'intake.purposeConfig.currentDefault': 'Current default for {{purpose}}',
  'intake.purposeConfig.noDefault': 'No default set',
  'intake.purposeConfig.updated': 'Default template updated for {{purpose}}',

  // Investment Type Configuration (Issue #526)
  'intake.investmentTypeConfig.title': 'Supported Investment Types',
  'intake.investmentTypeConfig.description':
    'Configure which loan product types appear on your intake forms. Consumers will only see enabled options.',
  'intake.investmentTypeConfig.type.bridge': 'Bridge / Fix & Flip',
  'intake.investmentTypeConfig.type.bridge.description': 'Short-term financing for property flipping',
  'intake.investmentTypeConfig.type.construction': 'Ground-Up Construction',
  'intake.investmentTypeConfig.type.construction.description': 'Financing for new construction projects',
  'intake.investmentTypeConfig.type.rental': 'DSCR Rental',
  'intake.investmentTypeConfig.type.rental.description': 'Debt Service Coverage Ratio loans for rental properties',
  'intake.investmentTypeConfig.type.commercial': 'Commercial',
  'intake.investmentTypeConfig.type.commercial.description': 'Loans for commercial real estate investments',
  'intake.investmentTypeConfig.generalOption.title': '"Not Sure" Option',
  'intake.investmentTypeConfig.generalOption.description':
    'When enabled, consumers can select "Not Sure - I\'d like to speak with an advisor" for general consultation.',
  'intake.investmentTypeConfig.generalOption.label': 'Always show "Not Sure" option',
  'intake.investmentTypeConfig.updated': 'Investment type configuration updated',
  'intake.investmentTypeConfig.validation.minOneRequired': 'At least one investment type must be enabled',
  'intake.investmentTypeConfig.enabled': 'Enabled',
  'intake.investmentTypeConfig.disabled': 'Disabled',

  // Partner Template Defaults (Issue #475)
  'intake.partnerDefaults.title': 'Partner Template Defaults',
  'intake.partnerDefaults.description': 'Set custom intake templates for specific partners',
  'intake.partnerDefaults.partnerId': 'Partner ID',
  'intake.partnerDefaults.template': 'Default Template',
  'intake.partnerDefaults.setDefault': 'Set Default',
  'intake.partnerDefaults.remove': 'Remove',
  'intake.partnerDefaults.noDefaults': 'No partner-specific defaults configured',
  'intake.partnerDefaults.addNew': 'Add Partner Default',
  'intake.partnerDefaults.updated': 'Partner template default updated',
  'intake.partnerDefaults.removed': 'Partner template default removed',

  // Lead Conversion Pre-fill (Issue #475)
  'intake.leadConversion.title': 'Convert Lead to Application',
  'intake.leadConversion.description': 'Generate a pre-filled intake link from lead data',
  'intake.leadConversion.generateLink': 'Generate Application Link',
  'intake.leadConversion.linkGenerated': 'Application link generated',
  'intake.leadConversion.linkCopied': 'Link copied to clipboard',
  'intake.leadConversion.selectTemplate': 'Select intake template (optional)',
  'intake.leadConversion.useDefault': 'Use default template',
  'intake.leadConversion.expiresIn': 'Link expires in {{hours}} hours',
  'intake.leadConversion.prefillFields': 'Pre-filled fields',
  'intake.leadConversion.noLeadData': 'No lead data available for pre-fill',
  'intake.leadConversion.invalidToken': 'Invalid or expired pre-fill token',
  'intake.leadConversion.tokenExpired': 'This application link has expired',
  'intake.leadConversion.sendToApplicant': 'Send to Applicant',
  'intake.leadConversion.openLink': 'Open Link',
  'intake.leadConversion.emailSent': 'Application link sent to {{email}}',

  // Partner Referral API (Issue #475)
  'intake.partnerReferral.title': 'Partner Referrals',
  'intake.partnerReferral.description': 'Referrals submitted by partners via API',
  'intake.partnerReferral.status.pending': 'Pending',
  'intake.partnerReferral.status.viewed': 'Viewed',
  'intake.partnerReferral.status.started': 'Started',
  'intake.partnerReferral.status.submitted': 'Submitted',
  'intake.partnerReferral.status.converted': 'Converted',
  'intake.partnerReferral.status.expired': 'Expired',
  'intake.partnerReferral.columns.referralId': 'Referral ID',
  'intake.partnerReferral.columns.partner': 'Partner',
  'intake.partnerReferral.columns.borrower': 'Borrower',
  'intake.partnerReferral.columns.loanType': 'Loan Type',
  'intake.partnerReferral.columns.status': 'Status',
  'intake.partnerReferral.columns.createdAt': 'Created',
  'intake.partnerReferral.columns.expiresAt': 'Expires',
  'intake.partnerReferral.externalRef': 'External Reference',
  'intake.partnerReferral.viewApplication': 'View Application',
  'intake.partnerReferral.resendLink': 'Resend Link',
  'intake.partnerReferral.linkExpired': 'Link has expired',
  'intake.partnerReferral.noReferrals': 'No partner referrals yet',

  // ============================================================================
  // LOAN APPLICATION (Consumer Portal)
  // ============================================================================
  'loan.title': 'Loan Application',
  'loan.type.purchase': 'Purchase',
  'loan.type.refinance': 'Refinance',
  'loan.type.cashOut': 'Cash-Out Refinance',
  'loan.type.bridge': 'Bridge Loan',
  'loan.propertyType': 'Property Type',
  'loan.propertyType.singleFamily': 'Single Family',
  'loan.propertyType.multiFamily': 'Multi-Family (2-4 units)',
  'loan.propertyType.multifamily': 'Multifamily',
  'loan.propertyType.commercial': 'Commercial',
  'loan.propertyType.mixedUse': 'Mixed Use',
  'loan.amount': 'Loan Amount',
  'loan.assetType': 'Asset Type',
  'loan.liabilityType': 'Liability Type',
  'loan.creditScoreRange': 'Credit Score Range',
  'loan.recentApplications': 'Recent Applications',

  // ============================================================================
  // EMPLOYMENT (Consumer Portal)
  // ============================================================================
  'employment.status': 'Employment Status',
  'employment.status.employed': 'Employed',
  'employment.status.selfEmployed': 'Self-Employed',
  'employment.status.retired': 'Retired',
  'employment.status.investor': 'Real Estate Investor',
  'employment.financialProfile': 'Financial Profile',
  'employment.description': 'Provide your employment information to help us understand your income sources.',
  'employment.monthlyIncome': 'Monthly Income',
  'employment.employerName': 'Employer Name',
  'employment.jobTitle': 'Job Title',
  'employment.yearsAtCurrentJob': 'Years at Current Job',
  'employment.businessName': 'Business Name',
  'employment.businessType': 'Business Type',
  'employment.yearsInBusiness': 'Years in Business',

  // ============================================================================
  // ASSETS (Consumer Portal - Financial Profile)
  // ============================================================================
  'assets.title': 'Assets',
  'assets.description': 'List all your assets including bank accounts, investments, and property.',
  'assets.empty': 'No assets added yet. Click the button below to add your first asset.',
  'assets.assetNumber': 'Asset {{number}}',
  'assets.removeAsset': 'Remove asset',
  'assets.assetType': 'Asset Type',
  'assets.institution': 'Institution / Description',
  'assets.institutionPlaceholder': 'e.g., Chase Bank, Vanguard, Property Address',
  'assets.approximateValue': 'Approximate Value',
  'assets.additionalNotes': 'Additional Notes (Optional)',
  'assets.notesPlaceholder': 'e.g., Joint account, Primary residence',
  'assets.addAsset': 'Add Asset',
  'assets.maxAllowed': 'Maximum of {{max}} assets allowed.',

  // ============================================================================
  // LIABILITIES (Consumer Portal - Financial Profile)
  // ============================================================================
  'liabilities.title': 'Liabilities',
  'liabilities.description': 'List all your debts and monthly payment obligations.',
  'liabilities.empty': 'No liabilities added yet. Click the button below to add your first liability.',
  'liabilities.liabilityNumber': 'Liability {{number}}',
  'liabilities.removeLiability': 'Remove liability',
  'liabilities.liabilityType': 'Liability Type',
  'liabilities.creditor': 'Creditor / Lender',
  'liabilities.creditorPlaceholder': 'e.g., Wells Fargo, Capital One',
  'liabilities.monthlyPayment': 'Monthly Payment',
  'liabilities.totalBalance': 'Total Balance',
  'liabilities.interestRate': 'Interest Rate (Optional)',
  'liabilities.addLiability': 'Add Liability',
  'liabilities.maxAllowed': 'Maximum of {{max}} liabilities allowed.',

  // ============================================================================
  // FINANCIAL PROFILE (Consumer Portal)
  // ============================================================================
  'financial.profile.title': 'Financial Profile',
  'financial.profile.tabs.employment': 'Employment',
  'financial.profile.tabs.assets': 'Assets',
  'financial.profile.tabs.liabilities': 'Liabilities',
  // Bare keys needed because PageTabBar double-translates: t(tab.label) where label is already t()-resolved
  'Employment': 'Employment',
  'Assets': 'Assets',
  'Liabilities': 'Liabilities',
  'financial.profile.failedToSave': 'Failed to save changes: {{error}}',
  'financial.profile.lastUpdated': 'Last updated: {{date}}',

  // ============================================================================
  // COMMISSION (Consumer Portal - Partner Features)
  // ============================================================================
  'commission.title': 'Commissions',
  'commission.statement': 'Commission Statement',
  'commission.loanAmount': 'Loan Amount',
  'commission.rate': 'Rate',
  'commission.commission': 'Commission',
  'commission.earned': 'Earned',
  'commission.paid': 'Paid',
  'commission.previousBalance': 'Previous Balance',
  'commission.commissionsEarned': 'Commissions Earned',
  'commission.payments': 'Payments',
  'commission.currentBalance': 'Current Balance',

  // ============================================================================
  // CASE PAGES (Consumer Portal)
  // ============================================================================
  'case.loadError': 'Failed to load application. Please try again.',
  'case.application': 'Application',
  'case.documents': 'Documents',
  'case.documentsRequiringSignature': 'Documents Requiring Your Signature',
  'case.noDocumentsUploaded': 'No documents uploaded yet',
  'case.progress': 'Progress',
  'case.needHelp': 'Need Help?',
  'case.messageDescription': 'Our support team is here to help you with any questions about your application.',
  'case.documentCategory': 'Document Category',
  'case.documentChecklist': 'Document Checklist',
  'case.requestType': 'Request Type',
  'case.chooseFiles': 'Choose Files',
  'case.columns.type': 'Type',
  'case.columns.status': 'Status',
  'case.columns.created': 'Created',
  'case.columns.lastUpdate': 'Last Update',
  // Issue #475: Application-Proposal linking
  'case.relatedProposals': 'Related Proposals',
  'case.viewProposal': 'View Proposal',

  // Consumer Portal — Case Detail (Loan Details, Closing, Parties, SLA, Documents)
  'case.loanDetails': 'Loan Details',
  'case.loanType': 'Loan Type',
  'case.loanAmount': 'Loan Amount',
  'case.interestRate': 'Interest Rate',
  'case.ltv': 'Loan-to-Value',
  'case.propertyAddress': 'Property Address',
  'case.propertyType': 'Property Type',
  'case.purchasePrice': 'Purchase Price',
  'case.loanTerm': 'Loan Term',
  'case.arv': 'After Repair Value (ARV)',
  'case.rehabBudget': 'Rehab Budget',
  'case.exitStrategy': 'Exit Strategy',
  'case.estimatedMonthly': 'Est. Monthly Payment',
  'case.yourLoanOfficer': 'Your Loan Officer',
  'case.assignedTo': 'Assigned To',
  'case.dataRoom': 'Data Room',
  'case.closing': 'Closing',
  'case.parties': 'Parties',
  'case.closingSchedule': 'Closing Schedule',
  'case.closingChecklist': 'Closing Checklist',
  'case.wireInstructions': 'Wire Instructions',
  'case.closingReadOnly': 'Your closing details are managed by your loan officer.',
  'case.noClosingData': 'Closing details will appear here when your application reaches the closing stage.',
  'case.borrowers': 'Borrowers',
  'case.guarantors': 'Guarantors',
  'case.noBorrowers': 'No borrowers linked to this case.',
  'case.noGuarantors': 'No guarantors linked to this case.',
  'case.slaOnTrack': 'On Track',
  'case.slaAtRisk': 'At Risk',
  'case.expectedTimeline': 'Expected Timeline',
  'case.daysInStage': '{{days}} days in current stage',
  'case.requiredDocsMissing': '{{count}} required document(s) still needed',
  'case.allDocsReceived': 'All required documents received',
  'case.uploadForType': 'Upload {{type}}',
  'case.rejectionReason': 'Rejection Reason',
  'case.rejectionCategory': 'Rejection Category',
  'case.severity': 'Severity',
  'case.referralSource': 'Referral Source',
  'case.referralCode': 'Referral Code',
  'case.sourceBusinessId': 'Source Business ID',
  'case.referredBy': 'Referred by {{partner}}',
  'case.referredByPartner': 'Referred by a partner',

  // ============================================================================
  // CASE MANAGEMENT - LOAN APPLICATION WORKFLOW (Issue #486)
  // ============================================================================

  // Labels
  'label.assignedTo': 'Assigned To',
  'label.noteType': 'Note Type',
  'label.noteContent': 'Note Content',
  'label.commentContent': 'Comment Content',
  'label.messageContent': 'Message Content',
  'label.visibleToApplicant': 'Visible to Applicant',
  'label.internalOnly': 'Internal Only',
  'label.rejectionCategory': 'Rejection Category',
  'label.rejectionReason': 'Rejection Reason',
  'label.documentStatus': 'Document Status',
  'label.slaDueDate': 'SLA Due Date',
  'label.workflowProgress': 'Workflow Progress',
  'label.dateRange': 'Date Range',
  'label.selectedFiles': 'Selected Files',
  'label.documentType': 'Document Type',
  'label.category': 'Category',
  'label.loanAmount': 'Loan Amount',
  'label.loanType': 'Loan Type',
  'label.estimatedClose': 'Estimated Close',
  'label.stage': 'Stage',
  'label.address': 'Address',
  'label.propertyType': 'Property Type',
  'label.interestRate': 'Interest Rate',
  'label.ltv': 'LTV',
  'label.purchasePrice': 'Purchase Price',
  'label.estMonthlyPayment': 'Est. Monthly Payment',
  'label.loanTerm': 'Loan Term',
  'label.arv': 'After Repair Value (ARV)',
  'label.rehabBudget': 'Rehab Budget',
  'label.exitStrategy': 'Exit Strategy',
  'label.originationPoints': 'Origination Points',
  'label.propertyAddress': 'Property Address',
  'label.loanRate': 'Loan Rate',
  'label.loanPoints': 'Loan Points',
  'label.sfLiving': 'SF Living',
  'label.redfinLink': 'Redfin Link',
  'label.ltarv': 'LTARV',
  'label.ltc': 'LTC',
  'label.maxLoan': 'Max Loan',
  'label.constraint': 'Constraint',
  'label.bindingConstraint': 'Binding Constraint',
  'label.arvPerSf': 'ARV / SF',
  'label.purchaseType': 'Purchase Type',
  'label.costSpentToDate': 'Cost Spent To Date',
  'label.totalCostToDate': 'Total Cost To Date',
  'label.remainingCostToComplete': 'Remaining Cost to Complete',
  'label.totalProjectCost': 'Total Project Cost',
  'label.totalLoanRequest': 'Total Loan Request',
  'label.loanPayoff': 'Loan Payoff',
  'label.constructionHoldback': 'Construction Holdback',
  'label.interestReserve': 'Interest Reserve',
  'label.cashOutLessClosing': 'Cash Out Less Closing Cost',
  'label.remainingBorrowerBasis': 'Remaining Borrower Basis',
  'label.lender': 'Lender',
  'label.borrowerTier': 'Borrower Tier',
  'label.borrowerLiquidity': 'Borrower Liquidity',
  'label.borrowerNetWorth': 'Borrower Net Worth',
  'label.borrowerCredit': 'Borrower Credit',
  'label.targetClose': 'Target Close',
  'label.dealCount': 'Deal Count',
  'label.dealSummary': 'Deal Summary',
  'label.whyLoan': 'Why This Loan',
  'label.borrowerNarrative': 'Borrower',
  'label.executionNarrative': 'Execution',
  'label.assetNarrative': 'Asset',
  'label.percentOfArv': '% of ARV',
  'label.percentOfCost': '% of Cost',
  'section.dealSummary': 'Deal Summary',
  'section.loanInfo': 'Loan Info',
  'section.borrowerInfo': 'Borrower Info',
  'section.dealNarrative': 'Deal Narrative',

  // ============================================================================
  // CASE DETAIL — ORIGINATION (Issue #1470)
  // ============================================================================
  // Origination details section on the loan-detail overview tab. Surfaces the
  // four first-class case columns promoted from metadata in PR #1492 (source
  // channel, vendors, Dutch toggle + rate) plus live-computed leverage ratios.
  'caseDetail.origination.sectionTitle': 'Origination details',
  'caseDetail.origination.sectionTooltip': 'Channel, vendors, and Dutch interest accrual settings for this loan.',

  'caseDetail.origination.sourceChannel.label': 'Source channel',
  'caseDetail.origination.sourceChannel.placeholder': 'Select a channel',
  'caseDetail.origination.sourceChannel.options.direct': 'Direct',
  'caseDetail.origination.sourceChannel.options.broker': 'Broker',
  'caseDetail.origination.sourceChannel.options.partner': 'Partner',
  'caseDetail.origination.sourceChannel.options.marketing': 'Marketing',
  'caseDetail.origination.sourceChannel.options.repeat': 'Repeat borrower',
  'caseDetail.origination.sourceChannel.options.referral_program': 'Referral program',
  'caseDetail.origination.sourceChannel.options.other': 'Other (free-form)',
  'caseDetail.origination.sourceChannel.otherLabel': 'Custom channel',
  'caseDetail.origination.sourceChannel.otherPlaceholder': 'Describe the source channel',
  'caseDetail.origination.sourceChannel.errorOtherRequired': 'Describe the source channel when selecting Other',
  'caseDetail.origination.sourceChannel.errorTooLong': 'Source channel must be 64 characters or fewer',

  'caseDetail.origination.vendors.sectionLabel': 'Vendors',
  'caseDetail.origination.vendors.closerLabel': 'Closer',
  'caseDetail.origination.vendors.escrowLabel': 'Escrow',
  'caseDetail.origination.vendors.titleLabel': 'Title',
  'caseDetail.origination.vendors.appraiserLabel': 'Appraiser',
  'caseDetail.origination.vendors.attorneyLabel': 'Attorney',
  'caseDetail.origination.vendors.placeholder': 'Vendor ID',
  'caseDetail.origination.vendors.customVendors': 'Custom vendors',
  'caseDetail.origination.vendors.addCustom': 'Add vendor',
  'caseDetail.origination.vendors.removeCustom': 'Remove vendor',
  'caseDetail.origination.vendors.customRolePlaceholder': 'Role label (e.g., inspector)',
  'caseDetail.origination.vendors.customIdPlaceholder': 'Vendor ID',
  'caseDetail.origination.vendors.errorEmptyRole': 'Role label is required',
  'caseDetail.origination.vendors.errorEmptyId': 'Vendor ID is required',
  'caseDetail.origination.vendors.errorRoleTooLong': 'Role label must be 64 characters or fewer',
  'caseDetail.origination.vendors.errorIdTooLong': 'Vendor ID must be 120 characters or fewer',
  'caseDetail.origination.vendors.errorDuplicateRole': 'Role labels must be unique',
  'caseDetail.origination.vendors.errorInvalidShape': 'Vendor entry is invalid',

  'caseDetail.origination.dutch.toggleLabel': 'Dutch interest accrual',
  'caseDetail.origination.dutch.rateLabel': 'Daily accrual rate',
  'caseDetail.origination.dutch.ratePlaceholder': '0.000274',
  'caseDetail.origination.dutch.tooltip': 'Dutch loans accrue interest on the full committed amount from day 1.',
  'caseDetail.origination.dutch.errorRateRange':
    'Daily rate must be between 0 and 1 (e.g., 0.000274 = 0.0274% per day)',
  'caseDetail.origination.dutch.errorRateRequired': 'Daily rate is required when Dutch accrual is enabled',

  'caseDetail.origination.ratios.sectionLabel': 'Live leverage ratios',
  'caseDetail.origination.ratios.ltvLabel': 'LTV',
  'caseDetail.origination.ratios.ltcLabel': 'LTC',
  'caseDetail.origination.ratios.ltArvLabel': 'LT-ARV',
  'caseDetail.origination.ratios.ltvHint': 'Loan amount divided by purchase price.',
  'caseDetail.origination.ratios.ltcHint': 'Loan amount divided by total cost (purchase price + rehab budget).',
  'caseDetail.origination.ratios.ltArvHint': 'Loan amount divided by after-repair value.',

  'action.size': 'Size',
  'loanSizer.runSizer': 'Run Loan Sizer',
  'loanSizer.applyAndClose': 'Apply to Loan',
  'label.streetAddress': 'Street Address',
  'label.streetAddress2': 'Street Address 2',
  'label.city': 'City',
  'label.stateProvince': 'State / Province',
  'label.postalCode': 'Postal Code',
  'label.country': 'Country',
  'placeholder.streetAddress': '123 Main Street',
  'placeholder.streetAddress2': 'Apt, Suite, Unit, etc.',
  'placeholder.selectCountry': 'Select country',
  'placeholder.enterAddress': 'Enter address...',
  'section.loanDetails': 'Loan Details',
  'tabs.application': 'Application',
  'cases.noApplicationData': 'No application data available for this case.',

  // Placeholders
  'placeholder.selectTeamMember': 'Select team member...',
  'placeholder.selectCategory': 'Select category...',
  'label.assignee': 'Assignee',
  'label.borrower': 'Borrower',
  'label.borrowerName': 'Borrower Name',
  'label.consumerEmail': 'Consumer Email',
  'label.created': 'Created',
  'label.description': 'Description',
  'label.dueDate': 'Due Date',
  'label.pendingDocs': 'pending docs',
  'label.priority': 'Priority',
  'label.required': 'Required',
  'label.status': 'Status',
  'label.title': 'Title',
  'label.type': 'Type',
  'placeholder.enterNote': 'Enter your note here...',
  'placeholder.enterComment': 'Enter your comment here...',

  // ============================================================================
  // QUOTE / PRICING (QuoteCard, TierDisplay)
  // ============================================================================
  'quote.title': 'Quote #{{referenceNumber}}',
  'quote.validFrom': 'Valid From',
  'quote.validUntil': 'Valid Until',
  'quote.subtotal': 'Subtotal',
  'quote.discounts': 'Discounts',
  'quote.taxes': 'Taxes',
  'quote.total': 'Total',
  'quote.showDetails': 'Show Details',
  'quote.hideDetails': 'Hide Details',
  'quote.notes': 'Notes',
  'quote.accept': 'Accept Quote',
  'quote.accepting': 'Accepting...',
  'quote.reject': 'Reject',
  'quote.rejecting': 'Rejecting...',
  'quote.extend': 'Extend',
  'quote.status.draft': 'Draft',
  'quote.status.pending': 'Pending',
  'quote.status.sent': 'Sent',
  'quote.status.accepted': 'Accepted',
  'quote.status.rejected': 'Rejected',
  'quote.status.expired': 'Expired',
  'quote.status.cancelled': 'Cancelled',
  'tier.noTiersConfigured': 'No tiers configured',
  'tier.currentValue': 'Current: {{value}}',
  'tier.graduated': 'Graduated',
  'tier.marginal': 'Marginal',
  'tier.graduatedTiers': 'Graduated Tiers',
  'tier.marginalTiers': 'Marginal Tiers',
  'tier.graduatedPricing': 'Graduated Pricing',
  'tier.marginalPricing': 'Marginal Pricing',
  'tier.graduatedDescription': 'Each tier rate applies to the full amount when that tier is reached.',
  'tier.marginalDescription': 'Each tier rate applies only to the amount within that tier range.',

  // ============================================================================
  // IMPORT WIZARD (FieldMappingTable)
  // ============================================================================
  'importWizard.columnsMapped': '{{mapped}} of {{total}} columns mapped',
  'importWizard.requiredFieldsMissing': '{{count}} required fields missing',
  'importWizard.autoMapTooltip': 'Auto-map columns to fields',
  'importWizard.sourceColumn': 'Source Column',
  'importWizard.targetField': 'Target Field',
  'importWizard.transform': 'Transform',
  'importWizard.skipColumn': 'Skip this column',
  'importWizard.alreadyMapped': '(already mapped)',

  // ============================================================================
  // PLACEHOLDERS (Issue #383)
  // ============================================================================

  'placeholder.enterRejectionReason': 'Provide a detailed explanation for rejection...',
  'placeholder.typeMessage': 'Type a message...',

  // Sections
  'section.additionalDetails': 'Additional Details',
  'section.analytics': 'Application Analytics',
  'section.comments': 'Comments',
  'section.documentChecklist': 'Document Checklist',
  'section.eSignatures': 'E-Signatures',
  'section.internalNotes': 'Internal Notes',
  'section.messages': 'Messages',
  'section.requiredDocuments': 'Required Documents',
  'section.tasks': 'Tasks',

  // Actions
  'action.addNote': 'Add Note',
  'action.editNote': 'Edit Note',
  'action.deleteNote': 'Delete Note',
  'message.editInternalNote': 'Update this internal note.',
  'dialog.deleteNoteTitle': 'Delete Note',
  'dialog.deleteNoteConfirm': 'Are you sure you want to delete this note? This action cannot be undone.',
  'error.failedToUpdateNote': 'Failed to update note. Please try again.',
  'error.failedToDeleteNote': 'Failed to delete note. Please try again.',
  'action.addComment': 'Add Comment',
  'action.addMessage': 'Add Message',
  'action.confirmRejection': 'Confirm Rejection',
  'action.bulkAssign': 'Bulk Assign',
  'action.bulkStatusUpdate': 'Bulk Status Update',
  'action.bulkExport': 'Bulk Export',
  'action.bulkExport.headers.caseId': 'Case ID',
  'action.bulkExport.headers.title': 'Title',
  'action.bulkExport.headers.status': 'Status',
  'action.bulkExport.headers.priority': 'Priority',
  'action.bulkExport.headers.created': 'Created',
  'action.sendMessage': 'Send',
  'action.exportPdf': 'Export to PDF',
  'action.createTask': 'Create Task',
  'action.viewDocument': 'View',
  'action.verifyDocument': 'Verify',
  'action.rejectDocument': 'Reject',
  'action.selectAll': 'Select All',
  'action.deselectAll': 'Deselect All',
  'action.attach': 'Attach',
  'action.detach': 'Detach',
  'action.bulkUpload': 'Bulk Upload',
  'action.dropFilesHere': 'Drop files here',
  'action.upload': 'Upload',
  'action.remove': 'Remove',
  'action.verify': 'Verify',
  'action.reject': 'Reject',
  'action.requestReupload': 'Request Re-upload',
  'action.backToDeals': 'Back to Deals',
  'action.editDeal': 'Edit Deal',
  'action.refresh': 'Refresh',

  // Messages
  'message.noNotesYet': 'No internal notes yet. Add one to track team communication.',
  'message.noCommentsYet': 'No comments yet. Add one to communicate with the applicant.',
  'message.addInternalNote': 'Add an internal note visible only to your team.',
  'message.addCommentToCase': 'Add a comment to this loan.',
  'message.addMessageToCase': 'Add a message to this loan.',
  'message.publicCommentHelp': 'This comment will be visible to the applicant in their portal.',
  'message.publicMessageHelp': 'This message will be visible to the applicant in their portal.',
  'message.internalCommentHelp': 'This comment will only be visible to your team.',
  'message.internalMessageHelp': 'This message will only be visible to your team.',
  'message.bulkActionConfirm': 'Are you sure you want to perform this action on {{count}} loans?',
  'message.daysLeft': '{{count}} days left',
  'message.hoursLeft': '{{count}} hours left',
  'message.overdueBy': 'Overdue by {{count}} {{unit}}',
  'message.noTasksYet': 'No tasks yet. Create one to track work items.',
  'message.requiredDocsMissing': 'Cannot approve: {{count}} required document(s) missing',
  'message.noMessagesYet': 'No messages yet. Start the conversation.',
  'message.uploadAndAttach': 'Upload and attach documents',
  'message.uploadDocuments': 'Upload documents',
  'message.orClickToSelect': 'or click to select',
  'message.noDealsHint': 'Deals will appear here once they are created.',

  // Filters
  'filter.assignedToMe': 'Assigned to Me',
  'filter.unassigned': 'Unassigned',
  'filter.publicComments': 'Public Comments',
  'filter.publicMessages': 'Public Messages',
  'filter.internalOnly': 'Internal Only',
  'filter.atRisk': 'At Risk',
  'filter.overdue': 'Overdue',
  // Saved-views chip row aria-label (v3 design — replaces floating-label
  // Status dropdowns on list pages with a horizontal preset chip row).
  // Shared across surfaces because every list uses the same chip-row
  // pattern; per-surface labels remain in their own namespaces.
  'filter.savedViewsAriaLabel': 'Saved views',

  // Document Status
  'filter.all': 'All',
  'filter.status': 'Status',
  'filter.loanType': 'Loan Type',
  'status.uploaded': 'Uploaded',
  'status.missing': 'Missing',
  'status.verified': 'Verified',
  'status.needs_reupload': 'Needs Re-upload',
  'status.blocked': 'Blocked',
  'status.overdue': 'Overdue',

  // Workflow Stages
  'stage.submitted': 'Submitted',
  'stage.reviewing': 'Reviewing',
  'stage.underwriting': 'Underwriting',
  'stage.applied': 'Applied',
  'stage.underReview': 'Under Review',
  'stage.approved': 'Approved',
  'stage.closing': 'Closing',
  'stage.funded': 'Funded',

  // Charts
  'chart.applicationFunnel': 'Application Funnel',
  'chart.avgReviewTime': 'Average Review Time',
  'chart.rejectionRate': 'Rejection Rate',
  'chart.priorityBreakdown': 'Priority Breakdown',
  'chart.statusDistribution': 'Status Distribution',
  'chart.axisHours': 'Hours',
  'chart.seriesAvgHours': 'Avg Hours',
  'chart.seriesLoansMaturing': 'Loans Maturing',
  'chart.seriesVolume': 'Volume',
  'chart.loading': 'Loading chart data...',
  'chart.loadFailed': 'Failed to load chart',
  'chart.noData': 'No data available',

  // Errors
  'error.failedToSaveNote': 'Failed to save note. Please try again.',
  'error.failedToSaveComment': 'Failed to save comment. Please try again.',
  'error.failedToSaveMessage': 'Failed to save message. Please try again.',
  'error.failedToSaveRejection': 'Failed to save rejection. Please try again.',
  'error.rejectionCategoryRequired': 'Please select a rejection category.',
  'error.rejectionReasonMinLength': 'Rejection reason must be at least 20 characters.',

  // Dialogs
  'dialog.rejectionReason.title': 'Rejection Reason Required',
  'dialog.rejectionReason.subtitle': 'Provide a reason for rejecting this application',
  'dialog.rejectionReason.warning': 'This action will reject the application. The applicant will be notified.',

  // Help text
  'help.selectRejectionCategory': 'Choose the primary reason for rejection',

  // Note types
  'noteType.general': 'General Note',
  'noteType.follow-up': 'Follow-up Required',
  'noteType.important': 'Important',
  'noteType.call': 'Phone Call',
  'noteType.email': 'Email Communication',

  // Rejection categories
  'rejectionCategory.insufficient_documentation': 'Insufficient Documentation',
  'rejectionCategory.credit_score': 'Credit Score',
  'rejectionCategory.debt_to_income_ratio': 'Debt-to-Income Ratio',
  'rejectionCategory.collateral_value': 'Collateral Value',
  'rejectionCategory.employment_verification': 'Employment Verification',
  'rejectionCategory.other': 'Other',

  // Common
  'common.charactersMinimum': 'characters minimum',

  // Widget Titles
  'widget.myAssignments': 'My Assignments',
  'widget.myTasks': 'My Tasks',
  'widget.unassignedCases': 'Unassigned Loans',
  'widget.overdueCases': 'Overdue Loans',
  'widget.slaCompliance': 'SLA Compliance',
  'widget.recentActivity': 'Recent Activity',

  // ============================================================================
  // DEAL STEPS (Consumer Portal)
  // ============================================================================

  // Deal Wizard (Issue #475 - i18n migration)

  // Client Info Form (Issue #475 - i18n migration)

  // Pipeline Status Labels (Issue #475 - i18n migration)

  // ============================================================================
  // REFERRAL (Consumer Portal)
  // ============================================================================
  'referral.type': 'Referral Type',

  // ============================================================================
  // REFERRAL — Outbound (Issue #1735)
  // Cross-tenant one-click referral from a partner broker to a destination
  // lender they have an active ReferralAgreement with.
  // ============================================================================
  'referral.outbound.menuLabel': 'Refer to lender',
  'referral.outbound.title': 'Refer to a lender',
  'referral.outbound.subtitle':
    'Share this borrower with a lender you have an active referral agreement with. The borrower must consent to the cross-tenant share.',
  'referral.outbound.borrowerLabel': 'Borrower',
  'referral.outbound.selectDestination': 'Select destination lender',
  'referral.outbound.noDestinations': 'No active referral agreements',
  'referral.outbound.noDestinationsDescription':
    'You need at least one active ReferralAgreement before you can refer a borrower. Visit the Agreements tab to set one up.',
  'referral.outbound.agreementOneWay': 'One-way',
  'referral.outbound.agreementTwoWay': 'Two-way',
  'referral.outbound.agreementUnknown': 'Agreement',
  'referral.outbound.exclusivityWarning':
    'This lender has exclusive rights in {{state}}. Referring here may violate an existing agreement.',
  'referral.outbound.exclusivityWarningGeneric':
    'This lender has an exclusivity agreement that may conflict with this referral.',
  'referral.outbound.borrowerStateLabel': "Borrower's state (optional)",
  'referral.outbound.borrowerStateHelper':
    'Enter the 2-letter state code (e.g. CA, NY) to surface exclusivity warnings on the picker.',
  'referral.outbound.consentHeading': 'Borrower consent',
  'referral.outbound.consentText':
    'I, {{borrower}}, authorize {{source}} to share my contact information with {{destination}} for loan-product evaluation.',
  'referral.outbound.consentCheckboxLabel': 'I confirm the borrower has acknowledged this consent.',
  'referral.outbound.notesLabel': 'Notes for the destination lender (optional)',
  'referral.outbound.notesPlaceholder': 'Anything the destination should know about this borrower…',
  'referral.outbound.notesCharCount': '{{count}}/{{max}} characters',
  'referral.outbound.submit': 'Send referral',
  'referral.outbound.sending': 'Sending…',
  'referral.outbound.cancel': 'Cancel',
  'referral.outbound.successTitle': 'Referral sent',
  'referral.outbound.successBody': '{{destination}} will see this in their inbox shortly. Referral ID: {{id}}.',
  'referral.outbound.duplicate':
    'This referral was already sent (within the last 60 seconds). No duplicate row was created.',
  'referral.outbound.errorPrefix': 'Could not send referral: ',
  'referral.outbound.viewInReferrals': 'View in Referrals',
  'referral.outbound.unnamedLender': 'Unnamed lender',
  'referral.outbound.unknownSource': 'your business',
  'referral.outbound.fallbackName': 'this borrower',
  'referral.outbound.stepDestination': 'Choose lender',
  'referral.outbound.stepConsent': 'Capture consent',
  'referral.outbound.back': 'Back',
  'referral.outbound.next': 'Continue',

  // ============================================================================
  // REFERRAL — Inbound (Issue #1735)
  // Receiver-side badging for cross-tenant referrals routed via a
  // ReferralAgreement, so the destination lender knows the row came from a
  // partner (not a self-created one).
  // ============================================================================
  'referral.inbound.fromPartner': 'From partner',
  'referral.inbound.fromPartnerNamed': 'From partner: {{partnerName}}',
  'referral.inbound.viaAgreement': 'via referral agreement',

  // ============================================================================
  // ONBOARDING (Consumer Portal)
  // ============================================================================
  'onboarding.businessType': 'Business Type',
  'onboarding.stateOfFormation': 'State of Formation',
  'onboarding.selectState': 'Select state',
  'onboarding.timezone': 'Your Timezone',
  'onboarding.bestTimeToContact': 'Best Time to Contact',
  'onboarding.noPreference': 'No preference',

  // Business Type Step
  'onboarding.businessEntity.title': 'How will you hold title to the property?',
  'onboarding.businessEntity.description': 'Select the business entity type that will be on the loan documents.',
  'onboarding.businessEntity.individual': 'Individual',
  'onboarding.businessEntity.individual.desc': 'Personal name, no business entity',
  'onboarding.businessEntity.llc': 'LLC',
  'onboarding.businessEntity.llc.desc': 'Limited Liability Company',
  'onboarding.businessEntity.corporation': 'Corporation',
  'onboarding.businessEntity.corporation.desc': 'C-Corp or S-Corp',
  'onboarding.businessEntity.trust': 'Trust',
  'onboarding.businessEntity.trust.desc': 'Revocable or Irrevocable Trust',
  'onboarding.businessEntity.partnership': 'Partnership',
  'onboarding.businessEntity.partnership.desc': 'LP, LLP, or General Partnership',
  'onboarding.entityDetails': 'Entity Details',
  'onboarding.businessNamePlaceholder': 'ABC Investments LLC',
  'onboarding.yearsInBusinessPlaceholder': 'e.g., 5',

  // Eligibility Step
  'onboarding.eligibility.title': 'Tell us about your loan needs',
  'onboarding.eligibility.description': 'This helps us determine which loan programs you may qualify for.',
  'onboarding.eligibility.loanPurpose': 'What is the purpose of this loan?',
  'onboarding.eligibility.loanPurpose.purchase': 'Purchase',
  'onboarding.eligibility.loanPurpose.purchase.desc': 'Buy a new property',
  'onboarding.eligibility.loanPurpose.refinance': 'Refinance',
  'onboarding.eligibility.loanPurpose.refinance.desc': 'Refinance existing loan',
  'onboarding.eligibility.loanPurpose.cashOut': 'Cash-Out Refinance',
  'onboarding.eligibility.loanPurpose.cashOut.desc': 'Pull equity from property',
  'onboarding.eligibility.loanPurpose.bridge': 'Bridge Loan',
  'onboarding.eligibility.loanPurpose.bridge.desc': 'Short-term financing',
  'onboarding.eligibility.propertyType': 'What type of property?',
  'onboarding.eligibility.propertyType.singleFamily': 'Single Family',
  'onboarding.eligibility.propertyType.multiFamily': 'Multi-Family (2-4)',
  'onboarding.eligibility.propertyType.commercial': 'Commercial',
  'onboarding.eligibility.propertyType.mixedUse': 'Mixed Use',
  'onboarding.eligibility.estimatedLoanAmount': 'Estimated Loan Amount',
  'onboarding.eligibility.estimatedLoanAmountHelper': "Approximate loan amount you're seeking",
  'onboarding.eligibility.creditScoreRange': 'Credit Score Range',
  'onboarding.eligibility.selectRange': 'Select range',
  'onboarding.eligibility.creditScore.excellent': 'Excellent (720+)',
  'onboarding.eligibility.creditScore.good': 'Good (680-719)',
  'onboarding.eligibility.creditScore.fair': 'Fair (620-679)',
  'onboarding.eligibility.creditScore.poor': 'Below 620',
  'onboarding.eligibility.experience': 'Real Estate Investment Experience',
  'onboarding.eligibility.hasExperience': 'I have experience with real estate investments',
  'onboarding.eligibility.numberOfProperties': 'Number of Properties Owned/Managed',
  'onboarding.eligibility.propertiesHelper': 'Current or past investment properties',

  // ============================================================================
  // PORTFOLIO EXPORT (Consumer Portal)
  // ============================================================================
  'portfolio.export.title': 'Export Portfolio',
  'portfolio.export.fieldsToExport': 'Fields to Export',
  'portfolio.export.format': 'Export Format',
  'portfolio.export.csv.desc': 'Comma-separated values, opens in Excel',
  'portfolio.export.xlsx.desc': 'Native Excel format with formatting',
  'portfolio.export.json.desc': 'Raw data for developers',
  'portfolio.export.fieldsSelected': '{{selected}} of {{total}} fields selected',
  'portfolio.export.filtersApplied': 'Your current filters will be applied to the export.',
  'portfolio.export.exporting': 'Exporting...',
  'portfolio.export.recordsProcessed': '{{processed}} of {{total}} records processed',
  'portfolio.export.largeDatasetNote': 'This may take a few moments for large datasets',
  'portfolio.export.complete': 'Export Complete',
  'portfolio.export.recordsExported': '{{count}} records exported successfully',
  'portfolio.export.another': 'Export Another',
  'portfolio.export.downloadFile': 'Download File',
  'portfolio.export.failed': 'Export Failed',
  'portfolio.export.tryAgain': 'Try Again',
  'portfolio.dateRange': 'Date Range',
  'portfolio.selectRange': 'Select range',
  'portfolio.allTime': 'All time',
  'portfolio.last7Days': 'Last 7 Days',
  'portfolio.last30Days': 'Last 30 Days',
  'portfolio.last90Days': 'Last 90 Days',
  'portfolio.yearToDate': 'Year to Date',

  // Portfolio Filters (unique keys - searchDeals already exists)
  'portfolio.filters': 'Filters',
  'portfolio.loanType': 'Loan Type',
  'portfolio.allTypes': 'All Types',
  'portfolio.listView': 'List View',
  'portfolio.pipelineView': 'Pipeline View',
  'portfolio.clearAllFilters': 'Clear all filters',
  'portfolio.loanType.commercial': 'Commercial Real Estate',
  'portfolio.loanType.equipment': 'Equipment Financing',
  'portfolio.loanType.workingCapital': 'Working Capital',
  'portfolio.loanType.sba': 'SBA Loan',
  'portfolio.loanType.term': 'Term Loan',
  'portfolio.status.submitted': 'Submitted',
  'portfolio.status.processing': 'Processing',
  'portfolio.status.underwriting': 'Underwriting',
  'portfolio.status.approved': 'Approved',
  'portfolio.status.denied': 'Denied',
  'portfolio.status.closed': 'Closed',

  // ============================================================================
  // DASHBOARD - Consumer Portal Specific (unique keys)
  // Note: dashboard.overview, dashboard.welcomeBack, dashboard.pendingActions already defined earlier
  // ============================================================================
  'dashboard.applicationProgress': 'Application Progress',
  'dashboard.daysInStage': '{{days}} days in this stage',
  'dashboard.next': 'Next: {{step}}',
  'dashboard.uploadDocuments': 'Upload Documents',
  'dashboard.viewTimeline': 'View Timeline',
  'dashboard.documentsSubmitted': 'Documents Submitted',
  'dashboard.approved': 'Approved',
  'dashboard.pendingReview': 'Pending Review',
  'dashboard.rejected': 'Rejected',
  'dashboard.documentChecklist': 'Document Checklist',
  'dashboard.manageDocuments': 'Manage Documents',
  'dashboard.noDocumentsRequired': 'No document requirements for this loan.',
  'dashboard.yourLoanApplication': 'Your Loan Application',
  'dashboard.trackApplicationStatus': 'Track your application status and complete required actions.',
  'dashboard.applicationSubmittedSuccess': 'Application Submitted Successfully!',
  'dashboard.applicationSubmittedDesc':
    "Your loan application has been received. We'll review your information and contact you within 1-2 business days. Check your email for a confirmation.",
  'dashboard.failedToLoadApplication': 'Failed to load application data. Please try again later.',
  'dashboard.accountOverview': "Here's an overview of your account.",
  'dashboard.activeApplications': 'Active Applications',
  'dashboard.documents': 'Documents',
  'dashboard.recentApplications': 'Recent Applications',
  'dashboard.noApplicationsYet': 'No applications yet. Submit a new request to get started.',
  'dashboard.submitNew': 'Submit New',
  'dashboard.failedToLoadDashboard': 'Failed to load dashboard data. Please try again later.',
  'dashboard.uploadDocumentsTooltip': 'Upload Documents',

  // Guarantee Obligations Card (Issue #1199)
  'consumer.dashboard.guaranteeObligations': 'Guarantee Obligations',
  'consumer.dashboard.noGuarantees': 'No active guarantee obligations',
  'consumer.dashboard.guaranteeSummary': 'You currently guarantee {{amount}} across {{count}} loans',
  'consumer.dashboard.expiringIn': 'Expiring in {{days}} days',
  'consumer.dashboard.expired': 'Expired',
  'consumer.dashboard.viewEntities': 'View Entities',

  // ============================================================================
  // PROPOSAL (Consumer Portal)
  // ============================================================================
  'proposal.content': 'Proposal Content',
  'proposal.relatedApplication': 'Related Application',
  'proposal.viewApplication': 'View Application',
  'proposal.noProposalsForApplication': 'No proposals yet for this application',
  'proposal.proposalsForApplication': 'Proposals for This Application',
  'proposal.received': 'Received {{date}}',
  'proposal.status.draft': 'Draft',
  'proposal.status.sent': 'New',
  'proposal.status.viewed': 'Viewed',
  'proposal.status.accepted': 'Accepted',
  'proposal.status.rejected': 'Rejected',
  'proposal.status.expired': 'Expired',

  // case.relatedProposals and case.viewProposal are defined earlier (Issue #475)

  // ============================================================================
  // PORTFOLIO - Column Headers (Consumer Portal - Issue #475)
  // ============================================================================
  'portfolio.column.borrower': 'Borrower',
  'portfolio.column.loanAmount': 'Loan Amount',
  'portfolio.column.type': 'Type',
  'portfolio.column.status': 'Status',
  'portfolio.column.submitted': 'Submitted',
  'portfolio.column.lastUpdated': 'Last Updated',

  // ============================================================================
  // FINANCIAL SUMMARY (Consumer Portal - Issue #475)
  // ============================================================================
  'financial.summary.title': 'Financial Summary',
  'financial.totalAssets': 'Total Assets',
  'financial.totalDebt': 'Total Debt',
  'financial.monthlyPayments': 'Monthly Payments',
  'financial.netWorth': 'Net Worth',
  'financial.netWorthFormula': 'Net Worth = Total Assets - Total Debt',

  // ============================================================================
  // SIGNING STATUS (Consumer Portal - Issue #475)
  // ============================================================================
  'signing.status.pending': 'Awaiting Signatures',
  'signing.status.completed': 'Signed',
  'signing.status.expired': 'Expired',
  'signing.status.archived': 'Archived',
  'signing.submitter.pending': 'Not Started',
  'signing.submitter.sent': 'Sent',
  'signing.submitter.opened': 'Viewed',
  'signing.submitter.completed': 'Signed',
  'signing.signedCount': '{{completed}}/{{total}} signed',
  'signing.signersCompleted': '{{completed}} of {{total}} signer{{plural}} completed',
  'signing.created': 'Created:',
  'signing.expires': 'Expires:',
  'signing.signedAt': 'Signed:',

  // ============================================================================
  // RECORDS PAGE (Consumer Portal - Issue #475)
  // ============================================================================
  'records.title': 'My Records',
  'records.description': 'Access and manage your records across different categories.',
  'records.loadError': 'Failed to load record types. Please try again.',
  'records.viewManageEntity': 'View and manage your {{entity}}',
  'records.noRecordTypes': 'No Record Types Available',
  'records.noRecordTypesDescription': 'There are no record types configured for your account.',

  // ============================================================================
  // PROFILE PAGE (Consumer Portal - Issue #475)
  // ============================================================================
  'profile.title': 'Profile',
  'profile.personalInformation': 'Personal Information',
  'profile.fullName': 'Full Name',
  'profile.emailCannotBeChanged': 'Email cannot be changed',
  'profile.editProfile': 'Edit Profile',
  'profile.notificationSettings': 'Notification Settings',
  'profile.emailNotifications': 'Email notifications',
  'profile.pushNotifications': 'Push notifications',
  'profile.pushNotificationsUnsupported': 'Push notifications are not supported in this browser',
  'profile.pushNotificationsDenied': 'Push notifications were blocked. Enable them in your browser settings.',
  'profile.pushNotificationsError': 'Failed to enable push notifications',
  'profile.pushNotificationsEnabling': 'Enabling push notifications...',
  'profile.account': 'Account',
  'profile.signOut': 'Sign Out',
  'profile.updateSuccess': 'Profile updated successfully',
  'profile.updateFailed': 'Failed to update profile',
  'profile.loadFailed': 'Failed to load profile',

  // ============================================================================
  // BREADCRUMBS (Consumer Portal - Issue #475)
  // ============================================================================
  'breadcrumb.home': 'Home',
  'breadcrumb.dashboard': 'Dashboard',
  'breadcrumb.applications': 'Applications',
  'breadcrumb.documents': 'Documents',
  'breadcrumb.profile': 'Profile',
  'breadcrumb.records': 'Records',
  'breadcrumb.submit': 'Submit',
  'breadcrumb.onboarding': 'Onboarding',
  'breadcrumb.proposals': 'Proposals',
  'breadcrumb.portfolio': 'Portfolio',
  'breadcrumb.commissions': 'Commissions',
  'breadcrumb.referrals': 'Referrals',
  'breadcrumb.reports': 'Reports',
  'breadcrumb.quotes': 'Quotes',
  'breadcrumb.new': 'New',
  'breadcrumb.edit': 'Edit',
  'breadcrumb.referral': 'Referral',
  'breadcrumb.partner': 'Partner Portal',

  // ============================================================================
  // PARTNERS (Issue #400 - i18n audit)
  // ============================================================================
  'partners.backToPartners': 'Back to Partners',
  'partners.creatingInvitation': 'Creating invitation...',
  'partners.invitationSent': 'Invitation Sent',
  'partners.invitationSentDescription': 'Partner invitation has been sent successfully',
  'partners.invitationUpdated': 'Invitation Updated',
  'partners.invitationUpdatedDescription':
    'The partner will see the revised details the next time they open the invite link.',
  'partners.inviteAnother': 'Invite Another Partner',
  'partners.invitePartner': 'Invite Partner',
  'partners.invitePartnerSubtitle': 'Send an invitation to a new partner',

  // Contract package selection (preview + template management)
  'contractPackage.preview': 'Preview template',
  'contractPackage.manageTemplates': 'Manage templates',
  'contractPackage.openInControlCenter': 'Open in Control Center',
  'contractPackage.noTemplateAssigned':
    'No template is assigned to this contract yet. Upload one in the Control Center.',
  'contractPackage.templateId': 'Template ID',
  'contractPackage.previewLoading': 'Loading preview…',
  'contractPackage.previewError': 'Could not load the template preview. Open it in the Control Center instead.',
  'contractPackage.previewUnavailable':
    'This template has no rendered preview yet. It will appear here once the template sync completes.',

  // Partner-invite wizard — Step 3 (#1533 Phase 3 / #1540 partner-invite upgrade).
  // Per-card template+version dropdown, "upload custom" inline link,
  // "manage templates" link.
  'partners.invite.step3.templateLabel': 'Template',
  'partners.invite.step3.templateLoading': 'Loading templates…',
  'partners.invite.step3.templateNoneAvailable': 'No active templates for this contract type yet.',
  'partners.invite.step3.templateDefaultBadge': 'Default',
  'partners.invite.step3.templateVersionPrefix': 'v',
  'partners.invite.step3.uploadCustom': 'Upload a custom template',
  'partners.invite.step3.manageTemplates': 'Manage templates',

  'breadcrumb.tracking': 'Tracking',

  // ============================================================================
  // BASELINE PARITY — New Pages (Issue #596, #598)
  // ============================================================================

  // Inbox
  'pageHeader.inbox.title': 'Inbox',
  'pageHeader.inbox.description':
    'All your messages and work in one place — system events, tasks, approvals, and e-sign queue.',
  'inbox.title': 'Inbox',
  'inbox.description': 'All your messages in one place',
  'inbox.empty': 'Your inbox is empty',
  'inbox.emptyDescription': 'Messages from borrowers, partners, and team members will appear here.',
  // UX v3 (#1769) — Inbox hub tab labels for the unified 4-tab page.
  'inbox.tabs.notifications': 'Notifications',
  'inbox.tabs.tasks': 'Tasks',
  'inbox.tabs.approvals': 'Approvals',
  'inbox.tabs.eSign': 'E-Sign Queue',

  // Contacts
  'pageHeader.contacts.title': 'Contacts',
  'pageHeader.contacts.description': 'Manage borrowers, partners, and investors',
  'contacts.title': 'Contacts',
  'contacts.description': 'Manage borrowers, partners, and investors',
  'contacts.tabs.all': 'All',
  'contacts.tabs.borrowers': 'Borrowers',
  'contacts.tabs.partners': 'Partners',
  'contacts.tabs.investors': 'Investors',
  'contacts.empty': 'No contacts found',
  'contacts.emptyDescription': 'Your contacts will appear here once you add borrowers or partners.',
  'contacts.type.borrower': 'Borrower',
  'contacts.type.partner': 'Partner',
  'contacts.type.investor': 'Investor',
  'contacts.quickStats.totalLoans': 'Total Loans',
  'contacts.quickStats.activeLoans': 'Active Loans',
  'contacts.quickStats.totalVolume': 'Total Volume',
  'contacts.recentActivity': 'Recent Activity',
  'contacts.linkedLoans': 'Linked Loans',
  'contacts.investorsComingSoon': 'Investor profiles coming in a future release.',

  // Source filter (Issue #1621 — Close.com sync)
  'contacts.source.label': 'Source',
  'contacts.source.all': 'All',
  'contacts.source.manual': 'Manual',
  'contacts.source.close': 'Close',
  'contacts.source.sfr': 'SFR',
  'contacts.source.hubspot': 'HubSpot',
  'contacts.source.salesforce': 'Salesforce',
  'contacts.source.unknown': 'External',
  'contacts.source.lastSyncedAt': 'Last synced',
  'contacts.source.never': 'Never',
  'contacts.source.filterAria': 'Filter by source',

  // Borrower-detail Close tab (Issue #1621 — Phase 2 Stream 4b)
  'borrowers.close.tabLabel': 'Close',
  'borrowers.close.identityTitle': 'Close identity',
  'borrowers.close.identityDescription': 'Linked Close lead and contact, last sync time, and current sync status.',
  'borrowers.close.leadLink': 'Lead in Close',
  'borrowers.close.contactLink': 'Contact in Close',
  'borrowers.close.openInClose': 'Open in Close',
  'borrowers.close.lastSyncedAt': 'Last synced',
  'borrowers.close.syncStatus': 'Sync status',
  'borrowers.close.syncStatus.synced': 'Synced',
  'borrowers.close.syncStatus.pending': 'Pending',
  'borrowers.close.syncStatus.error': 'Error',
  'borrowers.close.syncStatus.unknown': 'Unknown',
  'borrowers.close.communicationsTitle': 'Communications',
  'borrowers.close.communicationsDescription': 'Email, SMS, and call activity synced from Close.',
  'borrowers.close.communications.empty': 'No communications synced from Close yet.',
  'borrowers.close.communications.loadMore': 'Load more',
  'borrowers.close.communications.weekOf': 'Week of {date}',
  'borrowers.close.communications.channel.email': 'Email',
  'borrowers.close.communications.channel.sms': 'SMS',
  'borrowers.close.communications.channel.call': 'Call',
  'borrowers.close.communications.channel.note': 'Note',
  'borrowers.close.communications.direction.inbound': 'Inbound',
  'borrowers.close.communications.direction.outbound': 'Outbound',
  'borrowers.close.communications.viewBody': 'Show full body',
  'borrowers.close.communications.hideBody': 'Hide body',
  'borrowers.close.communications.viewTranscript': 'Show transcript',
  'borrowers.close.communications.hideTranscript': 'Hide transcript',
  'borrowers.close.communications.noSubject': 'No subject',
  'borrowers.close.communications.noBody': 'No content',
  'borrowers.close.customFieldsTitle': 'Custom fields',
  'borrowers.close.customFieldsDescription': 'Synced custom field values from Close.',
  'borrowers.close.customFields.empty':
    'No custom fields synced yet. Use Refresh from Close to pull the latest values.',
  'borrowers.close.refreshButton': 'Refresh from Close',
  'borrowers.close.refreshing': 'Refreshing…',
  'borrowers.close.refreshSuccess': 'Refreshed from Close.',
  'borrowers.close.refreshError': 'Failed to refresh from Close. Please try again.',

  // Borrower-detail Relationships tab
  'borrowers.relationships.tabLabel': 'Relationships',
  'borrowers.relationships.entitiesTitle': 'Entities owned',
  'borrowers.relationships.entitiesDescription': 'Borrowing entities this borrower owns or is a member of.',
  'borrowers.relationships.entities.empty': 'No entity ownership recorded for this borrower.',
  'borrowers.relationships.entities.role': 'Role',
  'borrowers.relationships.entities.ownership': 'Ownership',
  'borrowers.relationships.entities.entityName': 'Entity',
  'borrowers.relationships.entities.entityType': 'Type',
  'borrowers.relationships.coBorrowersTitle': 'Co-borrowers and sponsors',
  'borrowers.relationships.coBorrowersDescription': 'Other parties on loans this borrower is associated with.',
  'borrowers.relationships.coBorrowers.empty': 'No co-borrowers or sponsors found across this borrower’s loans.',
  'borrowers.relationships.coBorrowers.role': 'Role',
  'borrowers.relationships.coBorrowers.loan': 'Loan',
  'borrowers.relationships.coBorrowers.name': 'Name',

  // Borrower-detail Relationships tab — v3 (Issue #1749)
  'borrowers.relationshipsTab.originator.title': 'Originator / partner',
  'borrowers.relationshipsTab.originator.empty': 'No originating partner on record.',
  'borrowers.relationshipsTab.originator.emptyHint':
    'This borrower came in through a direct channel. Partner attribution appears here when a referral source is set.',
  'borrowers.relationshipsTab.originator.source': 'Source',
  'borrowers.relationshipsTab.originator.firstTouch': 'First touch',
  'borrowers.relationshipsTab.originator.commissionRate': 'Commission rate',
  'borrowers.relationshipsTab.originator.earnings': 'Earnings to date',
  'borrowers.relationshipsTab.connectedParties.title': 'Connected parties',
  'borrowers.relationshipsTab.connectedParties.description':
    'Entities, co-borrowers, guarantors, and vendors connected to this borrower across their loans.',
  'borrowers.relationshipsTab.connectedParties.empty':
    'No connected parties yet. Entities, co-borrowers, guarantors, and recurring vendors appear here as loans are originated.',
  'borrowers.relationshipsTab.partyType.personal': 'Personal',
  'borrowers.relationshipsTab.partyType.entity': 'Entity',
  'borrowers.relationshipsTab.partyType.vendor': 'Vendor',
  'borrowers.relationshipsTab.partyRole.principal': 'Principal / owner',
  'borrowers.relationshipsTab.partyRole.borrowingEntity': 'Borrowing entity',
  'borrowers.relationshipsTab.partyRole.coBorrowerOnCase': '{{role}} on {{caseNumber}}',
  'borrowers.relationshipsTab.graph.title': 'Relationship graph',
  'borrowers.relationshipsTab.graph.description':
    'Network view of this borrower and the entities, co-borrowers, and vendors connected to them.',

  // Borrower-detail duplicate banner
  'borrowers.duplicates.bannerTitle': 'Possible duplicate found',
  'borrowers.duplicates.bannerTitle_plural': '{{count}} possible duplicates found',
  'borrowers.duplicates.bannerDescription':
    'A Close sync flagged this borrower as a likely duplicate. Review and merge or dismiss.',
  'borrowers.duplicates.review': 'Review duplicates',
  'borrowers.duplicates.dismissAll': 'Dismiss all',
  'borrowers.duplicates.dismissAllSuccess': 'All duplicate candidates dismissed.',
  'borrowers.duplicates.dismissAllError': 'Failed to dismiss duplicates. Please try again.',

  // Borrower-detail merge modal (Stream 4c)
  'borrowers.duplicates.modalTitle': 'Resolve duplicate borrowers',
  'borrowers.duplicates.merge.subtitle': 'Compare each field, choose which value to keep, then merge.',
  'borrowers.duplicates.merge.progress': 'Candidate {{current}} of {{total}}',
  'borrowers.duplicates.merge.matchScore': 'Match score: {{score}}',
  'borrowers.duplicates.merge.primaryHeader': 'Primary borrower',
  'borrowers.duplicates.merge.candidateHeader': 'Possible duplicate',
  'borrowers.duplicates.merge.fieldName': 'Name',
  'borrowers.duplicates.merge.fieldEmail': 'Email',
  'borrowers.duplicates.merge.fieldPhone': 'Phone',
  'borrowers.duplicates.merge.fieldExternalRefs': 'External references',
  'borrowers.duplicates.merge.fieldLastSyncedAt': 'Last synced',
  'borrowers.duplicates.merge.choicePrimary': 'Keep primary',
  'borrowers.duplicates.merge.choiceCandidate': 'Keep candidate',
  'borrowers.duplicates.merge.choiceCustom': 'Custom value',
  'borrowers.duplicates.merge.customLabel': 'Enter custom value',
  'borrowers.duplicates.merge.empty': '—',
  'borrowers.duplicates.merge.submit': 'Merge',
  'borrowers.duplicates.merge.cancel': 'Cancel',
  'borrowers.duplicates.merge.skip': 'Skip this candidate',
  'borrowers.duplicates.merge.success': 'Borrowers merged successfully.',
  'borrowers.duplicates.merge.error': 'Failed to merge borrowers. Please try again.',
  'borrowers.duplicates.merge.allResolved': 'All duplicates resolved.',
  'borrowers.duplicates.merge.loading': 'Loading candidate details…',

  // Applications — Create from Close consumer (Issue #1621 Stream 4d)
  'applications.createFromConsumer.button': 'Create application',
  'applications.createFromConsumer.title': 'Create application from Close',
  'applications.createFromConsumer.subtitle':
    'AI-extracted answers from this borrower’s Close communications. Review, edit, and submit.',
  'applications.createFromConsumer.extracting': 'Extracting from communications…',
  'applications.createFromConsumer.extractionError': 'Failed to extract borrower data. Try again or skip pre-fill.',
  'applications.createFromConsumer.retry': 'Retry extraction',
  'applications.createFromConsumer.templateLabel': 'Application template',
  'applications.createFromConsumer.templatePlaceholder': 'Select a template…',
  'applications.createFromConsumer.templateLoading': 'Loading templates…',
  'applications.createFromConsumer.noTemplates': 'No intake templates available. Create one in Control Center first.',
  'applications.createFromConsumer.modelLabel': 'Extraction model',
  'applications.createFromConsumer.fromCache': 'Cached',
  'applications.createFromConsumer.fromFresh': 'Live',
  'applications.createFromConsumer.fieldsHeader': 'Pre-filled fields ({{filled}}/{{total}})',
  'applications.createFromConsumer.noFields': 'No pre-filled fields yet. Edit any field below to get started.',
  'applications.createFromConsumer.confidenceHigh': 'High confidence ({{percent}}%)',
  'applications.createFromConsumer.confidenceMedium': 'Medium confidence ({{percent}}%)',
  'applications.createFromConsumer.confidenceLow': 'Low confidence ({{percent}}%) — please review',
  'applications.createFromConsumer.sourceLabel': 'Source',
  'applications.createFromConsumer.sourceCitation': 'From {{channel}} {{occurredAt}}: “{{snippet}}”',
  'applications.createFromConsumer.sourceUnknown': 'Source not available for this field.',
  'applications.createFromConsumer.editedNote': 'Edited by you',
  'applications.createFromConsumer.fieldEmpty': 'Not extracted',
  'applications.createFromConsumer.submit': 'Create draft',
  'applications.createFromConsumer.submitting': 'Creating draft…',
  'applications.createFromConsumer.cancel': 'Cancel',
  'applications.createFromConsumer.success': 'Draft application created.',
  'applications.createFromConsumer.error': 'Failed to create draft application. Please try again.',
  'applications.createFromConsumer.draftReturnNote':
    'You can come back to this draft from the Loans → Applications list (drafts coming soon).',

  // Applications KPI strip (intake queue v3 design pass)
  'applications.kpi.newToday': 'New today',
  'applications.kpi.submitted7d': 'Submitted 7d',
  'applications.kpi.astorApprove': 'Astor approve',
  'applications.kpi.needReview': 'Need review',
  'applications.kpi.avgQuality': 'Avg quality',

  // Payments
  'pageHeader.payments.title': 'Payments',
  'pageHeader.payments.description': 'Track and manage payments',
  'payments.title': 'Payments',
  'payments.description': 'Track and manage loan payments, disbursements, and collections.',
  'payments.empty': 'Payment tracking coming soon',
  'payments.emptyDescription':
    'Payment management features are being built. You will be able to track loan payments, disbursements, and collections here.',
  'payments.setupPayments': 'Set Up Payments',

  // Reports
  'pageHeader.reports.title': 'Reports',
  'pageHeader.reports.description': 'Analytics and business intelligence',
  'reports.title': 'Reports',
  'reports.description': 'Analytics and business intelligence',
  'reports.tabs.pipelineAnalytics': 'Pipeline Analytics',
  'reports.tabs.loanPortfolio': 'Loan Portfolio',
  'reports.tabs.financial': 'Financial',
  'reports.tabs.performance': 'Performance',
  'reports.tabs.custom': 'Custom',
  'reports.comingSoon': 'Coming soon',
  'reports.comingSoonDescription': 'This report is being developed and will be available soon.',
  'reports.dateRange.last7Days': 'Last 7 days',
  'reports.dateRange.last30Days': 'Last 30 days',
  'reports.dateRange.last90Days': 'Last 90 days',
  'reports.dateRange.lastYear': 'Last year',
  'reports.bottleneck.title': 'Bottleneck Identified',
  'reports.bottleneck.description':
    'The {{stage}} stage has the longest average processing time of {{avgHours}} hours.',
  'reports.comparePeriod': 'Compare to previous period',
  'reports.exportAll': 'Export All',
  'reports.printView': 'Print View',
  'reports.noData': 'No data available for this period',
  // Loan Portfolio Report
  'reports.portfolio.title': 'Loan Portfolio',
  'reports.portfolio.totalVolume': 'Total Volume',
  'reports.portfolio.totalLoans': 'Total Loans',
  'reports.portfolio.weightedAvgRate': 'Weighted Avg Rate',
  'reports.portfolio.delinquencyRate': 'Delinquency Rate',
  'reports.portfolio.byType': 'By Loan Type',
  'reports.portfolio.byStatus': 'By Status',
  'reports.portfolio.byRate': 'By Interest Rate',
  'reports.portfolio.maturityTimeline': 'Maturity Timeline',
  // Financial Report
  'reports.financial.title': 'Financial',
  'reports.financial.totalRevenue': 'Total Revenue',
  'reports.financial.originationFees': 'Origination Fees',
  'reports.financial.interestIncome': 'Interest Income',
  'reports.financial.lateFees': 'Late Fees',
  'reports.financial.lossRate': 'Loss Rate',
  'reports.financial.revenueBreakdown': 'Revenue Breakdown',
  'reports.financial.monthlyIncome': 'Monthly Income Trends',
  // Performance Report
  'reports.performance.title': 'Performance',
  'reports.performance.avgResponseTime': 'Avg Response Time',
  'reports.performance.conversionRate': 'Conversion Rate',
  'reports.performance.loansPerOfficer': 'Loans per Officer',
  'reports.performance.avgTimeToClose': 'Avg Time to Close',
  'reports.performance.teamMetrics': 'Team Metrics',
  'reports.performance.officer': 'Officer',
  'reports.performance.loansProcessed': 'Loans Processed',
  'reports.performance.responseTime': 'Avg Response (hrs)',
  'reports.performance.conversion': 'Conversion %',
  'reports.performance.timeToClose': 'Avg Close (days)',
  // Custom Report
  'reports.custom.title': 'Custom Reports',
  'reports.custom.comingSoon': 'Custom report builder coming in Phase 34.',
  'chart.timeInStatusBreakdown': 'Time-in-Status Breakdown',
  // Pipeline Board (Issue #624)
  'board.backToPipeline': 'Back to Pipeline',
  'board.configure': 'Configure',
  'board.totalValue': '{{value}}',
  'board.records': '{{count}} records',
  'board.noRecords': 'No records',
  'board.daysInStage': '{{days}}d',
  'board.cardMoved': 'Moved to {{stage}}',
  'board.cardMoveFailed': 'Failed to move card',
  'board.filters': 'Filters',
  'board.filterLoanType': 'Loan Type',
  'board.filterAssignee': 'Assignee',
  'board.filterDateRange': 'Date Range',
  'board.filterAmountMin': 'Min Amount',
  'board.filterAmountMax': 'Max Amount',
  'board.clearFilters': 'Clear Filters',
  'board.allTypes': 'All Types',
  'board.allAssignees': 'All Assignees',
  // Document Templates (Issue #611)
  'docTemplates.title': 'Document Templates',
  'docTemplates.description': 'Upload and manage Word templates with merge fields for document generation.',
  'docTemplates.upload': 'Upload Template',
  'docTemplates.name': 'Template Name',
  'docTemplates.documentType': 'Document Type',
  'docTemplates.lastUpdated': 'Last Updated',
  'docTemplates.status': 'Status',
  'docTemplates.draft': 'Draft',
  'docTemplates.active': 'Active',
  'docTemplates.archived': 'Archived',
  'docTemplates.noTemplates': 'No document templates yet',
  'docTemplates.noTemplatesDescription': 'Upload a .docx with merge-field placeholders to generate signable documents.',
  'docTemplates.uploadTemplate': 'Upload Template',
  'docTemplates.uploadTitle': 'Upload Document Template',
  'docTemplates.uploadDescription':
    'Upload a .docx file. Merge fields like {{borrower.name}} will be detected automatically.',
  'docTemplates.uploadNameLabel': 'Template name',
  'docTemplates.uploadCategoryLabel': 'Category',
  'docTemplates.uploadFileLabel': 'File',
  'docTemplates.uploadFileHint': 'Drop a .docx or .pdf here, or click to browse',
  'docTemplates.uploadSubmit': 'Upload',
  'docTemplates.uploadCancel': 'Cancel',
  'docTemplates.uploadSuccess': 'Template uploaded and synced from DocuSeal.',
  'docTemplates.uploadError': 'Upload failed. Please try again.',
  'docTemplates.uploadFileTooLarge': 'File is too large. Maximum size is 25 MB.',
  'docTemplates.uploadInvalidType': 'Only .docx and .pdf files are supported.',
  'docTemplates.mergeFields': 'Merge Fields',
  'docTemplates.mergeFieldsDescription': 'Available fields that will be replaced with actual data.',
  'docTemplates.preview': 'Preview',
  'docTemplates.generate': 'Generate Document',
  'docTemplates.selectTemplate': 'Select Template',
  'docTemplates.selectTemplateDescription': 'Choose a template to generate a document.',
  // Document Types
  'docTemplates.type.preApproval': 'Pre-Approval Letter',
  'docTemplates.type.loanAgreement': 'Loan Agreement',
  'docTemplates.type.payoffLetter': 'Payoff Letter',
  'docTemplates.type.defaultNotice': 'Default Notice',
  'docTemplates.type.investorAgreement': 'Investor Agreement',
  'docTemplates.type.custom': 'Custom',

  // CRM template categories (Issue #1382 — wired to real data)
  'docTemplates.type.partnership': 'Partnership',
  'docTemplates.type.nda': 'NDA',
  'docTemplates.type.disclosure': 'Disclosure',
  'docTemplates.type.vendor': 'Vendor',
  'docTemplates.type.loanDocs': 'Loan Docs',
  'docTemplates.type.employment': 'Employment',

  'docTemplates.loadError': 'Could not load document templates. Please try again.',
  // Merge Field Categories
  'docTemplates.fields.borrower': 'Borrower',
  'docTemplates.fields.loan': 'Loan',
  'docTemplates.fields.property': 'Property',
  'docTemplates.fields.financial': 'Financial',
  'docTemplates.fields.business': 'Business',
  'docTemplates.fields.date': 'Date',
  'docTemplates.field.borrowerName': 'Borrower Name',
  'docTemplates.field.borrowerEmail': 'Borrower Email',
  'docTemplates.field.borrowerAddress': 'Borrower Address',
  'docTemplates.field.ssnLast4': 'SSN Last 4',
  'docTemplates.field.ssnLast4Digits': 'SSN Last 4 Digits',
  'docTemplates.field.loanNumber': 'Loan Number',
  'docTemplates.field.loanAmount': 'Loan Amount',
  'docTemplates.field.interestRate': 'Interest Rate',
  'docTemplates.field.loanTerm': 'Loan Term',
  'docTemplates.field.loanTermMonths': 'Loan Term (months)',
  'docTemplates.field.maturityDate': 'Maturity Date',
  'docTemplates.field.currentBalance': 'Current Balance',
  'docTemplates.field.propertyAddress': 'Property Address',
  'docTemplates.field.propertyType': 'Property Type',
  'docTemplates.field.propertyValue': 'Property Value',
  'docTemplates.field.appraisedValue': 'Appraised Value',
  'docTemplates.field.payoffTotal': 'Payoff Total',
  'docTemplates.field.perDiem': 'Per Diem',
  'docTemplates.field.perDiemInterest': 'Per Diem Interest',
  'docTemplates.field.goodThroughDate': 'Good Through Date',
  'docTemplates.field.businessName': 'Business Name',
  'docTemplates.field.businessAddress': 'Business Address',
  'docTemplates.field.businessLogo': 'Business Logo',
  'docTemplates.field.currentDate': 'Current Date',
  'docTemplates.field.currentDatePlus30': 'Current Date + 30 Days',
  'docTemplates.field.datePlus30': 'Date + 30 Days',

  // Deep-link banner (when opened from another page with ?templateId=)
  'docTemplates.deepLinkFound': 'Showing template from link: {{name}}',
  'docTemplates.deepLinkNotFound':
    'No template found for ID {{id}}. It may have been deleted or belong to another tenant.',
  'docTemplates.clearDeepLink': 'Show all templates',

  // Issue #1474 — Unified Document/Termsheet templates page
  'docTemplates.unified.title': 'Document Templates',
  'docTemplates.unified.description':
    'Reusable signable documents and termsheet templates. Upload PDFs, place fields, and reuse across loans.',
  'docTemplates.unified.newDocument': 'Document',
  'docTemplates.unified.newTermsheet': 'Termsheet',
  // Issue #1545 — Type → Subtype picker (replaces flat 5-card kind picker)
  'docTemplates.unified.types.contract.label': 'Contract',
  'docTemplates.unified.types.contract.description': 'Partnership, NDA, MSA, and other agreements.',
  'docTemplates.unified.types.termsheet.label': 'Termsheet',
  'docTemplates.unified.types.termsheet.description': 'Structured builder or PDF upload.',
  'docTemplates.unified.types.disclosure.label': 'Disclosure',
  'docTemplates.unified.types.disclosure.description': 'Regulatory or compliance PDF.',
  'docTemplates.unified.types.generic.label': 'Generic Document',
  'docTemplates.unified.types.generic.description': 'Any reusable PDF outside the other types.',
  'docTemplates.unified.subtypePicker.title.contract': 'Pick a contract subtype',
  'docTemplates.unified.subtypePicker.title.termsheet': 'Pick a termsheet format',
  'docTemplates.unified.subtypePicker.back': 'Back',
  'docTemplates.unified.subtypePicker.empty': 'No subtypes available for this type yet.',
  'docTemplates.unified.subtypePicker.error': "Couldn't load subtypes. Try again.",
  'docTemplates.unified.termsheet.subtypes.structured.label': 'Structured (Builder)',
  'docTemplates.unified.termsheet.subtypes.structured.description':
    'Use the wizard to build a sectioned termsheet from scratch.',
  'docTemplates.unified.termsheet.subtypes.pdf.label': 'PDF Upload',
  'docTemplates.unified.termsheet.subtypes.pdf.description': 'Upload a pre-formatted termsheet PDF.',

  'docTemplates.unified.newMenu': 'New',
  // Issue #1540 / #1533 Phase 4 — granular sub-menu under "+ New ▾". Splits
  // by kind + content type so the user picks intent up front.
  'docTemplates.unified.newMenu.termsheetStructured': 'Termsheet (Structured)',
  'docTemplates.unified.newMenu.termsheetPdf': 'Termsheet (PDF)',
  'docTemplates.unified.newMenu.contractPdf': 'Contract (PDF)',
  'docTemplates.unified.newMenu.disclosurePdf': 'Disclosure (PDF)',
  'docTemplates.unified.newMenu.genericDoc': 'Generic Document (PDF)',
  // SplitPanel-based create flow (replaces the legacy GlossyMenu dropdown +
  // Dialog combo). Step 1 is a kind picker rendered inside the page's
  // SplitPanel; step 2 is the kind-specific upload form.
  'docTemplates.unified.newPanel.title': 'New template',
  'docTemplates.unified.newPanel.subtitle': 'Choose a template type to get started',
  'docTemplates.unified.newPanel.descTermsheetStructured': 'Build a sectioned termsheet — no PDF required.',
  'docTemplates.unified.newPanel.descTermsheetPdf': 'Upload a pre-formatted termsheet PDF.',
  'docTemplates.unified.newPanel.descContractPdf': 'NDA, MSA, partnership, or other contract.',
  'docTemplates.unified.newPanel.descDisclosurePdf': 'Regulatory or compliance disclosure PDF.',
  'docTemplates.unified.newPanel.descGenericDoc': 'Any reusable PDF outside the other types.',
  'docTemplates.unified.filters.type': 'Type',
  'docTemplates.unified.filters.source': 'Source',
  'docTemplates.unified.filters.tags': 'Tags',
  'docTemplates.unified.filters.category': 'Category',
  'docTemplates.unified.filters.status': 'Status',
  'docTemplates.unified.filters.searchPlaceholder': 'Search templates by name…',
  'docTemplates.unified.filters.allTypes': 'All types',
  'docTemplates.unified.filters.allSources': 'All sources',
  'docTemplates.unified.filters.allCategories': 'All categories',
  'docTemplates.unified.filters.allStatuses': 'All statuses',
  'docTemplates.unified.filters.tagsPlaceholder': 'Filter by tags…',
  'docTemplates.unified.type.document': 'Document',
  'docTemplates.unified.type.termsheet': 'Termsheet',
  'docTemplates.unified.source.my': 'My templates',
  'docTemplates.unified.source.system': 'System',
  'docTemplates.unified.status.active': 'Active',
  'docTemplates.unified.status.draft': 'Draft',
  'docTemplates.unified.status.archived': 'Archived',
  'docTemplates.unified.column.name': 'Name',
  'docTemplates.unified.column.type': 'Type',
  'docTemplates.unified.column.source': 'Source',
  'docTemplates.unified.column.tags': 'Tags',
  'docTemplates.unified.column.category': 'Category',
  'docTemplates.unified.column.status': 'Status',
  'docTemplates.unified.column.signers': 'Signers',
  'docTemplates.unified.column.usage': 'Usage',
  'docTemplates.unified.column.updated': 'Updated',
  'docTemplates.unified.empty.title': 'No templates yet',
  'docTemplates.unified.empty.description':
    'Upload a PDF or create a termsheet to start your reusable document library.',
  'docTemplates.unified.empty.searchHint': 'Try clearing your filters or adjusting your search.',
  'docTemplates.unified.create.tooltip': 'New template',
  'docTemplates.unified.create.label': 'New template',
  'astor.nudge.emptyTemplates.message':
    'I can help you spin up a new template — upload a PDF or generate a termsheet from a brief.',
  'docTemplates.unified.actions.edit': 'Edit',
  'docTemplates.unified.actions.duplicate': 'Duplicate',
  'docTemplates.unified.actions.archive': 'Archive',
  'docTemplates.unified.actions.useAsStartingPoint': 'Use as starting point',
  'docTemplates.unified.panel.documentTitle': 'Document template',
  'docTemplates.unified.panel.termsheetTitle': 'Termsheet template',
  'docTemplates.unified.panel.preview': 'Preview',
  'docTemplates.unified.panel.fields': 'Fields',
  'docTemplates.unified.panel.signers': 'Signers',
  'docTemplates.unified.panel.lastSync': 'Last synced',
  'docTemplates.unified.panel.sourceFile': 'Source file',
  'docTemplates.unified.panel.sections': 'Sections',
  'docTemplates.unified.panel.signerPolicy': 'Signer policy',
  'docTemplates.unified.panel.branding': 'Branding',
  'docTemplates.unified.panel.snippet': 'Snippet',
  'docTemplates.unified.duplicateSuffix': '(Copy)',
  'docTemplates.unified.archiveConfirm': 'Archive this template?',
  'docTemplates.unified.archiveConfirmDescription':
    'It will be hidden from the active list. You can restore it from the Archived filter.',
  'docTemplates.unified.duplicated': 'Template duplicated.',
  'docTemplates.unified.archived': 'Template archived.',
  'docTemplates.unified.useAsStartingPointSuccess': 'Copied system template into your library.',
  'docTemplates.unified.savedFilters': 'Filters applied',
  'docTemplates.unified.clearFilters': 'Clear filters',
  // Termsheet-specific category labels (documents reuse `docTemplates.type.*`)
  'docTemplates.unified.category.bridge': 'Bridge',
  'docTemplates.unified.category.fix_and_flip': 'Fix & Flip',
  'docTemplates.unified.category.dscr': 'DSCR',
  'docTemplates.unified.category.construction': 'Construction',
  'docTemplates.unified.category.ground_up': 'Ground-up',
  'docTemplates.unified.category.custom': 'Custom',
  // Upload modal
  'docTemplates.unified.upload.title': 'Upload document template',
  // Issue #1540 / #1533 Phase 4 — kind-specific titles + descriptions for
  // the upload dialog (NewTemplateMenu sub-menu paths).
  'docTemplates.unified.upload.titleContract': 'Upload contract template',
  'docTemplates.unified.upload.titleTermsheet': 'Upload termsheet PDF',
  'docTemplates.unified.upload.titleDisclosure': 'Upload disclosure template',
  'docTemplates.unified.upload.titleGeneric': 'Upload generic document',
  'docTemplates.unified.upload.descriptionContract':
    'Upload a PDF and tag it with a contract type (NDA, MSA, SLA, etc.). After upload you will place fields and assign signers.',
  'docTemplates.unified.upload.descriptionTermsheet':
    'Upload a pre-formatted termsheet PDF. The structured wizard is the alternate path for sectioned termsheets.',
  'docTemplates.unified.upload.descriptionDisclosure':
    'Upload a disclosure PDF (Privacy Notice, Compliance Acknowledgment, etc.). After upload you will place signature fields.',
  'docTemplates.unified.upload.descriptionGeneric':
    'Upload any reusable PDF document. After upload, you will place fields and assign signers.',
  'docTemplates.unified.upload.description':
    'Upload a PDF to create a new reusable signable document. After upload, you will place fields and assign signers.',
  'docTemplates.unified.upload.nameLabel': 'Name',
  'docTemplates.unified.upload.namePlaceholder': 'e.g. Bridge Loan Note',
  // Issue #1545 — Type / Subtype dropdowns replace the legacy single Category select.
  'docTemplates.unified.upload.type': 'Type',
  'docTemplates.unified.upload.subtype': 'Subtype',
  'docTemplates.unified.upload.subtypePlaceholder.contract': 'Pick a contract subtype',
  'docTemplates.unified.upload.subtypePlaceholder.disclosure': 'e.g. privacy_notice (optional)',
  'docTemplates.unified.upload.subtypePlaceholder.generic': 'Optional — describe this document',
  'docTemplates.unified.upload.categoryLabel': 'Category',
  'docTemplates.unified.upload.tagsLabel': 'Tags',
  'docTemplates.unified.upload.tagsHint': 'Press Enter to add a tag',
  'docTemplates.unified.upload.fileHint': 'Drop a PDF here or click to browse (max 25 MB)',
  'docTemplates.unified.upload.fileTooLarge': 'File exceeds the 25 MB upload limit.',
  'docTemplates.unified.upload.fileWrongType': 'Only PDF files are supported.',
  'docTemplates.unified.upload.cancel': 'Cancel',
  'docTemplates.unified.upload.submit': 'Upload',
  'docTemplates.unified.upload.error': "Couldn't upload the template. Please try again.",
  // Side panel — extra labels
  'docTemplates.unified.panel.fieldCount': '{{count}} field(s)',
  'docTemplates.unified.panel.signerCount': '{{count}} signer(s)',
  'docTemplates.unified.panel.usageCount': 'Used {{count}} time(s)',
  'docTemplates.unified.panel.systemTemplate': 'Read-only system template — duplicate to edit.',
  'docTemplates.unified.panel.placeholderSnippet':
    'Sample sections render with merge fields ({{borrower.name}}, {{loan.amount}}…) at send time.',
  'docTemplates.unified.panel.noPreview': 'No preview available yet.',
  'docTemplates.unified.panel.previewError': "Couldn't load preview.",
  'docTemplates.unified.panel.uncategorized': 'Uncategorized',
  // Errors
  'docTemplates.unified.loadError': 'Could not load templates. Please try again.',
  'docTemplates.unified.filterTruncated':
    'Showing the first {{limit}} matches. The category filter may miss results past this cap — refine other filters to narrow the set.',

  // Issue #1540 (#1533 frontend) — unified Template kind / contentType / contractType filter axes
  // The legacy `type=document/termsheet` filter was replaced by the 4-way `kind` enum (CONTRACT,
  // TERMSHEET, DISCLOSURE, ADHOC) plus a separate `contentType` (PDF/STRUCTURED) and a
  // `contractType` sub-filter that's only visible when Kind=Contract.
  'docTemplates.unified.filters.kind': 'Type',
  'docTemplates.unified.filters.allKinds': 'All types',
  'docTemplates.unified.filters.content': 'Content',
  'docTemplates.unified.filters.allContent': 'All content',
  'docTemplates.unified.filters.contractType': 'Subtype',
  'docTemplates.unified.filters.allContractTypes': 'All subtypes',
  'docTemplates.unified.filters.subtype': 'Subtype',
  'docTemplates.unified.filters.allSubtypes': 'All subtypes',
  'docTemplates.unified.kind.CONTRACT': 'Contract',
  'docTemplates.unified.kind.TERMSHEET': 'Termsheet',
  'docTemplates.unified.kind.DISCLOSURE': 'Disclosure',
  'docTemplates.unified.kind.GENERIC': 'Generic',
  'docTemplates.unified.kind.ADHOC': 'Ad-hoc',
  'docTemplates.unified.content.PDF': 'PDF',
  'docTemplates.unified.content.STRUCTURED': 'Structured',
  // User-facing labels — see decision in PR #1543: "System"/"Authored" leaked
  // the internal data-model names; "Platform" / "Custom" matches user intent
  // (shipped by Fiops vs. created by tenant). ADHOC source is dropped from the
  // user-visible filter (case-scoped one-shots don't belong in the reusable
  // library), but the label is kept here so other surfaces that need it
  // (audit logs, debug tools) keep rendering it.
  'docTemplates.unified.source.SYSTEM': 'Platform',
  'docTemplates.unified.source.AUTHORED': 'Custom',
  'docTemplates.unified.source.ADHOC': 'Ad-hoc',
  'docTemplates.unified.status.ACTIVE': 'Active',
  'docTemplates.unified.status.DRAFT': 'Draft',
  'docTemplates.unified.status.ARCHIVED': 'Archived',
  'docTemplates.unified.column.kind': 'Type',
  'docTemplates.unified.column.content': 'Content',
  'docTemplates.unified.actions.versions': 'Versions',
  'docTemplates.unified.panel.pdfTitle': 'PDF template',
  'docTemplates.unified.panel.structuredTitle': 'Structured template',

  // Issue #1540 — TemplateVersionsModal copy
  'docTemplates.unified.versions.title': 'Version history',
  'docTemplates.unified.versions.empty':
    'This template has only one version. Publish a new version to start tracking history.',
  'docTemplates.unified.versions.headBadge': 'Current',
  'docTemplates.unified.versions.revert': 'Revert to this',
  'docTemplates.unified.versions.revertConfirm':
    'Revert to v{{version}}? This creates a new active version copying that revision’s contents — history is preserved.',
  'docTemplates.unified.versions.publishNew': 'Publish new version',
  'docTemplates.unified.versions.publishTitle': 'Publish a new version',
  'docTemplates.unified.versions.publishDescription':
    'Bumps this template to a new active version. Earlier versions remain in the history and can be reverted to at any time.',
  'docTemplates.unified.versions.changelogLabel': "What's changed?",
  'docTemplates.unified.versions.changelogPlaceholder': 'Optional. e.g. "Updated commission rate to 4%"',
  'docTemplates.unified.versions.publishConfirm': 'Publish',

  // Issue #1545 — Subtype labels are DB-driven (merged view of
  // ContractSubtypeTemplate + per-tenant ContractSubtype overrides). The
  // legacy `enums.ContractType.*` keys were removed when the hardcoded
  // ContractType enum was retired.

  // Issue #1474 — Document template editor (DocuSeal CE iframe)
  'docTemplates.editor.title': 'Edit document template',
  'docTemplates.editor.metadataTitle': 'Template details',
  'docTemplates.editor.nameLabel': 'Name',
  'docTemplates.editor.categoryLabel': 'Category',
  'docTemplates.editor.tagsLabel': 'Tags',
  'docTemplates.editor.tagsHint': 'Press Enter to add a tag',
  'docTemplates.editor.statusLabel': 'Status',
  'docTemplates.editor.savingMetadata': 'Saving…',
  'docTemplates.editor.metadataSaved': 'Saved',
  'docTemplates.editor.metadataError': "Couldn't save your changes. Try again.",
  'docTemplates.editor.done': 'Done',
  'docTemplates.editor.back': 'Back to library',
  'docTemplates.editor.fieldsPlaced': '{{count}} field(s) placed',
  'docTemplates.editor.placeAtLeastOneField': 'Place at least one signature field to make this template usable.',
  'docTemplates.editor.notFound': 'Template not found.',
  'docTemplates.editor.loadError': "Couldn't open the editor. Please try again.",
  'docTemplates.editor.loadingEditor': 'Loading editor…',
  'docTemplates.editor.replacePdf': 'Replace PDF',
  'docTemplates.editor.replacePdfIdentical': 'PDF is identical to the current file — no changes applied.',
  'docTemplates.editor.replacePdfUpdated':
    'PDF replaced. Every field binding has been flagged stale — please review them before sending.',
  'docTemplates.editor.replacePdfFailed': "Couldn't replace the PDF. Please try again.",
  'docTemplates.editor.mustForkBeforeUse':
    'This is a platform-canonical template. Click Edit to fork your tenant copy before binding fields.',
  'docTemplates.editor.fork': 'Fork to edit',
  'docTemplates.editor.forkInProgress': 'Forking…',

  // Funds & Payouts
  'pageHeader.funds.title': 'Funds',
  'pageHeader.funds.description': 'Manage payouts and fund disbursements',
  'funds.title': 'Funds',
  'funds.description': 'Manage payouts and fund disbursements.',
  'funds.empty': 'No payouts yet',
  'funds.emptyDescription': 'Create your first payout to disburse funds for a case.',
  'funds.createPayout': 'Create Payout',
  'funds.editPayout': 'Edit Payout',
  'funds.deletePayout': 'Delete Payout',
  'funds.amount': 'Amount',
  'funds.recipient': 'Recipient Name',
  'funds.bankName': 'Bank Name',
  'funds.routingNumber': 'Routing Number',
  'funds.accountNumber': 'Account Number',
  'funds.wireReference': 'Wire Reference',
  'funds.disbursementDate': 'Disbursement Date',
  'funds.type': 'Type',
  'funds.notes': 'Notes',
  'funds.caseId': 'Case ID',
  'funds.status.pending': 'Pending',
  'funds.status.approved': 'Approved',
  'funds.status.disbursed': 'Disbursed',
  'funds.approve': 'Approve',
  'funds.disburse': 'Disburse',
  'funds.confirmDelete': 'Are you sure you want to delete this payout? This action cannot be undone.',
  'funds.approveConfirm': 'Approve this payout?',
  'funds.disburseConfirm': 'Mark this payout as disbursed?',
  'funds.wireReferenceRequired': 'Wire reference is required to disburse',
  'funds.type.wire': 'Wire',
  'funds.type.ach': 'ACH',
  'funds.type.check': 'Check',
  'funds.createSuccess': 'Payout created successfully',
  'funds.updateSuccess': 'Payout updated successfully',
  'funds.approveSuccess': 'Payout approved successfully',
  'funds.disburseSuccess': 'Payout disbursed successfully',
  'funds.deleteSuccess': 'Payout deleted successfully',
  // Placeholders
  'funds.caseIdPlaceholder': 'e.g. case UUID',
  'funds.recipientPlaceholder': 'John Doe',
  'funds.bankNamePlaceholder': 'Bank of America',
  'funds.routingNumberPlaceholder': '021000021',
  'funds.accountNumberPlaceholder': '123456789',

  // Payouts (sub-page)
  'pageHeader.payouts.title': 'Payouts',
  'pageHeader.payouts.description': 'Manage investor payouts and distributions',
  'payouts.title': 'Payouts',
  'payouts.description': 'Manage investor payouts and distributions.',
  'payouts.empty': 'No payouts found',
  'payouts.emptyDescription': 'Payouts will appear here once fund disbursements are created.',

  // Offerings
  'pageHeader.offerings.title': 'Offerings',
  'pageHeader.offerings.description': 'Manage investment offerings and products',
  'offerings.title': 'Offerings',
  'offerings.description': 'Manage investment offerings and loan products.',
  'offerings.empty': 'Investment offerings coming soon',
  'offerings.emptyDescription': 'Create and manage investment offerings, loan products, and marketplace listings.',

  // ============================================================================
  // WEBSITE MANAGEMENT (Issue #637)
  // ============================================================================

  // Page Headers
  'pageHeader.website.title': 'Website',
  'pageHeader.website.description': 'Manage your business website content and settings',

  // Funded Deals
  'website.fundedDeals.title': 'Funded Deals',
  'website.fundedDeals.description': 'Showcase funded deals on your website',
  'website.fundedDeals.createDeal': 'Create Deal',
  'website.fundedDeals.editDeal': 'Edit Deal',
  'website.fundedDeals.deleteDeal': 'Delete Deal',
  'website.fundedDeals.deleteConfirm': 'Are you sure you want to delete this funded deal?',
  'website.fundedDeals.searchPlaceholder': 'Search deals...',
  'website.fundedDeals.location': 'Location',
  'website.fundedDeals.featured': 'Featured',
  'website.fundedDeals.closingDate': 'Closing Date',
  'website.fundedDeals.ltv': 'LTV (%)',
  'website.fundedDeals.fundingTimeline': 'Funding Timeline',
  'website.fundedDeals.fundingTimelineDays': 'Funding Timeline (days)',
  'website.fundedDeals.days': 'days',
  'website.fundedDeals.highlights': 'Deal Highlights',
  'website.fundedDeals.addHighlight': 'Add a highlight...',
  'website.fundedDeals.testimonial': 'Borrower Testimonial',
  'website.fundedDeals.photoUrls': 'Photo URLs',
  'website.fundedDeals.addPhotoUrl': 'Add photo URL...',
  'website.fundedDeals.city': 'City',
  'website.fundedDeals.state': 'State',
  'website.fundedDeals.empty': 'No funded deals yet',
  'website.fundedDeals.emptyDescription': 'Create your first funded deal to showcase on your website.',
  'website.fundedDeals.pageHiddenBanner':
    'This page is currently hidden on your website. Visitors cannot see your funded deals.',
  'website.fundedDeals.publishPage': 'Publish Page',
  'website.fundedDeals.pageHidden': 'Funded deals page hidden from website',
  'website.fundedDeals.pagePublished': 'Funded deals page is now visible on your website',

  // Testimonials
  'website.testimonials.title': 'Testimonials',
  'website.testimonials.description': 'Manage client testimonials for your website',
  'website.testimonials.createTestimonial': 'Create Testimonial',
  'website.testimonials.editTestimonial': 'Edit Testimonial',
  'website.testimonials.deleteTestimonial': 'Delete Testimonial',
  'website.testimonials.deleteConfirm': 'Are you sure you want to delete this testimonial?',
  'website.testimonials.searchPlaceholder': 'Search testimonials...',
  'website.testimonials.author': 'Author',
  'website.testimonials.authorName': 'Author Name',
  'website.testimonials.authorTitle': 'Author Title',
  'website.testimonials.authorCompany': 'Author Company',
  'website.testimonials.authorPhoto': 'Author Photo URL',
  'website.testimonials.content': 'Testimonial Content',
  'website.testimonials.rating': 'Rating',
  'website.testimonials.relatedDeal': 'Related Deal',
  'website.testimonials.featured': 'Featured',
  'website.testimonials.empty': 'No testimonials yet',
  'website.testimonials.emptyDescription': 'Add your first client testimonial to display on your website.',

  // Blog
  'website.blog.title': 'Blog',
  'website.blog.description': 'Manage blog posts for your website',
  'website.blog.createPost': 'Create Post',
  'website.blog.editPost': 'Edit Post',
  'website.blog.deletePost': 'Delete Post',
  'website.blog.deleteConfirm': 'Are you sure you want to delete this blog post?',
  'website.blog.searchPlaceholder': 'Search posts...',
  'website.blog.publishedDate': 'Published Date',
  'website.blog.excerpt': 'Excerpt',
  'website.blog.content': 'Content',
  'website.blog.coverImage': 'Cover Image URL',
  'website.blog.tags': 'Tags',
  'website.blog.addTag': 'Add a tag...',
  'website.blog.seoSettings': 'SEO Settings',
  'website.blog.metaTitle': 'Meta Title',
  'website.blog.metaDescription': 'Meta Description',
  'website.blog.ogImage': 'OG Image URL',
  'website.blog.slugHelper': 'Auto-generated from title. Edit to customize.',
  'website.blog.markdownSupported': 'Markdown formatting is supported.',
  'website.blog.publish': 'Publish',
  'website.blog.unpublish': 'Unpublish',
  'website.blog.empty': 'No blog posts yet',
  'website.blog.emptyDescription': 'Create your first blog post to share on your website.',
  'website.blog.authorName': 'Author Name',
  'website.blog.schedulePublish': 'Schedule Publish',
  'website.blog.scheduledFor': 'Scheduled for {{date}}',

  // Website Funded Deals Scheduling
  'website.fundedDeals.schedulePublish': 'Schedule Publish',
  'website.fundedDeals.scheduledFor': 'Scheduled for {{date}}',

  // CMS Hub (Landing Page)
  'website.hub.contentDashboard': 'Content Dashboard',
  'website.hub.published': 'published',
  'website.hub.drafts': 'drafts',
  'website.hub.blogPosts': 'Blog Posts',
  'website.hub.fundedDeals': 'Funded Deals',
  'website.hub.testimonials': 'Testimonials',
  'website.hub.quickActions': 'Quick Actions',
  'website.hub.newBlogPost': 'New Blog Post',
  'website.hub.addFundedDeal': 'Add Funded Deal',
  'website.hub.addTestimonial': 'Add Testimonial',
  'website.hub.previewSite': 'Preview Site',
  'website.hub.sectionContent': 'Content',
  'website.hub.sectionContentDescription': 'Pages, blog posts, deals, and testimonials',
  'website.hub.sectionDesign': 'Design & Layout',
  'website.hub.sectionDesignDescription': 'Branding, navigation, footer, and announcements',
  'website.hub.sectionConfiguration': 'Settings & Infrastructure',
  'website.hub.sectionConfigurationDescription': 'Domain, SEO, portal links, and integrations',
  'website.hub.announcementBar': 'Announcement Bar',
  'website.hub.announcementBarDescription': 'Top-of-page banner for promotions and updates',
  'website.hub.portalLinks': 'Portal Links',
  'website.hub.portalLinksDescription': 'Borrower login, partner portal, and apply URLs',
  'website.hub.domain': 'Domain',
  'website.hub.domainDescription': 'Custom domain and DNS configuration',
  'website.hub.seo': 'SEO',
  'website.hub.seoDescription': 'Search engine titles, descriptions, and social sharing',

  // Website CMS - SEO Health
  'website.seo.healthGood': 'SEO: Good',
  'website.seo.healthFair': 'SEO: Needs Work',
  'website.seo.healthPoor': 'SEO: Poor',
  'website.seo.missingTitle': 'Missing meta title',
  'website.seo.missingDescription': 'Missing meta description',
  'website.seo.missingOgImage': 'Missing OG image',
  'website.seo.titleTooShort': 'Meta title too short (aim for 50-60 characters)',
  'website.seo.titleTooLong': 'Meta title too long (aim for 50-60 characters)',
  'website.seo.descriptionTooShort': 'Meta description too short (aim for 120-160 characters)',
  'website.seo.descriptionTooLong': 'Meta description too long (aim for 120-160 characters)',

  // Website Templates
  'website.templates.bridge-flip.name': 'Bridge & Flip Specialist',
  'website.templates.bridge-flip.description': 'Speed-focused template for fix & flip and bridge lenders',
  'website.templates.dscr-rental.name': 'DSCR Rental Lender',
  'website.templates.dscr-rental.description': 'Investor-focused template for DSCR and rental lenders',
  'website.templates.full-service.name': 'Full-Service Lender',
  'website.templates.full-service.description': 'Comprehensive template for multi-product lending shops',
  'website.templates.construction.name': 'Construction Lender',
  'website.templates.construction.description': 'Specialized template for ground-up and heavy rehab lenders',
  'website.templates.commercial.name': 'Commercial Lender',
  'website.templates.commercial.description': 'Corporate-style template for large commercial deals',

  // Website Settings
  'website.settings.title': 'Website Settings',
  'website.settings.description': 'Configure your website template, domain, SEO, and integrations',
  'website.settings.templateSelection': 'Template Selection',
  'website.settings.chooseTemplate': 'Choose Template',
  'website.settings.pagesAndFeatures': 'Pages & Features',
  'website.settings.pagesAndFeaturesDescription':
    'Choose which pages to include on your website. Home page is always enabled.',
  'website.settings.pages.about': 'About',
  'website.settings.pages.programs': 'Loan Programs',
  'website.settings.pages.contact': 'Contact',
  'website.settings.pages.team': 'Team',
  'website.settings.pages.blog': 'Blog',
  'website.settings.pages.fundedDeals': 'Funded Deals',
  'website.settings.pages.partner': 'Partner',
  'website.settings.pages.investor': 'Investor',
  'website.settings.domainConfiguration': 'Domain Configuration',
  'website.settings.platformSubdomain': 'Platform Subdomain',
  'website.settings.platformSubdomainHelper': 'Your default Fiops address — always active, even with a custom domain',
  'website.settings.currentDomain': 'Current Domain',
  'website.settings.currentDomainHelper': 'Your default Fiops subdomain',
  'website.settings.customDomain': 'Custom Domain',
  'website.settings.customDomainHelper': 'Point your own domain to your website',
  'website.settings.customDomainActive': 'Custom Domain',
  'website.settings.noCustomDomain':
    'No custom domain configured. Set one up to use your own domain for your website and portals.',
  'website.settings.setupCustomDomain': 'Set up custom domain',
  'website.settings.seoDefaults': 'SEO Defaults',
  'website.settings.titleTemplate': 'Title Template',
  'website.settings.titleTemplateHelper': 'Use {{page_title}} and {{business_name}} as placeholders',
  'website.settings.defaultDescription': 'Default Meta Description',
  'website.settings.ogImage': 'Default OG Image',
  'website.settings.ogImageHelper': 'Recommended size: 1200x630 pixels',
  'website.settings.integrations': 'Integration Settings',
  'website.settings.ga4Id': 'Google Analytics 4 ID',
  'website.settings.ga4IdHelper': 'Your GA4 measurement ID (e.g., G-XXXXXXXXXX)',
  'website.settings.calendlyUrl': 'Calendly URL',
  'website.settings.enableChat': 'Enable Live Chat Widget',
  'website.settings.portalLinks': 'Portal Links',
  'website.settings.applyNowUrl': 'Apply Now URL',
  'website.settings.applyNowUrlHelper': 'Link for the "Apply Now" button on your website',
  'website.settings.borrowerLoginUrl': 'Borrower Login URL',
  'website.settings.partnerPortalUrl': 'Partner Portal URL',
  'website.settings.announcementBar': 'Announcement Bar',
  'website.settings.announcementBarDescription':
    'Show a promotional banner above your navigation bar to highlight rates, offers, or news.',
  'website.settings.enableAnnouncementBar': 'Enable Announcement Bar',
  'website.settings.announcementBadge': 'Badge Text',
  'website.settings.announcementBadgeHelper': 'Short accent label (e.g. "New Rate", "Limited Time")',
  'website.settings.announcementMessage': 'Message',
  'website.settings.announcementLinkText': 'Link Text',
  'website.settings.announcementLinkHref': 'Link URL',
  'website.settings.announcementDismissible': 'Allow visitors to dismiss the banner',
  'website.settings.sitePublished': 'Site Published',
  'website.settings.siteUnpublished': 'Site Unpublished',

  // Website CMS - Branding
  'website.branding.title': 'Branding',
  'website.branding.description': 'Customize your website logo, colors, and fonts',
  'website.branding.companyName': 'Company Name',
  'website.branding.brandName': 'Brand Name',
  'website.branding.brandNameHint': 'The name displayed on your website. Defaults to your business profile name.',
  'website.branding.logo': 'Logo',
  'website.branding.logoAlt': 'Logo Alt Text',
  'website.branding.favicon': 'Favicon',
  'website.branding.primaryColor': 'Primary Color',
  'website.branding.secondaryColor': 'Secondary Color',
  'website.branding.accentColor': 'Accent Color',
  'website.branding.fontFamily': 'Body Font',
  'website.branding.headingFontFamily': 'Heading Font',
  'website.branding.themeMode': 'Theme Mode',
  'website.branding.ogImage': 'Social Share Image',
  'website.branding.uploadLogo': 'Upload Logo',
  'website.branding.uploadFavicon': 'Upload Favicon',
  'website.branding.darkMode': 'Dark Mode',
  'website.branding.lightMode': 'Light Mode',
  'website.branding.systemMode': 'System (Auto)',
  'website.branding.logoDark': 'Dark Mode Logo',
  'website.branding.logoDarkHelper': 'Optional logo displayed when the site is in dark mode',
  'website.branding.uploadLogoDark': 'Upload Dark Logo',
  'website.branding.customCss': 'Custom CSS',
  'website.branding.customCssHelper': 'Advanced: Injected into your website. Max 50KB.',
  'website.branding.themeModeLight': 'Light',
  'website.branding.themeModeDark': 'Dark',
  'website.branding.themeModeSystem': 'System',
  'website.branding.heroLayout': 'Hero Layout',
  'website.branding.heroLayoutHelper': 'Choose the layout style for the hero section on your homepage.',
  'website.branding.heroVariantSplitScreen': 'Split Screen',
  'website.branding.heroVariantPrimary': 'Full Width Image',
  'website.branding.heroVariantAnimatedGradient': 'Animated Gradient',
  'website.branding.heroVariantMinimalText': 'Minimal Text',
  'website.branding.heroVariantVideoBackground': 'Video Background',

  // Website CMS - Portal Links
  'website.portalLinks.partnerPortal': 'Partner Portal URL',
  'website.portalLinks.investorPortal': 'Investor Portal URL',

  // Website CMS - Navigation
  'website.navigation.title': 'Navigation',
  'website.navigation.description': 'Configure your website navigation menu and CTA button',
  'website.navigation.menuItems': 'Menu Items',
  'website.navigation.addItem': 'Add Menu Item',
  'website.navigation.addChild': 'Add Sub-Item',
  'website.navigation.label': 'Label',
  'website.navigation.href': 'Link URL',
  'website.navigation.ctaButton': 'CTA Button',
  'website.navigation.sticky': 'Sticky Navigation',
  'website.navigation.showPhone': 'Show Phone Number',
  'website.navigation.phone': 'Phone Number',
  'website.navigation.variant': 'Variant',
  'website.navigation.variantPrimary': 'Primary',
  'website.navigation.variantOutline': 'Outline',
  'website.navigation.settings': 'Settings',

  // Website CMS - Pages
  'website.pages.title': 'Pages',
  'website.pages.description': 'Manage your website pages and their content',
  'website.pages.createPage': 'Create Page',
  'website.pages.editPage': 'Edit Page',
  'website.pages.pageSlug': 'URL Slug',
  'website.pages.pageTitle': 'Page Title',
  'website.pages.published': 'Published',
  'website.pages.sections': 'Sections',
  'website.pages.addSection': 'Add Section',
  'website.pages.sectionType': 'Section Type',
  'website.pages.noPages': 'No pages configured yet',
  'website.pages.editMode': 'Edit',
  'website.pages.previewMode': 'Preview',
  'website.pages.desktopView': 'Desktop',
  'website.pages.tabletView': 'Tablet',
  'website.pages.mobileView': 'Mobile',
  'website.pages.previewUnavailable': 'Preview unavailable — configure your domain in Website Settings first',
  'website.pages.seoSettings': 'SEO',
  'website.pages.seoTitle': 'SEO Title',
  'website.pages.metaDescription': 'Meta Description',
  'website.pages.sectionHeading': 'Heading',
  'website.pages.sectionSubheading': 'Subheading',
  'website.pages.sectionBodyText': 'Body Text',
  'website.pages.sectionCtaLabel': 'CTA Label',
  'website.pages.sectionCtaLink': 'CTA Link',
  'website.pages.slugPlaceholder': 'my-new-page',
  'website.pages.slugHelper': 'URL path for this page',
  'website.pages.draft': 'Draft',
  'website.pages.customPage': 'Custom',
  'website.pages.deleteConfirmation': 'Are you sure you want to delete the page "{{slug}}"? This cannot be undone.',

  // Website CMS - Footer
  'website.footer.title': 'Footer',
  'website.footer.description': 'Customize your website footer content and links',
  'website.footer.tagline': 'Tagline',
  'website.footer.columns': 'Footer Columns',
  'website.footer.addColumn': 'Add Column',
  'website.footer.columnHeading': 'Column Heading',
  'website.footer.compliance': 'Compliance',
  'website.footer.showNmls': 'Show NMLS Company ID',
  'website.footer.nmlsId': 'NMLS Company ID',
  'website.footer.equalHousing': 'Show Equal Housing Lender',
  'website.footer.disclaimer': 'Legal Disclaimer',
  'website.footer.legalLinks': 'Legal Links',
  'website.footer.linkLabel': 'Link Label',
  'website.footer.linkUrl': 'Link URL',
  'website.footer.addLink': 'Add Link',

  // Website CMS - Asset Upload
  'website.upload.title': 'Upload Image',
  'website.upload.dragDrop': 'Drag and drop an image, or click to browse',
  'website.upload.maxSize': 'Maximum file size: 5MB',
  'website.upload.acceptedFormats': 'Accepted formats: JPEG, PNG, WebP, SVG',
  'website.upload.uploading': 'Uploading...',
  'website.upload.replace': 'Replace',
  'website.upload.remove': 'Remove',
  'website.upload.uploadFailed': 'Upload failed. Please try again.',
  'website.upload.fileTooLarge': 'File is too large. Maximum size is 5MB.',
  'website.upload.invalidFormat': 'Invalid file format.',
  'website.upload.error': 'Upload failed. Please try again.',

  // ============================================================================
  // MARKETING DASHBOARD (Issue #638)
  // ============================================================================

  'marketing.title': 'Marketing',
  'marketing.description': 'Marketing performance dashboard and analytics',

  // Quick Stats
  'marketing.stats.leadsThisWeek': 'Leads This Week',
  'marketing.stats.applicationsThisWeek': 'Applications This Week',
  'marketing.stats.conversionRate': 'Conversion Rate',
  'marketing.stats.pipelineValue': 'Pipeline Value',

  // Charts
  'marketing.leadFunnel': 'Lead Funnel',
  'marketing.leadQuality': 'Lead Quality Distribution',
  'marketing.count': 'Count',
  'marketing.leads': 'Leads',

  // Tables
  'marketing.topSources': 'Top Converting Sources',
  'marketing.source': 'Source',
  'marketing.funded': 'Funded',
  'marketing.conversionRate': 'Conversion Rate',
  'marketing.pipelineValue': 'Pipeline Value',
  'marketing.campaignPerformance': 'Campaign Performance',
  'marketing.sequenceName': 'Sequence Name',
  'marketing.enrolled': 'Enrolled',
  'marketing.opens': 'Opens',
  'marketing.clicks': 'Clicks',
  'marketing.conversions': 'Conversions',

  // ============================================================================
  // CONSUMER PORTAL - Page Titles (AppNavBar metadata)
  // ============================================================================
  'consumerPortal.submitNewRequest': 'Submit New Request',
  'consumerPortal.proposals': 'Proposals',
  'consumerPortal.proposalsDescription': 'Review and respond to proposals from your service provider.',
  'consumerPortal.commissions': 'Commissions',
  'consumerPortal.commissionsDescription': 'Track your commission earnings and payouts.',
  'consumerPortal.myReferrals': 'My Referrals',
  'consumerPortal.myReferralsDescription': 'Track your referrals and their status.',
  'consumerPortal.reports': 'Reports',
  'consumerPortal.reportsDescription': 'View and download your reports.',
  'consumerPortal.myQuotes': 'My Quotes',
  'consumerPortal.myQuotesDescription': 'View and compare quotes you have received.',
  'consumerPortal.myEntities': 'My Entities',

  // Consumer Entity Hub (Issue #1199)
  'consumer.entityHub.title': 'Entity Details',
  'consumer.entityHub.description': 'View cases, ownership, and documents for this entity',
  'consumer.entityHub.cases': 'Linked Loans',
  'consumer.entityHub.noCases': 'No loans linked to this entity',
  'consumer.entityHub.viewHub': 'View Entity Hub',
  'consumer.entityHub.kpiActiveCases': 'Active Loans',
  'consumer.entityHub.kpiActiveGuarantees': 'Active Guarantees',

  // Consumer Guarantor Hub (Issue #1199)
  'consumer.guarantorHub.title': 'My Guarantee Obligations',
  'consumer.guarantorHub.description': 'View your guarantee obligations across all loans',
  'consumer.guarantorHub.active': 'Active Obligations',
  'consumer.guarantorHub.history': 'History',
  'consumer.guarantorHub.caseNumber': 'Loan',
  'consumer.guarantorHub.obligation': 'Your Obligation',
  'consumer.guarantorHub.amount': 'Amount',
  'consumer.guarantorHub.status': 'Status',
  'consumer.guarantorHub.expiration': 'Expires',
  'consumer.guarantorHub.empty': 'No guarantee obligations',
  'consumer.guarantorHub.emptyDescription': 'You do not currently guarantee any loans.',
  'consumer.guarantorHub.expiringSoon': 'Expiring Soon',
  'consumer.guarantorHub.totalExposure': 'Total Exposure',
  'consumer.guarantorHub.activeGuarantees': 'Active Guarantees',

  'consumerPortal.viewStatement': 'View Statement',
  'consumerPortal.submitReferral': 'Submit Referral',
  'consumerPortal.referralPartnerOnly': 'Partner Access Required',
  'consumerPortal.referralPartnerOnlyDescription':
    'Only referral partners and channel partners can submit referrals. If you believe this is an error, please contact support.',
  'consumerPortal.goBack': 'Go Back',
  'consumerPortal.dateRange.last7Days': 'Last 7 days',
  'consumerPortal.dateRange.last30Days': 'Last 30 days',
  'consumerPortal.dateRange.last90Days': 'Last 90 days',
  'consumerPortal.dateRange.last12Months': 'Last 12 months',
  'consumerPortal.dateRange.allTime': 'All time',

  // ============================================================================
  // Loan Modifications (Issue #617)
  // ============================================================================
  'modifications.title': 'Loan Modifications',
  'modifications.empty': 'No modifications have been requested for this loan.',
  'modifications.requestModification': 'Request Modification',
  'modifications.viewDetails': 'View Details',

  // Modification types
  'modifications.type.RATE_CHANGE': 'Rate Change',
  'modifications.type.TERM_EXTENSION': 'Term Extension',
  'modifications.type.MATURITY_EXTENSION': 'Maturity Extension',
  'modifications.type.FORBEARANCE': 'Forbearance',
  'modifications.type.ASSUMPTION': 'Assumption',
  'modifications.type.PARTIAL_PAYOFF': 'Partial Payoff',

  // Modification statuses
  'modifications.status.DRAFT': 'Draft',
  'modifications.status.PENDING_APPROVAL': 'Pending Approval',
  'modifications.status.APPROVED': 'Approved',
  'modifications.status.REJECTED': 'Rejected',
  'modifications.status.APPLIED': 'Applied',
  'modifications.status.CANCELLED': 'Cancelled',
  'modifications.status.EXPIRED': 'Expired',

  // Form fields
  'modifications.form.title': 'Request Loan Modification',
  'modifications.form.editTitle': 'Edit Modification',
  'modifications.form.type': 'Modification Type',
  'modifications.form.typePlaceholder': 'Select modification type',
  'modifications.form.effectiveDate': 'Effective Date',
  'modifications.form.reason': 'Reason for Modification',
  'modifications.form.reasonPlaceholder': 'Explain why this modification is needed...',
  'modifications.form.notes': 'Additional Notes',
  'modifications.form.notesPlaceholder': 'Optional notes or context...',
  'modifications.form.currentTerms': 'Current Terms',
  'modifications.form.newTerms': 'New Terms',
  'modifications.form.newRate': 'New Interest Rate (%)',
  'modifications.form.newTerm': 'New Term (months)',
  'modifications.form.newMaturityDate': 'New Maturity Date',
  'modifications.form.forbearanceMonths': 'Forbearance Period (months)',
  'modifications.form.payoffAmount': 'Payoff Amount',
  'modifications.form.amortizationPreview': 'Amortization Preview',

  // Detail view
  'modifications.detail.title': 'Modification Details',
  'modifications.detail.requestedBy': 'Requested By',
  'modifications.detail.requestedOn': 'Requested On',
  'modifications.detail.effectiveDate': 'Effective Date',
  'modifications.detail.beforeTerms': 'Before',
  'modifications.detail.afterTerms': 'After',
  'modifications.detail.termComparison': 'Term Comparison',
  'modifications.detail.approvalChain': 'Approval Chain',
  'modifications.detail.approvedAt': 'Approved At',
  'modifications.detail.appliedAt': 'Applied At',
  'modifications.detail.appliedBy': 'Applied By',
  'modifications.detail.amortizationSchedule': 'Amortization Schedule',

  // Term labels
  'modifications.terms.interestRate': 'Interest Rate',
  'modifications.terms.loanTerm': 'Loan Term',
  'modifications.terms.monthlyPayment': 'Monthly Payment',
  'modifications.terms.remainingBalance': 'Remaining Balance',
  'modifications.terms.maturityDate': 'Maturity Date',
  'modifications.terms.totalInterest': 'Total Interest',

  // Actions
  'modifications.actions.submit': 'Submit for Approval',
  'modifications.actions.approve': 'Approve',
  'modifications.actions.reject': 'Reject',
  'modifications.actions.apply': 'Apply Modification',
  'modifications.actions.cancel': 'Cancel Modification',
  'modifications.actions.edit': 'Edit',

  // Table columns
  'modifications.columns.type': 'Type',
  'modifications.columns.status': 'Status',
  'modifications.columns.effectiveDate': 'Effective Date',
  'modifications.columns.requestedBy': 'Requested By',
  'modifications.columns.approvalProgress': 'Approvals',
  'modifications.columns.createdAt': 'Created',

  // ============================================================================
  // RELEASE SCHEDULE (Issue #1468 — Pack Deal Multi-Unit Release)
  // ============================================================================
  'releaseSchedule.title': 'Release Schedule',
  'releaseSchedule.subtitle': 'Per-unit release tracking for multi-unit pack deals',
  'releaseSchedule.empty': 'No units have been added to this release schedule yet.',
  'releaseSchedule.addUnit': 'Add Unit',
  'releaseSchedule.bulkAdd': 'Bulk Add',
  'releaseSchedule.summary.totalAllocated': 'Total Allocated',
  'releaseSchedule.summary.released': 'Released',
  'releaseSchedule.summary.openBalance': 'Open Balance',
  'releaseSchedule.summary.unitsCount': '{{count}} units',
  'releaseSchedule.summary.releasedCount': '{{count}} of {{total}}',

  // Status labels
  'releaseSchedule.status.PENDING': 'Pending',
  'releaseSchedule.status.UNDER_CONTRACT': 'Under Contract',
  'releaseSchedule.status.RELEASED': 'Released',
  'releaseSchedule.status.CANCELLED': 'Cancelled',

  // Table columns
  'releaseSchedule.columns.sequence': '#',
  'releaseSchedule.columns.label': 'Unit',
  'releaseSchedule.columns.allocated': 'Allocated',
  'releaseSchedule.columns.releasePrice': 'Release Price',
  'releaseSchedule.columns.status': 'Status',
  'releaseSchedule.columns.releasedAt': 'Released',
  'releaseSchedule.columns.actions': 'Actions',

  // Actions
  'releaseSchedule.actions.markUnderContract': 'Mark Under Contract',
  'releaseSchedule.actions.release': 'Release',
  'releaseSchedule.actions.cancel': 'Cancel',
  'releaseSchedule.actions.delete': 'Delete',
  'releaseSchedule.actions.edit': 'Edit',

  // Form fields
  'releaseSchedule.form.addTitle': 'Add Unit',
  'releaseSchedule.form.editTitle': 'Edit Unit',
  'releaseSchedule.form.label': 'Unit Label',
  'releaseSchedule.form.labelPlaceholder': 'e.g. Unit A, Lot 12, 1234 Main #2B',
  'releaseSchedule.form.beds': 'Beds',
  'releaseSchedule.form.baths': 'Baths',
  'releaseSchedule.form.sqft': 'Sq Ft',
  'releaseSchedule.form.allocatedAmount': 'Allocated Amount',
  'releaseSchedule.form.releasePrice': 'Release Price',
  'releaseSchedule.form.notes': 'Notes',
  'releaseSchedule.form.notesPlaceholder': 'Optional notes about this unit...',
  'releaseSchedule.form.address': 'Address',
  'releaseSchedule.form.street': 'Street',
  'releaseSchedule.form.city': 'City',
  'releaseSchedule.form.state': 'State',
  'releaseSchedule.form.zip': 'Zip',
  'releaseSchedule.form.save': 'Save',
  'releaseSchedule.form.cancel': 'Cancel',

  // Release form
  'releaseSchedule.releaseForm.title': 'Release Unit',
  'releaseSchedule.releaseForm.releasedAt': 'Release Date',
  'releaseSchedule.releaseForm.releasedTo': 'Released To (Buyer)',
  'releaseSchedule.releaseForm.releasedToPlaceholder': 'Buyer name or ID',
  'releaseSchedule.releaseForm.notes': 'Notes',
  'releaseSchedule.releaseForm.confirm': 'Confirm Release',

  // Errors / validation
  'releaseSchedule.errors.allocatedRequired': 'Allocated amount is required',
  'releaseSchedule.errors.allocatedPositive': 'Allocated amount must be greater than zero',
  'releaseSchedule.errors.labelRequired': 'Unit label is required',
  'releaseSchedule.errors.releasedToRequired': 'Buyer is required to release a unit',
  // COMPARABLES (Issue #1467) — sale & rent comps tied to a Case.
  // ============================================================================
  'comparables.title': 'Comparables',
  'comparables.empty': 'No comparables have been added for this loan.',
  'comparables.emptySubTab': 'No {kind} comparables yet.',
  'comparables.add': 'Add Comparable',
  'comparables.edit': 'Edit Comparable',
  'comparables.delete': 'Delete Comparable',
  'comparables.deleteConfirm': 'Are you sure you want to delete this comparable? This cannot be undone.',
  'comparables.loadError': 'Failed to load comparables.',

  // Sub-tabs
  'comparables.tab.sale': 'Sale Comps',
  'comparables.tab.rent': 'Rent Comps',
  'comparables.kind.SALE': 'Sale',
  'comparables.kind.RENT': 'Rent',

  // Form / detail labels
  'comparables.form.addressStreet': 'Street',
  'comparables.form.addressCity': 'City',
  'comparables.form.addressState': 'State',
  'comparables.form.addressZip': 'ZIP',
  'comparables.form.beds': 'Beds',
  'comparables.form.baths': 'Baths',
  'comparables.form.sqft': 'Square Feet',
  'comparables.form.salePrice': 'Sale Price',
  'comparables.form.monthlyRent': 'Monthly Rent',
  'comparables.form.closedAt': 'Closed Date',
  'comparables.form.distanceMi': 'Distance (mi)',
  'comparables.form.source': 'Source',
  'comparables.form.sourcePlaceholder': 'MLS / Zillow / Redfin / Internal',
  'comparables.form.notes': 'Notes',
  'comparables.form.notesPlaceholder': 'Optional context, condition, comparability notes...',

  // Table columns (shared)
  'comparables.column.address': 'Address',
  'comparables.column.beds': 'Beds',
  'comparables.column.baths': 'Baths',
  'comparables.column.sqft': 'Sqft',
  'comparables.column.price': 'Price',
  'comparables.column.rent': 'Rent / mo',
  'comparables.column.closedAt': 'Closed',
  'comparables.column.distanceMi': 'Distance',
  'comparables.column.source': 'Source',
  'comparables.column.notes': 'Notes',
  'comparables.column.actions': 'Actions',

  // Flat column-header aliases used by auto-pulled comps tables
  // (PropertyAnalysis ComparablesSection, Issue #1617)
  'comparables.address': 'Address',
  'comparables.beds': 'Beds',
  'comparables.baths': 'Baths',
  'comparables.sqft': 'Sqft',
  'comparables.price': 'Price',
  'comparables.rent': 'Rent / mo',
  'comparables.closedAt': 'Closed',
  'comparables.distance': 'Distance',

  // ============================================================================
  // EXIT HISTORY (Issue #1467) — historical loan exits per borrower.
  // Read by the BorrowerTier classifier (Issue #1469).
  // ============================================================================
  'exitHistory.title': 'Exit History',
  'exitHistory.empty': 'No prior exits recorded for this borrower.',
  'exitHistory.add': 'Add Exit',
  'exitHistory.edit': 'Edit Exit',
  'exitHistory.delete': 'Delete Exit',
  'exitHistory.viewLinkedLoan': 'View linked loan',
  'exitHistory.deleteConfirm': 'Are you sure you want to delete this exit record? This cannot be undone.',
  'exitHistory.loadError': 'Failed to load exit history.',
  'exitHistory.statBadge': '{{count}} exits in trailing 36 months',

  // Outcomes
  'exitHistory.outcome.FUNDED': 'Funded',
  'exitHistory.outcome.PAID_OFF': 'Paid Off',
  'exitHistory.outcome.DEFAULTED': 'Defaulted',
  'exitHistory.outcome.SOLD': 'Sold',

  // Form labels
  'exitHistory.form.outcome': 'Outcome',
  'exitHistory.form.closedAt': 'Closed Date',
  'exitHistory.form.caseId': 'Linked Loan',
  'exitHistory.form.caseIdSelfReported': 'Self-reported (no linked loan)',
  'exitHistory.form.addressStreet': 'Property Street',
  'exitHistory.form.addressCity': 'City',
  'exitHistory.form.addressState': 'State',
  'exitHistory.form.addressZip': 'ZIP',
  'exitHistory.form.loanAmount': 'Loan Amount',
  'exitHistory.form.purchasePrice': 'Purchase Price',
  'exitHistory.form.salePrice': 'Sale Price',
  'exitHistory.form.daysToExit': 'Days to Exit',
  'exitHistory.form.notes': 'Notes',
  'exitHistory.form.notesPlaceholder': 'Optional context for this exit...',

  // Table columns
  'exitHistory.column.outcome': 'Outcome',
  'exitHistory.column.closedAt': 'Closed',
  'exitHistory.column.propertyAddress': 'Property',
  'exitHistory.column.loanAmount': 'Loan',
  'exitHistory.column.purchasePrice': 'Purchase',
  'exitHistory.column.salePrice': 'Sale',
  'exitHistory.column.daysToExit': 'Days',
  'exitHistory.column.source': 'Source',
  'exitHistory.column.notes': 'Notes',
  'exitHistory.column.actions': 'Actions',

  // Source badge — caseId-or-self-reported
  'exitHistory.source.linked': 'In-system',
  'exitHistory.source.selfReported': 'Self-reported',

  // ============================================================================
  // NOTIFICATIONS (Issue #699 - Notification System Fidelity)
  // ============================================================================
  // Bell & Badge
  'notification.bell.tooltip': 'Notifications',
  'notification.bell.noNew': 'No new notifications',
  'notification.bell.countLabel': '{{count}} unread notifications',

  // Dropdown
  'notification.dropdown.title': 'Notifications',
  'notification.dropdown.markAllRead': 'Mark all as read',
  'notification.dropdown.viewAll': 'View all notifications',
  'notification.dropdown.empty': "You're all caught up",
  'notification.dropdown.emptyDescription': 'No new notifications right now.',
  'notification.dropdown.loading': 'Loading notifications...',
  'notification.dropdown.filter.all': 'All',
  'notification.dropdown.filter.mentions': 'Mentions',
  'notification.dropdown.filter.astor': 'Astor',

  // Popover (v3 — Issue #1866)
  'notification.popover.title': 'Notifications',
  'notification.popover.unreadChip': '{{count}} new',
  'notification.popover.markAllRead': 'Mark all read',
  'notification.popover.openInbox': 'Open Inbox',
  'notification.popover.empty': "You're all caught up",
  'notification.popover.emptyDescription': 'No new notifications right now.',
  'notification.popover.loading': 'Loading notifications...',
  'notification.popover.filter.label': 'Notification filters',
  'notification.popover.filter.all': 'All',
  'notification.popover.filter.mentions': 'Mentions',
  'notification.popover.filter.astor': 'Astor',

  // Inbox
  'notification.inbox.title': 'Notifications',
  'notification.inbox.description': 'Manage your notifications and preferences.',
  'notification.inbox.all': 'All',
  'notification.inbox.unread': 'Unread',
  'notification.inbox.read': 'Read',
  'notification.inbox.archived': 'Archived',
  'notification.inbox.empty': 'No notifications',
  'notification.inbox.emptyDescription': "When you receive notifications, they'll show up here.",
  'notification.inbox.emptyUnread': 'No unread notifications',
  'notification.inbox.emptyUnreadDescription': "You've read all your notifications.",
  'notification.inbox.loadMore': 'Load more',
  'notification.inbox.markAllRead': 'Mark all as read',
  'notification.inbox.search': 'Search notifications...',
  'notification.inbox.error': 'Unable to load notifications. Please try again later.',

  // Item
  'notification.item.markRead': 'Mark as read',
  'notification.item.markUnread': 'Mark as unread',
  'notification.item.archive': 'Archive',
  'notification.item.delete': 'Delete',
  'notification.item.viewDetails': 'View details',
  'notification.item.justNow': 'Just now',
  'notification.item.minutesAgo': '{{count}}m ago',
  'notification.item.hoursAgo': '{{count}}h ago',
  'notification.item.daysAgo': '{{count}}d ago',
  'notification.item.weeksAgo': '{{count}}w ago',

  // Group Card
  'notification.group.title': '{{count}} notifications',
  'notification.group.expand': 'Show all',
  'notification.group.collapse': 'Show less',
  'notification.group.markAllRead': 'Mark group as read',

  // Preferences
  'notification.preferences.title': 'Notification Preferences',
  'notification.preferences.description': 'Choose how and when you receive notifications.',
  'notification.preferences.channels': 'Channels',
  'notification.preferences.channelEmail': 'Email',
  'notification.preferences.channelEmailDescription': 'Receive notifications via email.',
  'notification.preferences.channelSms': 'SMS',
  'notification.preferences.channelSmsDescription': 'Receive notifications via text message.',
  'notification.preferences.channelPush': 'Push',
  'notification.preferences.channelPushDescription': 'Receive browser push notifications.',
  'notification.preferences.channelInApp': 'In-App',
  'notification.preferences.channelInAppDescription': 'See notifications inside the app.',
  'notification.preferences.quietHours': 'Quiet Hours',
  'notification.preferences.quietHoursDescription': 'Pause notifications during these hours.',
  'notification.preferences.quietHoursStart': 'Start time',
  'notification.preferences.quietHoursEnd': 'End time',
  'notification.preferences.digest': 'Digest Mode',
  'notification.preferences.digestDescription': 'Batch notifications into periodic summaries.',
  'notification.preferences.digestFrequency': 'Digest Frequency',
  'notification.preferences.digestDaily': 'Daily',
  'notification.preferences.digestWeekly': 'Weekly',
  'notification.preferences.digestMonthly': 'Monthly',
  'notification.preferences.categories': 'Categories',
  'notification.preferences.categoriesDescription': 'Toggle notifications by category.',
  'notification.preferences.saved': 'Preferences saved',
  'notification.preferences.category.caseUpdates': 'Application Updates',
  'notification.preferences.category.caseUpdatesDescription': 'Status changes and updates to your applications.',
  'notification.preferences.category.documentRequests': 'Document Requests',
  'notification.preferences.category.documentRequestsDescription': 'Requests for additional documents.',
  'notification.preferences.category.messages': 'Messages',
  'notification.preferences.category.messagesDescription': 'New messages from your loan officer.',
  'notification.preferences.category.proposals': 'Proposals & Offers',
  'notification.preferences.category.proposalsDescription': 'New proposals and term sheet offers.',
  'notification.preferences.category.deadlines': 'Deadlines & Reminders',
  'notification.preferences.category.deadlinesDescription': 'Upcoming deadlines and action reminders.',

  // Severity labels
  'notification.severity.info': 'Info',
  'notification.severity.warning': 'Warning',
  'notification.severity.critical': 'Critical',
  'notification.severity.success': 'Success',

  // Type labels
  'notification.type.system': 'System',
  'notification.type.document': 'Document',
  'notification.type.agent': 'AI Agent',
  'notification.type.billing': 'Billing',
  'notification.type.security': 'Security',
  'notification.type.user': 'User',
  'notification.type.integration': 'Integration',
  'notification.type.custom': 'Custom',

  // Channel labels
  'notification.channel.email': 'Email',
  'notification.channel.sms': 'SMS',
  'notification.channel.push': 'Push',
  'notification.channel.inApp': 'In-App',

  // Push notification prompt
  'notification.pushPromptTitle': 'Get real-time updates',
  'notification.pushPromptBody': 'Enable push notifications to get instant updates on your {{entityType}}.',

  // E-Signature notification events (Issue #1283)
  'notification.esign.envelope_sent': 'You have a document to sign',
  'notification.esign.envelope_sent.description': '{{senderName}} sent you a document for e-signature',
  'notification.esign.envelope_completed': 'Document signed',
  'notification.esign.envelope_completed.description': '{{signerName}} has signed {{documentName}}',
  'notification.esign.envelope_voided': 'Signing request cancelled',
  'notification.esign.envelope_voided.description': 'The signing request for {{documentName}} has been cancelled',
  'notification.esign.envelope_expired': 'Signing request expired',
  'notification.esign.envelope_expired.description': 'The signing request for {{documentName}} has expired',

  // ============================================================================
  // DOMAIN INTELLIGENCE & DUPLICATE DETECTION
  // ============================================================================
  'domainIntelligence.signupDuplicateDetected':
    'Your company may already have an account on this platform. Your registration has been submitted for review.',
  'domainIntelligence.signupPendingReview.title': 'Registration Under Review',
  'domainIntelligence.signupPendingReview.matchInfo':
    'Your email domain matches an existing organization: {{business}}.',
  'domainIntelligence.signupPendingReview.nextSteps':
    'You will receive an email once an administrator reviews your registration. This usually takes 1-2 business days.',
  'domainIntelligence.domainMatchAlert.title': 'New signup from your domain',
  'domainIntelligence.domainMatchAlert.body':
    '{{newUserName}} ({{newUserEmail}}) attempted to create a new account. Their email domain matches your company. You can invite them from Settings > Team Members.',
  'domainIntelligence.partnerExistingBusiness.title': '{{invitingCompany}} has partnered with your company',
  'domainIntelligence.partnerExistingBusiness.body':
    '{{inviterName}} from {{invitingCompany}} has established a {{partnerType}} partnership with your company through the platform.',
  'domainIntelligence.businessOwnerInvited.title': 'Your business account is ready',
  'domainIntelligence.businessOwnerInvited.body':
    'An administrator has created a business account for {{businessName}}. Click the link to complete your profile and activate your account.',

  // ============================================================================
  // APPLICATION LIFECYCLE (Issue #809 - Feature 9)
  // ============================================================================

  // Status Tracking
  'application.statusTracking': 'Application Status',
  'application.totalApplications': 'Total Applications',
  'application.resume': 'Resume',
  'application.withdraw': 'Withdraw',
  'application.confirmWithdraw': 'Are you sure you want to withdraw this application? This action cannot be undone.',
  'application.withdrawTitle': 'Withdraw Application',
  'application.withdrawn': 'Application withdrawn successfully',
  'application.withdrawReason': 'Reason for withdrawal (optional)',
  'application.withdrawError': 'Failed to withdraw application. Please try again.',
  'application.startNew': 'Start Application',
  'application.noApplications': 'No applications yet',
  'application.noApplicationsDescription':
    'Start a new application to get started. We will guide you through the process.',
  'application.viewDetails': 'View Details',
  'application.filteredByEntity': 'Showing applications for entity: {{entityName}}',
  'application.clearEntityFilter': 'Show All',

  // Status Labels
  'application.status.draft': 'Draft',
  'application.status.inProgress': 'In Progress',
  'application.status.submitted': 'Submitted',
  'application.status.underReview': 'Under Review',
  'application.status.approved': 'Approved',
  'application.status.rejected': 'Rejected',
  'application.status.closing': 'Closing',
  'application.status.funded': 'Funded',
  'application.status.withdrawn': 'Withdrawn',
  'application.status.closed': 'Closed',
  'application.status.cancelled': 'Cancelled',
  'application.status.archived': 'Archived',

  // Tab Filters
  'application.tab.all': 'All',
  'application.tab.inProgress': 'In Progress',
  'application.tab.submitted': 'Submitted',
  'application.tab.underReview': 'Under Review',
  'application.tab.approved': 'Approved',
  'application.tab.closed': 'Closed',

  // Pipeline Steps
  'application.pipeline.applied': 'Applied',
  'application.pipeline.review': 'Under Review',
  'application.pipeline.approved': 'Approved',
  'application.pipeline.closing': 'Closing',
  'application.pipeline.funded': 'Funded',

  // Detail Sections
  'application.summary': 'Application Summary',
  'application.documents': 'Documents',
  'application.messages': 'Messages',
  'application.activity': 'Activity',
  'application.loanAmount': 'Loan Amount',
  'application.loanType': 'Loan Type',
  'application.property': 'Property',
  'application.submittedDate': 'Submitted Date',
  'application.lastUpdated': 'Last Updated',
  'application.applicationNumber': 'Application #',
  'application.amount': 'Amount',

  // Summary Card Labels
  'application.summary.inProgress': 'In Progress',
  'application.summary.submitted': 'Submitted',
  'application.summary.underReview': 'Under Review',
  'application.summary.approved': 'Approved',

  // Submission Detail View
  'application.submissionDetails': 'Submission Details',
  'application.pendingReview': 'Your application is pending review. You will be notified when it is processed.',
  'application.submissionInfo': 'Submission Information',
  'application.formData': 'Submitted Form Data',
  'application.noFormData': 'No form data available for this submission.',
  'application.rejectionReason': 'Rejection Reason',
  'application.rejectedCanResubmit': 'Your application was rejected. You can edit and resubmit it below.',
  'application.resubmit': 'Resubmit Application',
  'application.resubmitFailed': 'Failed to resubmit application. Please try again.',
  'application.submitted': 'Submitted',
  'application.rate': 'Rate',
  'application.ltv': 'LTV',
  'application.ltc': 'LTC',
  'application.noSearchResults': 'No {{entity}} match your search "{{search}}". Try adjusting your search terms.',
  'application.failedToLoad': 'Failed to load {{entity}}: {{error}}',
  'application.originalSubmission': 'Original Submission',

  // ============================================================================
  // QUOTES (Issue #809 - Quote Lifecycle)
  // ============================================================================
  'quotes.title': 'Quotes',
  'quotes.detail': 'Quote Detail',
  'quotes.accept': 'Accept Quote',
  'quotes.reject': 'Reject Quote',
  'quotes.counter': 'Counter Offer',

  // Stats
  'quotes.totalQuotes': 'Total Quotes',
  'quotes.pendingReview': 'Pending Review',
  'quotes.accepted': 'Accepted',
  'quotes.bestRate': 'Best Rate',
  'quotes.active': 'Active',
  'quotes.totalValue': 'Total Value',
  'quotes.winRate': 'Win Rate',

  // Info banner
  'quotes.infoBanner':
    'Quotes are generated based on your case details. Review and compare to find the best option for your needs.',

  // Table columns
  'quotes.column.case': 'Case',
  'quotes.column.lender': 'Lender',
  'quotes.column.amount': 'Amount',
  'quotes.column.rate': 'Rate',
  'quotes.column.term': 'Term',
  'quotes.column.status': 'Status',
  'quotes.column.received': 'Received',
  'quotes.column.monthlyPayment': 'Monthly Payment',
  'quotes.unknownCustomer': 'Unknown Customer',

  // Detail sections
  'quotes.loanSummary': 'Loan Summary',
  'quotes.feeBreakdown': 'Fee Breakdown',
  'quotes.terms': 'Terms & Conditions',
  'quotes.loanAmount': 'Loan Amount',
  'quotes.interestRate': 'Interest Rate',
  'quotes.term': 'Term',
  'quotes.monthlyPayment': 'Monthly Payment',
  'quotes.originationFee': 'Origination Fee',
  'quotes.appraisalFee': 'Appraisal Fee',
  'quotes.totalClosingCosts': 'Total Closing Costs',
  'quotes.expirationDate': 'Expiration Date',
  'quotes.lockStatus': 'Lock Status',
  'quotes.conditions': 'Conditions',
  'quotes.receivedDate': 'Received Date',
  'quotes.linkedCase': 'Linked Case',
  'quotes.editQuote': 'Edit Quote',
  'quotes.subtotal': 'Subtotal',
  'quotes.tax': 'Tax',
  'quotes.total': 'Total',

  // Accept/Reject
  'quotes.confirmAccept': 'Confirm Accept',
  'quotes.confirmAcceptMessage':
    'Are you sure you want to accept this quote? By accepting, you agree to the terms and conditions outlined. This action cannot be undone.',
  'quotes.rejectReason': 'Reason for Rejection',
  'quotes.rejectReasonPlaceholder': 'Please provide a reason for rejecting this quote...',
  'quotes.confirmReject': 'Confirm Rejection',
  'quotes.confirmRejectMessage':
    'Are you sure you want to reject this quote? You can optionally provide a reason below.',
  'quotes.submitting': 'Submitting...',
  'quotes.responseError': 'Failed to submit response. Please try again.',

  // Statuses
  'quotes.status.draft': 'Draft',
  'quotes.status.active': 'Active',
  'quotes.status.sent': 'Sent',
  'quotes.status.viewed': 'Viewed',
  'quotes.status.pending': 'Pending',
  'quotes.status.locked': 'Locked',
  'quotes.status.accepted': 'Accepted',
  'quotes.status.rejected': 'Rejected',
  'quotes.status.expired': 'Expired',
  'quotes.status.declined': 'Declined',

  // Response statuses
  'quotes.acceptedOn': 'Accepted on {{date}}',
  'quotes.rejectedWith': 'Rejected: {{reason}}',
  'quotes.yourResponse': 'Your Response',
  'quotes.youAccepted': 'You accepted this quote',
  'quotes.youRejected': 'You rejected this quote',

  // Empty state
  'quotes.empty': 'No quotes yet',
  'quotes.emptyDescription': 'Submit a case to receive quotes from lenders.',
  'quotes.noQuotes': 'No quotes match your search.',
  'quotes.submitNewCase': 'Submit New Case',

  // Search/Filter
  'quotes.searchPlaceholder': 'Search quotes...',
  'quotes.filters': 'Filters',
  'quotes.viewCase': 'View Case',
  'quotes.termYears': '{{count}} years',
  'quotes.termMonths': '{{count}} months',
  'quotes.locked': 'Locked',
  'quotes.unlocked': 'Not Locked',
  'quotes.noConditions': 'No additional conditions',

  // Counter-Offer (Issue #809 - Quote Counter-Offer Flow)
  'quotes.counterOffer.title': 'Counter-Offer',
  'quotes.counterOffer.description': 'Propose alternative terms for this quote. At least one field must be changed.',
  'quotes.counterOffer.proposedRate': 'Proposed Rate (%)',
  'quotes.counterOffer.proposedTerm': 'Proposed Term',
  'quotes.counterOffer.proposedAmount': 'Proposed Amount ($)',
  'quotes.counterOffer.reason': 'Reason / Notes',
  'quotes.counterOffer.reasonPlaceholder': 'Explain why you are proposing different terms...',
  'quotes.counterOffer.submit': 'Submit Counter-Offer',
  'quotes.counterOffer.success': 'Counter-offer submitted successfully',
  'quotes.counterOffer.error': 'Failed to submit counter-offer. Please try again.',
  'quotes.counterOffer.atLeastOneField': 'Please propose at least one different term (rate, term, or amount).',
  'quotes.counterOffer.sent': 'Counter-Offer Sent',
  'quotes.status.counter_offer': 'Counter-Offer',

  // Comparison (Issue #809 - Quote Comparison View)
  'quotes.compare.title': 'Compare Quotes',
  'quotes.compare.select': 'Select quotes to compare',
  'quotes.compare.selected': '{{count}} selected',
  'quotes.compare.compare': 'Compare Selected',
  'quotes.compare.exitCompare': 'Exit Compare',
  'quotes.compare.rate': 'Interest Rate',
  'quotes.compare.term': 'Term',
  'quotes.compare.amount': 'Loan Amount',
  'quotes.compare.monthlyPayment': 'Monthly Payment',
  'quotes.compare.fees': 'Total Fees',
  'quotes.compare.totalCost': 'Total Cost',
  'quotes.compare.lender': 'Lender',
  'quotes.compare.expiration': 'Expiration',
  'quotes.compare.status': 'Status',
  'quotes.compare.best': 'Best',
  'quotes.compare.selectTwo': 'Select 2-3 quotes to compare',
  'quotes.compare.noData': 'N/A',

  // ============================================================================
  // CLOSING SCHEDULE (Issue #809 - Closing Tab)
  // ============================================================================
  'closing.schedule': 'Closing Schedule',
  'closing.targetDate': 'Target Closing Date',
  'closing.conditions': 'Closing Conditions',
  'closing.conditionsDescription': 'Conditions that must be met before closing',
  'closing.addCondition': 'Add Condition',
  'closing.noConditions': 'No closing conditions have been added yet.',
  'closing.checklist': 'Closing Checklist',
  'closing.checklistDescription': 'Track required items for closing',
  'closing.wireInstructions': 'Wire Instructions',
  'closing.wireInstructionsDescription': 'Wire transfer details for closing disbursement',
  'closing.addItem': 'Add Item',
  'closing.removeItem': 'Remove Item',
  'closing.markComplete': 'Mark Complete',
  'closing.progress': 'Progress',
  'closing.progressLabel': '{{completed}} of {{total}} items completed',
  'closing.bankName': 'Bank Name',
  'closing.routingNumber': 'Routing Number',
  'closing.accountNumber': 'Account Number',
  'closing.wireReference': 'Wire Reference Number',
  'closing.disbursementDetails': 'Disbursement Details',
  'closing.noItems': 'No checklist items yet.',
  'closing.empty': 'No closing schedule has been set up for this case.',
  'closing.notes': 'Closing Notes',
  'closing.notesPlaceholder': 'Add any notes related to the closing...',
  'closing.saveSchedule': 'Save Schedule',
  'closing.saveWireInstructions': 'Save Wire Instructions',
  'closing.scheduleUpdated': 'Closing schedule updated successfully',
  'closing.wireInstructionsUpdated': 'Wire instructions updated successfully',
  'closing.checklistItemAdded': 'Checklist item added',
  'closing.checklistItemRemoved': 'Checklist item removed',
  'closing.checklistItemUpdated': 'Checklist item updated',
  'closing.itemTitle': 'Item Title',
  'closing.itemDescription': 'Description',
  'closing.itemCategory': 'Category',
  'closing.itemRequired': 'Required',
  'closing.categoryGeneral': 'General',
  'closing.categoryDocuments': 'Documents',
  'closing.categoryFinancial': 'Financial',
  'closing.categoryLegal': 'Legal',
  'closing.categoryInsurance': 'Insurance',
  'closing.categoryTitle': 'Title',
  'closing.conditionText': 'Condition',
  'closing.conditionSatisfied': 'Satisfied',
  'closing.documentRequirements': 'Document Requirements',
  'closing.maskedValue': '****{{lastFour}}',

  // ============================================================================
  // CALCULATORS (Issue #603 — Standalone Lending Calculators)
  // ============================================================================
  'pageHeader.calculators.title': 'Lending Calculators',
  'pageHeader.calculators.description': 'Financial tools for loan analysis and qualification',
  'pageHeader.monthlyPayment.title': 'Monthly Payment Calculator',
  'pageHeader.monthlyPayment.description': 'Calculate monthly loan payments with amortization schedules',
  'pageHeader.dscrCalculator.title': 'DSCR Calculator',
  'pageHeader.dscrCalculator.description': 'Calculate Debt Service Coverage Ratio for rental properties',
  'pageHeader.cashToClose.title': 'Cash to Close Calculator',
  'pageHeader.cashToClose.description': 'Estimate total cash needed at closing',
  'pageHeader.loanSizer.title': 'Loan Sizer',
  'pageHeader.loanSizer.description': 'Deterministic loan sizing for Fix & Flip, Ground-Up, and Bridge products.',

  // Loan Sizer form (portal)
  'loanSizer.dealDetails': 'Loan Details',
  'loanSizer.borrowerDetails': 'Borrower Details',
  'loanSizer.results': 'Sizing Result',
  // "Size loan" (not "Calculate") matches the redesigned officer-facing
  // sizer button — the page is no longer a generic calculator.
  'loanSizer.calculate': 'Size loan',
  // "Save" (not "Save sizing") — the redesigned form button row reads
  // "Size loan / Save" so the second button keeps its label tight.
  'loanSizer.saveSizing': 'Save',
  'loanSizer.loanType': 'Loan type',
  'loanSizer.loanType.fixFlip': 'Fix & Flip',
  'loanSizer.loanType.groundUp': 'Ground-Up',
  'loanSizer.loanType.bridge': 'Bridge',
  'loanSizer.purchasePrice': 'Purchase price',
  'loanSizer.constructionBudget': 'Construction / rehab budget',
  'loanSizer.arv': 'After-repair value (ARV)',
  'loanSizer.costSpentToDate': 'Cost spent to date',
  'loanSizer.remainingCost': 'Remaining cost to complete',
  'loanSizer.loanPayoff': 'Existing loan payoff',
  'loanSizer.otherClosingCosts': 'Other closing costs',
  'loanSizer.requestedLoan': 'Requested loan amount',
  'loanSizer.fico': 'FICO',
  'loanSizer.foreignNational': 'Foreign-national borrower',
  'loanSizer.rate': 'Interest rate',
  // See duplicate at line ~9743 — kept a single 'loanSizer.points': 'Points'
  // (the % suffix in the form is rendered via InputAdornment, not the label)
  'loanSizer.termMonths': 'Term (months)',
  'loanSizer.termMonthsUnit': 'mo',
  'loanSizer.targetMaxLoan': 'Target max loan',
  'loanSizer.bindingConstraint': 'Binding constraint',
  'loanSizer.variance': 'Variance vs. requested',
  'loanSizer.disclaimer': 'Preliminary internal sizing only — not final credit approval.',
  'loanSizer.flags': 'Flags',
  'loanSizer.upsizePath': 'Upsize path',
  'loanSizer.leverageMetrics': 'Leverage metrics',
  'loanSizer.cashMechanics': 'Cash mechanics',
  'loanSizer.constructionHoldback': 'Construction holdback',
  'loanSizer.proceedsAtClosing': 'Proceeds at closing',
  'loanSizer.cashToClose': 'Cash to close (borrower)',
  'loanSizer.interestReserve': 'Interest reserve',
  'loanSizer.closingProceeds': 'Closing proceeds (Bridge)',
  'loanSizer.remainingBasis': 'Remaining basis',
  // status/loanType keys use the proto enum values (WITHIN_TARGET etc.) so
  // callers can pass the raw enum — see line ~9753 below for the canonical set.
  'loanSizer.openQuestions': 'Open questions',
  'loanSizer.emptyState': 'Enter the loan details and run Calculate to get a target sizing.',
  'loanSizer.noSizings': 'No sizings saved for this case yet.',
  'loanSizer.newSizing': 'New sizing',
  'loanSizer.savedAt': 'Saved {time} by {actor}',
  'loanSizer.sizingsTabTitle': 'Sizing',
  'loanSizer.loadingSizings': 'Loading sizings…',
  'loanSizer.backToSizings': '← Back to sizings',
  'loanSizer.view': 'View',
  'loanSizer.applyToCase': 'Apply to case',
  'loanSizer.applyToCase.success': 'Applied ✓',
  'loanSizer.points': 'Points',
  'loanSizer.metricResult.pass': 'Pass',
  'loanSizer.metricResult.warn': 'Warn',
  'loanSizer.metricResult.fail': 'Fail',
  'loanSizer.metricResult.skipped': 'Skipped',
  'loanSizer.metricName.initial_ltc': 'Initial LTC',
  'loanSizer.metricName.ltc': 'LTC',
  'loanSizer.metricName.arltv': 'ARLTV',
  'loanSizer.metricName.ltv': 'LTV',
  'loanSizer.status.WITHIN_TARGET': 'Within target',
  'loanSizer.status.EXCEPTION': 'Exception / desk review',
  'loanSizer.status.OUTSIDE': 'Outside target',
  'loanSizer.loanType.FIX_FLIP': 'Fix & Flip',
  'loanSizer.loanType.GROUND_UP': 'Ground-Up',
  'loanSizer.loanType.BRIDGE': 'Bridge',

  // Loan Sizer redesign — Phase 5/6 (officer-facing input + results)
  'loanSizer.tab.fixFlip': 'Fix & Flip',
  'loanSizer.tab.groundUp': 'Ground Up',
  'loanSizer.tab.bridge': 'Bridge',
  'loanSizer.address': 'Property address',
  'loanSizer.addressPlaceholder': '123 Main St, San Diego, CA 92101',
  'loanSizer.borrowerTier': 'Borrower tier',
  'loanSizer.holdbackHint':
    '100% of rehab/construction budget is always held back. Remaining proceeds go to purchase funding or cash out at closing.',
  'loanSizer.preliminaryTarget': 'Preliminary target',
  'loanSizer.initialLtc': 'Initial LTC',
  'loanSizer.totalLtc': 'Total LTC',
  'loanSizer.arltv.label': 'ARLTV',
  'loanSizer.proceedsAtClosing.label': 'Proceeds at closing',
  'loanSizer.holdbackShareOfLoan': 'Holdback share of loan',
  'loanSizer.borrowerCashToClose': 'Borrower cash to close before fees',
  'loanSizer.target': 'Target {value}',
  'loanSizer.subjectRedfin': 'Subject Redfin property',
  'loanSizer.areaSearch': 'Area sold search',
  'loanSizer.sizingPosture': 'Sizing posture',
  'loanSizer.flagsTitle': 'Flags',
  'loanSizer.noFlags': 'No guideline flags based on entered data.',
  'loanSizer.approvalPath': 'Approval path',
  'loanSizer.sizeLoan': 'Size loan',
  'loanSizer.save': 'Save',
  // Friendly binding-constraint labels — interpolate {pct} from the
  // active tier profile (formatted "12.3%"). The raw enum values come
  // straight from the engine via `formatBindingConstraint(...)`.
  'loanSizer.bindingConstraint.INITIAL_LTC_PLUS_HOLDBACK': 'Initial LTC cap {pct} plus holdback',
  'loanSizer.bindingConstraint.TOTAL_LTC': 'Total LTC cap {pct}',
  'loanSizer.bindingConstraint.ARLTV': 'ARLTV cap {pct}',
  'loanSizer.bindingConstraint.LTV': 'LTV cap {pct}',
  'loanSizer.bindingConstraint.TIER_MAX_LOAN': 'Hard max loan cap',
  'loanSizer.bindingConstraint.MAX_LOAN': 'Hard max loan cap',
  'loanSizer.bindingConstraint.NONE': 'No caps could be computed',
  // Tab strip is the primary control; the legacy product picker becomes
  // an opt-in "advanced" toggle.
  'loanSizer.advanced': 'Advanced',

  // AI insights card (Phase 8 — bonus). Concise advisory bullets generated
  // by Gemini from the sizer inputs + result. NOT financial advice; rendered
  // with a caveat caption so the officer treats them as observations only.
  'loanSizer.aiInsights.title': 'AI insights',
  'loanSizer.aiInsights.loading': 'Generating insights…',
  'loanSizer.aiInsights.empty': 'No additional insights for this scenario.',
  'loanSizer.aiInsights.refresh': 'Refresh',
  'loanSizer.aiInsights.disclaimer': 'AI-generated suggestions, not financial advice.',

  // Case-detail tab label
  'tabs.sizing': 'Sizing',

  // Calculator index page
  'calculators.title': 'Lending Calculators',
  'calculators.subtitle': 'Financial tools to help you analyze loans, qualify properties, and estimate closing costs.',
  'calculators.monthlyPayment.title': 'Monthly Payment Calculator',
  'calculators.monthlyPayment.description':
    'Calculate monthly loan payments for interest-only or fully amortized loans, with full amortization schedule.',
  'calculators.monthlyPayment.cta': 'Calculate Payment',
  'calculators.dscr.title': 'DSCR Calculator',
  'calculators.dscr.description':
    'Determine if rental income covers debt obligations. Essential for DSCR loan qualification.',
  'calculators.dscr.cta': 'Calculate DSCR',
  'calculators.cashToClose.title': 'Cash to Close Calculator',
  'calculators.cashToClose.description':
    'Estimate total cash needed at closing including down payment, lender fees, and third-party costs.',
  'calculators.cashToClose.cta': 'Calculate Cash to Close',
  'calculators.termSheet.title': 'Term Sheet Generator',
  'calculators.termSheet.description': 'Generate professional loan term sheets with customizable parameters.',
  'calculators.termSheet.cta': 'Generate Term Sheet',

  // Monthly Payment Calculator
  'calculators.paymentType': 'Payment Type',
  'calculators.interestOnly': 'Interest Only',
  'calculators.amortized': 'Amortized',
  'calculators.loanAmount': 'Loan Amount',
  'calculators.interestRate': 'Interest Rate',
  'calculators.loanTerm': 'Loan Term (Months)',
  'calculators.amortizationPeriod': 'Amortization Period (Months)',
  'calculators.monthlyPayment': 'Monthly Payment',
  'calculators.totalInterest': 'Total Interest',
  'calculators.totalCost': 'Total Cost',
  'calculators.showSchedule': 'Show Amortization Schedule',
  'calculators.hideSchedule': 'Hide Amortization Schedule',
  'calculators.period': 'Period',
  'calculators.payment': 'Payment',
  'calculators.principal': 'Principal',
  'calculators.interest': 'Interest',
  'calculators.balance': 'Balance',
  'calculators.loanDetails': 'Loan Details',
  'calculators.results': 'Results',
  'calculators.clear': 'Clear',

  // DSCR Calculator
  'calculators.propertyIncome': 'Property Income',
  'calculators.monthlyRentalIncome': 'Monthly Rental Income',
  'calculators.annualTaxes': 'Annual Property Taxes',
  'calculators.annualInsurance': 'Annual Insurance',
  'calculators.monthlyHOA': 'Monthly HOA',
  'calculators.monthlyOtherExpenses': 'Monthly Other Expenses',
  'calculators.loanTerms': 'Loan Terms',
  'calculators.amortizationType': 'Amortization Type',
  'calculators.amortizationTerm': 'Amortization Term (Months)',
  'calculators.dscrRatio': 'DSCR Ratio',
  'calculators.dscrStrong': 'Strong — Income well exceeds debt',
  'calculators.dscrAdequate': 'Adequate — Income covers debt',
  'calculators.dscrBorderline': 'Borderline — Tight coverage',
  'calculators.dscrWeak': 'Weak — Income may not cover debt',
  'calculators.noiBreakdown': 'NOI Breakdown',
  'calculators.annualGrossIncome': 'Annual Gross Income',
  'calculators.annualExpenses': 'Annual Operating Expenses',
  'calculators.netOperatingIncome': 'Net Operating Income',
  'calculators.debtService': 'Annual Debt Service',
  'calculators.monthlyCashFlow': 'Monthly Cash Flow',

  // Cash to Close Calculator
  'calculators.purchasePrice': 'Purchase Price',
  'calculators.constructionHoldback': 'Construction Holdback',
  'calculators.interestReserve': 'Interest Reserve',
  'calculators.lenderFees': 'Lender Fees',
  'calculators.originationPoints': 'Origination Points (%)',
  'calculators.originationFee': 'Origination Fee',
  'calculators.processingFee': 'Processing Fee',
  'calculators.underwritingFee': 'Underwriting Fee',
  'calculators.docPrepFee': 'Doc Prep Fee',
  'calculators.miscLenderFees': 'Misc. Lender Fees',
  'calculators.thirdPartyFees': 'Third-Party Fees',
  'calculators.titleInsurance': 'Title Insurance',
  'calculators.appraisal': 'Appraisal',
  'calculators.brokerFee': 'Broker Fee',
  'calculators.taxes': 'Taxes & Recording',
  'calculators.survey': 'Survey',
  'calculators.miscThirdPartyFees': 'Misc. Third-Party Fees',
  'calculators.totalCashToClose': 'Total Cash to Close',
  'calculators.breakdown': 'Breakdown',
  'calculators.downPayment': 'Down Payment',
  'calculators.totalLenderFees': 'Total Lender Fees',
  'calculators.totalThirdPartyFees': 'Total Third-Party Fees',
  'calculators.deductions': 'Deductions',
  'calculators.balloonPayment': 'Balloon Payment',
  'calculators.numYears': '{{count}} years',
  'calculators.enterDetailsToSeeResults': 'Enter loan details to see results',

  // ============================================================================
  // WEBSITE SECTION EDITOR (CMS Page Builder)
  // ============================================================================

  // Section type categories
  'website.sectionEditor.category.content': 'Content',
  'website.sectionEditor.category.programs': 'Programs & Showcases',
  'website.sectionEditor.category.calculators': 'Calculators',
  'website.sectionEditor.category.forms': 'Forms & Intake',
  'website.sectionEditor.category.partners': 'Partners & Investors',

  // Section type labels
  'website.sectionEditor.type.hero': 'Hero',
  'website.sectionEditor.type.ctaBanner': 'CTA Banner',
  'website.sectionEditor.type.statsCounter': 'Stats Counter',
  'website.sectionEditor.type.faq': 'FAQ',
  'website.sectionEditor.type.contact': 'Contact',
  'website.sectionEditor.type.teamGrid': 'Team Grid',
  'website.sectionEditor.type.processTimeline': 'Process Timeline',
  'website.sectionEditor.type.glossary': 'Glossary',
  'website.sectionEditor.type.loanPrograms': 'Loan Programs',
  'website.sectionEditor.type.fundedDeals': 'Funded Deals',
  'website.sectionEditor.type.testimonials': 'Testimonials',
  'website.sectionEditor.type.blogList': 'Blog List',
  'website.sectionEditor.type.calculator': 'Calculator',
  'website.sectionEditor.type.fixFlipCalculator': 'Fix & Flip Calculator',
  'website.sectionEditor.type.dscrCalculator': 'DSCR Calculator',
  'website.sectionEditor.type.leadCapture': 'Lead Capture',
  'website.sectionEditor.type.preQualWizard': 'Pre-Qualification Wizard',
  'website.sectionEditor.type.partner': 'Partners',
  'website.sectionEditor.type.investor': 'Investors',
  'website.sectionEditor.type.partnerApplication': 'Partner Application',

  // Hero section editor
  'website.sectionEditor.hero.variant': 'Layout Variant',
  'website.sectionEditor.hero.fullWidth': 'Full Width',
  'website.sectionEditor.hero.splitScreen': 'Split Screen',
  'website.sectionEditor.hero.videoBackground': 'Video Background',
  'website.sectionEditor.hero.minimalText': 'Minimal Text',
  'website.sectionEditor.hero.animatedGradient': 'Animated Gradient',
  'website.sectionEditor.hero.headline': 'Headline',
  'website.sectionEditor.hero.subheadline': 'Subheadline',
  'website.sectionEditor.hero.backgroundImage': 'Background Image URL',
  'website.sectionEditor.hero.backgroundVideo': 'Background Video URL',
  'website.sectionEditor.hero.overlayOpacity': 'Overlay Opacity',
  'website.sectionEditor.hero.primaryCta': 'Primary CTA',
  'website.sectionEditor.hero.secondaryCta': 'Secondary CTA',

  // Calculator section editor
  'website.sectionEditor.calculator.rateSource': 'Rate Source',
  'website.sectionEditor.calculator.rateSourceManual': 'Manual (use defaults below)',
  'website.sectionEditor.calculator.rateSourcePublished': 'Published Rates (from Pricing Engine)',
  'website.sectionEditor.calculator.rateSourceManualHelp': 'Calculator will use the default values configured below.',
  'website.sectionEditor.calculator.rateSourcePublishedHelp':
    'Calculator will fetch the latest published rates from your Pricing Engine. Manual rate defaults are ignored.',
  'website.sectionEditor.calculator.defaultValues': 'Default Values',
  'website.sectionEditor.calculator.purchasePrice': 'Purchase Price',
  'website.sectionEditor.calculator.arv': 'After Repair Value (ARV)',
  'website.sectionEditor.calculator.rehabBudget': 'Rehab Budget',
  'website.sectionEditor.calculator.interestRate': 'Interest Rate (%)',
  'website.sectionEditor.calculator.originationPoints': 'Origination Points',
  'website.sectionEditor.calculator.propertyValue': 'Property Value',
  'website.sectionEditor.calculator.monthlyRent': 'Monthly Rent',
  'website.sectionEditor.calculator.ltvPercent': 'LTV (%)',
  'website.sectionEditor.calculator.calculatorType': 'Calculator Type',
  'website.sectionEditor.calculator.typePayment': 'Payment',
  'website.sectionEditor.calculator.typeAffordability': 'Affordability',
  'website.sectionEditor.calculator.typeRefinance': 'Refinance',
  'website.sectionEditor.calculator.typeRoi': 'ROI',
  'website.sectionEditor.calculator.defaultLoanAmount': 'Default Loan Amount',
  'website.sectionEditor.calculator.minLoanAmount': 'Minimum Loan Amount',
  'website.sectionEditor.calculator.maxLoanAmount': 'Maximum Loan Amount',
  'website.sectionEditor.calculator.ctaSection': 'Call to Action',

  // Intake form section editor
  'website.sectionEditor.intake.templateSelection': 'Intake Template',
  'website.sectionEditor.intake.selectTemplate': 'Select Template',
  'website.sectionEditor.intake.noTemplate': 'None (use default fields)',
  'website.sectionEditor.intake.templatesUnavailable':
    'Intake template picker is not yet wired up — available after the CMS → intake integration lands.',
  'website.sectionEditor.intake.templateHelp':
    'Select an intake template to power this form. The template defines the form fields and wizard steps.',
  'website.sectionEditor.intake.stepCount': '{{count}} steps',
  'website.sectionEditor.intake.layout': 'Layout',
  'website.sectionEditor.intake.layoutInline': 'Inline',
  'website.sectionEditor.intake.layoutModal': 'Modal',
  'website.sectionEditor.intake.layoutSidebar': 'Sidebar',
  'website.sectionEditor.intake.layoutFullPage': 'Full Page',
  'website.sectionEditor.intake.successMessage': 'Success Message',
  'website.sectionEditor.intake.leadSource': 'Lead Source Tag',
  'website.sectionEditor.intake.leadSourcePlaceholder': 'e.g. website-contact-form',
  'website.sectionEditor.intake.incentiveText': 'Incentive Text',
  'website.sectionEditor.intake.incentiveTextPlaceholder': 'e.g. Get your free rate quote',

  // Published rates
  'pricing.publishedRates.title': 'Published Rates',
  'pricing.publishedRates.description': 'Manage rates published to your website calculators',
  'pricing.publishedRates.currentRates': 'Current Published Rates',
  'pricing.publishedRates.noRatesPublished':
    'No rates published yet. Configure and publish rates to have them appear on your website calculators.',
  'pricing.publishedRates.publishToWebsite': 'Publish to Website',
  'pricing.publishedRates.publishing': 'Publishing...',
  'pricing.publishedRates.publishSuccess': 'Rates published successfully',
  'pricing.publishedRates.version': 'Version {{version}}',
  'pricing.publishedRates.publishedAt': 'Published {{date}}',
  'pricing.publishedRates.versionHistory': 'Version History',
  'pricing.publishedRates.loanType': 'Loan Type',
  'pricing.publishedRates.baseRate': 'Base Rate (%)',
  'pricing.publishedRates.points': 'Points',
  'pricing.publishedRates.maxLtv': 'Max LTV (%)',
  'pricing.publishedRates.minLoan': 'Min Loan',
  'pricing.publishedRates.maxLoan': 'Max Loan',
  'pricing.publishedRates.addLoanType': 'Add Loan Type',
  'pricing.publishedRates.removeLoanType': 'Remove',
  'pricing.publishedRates.fixFlip': 'Fix & Flip',
  'pricing.publishedRates.dscr': 'DSCR',
  'pricing.publishedRates.bridge': 'Bridge',
  'pricing.publishedRates.commercial': 'Commercial',
  'pricing.publishedRates.construction': 'Construction',
  'pricing.publishedRates.versionColumn': 'Version',
  'pricing.publishedRates.publishedColumn': 'Published',
  'pricing.publishedRates.statusColumn': 'Status',
  'pricing.publishedRates.active': 'Active',
  'pricing.publishedRates.archived': 'Archived',

  // ============================================================================
  // PRICING — RATE MATRICES (multi-dimensional pricing matrix editor)
  // ============================================================================
  'pricing.matrices.editTitle': 'Edit Rate Matrix',
  'pricing.matrices.dimensions': 'Dimensions',
  'pricing.matrices.dimensionType.discrete': 'Discrete',
  'pricing.matrices.dimensionType.range': 'Range',
  'pricing.matrices.title': 'Rate Matrices',
  'pricing.matrices.subtitle': 'Create and manage multi-dimensional pricing matrices',
  'pricing.matrices.totalMatrices': 'Total Matrices',
  'pricing.matrices.active': 'Active',
  'pricing.matrices.totalRates': 'Total Rates',
  'pricing.matrices.dimensionsCount': 'Dimensions',
  'pricing.rules.detail.ruleType': 'Rule Type',
  'pricing.rules.detail.timesUsed': 'Times Used',
  'pricing.rules.detail.totalCalculated': 'Total Calculated',
  'pricing.rules.detail.version': 'Version',

  // ============================================================================
  // ADMIN — LOAN SIZER RULES (Issue #1345 — Phase 4 of Loan Sizer epic)
  // Admin matrix editor for SizerProfile + SizerAdjustment management.
  // CASL-gated by Permission.loanSizer.manageRules.
  // ============================================================================
  'pageHeader.admin.sizerRules.title': 'Loan Sizer Rules',
  'pageHeader.admin.sizerRules.description':
    'Configure tier matrices and post-rule adjustments used by the loan sizer.',
  'navigation.sizerRules': 'Sizer Rules',

  'admin.sizerRules.pageTitle': 'Loan Sizer Rules',
  'admin.sizerRules.pageDescription':
    'Configure leverage caps, FICO floors, and adjustment overlays for Fix & Flip, Ground-Up, and Bridge products.',
  'admin.sizerRules.forbiddenTitle': 'Access denied',
  'admin.sizerRules.forbiddenMessage':
    'You do not have permission to manage sizer rules. Contact a platform administrator if you need access.',
  'admin.sizerRules.loading': 'Loading sizer rules…',

  // Sections
  'admin.sizerRules.profilesSection.title': 'Tier Profiles',
  'admin.sizerRules.profilesSection.description':
    'Each loan product has tiered leverage caps. Higher tiers typically offer more leverage at higher FICO floors.',
  'admin.sizerRules.adjustmentsSection.title': 'Adjustments & Overlays',
  'admin.sizerRules.adjustmentsSection.description':
    'Conditional rules that haircut leverage or raise flags when borrower / property attributes match.',

  // Loan-type group headings (re-uses existing loanSizer.loanType.* labels for the names)
  'admin.sizerRules.loanType.FIX_FLIP': 'Fix & Flip',
  'admin.sizerRules.loanType.GROUND_UP': 'Ground-Up',
  'admin.sizerRules.loanType.BRIDGE': 'Bridge',
  'admin.sizerRules.loanType.ALL': 'All loan types',

  // Profile table columns
  'admin.sizerRules.column.tier': 'Tier',
  'admin.sizerRules.column.label': 'Label',
  'admin.sizerRules.column.initialLtc': 'Initial LTC',
  'admin.sizerRules.column.totalLtc': 'Total LTC',
  'admin.sizerRules.column.arltv': 'ARLTV',
  'admin.sizerRules.column.maxLoan': 'Max Loan',
  'admin.sizerRules.column.maxTerm': 'Max Term',
  'admin.sizerRules.column.ficoFloor': 'FICO Floor',
  'admin.sizerRules.column.effective': 'Effective',
  'admin.sizerRules.column.active': 'Active',
  'admin.sizerRules.column.actions': 'Actions',

  // Adjustment table columns
  'admin.sizerRules.adjustment.column.key': 'Key',
  'admin.sizerRules.adjustment.column.loanType': 'Loan Type',
  'admin.sizerRules.adjustment.column.description': 'Description',
  'admin.sizerRules.adjustment.column.haircut': 'Haircut',
  'admin.sizerRules.adjustment.column.flagCode': 'Flag Code',
  'admin.sizerRules.adjustment.column.flagSeverity': 'Severity',

  // Profile editor form labels
  'admin.sizerRules.form.profile.title.create': 'New Tier Profile',
  'admin.sizerRules.form.profile.title.edit': 'Edit Tier Profile',
  'admin.sizerRules.form.loanType': 'Loan Type',
  'admin.sizerRules.form.tier': 'Tier',
  'admin.sizerRules.form.tierHelp': 'Positive integer. Higher tiers typically offer more leverage.',
  'admin.sizerRules.form.initialLtcCap': 'Initial LTC Cap (%)',
  'admin.sizerRules.form.totalLtcCap': 'Total LTC Cap (%)',
  'admin.sizerRules.form.arltvCap': 'ARLTV Cap (%)',
  'admin.sizerRules.form.maxLoan': 'Max Loan',
  'admin.sizerRules.form.maxLoanHelp': 'Optional — leave blank for no tier max.',
  'admin.sizerRules.form.maxLoanEmpty': 'No tier max',
  'admin.sizerRules.form.maxTermMonths': 'Max Term (months)',
  'admin.sizerRules.form.ficoFloor': 'FICO Floor',
  'admin.sizerRules.form.ficoFloorHelp': 'Minimum borrower FICO required for this tier (300–850).',
  'admin.sizerRules.form.label': 'Label',
  'admin.sizerRules.form.labelPlaceholder': 'e.g., Premier — 700+ FICO',
  'admin.sizerRules.form.effectiveFrom': 'Effective From',
  'admin.sizerRules.form.effectiveTo': 'Effective To',
  'admin.sizerRules.form.effectiveToHelp': 'Optional — leave blank for open-ended.',
  'admin.sizerRules.form.isActive': 'Active',

  // Adjustment editor form labels
  'admin.sizerRules.form.adjustment.title.create': 'New Adjustment',
  'admin.sizerRules.form.adjustment.title.edit': 'Edit Adjustment',
  'admin.sizerRules.form.key': 'Key',
  'admin.sizerRules.form.keyPlaceholder': 'e.g., FOREIGN_NATIONAL_HAIRCUT',
  'admin.sizerRules.form.keyHelp': 'Unique identifier for this rule (max 64 chars).',
  'admin.sizerRules.form.description': 'Description',
  'admin.sizerRules.form.condition': 'Condition (JSON)',
  'admin.sizerRules.form.conditionHelp':
    'JSON object expressing the condition. Example: {"foreignNational": true} or {"fico": {"lt": 680}}.',
  'admin.sizerRules.form.conditionInvalid': 'Invalid JSON',
  'admin.sizerRules.form.leverageHaircut': 'Leverage Haircut (%)',
  'admin.sizerRules.form.leverageHaircutHelp':
    'Percentage to subtract from leverage caps when condition matches. Optional.',
  'admin.sizerRules.form.flagCode': 'Flag Code',
  'admin.sizerRules.form.flagSeverity': 'Flag Severity',
  'admin.sizerRules.form.flagSeverity.INFO': 'Info',
  'admin.sizerRules.form.flagSeverity.WARN': 'Warn',
  'admin.sizerRules.form.flagSeverity.EXCEPTION': 'Exception',
  'admin.sizerRules.form.flagMessage': 'Flag Message',

  // Validation messages
  'admin.sizerRules.validation.tierRequired': 'Tier is required',
  'admin.sizerRules.validation.tierPositive': 'Tier must be a positive integer',
  'admin.sizerRules.validation.percentRange': 'Must be between 0 and 100',
  'admin.sizerRules.validation.ficoRange': 'FICO must be between 300 and 850',
  'admin.sizerRules.validation.maxTermPositive': 'Max term must be a positive integer',
  'admin.sizerRules.validation.maxLoanInvalid': 'Max loan must be a non-negative number',
  'admin.sizerRules.validation.effectiveFromRequired': 'Effective From is required',
  'admin.sizerRules.validation.keyRequired': 'Key is required',
  'admin.sizerRules.validation.conditionRequired': 'Condition (JSON object) is required',

  // Empty states
  'admin.sizerRules.empty.profiles': 'No tier profiles defined for this loan type.',
  'admin.sizerRules.empty.adjustments': 'No adjustments configured.',
  'admin.sizerRules.empty.profilesAll': 'No tier profiles defined yet. Add one to get started.',

  // Actions
  'admin.sizerRules.action.addProfile': 'Add Profile',
  'admin.sizerRules.action.addAdjustment': 'Add Adjustment',
  'admin.sizerRules.action.edit': 'Edit',
  'admin.sizerRules.action.delete': 'Delete',
  'admin.sizerRules.action.save': 'Save',
  'admin.sizerRules.action.cancel': 'Cancel',
  'admin.sizerRules.action.refresh': 'Refresh',

  // Confirmation + toasts
  'admin.sizerRules.confirm.deleteProfile': 'Delete tier {{tier}} for {{loanType}}? This cannot be undone.',
  'admin.sizerRules.confirm.deleteAdjustment': 'Delete adjustment "{{key}}"? This cannot be undone.',
  'admin.sizerRules.toast.profileSaved': 'Tier profile saved',
  'admin.sizerRules.toast.profileDeleted': 'Tier profile deleted',
  'admin.sizerRules.toast.adjustmentSaved': 'Adjustment saved',
  'admin.sizerRules.toast.adjustmentDeleted': 'Adjustment deleted',
  'admin.sizerRules.toast.saveFailed': 'Save failed: {{message}}',
  'admin.sizerRules.toast.deleteFailed': 'Delete failed: {{message}}',

  // Status / chips
  'admin.sizerRules.status.active': 'Active',
  'admin.sizerRules.status.inactive': 'Inactive',
  'admin.sizerRules.effective.unbounded': 'No end date',
  'admin.sizerRules.value.notSet': '—',
  'admin.sizerRules.unit.months': '{{count}} mo',

  // ============================================================================
  // ADMIN — SIZER POLICY (Issue #1438)
  // Three editor cards on /pricing/sizer-rules: Platform Thresholds,
  // Rate Tiers, Points Tiers. CASL-gated by Permission.loanSizer.managePolicy
  // (thresholds) and Permission.loanSizer.manageRules (rate/points tiers).
  // ============================================================================

  // Platform Thresholds
  'admin.sizerPolicy.platformThresholds.title': 'Platform Thresholds',
  'admin.sizerPolicy.platformThresholds.description':
    'Lifted constants used by the sizer engine — minimum loan size, holdback warning threshold, and Ground-Up feasibility floors.',
  'admin.sizerPolicy.platformThresholds.empty':
    'No active platform threshold record yet. Create one to start enforcing sizing limits.',
  'admin.sizerPolicy.platformThresholds.minLoan': 'Minimum loan ($)',
  'admin.sizerPolicy.platformThresholds.minLoanHelp': 'Smallest loan amount that can be sized.',
  'admin.sizerPolicy.platformThresholds.holdbackShareWarn': 'Holdback share warning (%)',
  'admin.sizerPolicy.platformThresholds.holdbackShareWarnHelp':
    'Warn when construction holdback exceeds this share of the loan.',
  'admin.sizerPolicy.platformThresholds.guFeasibilityTier1': 'GU feasibility — Tier 1 ($/sf)',
  'admin.sizerPolicy.platformThresholds.guFeasibilityTier1Help':
    'Minimum ARV per square foot for Ground-Up Tier 1 deals.',
  'admin.sizerPolicy.platformThresholds.guFeasibilityOther': 'GU feasibility — other tiers ($/sf)',
  'admin.sizerPolicy.platformThresholds.guFeasibilityOtherHelp':
    'Minimum ARV per square foot for Ground-Up Tier 2+ deals.',
  'admin.sizerPolicy.platformThresholds.notes': 'Internal notes',
  'admin.sizerPolicy.platformThresholds.notesPlaceholder': 'Optional change rationale or links to context.',
  'admin.sizerPolicy.platformThresholds.effectiveFrom': 'Effective from',
  'admin.sizerPolicy.platformThresholds.effectiveTo': 'Effective to',
  'admin.sizerPolicy.platformThresholds.effectiveToHelp': 'Leave blank for open-ended.',
  'admin.sizerPolicy.platformThresholds.isActive': 'Active',
  'admin.sizerPolicy.platformThresholds.editTitle': 'Edit Platform Thresholds',
  'admin.sizerPolicy.platformThresholds.createTitle': 'Create Platform Thresholds',
  'admin.sizerPolicy.platformThresholds.editAction': 'Edit thresholds',
  'admin.sizerPolicy.platformThresholds.createAction': 'Create thresholds',
  'admin.sizerPolicy.platformThresholds.scope.platform': 'Platform default',
  'admin.sizerPolicy.platformThresholds.scope.tenant': 'Tenant override',

  // Rate Tiers
  'admin.sizerPolicy.rateTiers.title': 'Rate Tiers',
  'admin.sizerPolicy.rateTiers.description':
    'Pricing bands for interest rates per loan type and tier. The base rate is the published rate; min/max define the negotiation band.',
  'admin.sizerPolicy.rateTiers.empty': 'No rate tiers defined for this loan type.',
  'admin.sizerPolicy.rateTiers.addTier': 'Add rate tier',
  'admin.sizerPolicy.rateTiers.editTitle': 'Edit Rate Tier',
  'admin.sizerPolicy.rateTiers.createTitle': 'New Rate Tier',
  'admin.sizerPolicy.rateTiers.column.tier': 'Tier',
  'admin.sizerPolicy.rateTiers.column.label': 'Label',
  'admin.sizerPolicy.rateTiers.column.baseRate': 'Base Rate',
  'admin.sizerPolicy.rateTiers.column.minRate': 'Min Rate',
  'admin.sizerPolicy.rateTiers.column.maxRate': 'Max Rate',
  'admin.sizerPolicy.rateTiers.form.baseRate': 'Base rate (%)',
  'admin.sizerPolicy.rateTiers.form.minRate': 'Min rate (%)',
  'admin.sizerPolicy.rateTiers.form.maxRate': 'Max rate (%)',
  'admin.sizerPolicy.rateTiers.form.label': 'Label',
  'admin.sizerPolicy.rateTiers.form.labelPlaceholder': 'e.g., Premier — 700+ FICO',
  'admin.sizerPolicy.rateTiers.confirmDelete': 'Delete rate tier #{{tier}} for {{loanType}}? This cannot be undone.',
  'admin.sizerPolicy.rateTiers.toast.saved': 'Rate tier saved',
  'admin.sizerPolicy.rateTiers.toast.deleted': 'Rate tier deleted',
  'admin.sizerPolicy.rateTiers.validation.bandOrder': 'Min ≤ base ≤ max must hold',

  // Points Tiers
  'admin.sizerPolicy.pointsTiers.title': 'Points Tiers',
  'admin.sizerPolicy.pointsTiers.description':
    'Pricing bands for origination points per loan type and tier. Base points are the published value; min/max define the band.',
  'admin.sizerPolicy.pointsTiers.empty': 'No points tiers defined for this loan type.',
  'admin.sizerPolicy.pointsTiers.addTier': 'Add points tier',
  'admin.sizerPolicy.pointsTiers.editTitle': 'Edit Points Tier',
  'admin.sizerPolicy.pointsTiers.createTitle': 'New Points Tier',
  'admin.sizerPolicy.pointsTiers.column.tier': 'Tier',
  'admin.sizerPolicy.pointsTiers.column.label': 'Label',
  'admin.sizerPolicy.pointsTiers.column.basePoints': 'Base Points',
  'admin.sizerPolicy.pointsTiers.column.minPoints': 'Min Points',
  'admin.sizerPolicy.pointsTiers.column.maxPoints': 'Max Points',
  'admin.sizerPolicy.pointsTiers.form.basePoints': 'Base points (%)',
  'admin.sizerPolicy.pointsTiers.form.minPoints': 'Min points (%)',
  'admin.sizerPolicy.pointsTiers.form.maxPoints': 'Max points (%)',
  'admin.sizerPolicy.pointsTiers.form.label': 'Label',
  'admin.sizerPolicy.pointsTiers.form.labelPlaceholder': 'e.g., Premier — 700+ FICO',
  'admin.sizerPolicy.pointsTiers.confirmDelete':
    'Delete points tier #{{tier}} for {{loanType}}? This cannot be undone.',
  'admin.sizerPolicy.pointsTiers.toast.saved': 'Points tier saved',
  'admin.sizerPolicy.pointsTiers.toast.deleted': 'Points tier deleted',
  'admin.sizerPolicy.pointsTiers.validation.bandOrder': 'Min ≤ base ≤ max must hold',

  // Shared toast / validation
  'admin.sizerPolicy.toast.thresholdsSaved': 'Platform thresholds saved',
  'admin.sizerPolicy.toast.saveFailed': 'Save failed: {{message}}',
  'admin.sizerPolicy.toast.deleteFailed': 'Delete failed: {{message}}',
  'admin.sizerPolicy.validation.percentRange': 'Must be between 0 and 100',
  'admin.sizerPolicy.validation.nonNegative': 'Must be a non-negative number',
  'admin.sizerPolicy.validation.tierRequired': 'Tier is required',
  'admin.sizerPolicy.validation.effectiveFromRequired': 'Effective from is required',
  'admin.sizerPolicy.forbiddenTitle': 'Access denied',
  'admin.sizerPolicy.forbiddenMessage':
    'You do not have permission to manage platform sizing thresholds. Contact a platform admin to request access.',

  // ============================================================================
  // ADMIN — SIZING POLICY (Read-only formula viewer, /pricing/sizing-policy)
  // CASL-gated by Permission.loanSizer.viewRules. Issue #1438.
  // ============================================================================
  'pageHeader.admin.sizingPolicy.title': 'Sizing Policy & Formulas',
  'pageHeader.admin.sizingPolicy.description':
    'Read-only view of the active platform sizing policy, the formulas the engine uses, and a worked example with current inputs.',
  'admin.sizingPolicy.title': 'Sizing Policy & Formulas',
  'admin.sizingPolicy.description':
    'A transparent view of how loan sizing is computed for this tenant — active thresholds, rate/points tiers, formulas, and a worked example.',
  'admin.sizingPolicy.loading': 'Loading sizing policy…',
  'admin.sizingPolicy.forbiddenTitle': 'Access denied',
  'admin.sizingPolicy.forbiddenMessage':
    'You do not have permission to view sizing rules. Contact your administrator to request access.',
  'admin.sizingPolicy.source.tenant': 'Tenant override',
  'admin.sizingPolicy.source.platform': 'Platform default',
  'admin.sizingPolicy.platformPolicy.title': 'Active Platform Policy',
  'admin.sizingPolicy.platformPolicy.minLoan': 'Minimum loan',
  'admin.sizingPolicy.platformPolicy.holdbackShareWarn': 'Holdback share warn',
  'admin.sizingPolicy.platformPolicy.guFeasibilityTier1': 'GU feasibility — Tier 1',
  'admin.sizingPolicy.platformPolicy.guFeasibilityOther': 'GU feasibility — other tiers',
  'admin.sizingPolicy.platformPolicy.effectiveFrom': 'Effective from',
  'admin.sizingPolicy.platformPolicy.effectiveTo': 'Effective to',
  'admin.sizingPolicy.platformPolicy.unbounded': 'Open-ended',
  'admin.sizingPolicy.formulas.title': 'Formulas',
  'admin.sizingPolicy.formulas.empty': 'No formulas published.',
  'admin.sizingPolicy.workedExample.title': 'Worked Example',
  'admin.sizingPolicy.workedExample.empty': 'Worked example unavailable for the current tenant.',
  'admin.sizingPolicy.workedExample.column.label': 'Step',
  'admin.sizingPolicy.workedExample.column.formula': 'Formula',
  'admin.sizingPolicy.workedExample.column.inputs': 'Inputs',
  'admin.sizingPolicy.workedExample.column.output': 'Output',
  'admin.sizingPolicy.missingTier.title': 'Missing tier configuration',
  'admin.sizingPolicy.missingTier.rate':
    'No active rate tiers — the sizer cannot compute interest-rate bands until a platform admin adds one.',
  'admin.sizingPolicy.missingTier.points':
    'No active points tiers — the sizer cannot compute points bands until a platform admin adds one.',
  'admin.sizingPolicy.tiersSummary.rateCount': '{{count}} rate tier(s) active',
  'admin.sizingPolicy.tiersSummary.pointsCount': '{{count}} points tier(s) active',

  // Pricing index quick-action card
  'pricing.sizingPolicy.title': 'Sizing Policy & Formulas',
  'pricing.sizingPolicy.description':
    'Read-only view of the active sizing policy, formulas, and a worked example for transparency.',

  // Website calculator published rate badge
  'website.calculator.publishedRateBadge': 'Published rate as of {{date}}',

  // ============================================================================
  // TERMSHEETS
  // ============================================================================
  'termsheets.personalMessage': 'Personal Message (Optional)',
  'termsheets.personalMessagePlaceholder': 'Add a personal note to include in the email...',
  'termsheets.sendButton': 'Send Term Sheet',
  'termsheets.sendFailed': 'Failed to send term sheet. Please try again.',
  'termsheets.sendingTo': 'Sending term sheet to:',
  'termsheets.sendTitle': 'Send Term Sheet',
  'termsheets.subject': 'Subject (Optional)',
  'termsheets.subjectPlaceholder': 'Your Term Sheet from [Company Name]',
  // Issue #1083: Deal linkage & consumer visibility
  'termsheets.linkedDeal': 'Linked Deal',
  'termsheets.createFromDeal': 'Create Term Sheet',
  'termsheets.consumerViewTitle': 'Term Sheets',
  'termsheets.type': 'Term Sheet',
  'termsheets.acceptSuccess': 'Term sheet accepted',
  'termsheets.rejectSuccess': 'Term sheet rejected',
  'termsheets.loadFailed': 'Some term sheets could not be loaded.',
  'termsheets.months': 'months',
  // Issue #1540 — unified-templates picker on the Send Term Sheet panel.
  // Surfaces both PDF and STRUCTURED templates (kind=TERMSHEET, status=ACTIVE)
  // and lets the user pick a non-latest version inline.
  'termsheets.send.success': 'Term sheet sent successfully',
  'termsheets.send.esignSuccess': 'Term sheet sent for e-signature',
  'termsheets.send.error': 'Failed to send term sheet',
  'termsheets.send.picker.rowAriaLabel': '{{name}} — {{kind}}',
  'termsheets.send.picker.title': 'Choose a template',
  'termsheets.send.picker.subtitle':
    'Pick which termsheet template (and version) to use for this send. Defaults to the most recently updated active template.',
  'termsheets.send.picker.contentTypeLabel': 'Format',
  'termsheets.send.picker.contentTypeAny': 'PDF or structured',
  'termsheets.send.picker.versionsButton': 'Versions',
  'termsheets.send.picker.versionCurrent': 'Using v{{version}}',
  'termsheets.send.picker.pickVersion': 'Switch version',
  'termsheets.send.picker.versionOption': 'v{{version}} — {{status}}',
  'termsheets.send.picker.selectedBadge': 'Selected',
  'termsheets.send.picker.empty': 'No active termsheet templates yet. Create one from Document Templates first.',
  'termsheets.send.picker.loadFailed': "Couldn't load templates. Please try again.",
  // Issue #1559 — ApprovalGatedButton CTA labels for the Send Term Sheet flow.
  'termsheets.requestApproval': 'Request Approval to Send Term Sheet',

  // ============================================================================
  // APPROVAL WORKFLOWS (Issue #1559 — BIC Stream D / Phase 2)
  // Strings for <ApprovalGatedButton> + <ApprovalRequestPanel>. The same
  // namespace covers every BIC-gated trigger action — the term-sheet send is
  // the canonical first wiring.
  // ============================================================================
  'approval.notPermitted': "Your role isn't permitted to take this action",
  'approval.tooltipRequiresApproval': 'Requires approval from {{roles}} (SLA {{slaHours}}h)',
  'approval.requestPanel.title': 'Request Approval',
  'approval.requestPanel.summary': 'Routes to {{roles}}, decision SLA {{slaHours}}h.',
  'approval.requestPanel.payloadHeader': 'Action details',
  'approval.requestPanel.commentLabel': 'Optional message to approver',
  'approval.requestPanel.submit': 'Request Approval',
  'approval.requestSubmitted': 'Approval request submitted ({{id}})',
  'approval.requestFailed': 'Failed to submit approval request. Please try again.',

  // ---------------------------------------------------------------------------
  // Approver dashboard at /approvals (Issue #1559 — Stream E / Phase 4)
  // Page that shows the caller their approval inbox AND their own pending
  // requests. All copy here is portal-wide (visible to any authed user).
  // ---------------------------------------------------------------------------
  // Page chrome
  'approval.dashboard.title': 'Approvals',
  'approval.dashboard.description': 'Review approval requests waiting for you and track requests you’ve sent.',
  // Section headers (the two queues on the page)
  'approval.dashboard.waitingForMe': 'Waiting for me',
  'approval.dashboard.waitingForMeDescription': 'Approval requests that need your decision.',
  'approval.dashboard.myRequests': 'My requests',
  'approval.dashboard.myRequestsDescription': 'Approval requests you submitted.',
  // Table column headers
  'approval.dashboard.column.priority': 'Priority',
  'approval.dashboard.column.requestor': 'Requestor',
  'approval.dashboard.column.action': 'Action',
  'approval.dashboard.column.sla': 'SLA',
  'approval.dashboard.column.status': 'Status',
  'approval.dashboard.column.requested': 'Requested',
  'approval.dashboard.column.workflow': 'Workflow',
  // Empty states (one per scope) — direct, not chirpy.
  'approval.dashboard.emptyWaiting': 'No approvals are waiting for you right now.',
  'approval.dashboard.emptyMyRequests': 'You haven’t submitted any approval requests.',
  // Generic load-failure inline alert
  'approval.dashboard.loadFailed': 'Failed to load approval requests.',
  // Refresh / retry CTA on the inline error alert
  'approval.dashboard.retry': 'Retry',
  // Priority chip labels (derived from SLA on the client)
  'approval.priority.overdue': 'Overdue',
  'approval.priority.urgent': 'Urgent',
  'approval.priority.high': 'High',
  'approval.priority.normal': 'Normal',
  // Status chip labels — keyed by ApprovalDecisionStatus values.
  'approval.status.pending': 'Pending',
  'approval.status.approved': 'Approved',
  'approval.status.denied': 'Denied',
  'approval.status.escalated': 'Escalated',
  'approval.status.expired': 'Expired',
  // Fallback rendered when the BE hasn’t resolved the requestor display name yet.
  'approval.dashboard.unknownRequestor': 'Unknown requestor',
  // SLA-cell formatting (relative time vs dueAt). The countdown uses date-fns
  // formatDistance(); these labels wrap the result.
  'approval.dashboard.sla.dueIn': 'Due {{relative}}',
  'approval.dashboard.sla.overdueBy': 'Overdue {{relative}}',
  'approval.dashboard.sla.noDeadline': 'No deadline',
  // ---------------------------------------------------------------------------
  // Approver review panel (SplitPanel content shown when an approver clicks a
  // row in the "Waiting for me" queue).
  // ---------------------------------------------------------------------------
  'approval.approverPanel.title': 'Review approval request',
  'approval.approverPanel.requestor': 'Requestor',
  'approval.approverPanel.requestedAt': 'Requested',
  'approval.approverPanel.workflowCode': 'Workflow',
  'approval.approverPanel.dueAt': 'Decision due',
  'approval.approverPanel.actionDetails': 'Action details',
  'approval.approverPanel.payloadEmpty': 'No payload supplied with this request.',
  // Optional requestor note — only rendered when present.
  'approval.approverPanel.requestorComment': 'Requestor note',
  // Decision-reason controls.
  'approval.approverPanel.reasonLabelOptional': 'Reason for approval (optional)',
  'approval.approverPanel.reasonLabelRequired': 'Reason for denial',
  'approval.approverPanel.reasonHelper': 'Visible to the requestor and recorded in the audit log.',
  'approval.approverPanel.reasonRequired': 'A reason is required when denying a request.',
  // Buttons.
  'approval.approverPanel.approve': 'Approve',
  'approval.approverPanel.deny': 'Deny',
  // Toasts after a decision.
  'approval.approverPanel.approveSuccess': 'Approval granted',
  'approval.approverPanel.approveFailed': 'Failed to approve request. Please try again.',
  'approval.approverPanel.denySuccess': 'Approval denied',
  'approval.approverPanel.denyFailed': 'Failed to deny request. Please try again.',
  // Payload-diff section is intentionally deferred — surface the TODO marker
  // in copy too so a future Stream E2 fill-in is obvious.
  'approval.approverPanel.payloadDiffPending':
    'Comparison with previous request version will appear here once available.',

  // Issue #1559 Phase 3 — ApprovalGatedButton CTA labels for additional
  // BIC-gated actions wired across 10-business-portal. Each pair is
  // (originalLabel, approvalLabel) for the same surface.
  // Final approval (loan detail header Approve button → loan:approval:final:issue)
  'cases.requestApprovalFinal': 'Request Approval to Issue Final Approval',
  // Funds disbursement (funds page wire-out → loan:fund:wire)
  'funds.requestApprovalDisburse': 'Request Approval to Fire Wire',
  // Loan modification submission (loan detail modifications tab → loan:modification:initiate)
  'modifications.actions.requestApproval': 'Request Approval to Submit Modification',
  // Sizer "apply to case" (case sizing tab saved-record detail → loan:sizer:override)
  'loanSizer.requestApprovalApplyToCase': 'Request Approval to Apply Sizing',

  // ============================================================================
  // BUSINESS PORTAL — LOAN DETAIL TERM SHEETS TAB (Issue #1309)
  // Multi-signer e-signature flow on the loan detail page. Copy is kept
  // white-label safe — lender brand name is NEVER hardcoded here.
  // ============================================================================
  'business.loans.termsheets.header': 'Term Sheets for {{loanTitle}}',
  'business.loans.termsheets.emptyTitle': 'No term sheets yet',
  'business.loans.termsheets.emptyDescription':
    'Generate a term sheet from a template, merge in loan attributes, and send for e-signature.',
  'business.loans.termsheets.newButton': 'New term sheet',
  'business.loans.termsheets.disabledTooltip':
    'Term Sheet e-signature is not enabled for this business. Contact your admin.',
  'business.loans.termsheets.newDisabledTooltip':
    'Term sheets can be created in the Term Sheet stage or any stage with term-sheet authority. Move the loan to Term Sheet via "Change stage" first.',
  'business.loans.termsheets.tabHidden': 'You do not have permission to view term sheets for this loan.',
  'business.loans.termsheets.loadFailed': 'Failed to load term sheets.',
  'business.loans.termsheets.retry': 'Retry',

  // Column/Status labels
  'business.loans.termsheets.column.number': 'Number',
  'business.loans.termsheets.column.status': 'Status',
  'business.loans.termsheets.column.amount': 'Amount',
  'business.loans.termsheets.column.rate': 'Rate',
  'business.loans.termsheets.column.term': 'Term',
  'business.loans.termsheets.column.expires': 'Expires',
  'business.loans.termsheets.column.actions': 'Actions',

  // Create flow — template picker (Step 1)
  'business.loans.termsheets.create.title': 'New Term Sheet',
  'business.loans.termsheets.create.pickTemplate': 'Pick a template',
  'business.loans.termsheets.create.pickTemplateHelp': 'Templates matching this loan’s category appear first.',
  'business.loans.termsheets.create.filteredByCategory': 'Filtered by loan category: {{category}}',
  'business.loans.termsheets.create.allCategories': 'All categories',
  'business.loans.termsheets.create.noTemplates':
    'No term sheet templates available. Your admin can add custom templates in the control center.',
  'business.loans.termsheets.create.signerCount': '{{count}} signers',
  'business.loans.termsheets.create.usageCount': 'Used {{count}} times',
  'business.loans.termsheets.create.preview': 'Preview',
  'business.loans.termsheets.create.selectTemplate': 'Select',
  'business.loans.termsheets.create.step1Title': 'Template',
  'business.loans.termsheets.create.step2Title': 'Preview',
  'business.loans.termsheets.create.step3Title': 'Send',

  // Adhoc (upload-your-own-PDF) flow — Issue #1393
  'business.loans.termsheets.adhoc.uploadOwnPdf': 'Upload your own PDF',
  'business.loans.termsheets.adhoc.comingSoon':
    'Uploading a custom PDF and designing fields in DocuSeal is tracked in issue #1393 and will ship in an upcoming release.',
  'business.loans.termsheets.adhoc.step1Title': 'Upload & design',
  'business.loans.termsheets.adhoc.step2Title': 'Configure',
  'business.loans.termsheets.adhoc.step3Title': 'Send',
  'business.loans.termsheets.adhoc.dropPdfPrompt': 'Drop a PDF here, or click to browse',
  'business.loans.termsheets.adhoc.pdfRequirements': 'PDF up to 10 MB',
  'business.loans.termsheets.adhoc.pdfTooLarge': 'PDF must be 10 MB or smaller.',
  'business.loans.termsheets.adhoc.pdfWrongType': 'Only PDF files are accepted.',
  'business.loans.termsheets.adhoc.uploading': 'Uploading PDF…',
  'business.loans.termsheets.adhoc.builderLoading': 'Loading DocuSeal builder…',
  'business.loans.termsheets.adhoc.builderError': 'The DocuSeal builder ran into an error. Please retry.',
  // R2-6 / R2-7 / R2-8 (Issue #1430) — keys read by AdhocUploadAndDesign /
  // AdhocConfigure with English fallbacks. Registering them here so the
  // translated value renders instead of the raw fallback (and so
  // `{{fields}}` / `{{submitters}}` actually interpolate).
  'business.loans.termsheets.adhoc.designSessionStarting': 'Preparing the secure design session…',
  'business.loans.termsheets.adhoc.designIframeTitle': 'Adhoc term sheet template editor',
  'business.loans.termsheets.adhoc.designReady':
    'Template ready: at least one signature field is placed and assigned. Click Continue.',
  'business.loans.termsheets.adhoc.designInstructions':
    'Place at least one signature field on the document and assign it to a signer (Borrower / Lender / etc) to continue.',
  'business.loans.termsheets.adhoc.designStatus': '{{fields}} field(s) placed across {{submitters}} signer slot(s)',
  'business.loans.termsheets.adhoc.designPollError':
    'Unable to reach the design server — retrying. If this persists, refresh the page.',
  'business.loans.termsheets.adhoc.designSummary': 'Designed template summary',
  'business.loans.termsheets.adhoc.designSummaryHelp':
    '{{fields}} field(s) placed across {{submitters}} signer slot(s).',
  'business.loans.termsheets.adhoc.uploadError':
    'We couldn’t upload your PDF. Please try again. If the problem persists, share this reference with support: {{traceId}}',
  'business.loans.termsheets.adhoc.continue': 'Continue',
  'business.loans.termsheets.adhoc.back': 'Back',
  'business.loans.termsheets.adhoc.saveAndContinue': 'Save & continue',
  'business.loans.termsheets.adhoc.fieldName': 'Name',
  'business.loans.termsheets.adhoc.fieldNameHelp': 'Shown to signers and in your term sheet list',
  'business.loans.termsheets.adhoc.fieldValidityDays': 'Valid for (days)',
  'business.loans.termsheets.adhoc.fieldInternalNote': 'Internal note (optional)',
  'business.loans.termsheets.adhoc.fieldInternalNoteHelp': 'Visible only to your team',
  'business.loans.termsheets.adhoc.prefillTitle': 'Pre-fill values',
  'business.loans.termsheets.adhoc.prefillHelp':
    'Map loan values from this case to the field names you created in the DocuSeal builder.',
  'business.loans.termsheets.adhoc.prefillNoFields':
    'No fields were placed in the builder. Signers will still be prompted to sign.',
  'business.loans.termsheets.adhoc.customPdfChip': 'Custom PDF',
  'business.loans.termsheets.adhoc.abandonConfirmTitle': 'Discard this upload?',
  'business.loans.termsheets.adhoc.abandonConfirmBody':
    'The PDF and any fields you placed in the builder will be lost. The envelope has not been sent.',
  'business.loans.termsheets.adhoc.abandonConfirm': 'Discard',
  'business.loans.termsheets.adhoc.abandonCancel': 'Keep editing',
  // Dev-mock stub (D15) — only rendered in non-production when DocuSeal credentials are absent
  'business.loans.termsheets.adhoc.devMock.banner':
    'Dev mock — DocuSeal credentials are not configured. This stub saves a template with no fields so the flow can be tested end-to-end.',
  'business.loans.termsheets.adhoc.devMock.saveButton': 'Save template (dev mock)',

  // Create flow — preview (Step 2)
  'business.loans.termsheets.preview.title': 'Preview with merged loan data',
  'business.loans.termsheets.preview.borrowerSection': 'Borrower',
  'business.loans.termsheets.preview.propertySection': 'Property',
  'business.loans.termsheets.preview.loanTermsSection': 'Loan Terms',
  'business.loans.termsheets.preview.calculatedSection': 'Calculated Values',
  'business.loans.termsheets.preview.fieldOverride': 'Override',
  'business.loans.termsheets.preview.fieldOverrideHelp':
    'Override a value before sending. The template will still reference the original loan attribute.',
  'business.loans.termsheets.preview.missingData':
    'Some fields are blank because the loan record is missing data. Fill them in on the loan overview tab or override them here.',
  'business.loans.termsheets.preview.cannotGenerate':
    'Cannot generate — missing required data: {fields}. Provide the values above or edit the loan to continue.',
  'business.loans.termsheets.preview.generate': 'Generate term sheet',
  'business.loans.termsheets.preview.generating': 'Generating…',
  'business.loans.termsheets.preview.generated': 'Term sheet created. Choose signers on the next step.',
  'business.loans.termsheets.preview.generateFailed': 'Failed to generate term sheet. Please try again.',
  'business.loans.termsheets.preview.back': 'Back',

  // Send dialog — multi-signer (Step 3)
  'business.loans.termsheets.send.title': 'Send for e-signature',
  'business.loans.termsheets.send.previewTitle': 'Preview',
  'business.loans.termsheets.send.previewFailed':
    'Could not load the rendered PDF for in-line preview. The document is still available in the envelope progress view after sending.',
  'business.loans.termsheets.send.previewParityNote':
    'Preview shows the rendered termsheet content. The signed copy will use the lender-authored master template with these values pre-filled — layout may differ.',
  'business.loans.termsheets.send.resolving': 'Resolving signers…',
  'business.loans.termsheets.send.resolveFailed': 'Could not resolve signers from loan data.',
  'business.loans.termsheets.send.autoResolved': 'Auto-resolved from loan',
  'business.loans.termsheets.send.manualSource': 'Manual — please fill',
  'business.loans.termsheets.send.sourceLabel': 'Source',
  'business.loans.termsheets.send.signingOrder': 'Order',
  'business.loans.termsheets.send.counterSign': 'Counter-signs after borrower',
  'business.loans.termsheets.send.addSigner': 'Add signer',
  'business.loans.termsheets.send.removeSigner': 'Remove',
  'business.loans.termsheets.send.editSigner': 'Edit',
  'business.loans.termsheets.send.role': 'Role',
  'business.loans.termsheets.send.email': 'Email',
  'business.loans.termsheets.send.name': 'Name',
  'business.loans.termsheets.send.phone': 'Phone',
  'business.loans.termsheets.send.expiresInDays': 'Expires in (days)',
  'business.loans.termsheets.send.expiresInDaysHelp': 'Envelopes expire after this many days. Default 14, maximum 90.',
  'business.loans.termsheets.send.sendButton': 'Send for signature',
  'business.loans.termsheets.send.sending': 'Sending…',
  'business.loans.termsheets.send.sendFailed': 'Failed to send. Please try again.',
  'business.loans.termsheets.send.sent': 'Envelope sent. Tracking signers below.',
  'business.loans.termsheets.send.policyValid': 'Signer policy satisfied',
  'business.loans.termsheets.send.policyViolationTitle': 'Signer policy not yet satisfied',
  'business.loans.termsheets.send.policyRequireRole': 'Policy requires at least one signer with role “{{role}}”.',
  'business.loans.termsheets.send.policyRequireCounterSign':
    'Policy requires at least one counter-signer with role “{{role}}”.',
  'business.loans.termsheets.send.policyMaxRole':
    'Policy allows up to {{max}} signer(s) with role “{{role}}” (currently {{count}}).',
  'business.loans.termsheets.send.policyDuplicateOrder':
    'Two signers share signing order {{order}}. Each order must be unique.',
  'business.loans.termsheets.send.policyInvalidEmail': 'Signer {{index}} must have a valid email address.',
  'business.loans.termsheets.send.policyMissingName': 'Signer {{index}} must have a name.',
  'business.loans.termsheets.send.policyDuplicateEmail':
    'Two signers share the email address "{{email}}" — each recipient must be unique.',
  'business.loans.termsheets.send.policyManualRow':
    'Signer for role “{{role}}” could not be found in this loan — please fill in manually.',
  // Template-driven policy validation messages (#1309)
  'business.loans.termsheets.send.policyRequireExactlyOne':
    'Policy requires exactly one signer with role “{{role}}” (currently {{count}}).',
  'business.loans.termsheets.send.policyRequireAtLeastOne': 'Policy requires at least one signer with role “{{role}}”.',
  'business.loans.termsheets.send.policyTooManyForOne':
    'Policy allows only one signer with role “{{role}}”, but {{count}} are listed.',
  'business.loans.termsheets.send.policyMissingCounterSign':
    'Policy requires a counter-signer for role “{{role}}”; none is listed.',
  'business.loans.termsheets.send.policyExtraRole':
    'Role “{{role}}” is not declared by the template policy — it will still be sent but may fall outside the signed policy.',
  'business.loans.termsheets.send.policyInfoTitle': 'Additional notes',
  'business.loans.termsheets.send.cancel': 'Cancel',
  // Role-derived source labels surfaced on each signer row (#1309)
  'business.loans.termsheets.send.sourceManual': 'Manual entry',
  'business.loans.termsheets.send.sourceFromBorrowers': 'From case borrowers',
  'business.loans.termsheets.send.sourceFromGuarantors': 'From case guarantors',
  'business.loans.termsheets.send.sourceFromAssignee': 'From case assignee',
  'business.loans.termsheets.send.sourceFromLoan': 'From loan data',
  'business.loans.termsheets.send.moveUp': 'Move up',
  'business.loans.termsheets.send.moveDown': 'Move down',

  // Envelope progress view
  'business.loans.termsheets.progress.title': 'Envelope Progress',
  'business.loans.termsheets.progress.envelopeStatus': 'Envelope status',
  'business.loans.termsheets.progress.createdBy': 'Created by',
  'business.loans.termsheets.progress.createdAt': 'Created',
  'business.loans.termsheets.progress.sentAt': 'Sent',
  'business.loans.termsheets.progress.expiresAt': 'Expires',
  'business.loans.termsheets.progress.download': 'Download signed PDF',
  'business.loans.termsheets.progress.downloadPending': 'Available once all signers complete',
  'business.loans.termsheets.progress.voidEnvelope': 'Void envelope',
  'business.loans.termsheets.progress.voidConfirmTitle': 'Void this envelope?',
  'business.loans.termsheets.progress.voidConfirmBody':
    'Voiding cancels the DocuSeal submission and notifies all signers. This cannot be undone.',
  'business.loans.termsheets.progress.voidReason': 'Reason (optional)',
  'business.loans.termsheets.progress.voidSuccess': 'Envelope voided',
  'business.loans.termsheets.progress.voidFailed': 'Failed to void envelope',
  'business.loans.termsheets.progress.viewAuditTrail': 'View audit trail',
  'business.loans.termsheets.progress.notSentYet':
    'This term sheet has not been sent for e-signature yet. Send it to start tracking signer progress.',

  // Per-signer row
  'business.loans.termsheets.progress.signerName': 'Name',
  'business.loans.termsheets.progress.signerEmail': 'Email',
  'business.loans.termsheets.progress.signerRole': 'Role',
  'business.loans.termsheets.progress.signerOrder': 'Order',
  'business.loans.termsheets.progress.signerStatus': 'Status',
  'business.loans.termsheets.progress.signerResend': 'Resend',
  'business.loans.termsheets.progress.signerSignNow': 'Sign now',
  'business.loans.termsheets.progress.signerResendSuccess': 'Reminder sent',
  'business.loans.termsheets.progress.signerResendFailed': 'Failed to send reminder',
  'business.loans.termsheets.progress.signerResendRateLimited':
    'Please wait 1 hour between reminders for the same signer.',
  'business.loans.termsheets.progress.signerResendDisabled':
    'Reminder was sent recently — try again in {{minutes}} minutes.',

  // Per-transition timeline under each signer row (Issue #1309 Q5)
  'business.loans.termsheets.progress.timeline.sent': 'Sent {{time}}',
  'business.loans.termsheets.progress.timeline.viewed': 'Viewed {{time}}',
  'business.loans.termsheets.progress.timeline.signed': 'Signed {{time}}',
  'business.loans.termsheets.progress.timeline.declined': 'Declined {{time}}',

  // Signer statuses (maps DocuSeal states to human strings)
  'business.loans.termsheets.signerStatus.pending': 'Pending',
  'business.loans.termsheets.signerStatus.sent': 'Sent',
  'business.loans.termsheets.signerStatus.delivered': 'Delivered',
  'business.loans.termsheets.signerStatus.viewed': 'Viewed',
  'business.loans.termsheets.signerStatus.signed': 'Signed',
  'business.loans.termsheets.signerStatus.completed': 'Completed',
  'business.loans.termsheets.signerStatus.declined': 'Declined',
  'business.loans.termsheets.signerStatus.voided': 'Voided',
  'business.loans.termsheets.signerStatus.expired': 'Expired',

  // Term sheet statuses (aggregate record state — distinct from per-signer status)
  'business.loans.termsheets.status.draft': 'Draft',
  'business.loans.termsheets.status.generating': 'Generating',
  'business.loans.termsheets.status.generated': 'Generated',
  'business.loans.termsheets.status.sent': 'Sent',
  'business.loans.termsheets.status.viewed': 'Viewed',
  'business.loans.termsheets.status.accepted': 'Accepted',
  'business.loans.termsheets.status.rejected': 'Rejected',
  'business.loans.termsheets.status.signing_requested': 'Signing Requested',
  'business.loans.termsheets.status.signed': 'Signed',
  'business.loans.termsheets.status.expired': 'Expired',
  'business.loans.termsheets.status.voided': 'Voided',
  'business.loans.termsheets.status.failed': 'Failed',
  'business.loans.termsheets.status.completed': 'Completed',

  // Signer role labels (UI-only; canonical names live in the SignerRole enum)
  'business.loans.termsheets.role.borrower': 'Borrower',
  'business.loans.termsheets.role.co_borrower': 'Co-Borrower',
  'business.loans.termsheets.role.guarantor': 'Guarantor',
  'business.loans.termsheets.role.authorized_officer': 'Authorized Officer',
  'business.loans.termsheets.role.lender_signer': 'Lender Signer',
  'business.loans.termsheets.role.credit_manager': 'Credit Manager',
  'business.loans.termsheets.role.witness': 'Witness',
  'business.loans.termsheets.role.notary': 'Notary',
  'business.loans.termsheets.role.cc': 'Carbon Copy',

  // ============================================================================
  // ASTOR — AI Assistant Onboarding
  // ============================================================================

  // Widget
  'astor.widget.greeting': "Hi {{name}}, I'm Astor. How can I help you today?",
  'astor.widget.placeholder': 'Ask Astor anything...',
  'astor.widget.suggestedActions': 'Suggested Actions',
  'astor.widget.poweredBy': 'Powered by Astor AI',
  'astor.widget.newConversation': 'New Conversation',

  // Onboarding
  'astor.onboarding.title': 'Your Onboarding Plan',
  'astor.onboarding.completionLabel': '{{completed}} of {{total}} complete',
  'astor.onboarding.letAstorHelp': 'Let Astor help you with this',
  'astor.onboarding.skipForNow': 'Skip for now',
  'astor.onboarding.markComplete': 'Mark as Complete',
  'astor.onboarding.congratulations': "Congratulations! You've completed your onboarding.",
  'astor.onboarding.dismissed': 'Onboarding dismissed. You can resume anytime from settings.',
  'astor.onboarding.estimatedTime': '~{{minutes}} min',
  'astor.onboarding.generating': 'Generating your personalized onboarding plan...',
  'astor.onboarding.sectionLocked': 'Complete previous section to unlock',
  'astor.onboarding.resumeOnboarding': 'Resume Onboarding',

  // Nudges
  'astor.nudge.emptyCases.title': 'No loans yet',
  'astor.nudge.emptyCases.message': 'Let me help you create your first loan to get started.',
  'astor.nudge.emptyTeam.title': 'Build your team',
  'astor.nudge.emptyTeam.message': 'Invite team members to collaborate on your workspace.',
  'astor.nudge.emptyDocs.title': 'No documents uploaded',
  'astor.nudge.emptyDocs.message': 'Upload your first document or let me guide you through it.',
  'astor.nudge.firstVisit.title': 'Welcome to {{pageName}}',
  'astor.nudge.firstVisit.message': 'This is your first time here. Want a quick tour?',
  'astor.nudge.idle.title': 'Need help?',
  'astor.nudge.idle.message': 'You seem to be stuck. Can I help you with anything?',
  'astor.nudge.errorRecovery.title': 'Something went wrong',
  'astor.nudge.errorRecovery.message': 'I noticed an error. Let me help you recover.',
  'astor.nudge.milestone.title': 'Milestone completed!',
  'astor.nudge.milestone.message': 'Great job completing "{{milestoneName}}". Ready for the next step?',
  'astor.nudge.returnVisit.title': 'Welcome back!',
  'astor.nudge.returnVisit.message': 'You left off at {{lastPage}}. Want to continue where you were?',
  'astor.nudge.dontShowAgain': "Don't show this again",

  // Approval
  'astor.approval.title': 'Action Approval Required',
  'astor.approval.approve': 'Approve',
  'astor.approval.edit': 'Edit',
  'astor.approval.cancel': 'Cancel',
  'astor.approval.expiresIn': 'Expires in {{time}}',
  'astor.approval.approved': 'Action approved',
  'astor.approval.rejected': 'Action rejected',

  // Empty states
  'astor.empty.startWithAstor': 'Start with Astor',
  'astor.empty.createManually': 'Create Manually',
  'astor.empty.importFromFile': 'Import from File',

  // Astor empty-state starter prompts (full-page /astor empty state — UX v3
  // "What do you want to do?" surface from docs/ux-v3-1749/pages/home-astor.jsx).
  // Clicking a prompt routes the user into the canonical business assistant
  // and forwards the prompt as the first message via the ?q=&from=home
  // handoff already wired in /astor/chat/[agentId]/page.tsx.
  'astor.empty.starterPromptsHeader': 'Or pick a starting point',
  'astor.empty.starterPrompts.summarizeFunded': "Summarize last week's funded loans",
  'astor.empty.starterPrompts.atRiskSLAs': 'Show me at-risk SLAs',
  'astor.empty.starterPrompts.recentChanges': 'What changed in my pipeline since yesterday?',
  'astor.empty.starterPrompts.findMatching': 'Find loans matching credit memo template X',

  // v3 Astor welcome surface (Issue #1769 wave 3). Renders the mint-glow
  // hero tile + 4-up starter prompt grid for both /astor (no agent picked)
  // and /astor/chat/[agentId] (agent picked, no thread yet). Mirrors the
  // AstorEmpty artboard in docs/ux-v3-1769/project/pages/home-astor.jsx.
  'astor.welcome.heading': 'What do you want to do?',
  'astor.welcome.subtitle': 'Ask in plain English, or pick a starting point.',
  'astor.welcome.fallbackTitle': 'Welcome to Astor',
  'astor.welcome.fallbackBody':
    'Select an agent from the sidebar to start chatting, or create a new agent in settings.',
  'astor.welcome.createFirstAgent': 'Create Your First Agent',
  'astor.welcome.starterPrompts.summarizeFunded.sub': 'Volume + originator breakdown',
  'astor.welcome.starterPrompts.atRiskSLAs.sub': 'Ranked by hours-to-breach',
  'astor.welcome.starterPrompts.recentChanges.sub': 'Loans, partners, applications',
  'astor.welcome.starterPrompts.findMatching.sub': 'Pick a template to start from',

  // v3 Astor active-thread header (Issue #1769 wave 3). Strip above the
  // AstorThread that surfaces tool count, citation count, and agent
  // metadata — mirrors AstorActive artboard. Counts use `{{count}}` and
  // singular `_one` sibling keys so the call site picks between
  // singular/plural at render time (the existing useI18n loader doesn't
  // auto-pluralize — matches the pattern used by
  // `applicationDetail.attachments.heading_*`).
  'astor.chat.header.assistantBadge': '{{name}} · v4.2',
  'astor.chat.header.toolsUsedCount_one': '{{count}} tool',
  'astor.chat.header.toolsUsedCount': '{{count}} tools',
  'astor.chat.header.citationsCount_one': '{{count}} citation',
  'astor.chat.header.citationsCount': '{{count}} citations',
  'astor.chat.header.clearContext': 'Clear context',
  'astor.chat.header.exportThread': 'Export',

  // Analytics
  'astor.analytics.title': 'Astor Analytics',
  'astor.analytics.subtitle':
    "See how Astor — your in-app AI assistant — is helping your team get work done. Track who's interacting with it, which features it surfaces, and where it could help more.",
  'astor.analytics.emptyState.title': 'No Astor activity yet',
  'astor.analytics.emptyState.description':
    'Analytics appear here once your team starts using Astor. Give it a few days of activity for meaningful numbers — about 7 days is a good baseline.',
  'astor.analytics.onboardingCompletion': 'Onboarding Completion',
  'astor.analytics.onboardingCompletionSubtitle': 'Users who finished onboarding',
  'astor.analytics.engagement': 'Astor Engagement',
  'astor.analytics.engagementSubtitle': 'Users interacting with Astor this period',
  'astor.analytics.featureAdoption': 'Feature Adoption',
  'astor.analytics.featureAdoptionSubtitle': 'Features discovered via Astor',
  'astor.analytics.topQuestions': 'Top Questions',
  'astor.analytics.topQuestionsEmpty': 'No questions recorded yet',
  'astor.analytics.nudgeEffectiveness': 'Nudge Effectiveness',
  'astor.analytics.nudgeAccepted': 'Accepted',
  'astor.analytics.nudgeDismissed': 'Dismissed',
  'astor.analytics.nudgeAutoDismissed': 'Auto-dismissed',
  'astor.analytics.nudgeTotal': '{{count}} total nudges',
  'astor.analytics.activeUsers': 'Active Users',
  'astor.analytics.dateRange': 'Date Range',
  'astor.analytics.last7Days': 'Last 7 days',
  'astor.analytics.last30Days': 'Last 30 days',
  'astor.analytics.last90Days': 'Last 90 days',
  'astor.analytics.timesAsked': '{{count}} times asked',
  'astor.analytics.noData': 'No analytics data available yet. Data will appear as users interact with Astor.',

  // ============================================================================
  // CONTEXTUAL HINTS (Astor-driven feature hints)
  // ============================================================================

  // Dashboard hints
  'hints.dashboard.newLead.title': 'Create a New Lead',
  'hints.dashboard.newLead.description':
    'Start prospecting by adding a new lead. Leads flow through your pipeline and can be converted into loan cases.',
  'hints.dashboard.newCase.title': 'Open a New Case',
  'hints.dashboard.newCase.description':
    'Create a loan case to begin origination. Cases track documents, communications, and status through your workflow.',
  'hints.dashboard.uploadDoc.title': 'Upload Documents',
  'hints.dashboard.uploadDoc.description':
    'Upload supporting documents like pay stubs, tax returns, or bank statements. AI will auto-classify them for you.',

  // Cases & Entities hints
  'hints.cases.dataTable.title': 'Your Loans at a Glance',
  'hints.cases.dataTable.description':
    'Sort, filter, and search your active loans. Click any row to view full details, documents, and communication history.',
  'hints.entities.newEntity.title': 'Add a New Entity',
  'hints.entities.newEntity.description':
    'Entities represent borrowers, guarantors, or businesses. Create one to link them to cases and track their records.',
  'hints.entities.dataTable.title': 'Entity Directory',
  'hints.entities.dataTable.description':
    'Browse all entities across your organization. Use filters to find specific borrowers or businesses quickly.',
  'hints.documents.container.title': 'Document Management',
  'hints.documents.container.description':
    'Upload, review, and organize loan documents. Documents are automatically linked to their associated cases.',

  // Settings & Pipeline hints
  'hints.settings.astorAgents.title': 'Configure AI Agents',
  'hints.settings.astorAgents.description':
    'Set up Astor AI agents with custom instructions, autonomy levels, and tool access to automate your workflows.',
  'hints.settings.team.title': 'Team Management',
  'hints.settings.team.description':
    'Invite team members, assign roles, and manage permissions. Control who can access loans, documents, and settings.',
  'hints.settings.notifications.title': 'Notification Preferences',
  'hints.settings.notifications.description':
    'Customize which events trigger notifications and how they are delivered — email, in-app, or both.',

  // Consumer Portal hints
  'hints.consumer.loanApplication.title': 'Start Your Loan Application',
  'hints.consumer.loanApplication.description':
    'Begin your loan journey here. Astor will guide you through each step of the application process.',
  'hints.consumer.uploadDocuments.title': 'Upload Required Documents',
  'hints.consumer.uploadDocuments.description':
    'Upload your income verification, identification, and other required documents to speed up your application.',
  'hints.consumer.applicationStatus.title': 'Track Your Applications',
  'hints.consumer.applicationStatus.description':
    'View the status of all your loan applications. Click any application to see detailed progress and next steps.',
  'hints.consumer.uploadFromDashboard.title': 'Quick Document Upload',
  'hints.consumer.uploadFromDashboard.description':
    'Upload documents directly from the dashboard without navigating away from your overview.',
  'hints.consumer.viewTimeline.title': 'Loan Timeline',
  'hints.consumer.viewTimeline.description':
    'Track the progress of your loan application through each stage with a detailed timeline view.',
  'hints.consumer.startApplication.title': 'Start a New Application',
  'hints.consumer.startApplication.description':
    'Ready for a new loan? Start your application here and Astor will walk you through each step.',

  // Admin Portal hints
  'hints.admin.businesses.title': 'Business Management',
  'hints.admin.businesses.description':
    'View and manage all businesses on the platform. Monitor onboarding status, subscription tiers, and usage metrics.',
  'hints.admin.users.title': 'User Administration',
  'hints.admin.users.description':
    'Manage platform users, assign roles, and review access permissions across businesses and realms.',
  'hints.admin.systemHealth.title': 'System Health Overview',
  'hints.admin.systemHealth.description':
    'Monitor service health, view recent incidents, and track canary test results across the platform.',
  'hints.admin.dashboard.title': 'Admin Dashboard',
  'hints.admin.dashboard.description':
    'Your command center — monitor platform-wide metrics, active tenants, and system status at a glance.',
  'hints.admin.tenants.title': 'Tenant Management',
  'hints.admin.tenants.description':
    'Create, configure, and monitor tenants. Manage realm isolation, subscription tiers, and resource quotas.',
  'hints.admin.platformOverview.title': 'Platform Overview',
  'hints.admin.platformOverview.description':
    'Understand the full platform architecture, capabilities, and how all services work together.',
  'hints.admin.platformArchitecture.title': 'Architecture View',
  'hints.admin.platformArchitecture.description':
    'Explore the technical architecture — service dependencies, data flows, and infrastructure layout.',
  'hints.admin.platformPersonas.title': 'Persona Management',
  'hints.admin.platformPersonas.description':
    'Configure user personas that define default permissions, onboarding flows, and feature access per role.',
  'hints.admin.platformWorkflows.title': 'Workflow Templates',
  'hints.admin.platformWorkflows.description':
    'Design and manage workflow templates that businesses can use to automate their operations.',

  // ============================================================================
  // SPOTLIGHT TOURS (Astor-driven guided tours)
  // ============================================================================

  // Welcome tour
  'tours.welcome.title': 'Welcome to {{platformName}}',
  'tours.welcome.steps.dashboardOverview':
    'This is your dashboard — a real-time overview of your pipeline, recent activity, and key metrics.',
  'tours.welcome.steps.createLead':
    'Click here to create a new {{lead}}. {{leads}} are the starting point of your sales pipeline.',
  'tours.welcome.steps.createCase':
    'Create a {{case}} to begin the origination process. {{cases}} track everything from application to closing.',
  'tours.welcome.steps.uploadDocuments':
    'Upload {{documents}} directly from the dashboard. AI will auto-classify and link them to the right {{case}}.',

  // First case tour
  'tours.firstCase.title': 'Creating Your First {{case}}',
  'tours.firstCase.steps.clickNewCase':
    'Start by clicking this button to open the new {{case}} form. You will enter borrower details and {{loan}} parameters.',
  'tours.firstCase.steps.addBorrower':
    'Add or select a borrower for this {{case}}. You can create a new borrower or link an existing one from your contacts.',
  'tours.firstCase.steps.configureSettings':
    'Set the priority and due date for this {{case}}. These help you track urgency and SLA commitments.',
  'tours.firstCase.steps.casesTable':
    'After creation, your {{case}} appears here. Click any row to view details, update status, or add {{documents}}.',
  'tours.firstCase.steps.generateTermSheet':
    'Generate a term sheet for your {{case}}. Pricing rules auto-populate the terms — review, customize, and send for e-signature.',
  'tours.firstCase.steps.attachDocuments':
    'Upload required {{documents}} here. Each {{document}} is linked to its {{case}} and validated by AI.',

  // Pipeline tour
  'tours.pipeline.title': 'Managing Your Pipeline',
  'tours.pipeline.steps.createEntity':
    'Create entities to represent borrowers, guarantors, or businesses involved in your deals.',
  'tours.pipeline.steps.entitiesTable':
    'Your entity directory shows all contacts. Filter by type, status, or search by name to find anyone quickly.',
  'tours.pipeline.steps.trackCases':
    'Track every {{case}} through your pipeline. Sort by status, assignee, or date to prioritize your work.',

  // Team tour
  'tours.team.title': 'Inviting Team Members',
  'tours.team.steps.teamSettings':
    'Manage your team here. Invite new members, assign roles, and control access to sensitive data.',
  'tours.team.steps.configureAstor':
    'Configure AI agents to automate repetitive tasks. Set autonomy levels and approve actions before they execute.',
  'tours.team.steps.notifications':
    'Set up notification rules so your team stays informed about {{case}} updates, {{document}} requests, and deadlines.',

  // Control Center tour
  'tours.controlCenter.title': 'Configuring Your Workspace',
  'tours.controlCenter.steps.hub':
    'The Control Center is your settings hub. From here you can configure pipelines, integrations, permissions, and more.',
  'tours.controlCenter.steps.pipeline':
    'Set up pipeline stages to track {{cases}} through your workflow. Define custom stages and automation rules.',
  'tours.controlCenter.steps.integrations':
    'Connect third-party services like email, CRM, and document providers to streamline your workflow.',
  'tours.controlCenter.steps.astorAgents':
    'Configure AI agents to handle repetitive tasks. Set autonomy levels and review actions before they execute.',

  // Documents tour
  'tours.documents.title': 'Managing {{documents}}',
  'tours.documents.steps.overview':
    'This is your {{document}} center. Upload, organize, and attach {{documents}} to {{cases}} from here.',
  'tours.documents.steps.verification':
    'Track {{document}} verification status here. Each {{document}} goes through review — pending, verified, or needs re-upload.',
  'tours.documents.steps.signingQueue':
    'Your signing queue shows all {{documents}} awaiting e-signatures. Send, track, and manage signature requests from here.',
  'tours.documents.steps.retainedCopies':
    'Signed and completed {{documents}} are archived here as retained copies for your records.',

  // Reports & Analytics tour
  'tours.reports.title': 'Reports & Analytics',
  'tours.reports.steps.reports':
    'Access pipeline analytics, loan portfolio reports, financial summaries, and performance metrics all in one place.',
  'tours.reports.steps.analytics':
    'Dive deeper into application analytics — funnel analysis, review times, rejection rates, and bottleneck identification.',
  'tours.reports.steps.astorAnalytics':
    'Monitor how your team uses Astor AI — completion rates, engagement, and feature adoption.',

  // Prospecting tour
  'tours.prospecting.title': 'Prospecting & Lead Management',
  'tours.prospecting.steps.discovery':
    'Use AI-powered discovery to find potential borrowers and investors. Search by location, property type, or investment criteria.',
  'tours.prospecting.steps.leads':
    'Your leads pipeline. Track every prospect from first contact through conversion. Score, filter, and prioritize your outreach.',
  'tours.prospecting.steps.sequences':
    'Create automated email and voice sequences to nurture leads. Multi-step campaigns run on autopilot.',
  'tours.prospecting.steps.deals':
    'Track active deals through your sales pipeline. Drag and drop to update stages as deals progress.',

  // Partnerships tour
  'tours.partnerships.title': 'Partner & Referral Management',
  'tours.partnerships.steps.partnerDirectory':
    'Your partner directory. Add brokers, correspondents, and referral sources. Track each relationship and its performance.',
  'tours.partnerships.steps.referrals':
    'Manage inbound and outbound referrals. Track which partners are sending you deals and where you are referring business.',
  'tours.partnerships.steps.agreements':
    'Set up partnership agreements with commission terms, territory exclusivity, and expiration dates.',
  'tours.partnerships.steps.commissions':
    'Track commission accruals and payouts for each partner. View earnings by deal, partner, or time period.',

  // ============================================================================
  // NOTIFICATION TEMPLATES & TRIGGERS
  // ============================================================================
  'notifications.templates.category': 'Category',
  'notifications.templates.create': 'Create Template',
  'notifications.templates.createHint': 'Configure the template content after creation',
  'notifications.templates.name': 'Template Name',
  'notifications.triggers.create': 'Create Trigger',

  // ============================================================================
  // PIPELINES AUTOMATIONS
  // ============================================================================
  'pipelines.automations.create': 'Create Automation',
  'pipelines.automations.stage': 'Stage',
  'pipelines.automations.trigger': 'Trigger',
  'pipelines.automations.triggerType.stageEntered': 'Stage Entered',
  'pipelines.automations.triggerType.stageExited': 'Stage Exited',
  'pipelines.automations.triggerType.recordCreated': 'Record Created',
  'pipelines.automations.triggerType.recordUpdated': 'Record Updated',
  'pipelines.automations.triggerType.fieldChanged': 'Field Changed',
  'pipelines.automations.triggerType.scheduled': 'Scheduled',
  'pipelines.automations.triggerType.manual': 'Manual',
  'pipelines.automations.triggerType.assessmentScoreChanged': 'Assessment Score Changed',
  'pipelines.automations.triggerType.assessmentRecommendationChanged': 'Assessment Recommendation Changed',

  // ============================================================================
  // CASE MANAGEMENT SETTINGS
  // ============================================================================
  'settings.caseManagement.addPriority': 'Add Priority Level',
  'settings.caseManagement.addSla': 'Add SLA Policy',
  'settings.caseManagement.addStatus': 'Add Loan Status',
  'settings.caseManagement.addType': 'Add Loan Category',
  'settings.caseManagement.editPriority': 'Edit Priority Level',
  'settings.caseManagement.editSla': 'Edit SLA Policy',
  'settings.caseManagement.editStatus': 'Edit Loan Status',
  'settings.caseManagement.editType': 'Edit Loan Category',
  'settings.caseManagement.color': 'Color',
  'settings.caseManagement.enableEscalation': 'Enable escalation',
  'settings.caseManagement.escalationTime': 'Escalation Time (hours)',
  'settings.caseManagement.finalStatus': 'Final status (loan closed)',
  'settings.caseManagement.priorityNamePlaceholder': 'e.g., High',
  'settings.caseManagement.resolutionTime': 'Resolution Time (hours)',
  'settings.caseManagement.resolvedStatus': 'Resolved status (loan successfully completed)',
  'settings.caseManagement.responseTime': 'Response Time (hours)',
  'settings.caseManagement.setDefaultSla': 'Set as default SLA policy',
  'settings.caseManagement.slaMultiplier': 'SLA Multiplier',
  'settings.caseManagement.slaMultiplierPlaceholder': 'e.g., 0.5 for urgent, 2 for low',
  'settings.caseManagement.slaNamePlaceholder': 'e.g., Standard SLA',
  'settings.caseManagement.statusNamePlaceholder': 'e.g., In Progress',
  'settings.caseManagement.typeNamePlaceholder': 'e.g., Fix and Flip',
  'settings.caseManagement.tabCaseTypes': 'Loan Categories',
  'settings.caseManagement.tabStatuses': 'Loan Statuses',
  'settings.caseManagement.tabSla': 'SLA Policies',
  'settings.caseManagement.tabPriorities': 'Priority Levels',
  'settings.caseManagement.sectionCaseStatuses': 'Loan Statuses',

  // ============================================================================
  // TASKS (Additional)
  // ============================================================================
  'tasks.actualTime': 'Actual Time (minutes)',
  'tasks.assign': 'Assign Task',
  'tasks.assignDescription': 'Assign {{title}} to a team member',
  'tasks.assigneeHelp': 'Select a team member to assign this task to',
  'tasks.complete': 'Complete Task',
  'tasks.completeDescription': 'Mark {{title}} as completed',
  'tasks.completeNote': 'Completion Notes',
  'tasks.descriptionPlaceholder': 'Describe the task...',
  'tasks.dueDateHelp': 'When should this task be completed?',
  'tasks.entityRequired': 'An entity is required',
  'tasks.estimatedTime': 'Estimated: {{minutes}} min',
  'tasks.estimatedTimeHelp': 'How long do you expect this task to take?',
  'tasks.estimatedTimeLabel': 'Estimated Time (minutes)',
  'tasks.howLong': 'How long did this take?',
  'tasks.instructions': 'Instructions',
  'tasks.markComplete': 'Mark Complete',
  'tasks.selectAssignee': 'Select assignee...',
  'tasks.selectTemplate': 'Select a template...',
  'tasks.template': 'Template',
  'tasks.templateHelp': 'Use a template to pre-fill task details',
  'tasks.titlePlaceholder': 'Enter task title...',
  'tasks.useTemplate': 'Use Template',
  'tasks.completedAt': 'Completed',
  'tasks.deleteConfirmTitle': 'Delete Task',
  'tasks.deleteConfirmMessage': 'Are you sure you want to delete "{{title}}"? This action cannot be undone.',

  // ============================================================================
  // CONTEXT RAIL
  // ============================================================================
  'contextRail.activity': 'Activity',
  'contextRail.timeline': 'Timeline',
  'contextRail.messages': 'Messages',
  'contextRail.related': 'Requests',
  // v3 rail-tab labels (Issue #1769) — four-tab shape shared across loan,
  // application, partner, entity, and borrower detail pages. Kept distinct
  // from the legacy `contextRail.{activity,messages,related}` keys (which
  // are still used as default-prop fallbacks elsewhere).
  'contextRail.tab.activity': 'Activity',
  'contextRail.tab.messages': 'Messages',
  'contextRail.tab.requests': 'Requests',
  'contextRail.tab.astor': 'Astor',
  'contextRail.related.assignee': 'Assigned to',
  'contextRail.related.borrowers': 'Borrowers',
  'contextRail.related.entities': 'Entities',
  'contextRail.related.guarantors': 'Guarantors',
  'contextRail.related.coBorrowers': 'Co-borrowers',
  'contextRail.related.loans': 'Loans',
  'contextRail.related.convertedCase': 'Converted case',
  'contextRail.related.viewCase': 'View case',
  'contextRail.related.borrower': 'Borrower',
  'contextRail.empty.activity': 'No activity yet.',
  'contextRail.empty.messages': 'No messages yet.',
  'contextRail.empty.assignee': 'Unassigned',
  'contextRail.empty.borrower': 'No borrower assigned.',
  'contextRail.empty.borrowers': 'No borrowers yet.',
  'contextRail.empty.entities': 'No entities linked.',
  'contextRail.empty.guarantors': 'No guarantors.',
  'contextRail.empty.coBorrowers': 'No co-borrowers.',
  'contextRail.empty.loans': 'No loans yet.',
  'contextRail.messages.placeholder': 'Reply…',
  'contextRail.add.entity': 'Link entity',
  'contextRail.add.guarantor': 'Add guarantor',
  'contextRail.removeConfirm.entity': 'Remove this entity from the loan?',
  'contextRail.removeConfirm.guarantor': 'Remove this guarantor?',
  'contextRail.placeholder.ownershipInterest': '0-100',
  'contextRail.placeholder.guaranteeAmount': '$0',
  'contextRail.placeholder.guaranteePercentage': '0-100%',
  // Astor pane (v3 rail) — Issue #1769. Backend wiring for case-level
  // suggestions / actions lives in filed sub-issues; the pane renders an
  // empty-state and disables actions with the `common.comingSoon` tooltip
  // until those land.
  'contextRail.astor.summaryHeading': 'Astor · summary',
  'contextRail.astor.summaryEmpty':
    'No summary yet. Astor will post a recommendation once it finishes evaluating this loan.',
  'contextRail.astor.suggestedActions': 'Suggested actions',
  'contextRail.astor.action.approve': 'Approve recommendation',
  'contextRail.astor.action.openCreditMemo': 'Open credit memo',
  'contextRail.astor.action.draftReply': 'Draft reply to borrower',
  'contextRail.astor.action.rerunSizer': 'Re-run sizer',

  // Consolidated activity feed — grouped row titles (ConsolidatedActivityList)
  'contextRail.activity.group.factorWithActor': '{{actor}} evaluated {{count}} factors',
  'contextRail.activity.group.factor': '{{count}} factors evaluated',
  'contextRail.activity.group.scoreWithActor': '{{actor}} updated {{count}} scores',
  'contextRail.activity.group.score': '{{count}} scores updated',
  'contextRail.activity.group.dataWithActor': '{{actor}} ingested {{count}} data updates',
  'contextRail.activity.group.data': '{{count}} data updates',
  'contextRail.activity.group.editWithActor': '{{actor}} made {{count}} edits',
  'contextRail.activity.group.edit': '{{count}} edits',
  'contextRail.activity.group.defaultWithActor': '{{actor}} · {{count}} updates',
  'contextRail.activity.group.default': '{{count}} updates',

  // ============================================================================
  // DATA ROOM
  // ============================================================================
  'dataRoom.title': 'Data Room',
  'dataRoom.unknownType': 'Unknown',
  'dataRoom.sources': 'Document Sources',
  'dataRoom.loanDocuments': 'Loan Documents',
  'dataRoom.borrowerDocuments': '{{name}} Documents',
  'dataRoom.entityDocuments': '{{name}} Documents',
  'dataRoom.browse': 'Browse & Link',
  'dataRoom.linkDocument': 'Link to Data Room',
  'dataRoom.unlinkDocument': 'Remove from Data Room',
  'dataRoom.linkedFrom': 'Linked from {{source}}',
  'dataRoom.uploadNew': 'Upload New',
  'dataRoom.scope.loan': 'Loan-Specific',
  'dataRoom.scope.personal': 'Personal',
  'dataRoom.scope.entity': 'Entity',
  'dataRoom.checklist.progress': '{{fulfilled}} of {{total}} required',
  'dataRoom.checklist.allComplete': 'All required documents collected',
  'dataRoom.checklist.missingItems': '{{count}} documents still needed',
  'dataRoom.reuse.title': 'Reuse My Documents',
  'dataRoom.reuse.description': 'Documents from your profile that can be used for this loan',
  'dataRoom.reuse.useForLoan': 'Use for This Loan',
  'dataRoom.reuse.alreadyLinked': 'Already linked',
  'dataRoom.reuse.previouslyUploaded': 'Previously uploaded',
  'dataRoom.reuse.linkSuccess': 'Document linked to this loan',
  'dataRoom.reuse.linkError': 'Failed to link document',
  'dataRoom.empty.title': 'No Documents Yet',
  'dataRoom.empty.description': 'Browse related sources or upload new documents to get started.',
  'dataRoom.guardrail.unauthorized': 'This document cannot be linked to this loan.',
  'dataRoom.guardrail.notFound': 'Document not found on the specified source.',
  'dataRoom.guardrail.alreadyLinked': 'This document is already linked to this loan.',
  'dataRoom.source.primaryBorrower': 'Primary Borrower',
  'dataRoom.source.coBorrower': 'Co-Borrower',
  'dataRoom.source.sponsor': 'Sponsor',
  'dataRoom.source.guarantor': 'Guarantor',
  'dataRoom.source.entityOwner': 'Entity Owner',
  'dataRoom.source.entity': 'Entity',
  'dataRoom.linkedBadge': 'Data Room',
  'dataRoom.postIntake.title': 'Link Existing Documents',
  'dataRoom.postIntake.description': 'We found documents from case participants that can be linked to this case.',
  'dataRoom.postIntake.linkAll': 'Link All',
  'dataRoom.postIntake.skip': 'Skip',
  'dataRoom.postIntake.linked': 'Documents linked successfully',
  'dataRoom.postIntake.noDocuments': 'No reusable documents found',
  'dataRoom.postIntake.documentsAvailable': '{{count}} documents available',
  'dataRoom.postIntake.linking': 'Linking documents...',
  'dataRoom.uploadTitle': 'Upload to Data Room',
  'dataRoom.unlinkTitle': 'Unlink Document',
  'dataRoom.unlinkMessage': 'Are you sure you want to unlink "{{name}}" from this case?',
  'dataRoom.unlinkConfirm': 'Unlink',
  'dataRoom.upload': 'Upload Document',
  'dataRoom.downloadUnavailable':
    'Document preview is not available. The file may not be stored yet or cloud storage is not configured.',
  'dataRoom.downloadFailed': 'Failed to download document. Please try again later.',
  'dataRoom.browseMyDocuments': 'My Documents',
  'dataRoom.browseAndLink': 'Browse & Link',
  'dataRoom.noReusableDocuments': 'No documents available to link',
  'dataRoom.noReusableDocumentsHint': 'Documents you upload to other applications will appear here for reuse',
  'dataRoom.browseMyDocumentsDescription': '{{count}} document(s) from your other applications available to link',
  'dataRoom.link': 'Link',
  'dataRoom.requiredDocuments': 'Required Documents',
  'dataRoom.required': 'Required',
  'dataRoom.optional': 'Optional',

  // Document detail SplitPanel — opens when an attachment row is clicked.
  'dataRoom.documentDetail.title': 'Document',
  'dataRoom.documentDetail.unnamedDoc': 'Document',
  'dataRoom.documentDetail.tab.info': 'Info',
  'dataRoom.documentDetail.tab.extracted': 'Extracted fields',
  'dataRoom.documentDetail.tab.activity': 'Activity',
  'dataRoom.documentDetail.noFields': 'No extracted fields for this document yet.',
  'dataRoom.documentDetail.noActivity': 'No activity recorded for this document yet.',
  'dataRoom.documentDetail.resourceLabel': 'document',
  'dataRoom.documentDetail.uploadedBy': 'Uploaded by',
  'dataRoom.documentDetail.uploadedAt': 'Uploaded',
  'dataRoom.documentDetail.openInDocuments': 'Open in Documents',
  'dataRoom.verification.pending': 'Pending',
  'dataRoom.verification.verified': 'Verified',
  'dataRoom.verification.rejected': 'Rejected',
  'dataRoom.verification.needs_reupload': 'Needs re-upload',
  'dataRoom.verification.expired': 'Expired',

  // Attachment Management (Issue #948 - doc-mgmt gaps)
  'attachment.detachTitle': 'Detach Document',
  'attachment.detachMessage':
    'This will remove the document link from this entity. The document itself will not be deleted.',
  'attachment.editTitle': 'Edit Attachment',
  'attachment.displayName': 'Display Name',
  'attachment.expiresAt': 'Expiration Date',
  'attachment.moveTitle': 'Move Attachment',
  'attachment.moveMessage': 'Move this attachment to a different entity.',
  'attachment.reject.title': 'Reject Document',
  'attachment.reject.message': 'Please provide a reason for rejecting this document.',
  'attachment.reject.reasonLabel': 'Rejection Reason',
  'attachment.reject.placeholder': 'Enter rejection reason...',
  'attachment.reupload.title': 'Request Re-upload',
  'attachment.reupload.message': 'Please provide a reason for requesting a re-upload of this document.',
  'attachment.reupload.reasonLabel': 'Re-upload Reason',
  'attachment.reupload.placeholder': 'Enter reason for re-upload...',
  'attachment.edit.title': 'Edit Attachment',
  'attachment.edit.displayName': 'Display Name',
  'attachment.edit.description': 'Description',
  'attachment.edit.expiresAt': 'Expiration Date',
  'attachment.edit.documentType': 'Document Type',
  'attachment.edit.category': 'Category',
  'attachment.edit.notes': 'Notes',
  'attachment.move.title': 'Move Attachment',
  'attachment.move.message': 'Move this attachment to a different entity.',
  'attachment.move.entityTypeLabel': 'Entity Type',
  'attachment.move.entityIdLabel': 'Entity ID',
  'attachment.move.confirm': 'Move',

  // Financial profile option labels (Issue #13 — i18n)
  'employment.status.unemployed': 'Unemployed',
  'employment.status.student': 'Student',
  'employment.status.other': 'Other',
  'employment.businessType.soleProprietorship': 'Sole Proprietorship',
  'employment.businessType.llc': 'LLC',
  'employment.businessType.sCorp': 'S Corporation',
  'employment.businessType.cCorp': 'C Corporation',
  'employment.businessType.partnership': 'Partnership',
  'employment.businessType.other': 'Other',
  'assets.type.checking': 'Checking Account',
  'assets.type.savings': 'Savings Account',
  'assets.type.investment': 'Investment Account',
  'assets.type.retirement': 'Retirement Account (401k, IRA)',
  'assets.type.realEstate': 'Real Estate',
  'assets.type.vehicle': 'Vehicle',
  'assets.type.other': 'Other Asset',
  'liabilities.type.mortgage': 'Mortgage',
  'liabilities.type.autoLoan': 'Auto Loan',
  'liabilities.type.studentLoan': 'Student Loan',
  'liabilities.type.creditCard': 'Credit Card',
  'liabilities.type.personalLoan': 'Personal Loan',
  'liabilities.type.heloc': 'HELOC',
  'liabilities.type.other': 'Other Debt',

  // ============================================================================
  // PLATFORM GUIDE CONTENT
  // ============================================================================
  'platform.hero.title': 'Fiops Lending Platform',
  'platform.hero.subtitle':
    'End-to-end commercial real estate lending operations — from loan origination and underwriting to partner management and consumer self-service.',
  'platform.stats.services': 'Microservices',
  'platform.stats.portals': 'Portals',
  'platform.stats.workflows': 'Workflows',
  'platform.stats.healthyServices': 'Healthy Services',

  // Platform capabilities
  'platform.section.whatWeDo': 'What We Do',
  'platform.capability.origination.title': 'Loan Origination',
  'platform.capability.origination.desc':
    'Case management with stage-based pipelines, intake forms, underwriting review, and approval workflows. Full SLA tracking and audit trails.',
  'platform.capability.partners.title': 'Partner Network',
  'platform.capability.partners.desc':
    'Referral management, commission tracking, partner onboarding with secure invite tokens, and B2B2C lending workflows.',
  'platform.capability.consumer.title': 'Consumer Portal',
  'platform.capability.consumer.desc':
    'Borrower self-service for loan applications, document uploads, proposal review, messaging, and real-time status tracking.',
  'platform.capability.ai.title': 'AI-Powered',
  'platform.capability.ai.desc':
    'Astor AI assistant for smart recommendations, agent workflows, document analysis, and automated compliance checks.',

  // Section headings
  'platform.section.techStack': 'Tech Stack',
  'platform.section.accessLevel': 'Access Level',
  'platform.section.keyWorkflows': 'Key Workflows',

  // Tech stack
  'platform.tech.runtime': 'Runtime',
  'platform.tech.backend': 'Backend',
  'platform.tech.frontend': 'Frontend',
  'platform.tech.monorepo': 'Monorepo',
  'platform.tech.api': 'API Protocol',
  'platform.tech.serviceMesh': 'Service Mesh',
  'platform.tech.orm': 'ORM',
  'platform.tech.database': 'Database',

  // Persona groups
  'platform.personas.group.admin': 'Admin Portal',
  'platform.personas.group.business': 'Business Portal',
  'platform.personas.group.consumer': 'Consumer Portal',

  // Personas — Admin Portal
  'platform.persona.superAdmin.name': 'Super Admin',
  'platform.persona.superAdmin.role': 'Platform Owner',
  'platform.persona.superAdmin.email': 'superadmin@platform.com',
  'platform.persona.superAdmin.portal': 'Admin',
  'platform.persona.superAdmin.access':
    'Full platform access — all tenants, realms, settings, billing, and system configuration.',
  'platform.persona.superAdmin.workflows':
    'admin-user-management-security, admin-permission-boundaries, admin-billing-subscription-management',

  'platform.persona.platformAdmin.name': 'Platform Admin',
  'platform.persona.platformAdmin.role': 'Platform Operations',
  'platform.persona.platformAdmin.email': 'admin@platform.com',
  'platform.persona.platformAdmin.portal': 'Admin',
  'platform.persona.platformAdmin.access':
    'Multi-tenant management — monitoring, service health, analytics, and tenant lifecycle. No billing access.',
  'platform.persona.platformAdmin.workflows':
    'admin-dashboard-navigation, admin-analytics-dashboards, admin-tenant-business-realm-lifecycle',

  // Personas — Business Portal
  'platform.persona.businessAdmin.name': 'Rachel Martinez',
  'platform.persona.businessAdmin.role': 'Business Admin',
  'platform.persona.businessAdmin.email': 'rachel.martinez@opsmatrix.local',
  'platform.persona.businessAdmin.portal': 'Business',
  'platform.persona.businessAdmin.access':
    'Full business operations — all loans, users, settings, reports, and team management.',
  'platform.persona.businessAdmin.workflows':
    'loan-anonymous-to-approval, permission-boundaries, team-invite-onboarding',

  'platform.persona.opsManager.name': 'Operations Manager',
  'platform.persona.opsManager.role': 'Ops Manager',
  'platform.persona.opsManager.email': 'ops.manager@opsmatrix.local',
  'platform.persona.opsManager.portal': 'Business',
  'platform.persona.opsManager.access':
    'Pipeline overview — all loans, task assignments, reporting, and team coordination.',
  'platform.persona.opsManager.workflows': 'lead-to-deal-conversion, case-approval-to-termsheet, task-management',

  'platform.persona.underwriter.name': 'Sarah Chen',
  'platform.persona.underwriter.role': 'Senior Underwriter',
  'platform.persona.underwriter.email': 'sarah.chen@opsmatrix.local',
  'platform.persona.underwriter.portal': 'Business',
  'platform.persona.underwriter.access':
    'Credit analysis, underwriting review, loan conditions, and approval recommendations.',
  'platform.persona.underwriter.workflows':
    'underwriting-review-approval, application-to-loan-origination, case-approval-to-termsheet',

  'platform.persona.processor.name': 'Lisa Wang',
  'platform.persona.processor.role': 'Senior Processor',
  'platform.persona.processor.email': 'lisa.wang@opsmatrix.local',
  'platform.persona.processor.portal': 'Business',
  'platform.persona.processor.access':
    'Document management, workflow processing, case updates, and borrower communication.',
  'platform.persona.processor.workflows':
    'consumer-doc-upload-approval, document-upload-approval-signing, intake-form-to-lead',

  'platform.persona.closer.name': 'Mike Johnson',
  'platform.persona.closer.role': 'Loan Closer',
  'platform.persona.closer.email': 'mike.johnson@opsmatrix.local',
  'platform.persona.closer.portal': 'Business',
  'platform.persona.closer.access': 'Closing preparation, funding queue, final document review, and disbursement.',
  'platform.persona.closer.workflows':
    'case-approval-to-termsheet, document-upload-approval-signing, data-room-document-gathering',

  'platform.persona.viewer.name': 'External Auditor',
  'platform.persona.viewer.role': 'Viewer (Read-Only)',
  'platform.persona.viewer.email': 'auditor@opsmatrix.local',
  'platform.persona.viewer.portal': 'Business',
  'platform.persona.viewer.access': 'Read-only access to all entities for external auditing and compliance review.',
  'platform.persona.viewer.workflows': 'permission-boundaries, cross-tenant-mutation-boundaries',

  // Personas — Consumer Portal
  'platform.persona.borrower.name': 'Borrower',
  'platform.persona.borrower.role': 'Loan Applicant',
  'platform.persona.borrower.email': 'Self-registration',
  'platform.persona.borrower.portal': 'Consumer',
  'platform.persona.borrower.access':
    'Loan applications, document uploads, proposal review, messaging with lender, and status tracking.',
  'platform.persona.borrower.workflows':
    'loan-anonymous-to-approval, consumer-doc-upload-approval, borrower-apply-to-chat',

  'platform.persona.partner.name': 'Referral Partner',
  'platform.persona.partner.role': 'Partner',
  'platform.persona.partner.email': 'Invited by lender',
  'platform.persona.partner.portal': 'Consumer',
  'platform.persona.partner.access':
    'Refer borrowers, track referral status, view commissions, and manage own referred cases.',
  'platform.persona.partner.workflows':
    'partner-referral-to-commission, partner-referral-to-application, partner-onboarding-agreement',

  // Workflow catalog page
  'platform.workflows.total': 'Total Workflows',
  'platform.workflows.ready': 'Ready',
  'platform.workflows.partial': 'Partial',
  'platform.workflows.stub': 'Stub',
  'platform.workflows.column.id': '#',
  'platform.workflows.column.key': 'Workflow',
  'platform.workflows.column.readiness': 'Readiness',
  'platform.workflows.column.priority': 'Priority',
  'platform.workflows.column.portals': 'Portals',
  'platform.workflows.filter.all': 'All',
  'platform.workflows.filter.readiness': 'Readiness',
  'platform.workflows.filter.priority': 'Priority',
  'platform.workflows.filter.portal': 'Portal',
  'platform.workflows.resultCount': '{{filtered}} of {{total}} workflows',

  // Stage Permissions admin editor (Issue #1559 — Stream G / Phase 5b)
  'platform.stagePermissions.title': 'Stage Permissions',
  'platform.stagePermissions.description':
    'Configure who can move loans through each pipeline stage, dual-approval requirements, single-actor caps, and team restrictions.',
  'platform.stagePermissions.forbiddenTitle': 'Access denied',
  'platform.stagePermissions.forbiddenMessage':
    'You do not have permission to view stage permissions. Contact your administrator if you need access.',
  'platform.stagePermissions.pipelinePicker.label': 'Pipeline',
  'platform.stagePermissions.pipelinePicker.placeholder': 'Select a pipeline to configure',
  'platform.stagePermissions.pipelinePicker.empty': 'No pipelines available',
  'platform.stagePermissions.pipelinePicker.loading': 'Loading pipelines…',
  'platform.stagePermissions.matrix.title': 'Pipeline stages',
  'platform.stagePermissions.matrix.empty': 'This pipeline has no stages defined yet.',
  'platform.stagePermissions.matrix.notConfigured': 'Default (no restriction)',
  'platform.stagePermissions.matrix.column.stage': 'Stage',
  'platform.stagePermissions.matrix.column.allowedRoles': 'Allowed roles',
  'platform.stagePermissions.matrix.column.cap': 'Single-approval cap',
  'platform.stagePermissions.matrix.column.dualControl': 'Dual control',
  'platform.stagePermissions.matrix.column.teams': 'Restricted teams',
  'platform.stagePermissions.matrix.column.active': 'Status',
  'platform.stagePermissions.matrix.column.actions': 'Actions',
  'platform.stagePermissions.matrix.cap.unlimited': 'Unlimited',
  'platform.stagePermissions.matrix.dualControl.required': 'Required',
  'platform.stagePermissions.matrix.dualControl.minApprovers': '{{count}} approvers',
  'platform.stagePermissions.matrix.teams.count': '{{count}} team',
  'platform.stagePermissions.matrix.teams.countPlural': '{{count}} teams',
  'platform.stagePermissions.matrix.teams.none': 'No team restriction',
  'platform.stagePermissions.matrix.status.active': 'Active',
  'platform.stagePermissions.matrix.status.inactive': 'Inactive',
  'platform.stagePermissions.matrix.allowedRoles.any': 'Any role',
  'platform.stagePermissions.matrix.allowedRoles.more': '+{{count}} more',
  'platform.stagePermissions.matrix.action.edit': 'Edit stage permissions',
  'platform.stagePermissions.matrix.action.view': 'View stage permissions',
  'platform.stagePermissions.matrix.action.softDelete': 'Deactivate',
  'platform.stagePermissions.matrix.action.resetAll': 'Reset entire pipeline to defaults',
  'platform.stagePermissions.editor.title.create': 'Configure stage',
  'platform.stagePermissions.editor.title.edit': 'Edit stage permissions',
  'platform.stagePermissions.editor.title.view': 'Stage permissions',
  'platform.stagePermissions.editor.section.access': 'Access',
  'platform.stagePermissions.editor.section.approval': 'Approval',
  'platform.stagePermissions.editor.section.scope': 'Scope',
  'platform.stagePermissions.editor.section.status': 'Status',
  'platform.stagePermissions.editor.field.allowedRoles': 'Allowed roles',
  'platform.stagePermissions.editor.field.allowedRoles.placeholder': 'Add a role…',
  'platform.stagePermissions.editor.field.allowedRoles.help':
    'Roles permitted to transition into this stage. Leave empty to allow any role.',
  'platform.stagePermissions.editor.field.requiredLicenses': 'Required licenses',
  'platform.stagePermissions.editor.field.requiredLicenses.placeholder': 'Type a license code, then Enter',
  'platform.stagePermissions.editor.field.requiredLicenses.help':
    'License codes the actor must hold (e.g., NMLS_INDIVIDUAL, STATE_LOAN_OFFICER).',
  'platform.stagePermissions.editor.field.singleApprovalCap': 'Single-approval cap',
  'platform.stagePermissions.editor.field.singleApprovalCap.help':
    'Above this amount, dual approval is required regardless of the toggle. Leave blank for no cap.',
  'platform.stagePermissions.editor.field.requiresDualApproval': 'Require dual approval',
  'platform.stagePermissions.editor.field.requiresDualApproval.help':
    'Two distinct humans must approve any transition into this stage.',
  'platform.stagePermissions.editor.field.approverRoles': 'Approver roles',
  'platform.stagePermissions.editor.field.approverRoles.placeholder': 'Add an approver role…',
  'platform.stagePermissions.editor.field.approverRoles.help':
    'Roles eligible to act as the second approver in a dual-approval flow.',
  'platform.stagePermissions.editor.field.minApproversRequired': 'Minimum approvers',
  'platform.stagePermissions.editor.field.minApproversRequired.help':
    'Lower bound for the number of distinct approvers (1–10). Defaults to 2.',
  'platform.stagePermissions.editor.field.restrictedToTeams': 'Restricted to teams',
  'platform.stagePermissions.editor.field.restrictedToTeams.placeholder': 'Add a team…',
  'platform.stagePermissions.editor.field.restrictedToTeams.help':
    'Only members of these teams may transition the loan into this stage. Leave empty for no team restriction.',
  'platform.stagePermissions.editor.field.isActive': 'Active',
  'platform.stagePermissions.editor.field.isActive.help':
    'Inactive rules are ignored at runtime. Toggle off to soft-delete this configuration.',
  'platform.stagePermissions.editor.action.save': 'Save permissions',
  'platform.stagePermissions.editor.action.cancel': 'Cancel',
  'platform.stagePermissions.editor.validation.minApproversRange': 'Must be between 1 and 10.',
  'platform.stagePermissions.editor.validation.capInvalid': 'Cap must be a non-negative number.',
  'platform.stagePermissions.editor.validation.dualNeedsApprovers':
    'Add at least one approver role when dual approval is required.',
  'platform.stagePermissions.editor.toast.saved': 'Stage permissions saved.',
  'platform.stagePermissions.editor.toast.saveFailed': 'Failed to save: {{message}}',
  'platform.stagePermissions.editor.toast.softDeleted': 'Stage permission deactivated.',
  'platform.stagePermissions.editor.toast.softDeleteFailed': 'Failed to deactivate: {{message}}',
  'platform.stagePermissions.editor.toast.resetSucceeded': 'Pipeline reset to defaults.',
  'platform.stagePermissions.editor.toast.resetFailed': 'Failed to reset: {{message}}',
  'platform.stagePermissions.confirm.softDelete.title': 'Deactivate stage permissions?',
  'platform.stagePermissions.confirm.softDelete.body':
    'The configuration is preserved but ignored at runtime. You can re-activate it later.',
  'platform.stagePermissions.confirm.softDelete.confirm': 'Deactivate',
  'platform.stagePermissions.confirm.softDelete.cancel': 'Cancel',
  'platform.stagePermissions.confirm.resetAll.title': 'Reset all stages?',
  'platform.stagePermissions.confirm.resetAll.body':
    'All stage permission rules in this pipeline will be deactivated and reset to platform defaults. This cannot be undone in bulk — you will need to re-create custom rules one stage at a time.',
  'platform.stagePermissions.confirm.resetAll.confirm': 'Reset all',
  'platform.stagePermissions.confirm.resetAll.cancel': 'Cancel',
  'platform.stagePermissions.audit.title': 'Recent changes',
  'platform.stagePermissions.audit.empty': 'No recent stage-permission changes.',
  'platform.stagePermissions.audit.loadFailed':
    'Could not load audit history (you may lack the audit-read permission).',
  'platform.stagePermissions.audit.row': '{{actor}} {{action}} on {{when}}',
  'platform.stagePermissions.audit.action.created': 'created',
  'platform.stagePermissions.audit.action.updated': 'updated',
  'platform.stagePermissions.audit.action.deleted': 'deactivated',
  'platform.stagePermissions.audit.action.unknown': 'changed',
  'platform.stagePermissions.audit.actor.system': 'System',
  'platform.stagePermissions.role.super_admin': 'Super Admin',
  'platform.stagePermissions.role.platform_admin': 'Platform Admin',
  'platform.stagePermissions.role.business_admin': 'Business Admin',
  'platform.stagePermissions.role.business_user': 'Business User',
  'platform.stagePermissions.role.business_viewer': 'Business Viewer',
  'platform.stagePermissions.role.consumer': 'Consumer',
  'platform.stagePermissions.role.loan_officer': 'Loan Officer',
  'platform.stagePermissions.role.processor': 'Processor',
  'platform.stagePermissions.role.closer': 'Closer',
  'platform.stagePermissions.role.underwriter': 'Underwriter',
  'platform.stagePermissions.role.senior_underwriter': 'Senior Underwriter',
  'platform.stagePermissions.role.analyst': 'Analyst',
  'platform.stagePermissions.role.ops_manager': 'Ops Manager',
  'platform.stagePermissions.role.portfolio_manager': 'Portfolio Manager',
  'platform.stagePermissions.role.fund_manager': 'Fund Manager',
  'platform.stagePermissions.role.principal': 'Principal',
  'platform.stagePermissions.role.draw_inspector': 'Draw Inspector',
  'platform.stagePermissions.role.servicing': 'Servicing',

  // ============================================================================
  // FIELD PERMISSIONS — Issue #1560 BIC Stream J / Phase 3
  // Per-LoanDataPoint role attribution editor surfaced in the platform admin
  // portal. Bridges the per-tenant `Permission.fieldPermissions.{read,edit}`
  // capability into 29-case-management-service's `FieldPermissionService`.
  // ============================================================================
  'platform.fieldPermissions.title': 'Field Permissions',
  'platform.fieldPermissions.description':
    'Override per-field role attribution for loan data points. Each field has separate viewable, editable, and hidden role lists, with optional per-stage refinements.',
  'platform.fieldPermissions.forbiddenTitle': 'Access denied',
  'platform.fieldPermissions.forbiddenMessage':
    'You do not have permission to view field permissions. Ask a Super Admin or Platform Admin for the `fieldPermissions:read` capability.',
  'platform.fieldPermissions.errorTitle': 'Failed to load field permissions',
  'platform.fieldPermissions.empty.title': 'No fields available',
  'platform.fieldPermissions.empty.description':
    'Bootstrap has not run for this business. Re-run the BIC bootstrap to seed the platform-default loan data points.',
  'platform.fieldPermissions.searchPlaceholder': 'Search by name or canonical key…',
  'platform.fieldPermissions.viewToggle.list': 'List view',
  'platform.fieldPermissions.viewToggle.matrix': 'Matrix view',
  'platform.fieldPermissions.categoryFilter.label': 'Category',
  'platform.fieldPermissions.categoryFilter.all': 'All categories',
  'platform.fieldPermissions.categoryFilter.borrower': 'Borrower',
  'platform.fieldPermissions.categoryFilter.property': 'Property',
  'platform.fieldPermissions.categoryFilter.deal': 'Deal',
  'platform.fieldPermissions.categoryFilter.guaranty': 'Guaranty',
  'platform.fieldPermissions.categoryFilter.entity': 'Entity',
  'platform.fieldPermissions.categoryFilter.insurance': 'Insurance',
  'platform.fieldPermissions.categoryFilter.dscr': 'DSCR',
  'platform.fieldPermissions.categoryFilter.construction': 'Construction',
  'platform.fieldPermissions.categoryFilter.document_slot': 'Document slot',
  'platform.fieldPermissions.categoryFilter.other': 'Other',
  'platform.fieldPermissions.resultCount': '{{filtered}} of {{total}} fields',

  // Reset-all (bulk) confirmation
  'platform.fieldPermissions.resetAll.button': 'Reset all to BIC default',
  'platform.fieldPermissions.resetAll.confirmTitle': 'Reset every forked field permission?',
  'platform.fieldPermissions.resetAll.confirmBody':
    'This restores the BIC seed for every field that has been overridden. Original tenant-only fields are unaffected.',
  'platform.fieldPermissions.resetAll.confirm': 'Reset all',
  'platform.fieldPermissions.resetAll.cancel': 'Cancel',

  // Toasts
  'platform.fieldPermissions.toast.saved': 'Field permissions saved.',
  'platform.fieldPermissions.toast.saveFailed': 'Failed to save: {{message}}',
  'platform.fieldPermissions.toast.reset': 'Field reset to BIC default.',
  'platform.fieldPermissions.toast.resetFailed': 'Failed to reset: {{message}}',
  'platform.fieldPermissions.toast.resetAllComplete': '{{count}} fields reset to BIC defaults.',
  'platform.fieldPermissions.toast.resetAllFailed': 'Failed to reset fields: {{message}}',

  // Table columns
  'platform.fieldPermissions.table.field': 'Field',
  'platform.fieldPermissions.table.category': 'Category',
  'platform.fieldPermissions.table.viewableByRoles': 'Viewable',
  'platform.fieldPermissions.table.editableByRoles': 'Editable',
  'platform.fieldPermissions.table.hiddenForRoles': 'Hidden',
  'platform.fieldPermissions.table.stageOverridesCount': 'Stage overrides',
  'platform.fieldPermissions.table.status': 'Source',
  'platform.fieldPermissions.table.actions': 'Actions',
  'platform.fieldPermissions.table.empty': 'No fields match the current filters.',
  'platform.fieldPermissions.table.allRoles': 'Any role',
  'platform.fieldPermissions.table.noRoles': 'None',
  'platform.fieldPermissions.table.rolesMore': '+{{count}} more',
  'platform.fieldPermissions.table.stageOverridesNone': 'None',
  'platform.fieldPermissions.table.stageOverridesCountValue': '{{count}} stages',
  'platform.fieldPermissions.table.bicDefault': 'BIC default',
  'platform.fieldPermissions.table.tenantEdited': 'Tenant edited',
  'platform.fieldPermissions.table.action.edit': 'Edit field permissions',
  'platform.fieldPermissions.table.action.view': 'View field permissions',

  // Matrix view
  'platform.fieldPermissions.matrix.title': 'Field × Role matrix',
  'platform.fieldPermissions.matrix.empty': 'No fields match the current filters.',
  'platform.fieldPermissions.matrix.field': 'Field',
  'platform.fieldPermissions.matrix.legendTitle': 'Legend',
  'platform.fieldPermissions.matrix.legend.editable': 'Editable',
  'platform.fieldPermissions.matrix.legend.viewable': 'Viewable',
  'platform.fieldPermissions.matrix.legend.hidden': 'Hidden',
  'platform.fieldPermissions.matrix.legend.unrestricted': 'Unrestricted',
  'platform.fieldPermissions.matrix.cell.editable': 'Editable',
  'platform.fieldPermissions.matrix.cell.viewable': 'Viewable',
  'platform.fieldPermissions.matrix.cell.hidden': 'Hidden',
  'platform.fieldPermissions.matrix.cell.unrestricted': 'Unrestricted',
  'platform.fieldPermissions.matrix.cell.aria': '{{role}} on {{field}}: {{access}}. Click to edit field permissions.',

  // Editor (SplitPanel)
  'platform.fieldPermissions.editor.title.create': 'Configure field permissions',
  'platform.fieldPermissions.editor.title.edit': 'Edit field permissions',
  'platform.fieldPermissions.editor.title.view': 'Field permissions',
  'platform.fieldPermissions.editor.subtitle.canonicalKey': 'Canonical key: {{key}}',
  'platform.fieldPermissions.editor.section.access': 'Role attribution',
  'platform.fieldPermissions.editor.section.stageOverrides': 'Per-stage overrides',
  'platform.fieldPermissions.editor.section.audit': 'Audit',
  'platform.fieldPermissions.editor.field.viewable': 'Viewable by',
  'platform.fieldPermissions.editor.field.viewable.placeholder': 'Add a role…',
  'platform.fieldPermissions.editor.field.viewable.help':
    'Roles permitted to view this field. Leave empty to allow any authenticated user (use "Hidden for" to deny specific roles).',
  'platform.fieldPermissions.editor.field.editable': 'Editable by',
  'platform.fieldPermissions.editor.field.editable.placeholder': 'Add a role…',
  'platform.fieldPermissions.editor.field.editable.help':
    'Roles permitted to edit this field. Leave empty to allow anyone with case-edit access.',
  'platform.fieldPermissions.editor.field.hidden': 'Hidden for',
  'platform.fieldPermissions.editor.field.hidden.placeholder': 'Add a role to hide from…',
  'platform.fieldPermissions.editor.field.hidden.help':
    'Explicit deny list — wins over "Viewable by" for the listed roles.',
  'platform.fieldPermissions.editor.field.reason': 'Reason for change',
  'platform.fieldPermissions.editor.field.reason.placeholder': 'e.g., "Hide SSN from loan officers until KYC clears"',
  'platform.fieldPermissions.editor.field.reason.help':
    'Recorded in the audit log; shown in compliance reviews. Optional but recommended.',
  'platform.fieldPermissions.editor.stageOverrides.empty':
    'No per-stage overrides configured. Add one to refine access for a specific pipeline stage.',
  'platform.fieldPermissions.editor.stageOverrides.add': 'Add stage override',
  'platform.fieldPermissions.editor.stageOverrides.stageLabel': 'Stage',
  'platform.fieldPermissions.editor.stageOverrides.stagePlaceholder': 'Select a stage',
  'platform.fieldPermissions.editor.stageOverrides.viewable': 'Viewable by (in stage)',
  'platform.fieldPermissions.editor.stageOverrides.editable': 'Editable by (in stage)',
  'platform.fieldPermissions.editor.stageOverrides.remove': 'Remove override',
  'platform.fieldPermissions.editor.stageOverrides.help':
    'Per-stage refinement of the role lists above. Fields omitted in a stage override fall back to the top-level lists.',
  'platform.fieldPermissions.editor.diff.title': 'Changes vs BIC default',
  'platform.fieldPermissions.editor.diff.viewableAdded': 'Adding to viewable: {{roles}}',
  'platform.fieldPermissions.editor.diff.viewableRemoved': 'Removing from viewable: {{roles}}',
  'platform.fieldPermissions.editor.diff.editableAdded': 'Adding to editable: {{roles}}',
  'platform.fieldPermissions.editor.diff.editableRemoved': 'Removing from editable: {{roles}}',
  'platform.fieldPermissions.editor.diff.hiddenAdded': 'Adding to hidden: {{roles}}',
  'platform.fieldPermissions.editor.diff.hiddenRemoved': 'Removing from hidden: {{roles}}',
  'platform.fieldPermissions.editor.diff.stageOverridesChanged': 'Per-stage overrides modified.',
  'platform.fieldPermissions.editor.diff.noChanges': 'No changes.',
  'platform.fieldPermissions.editor.bicDefault.banner': 'This field still matches the seeded BIC default.',
  'platform.fieldPermissions.editor.tenantEdited.banner':
    'This field has been forked from the BIC default. Reset to revert to the platform-managed seed.',
  'platform.fieldPermissions.editor.tenantOriginal.banner': 'Original tenant field — no template to reset to.',
  'platform.fieldPermissions.editor.lastUpdated': 'Last updated: {{when}}',
  'platform.fieldPermissions.editor.action.save': 'Save',
  'platform.fieldPermissions.editor.action.cancel': 'Cancel',
  'platform.fieldPermissions.editor.action.resetToDefault': 'Reset to BIC default',

  // Stage codes — reuse the BIC stage taxonomy (BicStageCode).
  'platform.fieldPermissions.stage.lead': 'Lead',
  'platform.fieldPermissions.stage.application': 'Application',
  'platform.fieldPermissions.stage.term_sheet': 'Term sheet',
  'platform.fieldPermissions.stage.underwriting': 'Underwriting',
  'platform.fieldPermissions.stage.conditional_approval': 'Conditional approval',
  'platform.fieldPermissions.stage.final_approval': 'Final approval',
  'platform.fieldPermissions.stage.closing_prep': 'Closing prep',
  'platform.fieldPermissions.stage.closing_scheduled': 'Closing scheduled',
  'platform.fieldPermissions.stage.funded': 'Funded',
  'platform.fieldPermissions.stage.draw_schedule': 'Draw schedule',
  'platform.fieldPermissions.stage.draw_disbursement': 'Draw disbursement',
  'platform.fieldPermissions.stage.final_inspection': 'Final inspection',
  'platform.fieldPermissions.stage.conversion_to_perm': 'Conversion to perm',
  'platform.fieldPermissions.stage.lease_verification': 'Lease verification',
  'platform.fieldPermissions.stage.rent_roll_review': 'Rent roll review',
  'platform.fieldPermissions.stage.exit_strategy_validation': 'Exit strategy validation',
  'platform.fieldPermissions.stage.servicing': 'Servicing',
  'platform.fieldPermissions.stage.paid_off': 'Paid off',
  'platform.fieldPermissions.stage.declined': 'Declined',
  'platform.fieldPermissions.stage.withdrawn': 'Withdrawn',
  'platform.fieldPermissions.stage.on_hold': 'On hold',

  // Architecture
  'platform.arch.systemDiagram': 'System Diagram',
  'platform.arch.multiTenancy': 'Multi-Tenancy Model',
  'platform.arch.serviceCatalog': 'Service Catalog',
  'platform.arch.protocols': 'Communication Protocols',
  'platform.arch.authFlow': 'Authentication Flow',
  'platform.arch.tier.portals': 'Portals',
  'platform.arch.tier.core': 'Core Infrastructure',
  'platform.arch.tier.aiMl': 'AI & Machine Learning',
  'platform.arch.tier.business': 'Business Operations',
  'platform.arch.tier.data': 'Data & Compliance',
  'platform.arch.tier.monitoring': 'Monitoring & Operations',

  // Architecture — Multi-tenancy hierarchy
  'platform.arch.mt.platform': 'Platform',
  'platform.arch.mt.tenant': 'Tenant',
  'platform.arch.mt.tenantBoundary': 'Organization boundary',
  'platform.arch.mt.realm': 'Realm',
  'platform.arch.mt.realmSchema': 'Own PostgreSQL schema',
  'platform.arch.mt.business': 'Business',
  'platform.arch.mt.users': 'Users',
  'platform.arch.mt.schemaPerRealm': 'Schema-per-Realm',
  'platform.arch.mt.schemaPerRealmDesc': 'Each realm gets its own PostgreSQL schema for full data isolation',
  'platform.arch.mt.rowLevel': 'Row-Level (businessId)',
  'platform.arch.mt.rowLevelDesc': 'Every query filters by businessId for row-level isolation',

  // Architecture — Communication protocols
  'platform.arch.requestFlow': 'Request Flow',
  'platform.arch.proto.portals': 'Portals',
  'platform.arch.proto.trpc': 'tRPC',
  'platform.arch.proto.apiGateway': 'API Gateway',
  'platform.arch.proto.grpc': 'gRPC',
  'platform.arch.proto.backendServices': 'Backend Services',
  'platform.arch.proto.firebaseAuth': 'Firebase Auth',
  'platform.arch.proto.authExchange': 'POST /auth/exchange',
  'platform.arch.proto.jwt': 'JWT',
  'platform.arch.proto.allRequests': 'All requests',

  // Architecture — Service detail panel
  'platform.arch.detail.serviceId': 'Service ID',
  'platform.arch.detail.name': 'Name',
  'platform.arch.detail.folder': 'Folder',
  'platform.arch.detail.port': 'Port',
  'platform.arch.detail.protocol': 'Protocol',
  'platform.arch.detail.tier': 'Tier',
  'platform.arch.detail.health': 'Health',
  'platform.arch.detail.details': 'Details',
  'platform.arch.detail.description': 'Description',
  'platform.arch.detail.serviceDetails': 'Service details',
  'platform.arch.moreServices': 'more',

  // ============================================================================
  // PARTIES (Consumer Portal — Case Parties)
  // ============================================================================
  'parties.role.primaryBorrower': 'Primary Borrower',
  'parties.role.coBorrower': 'Co-Borrower',
  'parties.role.sponsor': 'Sponsor',
  'parties.role.guarantor': 'Guarantor',
  'parties.ownershipInterest': 'Ownership Interest',
  'parties.guaranteeType': 'Guarantee Type',

  // ============================================================================
  // CONSUMER PORTAL (Issue #992)
  // ============================================================================

  // Loan Application Page
  'consumer.loan.tellUsAboutYourself': 'Tell us about yourself',
  'consumer.loan.contactInfoDescription': "We'll use this information to contact you about your loan application.",
  'consumer.loan.loanAmount': 'Loan Amount',
  'consumer.loan.purchasePrice': 'Purchase Price (if applicable)',
  'consumer.loan.propertyAddress': 'Property Address (if known)',
  'consumer.loan.streetAddress': 'Street address',
  'consumer.loan.city': 'City',
  'consumer.loan.state': 'State',
  'consumer.loan.zipCode': 'ZIP Code',
  'consumer.loan.loanPurpose': 'Loan Purpose',
  'consumer.loan.propertyAndLoanDetails': 'Property & Loan Details',
  'consumer.loan.propertyAndLoanDescription': "Tell us about the property and loan you're looking for.",
  'consumer.loan.createYourAccount': 'Create Your Account',
  'consumer.loan.createAccountDescription':
    'To continue with your application and protect your sensitive information, please create an account or sign in.',
  'consumer.loan.createAccount': 'Create Account',
  'consumer.loan.signIn': 'Sign In',
  'consumer.loan.infoSecure': 'Your information is secure and will be saved automatically.',
  'consumer.loan.financialInformation': 'Financial Information',
  'consumer.loan.financialDescription': 'This information helps us find the best loan options for you.',
  'consumer.loan.annualIncome': 'Annual Income',
  'consumer.loan.employmentStatus': 'Employment Status',
  'consumer.loan.creditScoreRange': 'Credit Score Range',
  'consumer.loan.creditScore.excellent': 'Excellent (740+)',
  'consumer.loan.creditScore.good': 'Good (700-739)',
  'consumer.loan.creditScore.fair': 'Fair (660-699)',
  'consumer.loan.creditScore.belowFair': 'Below Fair (Below 660)',
  'consumer.loan.existingDebt': 'Existing Debt (monthly)',
  'consumer.loan.existingDebtHelper': 'Include mortgage, car loans, credit cards, etc.',
  'consumer.loan.otherProperties': 'Do you own other investment properties?',
  'consumer.loan.reviewSignDisclosures': 'Review & Sign Disclosures',
  'consumer.loan.reviewSignDescription':
    'Please review and sign the following required disclosures to complete your application.',
  'consumer.loan.required': 'Required',
  'consumer.loan.signed': 'Signed',
  'consumer.loan.alreadySigned': 'Already signed',
  'consumer.loan.reviewAndSign': 'Review & Sign',
  'consumer.loan.documentsSignedProgress': '{{signed}} of {{total}} required documents signed',
  'consumer.loan.allDocumentsSigned': 'All required documents have been signed! You can now submit your application.',
  'consumer.loan.signDocumentsNotice':
    'Please sign all required documents to continue. By signing these documents, you authorize us to process your loan application.',
  'consumer.loan.title': 'Loan Application',
  'consumer.loan.subtitle': 'Get pre-qualified in minutes. No impact to your credit score.',
  'consumer.loan.submitApplication': 'Submit Application',
  'consumer.loan.infoEncrypted': 'Your information is encrypted and secure',
  'consumer.loan.signDocument': 'Sign Document',
  'consumer.loan.reviewDocumentSubtitle': 'Review the document and sign below',
  'consumer.loan.loadingDocument': 'Loading document...',
  'consumer.loan.unableToLoad': 'Unable to load the signing document. Please try again or contact support.',
  'consumer.loan.loadingApplication': 'Loading application...',
  'consumer.loan.referredBy': 'Referred by: {{partner}}',
  'consumer.loan.referralExpired': 'Referral link has expired',
  'consumer.loan.invalidReferral': 'Invalid referral code',

  // Issue #1274 — Dynamic disclosure loading and batch envelope creation
  'consumer.loan.disclosureDefaultDescription': 'Review the disclosure content and sign to continue.',
  'consumer.loan.preparingDisclosures': 'Preparing disclosure documents for signing...',
  'consumer.loan.preparing': 'Preparing',
  'consumer.loan.disclosureLoadError':
    "We couldn't load the required disclosures. Please refresh the page or contact support.",
  'consumer.loan.noDisclosuresConfigured':
    'No disclosure documents are currently required for this business. You may continue to submit your application.',
  'consumer.loan.disclosureEnvelopeError':
    "We couldn't prepare your disclosure documents. Please try again or contact support.",
  'consumer.loan.disclosureEnvelopePartialFailure':
    '{{failed}} of {{total}} disclosures could not be prepared. You may retry or contact support.',
  'consumer.loan.disclosuresStillLoading': 'Disclosures are still loading. Please wait a moment and try again.',
  'consumer.loan.pleaseSignDocuments': 'Please sign the following documents: {{documents}}',
  'consumer.loan.submitApplicationError': 'Failed to submit application. Please try again.',

  // Loan Application Steps
  'consumer.loan.step.basicInfo': 'Basic Info',
  'consumer.loan.step.property': 'Property',
  'consumer.loan.step.createAccount': 'Create Account',
  'consumer.loan.step.financials': 'Financials',
  'consumer.loan.step.signAndSubmit': 'Sign & Submit',

  // Onboarding Review Step
  'consumer.onboarding.businessInformation': 'Business Information',
  'consumer.onboarding.businessType': 'Business Type',
  'consumer.onboarding.businessName': 'Business Name',
  'consumer.onboarding.stateOfFormation': 'State of Formation',
  'consumer.onboarding.yearsInBusiness': 'Years in Business',
  'consumer.onboarding.loanPurpose': 'Loan Purpose',
  'consumer.onboarding.estimatedLoanAmount': 'Estimated Loan Amount',
  'consumer.onboarding.creditScoreRange': 'Credit Score Range',
  'consumer.onboarding.investmentExperience': 'Investment Experience',
  'consumer.onboarding.communicationPreferences': 'Communication Preferences',
  'consumer.onboarding.preferredContactMethod': 'Preferred Contact Method',
  'consumer.onboarding.timezone': 'Timezone',
  'consumer.onboarding.bestTimeToContact': 'Best Time to Contact',
  'consumer.onboarding.marketingUpdates': 'Marketing Updates',
  'consumer.onboarding.editSection': 'Edit this section',
  'consumer.onboarding.reviewDescription': 'Please review your information below. Click the edit icon to make changes.',
  'consumer.onboarding.allSet': 'You\'re all set! Click "Complete & Submit Deal" to proceed to your loan application.',
  'consumer.onboarding.optedIn': 'Opted In',
  'consumer.onboarding.optedOut': 'Opted Out',
  'consumer.onboarding.notSpecified': 'Not specified',
  'consumer.onboarding.noPriorExperience': 'No prior experience',
  'consumer.onboarding.yearsLabel': '{{years}} years',
  'consumer.onboarding.propertiesExperience': 'Yes - {{count}} properties',
  'consumer.onboarding.loanAndPropertyDetails': 'Loan & Property Details',

  // Proposals
  'consumer.proposals.noProposals': 'No proposals found',
  'consumer.proposals.noProposalsDescription':
    "Proposals sent to you will appear here. You'll be notified when you receive a new proposal.",
  'consumer.proposals.timeline': 'Timeline',
  'consumer.proposals.received': 'Received',
  'consumer.proposals.viewed': 'Viewed',
  'consumer.proposals.expires': 'Expires',
  'consumer.proposals.totalProposals': 'Total Proposals',
  'consumer.proposals.pendingReview': 'Pending Review',
  'consumer.proposals.awaitingResponse': 'Awaiting your response',
  'consumer.proposals.allProposals': 'All Proposals',
  'consumer.proposals.proposalDetails': 'Proposal details',
  'consumer.proposals.accepted': 'Accepted',
  'consumer.proposals.rejected': 'Rejected',
  'consumer.proposals.proposalContent': 'Proposal Content',
  'consumer.proposals.noContent': 'No content available for this proposal.',
  'consumer.proposals.relatedDeal': 'Related Deal',
  'consumer.proposals.firstViewed': 'First Viewed',
  'consumer.proposals.yourResponse': 'Your Response',
  'consumer.proposals.youAccepted': 'You accepted this proposal',
  'consumer.proposals.youRejected': 'You rejected this proposal',
  'consumer.proposals.acceptProposal': 'Accept Proposal',
  'consumer.proposals.rejectProposal': 'Reject Proposal',
  'consumer.proposals.confirmAccept': 'Are you sure you want to accept this proposal? This action cannot be undone.',
  'consumer.proposals.confirmReject':
    'Are you sure you want to reject this proposal? You can optionally provide a reason below.',
  'consumer.proposals.reasonOptional': 'Reason (optional)',
  'consumer.proposals.failedToRespond': 'Failed to submit response. Please try again.',
  'consumer.proposals.proposal': 'Proposal',
  // Issue #1083: Term sheet detail labels
  'consumer.proposals.borrowerInfo': 'Borrower Information',
  'consumer.proposals.propertyInfo': 'Property Information',
  'consumer.proposals.loanTerms': 'Loan Terms',
  'consumer.proposals.loanAmount': 'Loan Amount',
  'consumer.proposals.interestRate': 'Interest Rate',
  'consumer.proposals.term': 'Term',
  'consumer.proposals.monthlyPayment': 'Monthly Payment',
  'consumer.proposals.downloadPdf': 'Download PDF',
  'consumer.proposals.termSheetLabel': 'Term Sheet',
  'consumer.proposals.untitledProposal': 'Untitled Proposal',

  // Entity Records
  'consumer.entity.fields': 'Fields',
  'consumer.entity.recordId': 'Record ID',
  'consumer.entity.entityDetails': 'Entity details',
  'consumer.entity.noRecords': 'No {{entities}} found.',
  'consumer.entity.createFirst': 'Create your first {{entity}}',
  'consumer.entity.newEntity': 'New {{entity}}',
  'consumer.entity.recordDetails': '{{entity}} Details',
  'consumer.entity.noDocuments': 'No documents attached to this record.',
  'consumer.entity.activityTimeline': 'Activity Timeline',
  'consumer.entity.entityTypeNotSpecified': 'Entity type not specified.',
  'consumer.entity.failedToLoadRecords': 'Failed to load records. Please try again later.',
  'consumer.entity.failedToLoadRecord': 'Failed to load record. Please try again later.',
  'consumer.entity.failedToLoadForm': 'Failed to load form. Please try again later.',
  'consumer.entity.newRecord': 'New {{entity}}',
  'consumer.entity.editRecord': 'Edit {{entity}}',
  'consumer.entity.draftSavedAt': 'Draft saved at {{time}}',
  'consumer.entity.saveDraft': 'Save Draft',
  'consumer.entity.savingDraft': 'Saving...',
  'consumer.entity.saveChanges': 'Save Changes',
  'consumer.entity.lastUpdated': 'Last updated {{date}}',

  // Documents
  'consumer.documents.preview': 'Preview',
  'consumer.documents.download': 'Download',
  'consumer.documents.delete': 'Delete',

  // Auth
  'consumer.auth.tryAgain': 'Please try logging in again.',
  'consumer.auth.completingAuth': 'Completing authentication...',

  // Search
  'consumer.search.openSearch': 'Open search',
  'consumer.search.placeholder': 'Search...',

  // ============================================================================
  // WEBSITE PORTAL (Issue #992 Part C)
  // ============================================================================

  // Error / state pages
  'website.errors.siteNotFound': 'Site Not Found',
  'website.errors.siteNotFoundDescription': 'Unable to resolve this domain to a business.',
  'website.errors.unableToLoadSite': 'Unable to Load Site',
  'website.errors.unableToLoadSiteDescription':
    'We are experiencing a temporary issue loading this site. Please try again shortly.',
  'website.errors.comingSoon': 'Coming Soon',
  'website.errors.comingSoonDescription': '{{companyName}} is setting up their website. Check back soon.',
  'website.errors.somethingWentWrong': 'Something went wrong',
  'website.errors.temporaryIssue': "We're experiencing a temporary issue. Please try again shortly.",
  'website.errors.tryAgain': 'Try again',
  'website.errors.unableToLoadPage': 'Unable to Load Page',
  'website.errors.unableToLoadPageDescription': 'We are experiencing a temporary issue. Please try again shortly.',

  // ============================================================================
  // Website v4 dispatcher — opt-in rendering errors. Surfaced when a tenant
  // has flagged a page as `v4Page` in their CMS but is missing the brand
  // config or per-page data the v4 component needs. Strict white-label
  // invariant (CLAUDE.md §11d) — these messages must stay generic; no
  // tenant or platform brand names in copy.
  // ============================================================================
  'website.v4.errors.missingBrand': 'Brand not configured',
  'website.v4.errors.missingBrandDescription':
    'This site is configured to use v4 page rendering, but the brand palette has not been published yet. The site administrator can finish brand setup in the website CMS.',
  'website.v4.errors.missingPageData': 'Page content not configured',
  'website.v4.errors.missingPageDataDescription':
    'This page is configured to use v4 rendering, but the required content has not been published yet. The site administrator can finish page content setup in the website CMS.',
  'website.v4.errors.unknownPageType': 'Unsupported page type',
  'website.v4.errors.unknownPageTypeDescription':
    'This page is configured with an unrecognized v4 page type. The site administrator can correct the configuration in the website CMS.',

  // Calculator — Fix & Flip
  'website.calculator.fixFlip.dealInputs': 'Deal Inputs',
  'website.calculator.fixFlip.loanTerms': 'Loan Terms',
  'website.calculator.fixFlip.holdingAndExit': 'Holding & Exit',
  'website.calculator.fixFlip.purchasePrice': 'Purchase Price',
  'website.calculator.fixFlip.arv': 'After Repair Value (ARV)',
  'website.calculator.fixFlip.rehabBudget': 'Rehab Budget',
  'website.calculator.fixFlip.ltc': 'Loan-to-Cost (LTC)',
  'website.calculator.fixFlip.interestRate': 'Interest Rate',
  'website.calculator.fixFlip.originationPoints': 'Origination Points',
  'website.calculator.fixFlip.holdingPeriod': 'Holding Period',
  'website.calculator.fixFlip.monthlyHoldingCosts': 'Monthly Holding Costs',
  'website.calculator.fixFlip.sellingCommission': 'Selling Commission',
  'website.calculator.fixFlip.estimatedNetProfit': 'Estimated Net Profit',
  'website.calculator.fixFlip.roiOnCash': '{{roi}}% ROI on Cash Invested',
  'website.calculator.fixFlip.loanAmount': 'Loan Amount',
  'website.calculator.fixFlip.cashToClose': 'Cash to Close',
  'website.calculator.fixFlip.originationFee': 'Origination Fee',
  'website.calculator.fixFlip.monthlyPayment': 'Monthly Payment',
  'website.calculator.fixFlip.totalInterest': 'Total Interest',
  'website.calculator.fixFlip.totalHoldingCosts': 'Total Holding Costs',
  'website.calculator.fixFlip.totalProjectCost': 'Total Project Cost',
  'website.calculator.fixFlip.sellingCosts': 'Selling Costs',
  'website.calculator.fixFlip.applyNow': 'Get Prequalified',
  'website.calculator.fixFlip.months': '{{count}} months',

  // Calculator — DSCR
  'website.calculator.dscr.propertyDetails': 'Property Details',
  'website.calculator.dscr.loanTerms': 'Loan Terms',
  'website.calculator.dscr.monthlyExpenses': 'Monthly Expenses',
  'website.calculator.dscr.propertyValue': 'Property Value',
  'website.calculator.dscr.monthlyRent': 'Monthly Rent',
  'website.calculator.dscr.vacancyRate': 'Vacancy Rate',
  'website.calculator.dscr.interestRate': 'Interest Rate',
  'website.calculator.dscr.loanTerm': 'Loan Term',
  'website.calculator.dscr.ltv': 'Loan-to-Value (LTV)',
  'website.calculator.dscr.propertyTaxes': 'Property Taxes',
  'website.calculator.dscr.insurance': 'Insurance',
  'website.calculator.dscr.hoa': 'HOA',
  'website.calculator.dscr.debtServiceCoverageRatio': 'Debt Service Coverage Ratio',
  'website.calculator.dscr.statusStrong': 'Strong',
  'website.calculator.dscr.statusBorderline': 'Borderline',
  'website.calculator.dscr.statusDoesNotQualify': 'Does Not Qualify',
  'website.calculator.dscr.loanAmount': 'Loan Amount',
  'website.calculator.dscr.downPayment': 'Down Payment',
  'website.calculator.dscr.effectiveGrossIncome': 'Effective Gross Income',
  'website.calculator.dscr.netOperatingIncome': 'Net Operating Income',
  'website.calculator.dscr.monthlyPI': 'Monthly P&I',
  'website.calculator.dscr.monthlyCashFlow': 'Monthly Cash Flow',
  'website.calculator.dscr.cashOnCashReturn': 'Cash-on-Cash Return',

  // Calculator — Lead Capture
  'website.calculator.leadCapture.defaultHeadline': 'Like what you see? Get a personalized quote.',
  'website.calculator.leadCapture.subtitle': "We'll review your numbers and call you within 24 hours.",
  'website.calculator.leadCapture.successTitle': "We'll be in touch shortly!",
  'website.calculator.leadCapture.successMessage':
    'A lending specialist will review your numbers and reach out within 1 business day.',
  'website.calculator.leadCapture.name': 'Name',
  'website.calculator.leadCapture.email': 'Email',
  'website.calculator.leadCapture.phone': 'Phone',
  'website.calculator.leadCapture.sending': 'Sending...',
  'website.calculator.leadCapture.getMyQuote': 'Get My Quote',
  'website.calculator.leadCapture.submitError': 'Failed to submit. Please try again.',

  // SMS + Voice / TCPA opt-in disclosure (TCR A2P SMS registration AND
  // outbound dialer-platform approval). Rendered identically on every
  // lead-capture form (/contact, /apply, calculator, partner application,
  // lead capture). The {{companyName}} interpolation receives the site's
  // branding.companyName.
  //
  // Twilio (2026-05-13 rejection feedback) requires:
  //   • SMS consent and voice/call consent as SEPARATE, unchecked checkboxes
  //   • Opt-in language that matches the registered campaign use case
  //     (Customer Care / transactional — NOT marketing)
  //   • No bundling of marketing / promotional opt-in with transactional
  //   • Identify the sender, message + call type, frequency, opt-out
  //     (HELP / STOP), cost notice (Msg & data rates), link to Terms +
  //     Privacy Policy, and "consent is not a condition of any purchase
  //     or loan"
  //
  // Promotional / marketing SMS is intentionally NOT offered here. Tenants
  // who want marketing SMS must register a separate Marketing-use-case
  // campaign with their SMS provider and add a dedicated promotional opt-
  // in surface — bundling marketing into a transactional opt-in is the
  // most common carrier-rejection cause.
  //
  // TCR requires explicit message-frequency language ("message frequency
  // varies") — do NOT remove it without an alternate frequency phrase,
  // or the campaign will be rejected.
  // See https://help.close.com/docs/a2p-sms-registration-rejected.
  'website.smsOptInDisclosure.body':
    '{{companyName}} contacts you only about the inquiry, application, or loan you start with us. SMS and phone calls are SEPARATE opt-ins below — check each one you want. Message frequency varies based on your inquiry or loan. Msg & data rates may apply; standard call rates may apply. Reply STOP to opt out of SMS, HELP for help. Consent is not a condition of any purchase or loan. See our',
  'website.smsOptInDisclosure.terms': 'Terms of Service',
  'website.smsOptInDisclosure.privacy': 'Privacy Policy',
  'website.smsOptInDisclosure.and': 'and',
  // Active opt-in checkbox labels. Both unchecked by default.
  // - SMS consent (first checkbox) is OPTIONAL by default. Manually-typed
  //   one-off transactional texts triggered by user actions don't require
  //   prior express written consent under TCPA, so the platform stopped
  //   gating submit on this box. Tenants whose downstream comms include
  //   autodialed / prerecorded SMS reminders (which DO need prior express
  //   written consent) opt back into the strict gate by passing
  //   `useSmsOptInConsent({ smsConsentRequired: true })`.
  // - Voice consent (second checkbox) is OPTIONAL — covers autodialed and
  //   prerecorded-voice calls only (manually-dialed live-agent calls do
  //   not need it under TCPA). Forms must NEVER gate submit on this
  //   checkbox.
  'website.smsOptInDisclosure.consentSms':
    'I agree to receive transactional SMS messages from {{companyName}} about my inquiry, application, or loan — for example, status updates, document requests, scheduling, and reminders. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.',
  'website.smsOptInDisclosure.consentVoice':
    'I agree to receive phone calls from {{companyName}} or its agents about my inquiry, application, or loan, including calls placed using an autodialer or an artificial or prerecorded voice. Consent is not a condition of any purchase or loan. Standard call rates may apply. I may opt out at any time by telling the caller "do not call."',
  'website.smsOptInDisclosure.requiredError': 'Required to submit — please check this box to continue.',

  // Calculator — Sizer (Issue #1344 — engine-backed public calculator)
  'website.calculator.sizer.heading': 'Loan Sizer',
  'website.calculator.sizer.subheading':
    'See what deal size our underwriting guidelines support. Same engine our originators use internally.',
  'website.calculator.sizer.loanTypeLabel': 'Loan Product',
  'website.calculator.sizer.loanType.FIX_FLIP': 'Fix & Flip',
  'website.calculator.sizer.loanType.GROUND_UP': 'Ground-Up Construction',
  'website.calculator.sizer.loanType.BRIDGE': 'Bridge',
  'website.calculator.sizer.dealInputs': 'Deal Inputs',
  'website.calculator.sizer.loanTerms': 'Loan Terms',
  'website.calculator.sizer.borrower': 'Borrower',
  'website.calculator.sizer.purchasePrice': 'Purchase Price',
  'website.calculator.sizer.constructionBudget': 'Construction Budget',
  'website.calculator.sizer.arv': 'After Repair Value (ARV)',
  'website.calculator.sizer.costSpentToDate': 'Cost Spent to Date',
  'website.calculator.sizer.remainingCostToComplete': 'Remaining Cost to Complete',
  'website.calculator.sizer.loanPayoff': 'Existing Loan Payoff',
  'website.calculator.sizer.requestedLoan': 'Requested Loan Amount',
  'website.calculator.sizer.fico': 'FICO Score',
  'website.calculator.sizer.foreignNational': 'Foreign National',
  'website.calculator.sizer.rate': 'Interest Rate',
  'website.calculator.sizer.points': 'Origination Points',
  'website.calculator.sizer.targetMaxLoan': 'Target Max Loan',
  'website.calculator.sizer.variance': 'Variance vs. Requested',
  'website.calculator.sizer.leverageMetrics': 'Leverage Metrics',
  'website.calculator.sizer.cashMechanics': 'Cash Mechanics',
  'website.calculator.sizer.cashToClose': 'Cash to Close',
  'website.calculator.sizer.proceedsAtClosing': 'Proceeds at Closing',
  'website.calculator.sizer.constructionHoldback': 'Construction Holdback',
  'website.calculator.sizer.closingProceeds': 'Closing Proceeds',
  'website.calculator.sizer.considerations': 'Considerations',
  'website.calculator.sizer.upsizeOptions': 'Ways to Unlock More',
  'website.calculator.sizer.upsizeHelp': '+{{amount}} with this change',
  'website.calculator.sizer.openQuestions': 'Missing Inputs',
  'website.calculator.sizer.statusWithinTarget': 'Within target',
  'website.calculator.sizer.statusNeedsReview': 'Needs a closer look',
  'website.calculator.sizer.flagSeverityInfo': 'Info',
  'website.calculator.sizer.flagSeverityWarn': 'Heads up',
  'website.calculator.sizer.calculating': 'Calculating…',
  'website.calculator.sizer.retry': 'Retry',
  'website.calculator.sizer.calculationError':
    'We could not size this deal right now. Check the inputs and try again, or contact us and we will size it for you.',
  'website.calculator.sizer.disclaimer':
    'Preliminary internal sizing only; not final credit approval. Final terms are determined during underwriting.',
  'website.calculator.sizer.applyNow': 'Apply for This Deal',

  // Calculator widget (generic)
  'website.calculator.widget.loanAmount': 'Loan Amount',
  'website.calculator.widget.interestRate': 'Interest Rate',
  'website.calculator.widget.term': 'Term',
  'website.calculator.widget.termMonths': '{{count}} months',
  'website.calculator.widget.estimatedMonthlyPayment': 'Estimated Monthly Payment',
  'website.calculator.widget.totalInterest': 'Total Interest',
  'website.calculator.widget.totalPayment': 'Total Payment',
  'website.calculator.widget.applyNow': 'Apply Now',

  // Search / filter
  'website.search.searchTerms': 'Search terms...',
  'website.search.searchQuestions': 'Search questions...',
  'website.search.noTermsFound': 'No terms found matching "{{query}}"',
  'website.search.noQuestionsMatch': 'No questions match your search.',

  // Navigation
  'website.navigation.openMenu': 'Open menu',
  'website.navigation.closeMenu': 'Close menu',
  'website.navigation.lightMode': 'Light Mode',
  'website.navigation.darkMode': 'Dark Mode',
  'website.navigation.switchToLightMode': 'Switch to light mode',
  'website.navigation.switchToDarkMode': 'Switch to dark mode',
  'website.navigation.login': 'Sign In',
  'website.navigation.borrowerLogin': 'Borrower',
  'website.navigation.partnerPortal': 'Partners',

  // Announcement bar
  'website.announcementBar.dismiss': 'Dismiss announcement',
  'website.announcementBar.learnMore': 'Learn more',

  // Footer
  'website.footer.equalHousingLender': 'Equal Housing Lender',

  // Not found states
  'website.notFound.posts': 'No posts available yet.',
  'website.notFound.teamMember': 'Team member not found.',
  'website.notFound.post': 'Post not found.',
  'website.notFound.program': 'Program not found.',

  // Content labels
  'website.content.readMore': 'Read More',
  'website.content.backToBlog': 'Back to Blog',
  'website.content.contributingAuthor': 'Contributing author',
  'website.content.relatedArticles': 'Related Articles',
  'website.content.moreFundedDeals': 'More Funded Deals',
  'website.content.daysToClose': 'Days to Close',
  'website.content.closeDate': 'Close Date',
  'website.content.keyFeatures': 'Key Features',
  'website.content.qualificationRequirements': 'Qualification Requirements',
  'website.content.qualificationContact':
    'Contact us for detailed qualification requirements for the {{programName}} program.',
  'website.content.programOverview': 'Program Overview',
  'website.content.rateRange': 'Rate Range',
  'website.content.maxLtv': 'Max LTV',
  'website.content.loanRange': 'Loan Range',
  'website.content.terms': 'Terms',
  'website.content.getStarted': 'Get Started',
  'website.content.relatedPrograms': 'Related Programs',
  'website.content.contact': 'Contact',
  'website.content.linkedInProfile': 'LinkedIn Profile',

  // ============================================================================
  // EMBEDDED INTAKE WIDGET (36-website-portal /embed routes - B6, Issue #1821)
  // Iframe-embeddable intake forms. Brand-agnostic (host site provides its
  // own brand chrome). Light + dark themes; page-by-page + infinite-scroll modes.
  // ============================================================================

  // Shared embed copy
  'website.embed.poweredBy': 'Powered by Fiops',
  'website.embed.security.soc2': 'SOC 2 Type II',
  'website.embed.security.encryption': 'Bank-grade encryption',
  'website.embed.security.noCreditPull': 'No credit pull',
  'website.embed.security.estimatedTime': 'Takes about 2 minutes',
  'website.embed.autoSaved': 'Auto-saved',
  'website.embed.saveAndExit': 'Save & exit',
  'website.embed.saveForLater': 'Save for later',
  'website.embed.back': 'Back',
  'website.embed.continue': 'Continue',
  'website.embed.submit': 'Submit for review',
  'website.embed.edit': 'Edit',
  'website.embed.completed': 'Completed',
  'website.embed.required': 'Required',
  'website.embed.optional': 'Optional',
  'website.embed.other': 'Other',
  'website.embed.otherSpecify': 'Please specify',
  'website.embed.bestFit': 'BEST FIT',
  'website.embed.selectPlaceholder': 'Select',

  // Errors
  'website.embed.error.businessIdMissing':
    'Embed misconfigured — host site must be served from a registered Fiops domain.',
  'website.embed.error.submitFailed': 'Submission failed. Please try again or contact support.',
  'website.embed.error.required': 'This field is required.',

  // Step indicator
  'website.embed.step.label': 'Step {{current}} of {{total}}',

  // Intake landing
  'website.embed.intake.heading': 'Pre-Qualification Application',
  'website.embed.intake.subheading': 'Choose one to start — you can always change it before submitting.',
  'website.embed.intake.loanType.title': 'What kind of financing are you looking for?',
  'website.embed.intake.loanType.fixAndFlip': 'Fix & Flip',
  'website.embed.intake.loanType.fixAndFlip.sub': 'Purchase, renovate, and resell residential properties (1–4 units)',
  'website.embed.intake.loanType.construction': 'Ground-Up Construction',
  'website.embed.intake.loanType.construction.sub': 'Build new 1–4 unit residential properties from scratch',
  'website.embed.intake.loanType.bridge': 'Bridge Loan',
  'website.embed.intake.loanType.bridge.sub':
    'Short-term financing for acquisitions, refinances, or time-sensitive deals',
  'website.embed.intake.loanType.dscr': 'DSCR / Rental',
  'website.embed.intake.loanType.dscr.sub':
    'Investment property loans based on rental income — no income docs required',
  'website.embed.intake.loanType.other': 'Other',

  // Intake sections
  'website.embed.intake.section.contact': 'Contact Information',
  'website.embed.intake.section.contact.sub': 'Tell us how to reach you and we will have an advisor contact you',
  'website.embed.intake.section.property': 'Property Details',
  'website.embed.intake.section.property.sub': 'Describe the subject property',
  'website.embed.intake.section.deal': 'Deal Financials',
  'website.embed.intake.section.deal.sub': 'Provide the financial details of the deal',
  'website.embed.intake.section.borrower': 'Borrower Information',
  'website.embed.intake.section.borrower.sub': 'Key financial details for deal sizing',
  'website.embed.intake.section.entity': 'Entity & Experience',
  'website.embed.intake.section.entity.sub': 'Tell us about your entity and experience',
  'website.embed.intake.section.review': 'Review & Submit',
  'website.embed.intake.section.review.sub': 'One last look — does this match your deal?',

  // Contact fields
  'website.embed.intake.contact.firstName': 'First name',
  'website.embed.intake.contact.lastName': 'Last name',
  'website.embed.intake.contact.email': 'Email',
  'website.embed.intake.contact.phone': 'Phone',

  // Property fields
  'website.embed.intake.property.address': 'Property address',
  'website.embed.intake.property.type': 'Property type',
  'website.embed.intake.property.type.sfr': 'Single Family (1 unit)',
  'website.embed.intake.property.type.multi': 'Multi-Family (2–4 units)',
  'website.embed.intake.property.type.condo': 'Condo / Townhome',
  'website.embed.intake.property.type.other': 'Other',
  'website.embed.intake.property.state': 'State',

  // Deal fields
  'website.embed.intake.deal.purchasePrice': 'Purchase price',
  'website.embed.intake.deal.rehabBudget': 'Rehab budget',
  'website.embed.intake.deal.rehabBudget.hint': 'All-in including soft costs',
  'website.embed.intake.deal.arv': 'After-repair value (ARV)',
  'website.embed.intake.deal.asIsValue': 'As-is value (today)',
  'website.embed.intake.deal.exitStrategy': 'Exit strategy',
  'website.embed.intake.deal.exitStrategy.sale': 'Sale within 12 months',
  'website.embed.intake.deal.exitStrategy.refi': 'Refinance to long-term',
  'website.embed.intake.deal.exitStrategy.hold': 'Hold as rental',
  'website.embed.intake.deal.exitStrategy.other': 'Other',

  // Borrower fields
  'website.embed.intake.borrower.entityName': 'Entity or principal name',
  'website.embed.intake.borrower.entityName.hint': 'The borrowing entity name or individual borrower',
  'website.embed.intake.borrower.creditScore': 'Credit score range',
  'website.embed.intake.borrower.creditScore.below620': 'Below 620',
  'website.embed.intake.borrower.creditScore.620to659': '620–659',
  'website.embed.intake.borrower.creditScore.660to699': '660–699',
  'website.embed.intake.borrower.creditScore.700to739': '700–739',
  'website.embed.intake.borrower.creditScore.740plus': '740+',
  'website.embed.intake.borrower.liquidity': 'Borrower liquidity',
  'website.embed.intake.borrower.liquidity.hint': 'Cash + liquid assets (checking, savings, stocks)',
  'website.embed.intake.borrower.netWorth': 'Borrower net worth',
  'website.embed.intake.borrower.priorExits': 'Exits or refis in past 36 months',
  'website.embed.intake.borrower.priorExits.zero': 'None',
  'website.embed.intake.borrower.priorExits.oneToTwo': '1–2 transactions',
  'website.embed.intake.borrower.priorExits.threeToFour': '3–4 transactions',
  'website.embed.intake.borrower.priorExits.fivePlus': '5+ transactions',

  // Sidebar nav (infinite-scroll mode)
  'website.embed.intake.sectionsCount': '{{done}} of {{total}} done',
  'website.embed.intake.sectionsToGo': '{{remaining}} sections to go',

  // Review consent
  'website.embed.intake.review.consent':
    'I authorize this lender to review my submission and contact me about my application. I understand this is a soft pre-qualification — no credit pull, no commitment.',
  'website.embed.intake.review.heading': 'One last look — does this match your deal?',
  'website.embed.intake.review.subheading':
    'Click any section to edit. Once submitted, an underwriter will review within 24 hours.',

  // Success
  'website.embed.intake.success.heading': 'Application received',
  'website.embed.intake.success.subheading':
    'We have sent a confirmation to your email. An underwriter will review your submission and reach out within 24 hours.',
  'website.embed.intake.success.reference': 'YOUR REFERENCE',
  'website.embed.intake.success.submitted': 'SUBMITTED',
  'website.embed.intake.success.nextStep': 'NEXT STEP',
  'website.embed.intake.success.nextStep.underwriter': 'Underwriter review',
  'website.embed.intake.success.whatHappensNext': 'WHAT HAPPENS NEXT',
  'website.embed.intake.success.step1': 'Application pre-screened',
  'website.embed.intake.success.step1.sub': 'Within 30 seconds · already done',
  'website.embed.intake.success.step2': 'Underwriter reviews your deal',
  'website.embed.intake.success.step2.sub': 'Within 24 hours',
  'website.embed.intake.success.step3': 'You get a term sheet or a call',
  'website.embed.intake.success.step3.sub': 'If pre-qualified, we move fast',
  'website.embed.intake.success.step4': 'Close in as few as 7 days',
  'website.embed.intake.success.step4.sub': 'Once docs and appraisal are in',

  // Partner Apply
  'website.embed.partner.heading': 'Apply to Become a Partner',
  'website.embed.partner.subheading': 'Reviewed within 48 hours.',
  'website.embed.partner.step1.title': 'Company Information',
  'website.embed.partner.step1.sub': 'Tell us about your company.',
  'website.embed.partner.step2.title': 'Contact Information',
  'website.embed.partner.step2.sub': 'How should we reach you?',
  'website.embed.partner.step3.title': 'Partnership Details',
  'website.embed.partner.step3.sub': 'Tell us about the partnership you are seeking.',
  'website.embed.partner.company.name': 'Company name',
  'website.embed.partner.company.type': 'Business type',
  'website.embed.partner.company.type.broker': 'Mortgage broker',
  'website.embed.partner.company.type.realtor': 'Real estate agency',
  'website.embed.partner.company.type.advisor': 'Financial advisor',
  'website.embed.partner.company.type.other': 'Other',
  'website.embed.partner.company.years': 'Years in business',
  'website.embed.partner.company.years.lessThan1': 'Less than 1 year',
  'website.embed.partner.company.years.oneToThree': '1–3 years',
  'website.embed.partner.company.years.fourToTen': '4–10 years',
  'website.embed.partner.company.years.tenPlus': '10+ years',
  'website.embed.partner.company.statesLicensed': 'States licensed in',
  'website.embed.partner.company.statesLicensed.hint': 'Comma-separated list of state abbreviations',
  'website.embed.partner.company.originations': 'Annual originations',
  'website.embed.partner.company.originations.hint': 'Best estimate — for product matching only',
  'website.embed.partner.company.products': 'Loan products you originate',
  'website.embed.partner.company.products.bridge': 'Bridge / Fix & Flip',
  'website.embed.partner.company.products.dscr': 'DSCR Rental',
  'website.embed.partner.company.products.construction': 'Construction',
  'website.embed.partner.company.products.commercial': 'Commercial',
  'website.embed.partner.company.products.other': 'Other',
  'website.embed.partner.contact.firstName': 'First name',
  'website.embed.partner.contact.lastName': 'Last name',
  'website.embed.partner.contact.email': 'Work email',
  'website.embed.partner.contact.phone': 'Phone',
  'website.embed.partner.contact.title': 'Job title',
  'website.embed.partner.referral.expectedVolume': 'Expected monthly referrals',
  'website.embed.partner.referral.expectedVolume.lessThan5': 'Less than 5',
  'website.embed.partner.referral.expectedVolume.fiveToTwenty': '5–20',
  'website.embed.partner.referral.expectedVolume.twentyPlus': '20+',
  'website.embed.partner.referral.notes': 'Anything else we should know?',
  'website.embed.partner.referral.notes.placeholder':
    'Optional — tell us about your borrower base, focus markets, or goals.',
  'website.embed.partner.review.heading': 'Confirm your application',
  'website.embed.partner.review.subheading': 'A partner-success manager will reach out within 48 hours.',
  'website.embed.partner.benefits.heading': 'What you get',
  'website.embed.partner.benefits.referralLinks': 'Custom referral link per employee',
  'website.embed.partner.benefits.commissionTracking': 'Real-time commission tracking',
  'website.embed.partner.benefits.coBranded': 'Co-branded borrower experience',
  'website.embed.partner.benefits.directSupport': 'Direct support',

  // ============================================================================
  // FIOPS CORPORATE WEBSITE (37-fiops-website)
  // ============================================================================

  // Navigation
  'fiops.nav.howItWorks': 'How it works',
  'fiops.nav.platform': 'Platform',
  'fiops.nav.about': 'About',
  'fiops.nav.getStarted': 'Get started',

  // Hero
  'fiops.hero.line1': 'Operations Automation',
  'fiops.hero.line2': 'For Private Lending',
  'fiops.hero.subtitle': "Manual processes are costly. Your lending operations shouldn't be.",

  // Animated text
  'fiops.animatedText':
    'Fiops automates your borrower intake, document processing, and underwriting to eliminate manual work, reduce errors, and close loans faster.',

  // Problem section
  'fiops.problem.sectionTitle': 'Private Lending Is Complex.',
  'fiops.problem.sectionTitleAccent': 'Fiops Automates It.',
  'fiops.problem.heading1': 'Loan officers spend 60% of their time on manual document review',
  'fiops.problem.stat1': '60%',
  'fiops.problem.statLabel1': 'time wasted',
  'fiops.problem.heading2': '50%+ of deals stall in the underwriting pipeline',
  'fiops.problem.stat2': '50%',
  'fiops.problem.statLabel2': 'deals stalled',
  'fiops.problem.heading3': 'Draw management is trapped in spreadsheets and email',
  'fiops.problem.stat3': '3x',
  'fiops.problem.statLabel3': 'slower processing',

  // Solution section
  'fiops.solution.sectionLabel': 'Platform',
  'fiops.solution.sectionTitle': 'The complete lending OS',
  'fiops.solution.docIntel.title': 'AI Document Intelligence',
  'fiops.solution.docIntel.subtitle': 'Digitize and validate loan documents automatically.',
  'fiops.solution.docIntel.description':
    'Extract data from tax returns, appraisals, credit reports, and entity docs using AI — ensuring every field is captured and validated before human review.',
  'fiops.solution.underwriting.title': 'Automated Underwriting',
  'fiops.solution.underwriting.subtitle': 'Score deals against your criteria — automatically.',
  'fiops.solution.underwriting.description':
    'Pre-built decision models evaluate deals against your lending parameters. Surface exceptions and risk factors, not every document in every file.',
  'fiops.solution.drawMgmt.title': 'Draw Management',
  'fiops.solution.drawMgmt.subtitle': 'Track construction progress and manage disbursements.',
  'fiops.solution.drawMgmt.description':
    'Draw requests, inspection scheduling, lien waiver collection, budget tracking, and disbursement workflows — all automated and connected.',
  'fiops.solution.analytics.title': 'Portfolio Analytics',
  'fiops.solution.analytics.subtitle': 'Real-time insights across your entire book.',
  'fiops.solution.analytics.description':
    'Pipeline velocity, portfolio performance, borrower concentration, and geographic exposure — updated in real time with predictive trend analysis.',

  // Benefits section
  'fiops.benefits.title': 'Stop losing deals to manual processes',
  'fiops.benefits.subtitle':
    'Manual workflows, disconnected systems, and paper-heavy processes lead to lost deals, slow closings, and compliance risk.',
  'fiops.benefits.closeDeals.title': 'Close more deals',
  'fiops.benefits.closeDeals.description':
    'Eliminate bottlenecks in your pipeline so more deals reach the finish line.',
  'fiops.benefits.reduceTime.title': 'Reduce processing time',
  'fiops.benefits.reduceTime.description': 'Automate manual tasks that slow down underwriting and document review.',
  'fiops.benefits.cutCosts.title': 'Cut operational costs',
  'fiops.benefits.cutCosts.description': 'Do more with fewer people by automating repetitive back-office work.',
  'fiops.benefits.compliant.title': 'Stay compliant',
  'fiops.benefits.compliant.description': 'Automated regulatory checks and audit-ready documentation at every step.',

  // Audience section
  'fiops.audience.sectionLabel': 'Who we serve',
  'fiops.audience.sectionTitle': 'Built for private lenders',
  'fiops.audience.hardMoney': 'Hard Money Lenders',
  'fiops.audience.bridge': 'Bridge Lenders',
  'fiops.audience.construction': 'Construction Lenders',
  'fiops.audience.privateCredit': 'Private Credit Funds',
  'fiops.audience.fixFlip': 'Fix & Flip Lenders',
  'fiops.audience.dscr': 'DSCR Lenders',
  'fiops.audience.commercial': 'Commercial Lenders',
  'fiops.audience.realEstate': 'Real Estate Funds',
  'fiops.audience.correspondent': 'Correspondent Lenders',
  'fiops.audience.groundUp': 'Ground-Up Lenders',
  'fiops.audience.mezzanine': 'Mezzanine Lenders',
  'fiops.audience.warehouse': 'Warehouse Lenders',

  // CTA section
  'fiops.cta.title': "Let's connect",
  'fiops.cta.description':
    "Our team is here to listen, provide guidance, and explore how Fiops can transform your lending operations. Whether you're curious about our platform or ready to get started, we'd love to hear from you.",

  // Footer
  'fiops.footer.platform': 'Platform',
  'fiops.footer.about': 'About',
  'fiops.footer.contact': 'Contact',
  'fiops.footer.privacy': 'Privacy',
  'fiops.footer.copyright': '{{year}} \u00A9 Fiops Inc. All rights reserved.',

  // ============================================================================
  // CONSUMER PORTAL — Remaining i18n gaps
  // ============================================================================

  // Auth layout
  'consumer.layout.portalName': 'Consumer Portal',
  'consumer.layout.poweredBy': 'Powered by Fiops',
  'consumer.layout.defaultTitle': 'Borrower Portal',

  // Public header (white-labeled)
  'consumer.publicHeader.defaultName': 'Your Lender',
  'consumer.publicHeader.poweredBy': 'powered by Fiops',

  // Intake wizard
  'consumer.intake.title': "Let's Get Started",
  'consumer.intake.subtitle': 'Answer a few quick questions to help us find the best loan options for you.',
  'consumer.intake.step.businessType': 'Business Type',
  'consumer.intake.step.eligibility': 'Eligibility',
  'consumer.intake.step.preferences': 'Preferences',
  'consumer.intake.step.review': 'Review',
  'consumer.intake.continue': 'Continue',
  'consumer.intake.skipForNow': 'Skip for now',
  'consumer.intake.completeSubmit': 'Complete & Submit Deal',
  'consumer.intake.validation.selectBusinessType': 'Please select a business type',
  'consumer.intake.validation.selectLoanPurpose': 'Please select a loan purpose',
  'consumer.intake.validation.selectPropertyType': 'Please select a property type',
  'consumer.intake.validation.selectContactMethod': 'Please select a preferred contact method',

  // Record pages
  'consumer.record.notFound': 'Record not found. Missing entity or record identifier.',
  // Chat
  'consumer.chat.startConversation': 'Start a conversation',
  'consumer.chat.askAnything': 'Ask me anything about your documents',
  'consumer.chat.generating': 'Generating...',
  'consumer.chat.sources': 'Sources:',
  'consumer.chat.docIndex': 'Doc {{index}}',

  // ============================================================================
  // CUSTOM DOMAIN (Self-Service Custom Domain Feature)
  // ============================================================================
  'customDomain.title': 'Custom Domain',
  'customDomain.description': 'Configure a custom domain for your website and portals',
  'customDomain.setupButton': 'Set up custom domain',
  'customDomain.domainLabel': 'Domain name',
  'customDomain.domainPlaceholder': 'yourdomain.com',
  'customDomain.domainHint': 'Enter your apex domain without www or subdomains',
  'customDomain.domainInvalid': 'Enter a valid domain (e.g. yourdomain.com)',
  'customDomain.submitRequest': 'Submit request',
  'customDomain.verifyDns': 'Verify DNS',
  'customDomain.recheckDns': 'Recheck DNS',
  'customDomain.cancelRequest': 'Cancel request',
  'customDomain.removeDomain': 'Remove domain',
  'customDomain.removeConfirmTitle': 'Remove custom domain?',
  'customDomain.removeConfirmMessage':
    'This will remove all custom domain routing. Your site will no longer be accessible at this domain.',
  'customDomain.statusPendingVerification': 'Pending DNS verification',
  'customDomain.statusPendingApproval': 'Awaiting admin approval',
  'customDomain.statusProvisioning': 'Setting up SSL certificates...',
  'customDomain.statusActive': 'Active',
  'customDomain.statusFailed': 'Provisioning failed',
  'customDomain.statusSuspended': 'Suspended — DNS records missing',
  'customDomain.dnsInstructionsTitle': 'Add these DNS records',
  'customDomain.dnsInstructionsDescription':
    'Add the following records to your domain DNS settings. This typically takes a few minutes to propagate.',
  'customDomain.dnsRecordType': 'Type',
  'customDomain.dnsRecordHostname': 'Hostname',
  'customDomain.dnsRecordPurpose': 'Purpose',
  'customDomain.dnsRecordDefaultPath': 'Default Path',
  'customDomain.dnsRecordValue': 'Value',
  'customDomain.dnsPurposeVerification': 'Domain ownership verification',
  'customDomain.dnsPurposeWebsite': 'Public website',
  'customDomain.dnsPurposeWebsiteWww': 'Public website (www)',
  'customDomain.dnsPurposeBusinessPortal': 'Business portal (staff login)',
  'customDomain.dnsPurposeBorrowerPortal': 'Borrower portal (customer login)',
  'customDomain.dnsOptional': 'Optional',
  'customDomain.dnsLocalDevNotice':
    'You are running locally — DNS records and verification will not work. Deploy to staging or production to configure custom domains.',
  'customDomain.dnsCopied': 'Copied!',
  'customDomain.tokenExpiresAt': 'Verification token expires: {{date}}',
  'customDomain.verificationPassed': 'Verified',
  'customDomain.verificationFailed': 'Not found',
  'customDomain.allRecordsVerified': 'All DNS records verified',
  'customDomain.someRecordsFailed': '{{count}} record(s) not yet verified',
  'customDomain.adminTitle': 'Custom Domains',
  'customDomain.adminDescription': 'Manage customer domain requests',
  'customDomain.approveButton': 'Approve',
  'customDomain.rejectButton': 'Reject',
  'customDomain.retryButton': 'Retry provisioning',
  'customDomain.suspendButton': 'Suspend',
  'customDomain.removeButton': 'Remove',
  'customDomain.rejectReasonLabel': 'Rejection reason',
  'customDomain.rejectReasonPlaceholder': 'Explain why this domain request is being rejected',
  'customDomain.columnBusinessName': 'Business',
  'customDomain.columnDomain': 'Domain',
  'customDomain.columnStatus': 'Status',
  'customDomain.columnRequestedDate': 'Requested',
  'customDomain.columnRequestedBy': 'Requested by',
  'customDomain.filterAll': 'All',
  'customDomain.filterPendingApproval': 'Pending Approval',
  'customDomain.filterActive': 'Active',
  'customDomain.filterFailed': 'Failed',
  'customDomain.filterSuspended': 'Suspended',
  'customDomain.sectionDomainDetails': 'Domain Details',
  'customDomain.sectionDnsVerification': 'DNS Verification',
  'customDomain.sectionApprovalInfo': 'Approval Info',
  'customDomain.sectionSslInfo': 'SSL Info',
  'customDomain.labelDomain': 'Domain',
  'customDomain.labelStatus': 'Status',
  'customDomain.labelRequestedBy': 'Requested by',
  'customDomain.labelRequestedDate': 'Requested date',
  'customDomain.labelTxtVerified': 'TXT record',
  'customDomain.labelARecordVerified': 'A record',
  'customDomain.labelApprovedBy': 'Approved by',
  'customDomain.labelApprovedAt': 'Approved at',
  'customDomain.labelSslCertName': 'SSL certificate',
  'customDomain.labelFailureReason': 'Failure reason',
  'customDomain.emptyTitle': 'No domain requests',
  'customDomain.emptyDescription': 'Domain requests from businesses will appear here for review.',
  'customDomain.suspendReasonLabel': 'Suspension reason',
  'customDomain.suspendReasonPlaceholder': 'Explain why this domain is being suspended',
  'customDomain.removeConfirmation':
    'Are you sure you want to permanently remove the custom domain {{domain}}? This will delete all DNS mappings and SSL certificates.',
  'customDomain.portalMapWebsite': 'Website',
  'customDomain.portalMapWebsiteWww': 'Website (www)',
  'customDomain.portalMapBusiness': 'Business Portal',
  'customDomain.portalMapBorrower': 'Borrower Portal',
  'customDomain.subdomainPreviewTitle': 'This will create the following domains:',
  'customDomain.subdomainPreviewNote':
    'You will need to add A records for all four subdomains pointing to our load balancer.',
  'customDomain.notConfigured': 'No custom domain configured',
  'customDomain.retryStarted': 'Retry started — provisioning will resume shortly',

  // Document Checklists (Control Center - Checklist Template Management)
  'docChecklists.searchPlaceholder': 'Search checklist templates...',
  'docChecklists.createTemplate': 'Create Checklist Template',
  'docChecklists.allLoanTypes': 'All Loan Types',
  'docChecklists.required': 'Required',
  'docChecklists.optional': 'Optional',
  'docChecklists.items': 'Checklist Items',
  'docChecklists.addItem': 'Add Item',
  'docChecklists.noItems': 'No items yet. Add document requirements to this checklist.',
  'docChecklists.emptyTitle': 'No checklist templates',
  'docChecklists.emptyDescription':
    'Create document checklist templates to define which documents are required for each loan type.',
  'docChecklists.columns.name': 'Template',
  'docChecklists.columns.loanType': 'Loan Type',
  'docChecklists.columns.items': 'Items',
  'docChecklists.columns.createdAt': 'Created',
  'docChecklists.form.name': 'Template Name',
  'docChecklists.form.description': 'Description',
  'docChecklists.form.loanType': 'Loan Type',
  'docChecklists.form.documentType': 'Document Type',
  'docChecklists.form.displayName': 'Display Name',
  'docChecklists.form.itemDescription': 'Description',
  'docChecklists.form.isRequired': 'Required document',

  // ==================== Field Suggestions (Document Intelligence) ====================
  'fieldSuggestions.title': 'Extracted Fields',
  'fieldSuggestions.empty': 'No field suggestions yet. Upload documents and run analysis.',
  'fieldSuggestions.emptyFiltered': 'No suggestions match the selected filters.',
  'fieldSuggestions.conflict': 'Conflict',
  'fieldSuggestions.conflictTooltip': 'Multiple documents provide different values for this field.',
  'fieldSuggestions.confidence': 'Confidence: {{value}}%',
  'fieldSuggestions.source': 'Source: {{documentType}}',
  'fieldSuggestions.status.PENDING': 'Pending',
  'fieldSuggestions.status.ACCEPTED': 'Accepted',
  'fieldSuggestions.status.REJECTED': 'Rejected',
  'fieldSuggestions.status.HUMAN_OVERRIDE': 'Manual Override',
  'fieldSuggestions.action.accept': 'Accept',
  'fieldSuggestions.action.reject': 'Reject',
  'fieldSuggestions.action.override': 'Override',
  'fieldSuggestions.action.cancelOverride': 'Cancel',
  'fieldSuggestions.action.confirmOverride': 'Save',
  'fieldSuggestions.bulkAccept': 'Auto-Accept High Confidence',
  'fieldSuggestions.bulkAcceptSuccess': '{{count}} suggestions accepted automatically.',
  'fieldSuggestions.bulkAcceptNone': 'No eligible suggestions to auto-accept.',
  'fieldSuggestions.filter.all': 'All',
  'fieldSuggestions.filter.pending': 'Pending',
  'fieldSuggestions.filter.accepted': 'Accepted',
  'fieldSuggestions.filter.rejected': 'Rejected',
  'fieldSuggestions.filter.conflictsOnly': 'Conflicts Only',
  'fieldSuggestions.loadMore': 'Load more',
  'fieldSuggestions.overridePlaceholder': 'Enter manual value...',
  'fieldSuggestions.acceptedValue': 'Accepted: {{value}}',
  'fieldSuggestions.candidates': '{{count}} candidate values',

  // ==================== Loan Coverage ====================
  'coverage.title': 'Loan Data Coverage',
  'coverage.required': '{{filled}}/{{total}} required fields ({{percentage}}%)',
  'coverage.optional': '{{filled}}/{{total}} optional fields',
  'coverage.pending': '{{count}} pending review',
  'coverage.conflicts': '{{count}} conflicts to resolve',
  'coverage.accepted': '{{count}} accepted',
  'coverage.missingFields': 'Missing Required Fields',
  'coverage.uploadHint': 'Can be extracted from: {{documentTypes}}',
  'coverage.noDocuments': 'No documents have been analyzed yet.',
  'coverage.category.BORROWER': 'Borrower',
  'coverage.category.PROPERTY': 'Property',
  'coverage.category.FINANCIAL': 'Financial',
  'coverage.category.LEGAL': 'Legal',
  'coverage.category.EMPLOYMENT': 'Employment',

  // ==================== Reconcile ====================
  'reconcile.button': 'Analyze Documents',
  'reconcile.loading': 'Analyzing...',
  'reconcile.success': 'Analysis complete: {{filled}} fields extracted, {{conflicts}} conflicts found.',
  'reconcile.error': 'Analysis failed. Please try again.',

  // ==================== Consumer Portal — Documents to Sign (Issue #1308) ====================
  'consumer.documents.title': 'Documents to sign',
  'consumer.documents.description': 'Review and sign documents from your lenders',
  'consumer.documents.tabPending': 'Pending',
  'consumer.documents.tabSigned': 'Signed',
  'consumer.documents.empty': 'No documents awaiting your signature',
  'consumer.documents.emptyHelp': 'When a lender sends you a document to sign, it will appear here.',
  'consumer.documents.signedEmpty': 'No recently signed documents',
  'consumer.documents.signedEmptyHelp': "Documents you've signed in the last 30 days will appear here.",
  'consumer.documents.loading': 'Loading documents...',
  'consumer.documents.errorTitle': 'Unable to load documents',
  'consumer.documents.errorHelp': 'Please try again or contact your lender if the issue persists.',
  'consumer.documents.retry': 'Retry',
  'consumer.documents.signNow': 'Sign now',
  'consumer.documents.waiting': 'Waiting on {{name}} to sign first',
  'consumer.documents.waitingUnknown': 'Waiting on an earlier signer',
  'consumer.documents.sentOn': 'Sent {{date}}',
  'consumer.documents.expiresOn': 'Expires {{date}}',
  'consumer.documents.expiresIn': 'Expires in {{count}} hours',
  'consumer.documents.expiringSoon': 'Expiring soon',
  'consumer.documents.noExpiration': 'No expiration',
  'consumer.documents.signedOn': 'Signed {{date}}',
  // Note: 'consumer.documents.download' is defined earlier in this file
  // (line ~10763) under the Documents grouping — kept there to preserve
  // the logical cluster. Do not re-define here.
  'consumer.documents.downloadUnavailable': 'Download not available',
  'consumer.documents.from': 'From {{business}}',
  'consumer.documents.fromSender': 'From {{business}} — {{sender}}',
  'consumer.documents.role.borrower': 'Sign as borrower',
  'consumer.documents.role.co_borrower': 'Sign as co-borrower',
  'consumer.documents.role.guarantor': 'Sign as guarantor',
  'consumer.documents.role.authorized_officer': 'Sign as authorized officer',
  'consumer.documents.role.witness': 'Sign as witness',
  'consumer.documents.role.notary': 'Sign as notary',
  'consumer.documents.role.cc': 'Copy recipient',
  'consumer.documents.role.default': 'Sign document',
  'consumer.documents.orderLabel': 'Signing order: {{order}}',
  'consumer.documents.widgetTitle': 'Documents to sign',
  'consumer.documents.widgetMore': '{{count}} more pending',
  'consumer.documents.widgetViewAll': 'View all',
  'consumer.documents.widgetItemAria': 'View pending document: {{name}}',
  'consumer.nav.documentsToSign': 'Documents to sign',
  'consumer.documents.modalNewTabNotice':
    'DocuSeal will open in a new tab. Return here after signing — the list will update automatically.',

  // Screen-reader labels (Issue #1308 Round 4 — accessibility)
  'consumer.documents.srSignButton': 'Sign {{document}} from {{business}}',
  'consumer.documents.srSignButtonBlocked': '{{document}} from {{business}} — waiting on {{waitingOn}}',
  'consumer.documents.srExpiringSoon': 'Expiring within 72 hours',
  'consumer.documents.srDownloadButton': 'Download signed {{document}} from {{business}}',
  'consumer.documents.srPendingRegion': 'Pending signature requests',
  'consumer.documents.srSignedRegion': 'Signed documents',
  'consumer.documents.srBadgeLabel': '{{count}} pending signatures',
  'consumer.documents.srBadgeNone': 'No pending signatures',
  'consumer.documents.srTabPendingCount': 'Pending tab, {{count}} items',
  'consumer.documents.srBlockedStatus': 'Blocked — not yet your turn to sign',

  // ============================================================================
  // SCORECARD (Issue #1389 — KPI Scorecard for business / team / partner scopes)
  // ============================================================================
  'scorecard.title': 'Scorecard',
  'scorecard.subtitle': 'Weekly KPI tracking for business, team, and partner scopes',
  'scorecard.newKpi': 'New KPI',
  'scorecard.editKpi': 'Edit KPI',
  'scorecard.deleteKpi': 'Delete KPI',
  'scorecard.deleteConfirm': 'Delete "{{name}}"? This removes all weekly measurements.',

  // Scope switcher
  'scorecard.scope.label': 'Scope',
  'scorecard.scope.business': 'Business-wide',
  'scorecard.scope.team': 'Team',
  'scorecard.scope.partner': 'Partner',
  'scorecard.scope.teamsGroup': 'Teams',
  'scorecard.scope.partnersGroup': 'Partners',

  // Table columns
  'scorecard.column.kpi': 'KPI',
  'scorecard.column.owner': 'Owner',
  'scorecard.column.goal': 'Goal',
  'scorecard.column.trend': 'Trend',
  'scorecard.column.weekOf': 'Week of {{date}}',

  // KPI form
  'scorecard.form.name': 'KPI Name',
  'scorecard.form.namePlaceholder': 'e.g., New Applications (weekly)',
  'scorecard.form.owner': 'Owner',
  'scorecard.form.ownerPlaceholder': 'Select an owner',
  'scorecard.form.unit': 'Unit',
  'scorecard.form.comparator': 'Comparator',
  'scorecard.form.goalValue': 'Goal',
  'scorecard.form.goalValueMax': 'Goal (max)',
  'scorecard.form.scopeType': 'Scope Type',
  'scorecard.form.scopeId': 'Scope',
  'scorecard.form.source': 'Source',
  'scorecard.form.note': 'Note (optional)',
  'scorecard.form.save': 'Save KPI',
  'scorecard.form.cancel': 'Cancel',

  // Units
  'scorecard.unit.count': 'Count',
  'scorecard.unit.currency': 'Currency ($)',
  'scorecard.unit.percentage': 'Percentage (%)',
  'scorecard.unit.duration_days': 'Duration (days)',
  'scorecard.unit.months': 'Duration (months)',
  'scorecard.unit.custom': 'Custom',

  // Comparators
  'scorecard.comparator.gte': 'At least (≥)',
  'scorecard.comparator.lte': 'At most (≤)',
  'scorecard.comparator.eq': 'Exactly (=)',
  'scorecard.comparator.between': 'Between',

  // Sources
  'scorecard.source.manual': 'Manual entry',
  'scorecard.source.computed': 'Computed (auto)',
  'scorecard.source.computed.badge': 'auto · Astor',
  'scorecard.source.computed.tooltip':
    'Auto-calculated by the metrics engine. Manual entry is disabled — the value updates from real platform data each week.',

  // Scorecard grid — per-cell hover tooltip (UX v3 #1749). Title is the week
  // header (e.g., "May 11"); content shows the value + status.
  'scorecard.cell.tooltip.value': '{{value}}',
  'scorecard.cell.tooltip.empty': 'No measurement recorded',
  'scorecard.cell.tooltip.status.green': 'On goal',
  'scorecard.cell.tooltip.status.yellow': 'Watch — close to goal',
  'scorecard.cell.tooltip.status.red': 'Off goal',
  'scorecard.cell.tooltip.editHint': 'Click to edit',

  // Empty states
  'scorecard.empty.title': 'No KPIs yet',
  'scorecard.empty.descriptionBusiness':
    "This business doesn't have any KPIs yet. Create your first one to start tracking weekly measurables.",
  'scorecard.empty.descriptionTeam':
    "This team doesn't have any KPIs yet. Create one to track the team's weekly progress.",
  'scorecard.empty.descriptionPartner':
    "This partner doesn't have any KPIs yet. Create one to track referrals, conversions, and funded volume.",
  'scorecard.empty.cta': 'Create first KPI',
  'scorecard.noScopes.title': 'No scopes available',
  'scorecard.noScopes.description': "You don't have access to any teams or partners with scorecards.",

  // Owner column fallback (when ownerUserId points to a user not in the
  // business member list — e.g., seeded by the platform admin or a
  // since-deactivated user)
  'scorecard.owner.unassigned': 'Unassigned',

  // Partner header strip
  'scorecard.partner.manager': 'Partner manager',
  'scorecard.partner.viewDetails': 'View partner details',
  'scorecard.partner.unassigned': 'Unassigned',

  // Status messages
  'scorecard.measurement.saving': 'Saving…',
  'scorecard.measurement.saved': 'Saved',
  'scorecard.measurement.error': 'Could not save measurement',
  'scorecard.measurement.placeholder': 'Enter value',
  'scorecard.measurement.noData': '—',

  // Errors
  'scorecard.error.partnerAccessDenied': 'You do not have access to this partner scorecard.',
  'scorecard.error.scopeIdRequired': 'A team or partner must be selected for this scope type.',
  'scorecard.error.goalRange': 'Goal (max) must be greater than goal value.',
  'scorecard.error.saveFailed': 'Could not save the KPI. Please try again.',
  'scorecard.error.deleteFailed': 'Could not delete the KPI. Please try again.',

  // Validation
  'scorecard.validation.nameRequired': 'KPI name is required',
  'scorecard.validation.ownerRequired': 'Owner is required',
  'scorecard.validation.goalRequired': 'Goal value is required',

  // ============================================================================
  // COMMAND CENTER (Issue #1424 — AI-powered Home / Dashboard)
  // ============================================================================
  'home.title': 'Command Center',
  'home.subtitle': 'What you should care about today.',
  'home.aiWatermark': 'AI summary — verify before acting.',
  'home.briefStamp': '// {{date}} · MORNING BRIEF',

  // Time-of-day greetings used as the dashboard page title. The `.anon`
  // variants render when the user's first name isn't loaded yet so the
  // header doesn't flash with a comma + empty name.
  'home.greeting.morning': 'Good morning, {{name}}.',
  'home.greeting.morning.anon': 'Good morning.',
  'home.greeting.afternoon': 'Good afternoon, {{name}}.',
  'home.greeting.afternoon.anon': 'Good afternoon.',
  'home.greeting.evening': 'Good evening, {{name}}.',
  'home.greeting.evening.anon': 'Good evening.',

  'home.brief.fallback': '{{loanCount}} active loans · {{messageCount}} unread messages · {{slaCount}} at SLA risk',
  'home.brief.empty': 'No new activity since your last visit. Astor will surface things as they happen.',
  'home.brief.refreshing': 'Refreshing your brief…',
  'home.brief.error': "Couldn't load your brief — showing a quick summary instead.",
  // Subtitle segments for the dashboard header (rendered inline so the
  // SLA segment can be tinted amber when count > 0). useI18n().t does NOT
  // apply plural selection — call sites must pick `_one` vs `_other` from
  // `count`. The bare keys remain as legacy aliases for any caller that
  // hasn't migrated; new code should always use the `_one`/`_other` form.
  // Round-2 Copilot finding on PR #1802 (IDs 3252965030 / 3252965032).
  'home.brief.activeLoans_one': '{{count}} active loan',
  'home.brief.activeLoans_other': '{{count}} active loans',
  'home.brief.activeLoans': '{{count}} active loans',
  'home.brief.unreadMessages_one': '{{count}} unread message',
  'home.brief.unreadMessages_other': '{{count}} unread messages',
  'home.brief.unreadMessages': '{{count}} unread messages',
  'home.brief.slaAtRisk_one': '{{count}} at SLA risk',
  'home.brief.slaAtRisk_other': '{{count}} at SLA risk',
  'home.brief.slaAtRisk': '{{count}} at SLA risk',
  'home.brief.allCaughtUp': 'All caught up',

  'home.ticker.activeLoans_one': '{{count}} active loan',
  'home.ticker.activeLoans_other': '{{count}} active loans',
  'home.ticker.activeLoans': '{{count}} active loans',
  'home.ticker.inboxReplies': '{{count}} inbox replies since 0600',
  'home.ticker.slaAtRisk': '{{count}} at SLA risk',
  'home.ticker.signalsSince': '{{count}} new signals',

  'home.ask.heading': 'What do you want to do?',
  'home.ask.toggleAsk': 'Ask',
  'home.ask.toggleFind': 'Find a loan / contact',
  // Third AskBar mode (v3 home design line 141, home-astor.jsx) — submits
  // through the same Astor handoff as Ask but prefixes the prompt with
  // `/` so the chat lands in slash-command mode.
  'home.ask.toggleRunCommand': 'Run command',
  'home.ask.placeholder': 'e.g., Show me loans at risk, or summarize approvals today',
  'home.ask.runCommandPlaceholder': 'e.g., /find loans at risk, or /create memo',
  'home.ask.cardTitle': 'Ask Astor anything — or type / for commands',
  'home.ask.submit': 'Ask',
  'home.ask.submitting': 'Asking…',
  'home.ask.findPlaceholder': 'Search by name, loan #, or partner',
  'home.ask.sendAria': 'Send',
  'home.ask.sendingAria': 'Sending…',

  'home.recent.title': 'PICK UP WHERE YOU LEFT OFF',
  'home.recent.untitled': 'Untitled conversation',
  'home.recent.resumeAria': 'Resume conversation: {{title}}',
  'home.recent.timeJustNow': 'just now',
  'home.recent.timeMinutes': '{{count}}m ago',
  'home.recent.timeHours': '{{count}}h ago',
  'home.recent.timeDays': '{{count}}d ago',

  // Quick actions — fixed shortcut row on the home page. SCOPE: read +
  // report + insights only (write paths like draft-reply / create-task /
  // upload-document aren't wired through Astor yet — when those land,
  // add them back here).
  'home.quickActions.title': 'Quick actions',
  'home.quickActions.findLoans.label': 'Find loans',
  'home.quickActions.findLoans.sublabel': 'Advanced search',
  'home.quickActions.browseApprovals.label': 'Browse approvals',
  'home.quickActions.browseApprovals.sublabel': "What's awaiting review",
  'home.quickActions.runReport.label': 'Run report',
  'home.quickActions.runReport.sublabel': 'View insights',
  'home.quickActions.viewInsights.label': 'View insights',
  'home.quickActions.viewInsights.sublabel': 'Astor analytics',

  'home.signals.title': 'Priority signals',
  'home.signals.autoRanked': 'Auto-ranked by Astor',
  'home.signals.autoRankedTooltip':
    'Astor ranks these by deadline pressure, blocking severity, and how recently they appeared.',
  'home.signals.newCount': '{{count}} new',
  'home.signals.newChip': 'NEW',
  'home.signals.empty': 'Nothing needs your attention right now. Watchlist updates appear here too.',
  'home.signals.viewAll': 'View all',
  'home.signals.viewAllSignals': 'View all signals',
  'home.signals.classification.urgent': 'Urgent',
  'home.signals.classification.sla': 'SLA',
  'home.signals.classification.approval': 'Approval',
  'home.signals.classification.message': 'Message',
  'home.signals.classification.document': 'Document',
  'home.signals.classification.partner': 'Partner',
  // Signal sublabels — gateway returns the i18n key, client renders.
  'home.signals.sublabel.slaBreached': 'SLA breached',
  'home.signals.sublabel.slaAtRisk': 'SLA at risk',
  'home.signals.sublabel.taskDue': 'Due {{date}}',
  'home.signals.sublabel.messageChannel': 'Channel: {{channel}}',
  // Surfaced on active-loan fallback signals (cases not at SLA risk that
  // backfill the loan-shaped soft cap when there are no urgent items).
  'home.signals.sublabel.loanStatus': 'Status: {{status}}',
  // Time-ago — locale-aware via Intl.RelativeTimeFormat in the helper, but
  // the "just now" / "yesterday" buckets still need a dictionary fallback.
  'home.timeAgo.justNow': 'just now',
  'home.timeAgo.yesterday': 'yesterday',

  'home.watchlist.title': 'Watchlist',
  'home.watchlist.add': 'Add',
  'home.watchlist.viewFull': 'View full watchlist',
  'home.watchlist.remove': 'Unpin',
  'home.watchlist.addToWatchlist': 'Add to watchlist',
  'home.watchlist.removeFromWatchlist': 'Remove from watchlist',
  'home.watchlist.empty': "You haven't pinned anything yet. Pin loans, applications, or borrowers you want to track.",
  // Display label for HomePinEntityType.case — in this domain, every case is a loan,
  // so we surface the user-friendly "Loan" label rather than the schema term "Case".
  'home.watchlist.entityType.case': 'Loan',
  // Legacy alias retained for any HomePin rows still stored as 'loan' in the DB
  // (the picker now writes 'case' for new pins; see #1437).
  'home.watchlist.entityType.loan': 'Loan',
  'home.watchlist.entityType.application': 'Application',
  'home.watchlist.entityType.partner': 'Partner',
  'home.watchlist.entityType.contact': 'Contact',
  'home.watchlist.entityType.consumer': 'Borrower',
  // Borrower-portal rate-watch — pins a rate threshold; 27-notification-service
  // fires when the threshold crosses.
  'home.watchlist.entityType.rate': 'Rate watch',
  'home.watchlist.viewAll': 'View all',
  'home.watchlist.pageTitle': 'Watchlist',
  'home.watchlist.pageDescription': 'Loans, borrowers, partners, and applications you want to keep close.',
  'home.watchlist.backToHome': 'Back to Home',
  'home.watchlist.column.type': 'Type',
  'home.watchlist.column.name': 'Name',
  // Watchlist v3 columns (#1868)
  'home.watchlist.column.status': 'Status',
  'home.watchlist.column.updated': 'Updated',
  'home.watchlist.column.astorSignal': 'Astor signal',
  'home.watchlist.signal.quiet': '— quiet —',
  'home.watchlist.status.unknown': '—',
  // Saved-view chip row (#1868) — counts interpolate as {{count}}.
  // `+ Saved view` is the "create a new saved view" affordance; persistence
  // is its own feature (NEW_GH_ISSUE), so the chip is disabled in v1.
  'home.watchlist.savedView.all': 'All',
  'home.watchlist.savedView.myQueue': 'My queue',
  'home.watchlist.savedView.atRiskSla': 'At-risk SLA',
  'home.watchlist.savedView.astorNeedsReview': 'Astor needs review',
  'home.watchlist.savedView.fundedMtd': 'Funded MTD',
  'home.watchlist.savedView.add': '+ Saved view',
  'home.watchlist.savedView.addDisabledTooltip': 'Custom saved views — coming soon.',
  // Refresh signals — distinct from "Refresh labels". Both buttons live in
  // the header; "signals" implies the Astor signal column re-pulls.
  'home.watchlist.refreshSignals': 'Refresh signals',
  'home.watchlist.emptyDescription':
    'Pin a loan, borrower, partner, or application from its detail page to keep it here for quick access.',
  'home.watchlist.startPinning': 'Pin your first item',
  'home.watchlist.refreshLabels': 'Refresh labels',
  'home.watchlist.refreshLabelsSuccess': 'Labels updated.',
  'home.watchlist.refreshLabelsNoChange': 'Labels are up to date.',
  'home.watchlist.staleBadge': 'Removed',
  'home.watchlist.removeStale': 'This item is no longer available. Remove it from your watchlist?',
  'home.watchlist.cleanupStale': 'Remove unavailable items',
  'home.watchlist.cleanupStaleSuccess': 'Removed unavailable items.',
  'home.watchlist.searchPlaceholder': 'Search loans, applications, borrowers, partners…',

  // Consumer-portal-specific copy — borrowers only see their own loans/applications.
  'home.watchlist.consumer.empty': "You haven't saved anything yet. Save loans or applications you want to track.",
  'home.watchlist.consumer.emptyDescription':
    'Save a loan or application from its detail page to keep it here for quick access.',
  'home.watchlist.consumer.pageDescription': 'Loans and applications you want to keep close.',
  'home.watchlist.consumer.addSearchPlaceholder': 'Search your loans and applications…',

  // Admin-portal-specific copy — admins can pin cases, applications, partners, and consumers
  // across the businesses they administer.
  'home.watchlist.admin.empty':
    "You haven't pinned anything yet. Pin cases, applications, partners, or consumers you want to monitor.",
  'home.watchlist.admin.emptyDescription':
    'Pin a case, application, partner, or consumer from its detail page to keep it here for quick access.',
  'home.watchlist.admin.pageDescription': 'Cases, applications, partners, and consumers you want to monitor.',
  'home.watchlist.admin.addSearchPlaceholder': 'Search cases, applications, partners, consumers…',

  'home.threads.title': 'Threads',
  'home.threads.empty': 'No recent conversations with Astor.',
  'home.threads.viewAll': 'Open Astor',
  'home.threads.subtitle': 'Pick up where you left off',

  // First-time Astor welcome popover — shown once per user (localStorage
  // gated; backend `astorWelcomeSeen` flag is a future enhancement tracked
  // as a sub-issue of #1769).
  'home.welcome.title': 'Welcome to your Dashboard',
  'home.welcome.body':
    "I'm Astor, your AI assistant. I can help you navigate the platform, summarize what needs your attention, and get things done faster.",
  'home.welcome.getStarted': 'Get started with Astor',
  'home.welcome.takeTour': 'Take a tour',
  'home.welcome.explore': "I'll explore on my own",
  'home.welcome.dontShowAgain': "Don't show this again",
  'home.welcome.close': 'Close',

  // AI Briefing — "Astor's read" narrative block (between the snapshot
  // grid and the latest-updates feed).
  'home.briefing.astorReadTitle': "Astor's read",

  'home.session.role': 'Role',
  'home.session.live': 'LIVE',

  'home.error.signalsFailed': "Couldn't load priority signals.",
  'home.error.watchlistFailed': "Couldn't load watchlist.",
  'home.error.pinFailed': "Couldn't update your watchlist.",
  'home.error.briefingFailed': "Couldn't load AI briefing.",
  'home.error.insightsFailed': "Couldn't load AI insights.",

  // Open-rail affordance (dashboard greeting-row, ContextRail collapsed state)
  'home.openBriefing': 'Open AI Briefing',

  // AI Briefing — role-aware "pay attention right now" panel
  'home.briefing.title': 'AI Briefing',
  'home.briefing.subtitle': 'Pay attention to this right now',
  'home.briefing.empty': 'No urgent items right now. Astor will surface things as they happen.',
  'home.briefing.fallback':
    '{{loanCount}} active loans, {{slaCount}} at SLA risk, and {{messageCount}} unread messages waiting for your review.',
  // Role labels — bare nouns so they read naturally both in the briefing
  // card ("AI Briefing · Loan Officer") and the page subtitle line
  // ("Loan Officer · 40 active loans · Friday").
  'home.briefing.role.loanOfficer': 'Loan Officer',
  'home.briefing.role.underwriter': 'Underwriter',
  'home.briefing.role.processor': 'Processor',
  'home.briefing.role.broker': 'Broker',
  'home.briefing.role.principal': 'Principal',
  'home.briefing.role.opsManager': 'Ops Manager',
  'home.briefing.role.fundManager': 'Fund Manager',
  'home.briefing.role.portfolioManager': 'Portfolio Manager',
  'home.briefing.role.businessAdmin': 'Business Admin',
  'home.briefing.role.businessUser': 'Business User',
  'home.briefing.severity.critical': 'Critical',
  'home.briefing.severity.warning': 'Watch',
  'home.briefing.severity.info': 'Notable',
  'home.briefing.severity.positive': 'Good',
  // Restructured AI Briefing rail (image 29) — two sections: a 2x2 stat
  // grid ("Today's snapshot") + a "Latest updates" feed.
  'home.briefing.snapshotTitle': "Today's snapshot",
  'home.briefing.latestUpdatesTitle': 'Latest updates',
  'home.briefing.viewAllUpdates': 'View all updates',
  'home.briefing.snapshot.activeLoans': 'Active loans',
  'home.briefing.snapshot.slaAtRisk': 'At SLA risk',
  'home.briefing.snapshot.unreadMessages': 'Unread messages',
  'home.briefing.snapshot.prioritySignals': 'Priority signals',

  // Dashboard right rail (ContextRail) — aria-label for the rail itself
  'home.dashboard.contextRail.ariaLabel': 'Dashboard context',

  // AI Insights — last 7 days retro + next 7 days forecast
  'home.insights7d.title': 'AI Insights',
  'home.insights7d.subtitle': '7-day retrospective and forecast',
  'home.insights7d.lastSevenDays': 'Last 7 days',
  'home.insights7d.nextSevenDays': 'Next 7 days',
  'home.insights7d.empty': 'Not enough activity yet to generate insights.',
  'home.insights7d.lastFallback':
    '{{closedCount}} loans closed, {{newCount}} new applications, and {{breachedCount}} SLA breaches.',
  'home.insights7d.nextFallback':
    '{{slaAtRiskCount}} loans approaching SLA, {{tasksDueCount}} tasks due, and {{approvalsCount}} pending your approval.',
  'home.insights7d.stats.loansClosed': '{{count}} loans closed',
  'home.insights7d.stats.newApplications': '{{count}} new applications',
  'home.insights7d.stats.slaBreaches': '{{count}} SLA breaches',
  'home.insights7d.stats.tasksCompleted': '{{count}} tasks completed',
  'home.insights7d.stats.slaAtRisk': '{{count}} approaching SLA',
  'home.insights7d.stats.tasksDue': '{{count}} tasks due',
  'home.insights7d.stats.approvalsPending': '{{count}} pending approval',
  'home.insights7d.stats.unreadMessages': '{{count}} unread messages',

  // ==========================================================================
  // Empty Home — bespoke first-day state for /dashboard when the workspace
  // has zero loans + zero signals + zero watchlist pins. Mirrors the v3
  // design at pages/states-scenarios.jsx:5-139.
  // ==========================================================================
  'dashboard.emptyStates.home.welcome.full': 'Welcome to {{tenant}}, {{name}}.',
  'dashboard.emptyStates.home.welcome.nameOnly': 'Welcome, {{name}}.',
  'dashboard.emptyStates.home.welcome.tenantOnly': 'Welcome to {{tenant}}.',
  'dashboard.emptyStates.home.welcome.anon': 'Welcome.',
  'dashboard.emptyStates.home.welcome.subtitle':
    'Your workspace is ready. Below is the fastest path to your first funded loan — Astor can walk you through any step.',
  'dashboard.emptyStates.home.checklist.heading': 'Setup · {{done}} of {{total}} complete',
  'dashboard.emptyStates.home.checklist.timeEstimate': '~12 min total',
  'dashboard.emptyStates.home.checklist.skip': "Skip · I'll explore",
  'dashboard.emptyStates.home.checklist.progressLabel': 'Setup {{pct}}% complete',
  'dashboard.emptyStates.home.checklist.continueCta': 'Continue setup',
  'dashboard.emptyStates.home.checklist.profile.title': 'Business profile',
  'dashboard.emptyStates.home.checklist.profile.sub': 'Name, branches, domain',
  'dashboard.emptyStates.home.checklist.team.title': 'Invite teammates',
  'dashboard.emptyStates.home.checklist.team.sub': 'Underwriters, processors, originators',
  'dashboard.emptyStates.home.checklist.product.title': 'Add a loan product',
  'dashboard.emptyStates.home.checklist.product.sub': 'Bridge, DSCR, Construction…',
  'dashboard.emptyStates.home.checklist.warehouse.title': 'Connect warehouse line',
  'dashboard.emptyStates.home.checklist.warehouse.sub': 'Tell us where capital comes from',
  'dashboard.emptyStates.home.checklist.sizer.title': 'Configure sizer policy',
  'dashboard.emptyStates.home.checklist.sizer.sub': 'LTC, LTARV, FICO floors',
  'dashboard.emptyStates.home.checklist.partner.title': 'Invite your first partner',
  'dashboard.emptyStates.home.checklist.partner.sub': 'Brokers, referral desks',
  'dashboard.emptyStates.home.hero.eyebrow': 'RECOMMENDED FIRST STEP',
  'dashboard.emptyStates.home.hero.title': 'Send your first application invite',
  'dashboard.emptyStates.home.hero.description':
    'Share a magic link with one of your borrowers. They fill out a 5-minute form, Astor pre-screens, and the application lands in your queue ready to underwrite.',
  'dashboard.emptyStates.home.hero.sendInvite': 'Send invite',
  'dashboard.emptyStates.home.hero.copyLink': 'Copy intake link',
  'dashboard.emptyStates.home.hero.copied': 'Copied',
  'dashboard.emptyStates.home.teamInvite.title': 'Or invite your team first',
  'dashboard.emptyStates.home.teamInvite.description':
    'Underwriters, processors, originators — give them workspace access and assign loans to specific people.',
  'dashboard.emptyStates.home.teamInvite.placeholder': 'name@yourcompany.com',
  'dashboard.emptyStates.home.teamInvite.ariaLabel': 'Teammate email address',
  'dashboard.emptyStates.home.teamInvite.send': 'Send invite',
  'dashboard.emptyStates.home.teamInvite.hint': 'Invites expire after 7 days',
  'dashboard.emptyStates.home.resources.quickstart.title': 'Quickstart guide',
  'dashboard.emptyStates.home.resources.quickstart.sub': '5-min walkthrough · video',
  'dashboard.emptyStates.home.resources.astor.title': 'Meet Astor',
  'dashboard.emptyStates.home.resources.astor.sub': 'What your AI co-pilot can do',
  'dashboard.emptyStates.home.resources.security.title': 'Security basics',
  'dashboard.emptyStates.home.resources.security.sub': 'SSO, SOC 2, data handling',

  // ==========================================================================
  // Loan Lifecycle Subsystem — Phase 3 (Issue #1388)
  // Control Center pages: Loan Products, Loan Stages, Stage Requirements,
  // Loan Data Points.
  // ==========================================================================

  // Loan Products page
  'loanProducts.title': 'Loan Products',
  'loanProducts.subtitle': 'Manage the loan products your business offers and how they map to lifecycle stages.',
  'loanProducts.createButton': 'Create product',
  'loanProducts.searchPlaceholder': 'Search products by name, code, or category...',
  'loanProducts.filterCategory': 'Category',
  'loanProducts.filterAll': 'All',
  'loanProducts.filterActive': 'Active',
  'loanProducts.filterArchived': 'Archived',
  'loanProducts.columnName': 'Product',
  'loanProducts.columnCode': 'Code',
  'loanProducts.columnCategory': 'Category',
  'loanProducts.columnStages': 'Stages',
  'loanProducts.columnRequirements': 'Requirements',
  'loanProducts.columnStatus': 'Status',
  'loanProducts.columnUpdated': 'Updated',
  'loanProducts.statusActive': 'Active',
  'loanProducts.statusArchived': 'Archived',
  'loanProducts.tabGeneral': 'General',
  'loanProducts.tabStages': 'Stages',
  'loanProducts.tabRequirements': 'Requirements',
  'loanProducts.tabDataPoints': 'Data points',
  'loanProducts.fieldName': 'Name',
  'loanProducts.fieldCode': 'Code',
  'loanProducts.fieldCodeHelper': 'Internal identifier (e.g., bridge, dscr, fix_and_flip). Cannot be changed once set.',
  'loanProducts.fieldDescription': 'Description',
  'loanProducts.fieldCategory': 'Category',
  'loanProducts.fieldIcon': 'Icon',
  'loanProducts.fieldColor': 'Color',
  'loanProducts.fieldDefaultIntakeTemplateId': 'Default intake template',
  'loanProducts.fieldSortOrder': 'Sort order',
  'loanProducts.usingDefaultStages': "This product uses your business's default stages.",
  'loanProducts.customizeStagesButton': 'Customize stages for this product',
  'loanProducts.customizedStagesNotice': 'This product has custom stages.',
  'loanProducts.archiveConfirmTitle': 'Archive product?',
  'loanProducts.archiveConfirmBody':
    'Archiving will hide this product from new loans. Existing cases keep their reference and audit history is preserved.',
  'loanProducts.unarchiveConfirmTitle': 'Restore product?',
  'loanProducts.unarchiveConfirmBody': 'Restoring will make this product available for new loans again.',
  'loanProducts.createTitle': 'Create loan product',
  'loanProducts.createSubtitle': 'Choose a starting point for your new product.',
  'loanProducts.startFromTemplate': 'Start from a template',
  'loanProducts.startFromTemplateDescription':
    'Pre-configured with stages and requirements for a common loan type — bridge, fix-and-flip, DSCR, ground-up, commercial, multifamily.',
  'loanProducts.startFromBlank': 'Start from blank',
  'loanProducts.startFromBlankDescription': 'Create an empty product and configure stages and requirements yourself.',
  'loanProducts.editTitle': 'Edit loan product',
  'loanProducts.toast.created': 'Product created',
  'loanProducts.toast.updated': 'Product saved',
  'loanProducts.toast.archived': 'Product archived',
  'loanProducts.toast.restored': 'Product restored',
  'loanProducts.toast.error': 'Failed to save: {message}',
  'loanProducts.empty.title': 'No loan products yet',
  'loanProducts.empty.subtitle':
    'Create your first product from a template or from scratch. Products determine the stages and requirements applied to each loan.',
  'loanProducts.referencedDataPoints': 'Data points referenced by this product',
  'loanProducts.manageDataPointsLink': 'Manage all data points',
  'loanProducts.stageCountChip': '{{count}} stages',
  'loanProducts.requirementCountChip': '{{count}} requirements',
  'loanProducts.dataPointCountChip': '{{count}} data points',

  // Loan Stages page
  'loanStages.title': 'Loan Stages',
  'loanStages.subtitle':
    'Configure the lifecycle stages that loans pass through. Drag to reorder, edit capabilities, and define transitions.',
  'loanStages.addStageButton': 'Add stage',
  'loanStages.addTransitionButton': 'Add transition',
  'loanStages.editingDefault': 'Editing: Business default',
  'loanStages.editingProduct': 'Editing: {product}',
  'loanStages.productFilterLabel': 'Stages for',
  'loanStages.productFilterDefault': 'Business default',
  'loanStages.transitionGraph': 'Transitions',
  'loanStages.resetToDefault': 'Reset to default stages',
  'loanStages.resetConfirmTitle': 'Reset stages to defaults?',
  'loanStages.resetConfirmBody':
    'This removes any per-product customizations and re-applies the business default stages. Existing cases keep their current stage.',
  'loanStages.unforkButton': 'Use business default',
  'loanStages.capabilityGroupLifecycle': 'Lifecycle',
  'loanStages.capabilityGroupTrigger': 'Triggers',
  'loanStages.capabilityGroupApproval': 'Approvals',
  'loanStages.capabilityGroupTerminal': 'Terminal',
  'loanStages.substages': 'Substages',
  'loanStages.addSubstageButton': 'Add substage',
  'loanStages.fieldName': 'Name',
  'loanStages.fieldDescription': 'Description',
  'loanStages.fieldColor': 'Color',
  'loanStages.fieldIcon': 'Icon',
  'loanStages.fieldSlaHours': 'SLA hours',
  'loanStages.fieldPauseSla': 'Pause SLA',
  'loanStages.badgeInitial': 'Initial',
  'loanStages.badgeTerminal': 'Terminal',
  'loanStages.badgeSystem': 'System',
  'loanStages.capabilitiesChip': '{{count}} capabilities',
  'loanStages.substagesChip': '{{count}} substages',
  'loanStages.fromStage': 'From',
  'loanStages.toStage': 'To',
  'loanStages.requireRole': 'Required role',
  'loanStages.requireApproval': 'Requires approval',
  'loanStages.isBackward': 'Backward transition',
  'loanStages.transitionsEmpty': 'No transitions defined yet. Add one to allow loans to move between stages.',
  'loanStages.toast.created': 'Stage created',
  'loanStages.toast.updated': 'Stage saved',
  'loanStages.toast.archived': 'Stage archived',
  'loanStages.toast.reordered': 'Stages reordered',
  'loanStages.toast.transitionCreated': 'Transition created',
  'loanStages.toast.transitionDeleted': 'Transition deleted',
  'loanStages.toast.forked': 'Customizing stages for this product',
  'loanStages.toast.error': 'Failed to save: {message}',
  'loanStages.graphInvalidWarning': 'Stage graph has issues:',
  'loanStages.empty.title': 'No stages configured',
  'loanStages.empty.subtitle':
    'Add stages to model how loans flow through your business. Each stage can carry capability tags, SLA hours, and substages.',

  // Loan Stage Requirements page
  'loanStageRequirements.title': 'Stage Requirements',
  'loanStageRequirements.subtitle':
    'Map data points to lifecycle stages. Required data points block stage exit; recommended ones surface as warnings.',
  'loanStageRequirements.productFilter': 'Editing requirements for',
  'loanStageRequirements.allProducts': 'Business default',
  'loanStageRequirements.businessDefault': 'Business default',
  'loanStageRequirements.categoryFilter': 'Category',
  'loanStageRequirements.searchPlaceholder': 'Search data points...',
  'loanStageRequirements.requirementRequired': 'Required',
  'loanStageRequirements.requirementRecommended': 'Recommended',
  'loanStageRequirements.requirementOptional': 'Optional',
  'loanStageRequirements.requirementNone': 'Not required',
  'loanStageRequirements.requirementShortRequired': 'Req',
  'loanStageRequirements.requirementShortRecommended': 'Rec',
  'loanStageRequirements.requirementShortOptional': 'Opt',
  'loanStageRequirements.saveButton': 'Save changes',
  'loanStageRequirements.discardButton': 'Discard',
  'loanStageRequirements.unsavedChanges': 'Unsaved changes',
  'loanStageRequirements.toast.saved': 'Requirements saved',
  'loanStageRequirements.toast.error': 'Failed to save requirements: {message}',
  'loanStageRequirements.empty.title': 'No data points to configure',
  'loanStageRequirements.empty.subtitle': 'Define data points first, then return here to map them to lifecycle stages.',
  'loanStageRequirements.legend': 'Legend',
  'loanStageRequirements.cellLockedSystem': 'System default — clone the product to override',

  // Loan Data Points page
  'loanDataPoints.title': 'Loan Data Points',
  'loanDataPoints.subtitle':
    'Catalog of structured fields collected during the loan lifecycle. Used by stage requirements and case fields.',
  'loanDataPoints.createButton': 'Create data point',
  'loanDataPoints.searchPlaceholder': 'Search data points...',
  'loanDataPoints.filterCategory': 'Category',
  'loanDataPoints.filterKind': 'Type',
  'loanDataPoints.columnDisplayName': 'Display name',
  'loanDataPoints.columnCanonicalKey': 'Canonical key',
  'loanDataPoints.columnCategory': 'Category',
  'loanDataPoints.columnValueType': 'Value type',
  'loanDataPoints.columnCaseField': 'Case field',
  'loanDataPoints.columnUsedByStages': 'Used by',
  'loanDataPoints.columnStatus': 'Status',
  'loanDataPoints.fieldDisplayName': 'Display name',
  'loanDataPoints.fieldCanonicalKey': 'Canonical key',
  'loanDataPoints.fieldCanonicalKeyHelper':
    'Snake-case identifier used by integrations and AI agents. Cannot be changed once set.',
  'loanDataPoints.fieldCategory': 'Category',
  'loanDataPoints.fieldValueType': 'Value type',
  'loanDataPoints.fieldCaseField': 'Mapped case field',
  'loanDataPoints.fieldCaseFieldHelper': 'Optional. Maps this data point onto a built-in field on the Case record.',
  'loanDataPoints.fieldExtractableFrom': 'Extractable from',
  'loanDataPoints.fieldExtractableFromHelper': 'Document types that AI extraction can pull this data point from.',
  'loanDataPoints.fieldSurvivorshipRule': 'Survivorship rule',
  'loanDataPoints.fieldValidation': 'Validation rules (JSON)',
  'loanDataPoints.fieldIsActive': 'Active',
  'loanDataPoints.usedByStagesEmpty': 'Not yet referenced by any stage requirements.',
  'loanDataPoints.usedByStagesCount': 'Referenced by {count} stage requirements',
  'loanDataPoints.createTitle': 'Create data point',
  'loanDataPoints.editTitle': 'Edit data point',
  'loanDataPoints.archiveConfirmTitle': 'Archive data point?',
  'loanDataPoints.archiveConfirmBody':
    'Archiving hides this from new requirements. Existing cases keep collected values and audit history.',
  'loanDataPoints.toast.created': 'Data point created',
  'loanDataPoints.toast.updated': 'Data point saved',
  'loanDataPoints.toast.archived': 'Data point archived',
  'loanDataPoints.toast.error': 'Failed to save: {message}',
  'loanDataPoints.empty.title': 'No data points yet',
  'loanDataPoints.empty.subtitle':
    'Create data points to define what your business collects during the loan lifecycle. Stage requirements then determine when each one is required.',
  'loanDataPoints.invalidJson': 'Validation rules must be valid JSON',

  // Loan Data Point categories (also used as labels in filters and matrix grouping)
  'loanDataPoints.category.BORROWER': 'Borrower',
  'loanDataPoints.category.PROPERTY': 'Property',
  'loanDataPoints.category.FINANCIAL': 'Financial',
  'loanDataPoints.category.LEGAL': 'Legal',
  'loanDataPoints.category.EMPLOYMENT': 'Employment',
  'loanDataPoints.valueType.STRING': 'Text',
  'loanDataPoints.valueType.NUMBER': 'Number',
  'loanDataPoints.valueType.DATE': 'Date',
  'loanDataPoints.valueType.BOOLEAN': 'Yes / No',
  'loanDataPoints.valueType.CURRENCY': 'Currency',
  'loanDataPoints.valueType.DOCUMENT': 'Document',

  // Loan Products page (additional keys for tabbed editor — Phase 3)
  'loanProducts.fieldDefaultIntakeTemplateIdHelper':
    'Optional. UUID of an intake template applied by default when a borrower starts an application for this product.',
  'loanProducts.fieldSortOrderHelper': 'Lower numbers appear first in lists and pickers.',
  'loanProducts.codeLockedHelper': 'System products keep their code locked to preserve integrations.',
  'loanProducts.stagesTabSubtitle':
    'These are the stages this product follows. Customize them to override the business default.',
  'loanProducts.stagesTabDefaultBanner':
    "This product currently uses your business's default stages. Customize to define product-specific stages.",
  'loanProducts.stagesTabForkedBanner':
    'This product has its own stage configuration. Edit them on the Loan Stages page using this product filter.',
  'loanProducts.requirementsTabSubtitle':
    'Map data points to lifecycle stages for this product. Open the full matrix to edit cells side-by-side.',
  'loanProducts.openRequirementsMatrix': 'Open requirements matrix',
  'loanProducts.dataPointsTabSubtitle': 'Data points referenced by stage requirements on this product.',
  'loanProducts.dataPointsTabEmpty':
    "No data points required yet. Use the Requirements tab to map data points to this product's stages.",
  'loanProducts.requirementsTabEmpty':
    'No requirements configured for this stage yet. Add data points from the catalog to gate this stage.',
  'loanProducts.editStagesForProduct': 'Edit stages for this product',
  'loanProducts.fieldNameRequired': 'Name is required',
  'loanProducts.fieldCodeRequired': 'Code is required',
  'loanProducts.codePattern': 'Use lowercase letters, numbers, and underscores only',
  'loanProducts.startFromTemplateBack': 'Pick a different option',
  'loanProducts.templateAlreadyInCatalog': 'In your catalog',

  // Loan Stages page (additional keys for editor + transitions — Phase 3)
  'loanStages.subtitleNotice':
    'Capabilities tag the stage so reports, AI agents, and integrations can react without hardcoding stage names.',
  'loanStages.editStageHelper': 'Click a stage to expand and edit.',
  'loanStages.collapseEdit': 'Collapse',
  'loanStages.expandEdit': 'Expand',
  'loanStages.fieldSlaHoursHelper': 'Optional. After this many hours in the stage, the SLA is breached.',
  'loanStages.fieldPauseSlaHelper':
    'Pause SLA timers while loans are in this stage (useful for waiting-on-borrower stages).',
  'loanStages.transitionFromPlaceholder': 'Select source stage',
  'loanStages.transitionToPlaceholder': 'Select target stage',
  'loanStages.transitionRolePlaceholder': 'Optional role (e.g. underwriter)',
  'loanStages.deleteTransition': 'Delete transition',
  'loanStages.deleteTransitionConfirm':
    'Remove this transition? Loans currently in the source stage will no longer be able to advance to the target.',
  'loanStages.archiveStageConfirmTitle': 'Archive stage?',
  'loanStages.archiveStageConfirmBody':
    'Archived stages stop accepting new loans. Existing cases keep their reference and audit history is preserved.',
  'loanStages.systemStageNotice': 'System stages cannot be archived from the UI.',
  'loanStages.fieldCapabilities': 'Capabilities',

  // Loan Stage Requirements page (additional keys for matrix — Phase 3)
  'loanStageRequirements.cellTooltipCycle': 'Click to cycle: Required → Recommended → Optional → Not required',
  'loanStageRequirements.legendRequired': 'Required — blocks stage exit until collected',
  'loanStageRequirements.legendRecommended': 'Recommended — surfaces a warning but does not block stage exit',
  'loanStageRequirements.legendOptional': 'Optional — collected if available, never blocks',
  'loanStageRequirements.legendNone': 'Not required at this stage',
  'loanStageRequirements.savingButton': 'Saving...',
  'loanStageRequirements.noStagesTitle': 'No stages configured for this product',
  'loanStageRequirements.noStagesSubtitle':
    'Configure loan stages first, then return here to map data point requirements.',

  // Issue #1388 — Phase 4: Loan Lifecycle Stepper / RequiredToAdvanceCard /
  // StageTransitionDialog (UUID-based)
  'loanLifecycle.stepper.empty': 'No loan stages configured for this product',
  'loanLifecycle.stepper.noStage': 'Stage not set yet',
  'loanLifecycle.stepper.advance': 'Advance stage',
  'loanLifecycle.stepper.advanceTo': 'Advance to {{stage}}',
  'loanLifecycle.stepper.pushBackUnavailable': 'No backward transition to {{stage}} configured for this product',
  'loanLifecycle.stepper.goToStageAria': 'Go to {{stage}}',
  'loanLifecycle.stepper.cannotAdvanceAria': 'Cannot advance to {{stage}}: {{count}} required field(s) missing',
  'loanLifecycle.stepper.stageNumberAria': 'Stage {{index}}: {{stage}}',
  'loanLifecycle.stepper.product': 'Product: {{name}}',
  // Clickable-stepper tooltips. Each dot now exposes a transition affordance
  // via click; these keys carry the hover/disabled-state copy.
  'loanLifecycle.stepper.advanceHint': 'Advance to {{stage}}',
  'loanLifecycle.stepper.pushBackHint': 'Push back to {{stage}}',
  'loanLifecycle.stepper.advanceOneAtATime': 'Advance one stage at a time — click {{stage}} first.',
  // Fallback when the next forward stage name isn't available (e.g. the
  // case has no transitions configured yet). Shouldn't fire in normal
  // flows, but keeps the disabled-stepper-dot copy meaningful.
  'loanLifecycle.stepper.advanceOneAtATimeNoTarget': 'Advance one stage at a time — click the next stage first.',
  'loanLifecycle.stepper.checkingRequirements': 'Checking stage requirements…',
  'loanLifecycle.stepper.gateTitle': 'Cannot advance to {{stage}}',
  'loanLifecycle.stepper.gateSubtitle': '{{count}} required field(s) still need to be filled in:',
  'loanLifecycle.stepper.gateMore': '+{{count}} more',
  'loanLifecycle.requiredToAdvance.title': 'Required to advance to {{stage}}',
  // Issue #1615 Bug 1: clarify directionality. Without this subtitle the
  // user reading "Required to advance to {target}" while sitting in the
  // CURRENT stage gets confused — they expect to see "what's left for
  // this stage" not "what gates the next one." Spell it out.
  'loanLifecycle.requiredToAdvance.subtitle': 'Complete these items before this loan can leave its current stage.',
  'loanLifecycle.requiredToAdvance.count': '{{count}} items',
  'loanLifecycle.requiredToAdvance.required': 'Required',
  'loanLifecycle.requiredToAdvance.recommended': 'Recommended',
  'loanLifecycle.requiredToAdvance.fillIn': 'Fill in',
  'loanLifecycle.requiredToAdvance.error': 'Could not load requirements check.',
  'loanLifecycle.requiredToAdvance.showMore': 'Show {{count}} more',
  'loanLifecycle.requiredToAdvance.showLess': 'Show less',
  'loanLifecycle.requiredToAdvance.viewAll': 'View all requirements',
  'loanLifecycle.requiredToAdvance.hideDetails': 'Hide details',
  'loanLifecycle.requiredToAdvance.readyTitle': 'Ready to advance',
  'loanLifecycle.requiredToAdvance.readyBody': 'All requirements for {{stage}} are satisfied.',
  'loanLifecycle.stageTransition.noTargets': 'No reachable stages',
  'loanLifecycle.stageTransition.terminal': 'Terminal',
  'loanLifecycle.stageTransition.overrideLabel': 'Override requirements gate (admin)',
  'loanLifecycle.stageTransition.overrideHelper': 'Bypass the data-completeness gate. Audited.',
  'loanLifecycle.stageTransition.gateError': 'Cannot advance: {{message}}',
  // Issue #1615 Bug 7 — defensive copy when the server returns an opaque error
  // (e.g., "Forbidden", "Not allowed") with no actionable detail. Use as a
  // friendly fallback so the user gets guidance instead of a one-word message.
  'loanLifecycle.stageTransition.gateErrorGeneric':
    'Cannot transition: please complete the required fields and try again.',
  // Issue #1615 Bug 7 — surfaced when the user hasn't actually changed the
  // target stage or substage (no-op). Prevents firing a server request that
  // the gateway will reject with a soft "no change" error.
  'loanLifecycle.stageTransition.noChangeHelper':
    'No change to apply — pick a different target stage or substage to continue.',
  // Issue #1615 Bug 7 — surfaced when the caller pre-set a target stage that
  // isn't reachable from the current stage (transition graph edge missing).
  // The dialog falls back to the first reachable target rather than letting
  // the server reject the request.
  'loanLifecycle.stageTransition.presetUnreachable':
    "The requested target stage isn't reachable from the current stage. Defaulting to {{fallback}}.",

  // Loan Sizer — product picker (Issue #1388 — Phase 4)
  'loanSizer.product': 'Loan product',
  'loanSizer.productPlaceholder': 'Pick a loan product…',
  'loanSizer.productHint': 'Engine type: {{type}}',
  'loanSizer.productEmptyHint': 'The product picks the engine type and pre-fills defaults.',
  'loanSizer.productLabel': 'Product: {{name}}',

  // Loan Insights Rail (Issue #1551) — debounced AI eval + persisted assessment merge
  'loanSizer.insights.title': 'Deal Insights',
  'loanSizer.insights.emptyState': 'Enter purchase + ARV to see AI evaluation',
  'loanSizer.insights.loadingTitle': 'Evaluating deal…',
  'loanSizer.insights.staleTitle': 'Updating in {{seconds}}s…',
  'loanSizer.insights.errorTitle': 'Evaluation failed',
  'loanSizer.insights.errorRetry': 'Retry',
  'loanSizer.insights.signals.positive': 'Positive Signals',
  'loanSizer.insights.signals.cautions': 'Cautions',
  'loanSizer.insights.signals.warnings': 'Warnings',
  'loanSizer.insights.signals.errors': 'Errors',
  'loanSizer.insights.nextSteps.title': 'Suggested Next Steps',
  'loanSizer.insights.overallAssessment': 'Overall',
  'loanSizer.insights.astorBrief.title': 'Astor Insights',
  'loanSizer.insights.astorBrief.executiveSummary': 'EXECUTIVE SUMMARY',
  'loanSizer.insights.astorBrief.keyRiskFactors': 'KEY RISK FACTORS',
  'loanSizer.insights.astorBrief.severity.healthy': 'Healthy',
  'loanSizer.insights.astorBrief.severity.medium': 'Watch',
  'loanSizer.insights.astorBrief.severity.elevated': 'Elevated Risk',
  'loanSizer.insights.astorBrief.severity.critical': 'Critical Risk',
  'loanSizer.insights.actionToolbar.triggerCreditPull': 'Trigger Credit Pull',
  'loanSizer.insights.actionToolbar.runFullAssessment': 'Run Full Assessment',
  'loanSizer.insights.actionToolbar.overrideRecommendation': 'Override Recommendation',
  'loanSizer.insights.scoreRing.label': 'Overall Score',
  'loanSizer.insights.factors.title': 'Assessment Factors',
  'loanSizer.insights.narrative.title': 'AI Narrative',
  'loanSizer.insights.timeline.title': 'Assessment History',
  'loanSizer.insights.autoChip': 'auto',
  'loanSizer.insights.aiOverview.title': 'AI Insights',
  'loanSizer.insights.aiOverview.summary.errors_one': '{{count}} error',
  'loanSizer.insights.aiOverview.summary.errors_other': '{{count}} errors',
  'loanSizer.insights.aiOverview.summary.errors': '{{count}} errors',
  'loanSizer.insights.aiOverview.summary.warnings_one': '{{count}} warning',
  'loanSizer.insights.aiOverview.summary.warnings_other': '{{count}} warnings',
  'loanSizer.insights.aiOverview.summary.warnings': '{{count}} warnings',
  'loanSizer.insights.aiOverview.summary.cautions': '{{count}} cautions',
  'loanSizer.insights.aiOverview.summary.positive': '{{count}} positive',
  'loanSizer.insights.aiOverview.summary.actions_one': '{{count}} action',
  'loanSizer.insights.aiOverview.summary.actions_other': '{{count}} actions',
  'loanSizer.insights.aiOverview.summary.actions': '{{count}} actions',
  // Decision Snapshot (header) — verdict chip + confidence + top blocker.
  // Always visible above the collapsable signal/overall/next-step body.
  'loanSizer.insights.aiOverview.confidence': '{{value}}% confident',
  'loanSizer.insights.aiOverview.topBlockerLabel': 'Top blocker',

  // Intake Templates — used-by-loan-products column (Issue #1388 — Phase 4)
  'intake.columns.usedByLoanProducts': 'Used by Loan Products',
  'intake.usedByLoanProducts.none': 'None',
  'intake.usedByLoanProducts.count': '{{count}} products',

  // ============================================================================
  // ASTOR TOOL RENDERERS (Issue #1432 Phase 2 - Generative UI)
  // ============================================================================
  // Typed React components that render structured tool_result payloads from
  // the agent service. Strings appear in chat messages when a tool returns
  // a result (cases list, partner stats, knowledge-base search, etc.).

  // AstorThread (Issue #1434) — assistant-ui-based chat shell strings.
  'astorThread.composer.placeholder': 'Type your message...',
  'astorThread.composer.send': 'Send',
  'astorThread.composer.cancel': 'Stop generating',
  'astorThread.composer.dictate': 'Dictate',
  'astorThread.action.copy': 'Copy',
  'astorThread.action.edit': 'Edit',
  'astorThread.action.regenerate': 'Regenerate',
  'astorThread.action.feedbackPositive': 'Helpful',
  'astorThread.action.feedbackNegative': 'Not helpful',
  'astorThread.branch.previous': 'Previous branch',
  'astorThread.branch.next': 'Next branch',
  'astorThread.empty.title': 'Start a conversation',
  'astorThread.empty.subtitle': 'Ask anything about your loans, cases, or partners.',
  'astorThread.empty.thinking': 'Thinking…',

  // Generic
  'astorTools.skeleton.loading': 'Loading tool result',
  'astorTools.error.parseFailed': "Couldn't display this result",
  'astorTools.error.parseFailedDescription':
    "The tool returned data we did not recognize. The conversation will continue with the assistant's text reply.",

  // Cases (case_management_list_cases)
  'astorTools.cases.title': 'Loans',
  'astorTools.cases.caseNumber': 'Loan #',
  'astorTools.cases.borrower': 'Borrower',
  'astorTools.cases.amount': 'Amount',
  'astorTools.cases.rate': 'Rate',
  'astorTools.cases.status': 'Status',
  'astorTools.cases.property': 'Property',
  'astorTools.cases.empty': 'No loans found',
  'astorTools.cases.emptyDescription': 'No loans match the current filters.',

  // Single case (case_management_get_case)
  'astorTools.case.notFound': 'Loan not found',
  'astorTools.case.notFoundDescription': "We couldn't find that loan, or it has been removed.",
  'astorTools.case.borrowerSection': 'Borrower',
  'astorTools.case.borrowerName': 'Name',
  'astorTools.case.borrowerEmail': 'Email',
  'astorTools.case.borrowerPhone': 'Phone',
  'astorTools.case.loanSection': 'Loan details',
  'astorTools.case.amount': 'Amount',
  'astorTools.case.rate': 'Interest rate',
  'astorTools.case.term': 'Term',
  'astorTools.case.property': 'Property',
  'astorTools.case.statusSection': 'Status',
  'astorTools.case.timelineSection': 'Timeline',
  'astorTools.case.created': 'Created',
  'astorTools.case.updated': 'Last updated',

  // Consumers (case_management_list_consumers)
  'astorTools.consumers.title': 'Borrowers',
  'astorTools.consumers.name': 'Name',
  'astorTools.consumers.email': 'Email',
  'astorTools.consumers.phone': 'Phone',
  'astorTools.consumers.status': 'Status',
  'astorTools.consumers.lastActivity': 'Last activity',
  'astorTools.consumers.empty': 'No borrowers found',
  'astorTools.consumers.emptyDescription': 'No borrowers match the current filters.',

  // Partners (crm_list_partners)
  'astorTools.partners.title': 'Partners',
  'astorTools.partners.name': 'Name',
  'astorTools.partners.type': 'Type',
  'astorTools.partners.status': 'Status',
  'astorTools.partners.activeDeals': 'Active deals',
  'astorTools.partners.mtdCommission': 'MTD commission',
  'astorTools.partners.empty': 'No partners found',
  'astorTools.partners.emptyDescription': 'No partners match the current filters.',

  // Stats (case_management_get_stats)
  'astorTools.stats.title': 'Loan stats',
  'astorTools.stats.totalCases': 'Total loans',
  'astorTools.stats.active': 'Active',
  'astorTools.stats.pending': 'Pending',
  'astorTools.stats.completed': 'Completed',
  'astorTools.stats.rejected': 'Rejected',
  'astorTools.stats.avgDaysToClose': 'Avg days to close',
  'astorTools.stats.empty': 'No stats available',
  'astorTools.stats.emptyDescription': 'No loan stats are available for this scope.',

  // Leads (crm_list_leads)
  'astorTools.leads.title': 'Leads',
  'astorTools.leads.name': 'Name',
  'astorTools.leads.source': 'Source',
  'astorTools.leads.status': 'Status',
  'astorTools.leads.score': 'Score',
  'astorTools.leads.createdAt': 'Created',
  'astorTools.leads.assignedTo': 'Assigned to',
  'astorTools.leads.empty': 'No leads found',
  'astorTools.leads.emptyDescription': 'No leads match the current filters.',

  // Partner stats (crm_get_partner_stats)
  'astorTools.partnerStats.title': 'Partner stats',
  'astorTools.partnerStats.activitySection': 'Activity',
  'astorTools.partnerStats.activeDeals': 'Active deals',
  'astorTools.partnerStats.mtdVolume': 'MTD volume',
  'astorTools.partnerStats.mtdCommission': 'MTD commission',
  'astorTools.partnerStats.performanceSection': 'Performance',
  'astorTools.partnerStats.closeRate': 'Close rate',
  'astorTools.partnerStats.avgDays': 'Avg days to close',
  'astorTools.partnerStats.empty': 'Partner stats unavailable',
  'astorTools.partnerStats.emptyDescription': "We couldn't load stats for that partner.",

  // Knowledge base search (search_knowledge_base)
  'astorTools.knowledgeBase.title': 'Knowledge base',
  'astorTools.knowledgeBase.score': 'match',
  'astorTools.knowledgeBase.empty': 'No results',
  'astorTools.knowledgeBase.emptyDescription': 'No knowledge-base articles match this query.',

  // ============================================================================
  // LOAN SIZING — Inline real-time sizing experience (Issue #1438)
  // Distinct namespace from the legacy `loanSizer.*` keys (modal flow).
  // ============================================================================

  // Header summary strip
  'loanSizing.summary.title': 'Live sizing',
  'loanSizing.summary.maxLoan': 'Max loan',
  'loanSizing.summary.bindingConstraint': 'Bound by',
  'loanSizing.summary.status.withinTarget': 'Within target',
  'loanSizing.summary.status.exception': 'Exception — desk review',
  'loanSizing.summary.status.outside': 'Outside target',
  'loanSizing.summary.empty': 'Enter loan details to see sizing.',
  'loanSizing.summary.recomputing': 'Recomputing…',
  'loanSizing.summary.scenarios': 'Scenarios',

  // Scenario tab strip + actions
  'loanSizing.scenarios.tabs.add': 'Add scenario',
  'loanSizing.scenarios.tabs.discard': 'Discard scenario',
  'loanSizing.scenarios.tabs.rename': 'Rename scenario',
  'loanSizing.scenarios.tabs.maxScenariosTooltip': 'Maximum of 4 scenarios reached.',
  'loanSizing.scenarios.tabs.saveSnapshot': 'Save snapshot',
  'loanSizing.scenarios.tabs.compare': 'Compare',

  // Scenario compare panel — read-only diff across active scenarios
  'loanSizing.compare.title': 'Compare scenarios',
  'loanSizing.compare.deltaOnly': 'Differences only',
  'loanSizing.compare.field': 'Field',
  'loanSizing.compare.inputs': 'Inputs',
  'loanSizing.compare.outputs': 'Result',
  'loanSizing.compare.baseline': 'baseline',
  'loanSizing.compare.statusRow': 'Status',
  'loanSizing.compare.noDifferences': 'All scenarios use identical inputs.',
  'loanSizing.compare.tooltipNeedsTwo': 'Add a second scenario to compare.',
  // Expand/collapse toggle in the SplitPanelHeader actions slot.
  'loanSizing.compare.expand': 'Expand panel',
  'loanSizing.compare.collapse': 'Collapse panel',

  // Snapshot dialog (rendered via SplitPanel)
  'loanSizing.scenarios.snapshot.title': 'Save sizing snapshot',
  'loanSizing.scenarios.snapshot.namePlaceholder': 'Optional name (e.g., “Base case”)',
  'loanSizing.scenarios.snapshot.helper': 'Snapshots are immutable and remain available in the Sizing tab.',
  'loanSizing.scenarios.snapshot.save': 'Save snapshot',
  'loanSizing.scenarios.snapshot.cancel': 'Cancel',

  // Constraint hint line under each slider
  'loanSizing.constraintHint.bindingFrom': 'Bound by {{field}}',
  'loanSizing.constraintHint.suggested': 'Suggested range',
  'loanSizing.constraintHint.over': 'Above the maximum',
  'loanSizing.constraintHint.under': 'Below the minimum',
  'loanSizing.constraintHint.supports': 'In range',
  'loanSizing.constraintHint.binds': 'Currently the binding constraint',

  // Field labels (used for the sliders and binding-from chips)
  'loanSizing.field.purchasePrice': 'Purchase price',
  'loanSizing.field.rehabBudget': 'Rehab budget',
  'loanSizing.field.constructionBudget': 'Construction budget',
  'loanSizing.field.arv': 'After-repair value',
  'loanSizing.field.requestedLoan': 'Requested loan',
  'loanSizing.field.rate': 'Interest rate',
  'loanSizing.field.points': 'Points',
  'loanSizing.field.termMonths': 'Term (months)',
  'loanSizing.field.fico': 'FICO',
  'loanSizing.field.asIsValue': 'As-is value',
  'loanSizing.field.initialLtcPlusHoldback': 'Initial LTC + holdback cap',
  'loanSizing.field.totalLtc': 'Total LTC cap',
  'loanSizing.field.arltv': 'ARLTV cap',
  'loanSizing.field.tierMaxLoan': 'Tier max loan',
  'loanSizing.field.proposedLoanAmount': 'Proposed loan amount',
  'loanSizing.field.constructionHoldback': 'Construction holdback',
  'loanSizing.field.interestReserve': 'Interest reserve',
  'loanSizing.field.cashOutLessClosing': 'Cash out (less closing)',

  // Resolution flags surfaced from ResolvedSizingPolicySnapshot.missingFlags.
  // The `missingTiers` form combines rate + points into a single warning when
  // both are missing — the underwriter only needs to be told once that this
  // product isn't fully tuned.
  'loanSizing.flags.missingRateTier': 'No rate tier configured for this product — sizing uses a fallback rate.',
  'loanSizing.flags.missingPointsTier':
    'No points tier configured for this product — sizing uses a fallback origination.',
  'loanSizing.flags.missingTiers': 'No rate or points tier configured for this product — sizing uses fallback values.',

  // ============================================================================
  // Control Center extensions (Issue #1480 — Closing checklist + DOCUMENT_SLOT)
  // ============================================================================
  // Data Points kind tabs — surfaces DOCUMENT_SLOT vs DATA_POINT in /control-center/loan-data-points
  'controlCenter.dataPoints.tabAll': 'All',
  'controlCenter.dataPoints.tabDataPoints': 'Data Points',
  'controlCenter.dataPoints.tabDocumentSlots': 'Document Slots',

  // Stage Requirements editor — per-product applicability rules in the loan-product detail panel
  'controlCenter.stageRequirements.title': 'Stage Requirements',
  'controlCenter.stageRequirements.subtitle':
    'Map data points to lifecycle stages for this product. Add an applicability rule to make a requirement conditional.',
  'controlCenter.stageRequirements.alwaysApplies': 'Always applies',
  'controlCenter.stageRequirements.applicabilityNote': 'Applicability note',
  'controlCenter.stageRequirements.applicabilityNoteHelper':
    'Optional rationale shown to closers (max 280 characters).',
  'controlCenter.stageRequirements.editRuleAria': 'Edit applicability rule',
  'controlCenter.stageRequirements.removeRule': 'Remove rule',
  'controlCenter.stageRequirements.addRule': 'Add applicability rule',
  'controlCenter.stageRequirements.requirementColumn': 'Requirement',
  'controlCenter.stageRequirements.dataPointColumn': 'Data point',
  'controlCenter.stageRequirements.applicabilityColumn': 'Applicability',
  'controlCenter.stageRequirements.notApplicable': 'Not applicable',
  'controlCenter.stageRequirements.required': 'Required',
  'controlCenter.stageRequirements.optional': 'Optional',
  'controlCenter.stageRequirements.recommended': 'Recommended',
  'controlCenter.stageRequirements.saveSuccess': 'Requirements saved',
  'controlCenter.stageRequirements.saveError': 'Failed to save requirements',
  'controlCenter.stageRequirements.empty':
    'No data points available for this stage. Add data points first, then return here to map them.',
  'controlCenter.stageRequirements.savePending': 'Unsaved changes',
  'controlCenter.stageRequirements.saveButton': 'Save requirements',
  'controlCenter.stageRequirements.discardButton': 'Discard changes',
  'controlCenter.stageRequirements.stagePicker': 'Stage',
  'controlCenter.stageRequirements.noStages': 'No stages configured for this product yet.',

  // JSON rule builder — used by stage requirements editor (and any future rule UIs)
  'controlCenter.ruleEditor.discriminator': 'Rule type',
  'controlCenter.ruleEditor.leaf': 'Single condition',
  'controlCenter.ruleEditor.all': 'All of (AND)',
  'controlCenter.ruleEditor.any': 'Any of (OR)',
  'controlCenter.ruleEditor.not': 'Not',
  'controlCenter.ruleEditor.field': 'Field',
  'controlCenter.ruleEditor.fieldPlaceholder': 'e.g. loanAmount',
  'controlCenter.ruleEditor.op': 'Operator',
  'controlCenter.ruleEditor.value': 'Value',
  'controlCenter.ruleEditor.valuesHelper': 'Press Enter to add another value',
  'controlCenter.ruleEditor.addRule': 'Add condition',
  'controlCenter.ruleEditor.removeRule': 'Remove',
  'controlCenter.ruleEditor.replaceRule': 'Replace',
  'controlCenter.ruleEditor.noChildren': 'No conditions yet — add at least one.',
  'controlCenter.ruleEditor.opEq': 'equals',
  'controlCenter.ruleEditor.opIn': 'is one of',
  'controlCenter.ruleEditor.opGt': 'greater than',
  'controlCenter.ruleEditor.opLt': 'less than',
  'controlCenter.ruleEditor.opGte': 'greater than or equal',
  'controlCenter.ruleEditor.opLte': 'less than or equal',
  'controlCenter.ruleEditor.opPresent': 'is set',
  // CREDIT MEMO / LOAN SUBMISSION ARTIFACT (Issue #1466)
  // ============================================================================
  'creditMemo.title': 'Credit Memo',
  'creditMemo.versionsTitle': 'Versions',
  'creditMemo.versionLabel': 'Version {version}',
  'creditMemo.emptyState': 'No credit memos for this loan yet. Click Create to start a draft.',
  'creditMemo.emptyHint': 'Select a memo on the left, or create the first one.',
  'creditMemo.createButton': 'Create',
  'creditMemo.saveDraftButton': 'Save draft',
  'creditMemo.submitButton': 'Submit for approval',
  'creditMemo.sectionPlaceholder': 'Type your narrative…',

  // Status labels (mirrors CreditMemoStatus enum).
  'creditMemo.statusDraft': 'Draft',
  'creditMemo.statusSubmitted': 'Submitted',
  'creditMemo.statusApproved': 'Approved',
  'creditMemo.statusRejected': 'Rejected',
  'creditMemo.statusArchived': 'Archived',

  // Narrative section labels (mirrors CreditMemoNarrativeSection enum).
  'creditMemo.section.borrowerSummary': 'Borrower summary',
  'creditMemo.section.propertySummary': 'Property summary',
  'creditMemo.section.financialSummary': 'Financial summary',
  'creditMemo.section.riskFactors': 'Risk factors',
  'creditMemo.section.mitigants': 'Mitigants',
  'creditMemo.section.recommendation': 'Recommendation',

  // Reject dialog.
  'creditMemo.rejectDialog.title': 'Reject credit memo',
  'creditMemo.rejectDialog.body':
    'Provide a reason — this is recorded in the audit log and visible to the underwriter when they revise the memo.',
  'creditMemo.rejectDialog.noteLabel': 'Reason',

  // Audit footer.
  'creditMemo.audit.submitted': 'Submitted by {actor} on {at}',
  'creditMemo.audit.approved': 'Approved by {actor} on {at}',
  'creditMemo.audit.rejected': 'Rejected by {actor} on {at}',
  'creditMemo.audit.note': 'Note: {note}',

  // Toasts (used by callers; the tab itself relies on optimistic invalidate).
  'creditMemo.toast.created': 'Credit memo created',
  'creditMemo.toast.updated': 'Credit memo saved',
  'creditMemo.toast.submitted': 'Credit memo submitted for approval',
  'creditMemo.toast.approved': 'Credit memo approved',
  'creditMemo.toast.rejected': 'Credit memo rejected',

  // Typed narratives header / meta (wave 4).
  'cases.creditMemo.headerMeta.generatedBy': 'Generated by {actor}',
  'cases.creditMemo.headerMeta.toolCalls': 'from {count} tool calls',
  'cases.creditMemo.headerMeta.policyChecks': 'and {count} policy checks',
  'cases.creditMemo.headerMeta.retrieved': 'retrieved {when}',
  'cases.creditMemo.headerMeta.notGenerated': 'not generated yet',

  // Typed narratives — empty / loading state.
  'cases.creditMemo.notGenerated.title': 'Credit memo not generated yet',
  'cases.creditMemo.notGenerated.body':
    'Astor will draft the memo once enough source documents are in. You can also start a manual draft.',
  'cases.creditMemo.notGenerated.cta': 'Start a draft',

  // Typed narratives — reasoning callout attribution.
  'cases.creditMemo.callout.astorReasoning': "Astor's reasoning",

  // Typed narratives — right rail.
  'cases.creditMemo.citations.title': 'CITATIONS',
  'cases.creditMemo.citations.empty': 'No citations yet.',
  'cases.creditMemo.notes.title': 'REVIEWER NOTES',
  'cases.creditMemo.notes.placeholder': 'Add a note for the committee…',

  // v3 — Credit Memo tab pixel-match strings (Issue #1749).
  // The v3 layout introduces a strict "Credit memo · draft" header strip,
  // a sub-meta row (last edit / unresolved / reading mode), section labels
  // prefixed with `§`, tool-call chips below the Astor reasoning callout,
  // and a signed footer attribution. Keep these labels short — they read as
  // mono caption strings in the rendered UI.
  'cases.creditMemoTab.title': 'Credit memo',
  'cases.creditMemoTab.status.draft': 'draft',
  'cases.creditMemoTab.status.submitted': 'submitted',
  'cases.creditMemoTab.status.approved': 'approved',
  'cases.creditMemoTab.status.rejected': 'rejected',
  'cases.creditMemoTab.status.archived': 'archived',
  'cases.creditMemoTab.versionChip': 'v{{version}}',
  // Confidence chip — `confidence` is a percent string formatted at the call site (e.g. `82%`).
  'cases.creditMemoTab.confidenceChip': 'conf {{confidence}}',
  'cases.creditMemoTab.headerHelp':
    'Generated by Astor from {toolCalls} tool calls and {policyChecks} policy checks. Hover any highlighted phrase to see its source.',
  'cases.creditMemoTab.subMeta.draftedBy': 'drafted by {{actor}}',
  'cases.creditMemoTab.subMeta.lastEdit': 'last edit {{when}}',
  'cases.creditMemoTab.subMeta.citations': '{{count}} citations',
  'cases.creditMemoTab.subMeta.unresolved': '{{count}} unresolved',
  'cases.creditMemoTab.subMeta.readingMode': 'Reading mode',
  // Title row above the prose body: "Memo — <case ref>". The case ref
  // fallback is the case id when no friendlier label is available.
  'cases.creditMemoTab.memoTitle': 'Memo — {{caseRef}}',
  // Section heading template — `§ {number} — {TITLE}`. Renders as h5 to
  // stay at-or-below the page-title ceiling per §11b2.
  'cases.creditMemoTab.sectionHeading': '§ {{number}} — {{title}}',
  // Action buttons (right-side of header strip).
  'cases.creditMemoTab.action.regenerate': 'Regenerate',
  'cases.creditMemoTab.action.exportPdf': 'Export PDF',
  'cases.creditMemoTab.action.submitToRisk': 'Submit to risk',
  // Footer signature ("— Astor, on behalf of OpsMatrix Capital").
  'cases.creditMemoTab.footer.signature': '— {{actor}}, on behalf of {{tenant}}. Case {{caseRef}}',
  'cases.creditMemoTab.footer.signatureFallback': '— Drafted by {{actor}}',
  // Right-rail panel headers.
  'cases.creditMemoTab.rail.citations.title': 'Citations',
  'cases.creditMemoTab.rail.notes.title': 'Reviewer notes',
  // Right-rail "Citations" rows.
  'cases.creditMemoTab.rail.citations.empty': 'No sources cited yet.',
  // Right-rail "Reviewer notes" empty/composer placeholder.
  'cases.creditMemoTab.rail.notes.empty': 'No reviewer notes yet.',
  'cases.creditMemoTab.rail.notes.composerPlaceholder': 'Add a note…',
  // ARIA labels.
  'cases.creditMemoTab.aria.header': 'Credit memo header',
  'cases.creditMemoTab.aria.body': 'Credit memo body',
  'cases.creditMemoTab.aria.citationsPanel': 'Citations panel',
  'cases.creditMemoTab.aria.notesPanel': 'Reviewer notes panel',

  // NOTIFICATION TEMPLATES — Loan Origination Workflow Cascade
  // ============================================================================
  // Issue #1483 PR 6 / epic #1460. Convention from PR #1491:
  //   notifications.<feature>.<event>.{subject,body}
  //
  // These keys mirror the Handlebars-rendered output of the matching
  // notification templates seeded in 27-notification-service. They live
  // here as the canonical translation source for non-English locales and
  // for any portal-side rendering that chooses to localize from i18n keys
  // rather than the dispatcher-rendered Notification row fields.
  //
  // Current business-portal inbox mapping (10-business-portal/src/lib/
  // notification-mapper.ts) reads `n.title`/`n.subject` and `n.message`/
  // `n.body` directly — i.e. the strings the dispatcher already wrote into
  // the Notification row at send time. These keys should therefore be
  // treated as translation/template mirrors unless and until a consumer is
  // wired to use them for preview rendering.
  //
  // Placeholders use the same {{variable}} names as the seed templates so a
  // simple Handlebars pass against the notification's variables payload
  // produces identical output to the dispatcher.

  // Credit Memo — submitted
  'notifications.creditMemo.submittedForReview.subject': 'Credit Memo Submitted - {{caseNumber}}',
  'notifications.creditMemo.submittedForReview.body':
    '{{submittedByName}} submitted a Credit Memo on {{caseNumber}} ({{borrowerName}}, ${{loanAmount}}). Tap to review.',

  // Credit Memo — approved
  'notifications.creditMemo.approved.subject': 'Credit Memo Approved - {{caseNumber}}',
  'notifications.creditMemo.approved.body':
    '{{caseNumber}} ({{borrowerName}}) was approved by {{approvedByName}} for ${{loanAmount}}.',

  // Credit Memo — rejected
  'notifications.creditMemo.rejected.subject': 'Credit Memo Rejected - {{caseNumber}}',
  'notifications.creditMemo.rejected.body':
    '{{caseNumber}} ({{borrowerName}}) was rejected by {{rejectedByName}}. Reason: {{rejectionReason}}',

  // Credit Memo — document superseded (in-app only)
  'notifications.creditMemo.documentSuperseded.subject': 'Approved Credit Memo Document Replaced',
  'notifications.creditMemo.documentSuperseded.body':
    '{{documentName}} on {{caseNumber}} (slot {{slotLabel}}) was superseded by v{{newVersion}}. The Credit Memo was already approved — please review the new version.',

  // Closing checklist — items missing near close (digest)
  'notifications.closing.itemsMissingNearClose.subject':
    '{{missingCount}} closing items missing - {{caseNumber}} closes in {{daysUntilClose}} days',
  'notifications.closing.itemsMissingNearClose.body':
    '{{caseNumber}} closes in {{daysUntilClose}} days with {{missingCount}} required items still incomplete.',

  // Closing checklist — vendor missing near close (digest, in-app only)
  'notifications.closing.vendorMissingNearClose.subject': 'Vendors Missing Near Close',
  'notifications.closing.vendorMissingNearClose.body':
    '{{caseNumber}} closes in {{daysUntilClose}} days with no assignment for: {{missingVendorRoles}}.',

  // Borrower tier — reclassified (in-app only)
  // previousTier and tierReason are optional; the body uses Handlebars
  // conditionals so first-classification + reasonless events render cleanly.
  'notifications.borrowerTier.reclassified.subject': 'Borrower Tier Reclassified',
  'notifications.borrowerTier.reclassified.body':
    '{{borrowerName}} on {{caseNumber}} {{#if previousTier}}reclassified Tier {{previousTier}} → Tier {{newTier}}{{else}}classified as Tier {{newTier}}{{/if}}.{{#if tierReason}} Reason: {{tierReason}}{{/if}}',

  // Release schedule — unit released
  'notifications.releaseSchedule.unitReleased.subject': 'Unit Released - {{caseNumber}} {{unitLabel}}',
  'notifications.releaseSchedule.unitReleased.body':
    '{{unitLabel}} on {{caseNumber}} released for ${{releasePrice}}. Open balance: ${{remainingOpenBalance}}.',
  // ORIGINATION UX PATTERNS (Issue #1471)
  //
  // Foundational UX components used across loan-origination workflow templates
  // (FieldShell, CompletionTracker, MissingItemsBanner, RuleEvaluatorTooltip,
  // WarningBand, AutoSaveIndicator, BlockedCta, EmptyState, etc.). These are
  // generic, surface-agnostic strings — page-specific copy lives elsewhere.
  // ============================================================================

  // FieldShell — inline state pill / helper text
  'ux.field.required': 'Required',
  'ux.field.requiredAriaLabel': 'This field is required',
  'ux.field.valid': 'Looks good',
  'ux.field.warning': 'Warning',
  'ux.field.error': 'Error',
  'ux.field.optional': 'Optional',

  // CompletionTracker — section progress
  'ux.completion.progress': '{{complete}} of {{total}} sections complete',
  'ux.completion.navAriaLabel': 'Section completion',
  'ux.completion.sectionComplete': 'Complete',
  'ux.completion.sectionPartial': 'In progress',
  'ux.completion.sectionIncomplete': 'Not started',
  'ux.completion.sectionWarning': 'Needs attention',

  // MissingItemsBanner — non-blocking missing-items announcement
  'ux.missingItems.singular': '{{count}} required item missing:',
  'ux.missingItems.plural': '{{count}} required items missing:',
  'ux.missingItems.dismiss': 'Dismiss',
  'ux.missingItems.allResolved': 'All required items complete',

  // RuleEvaluatorTooltip — explains conditional applicability
  'ux.rule.heading': 'When does this apply?',
  'ux.rule.matchedTrue': 'Currently applies',
  'ux.rule.matchedFalse': 'Does not currently apply',
  'ux.rule.matchedTrueDetail': 'Currently: {{trace}} → matches → required.',
  'ux.rule.matchedFalseDetail': 'Currently: {{trace}} → does not match.',
  'ux.rule.requiredWhen': 'Required when {{rule}}.',
  'ux.rule.alwaysRequired': 'Always required.',
  'ux.rule.neverRequired': 'Never required.',

  // ComputedField — read-only computed value
  'ux.computed.computing': 'Computing…',
  'ux.computed.unavailable': 'Unavailable',

  // WarningBand — soft warnings
  'ux.warning.dismiss': 'Dismiss',

  // AutoSaveIndicator
  'ux.autoSave.saved': 'Saved',
  'ux.autoSave.savedAt': 'Saved {{when}}',
  'ux.autoSave.saving': 'Saving…',
  'ux.autoSave.unsaved': 'Unsaved changes',
  'ux.autoSave.error': 'Save failed',
  'ux.autoSave.retry': 'Retry',
  'ux.autoSave.justNow': 'just now',
  'ux.autoSave.secondsAgo': '{{seconds}}s ago',
  'ux.autoSave.minutesAgo': '{{minutes}}m ago',

  // BlockedCta — disabled CTA with missing-items tooltip
  'ux.blockedCta.tooltipHeading': 'Complete these to continue:',
  'ux.blockedCta.tooltipFooter': 'Required to continue',
  // ADMIN PLATFORM DEFAULTS (Issue #1483 Gap 16)
  // Superadmin-only pages for editing canonical platform-default rows
  // (`businessId IS NULL`). Two pages:
  //   - /control-tower/closing-checklist-defaults
  //   - /control-tower/tier-rule-defaults
  // ============================================================================

  // Page headers (used by PageTemplate)
  'pageHeader.admin.controlTower.closingChecklistDefaults.title': 'Closing checklist defaults',
  'pageHeader.admin.controlTower.closingChecklistDefaults.description':
    'Edit the platform-default closing-stage data points and per-product requirement matrix that new tenants pick up automatically.',
  'pageHeader.admin.controlTower.tierRuleDefaults.title': 'Tier rule defaults',
  'pageHeader.admin.controlTower.tierRuleDefaults.description':
    'Edit the platform-default borrower-tier classification rules. New tenants pick up changes on first business creation.',

  // Navigation labels
  'navigation.platformDefaults': 'Platform defaults',
  'navigation.closingChecklistDefaults': 'Closing checklist',
  'navigation.tierRuleDefaults': 'Tier rules',
  'navigation.sensitiveKeys': 'Sensitive keys',

  // ────────────────────────────────────────────────────────────────────
  // Templates → Sensitive-key admin overrides (Issue #1531 EG3 Phase 7)
  // Per-template canonical-key (ssn / dob / credit_score) override
  // toggles managed by super-admin / platform-admin only. Reason text is
  // required (≥20 chars) and audit-logged with 7-year retention.
  // ────────────────────────────────────────────────────────────────────
  'templates.sensitiveKeys.title': 'Sensitive-key overrides',
  'templates.sensitiveKeys.description':
    'Per-template opt-out of platform sensitive canonical-key blocks (SSN, date of birth, credit score). Each toggle is audit-logged with the supplied reason for 7-year retention.',
  'templates.sensitiveKeys.scopeBanner.title': 'Compliance control',
  'templates.sensitiveKeys.scopeBanner.body':
    'Use this surface only when a tenant template legitimately requires a sensitive field (e.g. tax-form bindings). Every change flips the template to DRAFT and forces a re-publish.',
  'templates.sensitiveKeys.businessPickerLabel': 'Business',
  'templates.sensitiveKeys.businessPickerPlaceholder': 'Select a business…',
  'templates.sensitiveKeys.businessPickerEmpty': 'No businesses available',
  'templates.sensitiveKeys.templatePickerLabel': 'Template',
  'templates.sensitiveKeys.templatePickerPlaceholder': 'Select a template…',
  'templates.sensitiveKeys.templatePickerEmpty': 'This business has no templates yet',
  'templates.sensitiveKeys.selectBusinessFirst': 'Select a business to load its templates',
  'templates.sensitiveKeys.selectTemplateFirst': 'Select a template to view canonical-key bindings',
  'templates.sensitiveKeys.adhocNotice': 'Sensitive-key overrides do not apply to ADHOC templates',
  'templates.sensitiveKeys.noSensitiveKeys':
    'This template kind has no sensitive canonical keys defined by the platform — nothing to override.',
  'templates.sensitiveKeys.tableHeaderKey': 'Canonical key',
  'templates.sensitiveKeys.tableHeaderStatus': 'Status',
  'templates.sensitiveKeys.tableHeaderActions': 'Actions',
  'templates.sensitiveKeys.statusBlocked': 'Platform-blocked',
  'templates.sensitiveKeys.statusOverridden': 'Override active',
  'templates.sensitiveKeys.markSensitive': 'Allow override',
  'templates.sensitiveKeys.removeOverride': 'Remove override',
  'templates.sensitiveKeys.dialogAddTitle': 'Allow sensitive-key override',
  'templates.sensitiveKeys.dialogAddBody':
    'Allowing this canonical key bypasses the platform PII guard for this template only. The reason text is retained for 7 years and surfaces in audit reports.',
  'templates.sensitiveKeys.dialogReasonLabel': 'Reason',
  'templates.sensitiveKeys.dialogReasonPlaceholder':
    'Explain the legal / compliance basis for allowing this sensitive field (≥20 characters)',
  'templates.sensitiveKeys.dialogReasonHelper': '{{count}} / 500 characters (minimum 20)',
  'templates.sensitiveKeys.dialogReasonError': 'Reason must be at least 20 characters for audit retention',
  'templates.sensitiveKeys.dialogConfirm': 'Allow override',
  'templates.sensitiveKeys.dialogCancel': 'Cancel',
  'templates.sensitiveKeys.dialogRemoveTitle': 'Remove sensitive-key override',
  'templates.sensitiveKeys.dialogRemoveBody':
    'Removing the override re-instates the platform PII block for this canonical key. The template will flip to DRAFT and require a re-publish.',
  'templates.sensitiveKeys.dialogRemoveConfirm': 'Remove override',
  'templates.sensitiveKeys.toastAdded': 'Override added — template flipped to DRAFT',
  'templates.sensitiveKeys.toastRemoved': 'Override removed — template flipped to DRAFT',
  'templates.sensitiveKeys.errorAddFailed': 'Failed to add override',
  'templates.sensitiveKeys.errorRemoveFailed': 'Failed to remove override',
  'templates.sensitiveKeys.auditTitle': 'Recent override changes',
  'templates.sensitiveKeys.auditEmpty': 'No override history for this template',
  'templates.sensitiveKeys.auditPending':
    'Per-template override audit history will appear here once the audit-service surfaces a per-business filter (TODO).',
  'templates.sensitiveKeys.refresh': 'Refresh',

  // Shared scope banner (sits above each page so it's clear we're not editing a tenant)
  'admin.platformDefaults.scopeBanner.title': 'Platform-default scope',
  'admin.platformDefaults.scopeBanner.body':
    'Changes here update the canonical defaults shipped to every new tenant via seedLoanProducts(businessId). Existing tenants are unaffected unless explicitly migrated via the data-migration path.',

  // ────────────────────────────────────────────────────────────────────
  // Closing checklist defaults page
  // ────────────────────────────────────────────────────────────────────
  'admin.platformDefaults.closingChecklist.title': 'Closing checklist defaults',
  'admin.platformDefaults.closingChecklist.subtitle':
    'Document slots and per-product requirements applied at the closing stage.',
  'admin.platformDefaults.closingChecklist.slotsHeading': 'Document slots (DOCUMENT_SLOT)',
  'admin.platformDefaults.closingChecklist.slotsHelper':
    'Canonical slots seeded by #1480 Phase C. Edits apply to new tenants automatically.',
  'admin.platformDefaults.closingChecklist.requirementsHeading': 'Closing-stage requirements',
  'admin.platformDefaults.closingChecklist.requirementsHelper':
    'Per-product matrix of which slots are required, recommended, optional, or not applicable at the closing stage.',
  'admin.platformDefaults.closingChecklist.searchPlaceholder': 'Search by canonical key or display name...',
  'admin.platformDefaults.closingChecklist.filterCategory': 'Category',
  'admin.platformDefaults.closingChecklist.filterAll': 'All',
  'admin.platformDefaults.closingChecklist.filterArchived': 'Status',
  'admin.platformDefaults.closingChecklist.statusActive': 'Active',
  'admin.platformDefaults.closingChecklist.statusArchived': 'Archived',
  'admin.platformDefaults.closingChecklist.addSlot': 'Add platform-default slot',
  'admin.platformDefaults.closingChecklist.editSlotTitle': 'Edit platform-default slot',
  'admin.platformDefaults.closingChecklist.createSlotTitle': 'Add platform-default slot',
  'admin.platformDefaults.closingChecklist.fieldDisplayName': 'Display name',
  'admin.platformDefaults.closingChecklist.fieldCanonicalKey': 'Canonical key',
  'admin.platformDefaults.closingChecklist.fieldCanonicalKeyHelper':
    'Stable machine identifier referenced by integrations. Cannot be changed after creation.',
  'admin.platformDefaults.closingChecklist.fieldCategory': 'Category',
  'admin.platformDefaults.closingChecklist.fieldValueType': 'Value type',
  'admin.platformDefaults.closingChecklist.fieldIsActive': 'Active',
  'admin.platformDefaults.closingChecklist.fieldCaseField': 'Case field',
  'admin.platformDefaults.closingChecklist.fieldCaseFieldNone': '—',
  'admin.platformDefaults.closingChecklist.fieldCaseFieldHelper':
    'Optional binding to a top-level Case column for survivorship.',
  'admin.platformDefaults.closingChecklist.columnDisplayName': 'Display name',
  'admin.platformDefaults.closingChecklist.columnCanonicalKey': 'Canonical key',
  'admin.platformDefaults.closingChecklist.columnCategory': 'Category',
  'admin.platformDefaults.closingChecklist.columnValueType': 'Value type',
  'admin.platformDefaults.closingChecklist.columnTenantsUsing': 'Tenants using',
  'admin.platformDefaults.closingChecklist.columnStatus': 'Status',
  'admin.platformDefaults.closingChecklist.tenantsUsingPending': '—',
  'admin.platformDefaults.closingChecklist.archiveConfirmTitle': 'Archive platform-default slot?',
  'admin.platformDefaults.closingChecklist.archiveConfirmBody':
    'New tenants will no longer pick up this slot. Existing tenants keep their forked overrides. This action is reversible — the slot can be restored from the archived view.',
  'admin.platformDefaults.closingChecklist.tenantImpactPreviewTitle': 'Tenant impact preview',
  'admin.platformDefaults.closingChecklist.tenantImpactPreviewBody':
    'This change will affect {affected} tenants who have not forked the default. {forked} tenants have already forked this slot and will be unaffected.',
  'admin.platformDefaults.closingChecklist.tenantImpactPreviewPending':
    'Tenant-impact counts are not yet wired. Once Service 29 exposes a CountTenantsUsingDefault RPC, the preview will appear here.',
  'admin.platformDefaults.closingChecklist.empty.title': 'No platform-default closing-checklist slots yet',
  'admin.platformDefaults.closingChecklist.empty.subtitle':
    'Add the first slot or wait until #1480 Phase C seed data lands.',
  'admin.platformDefaults.closingChecklist.toast.created': 'Platform-default slot created',
  'admin.platformDefaults.closingChecklist.toast.updated': 'Platform-default slot updated',
  'admin.platformDefaults.closingChecklist.toast.archived': 'Platform-default slot archived',
  'admin.platformDefaults.closingChecklist.toast.matrixSaved': 'Closing-stage requirements saved',
  'admin.platformDefaults.closingChecklist.toast.error': 'Could not save platform-default: {message}',

  // Closing-stage requirement matrix labels (mirror loanStageRequirements.*
  // for parity with the business-portal version)
  'admin.platformDefaults.closingChecklist.requirementRequired': 'Required',
  'admin.platformDefaults.closingChecklist.requirementRecommended': 'Recommended',
  'admin.platformDefaults.closingChecklist.requirementOptional': 'Optional',
  'admin.platformDefaults.closingChecklist.requirementNone': 'Not set',
  'admin.platformDefaults.closingChecklist.requirementShortRequired': 'Req',
  'admin.platformDefaults.closingChecklist.requirementShortRecommended': 'Rec',
  'admin.platformDefaults.closingChecklist.requirementShortOptional': 'Opt',
  'admin.platformDefaults.closingChecklist.cellTooltipCycle': 'Click to cycle requirement level',
  'admin.platformDefaults.closingChecklist.legend': 'Requirement levels',
  'admin.platformDefaults.closingChecklist.legendRequired': 'Must be present at closing.',
  'admin.platformDefaults.closingChecklist.legendRecommended': 'Should be present at closing.',
  'admin.platformDefaults.closingChecklist.legendOptional': 'Helpful but not blocking.',
  'admin.platformDefaults.closingChecklist.legendNone': 'Slot not configured for this product.',
  'admin.platformDefaults.closingChecklist.unsavedChanges': 'Unsaved changes',
  'admin.platformDefaults.closingChecklist.saveButton': 'Save changes',
  'admin.platformDefaults.closingChecklist.savingButton': 'Saving...',
  'admin.platformDefaults.closingChecklist.discardButton': 'Discard',
  'admin.platformDefaults.closingChecklist.productFilter': 'Product',
  'admin.platformDefaults.closingChecklist.productAll': 'All products',
  'admin.platformDefaults.closingChecklist.noClosingStage':
    'No stage named "CLOSING" exists in the platform-default scope. Seed it first.',

  // ────────────────────────────────────────────────────────────────────
  // Tier rule defaults page
  // ────────────────────────────────────────────────────────────────────
  'admin.platformDefaults.tierRules.title': 'Tier rule defaults',
  'admin.platformDefaults.tierRules.subtitle':
    'Borrower tier classification rules. Tiers are the canonical numeric buckets — Tier 1 (best) through Tier 3 (default). Rules are evaluated in priority order; the lowest matching tier wins.',
  'admin.platformDefaults.tierRules.addButton': 'Add platform-default tier rule',
  'admin.platformDefaults.tierRules.editTitle': 'Edit platform-default tier rule',
  'admin.platformDefaults.tierRules.createTitle': 'Add platform-default tier rule',
  'admin.platformDefaults.tierRules.columnTier': 'Tier',
  'admin.platformDefaults.tierRules.columnPriority': 'Priority',
  'admin.platformDefaults.tierRules.columnCreditScoreMin': 'Credit score (min)',
  'admin.platformDefaults.tierRules.columnCreditScoreMax': 'Credit score (max)',
  'admin.platformDefaults.tierRules.columnExitCount36mo': 'Exits (36mo)',
  'admin.platformDefaults.tierRules.columnLiquidReserves': 'Liquid reserves (min)',
  'admin.platformDefaults.tierRules.columnStatus': 'Status',
  'admin.platformDefaults.tierRules.columnActions': '',
  'admin.platformDefaults.tierRules.statusActive': 'Active',
  'admin.platformDefaults.tierRules.statusArchived': 'Archived',
  'admin.platformDefaults.tierRules.dragHandleAria': 'Drag to reorder priority',
  'admin.platformDefaults.tierRules.moveUpAria': 'Move rule up',
  'admin.platformDefaults.tierRules.moveDownAria': 'Move rule down',
  'admin.platformDefaults.tierRules.tierLabel': 'Tier {tier}',
  'admin.platformDefaults.tierRules.fieldTier': 'Tier',
  'admin.platformDefaults.tierRules.fieldTierHelper':
    'Canonical numeric bucket — Tier 1 (best) / Tier 2 / Tier 3 (default).',
  'admin.platformDefaults.tierRules.fieldPriority': 'Priority',
  'admin.platformDefaults.tierRules.fieldPriorityHelper': 'Lower number = evaluated first.',
  'admin.platformDefaults.tierRules.fieldCreditScoreMin': 'Credit score minimum',
  'admin.platformDefaults.tierRules.fieldCreditScoreMax': 'Credit score maximum',
  'admin.platformDefaults.tierRules.fieldExitCount36mo': 'Successful exits in last 36 months (min)',
  'admin.platformDefaults.tierRules.fieldLiquidReserves': 'Liquid reserves required',
  'admin.platformDefaults.tierRules.fieldIsActive': 'Active',
  'admin.platformDefaults.tierRules.empty.title': 'No platform-default tier rules yet',
  'admin.platformDefaults.tierRules.empty.subtitle':
    'Add the first rule or wait until #1469 BorrowerTierRule classifier lands.',
  'admin.platformDefaults.tierRules.deleteConfirmTitle': 'Delete platform-default tier rule?',
  'admin.platformDefaults.tierRules.deleteConfirmBody':
    'New tenants will no longer receive this rule. This action is permanent — there is no archived view for tier rules.',
  'admin.platformDefaults.tierRules.toast.created': 'Tier rule created',
  'admin.platformDefaults.tierRules.toast.updated': 'Tier rule updated',
  'admin.platformDefaults.tierRules.toast.deleted': 'Tier rule deleted',
  'admin.platformDefaults.tierRules.toast.reordered': 'Priorities updated',
  'admin.platformDefaults.tierRules.toast.error': 'Could not save tier rule: {message}',

  // Stage-requirements applicability badge + edit panel (Issue #1480 Phase D2b)
  'controlCenter.stageRequirements.ruleBadge.alwaysApplies': 'Always applies',
  'controlCenter.stageRequirements.ruleBadge.conditional': 'Conditional',
  'controlCenter.stageRequirements.ruleBadge.editAria': 'Edit applicability rule for {{dataPoint}} in {{stage}}',
  'controlCenter.stageRequirements.editRule.dialogTitle': 'Applicability rule',
  'controlCenter.stageRequirements.editRule.dialogSubtitle':
    'Limit when this requirement applies. Leave the rule empty to apply it to every case.',
  'controlCenter.stageRequirements.editRule.contextLabel': 'Editing {{dataPoint}} in {{stage}}',
  'controlCenter.stageRequirements.editRule.applicabilityNoteLabel': 'Applicability note',
  'controlCenter.stageRequirements.editRule.applicabilityNotePlaceholder':
    'e.g. Required only when scope amount exceeds $250k',
  'controlCenter.stageRequirements.editRule.applicabilityNoteHelper':
    'Optional rationale shown to closers (max 280 characters).',
  'controlCenter.stageRequirements.editRule.applicabilityNoteHelperOverflow':
    'Applicability note is over the 280-character limit — trim it before saving.',
  'controlCenter.stageRequirements.editRule.saveButton': 'Save rule',
  'controlCenter.stageRequirements.editRule.cancelButton': 'Cancel',
  'controlCenter.stageRequirements.editRule.saveSuccess': 'Applicability rule saved',
  'controlCenter.stageRequirements.editRule.saveError': 'Failed to save applicability rule: {{message}}',
  'controlCenter.stageRequirements.editRule.previewDeferred':
    'Live "matches X of Y cases" preview is coming in a follow-up.',
  'controlCenter.stageRequirements.editRule.invalidRule':
    'Applicability rule is invalid: {{message}}. Adjust the rule and try again.',
  'controlCenter.stageRequirements.malformedRulesBanner':
    '{{count}} requirement(s) have a malformed applicability rule from a previous schema version. Those cells now show "Always applies" — open them to set a fresh rule, then save.',

  // ============================================================================
  // TERM SHEETS — REDESIGN NAMESPACE (Issue #1531, Phase 0 Wave 1)
  // ----------------------------------------------------------------------------
  // New canonical `termsheets.*` keys for the comprehensive redesign. The
  // legacy `admin.templates.termsheets.*`, `esign.termSheet.*`, and
  // `business.loans.termsheets.*` keys above stay in place during Phase 0 —
  // Wave 2 flips component imports to these new keys; Phase 5 retires
  // anything left orphaned. Sentence case throughout to match the editor
  // and list pages introduced in the redesign plan §3.11 / §6.6.
  // ============================================================================
  'termsheets.pageTitle': 'Term sheets',

  // Editor — surfaces the create/edit term sheet template page (admin portal,
  // §3.11 plan). Wave 2 will flip TermsheetTemplateEditor / SectionEditor /
  // BrandingEditor / SignerPolicyBuilder imports to these keys.
  'termsheets.editor.title': 'Edit term sheet template',
  'termsheets.editor.sectionsTitle': 'Sections',
  'termsheets.editor.brandingTitle': 'Branding',
  'termsheets.editor.signerPolicyTitle': 'Signer policy',
  'termsheets.editor.handlebarsHint': 'Use {{variable}} to merge case attributes.',
  'termsheets.editor.saveCta': 'Save template',
  'termsheets.editor.cancelCta': 'Cancel',
  'termsheets.editor.duplicateCta': 'Duplicate',
  'termsheets.editor.archiveCta': 'Archive',

  // Editor — Phase 1 business-portal authoring keys (Issue #1531 Phase 1).
  // The business-portal mirror page at /control-center/document-templates/[id]/edit
  // reuses TermsheetTemplateEditor; these keys cover navigation, save/discard
  // chrome, error states, and feature gating that the admin pages already
  // express under `admin.templates.termsheets.*`. Sentence case throughout.
  'termsheets.editor.backToLibrary': 'Back to library',
  'termsheets.editor.saving': 'Saving…',
  'termsheets.editor.savedAt': 'Saved {{time}}',
  'termsheets.editor.unsavedWarning': 'You have unsaved changes. Leave anyway?',
  'termsheets.editor.discardConfirm.title': 'Discard changes?',
  'termsheets.editor.discardConfirm.description': 'Unsaved changes will be lost.',
  'termsheets.editor.discardConfirm.keep': 'Keep editing',
  'termsheets.editor.discardConfirm.confirm': 'Discard',
  'termsheets.editor.notFound': 'Template not found',
  'termsheets.editor.notFoundDescription': "This term sheet template doesn't exist or has been archived.",
  'termsheets.editor.permissionDenied': "You don't have permission to edit this template.",
  'termsheets.editor.featureDisabled': 'Term sheet editing is not enabled for your environment.',
  'termsheets.editor.loading': 'Loading template…',
  'termsheets.editor.loadFailed': 'Failed to load template. Please try again.',
  'termsheets.editor.saveFailed': 'Failed to save template.',

  // List — surfaces the term-sheet templates list page (admin portal).
  'termsheets.list.empty': 'No term sheet templates yet.',
  'termsheets.list.search': 'Search templates',
  'termsheets.list.newTemplate': 'New template',
  'termsheets.list.systemBadge': 'System',
  'termsheets.list.businessBadge': 'Custom',

  // List — Phase 1 business-portal create-from-scratch panel (Issue #1531 Phase 1).
  // Mirrors the admin "Create from scratch" flow at admin.templates.termsheets.cta.create
  // but presents the inline create panel (SplitPanel) before deep-linking
  // into the editor route at /[id]/edit.
  'termsheets.list.createAuthored': 'New term sheet template',
  'termsheets.list.createPanel.title': 'Create term sheet template',
  'termsheets.list.createPanel.nameLabel': 'Template name',
  'termsheets.list.createPanel.namePlaceholder': 'e.g. Bridge loan term sheet',
  'termsheets.list.createPanel.descriptionLabel': 'Description (optional)',
  'termsheets.list.createPanel.descriptionPlaceholder': 'What is this template for?',
  'termsheets.list.createPanel.cancelCta': 'Cancel',
  'termsheets.list.createPanel.createCta': 'Create and edit',

  // ----------------------------------------------------------------------------
  // Designer — Phase 2 PR B (Issue #1531 §6.4 / §6.6).
  // ContextRail (canonical-key picker) + MappingPanel (DocuSeal field UUID ↔
  // canonical-key bindings) wrapped around the DocuSeal iframe editor.
  // Sentence case throughout to match the editor + list pages above.
  // ----------------------------------------------------------------------------
  'termsheets.designer.title': 'Term sheet designer',
  'termsheets.designer.contextRail.title': 'Loan attributes',
  'termsheets.designer.contextRail.titlePartnership': 'Partner attributes',
  'termsheets.designer.contextRail.search': 'Search attributes',
  'termsheets.designer.contextRail.empty': 'No attributes match your search.',
  'termsheets.designer.contextRail.bound': '{{count}} bound',
  'termsheets.designer.contextRail.unbound': 'Unbound',
  'termsheets.designer.contextRail.bindField': 'Bind to field',
  'termsheets.designer.contextRail.bindAnother': 'Bind another',
  'termsheets.designer.contextRail.canonicalKey': 'Canonical key: {{key}}',
  'termsheets.designer.contextRail.valueType.currency': 'Currency',
  'termsheets.designer.contextRail.valueType.percent': 'Percent',
  'termsheets.designer.contextRail.valueType.date': 'Date',
  'termsheets.designer.contextRail.valueType.string': 'Text',
  'termsheets.designer.contextRail.valueType.number': 'Number',
  'termsheets.designer.contextRail.valueType.boolean': 'Yes / No',
  'termsheets.designer.mappingPanel.title': 'Field bindings',
  'termsheets.designer.mappingPanel.empty': 'No fields placed yet. Use the editor to drag a field onto the document.',
  'termsheets.designer.mappingPanel.fieldName': 'Field',
  'termsheets.designer.mappingPanel.canonicalKey': 'Canonical key',
  'termsheets.designer.mappingPanel.transform': 'Transform',
  'termsheets.designer.mappingPanel.transform.none': 'No transform',
  'termsheets.designer.mappingPanel.transform.currency': 'Format as currency',
  'termsheets.designer.mappingPanel.transform.percent': 'Format as percent',
  'termsheets.designer.mappingPanel.transform.date': 'Format as date',
  'termsheets.designer.mappingPanel.transform.defaultValue': 'Use a fixed default',
  'termsheets.designer.mappingPanel.required': 'Required',
  'termsheets.designer.mappingPanel.defaultValue': 'Default value',
  'termsheets.designer.mappingPanel.save': 'Save binding',
  'termsheets.designer.mappingPanel.delete': 'Remove binding',
  'termsheets.designer.mappingPanel.showOnDocument': 'Show on document',
  'termsheets.designer.mappingPanel.staleField': 'This field is no longer in the document — remove the binding.',
  'termsheets.designer.mappingPanel.deleteConfirmBody':
    'Are you sure you want to remove this field binding? The field on the document will revert to no prefill.',
  'termsheets.designer.mappingPanel.unmappedWarning': '{{count}} placed field(s) have no binding.',
  'termsheets.designer.toast.bindSuccess': 'Field binding saved.',
  'termsheets.designer.toast.bindFailed': "Couldn't save the binding. Try again.",
  'termsheets.designer.toast.deleteSuccess': 'Binding removed.',
  'termsheets.designer.toast.deleteFailed': "Couldn't remove the binding.",
  'termsheets.designer.toast.unbindSuccess': 'Field unbound.',
  // Bind picker (Issue #1531 Phase 2 PR E follow-up) — opens when the lender
  // clicks "Bind to field" on a canonical-key row in ContextRail. Lists every
  // placed DocuSeal field that doesn't yet have a binding for the chosen key
  // so they can pick which placement gets the value.
  'termsheets.designer.bindPicker.title': 'Choose a field to bind',
  'termsheets.designer.bindPicker.description':
    'Pick a placed field on the document to bind to "{{key}}". The next time this template is sent, the field will be pre-filled with the current value.',
  'termsheets.designer.bindPicker.empty':
    'No placed fields available. Drag a field onto the document in the editor first.',
  'termsheets.designer.bindPicker.legacyEmpty':
    'No DocuSeal field UUIDs were returned. Save the template in DocuSeal at least once so each field has a stable identifier, then try again.',
  'termsheets.designer.bindPicker.unnamedField': 'Unnamed field',
  'termsheets.designer.bindPicker.alreadyBound': 'Already bound',
  'termsheets.designer.bindPicker.boundTo': 'Bound to {{name}}',
  'termsheets.designer.bindPicker.pick': 'Bind this field',
  'termsheets.designer.bindPicker.replace': 'Replace binding',
  'termsheets.designer.bindPicker.confirmReplace':
    'This field is currently bound to "{{current}}". Rebinding it to "{{next}}" will remove the previous binding. Continue?',
  'termsheets.designer.bindPicker.cancel': 'Cancel',
  // Focused-binding panel — surfaces the binding for a placed field clicked
  // in the iframe. Used by FocusedBindingPanel + the picker pivot.
  'termsheets.designer.focused.title.bound': 'Selected binding',
  'termsheets.designer.focused.title.unbound': 'Selected field',
  'termsheets.designer.focused.unboundDescription': 'This field is not bound to an attribute yet.',
  'termsheets.designer.focused.bindThisField': 'Bind to attribute',
  'termsheets.designer.focused.boundFieldLabel': 'Field',
  'termsheets.designer.focused.staleWarning': 'This binding’s field is no longer on the document.',
  'termsheets.designer.focused.close': 'Close',
  'termsheets.designer.focused.pickAttribute.title': 'Bind field to attribute',
  'termsheets.designer.focused.pickAttribute.search': 'Search attributes',
  'termsheets.designer.focused.pickAttribute.empty': 'No attributes match',
  'termsheets.designer.focused.fieldRemoved': 'The selected field was removed from the document.',
  'termsheets.designer.focused.clickToBind': 'Click to bind',

  // ============================================================================
  // ENUMS — TermSheetStatus (Issue #1531, Phase 0 Wave 1)
  // ----------------------------------------------------------------------------
  // Localized labels for the `TermSheetStatus` const-as-enum in
  // `1-shared-types/src/dtos/term-sheet.dto.ts`. EnumName mirrors the TS
  // identifier exactly per the `enums.<EnumName>.<VALUE>` convention in
  // .claude/rules/portals.md (Feature-key namespacing, Issue #1483 Gap 12).
  // VALUE keys are SCREAMING_SNAKE_CASE matching the TS member names so
  // callers can do `t(\`enums.TermSheetStatus.${status.toUpperCase()}\`)`.
  // ============================================================================
  'enums.TermSheetStatus.DRAFT': 'Draft',
  'enums.TermSheetStatus.GENERATING': 'Generating',
  'enums.TermSheetStatus.GENERATED': 'Generated',
  'enums.TermSheetStatus.SENT': 'Sent',
  'enums.TermSheetStatus.VIEWED': 'Viewed',
  'enums.TermSheetStatus.ACCEPTED': 'Accepted',
  'enums.TermSheetStatus.REJECTED': 'Rejected',
  'enums.TermSheetStatus.EXPIRED': 'Expired',
  'enums.TermSheetStatus.FAILED': 'Failed',
  'enums.TermSheetStatus.SIGNING_REQUESTED': 'Signing requested',
  'enums.TermSheetStatus.SIGNED': 'Signed',

  // ============================================================================
  // BUSINESS PORTAL — SIZER POLICY (Control Center)
  // Per-business twin of /admin/sizer-rules. Tier caps + rule adjustments
  // editor scoped to a single business; the platform admin manages the
  // platform defaults that businesses can override here. Backend tRPC routes
  // live in `loanSizer.admin.*` and enforce `businessId` from JWT.
  // CASL-gated by Permission.loanSizer.manageRules.
  // ============================================================================

  'sizerPolicy.title': 'Sizer Policy',
  'sizerPolicy.description':
    'Tier caps and post-rule adjustments used by the loan sizer for your business. Defaults inherit from the platform — override any tier or rule to fit your underwriting policy.',

  'sizerPolicy.forbidden.title': 'Access denied',
  'sizerPolicy.forbidden.message':
    'You do not have permission to manage sizer policy. Contact a workspace administrator if you need access.',

  // Tabs
  'sizerPolicy.tab.tierCaps': 'Tier caps',
  'sizerPolicy.tab.tierCaps.description':
    'Each loan product has tiered leverage caps (Initial LTC, Total LTC, ARLTV) along with a max-loan ceiling, max term, and FICO floor. Higher tiers typically offer more leverage at higher FICO floors.',
  'sizerPolicy.tab.adjustments': 'Rule adjustments',
  'sizerPolicy.tab.adjustments.description':
    'Conditional rules that haircut leverage or raise flags when borrower / property attributes match. Default rules apply when their condition is true.',

  // Loan-type group headings
  'sizerPolicy.loanType.FIX_FLIP': 'Fix & Flip',
  'sizerPolicy.loanType.GROUND_UP': 'Ground Up',
  'sizerPolicy.loanType.BRIDGE': 'Bridge',
  'sizerPolicy.loanType.ALL': 'All loan types',

  // Profile table columns
  'sizerPolicy.column.tier': 'Tier',
  'sizerPolicy.column.label': 'Label',
  'sizerPolicy.column.initialLtc': 'Initial LTC',
  'sizerPolicy.column.totalLtc': 'Total LTC',
  'sizerPolicy.column.arltv': 'ARLTV',
  'sizerPolicy.column.maxLoan': 'Max loan',
  'sizerPolicy.column.maxTerm': 'Max term',
  'sizerPolicy.column.ficoFloor': 'FICO floor',
  'sizerPolicy.column.effective': 'Effective',
  'sizerPolicy.column.active': 'Active',
  'sizerPolicy.column.actions': 'Actions',

  // Adjustment table columns
  'sizerPolicy.adjustment.column.key': 'Key',
  'sizerPolicy.adjustment.column.loanType': 'Loan type',
  'sizerPolicy.adjustment.column.description': 'Description',
  'sizerPolicy.adjustment.column.haircut': 'Haircut',
  'sizerPolicy.adjustment.column.flagCode': 'Flag code',
  'sizerPolicy.adjustment.column.flagSeverity': 'Severity',

  // Adjustment section subtitles (per loan-type group on the adjustments tab)
  'sizerPolicy.adjustment.section.all.subtitle':
    'These adjustments apply to every loan type. Most platform defaults (e.g. foreign-national haircut) live here.',
  'sizerPolicy.adjustment.section.scoped.subtitle': 'These adjustments only apply to this loan product.',

  // Profile editor form labels
  'sizerPolicy.form.profile.title.create': 'New tier',
  'sizerPolicy.form.profile.title.edit': 'Edit tier',
  'sizerPolicy.form.loanType': 'Loan type',
  'sizerPolicy.form.tier': 'Tier',
  'sizerPolicy.form.tierHelp': 'Positive integer. Higher tiers typically offer more leverage.',
  'sizerPolicy.form.initialLtcCap': 'Initial LTC cap (%)',
  'sizerPolicy.form.totalLtcCap': 'Total LTC cap (%)',
  'sizerPolicy.form.arltvCap': 'ARLTV cap (%)',
  'sizerPolicy.form.maxLoan': 'Max loan',
  'sizerPolicy.form.maxLoanHelp': 'Optional — leave blank for no tier max.',
  'sizerPolicy.form.maxLoanEmpty': 'No tier max',
  'sizerPolicy.form.maxTermMonths': 'Max term (months)',
  'sizerPolicy.form.ficoFloor': 'FICO floor',
  'sizerPolicy.form.ficoFloorHelp': 'Minimum borrower FICO required for this tier (300–850).',
  'sizerPolicy.form.label': 'Label',
  'sizerPolicy.form.labelPlaceholder': 'e.g. Premier — 700+ FICO',
  'sizerPolicy.form.effectiveFrom': 'Effective from',
  'sizerPolicy.form.effectiveTo': 'Effective to',
  'sizerPolicy.form.effectiveToHelp': 'Optional — leave blank for open-ended.',
  'sizerPolicy.form.isActive': 'Active',

  // Adjustment editor form labels
  'sizerPolicy.form.adjustment.title.create': 'New adjustment',
  'sizerPolicy.form.adjustment.title.edit': 'Edit adjustment',
  'sizerPolicy.form.key': 'Key',
  'sizerPolicy.form.keyPlaceholder': 'e.g. FOREIGN_NATIONAL_HAIRCUT',
  'sizerPolicy.form.keyHelp': 'Unique identifier for this rule (max 64 chars).',
  'sizerPolicy.form.description': 'Description',
  'sizerPolicy.form.condition': 'Condition (JSON)',
  'sizerPolicy.form.conditionHelp':
    'JSON object expressing the condition. Example: {"foreignNational": true} or {"fico": {"lt": 680}}.',
  'sizerPolicy.form.conditionInvalid': 'Invalid JSON',
  'sizerPolicy.form.leverageHaircut': 'Leverage haircut (%)',
  'sizerPolicy.form.leverageHaircutHelp': 'Percentage to subtract from leverage caps when condition matches. Optional.',
  'sizerPolicy.form.flagCode': 'Flag code',
  'sizerPolicy.form.flagSeverity': 'Flag severity',
  'sizerPolicy.form.flagSeverity.INFO': 'Info',
  'sizerPolicy.form.flagSeverity.WARN': 'Warn',
  'sizerPolicy.form.flagSeverity.EXCEPTION': 'Exception',
  'sizerPolicy.form.flagMessage': 'Flag message',

  // Validation messages
  'sizerPolicy.validation.tierRequired': 'Tier is required',
  'sizerPolicy.validation.tierPositive': 'Tier must be a positive integer',
  'sizerPolicy.validation.percentRange': 'Must be between 0 and 100',
  'sizerPolicy.validation.ficoRange': 'FICO must be between 300 and 850',
  'sizerPolicy.validation.maxTermPositive': 'Max term must be a positive integer',
  'sizerPolicy.validation.maxLoanInvalid': 'Max loan must be a non-negative number',
  'sizerPolicy.validation.effectiveFromRequired': 'Effective from is required',
  'sizerPolicy.validation.keyRequired': 'Key is required',
  'sizerPolicy.validation.conditionRequired': 'Condition (JSON object) is required',

  // Empty states
  'sizerPolicy.empty.title': 'No tier caps configured',
  'sizerPolicy.empty.description':
    'Tier caps inherit from the platform until you override them. Add your first tier to start customizing the sizer for your business.',
  'sizerPolicy.empty.profilesForType': 'No tier caps for this loan type. Inherits from the platform default.',
  'sizerPolicy.empty.adjustments': 'No rule adjustments configured for this scope.',

  // Actions
  'sizerPolicy.action.addTier': 'Add tier',
  'sizerPolicy.action.editTier': 'Edit tier',
  'sizerPolicy.action.deleteTier': 'Delete tier',
  'sizerPolicy.action.addFirstTier': 'Add first tier',
  'sizerPolicy.action.addAdjustment': 'Add adjustment',
  'sizerPolicy.action.editAdjustment': 'Edit adjustment',
  'sizerPolicy.action.deleteAdjustment': 'Delete adjustment',
  'sizerPolicy.action.delete': 'Delete',
  'sizerPolicy.action.save': 'Save',
  'sizerPolicy.action.cancel': 'Cancel',

  // Confirmation + toasts
  'sizerPolicy.confirm.deleteProfile': 'Delete tier {{tier}} for {{loanType}}? This cannot be undone.',
  'sizerPolicy.confirm.deleteAdjustment': 'Delete adjustment "{{key}}"? This cannot be undone.',
  'sizerPolicy.toast.profileSaved': 'Tier saved',
  'sizerPolicy.toast.profileDeleted': 'Tier deleted',
  'sizerPolicy.toast.adjustmentSaved': 'Adjustment saved',
  'sizerPolicy.toast.adjustmentDeleted': 'Adjustment deleted',
  'sizerPolicy.toast.saveFailed': 'Save failed: {{message}}',
  'sizerPolicy.toast.deleteFailed': 'Delete failed: {{message}}',

  // Status / chips
  'sizerPolicy.status.active': 'Active',
  'sizerPolicy.status.inactive': 'Inactive',
  'sizerPolicy.effective.unbounded': 'No end date',
  'sizerPolicy.value.notSet': '—',
  'sizerPolicy.unit.months': '{{count}} mo',

  // ============================================================================
  // BIC Completeness Monitor (Issue #1557 — Phase 4)
  // Cross-service freshness score surfaced as a column on the admin portal
  // businesses list, plus a SplitPanel drill-down with the per-service
  // breakdown.
  // ============================================================================
  'bicCompleteness.columnTitle': 'BIC Score',
  'bicCompleteness.scoreUnit': '%',
  'bicCompleteness.scoreUnknown': '—',
  'bicCompleteness.detailPanelTitle': 'BIC Freshness — {{businessName}}',
  'bicCompleteness.partialReportWarning': 'Some services were unreachable; this score is best-effort.',
  'bicCompleteness.refresh': 'Refresh',
  'bicCompleteness.lastChecked': 'Last checked: {{timestamp}}',
  'bicCompleteness.bicVersion': 'BIC version: {{version}}',
  'bicCompleteness.tableHeaders.service': 'Service',
  'bicCompleteness.tableHeaders.table': 'Table',
  'bicCompleteness.tableHeaders.totalRows': 'Total',
  'bicCompleteness.tableHeaders.atCurrent': 'At current',
  'bicCompleteness.tableHeaders.atOlder': 'At older',
  'bicCompleteness.tableHeaders.unstamped': 'Unstamped',
  'bicCompleteness.tableHeaders.tenantEdited': 'Tenant-edited',
  'bicCompleteness.section.summary': 'Summary',
  'bicCompleteness.section.serviceBreakdown': 'Per-service breakdown',
  'bicCompleteness.summary.score': 'Freshness score',
  'bicCompleteness.summary.servicesReporting': 'Services reporting',
  'bicCompleteness.empty.noServices': 'No services reported BIC-managed rows for this business yet.',

  // ============================================================================
  // APPROVAL WORKFLOWS — Issue #1559 Stream F / Phase 5a UI
  // Per-tenant editor for the 9 BIC approval workflows (segregation-of-duties
  // controls). Mirrored in `9-admin-portal/src/i18n/locales/en/approval-workflows.json`
  // for stream-level discoverability — `en.ts` is the actual runtime source.
  // ============================================================================

  // Navigation entry
  'navigation.platformApprovalWorkflows': 'Approval Workflows',

  // Page header (consumed by PageContentHeader via PageTemplate)
  'pageHeader.admin.approvalWorkflows.title': 'Approval Workflows',
  'pageHeader.admin.approvalWorkflows.description':
    'Edit segregation-of-duties controls for the 9 BIC approval workflows. SLA targets, approver roles, and escalation rules are tenant-scoped.',

  // Page-level scaffolding
  'admin.approvalWorkflows.forbiddenTitle': 'Access denied',
  'admin.approvalWorkflows.forbiddenMessage':
    'You do not have permission to view approval workflows. Ask a Super Admin or Platform Admin for the `approvalWorkflows:read` capability.',
  'admin.approvalWorkflows.loading': 'Loading approval workflows…',
  'admin.approvalWorkflows.empty.title': 'No approval workflows yet',
  'admin.approvalWorkflows.empty.description':
    'Bootstrap has not run for this business. Re-run the BIC bootstrap to seed the 9 default workflows.',
  'admin.approvalWorkflows.errorTitle': 'Failed to load approval workflows',
  'admin.approvalWorkflows.section.workflows': 'Workflows',
  'admin.approvalWorkflows.section.workflowsDescription':
    'Click any row to edit its SLA, approvers, and escalation rule.',

  // DataGrid columns
  'admin.approvalWorkflows.column.code': 'Code',
  'admin.approvalWorkflows.column.name': 'Workflow',
  'admin.approvalWorkflows.column.triggerAction': 'Trigger',
  'admin.approvalWorkflows.column.approverRoles': 'Approvers',
  'admin.approvalWorkflows.column.slaTargetHours': 'SLA',
  'admin.approvalWorkflows.column.twoPersonRule': 'Two-person',
  'admin.approvalWorkflows.column.isBicDefault': 'Source',

  // Cell formatters
  'admin.approvalWorkflows.cell.bicDefault': 'BIC default',
  'admin.approvalWorkflows.cell.tenantEdited': 'Tenant edited',
  'admin.approvalWorkflows.cell.twoPersonOn': 'Required',
  'admin.approvalWorkflows.cell.twoPersonOff': 'Single approver',
  'admin.approvalWorkflows.cell.slaHours': '{{hours}}h',
  'admin.approvalWorkflows.cell.escalationConfigured': 'Escalation configured',
  'admin.approvalWorkflows.cell.noEscalation': 'No escalation',

  // Bulk actions / page-level toolbar
  'admin.approvalWorkflows.action.resetAll': 'Reset all to BIC v1.0',
  'admin.approvalWorkflows.action.resetAllTooltip':
    'Restore every workflow to its BIC v1.0 default. Tenant overrides will be lost. Audited as bulk operation.',
  'admin.approvalWorkflows.action.refresh': 'Refresh',

  // Bulk reset confirmation dialog
  'admin.approvalWorkflows.confirmResetAll.title': 'Reset all workflows to BIC default?',
  'admin.approvalWorkflows.confirmResetAll.body':
    'This restores all {{count}} workflows to BIC v1.0 defaults. Custom SLAs, approver roles, escalation rules, and two-person toggles will be reverted. The action is audited per workflow.',
  'admin.approvalWorkflows.confirmResetAll.confirm': 'Reset all',
  'admin.approvalWorkflows.confirmResetAll.cancel': 'Cancel',
  'admin.approvalWorkflows.toast.resetAllProgress': 'Resetting workflows ({{current}}/{{total}})…',
  'admin.approvalWorkflows.toast.resetAllComplete': 'Reset {{count}} workflows to BIC defaults',
  'admin.approvalWorkflows.toast.resetAllPartial':
    'Reset {{succeeded}} of {{total}} workflows; {{failed}} failed. See console for details.',
  'admin.approvalWorkflows.toast.resetAllFailed': 'Bulk reset failed: {{message}}',

  // Editor — header / general
  'admin.approvalWorkflows.editor.title': 'Edit workflow: {{name}}',
  'admin.approvalWorkflows.editor.subtitle': 'Code: {{code}}',
  'admin.approvalWorkflows.editor.readOnlyBadge': 'Read-only',
  'admin.approvalWorkflows.editor.lockedHint': 'Defined by BIC manifest — not editable per tenant.',
  'admin.approvalWorkflows.editor.section.identity': 'Identity',
  'admin.approvalWorkflows.editor.section.trigger': 'Trigger (read-only)',
  'admin.approvalWorkflows.editor.section.approvers': 'Approvers',
  'admin.approvalWorkflows.editor.section.escalation': 'Escalation',
  'admin.approvalWorkflows.editor.section.notifications': 'Notification templates (read-only)',
  'admin.approvalWorkflows.editor.section.attribution': 'Attribution',

  // Editor — read-only field labels
  'admin.approvalWorkflows.editor.field.code': 'Workflow code',
  'admin.approvalWorkflows.editor.field.name': 'Name',
  'admin.approvalWorkflows.editor.field.description': 'Description',
  'admin.approvalWorkflows.editor.field.triggerAction': 'Trigger action',
  'admin.approvalWorkflows.editor.field.triggerRoles': 'Trigger roles',
  'admin.approvalWorkflows.editor.field.bicVersionAtSeed': 'Seeded from BIC version',
  'admin.approvalWorkflows.editor.field.requestedNotificationCode': 'On request',
  'admin.approvalWorkflows.editor.field.grantedNotificationCode': 'On grant',
  'admin.approvalWorkflows.editor.field.deniedNotificationCode': 'On deny',
  'admin.approvalWorkflows.editor.field.notificationsHelp':
    'Notification templates are managed centrally by Platform Admins; per-tenant overrides are not supported.',
  'admin.approvalWorkflows.editor.field.updatedBy': 'Last updated by',

  // Editor — editable field labels & help
  'admin.approvalWorkflows.editor.field.slaTargetHours': 'SLA target (hours)',
  'admin.approvalWorkflows.editor.field.slaTargetHoursHelp':
    'Hours from request to required decision. Range 1–168 (1 hour to 1 week).',
  'admin.approvalWorkflows.editor.field.approverRoles': 'Approver roles',
  'admin.approvalWorkflows.editor.field.approverRolesHelp':
    'Any user holding any of these roles can approve. At least one role required.',
  'admin.approvalWorkflows.editor.field.twoPersonRule': 'Two-person rule',
  'admin.approvalWorkflows.editor.field.twoPersonRuleHelp':
    'When enabled, two distinct approvers must grant the request before the action proceeds.',

  // Escalation editor
  'admin.approvalWorkflows.editor.escalation.enabled': 'Escalation rule',
  'admin.approvalWorkflows.editor.escalation.enabledHelp':
    'Optional rule that re-routes approvals to a different role set when a condition is met (e.g. amount > $1M).',
  'admin.approvalWorkflows.editor.escalation.conditionPath': 'Condition field',
  'admin.approvalWorkflows.editor.escalation.conditionPathHelp':
    'Dot-path on the action context (e.g. `amount`, `tier`, `state`). Common fields: amount, tier, state.',
  'admin.approvalWorkflows.editor.escalation.conditionOp': 'Operator',
  'admin.approvalWorkflows.editor.escalation.conditionValue': 'Threshold',
  'admin.approvalWorkflows.editor.escalation.conditionValueHelp':
    'For comparison ops, a number or string. For `in`, a comma-separated list.',
  'admin.approvalWorkflows.editor.escalation.escalateRoles': 'Escalation approvers',
  'admin.approvalWorkflows.editor.escalation.escalateRolesHelp':
    'These roles approve when the condition is met (replaces the default approvers, not merged).',
  'admin.approvalWorkflows.editor.escalation.advancedShape':
    'This workflow uses an advanced escalation shape. Edit as raw JSON.',
  'admin.approvalWorkflows.editor.escalation.rawJson': 'Raw escalation JSON',
  'admin.approvalWorkflows.editor.escalation.rawJsonHelp':
    'Must be a JSON object. Use this if the structured form does not match the rule.',
  'admin.approvalWorkflows.editor.escalation.rawJsonInvalid': 'Invalid JSON — fix syntax before saving.',
  'admin.approvalWorkflows.editor.escalation.useStructured': 'Use structured editor',
  'admin.approvalWorkflows.editor.escalation.useRaw': 'Use raw JSON',
  'admin.approvalWorkflows.editor.escalation.op.gt': 'Greater than (>)',
  'admin.approvalWorkflows.editor.escalation.op.gte': 'Greater than or equal (≥)',
  'admin.approvalWorkflows.editor.escalation.op.lt': 'Less than (<)',
  'admin.approvalWorkflows.editor.escalation.op.lte': 'Less than or equal (≤)',
  'admin.approvalWorkflows.editor.escalation.op.eq': 'Equals (=)',
  'admin.approvalWorkflows.editor.escalation.op.in': 'In list (in)',

  // Editor — actions
  'admin.approvalWorkflows.editor.action.cancel': 'Cancel',
  'admin.approvalWorkflows.editor.action.save': 'Save changes',
  'admin.approvalWorkflows.editor.action.saving': 'Saving…',
  'admin.approvalWorkflows.editor.action.resetToBic': 'Reset to BIC default',
  'admin.approvalWorkflows.editor.action.previewImpact': 'Preview impact',
  'admin.approvalWorkflows.editor.action.previewImpactDisabled':
    'Impact preview not available — admin-scope approval listing is not yet wired.',

  // Reset-single confirm
  'admin.approvalWorkflows.confirmResetOne.title': 'Reset {{name}} to BIC default?',
  'admin.approvalWorkflows.confirmResetOne.body':
    'This restores the workflow to BIC v1.0 (SLA, approvers, escalation, two-person rule). The action is audited.',
  'admin.approvalWorkflows.confirmResetOne.confirm': 'Reset',
  'admin.approvalWorkflows.confirmResetOne.cancel': 'Cancel',

  // Validation
  'admin.approvalWorkflows.validation.slaRange': 'SLA must be between 1 and 168 hours.',
  'admin.approvalWorkflows.validation.approverRolesRequired': 'At least one approver role is required.',
  'admin.approvalWorkflows.validation.escalationRolesRequired': 'Escalation requires at least one role.',
  'admin.approvalWorkflows.validation.escalationConditionRequired':
    'Escalation requires a condition path and threshold.',

  // Toasts
  'admin.approvalWorkflows.toast.saved': 'Saved {{name}}',
  'admin.approvalWorkflows.toast.saveFailed': 'Save failed: {{message}}',
  'admin.approvalWorkflows.toast.reset': 'Reset {{name}} to BIC default',
  'admin.approvalWorkflows.toast.resetFailed': 'Reset failed: {{message}}',

  // ============================================================================
  // Control Center — Warehouse Lines (Issue #1631 — Multi-fund management M1)
  // ============================================================================
  // Page chrome
  'controlCenter.warehouseLines.title': 'Warehouse Lines',
  'controlCenter.warehouseLines.subtitle':
    'Manage your capital providers, warehouse lines, and per-loan-type credit profiles.',
  'controlCenter.warehouseLines.backToProviders': 'Back to providers',
  'controlCenter.warehouseLines.backToLine': 'Back to warehouse line',

  // Empty state (D12 — seed templates)
  'controlCenter.warehouseLines.empty.title': 'No capital providers yet',
  'controlCenter.warehouseLines.empty.subtitle':
    'Add your first capital provider to model your funding sources, or start from a common lender template.',
  'controlCenter.warehouseLines.empty.startFromTemplate': 'Start from common lender template',
  'controlCenter.warehouseLines.empty.addProvider': 'Add your first capital provider',

  // Seed picker dialog (Issue #1645 FO2 / G17)
  // The empty-state "Start from common lender template" button used to
  // auto-pick `seeds[0]` — now opens a picker dialog so the operator can
  // see what each template will provision before clicking Apply.
  'controlCenter.warehouseLines.seedPicker.title': 'Choose a starter template',
  'controlCenter.warehouseLines.seedPicker.subtitle':
    'Each template provisions a capital provider, a warehouse line, and a draft loan-type profile with credit-box rules and required documents. You can edit everything afterward.',
  'controlCenter.warehouseLines.seedPicker.providerLabel': 'Capital provider',
  'controlCenter.warehouseLines.seedPicker.lineLabel': 'Warehouse line',
  'controlCenter.warehouseLines.seedPicker.categoryLabel': 'Loan category',
  'controlCenter.warehouseLines.seedPicker.rulesCount': '{{count}} credit-box rule(s)',
  'controlCenter.warehouseLines.seedPicker.requiredDocsCount': '{{count}} required document(s)',
  'controlCenter.warehouseLines.seedPicker.payloadParseError':
    'Could not parse template details — Apply will still provision the server-side template.',
  'controlCenter.warehouseLines.seedPicker.empty':
    'No starter templates are available for your account yet. Add a capital provider manually.',
  'controlCenter.warehouseLines.seedPicker.loadError': 'Could not load starter templates: {{message}}',
  'controlCenter.warehouseLines.seedPicker.applyButton': 'Apply template',
  'controlCenter.warehouseLines.seedPicker.cancelButton': 'Cancel',
  'controlCenter.warehouseLines.seedPicker.selectCta': 'Select a template above to continue.',

  // Capital providers list
  'controlCenter.warehouseLines.providers.title': 'Capital providers',
  'controlCenter.warehouseLines.providers.subtitle': 'Each capital provider can have one or more warehouse lines.',
  'controlCenter.warehouseLines.providers.searchPlaceholder': 'Search providers by name...',
  'controlCenter.warehouseLines.providers.addProvider': 'Add provider',
  'controlCenter.warehouseLines.providers.columnName': 'Name',
  'controlCenter.warehouseLines.providers.columnLegalName': 'Legal name',
  'controlCenter.warehouseLines.providers.columnContact': 'Contact',
  'controlCenter.warehouseLines.providers.columnLines': 'Lines',
  'controlCenter.warehouseLines.providers.columnUpdated': 'Updated',
  'controlCenter.warehouseLines.providers.lineCountChip': '{{count}} lines',
  'controlCenter.warehouseLines.providers.viewLines': 'View lines',

  // Capital provider editor
  'controlCenter.warehouseLines.providerEditor.createTitle': 'Add capital provider',
  'controlCenter.warehouseLines.providerEditor.editTitle': 'Edit capital provider',
  'controlCenter.warehouseLines.providerEditor.fieldName': 'Display name',
  'controlCenter.warehouseLines.providerEditor.fieldNameHelper':
    'Short name shown across the platform (e.g., Coastal Funding).',
  'controlCenter.warehouseLines.providerEditor.fieldLegalName': 'Legal name',
  'controlCenter.warehouseLines.providerEditor.fieldLegalNameHelper':
    'Full registered legal entity name (used in legal documents).',
  'controlCenter.warehouseLines.providerEditor.fieldContactEmail': 'Contact email',
  'controlCenter.warehouseLines.providerEditor.fieldNotes': 'Notes',
  'controlCenter.warehouseLines.providerEditor.toast.created': 'Provider added',
  'controlCenter.warehouseLines.providerEditor.toast.updated': 'Provider saved',
  'controlCenter.warehouseLines.providerEditor.toast.error': 'Failed to save: {{message}}',
  'controlCenter.warehouseLines.providerEditor.validateName': 'Name is required',
  'controlCenter.warehouseLines.providerEditor.validateEmail': 'Enter a valid email',

  // Warehouse lines list (drill-down from a provider)
  'controlCenter.warehouseLines.lines.title': 'Warehouse lines',
  'controlCenter.warehouseLines.lines.subtitle': 'Each line is one funding facility from this capital provider.',
  'controlCenter.warehouseLines.lines.addLine': 'Add line',
  'controlCenter.warehouseLines.lines.empty.title': 'No warehouse lines yet',
  'controlCenter.warehouseLines.lines.empty.subtitle': 'Add your first warehouse line for this capital provider.',
  'controlCenter.warehouseLines.lines.columnName': 'Name',
  'controlCenter.warehouseLines.lines.columnInternalCode': 'Internal code',
  'controlCenter.warehouseLines.lines.columnStatus': 'Status',
  'controlCenter.warehouseLines.lines.columnFacilityCap': 'Facility cap',
  'controlCenter.warehouseLines.lines.columnUpdated': 'Updated',
  'controlCenter.warehouseLines.lines.openDetail': 'Open',

  // Warehouse line editor
  'controlCenter.warehouseLines.lineEditor.createTitle': 'Add warehouse line',
  'controlCenter.warehouseLines.lineEditor.editTitle': 'Edit warehouse line',
  'controlCenter.warehouseLines.lineEditor.fieldName': 'Line name',
  'controlCenter.warehouseLines.lineEditor.fieldInternalCode': 'Internal code',
  'controlCenter.warehouseLines.lineEditor.fieldInternalCodeHelper':
    'Short reference code (e.g., COA-1) used in audit records.',
  'controlCenter.warehouseLines.lineEditor.fieldStatus': 'Status',
  'controlCenter.warehouseLines.lineEditor.fieldFacilityCap': 'Facility cap (USD)',
  'controlCenter.warehouseLines.lineEditor.fieldFacilityCapHelper': 'Total committed facility size for this line.',
  'controlCenter.warehouseLines.lineEditor.fieldNotes': 'Notes',
  'controlCenter.warehouseLines.lineEditor.toast.created': 'Line added',
  'controlCenter.warehouseLines.lineEditor.toast.updated': 'Line saved',
  'controlCenter.warehouseLines.lineEditor.toast.error': 'Failed to save: {{message}}',
  'controlCenter.warehouseLines.lineEditor.validateName': 'Name is required',
  'controlCenter.warehouseLines.lineEditor.validateProvider': 'Capital provider is required',

  // Status labels
  'controlCenter.warehouseLines.status.ACTIVE': 'Active',
  'controlCenter.warehouseLines.status.INACTIVE': 'Inactive',
  'controlCenter.warehouseLines.status.ARCHIVED': 'Archived',

  // Detail page (line-level)
  'controlCenter.warehouseLines.detail.title': '{{name}}',
  'controlCenter.warehouseLines.detail.providerLabel': 'Capital provider',
  'controlCenter.warehouseLines.detail.statusLabel': 'Status',
  'controlCenter.warehouseLines.detail.facilityCapLabel': 'Facility cap',
  'controlCenter.warehouseLines.detail.tabProfiles': 'Loan-type profiles',
  'controlCenter.warehouseLines.detail.tabGlobalRules': 'Global rules',
  'controlCenter.warehouseLines.detail.tabSettings': 'Settings',
  'controlCenter.warehouseLines.detail.editLine': 'Edit line',
  'controlCenter.warehouseLines.detail.notFound': 'Warehouse line not found.',

  // Loan-type profile tabs (one tab per loan category)
  'controlCenter.warehouseLines.profiles.addLoanType': 'Add loan type',
  'controlCenter.warehouseLines.profiles.empty.title': 'No loan types configured',
  'controlCenter.warehouseLines.profiles.empty.subtitle':
    'Add a loan type (Bridge, DSCR, Ground-Up, Fix-and-Flip, etc.) to start configuring credit boxes for this line.',
  'controlCenter.warehouseLines.profiles.activeBadge': 'Active',
  'controlCenter.warehouseLines.profiles.draftBadge': 'Draft',
  'controlCenter.warehouseLines.profiles.scheduledBadge': 'Scheduled',
  'controlCenter.warehouseLines.profiles.supersededBadge': 'Superseded',
  'controlCenter.warehouseLines.profiles.archivedBadge': 'Archived',
  // Profile state labels (matches WarehouseLineProfileState enum values verbatim)
  'controlCenter.warehouseLines.profiles.state.DRAFT': 'Draft',
  'controlCenter.warehouseLines.profiles.state.SCHEDULED': 'Scheduled',
  'controlCenter.warehouseLines.profiles.state.ACTIVE': 'Active',
  'controlCenter.warehouseLines.profiles.state.SUPERSEDED': 'Superseded',
  'controlCenter.warehouseLines.profiles.state.ARCHIVED': 'Archived',
  'controlCenter.warehouseLines.profiles.versionLabel': 'Version {{version}}',
  'controlCenter.warehouseLines.profiles.editProfile': 'Edit profile',
  'controlCenter.warehouseLines.profiles.scheduleProfile': 'Schedule',
  'controlCenter.warehouseLines.profiles.publishProfile': 'Publish',
  'controlCenter.warehouseLines.profiles.archiveProfile': 'Archive',
  'controlCenter.warehouseLines.profiles.createNewVersion': 'Create new version',
  'controlCenter.warehouseLines.profiles.noActiveVersion': 'No active version yet.',
  'controlCenter.warehouseLines.profiles.activeFrom': 'Active since {{date}}',
  'controlCenter.warehouseLines.profiles.scheduledFor': 'Scheduled for {{date}}',
  'controlCenter.warehouseLines.profiles.toast.created': 'Profile created',
  'controlCenter.warehouseLines.profiles.toast.updated': 'Profile saved',
  'controlCenter.warehouseLines.profiles.toast.scheduled': 'Profile scheduled',
  'controlCenter.warehouseLines.profiles.toast.activated': 'Profile activated',
  'controlCenter.warehouseLines.profiles.toast.archived': 'Profile archived',
  'controlCenter.warehouseLines.profiles.toast.error': 'Failed to save: {{message}}',

  // Loan-type profile editor (the big form)
  'controlCenter.warehouseLines.profileEditor.createTitle': 'Add loan-type profile',
  'controlCenter.warehouseLines.profileEditor.editTitle': 'Edit profile',
  'controlCenter.warehouseLines.profileEditor.tabGeneral': 'General',
  'controlCenter.warehouseLines.profileEditor.tabLeverage': 'Leverage caps',
  'controlCenter.warehouseLines.profileEditor.tabPricing': 'Pricing',
  'controlCenter.warehouseLines.profileEditor.tabRules': 'Credit rules',
  'controlCenter.warehouseLines.profileEditor.tabRequiredDocs': 'Required docs',
  'controlCenter.warehouseLines.profileEditor.tabConditions': 'Conditions to fund',
  'controlCenter.warehouseLines.profileEditor.fieldLoanCategory': 'Loan category',
  'controlCenter.warehouseLines.profileEditor.fieldLoanCategoryHelper': 'Bridge, DSCR, Ground-Up, Fix-and-Flip, etc.',
  'controlCenter.warehouseLines.profileEditor.fieldMaxLtv': 'Max LTV %',
  'controlCenter.warehouseLines.profileEditor.fieldMaxLtc': 'Max LTC %',
  'controlCenter.warehouseLines.profileEditor.fieldMaxLtarv': 'Max LTARV %',
  'controlCenter.warehouseLines.profileEditor.fieldMinFico': 'Min FICO',
  'controlCenter.warehouseLines.profileEditor.fieldMinLoanAmount': 'Min loan amount',
  'controlCenter.warehouseLines.profileEditor.fieldMaxLoanAmount': 'Max loan amount',
  'controlCenter.warehouseLines.profileEditor.fieldAdvanceRate': 'Advance rate (0–1)',
  'controlCenter.warehouseLines.profileEditor.fieldAdvanceRateHelper':
    'Fraction of principal advanced by the lender (e.g., 0.9 = 90%).',
  'controlCenter.warehouseLines.profileEditor.fieldPersonalGuarantee': 'Personal guarantee required',
  'controlCenter.warehouseLines.profileEditor.fieldMaxHoldDays': 'Max hold period (days)',
  'controlCenter.warehouseLines.profileEditor.fieldTakeoutWindowDays': 'Takeout window (days)',
  'controlCenter.warehouseLines.profileEditor.fieldDefaultTermMonths': 'Default term (months)',
  'controlCenter.warehouseLines.profileEditor.fieldRateTier': 'Rate tier version ID',
  'controlCenter.warehouseLines.profileEditor.fieldPointsTier': 'Points tier ID',
  'controlCenter.warehouseLines.profileEditor.fieldRequiredFields': 'Required canonical fields',
  'controlCenter.warehouseLines.profileEditor.fieldRequiredFieldsHelper':
    'Comma-separated canonical field keys (e.g., borrower.fico, property.purchasePrice).',
  'controlCenter.warehouseLines.profileEditor.lockedNotice':
    "This profile is {{state}} — edits create a new version. Use 'Create new version' to revise.",
  'controlCenter.warehouseLines.profileEditor.validateLoanCategory': 'Loan category is required',
  'controlCenter.warehouseLines.profileEditor.validatePercentRange': 'Must be between 0 and 100',
  'controlCenter.warehouseLines.profileEditor.validateAdvanceRange': 'Must be between 0 and 1',

  // Credit-box rules editor (per-profile)
  'controlCenter.warehouseLines.creditRules.title': 'Credit-box rules',
  'controlCenter.warehouseLines.creditRules.subtitle':
    'Rules evaluated against each loan. BLOCK = hard stop, WARN = surfaced to the underwriter, INFO = note only.',
  'controlCenter.warehouseLines.creditRules.addRule': 'Add rule',
  'controlCenter.warehouseLines.creditRules.empty': 'No credit-box rules configured for this profile yet.',
  'controlCenter.warehouseLines.creditRules.fieldField': 'Field',
  'controlCenter.warehouseLines.creditRules.fieldFieldHelper':
    "Canonical field path (e.g., 'borrower.fico', 'property.purchasePrice', 'guarantors[].fico::min').",
  'controlCenter.warehouseLines.creditRules.fieldOp': 'Operator',
  'controlCenter.warehouseLines.creditRules.fieldValue': 'Value',
  'controlCenter.warehouseLines.creditRules.fieldValueHelper':
    'JSON value: number, string, [min, max] for between, or [a, b, c] for in/notIn.',
  'controlCenter.warehouseLines.creditRules.fieldSeverity': 'Severity',
  'controlCenter.warehouseLines.creditRules.fieldMessage': 'Message',
  'controlCenter.warehouseLines.creditRules.fieldMessageHelper': 'Shown to the underwriter when the rule trips.',
  'controlCenter.warehouseLines.creditRules.lockedNotice':
    'Credit-box rules can only be edited on DRAFT profiles. Create a new version to revise.',
  'controlCenter.warehouseLines.creditRules.toast.created': 'Rule added',
  'controlCenter.warehouseLines.creditRules.toast.updated': 'Rule saved',
  'controlCenter.warehouseLines.creditRules.toast.deleted': 'Rule removed',
  // Action labels (used as aria-label on icon-only buttons — must describe
  // the action, not the post-action toast). PR #1651 review: previously
  // this aria-label leaked the toast key "Rule removed" onto the delete
  // button, so screen readers announced the success state before any
  // action was taken.
  'controlCenter.warehouseLines.creditRules.actions.delete': 'Delete rule',
  // Same a11y fix for the sibling RequiredDocsEditor delete button —
  // PR #1651 review round 3 (cursor[bot] D1) caught this sibling that
  // round 2's CreditBoxRuleEditor fix missed.
  'controlCenter.warehouseLines.requiredDocs.actions.delete': 'Delete document',
  'controlCenter.warehouseLines.creditRules.toast.createError': 'Failed to add rule: {{message}}',
  'controlCenter.warehouseLines.creditRules.toast.updateError': 'Failed to save rule: {{message}}',
  'controlCenter.warehouseLines.creditRules.toast.deleteError': 'Failed to remove rule: {{message}}',
  'controlCenter.warehouseLines.creditRules.editRule': 'Edit rule',

  // Required docs editor (per-profile)
  'controlCenter.warehouseLines.requiredDocs.title': 'Required documents',
  'controlCenter.warehouseLines.requiredDocs.subtitle':
    'Documents this profile requires for closing. Use a trigger to make a document conditional.',
  'controlCenter.warehouseLines.requiredDocs.addDoc': 'Add required doc',
  'controlCenter.warehouseLines.requiredDocs.empty': 'No required documents configured for this profile yet.',
  'controlCenter.warehouseLines.requiredDocs.fieldDocumentTypeKey': 'Document type key',
  'controlCenter.warehouseLines.requiredDocs.fieldRequired': 'Required',
  'controlCenter.warehouseLines.requiredDocs.fieldHasTrigger': 'Conditional?',
  'controlCenter.warehouseLines.requiredDocs.lockedNotice':
    'Required docs can only be edited on DRAFT profiles. Create a new version to revise.',
  'controlCenter.warehouseLines.requiredDocs.toast.created': 'Document added',
  'controlCenter.warehouseLines.requiredDocs.toast.updated': 'Document saved',
  'controlCenter.warehouseLines.requiredDocs.toast.deleted': 'Document removed',
  'controlCenter.warehouseLines.requiredDocs.toast.createError': 'Failed to add document: {{message}}',
  'controlCenter.warehouseLines.requiredDocs.toast.updateError': 'Failed to save document: {{message}}',
  'controlCenter.warehouseLines.requiredDocs.toast.deleteError': 'Failed to remove document: {{message}}',
  'controlCenter.warehouseLines.requiredDocs.editDoc': 'Edit document',
  'controlCenter.warehouseLines.requiredDocs.requiredHelper':
    'When required, every loan under this profile must attach this document before it can fund.',

  // Global rules (line-level — applies to every profile)
  'controlCenter.warehouseLines.globalRules.title': 'Global rules',
  'controlCenter.warehouseLines.globalRules.subtitle':
    'Line-level rules applied on top of every loan-type profile under this warehouse line.',
  'controlCenter.warehouseLines.globalRules.addRule': 'Add global rule',
  'controlCenter.warehouseLines.globalRules.empty': 'No global rules configured for this warehouse line.',
  'controlCenter.warehouseLines.globalRules.toast.created': 'Global rule added',
  'controlCenter.warehouseLines.globalRules.toast.deleted': 'Global rule removed',
  'controlCenter.warehouseLines.globalRules.toast.createError': 'Failed to add global rule: {{message}}',
  'controlCenter.warehouseLines.globalRules.toast.deleteError': 'Failed to remove global rule: {{message}}',

  // Profile version timeline (D1 SCD2 chain)
  'controlCenter.warehouseLines.versionTimeline.title': 'Version history',
  'controlCenter.warehouseLines.versionTimeline.empty': 'No version history yet.',
  'controlCenter.warehouseLines.versionTimeline.activeNow': 'Active',
  'controlCenter.warehouseLines.versionTimeline.scheduled': 'Scheduled',
  'controlCenter.warehouseLines.versionTimeline.draft': 'Draft',
  'controlCenter.warehouseLines.versionTimeline.superseded': 'Superseded',
  'controlCenter.warehouseLines.versionTimeline.archived': 'Archived',

  // Common operators / severities (for inline rule editor)
  'controlCenter.warehouseLines.op.eq': 'equals',
  'controlCenter.warehouseLines.op.neq': 'not equals',
  'controlCenter.warehouseLines.op.gt': 'greater than',
  'controlCenter.warehouseLines.op.gte': 'greater than or equal',
  'controlCenter.warehouseLines.op.lt': 'less than',
  'controlCenter.warehouseLines.op.lte': 'less than or equal',
  'controlCenter.warehouseLines.op.between': 'between',
  'controlCenter.warehouseLines.op.in': 'in',
  'controlCenter.warehouseLines.op.notIn': 'not in',
  'controlCenter.warehouseLines.op.matches': 'matches',
  'controlCenter.warehouseLines.op.set': 'set to',
  'controlCenter.warehouseLines.severity.BLOCK': 'Block',
  'controlCenter.warehouseLines.severity.WARN': 'Warn',
  'controlCenter.warehouseLines.severity.INFO': 'Info',

  // ============================================================================
  // LOAN SIZER — Multi-fund comparison (Issue #1631 — M2)
  // ============================================================================
  'loanSizer.multiFund.pageTitle': 'Multi-fund Loan Sizer',
  'loanSizer.multiFund.pageDescription':
    'Size a deal against every active warehouse line in one shot — see which capital sources qualify, which need an exception, and which are blocked.',
  'loanSizer.multiFund.title': 'Capital line comparison',
  'loanSizer.multiFund.tabLabel': 'Sizing',
  'loanSizer.multiFund.caseTabTitle': 'Multi-fund sizing',
  'loanSizer.multiFund.caseTabDescription':
    'Run this case through every active warehouse line and pin a result back to the loan.',
  'loanSizer.multiFund.caseTabHint':
    'Pick a loan category and click "Run sizing" — the engine will fan out across every active warehouse line for your business.',
  // Orientation card — explains the purpose of this tab vs. the inline
  // ambient sizing on the Details tab (Issue #1438 vs. Issue #1631 M2).
  'loanSizer.multiFund.howThisWorks.title': 'How to use this page',
  'loanSizer.multiFund.howThisWorks.intro':
    'Inline sizing on the Details tab shows live constraint bands for a single line. This page evaluates the case against every active warehouse line in parallel and lets you pin the chosen line back to the loan.',
  'loanSizer.multiFund.howThisWorks.step1':
    'Confirm the loan inputs — they pre-fill from the case but you can override before running.',
  'loanSizer.multiFund.howThisWorks.step2':
    'Run the fan-out — pricing engine evaluates the case against every active line in parallel (credit-box rules, line-level rules, rate tiers).',
  'loanSizer.multiFund.howThisWorks.step3':
    'Pin a line from the comparison table — pinning attributes the case to that fund and unlocks the line-specific requirements.',
  // Section labels for the two main steps
  'loanSizer.multiFund.sections.inputs': 'Loan inputs',
  'loanSizer.multiFund.sections.comparison': 'Capital line comparison',
  'loanSizer.multiFund.emptyState':
    'No sizing run has been triggered yet. Submit the form above to fan out across every active warehouse line.',
  'loanSizer.multiFund.noResults':
    'The engine returned no results. Check that this business has active warehouse-line profiles for the selected loan category.',
  'loanSizer.multiFund.noCaseError': 'Pick a case before pinning a result.',
  'loanSizer.multiFund.runFailed': 'Sizing run failed. Check the engine logs and retry.',
  'loanSizer.multiFund.startError': 'Sizing run could not start: {{message}}',
  'loanSizer.multiFund.selectSuccess': 'Result pinned to the loan.',
  'loanSizer.multiFund.selectError': 'Could not pin result: {{message}}',
  'loanSizer.multiFund.pinned': 'Pinned',
  'loanSizer.multiFund.unknownProvider': 'Unknown provider',
  'loanSizer.multiFund.unknownLine': 'Unknown line',
  'loanSizer.multiFund.unnamedCase': 'Untitled loan',
  'loanSizer.multiFund.versionLabel': 'v{{version}}',
  'loanSizer.multiFund.termMonths': '{{count}} mo',
  'loanSizer.multiFund.missingCount': '{{count}} missing',
  'loanSizer.multiFund.evaluated': '{{count}} evaluated',
  'loanSizer.multiFund.qualifying': '{{count}} qualify',
  'loanSizer.multiFund.exception': '{{count}} need exception',
  'loanSizer.multiFund.blocked': '{{count}} blocked',
  'loanSizer.multiFund.duration': '{{ms}}ms',

  // Status chips
  'loanSizer.multiFund.status.QUALIFIES': 'Qualifies',
  'loanSizer.multiFund.status.EXCEPTION': 'Needs exception',
  'loanSizer.multiFund.status.BLOCKED': 'Blocked',

  // Loan-category picker
  'loanSizer.multiFund.category.BRIDGE': 'Bridge',
  'loanSizer.multiFund.category.DSCR': 'DSCR',
  'loanSizer.multiFund.category.GROUND_UP': 'Ground Up',
  'loanSizer.multiFund.category.FIX_AND_FLIP': 'Fix & Flip',
  'loanSizer.multiFund.category.OTHER': 'Other',

  // Comparison-table columns
  'loanSizer.multiFund.column.provider': 'Provider',
  'loanSizer.multiFund.column.line': 'Line',
  'loanSizer.multiFund.column.loanType': 'Loan type',
  'loanSizer.multiFund.column.status': 'Status',
  'loanSizer.multiFund.column.rate': 'Rate',
  'loanSizer.multiFund.column.points': 'Points',
  'loanSizer.multiFund.column.term': 'Term',
  'loanSizer.multiFund.column.maxLoan': 'Max loan',
  'loanSizer.multiFund.column.lenderFunds': 'Lender funds',
  'loanSizer.multiFund.column.missing': 'Missing',
  'loanSizer.multiFund.column.action': 'Actions',

  // Per-row CTAs
  'loanSizer.multiFund.actionSelect': 'Select',
  'loanSizer.multiFund.actionRequestException': 'Request exception',
  'loanSizer.multiFund.actionPinned': 'Pinned',
  'loanSizer.multiFund.actionUnavailable': '—',

  // Expanded detail strip
  'loanSizer.multiFund.blockingRulesTitle': 'Blocking rules',
  'loanSizer.multiFund.warningRulesTitle': 'Warnings',
  'loanSizer.multiFund.missingFieldsTitle': 'Missing required fields',
  'loanSizer.multiFund.missingDocsTitle': 'Missing documents',
  'loanSizer.multiFund.cleanResult': 'No issues — this profile evaluated cleanly against the deal inputs.',

  // Input form
  'loanSizer.multiFund.inputForm.title': 'Sizing inputs',
  'loanSizer.multiFund.inputForm.caseLabel': 'Loan',
  'loanSizer.multiFund.inputForm.casePlaceholder': 'Search loans by number, title, or borrower…',
  'loanSizer.multiFund.inputForm.caseHelp':
    'Pick the loan to size. The result can be pinned back to this loan once you select a winner.',
  'loanSizer.multiFund.inputForm.categoryLabel': 'Loan category',
  'loanSizer.multiFund.inputForm.submit': 'Run sizing',

  // Selected-line rail card
  'loanSizer.selectedLine.title': 'Selected warehouse line',
  'loanSizer.selectedLine.emptyState': 'No warehouse line selected for this loan yet.',
  'loanSizer.selectedLine.runSizing': 'Run sizing',
  'loanSizer.selectedLine.resize': 'Re-size',
  'loanSizer.selectedLine.line': 'Line',
  'loanSizer.selectedLine.loanType': 'Loan type',
  'loanSizer.selectedLine.version': 'Version',
  'loanSizer.selectedLine.pinnedChip': 'Active',
  // Secondary escape-hatch CTA on the empty state (Issue #1645 G10).
  // Phrasing intentionally matches the dialog title so operators can
  // visually trace the click → dialog continuity.
  'loanSizer.selectedLine.selectManually': 'Select manually',

  // Manual pin dialog (Issue #1645 G10) — escape hatch for pinning a line
  // without running the multi-fund sizer. Bypasses sizer rule evaluation;
  // the dialog surfaces an explicit warning before submission.
  'loanSizer.manualPin.title': 'Pin warehouse line manually',
  'loanSizer.manualPin.description':
    'Attribute this loan to a specific warehouse line and loan-type profile without running the multi-fund sizer. Use this for legacy or grandfathered loans where the line is already known.',
  'loanSizer.manualPin.skipsRulesWarning':
    "This skips sizer rule checks. The line may have credit-box or global rules this loan does not satisfy — those won't be flagged before pinning. Run the sizer instead if you're unsure.",
  'loanSizer.manualPin.lineLabel': 'Warehouse line',
  'loanSizer.manualPin.profileLabel': 'Loan-type profile (active version)',
  // Profile option label — combines loan category + version. e.g.
  // "FIX_AND_FLIP · v3"
  'loanSizer.manualPin.profileOption': '{{category}} · v{{version}}',
  'loanSizer.manualPin.noLinesHint': 'No active warehouse lines configured for this business.',
  'loanSizer.manualPin.noProfilesHint': 'This line has no active loan-type profile versions.',
  'loanSizer.manualPin.submit': 'Pin line',
  'loanSizer.manualPin.submitting': 'Pinning…',
  'loanSizer.manualPin.successToast': 'Warehouse line pinned to this loan.',
  // Surfaced verbatim from gateway/mapGrpcError so cross-tenant or
  // cross-line consistency rejections render the engine's structured
  // reason rather than a generic "pin failed".
  'loanSizer.manualPin.errorToast': 'Could not pin line: {{message}}',

  // Sizing history dropdown
  'loanSizer.history.menuLabel': 'Prior sizing runs',
  'loanSizer.history.title': 'Sizing history',
  'loanSizer.history.subtitle': 'Most recent first.',
  'loanSizer.history.empty': 'No prior sizing runs for this loan.',
  'loanSizer.history.currentRun': 'Current',

  // Issue #1631 M2 — Warehouse-line "Fund" attribution column + filter
  // Used by the FundCell helper and the /loans warehouse-line filter chip.
  // Provider · line · loanType · v{version} composition is forthcoming;
  // today's cells render the line UUID prefix as a chip.
  'common.unattributed': '—',
  'warehouseLineFund.column.header': 'Fund',
  'warehouseLineFund.column.tooltipPinned': 'Pinned to {{lineName}} · v{{version}}',
  'warehouseLineFund.column.tooltipUnattributed': 'No warehouse line pinned',
  'warehouseLineFund.column.lineLabel': 'Line',
  'warehouseLineFund.column.versionLabel': 'Version',
  'warehouseLineFund.column.providerLabel': 'Provider',
  'warehouseLineFund.column.loanTypeLabel': 'Loan type',
  'warehouseLineFund.filter.label': 'Warehouse line',
  'warehouseLineFund.filter.allLines': 'All lines',
  'warehouseLineFund.filter.unattributed': 'Unattributed',
  'warehouseLineFund.filter.placeholder': 'Filter by warehouse line…',
  'warehouseLineFund.filter.profileLabel': 'Loan-type profile',
  'warehouseLineFund.filter.profileAll': 'All profiles',
  'warehouseLineFund.filter.profilePlaceholder': 'Filter by loan-type profile…',
  'warehouseLineFund.filter.clearAll': 'Clear filters',
  'warehouseLineFund.filter.activeChip': 'Filtered: {{lineName}}',
  'warehouseLineFund.detail.pinnedProfileVersion': 'Pinned profile version: {{versionId}}',
  'warehouseLineFund.detail.pinnedLine': 'Pinned warehouse line: {{lineName}}',
  'warehouseLineFund.detail.unattributedHint':
    'This loan was not sized against a warehouse line. Run sizing to pin a fund.',

  // Issue #1631 M2 — Control Center fallback banners
  // Sizer Policy banner: appears at the top of /control-center/sizer-policy
  // when the lender has any warehouse line configured. Pricing note: appears
  // at the top of /control-center/pricing for the same reason. Both lead the
  // lender toward /control-center/warehouse-lines as the preferred surface.
  'controlCenter.sizerPolicy.warehouseLineFallbackBanner':
    'When warehouse lines are configured, the sizer evaluates against per-line profiles instead of this policy. This page now defines the platform-default fallback used when a loan has no pinned line.',
  'controlCenter.sizerPolicy.warehouseLineFallbackLink': 'Manage warehouse lines',
  'controlCenter.pricing.warehouseLineNote':
    "For warehouse-line-specific pricing, manage rate tiers under each line's profile. The rules below remain the platform-default fallback.",
  'controlCenter.pricing.warehouseLineLink': 'Open warehouse-line pricing',
  'controlCenter.pricing.previewOnlyNote':
    'Preview only — pricing-rule persistence is not yet wired. Edits and saves on this page are session-local and will be lost on navigation.',

  // ── Warehouse line — credit-box doc upload (Issue #1631 M3) ─────────────
  'controlCenter.warehouseLines.aiIngestion.hint':
    'Drop a credit-box PDF or Excel sheet to extract a complete profile via AI. Each loan type ships as DRAFT — review every rule before publishing.',
  'controlCenter.warehouseLines.uploadCreditBox.button': 'Upload credit-box doc',
  'controlCenter.warehouseLines.uploadCreditBox.dialogTitle': 'Extract credit box from document',
  'controlCenter.warehouseLines.uploadCreditBox.dialogDescription':
    'Upload the lender credit-box document (PDF or Excel). After extraction, every loan type, rule, and required doc lands as a DRAFT for your review before publishing.',
  'controlCenter.warehouseLines.uploadCreditBox.fileLabel': 'Credit-box document',
  'controlCenter.warehouseLines.uploadCreditBox.fileHelper': 'PDF or XLSX up to 10MB.',
  'controlCenter.warehouseLines.uploadCreditBox.uploadFile': 'Upload document',
  'controlCenter.warehouseLines.uploadCreditBox.uploadingFile': 'Uploading document…',
  'controlCenter.warehouseLines.uploadCreditBox.extracting': 'Extracting credit box with AI…',
  'controlCenter.warehouseLines.uploadCreditBox.ingestSubmit': 'Ingest credit box',
  'controlCenter.warehouseLines.uploadCreditBox.cancel': 'Cancel',
  'controlCenter.warehouseLines.uploadCreditBox.success.title': 'Credit box ingested',
  'controlCenter.warehouseLines.uploadCreditBox.success.summary':
    'Created {{profiles}} draft profile(s) with {{rules}} rule(s) and {{docs}} required doc(s). {{globals}} global rule(s) added at the line level.',
  'controlCenter.warehouseLines.uploadCreditBox.success.review':
    'Each new profile is in DRAFT — review and publish when ready.',
  'controlCenter.warehouseLines.uploadCreditBox.warningsTitle': 'Warnings from AI extraction',
  'controlCenter.warehouseLines.uploadCreditBox.errorTitle': 'Failed to ingest credit box',
  'controlCenter.warehouseLines.uploadCreditBox.fileTooLarge': 'File size must be less than 10MB.',
  'controlCenter.warehouseLines.uploadCreditBox.payloadLabel': 'Extraction payload (JSON)',
  'controlCenter.warehouseLines.uploadCreditBox.payloadHelper':
    'Review or edit the extracted JSON before ingesting. Each rule and required doc lands as a DRAFT — you can edit them on the line after ingest.',
  'controlCenter.warehouseLines.uploadCreditBox.payloadInvalid': 'Extraction payload must be valid JSON.',
  'controlCenter.warehouseLines.uploadCreditBox.uploadFirst': 'Upload a document before ingesting.',
  'controlCenter.warehouseLines.uploadCreditBox.close': 'Close',
  'controlCenter.warehouseLines.uploadCreditBox.uploadedAs': 'Uploaded as: {{name}}',

  // ── Warehouse line — credit-box upload doc-pipeline polling (Issue #1645 G21/FO4) ──
  // Status chip + progress hint + retry / timeout copy for the M3.5 polling loop
  // that watches doc-pipeline state until COMPLETED/FAILED before allowing ingest.
  'controlCenter.warehouseLines.uploadCreditBox.docStatus.title': 'Document processing',
  'controlCenter.warehouseLines.uploadCreditBox.docStatus.pending': 'Queued',
  'controlCenter.warehouseLines.uploadCreditBox.docStatus.processing': 'Processing',
  'controlCenter.warehouseLines.uploadCreditBox.docStatus.completed': 'Extracted',
  'controlCenter.warehouseLines.uploadCreditBox.docStatus.failed': 'Failed',
  'controlCenter.warehouseLines.uploadCreditBox.docStatus.processingHint':
    'Extracting structured data — usually takes a few seconds.',
  'controlCenter.warehouseLines.uploadCreditBox.docStatus.completedHint':
    'Extraction complete. Review the JSON below before ingesting.',
  'controlCenter.warehouseLines.uploadCreditBox.docStatus.failedHint':
    'Extraction failed. Try uploading again or paste the JSON manually below.',
  'controlCenter.warehouseLines.uploadCreditBox.docStatus.retryButton': 'Retry upload',
  'controlCenter.warehouseLines.uploadCreditBox.docStatus.timeoutHint':
    'Still processing — you can keep waiting, refresh the page, or paste the JSON manually below.',

  // ── Warehouse line — credit-box page-count plumbing (Issue #1645 G20/FO3) ─
  // Auto-extracted from the PDF when possible; manual override field is always
  // visible so spreadsheets and parse failures can still set the value.
  'controlCenter.warehouseLines.uploadCreditBox.pageCount.manualOverrideLabel': 'Page count',
  'controlCenter.warehouseLines.uploadCreditBox.pageCount.helperText':
    'Used for citation page-range validation in pricing-engine.',
  'controlCenter.warehouseLines.uploadCreditBox.pageCount.parseFailedCaption':
    'Page count could not be detected automatically — enter it manually if you need page-range citations.',
  'controlCenter.warehouseLines.uploadCreditBox.pageCount.extractedCaption': 'Extracted from {{count}} page',
  'controlCenter.warehouseLines.uploadCreditBox.pageCount.extractedCaption_other': 'Extracted from {{count}} pages',

  // ── Warehouse line — rate-sheet doc upload (Issue #1631 M3) ─────────────
  'controlCenter.warehouseLines.uploadRateSheet.button': 'Upload rate-sheet doc',
  'controlCenter.warehouseLines.uploadRateSheet.dialogTitle': 'Extract rate sheet from document',
  'controlCenter.warehouseLines.uploadRateSheet.dialogDescription':
    'Upload a lender rate-sheet (PDF or Excel). The AI will extract rate tiers and bands tied to existing loan-type profiles on this line.',
  'controlCenter.warehouseLines.uploadRateSheet.fileLabel': 'Rate-sheet document',
  'controlCenter.warehouseLines.uploadRateSheet.fileHelper': 'PDF or XLSX up to 10MB.',
  'controlCenter.warehouseLines.uploadRateSheet.ingestSubmit': 'Ingest rate sheet',
  'controlCenter.warehouseLines.uploadRateSheet.success.title': 'Rate sheet ingested',
  'controlCenter.warehouseLines.uploadRateSheet.success.summary':
    'Created {{tiers}} rate tier(s) with {{bands}} rate band(s).',
  'controlCenter.warehouseLines.uploadRateSheet.errorTitle': 'Failed to ingest rate sheet',

  // ── Warehouse line — citation hover preview (Issue #1631 M3) ───────────
  'controlCenter.warehouseLines.citationHover.iconLabel': 'AI citation',
  'controlCenter.warehouseLines.citationHover.pageLabel': 'Page {{page}}',
  'controlCenter.warehouseLines.citationHover.openSource': 'Open source doc at page {{page}}',
  'controlCenter.warehouseLines.citationHover.noQuote': 'No source quote captured.',

  // ── Warehouse line — publish profile (Issue #1631 M3) ───────────────────
  'controlCenter.warehouseLines.publishProfile.scheduleButton': 'Schedule…',
  'controlCenter.warehouseLines.publishProfile.activateNowButton': 'Activate now',
  'controlCenter.warehouseLines.publishProfile.scheduleDialogTitle': 'Schedule profile activation',
  'controlCenter.warehouseLines.publishProfile.scheduleDescription':
    'Pick the date this profile becomes active. Any existing ACTIVE profile in this loan-type chain will be SUPERSEDED at that time.',
  'controlCenter.warehouseLines.publishProfile.dateLabel': 'Effective from',
  'controlCenter.warehouseLines.publishProfile.dateInvalid': 'Pick a future date for activation.',
  'controlCenter.warehouseLines.publishProfile.confirmSchedule': 'Schedule',
  'controlCenter.warehouseLines.publishProfile.activateDialogTitle': 'Activate profile now',
  'controlCenter.warehouseLines.publishProfile.activateDescription':
    'This DRAFT becomes the ACTIVE profile immediately. Any existing ACTIVE profile in this loan-type chain will be SUPERSEDED.',
  'controlCenter.warehouseLines.publishProfile.activateConfirm': 'Activate',
  'controlCenter.warehouseLines.publishProfile.supersedeWarning':
    'Heads-up: version {{version}} is currently ACTIVE and will be superseded by this publish.',

  // ── Warehouse line — upload fallback / future-date validation (PR #1637 review) ──
  // Used when the upload mutation rejects without a server-supplied message
  // (e.g. network error before the server responds).
  'controlCenter.warehouseLines.uploadCreditBox.uploadFailedFallback': 'Upload failed.',
  'controlCenter.warehouseLines.uploadRateSheet.uploadFailedFallback': 'Upload failed.',
  // PublishProfileButton: scheduling requires a strictly-future activation
  // date. The original `dateInvalid` key was reused but the validation only
  // tested for parseability — this key is the dedicated future-date error.
  'controlCenter.warehouseLines.publishProfile.dateMustBeFuture': 'Effective-from date must be in the future.',

  // === Issue #1631 M5 — Re-extract change management ===
  // Per-profile diff view + supersession banner + notification template copy.

  // ── Profile diff view ───────────────────────────────────────────────────
  'controlCenter.warehouseLines.profileDiff.panelTitle': 'Compare v{{oldVersion}} → v{{newVersion}}',
  'controlCenter.warehouseLines.profileDiff.openButton': 'Compare version {{version}} against the active version',
  'controlCenter.warehouseLines.profileDiff.previousLabel': 'Previous',
  'controlCenter.warehouseLines.profileDiff.activeLabel': 'Active',
  'controlCenter.warehouseLines.profileDiff.supersededOn': 'Superseded on {{date}}',
  'controlCenter.warehouseLines.profileDiff.effectiveOn': 'Effective from {{date}}',
  'controlCenter.warehouseLines.profileDiff.legendAdded': 'Added',
  'controlCenter.warehouseLines.profileDiff.legendRemoved': 'Removed',
  'controlCenter.warehouseLines.profileDiff.legendChanged': 'Changed',
  'controlCenter.warehouseLines.profileDiff.noChanges': 'No differences between these two versions.',
  'controlCenter.warehouseLines.profileDiff.rulesTitle': 'Credit-box rules',
  'controlCenter.warehouseLines.profileDiff.rulesUnchanged': '{{count}} rule(s) unchanged.',
  'controlCenter.warehouseLines.profileDiff.docsTitle': 'Required documents',
  'controlCenter.warehouseLines.profileDiff.docsUnchanged': '{{count}} required-doc(s) unchanged.',
  'controlCenter.warehouseLines.profileDiff.pricingTitle': 'Pricing adjustments',
  'controlCenter.warehouseLines.profileDiff.pricingUnchanged': '{{count}} adjustment(s) unchanged.',

  // ── Supersession banner ─────────────────────────────────────────────────
  // Title keys use i18next plural form (`_one` / `_other`). The component
  // passes `count` and i18next selects the right variant automatically.
  // The legacy non-pluralized `.title` key is kept as a backstop for any
  // older renderers; new code should use `.title_one` / `.title_other`.
  'controlCenter.warehouseLines.supersessionBanner.title':
    '{{count}} in-flight case(s) pinned to v{{oldVersion}} of {{loanCategory}} profile',
  'controlCenter.warehouseLines.supersessionBanner.title_one':
    '{{count}} in-flight loan is pinned to v{{oldVersion}} of the {{loanCategory}} profile',
  'controlCenter.warehouseLines.supersessionBanner.title_other':
    '{{count}} in-flight loans are pinned to v{{oldVersion}} of the {{loanCategory}} profile',
  'controlCenter.warehouseLines.supersessionBanner.subtitle':
    'A new active version (v{{newVersion}}) just shipped. Re-size pinned cases if the new credit box changes their fundability.',
  'controlCenter.warehouseLines.supersessionBanner.resizeAction': 'Manually re-size',
  'controlCenter.warehouseLines.supersessionBanner.viewMore': '+ {{count}} more — open all',

  // ── Notification template (warehouse_line_credit_box_superseded) ────────
  // Copy mirrored from 27-notification-service/prisma/seed.ts; kept here so
  // any in-app preview surfaces don't duplicate the seed strings.
  //
  // The seed body uses a Handlebars `{{#if inflightCountKnown}}…{{else}}…
  // {{/if}}` conditional to distinguish "known and may be 0" from "could
  // not count". react-i18next has no equivalent conditional, so the body
  // is split into two keys; the consumer picks one based on the same
  // `inflightCountKnown` flag (see PR #1638 review C2). Both variables
  // (`inflightCount`, `inflightCountKnown`) must stay aligned with the
  // seed template — adding a third branch means updating both surfaces.
  'notifications.warehouseLineCreditBoxSuperseded.subject':
    'Warehouse line credit box updated: {{provider}} · {{line}} · {{loanCategory}}',
  'notifications.warehouseLineCreditBoxSuperseded.body.withCount':
    'Profile version v{{newVersion}} is now active for {{provider}} · {{line}} · {{loanCategory}}. {{inflightCount}} in-flight case(s) are pinned to v{{oldVersion}}.',
  'notifications.warehouseLineCreditBoxSuperseded.body.unknownCount':
    'Profile version v{{newVersion}} is now active for {{provider}} · {{line}} · {{loanCategory}}. In-flight cases pinned to v{{oldVersion}} may need re-sizing.',
  'notifications.warehouseLineCreditBoxSuperseded.action': 'Review chain',

  // ── Loan documents — fund-attribution badge (Issue #1631 M4) ──────────────
  // Renders next to a checklist item / data-room row when that requirement
  // was sourced from a pinned warehouse-line profile (vs. per-loan-product
  // or universal defaults). Two surfaces: the loan-detail data-room tab
  // (`{{fund}}` interpolation lets future polish swap in the resolved
  // provider · line · loan-type label) and the read-only admin
  // /control-center/document-checklists page (no interpolation — admins
  // manage warehouse-lines on a sibling page).
  'loans.documents.requiredByFund.badge': 'Required by {{fund}}',
  'loans.documents.requiredByFund.genericLabel': 'warehouse line',
  'loans.documents.requiredByFund.adminBadge': 'From warehouse line',
  'loans.documents.requiredByFund.tooltip':
    'This requirement is driven by the warehouse-line profile pinned on this loan. To change it, edit the profile under Control Center → Warehouse Lines.',

  // ============================================================================
  // Issue #1645 — Warehouse Lines gap audit (preallocated key stubs)
  // ============================================================================
  // These keys are placeholders for the Stream-T preallocation pass. Frontend
  // streams will refine copy in their respective PRs; the goal here is to
  // reserve namespaces so portal code can compile against the shared-types
  // package without a follow-up i18n round-trip.

  // ── Sidebar / Settings-hub tile copy ─────────────────────────────────────
  'controlCenter.warehouseLines.sidebarLabel': 'Warehouse Lines',
  'controlCenter.warehouseLines.tileTitle': 'Warehouse Lines',
  'controlCenter.warehouseLines.tileDescription':
    'Manage capital providers, warehouse lines, credit boxes, required documents, and per-loan-type profiles.',

  // ── Exception-desk queue page (D3 — /control-center/warehouse-line-exceptions) ──
  'controlCenter.warehouseLineExceptions.sidebarLabel': 'Exception Requests',
  'controlCenter.warehouseLineExceptions.tileTitle': 'Warehouse Line Exceptions',
  'controlCenter.warehouseLineExceptions.tileDescription':
    'Review pending warehouse-line exception requests and approve or deny in-flight overrides.',
  'controlCenter.warehouseLineExceptions.pageTitle': 'Warehouse Line Exceptions',
  'controlCenter.warehouseLineExceptions.pageSubtitle':
    'Approve or deny in-flight exceptions to warehouse-line credit boxes and required-doc rules.',
  'controlCenter.warehouseLineExceptions.tabs.pending': 'Pending',
  'controlCenter.warehouseLineExceptions.tabs.approved': 'Approved',
  'controlCenter.warehouseLineExceptions.tabs.denied': 'Denied',
  'controlCenter.warehouseLineExceptions.actions.approve': 'Approve',
  'controlCenter.warehouseLineExceptions.actions.deny': 'Deny',
  'controlCenter.warehouseLineExceptions.actions.viewCase': 'View loan',
  'controlCenter.warehouseLineExceptions.empty.pending.title': 'No pending requests',
  'controlCenter.warehouseLineExceptions.empty.pending.subtitle': 'New exception requests will appear here for review.',
  'controlCenter.warehouseLineExceptions.empty.approved.title': 'No approved requests yet',
  'controlCenter.warehouseLineExceptions.empty.approved.subtitle':
    'Approved exceptions will be archived here for audit.',
  'controlCenter.warehouseLineExceptions.empty.denied.title': 'No denied requests',
  'controlCenter.warehouseLineExceptions.empty.denied.subtitle': 'Denied exceptions will be archived here for audit.',

  // ── Request-exception dialog (D3 — case-detail FE1 trigger) ──
  'controlCenter.warehouseLineExceptions.requestDialog.cta': 'Request exception',
  'controlCenter.warehouseLineExceptions.requestDialog.title': 'Request warehouse-line exception',
  'controlCenter.warehouseLineExceptions.requestDialog.body':
    'Submit an override request for the rules that blocked this quote. Risk will review and approve or deny in the exception desk.',
  'controlCenter.warehouseLineExceptions.requestDialog.rulesHeading': 'Rules to override',
  'controlCenter.warehouseLineExceptions.requestDialog.noRulesAlert':
    'No blocking rules are eligible for an exception on this quote. Refresh the case and try again.',
  'controlCenter.warehouseLineExceptions.requestDialog.rationaleLabel': 'Rationale',
  'controlCenter.warehouseLineExceptions.requestDialog.rationalePlaceholder':
    'Explain why this exception is justified — borrower context, compensating factors, etc.',
  'controlCenter.warehouseLineExceptions.requestDialog.rationaleRequired': 'Rationale is required.',
  'controlCenter.warehouseLineExceptions.requestDialog.submit': 'Request exception',
  'controlCenter.warehouseLineExceptions.requestDialog.successToast':
    'Exception requested. Risk will review and notify you.',
  'controlCenter.warehouseLineExceptions.requestDialog.errorToast': 'Could not request exception: {{message}}',

  // ── Exception-desk queue page table columns (Issue #1645 G12/FE2) ────────
  'controlCenter.warehouseLineExceptions.columns.case': 'Loan',
  'controlCenter.warehouseLineExceptions.columns.profileVersion': 'Profile version',
  'controlCenter.warehouseLineExceptions.columns.rules': 'Rules',
  'controlCenter.warehouseLineExceptions.columns.requestedBy': 'Requested by',
  'controlCenter.warehouseLineExceptions.columns.requestedAt': 'Requested',
  'controlCenter.warehouseLineExceptions.columns.rationale': 'Rationale',
  'controlCenter.warehouseLineExceptions.columns.actions': 'Actions',
  'controlCenter.warehouseLineExceptions.columns.decidedAt': 'Decided',

  // ── Exception-desk queue page approve/deny dialog (Issue #1645 G12/FE2) ──
  'controlCenter.warehouseLineExceptions.decisionDialog.approveTitle': 'Approve exception?',
  'controlCenter.warehouseLineExceptions.decisionDialog.denyTitle': 'Deny exception?',
  'controlCenter.warehouseLineExceptions.decisionDialog.body':
    'Decision rationale is optional for approvals and required for denials. The borrower-facing toast and audit log will record this note.',
  'controlCenter.warehouseLineExceptions.decisionDialog.rationaleLabel': 'Rationale',
  'controlCenter.warehouseLineExceptions.decisionDialog.rationalePlaceholder':
    'Why is this exception approved or denied? Risk team and audit log will see this note.',
  'controlCenter.warehouseLineExceptions.decisionDialog.approveSubmit': 'Approve',
  'controlCenter.warehouseLineExceptions.decisionDialog.denySubmit': 'Deny',
  'controlCenter.warehouseLineExceptions.decisionDialog.approveSuccessToast': 'Exception approved.',
  'controlCenter.warehouseLineExceptions.decisionDialog.denySuccessToast': 'Exception denied.',
  // {{message}} is the canonical `translate()` interpolation token for error.message.
  'controlCenter.warehouseLineExceptions.decisionDialog.errorToast': 'Could not record decision: {{message}}',

  // ── Exception-desk queue page list-error banner (Issue #1645 G12/FE2) ────
  // {{message}} is the tRPC list error message bubbled up to the page.
  'controlCenter.warehouseLineExceptions.loadError': 'Could not load exception requests: {{message}}',

  // ── Exception-desk queue page detail SplitPanel (Issue #1645 G12/FE2) ────
  'controlCenter.warehouseLineExceptions.detail.title': 'Exception request',
  'controlCenter.warehouseLineExceptions.detail.requestSection': 'Request',
  'controlCenter.warehouseLineExceptions.detail.rulesSection': 'Blocking rules',
  'controlCenter.warehouseLineExceptions.detail.decisionSection': 'Decision',
  'controlCenter.warehouseLineExceptions.detail.requesterLabel': 'Requested by',
  'controlCenter.warehouseLineExceptions.detail.requestedAtLabel': 'Requested at',
  'controlCenter.warehouseLineExceptions.detail.rationaleLabel': 'Rationale',
  'controlCenter.warehouseLineExceptions.detail.deciderLabel': 'Decided by',
  'controlCenter.warehouseLineExceptions.detail.decidedAtLabel': 'Decided at',
  'controlCenter.warehouseLineExceptions.detail.decisionRationaleLabel': 'Decision rationale',
  'controlCenter.warehouseLineExceptions.detail.noDecisionRationale': '— no rationale recorded',

  // ── Case-detail unpin flow (Issue #1645 D4 — reason optional) ────────────
  'loans.unpinWarehouseLine.button': 'Unpin warehouse line',
  'loans.unpinWarehouseLine.dialogTitle': 'Unpin warehouse line?',
  'loans.unpinWarehouseLine.dialogBody':
    'Removing the pinned warehouse line will clear this loan’s fund attribution and re-open it for sizing. Existing CTFs are preserved.',
  'loans.unpinWarehouseLine.reasonLabel': 'Reason (optional)',
  'loans.unpinWarehouseLine.reasonPlaceholder': 'e.g. reassigning to a different fund, withdrawn loan, etc.',
  'loans.unpinWarehouseLine.confirm': 'Unpin',
  'loans.unpinWarehouseLine.cancel': 'Cancel',
  'loans.unpinWarehouseLine.successToast': 'Warehouse line unpinned.',
  // NOTE: `{{message}}` (double brace) is the canonical interpolation marker
  // for `translate()` (1-shared-types/src/i18n/utils.ts) — single-brace
  // `{message}` falls through unmodified into the rendered toast.
  'loans.unpinWarehouseLine.errorToast': 'Failed to unpin warehouse line: {{message}}',

  // ── Case-detail manual-pin flow (Issue #1645 D2 — sizer bypass) ──────────
  'loans.manualPinWarehouseLine.button': 'Pin without sizing',
  'loans.manualPinWarehouseLine.dialogTitle': 'Manually pin warehouse line',
  'loans.manualPinWarehouseLine.dialogBody':
    'Manual pin bypasses sizer rules. Use this only when the fund and profile version are already known (e.g. legacy or grandfathered loans).',
  'loans.manualPinWarehouseLine.linePickerLabel': 'Warehouse line',
  'loans.manualPinWarehouseLine.linePickerCaption':
    'Pick the capital provider and warehouse line this loan should be attributed to.',
  'loans.manualPinWarehouseLine.linePickerPlaceholder': 'Search warehouse lines…',
  'loans.manualPinWarehouseLine.linePickerEmpty': 'No active warehouse lines available.',
  'loans.manualPinWarehouseLine.profilePickerLabel': 'Loan-type profile version',
  'loans.manualPinWarehouseLine.profilePickerCaption':
    'Choose the published profile version that should govern documents and conditions to fund for this loan.',
  'loans.manualPinWarehouseLine.profilePickerPlaceholder': 'Select a profile version…',
  'loans.manualPinWarehouseLine.profilePickerEmpty':
    'No active profile versions for this line. Publish one before pinning manually.',
  'loans.manualPinWarehouseLine.reasonLabel': 'Reason',
  'loans.manualPinWarehouseLine.reasonPlaceholder': 'Why is this loan being pinned without running the sizer?',
  'loans.manualPinWarehouseLine.reasonRequired': 'A reason is required when pinning manually.',
  'loans.manualPinWarehouseLine.confirm': 'Pin',
  'loans.manualPinWarehouseLine.cancel': 'Cancel',
  'loans.manualPinWarehouseLine.successToast':
    'Warehouse line pinned manually. CTFs were not auto-created — generate them from the conditions tab if needed.',
  // See `loans.unpinWarehouseLine.errorToast` — `{{message}}` is the
  // canonical interpolation marker for the platform `translate()` utility.
  'loans.manualPinWarehouseLine.errorToast': 'Failed to manually pin warehouse line: {{message}}',
  'loans.manualPinWarehouseLine.ctfsNotCreatedNotice':
    'CTFs are not auto-created on manual pin. You will need to generate conditions to fund manually after pinning.',

  // ── Profile editor — shared "locked because PUBLISHED" gate (FC4) ────────
  'warehouseLines.profileGate.lockedTitle': 'This profile is {{state}}',
  'warehouseLines.profileGate.draftHint':
    'You can edit credit-box rules, required documents, and pricing while this version is in DRAFT. Publish to lock.',
  'warehouseLines.profileGate.publishedHint':
    'Published profile versions are immutable. To change anything, create a new version — the new draft starts as a clone of this version.',
  'warehouseLines.profileGate.archivedHint': 'Archived profile versions are read-only.',
  'warehouseLines.profileGate.cloneAction': 'Create new version',

  // ── Profile editor — credit-box rule edit (DRAFT only) ───────────────────
  'warehouseLines.creditBoxRule.edit.button': 'Edit rule',
  'warehouseLines.creditBoxRule.edit.dialogTitle': 'Edit credit-box rule',
  'warehouseLines.creditBoxRule.edit.save': 'Save changes',
  'warehouseLines.creditBoxRule.edit.cancel': 'Cancel',
  'warehouseLines.creditBoxRule.edit.draftOnlyHint':
    'Credit-box rules can only be edited while the parent profile version is in DRAFT. Publish to lock; re-version to change.',

  // ── Profile editor — required-doc rule edit (DRAFT only) ─────────────────
  'warehouseLines.requiredDocs.edit.requiredToggleLabel': 'Required',
  'warehouseLines.requiredDocs.edit.button': 'Edit rule',
  'warehouseLines.requiredDocs.edit.save': 'Save changes',
  'warehouseLines.requiredDocs.edit.cancel': 'Cancel',
  'warehouseLines.requiredDocs.edit.draftOnlyHint':
    'Required-doc rules can only be edited while the parent profile version is in DRAFT.',

  // ── Credit-box upload — extracted page count + manual override ───────────
  'warehouseLines.uploadCreditBox.pageCount.extractedCaption': 'Extracted from {{count}} page',
  'warehouseLines.uploadCreditBox.pageCount.extractedCaption_other': 'Extracted from {{count}} pages',
  'warehouseLines.uploadCreditBox.pageCount.manualOverrideLabel': 'Override page count',
  'warehouseLines.uploadCreditBox.pageCount.helperText':
    'Citations in extracted rules reference page numbers. We try to read this from PDFs automatically; for spreadsheets or if the auto-detect fails, type the page count here.',
  'warehouseLines.uploadCreditBox.pageCount.parseFailedCaption':
    'Could not auto-detect page count for this file — enter the count manually if you want page-range citation validation enabled.',

  // ── Credit-box upload — doc-pipeline status polling (Issue #1645 FO4 / G21)
  // After upload we poll `documents.byId` so the operator sees that the
  // doc-pipeline is processing their file rather than staring at a blank
  // textarea. Manual JSON paste stays available as the working fallback
  // until doc-pipeline exposes a typed `getCreditBoxExtraction` query —
  // tracked separately, this stream does the UI plumbing only.
  'warehouseLines.uploadCreditBox.docStatus.title': 'Document processing',
  'warehouseLines.uploadCreditBox.docStatus.pending': 'Queued',
  'warehouseLines.uploadCreditBox.docStatus.processing': 'Processing',
  'warehouseLines.uploadCreditBox.docStatus.completed': 'Processed',
  'warehouseLines.uploadCreditBox.docStatus.failed': 'Processing failed',
  'warehouseLines.uploadCreditBox.docStatus.unknown': 'Status unknown',
  'warehouseLines.uploadCreditBox.docStatus.processingHint':
    'The document-extraction pipeline is reading your file. You can paste the extracted JSON below as soon as it is ready, or wait for the automatic flow to land.',
  'warehouseLines.uploadCreditBox.docStatus.completedHint':
    'Document processed. Paste the extracted JSON payload below to ingest it.',
  'warehouseLines.uploadCreditBox.docStatus.failedHint':
    'The document-extraction pipeline failed for this file. Re-upload the file or paste the extraction JSON manually.',
  'warehouseLines.uploadCreditBox.docStatus.timeoutHint':
    'Still waiting on the document-extraction pipeline. You can keep waiting (the badge will update) or paste the JSON manually.',
  'warehouseLines.uploadCreditBox.docStatus.retryButton': 'Retry upload',

  // ── Profile editor — rate-tier + points-tier pickers ─────────────────────
  'warehouseLines.profileEditor.rateTier.pickerLabel': 'Rate tier',
  'warehouseLines.profileEditor.rateTier.empty': 'No active rate tiers defined yet.',
  'warehouseLines.profileEditor.rateTier.emptyAction': 'Manage rate tiers',
  'warehouseLines.profileEditor.rateTier.permissionDenied':
    'You do not have permission to list rate tiers. Ask an admin with the Sizer Manage-Rules role to attach a tier to this profile.',
  'warehouseLines.profileEditor.pointsTier.pickerLabel': 'Points tier',
  'warehouseLines.profileEditor.pointsTier.empty': 'No active points tiers defined yet.',
  'warehouseLines.profileEditor.pointsTier.emptyAction': 'Manage points tiers',
  'warehouseLines.profileEditor.tierOption.label': 'Tier {{tier}}',
  'warehouseLines.profileEditor.tierOption.labelWithName': 'Tier {{tier}} – {{name}}',
  'warehouseLines.profileEditor.tierOption.effective': 'Effective {{date}}',
  'warehouseLines.profileEditor.tierOption.unknown': 'Tier no longer active (id ends …{{idTail}})',
  'warehouseLines.profileEditor.tier.none': '— None —',

  // ── Detail page skeleton (loading state aria-labels) ─────────────────────
  'warehouseLines.detail.skeleton.header': 'Loading warehouse line details',
  'warehouseLines.detail.skeleton.creditBox': 'Loading credit box rules',
  'warehouseLines.detail.skeleton.requiredDocs': 'Loading required documents',
  'warehouseLines.detail.skeleton.profiles': 'Loading loan-type profiles',

  // ── Supersession banner (M5 follow-up — affected in-flight cases) ────────
  // i18next-style plural keys: the platform's `translate()` does not yet
  // execute the plural rule (see 1-shared-types/src/i18n/utils.ts), so the
  // bare key is the SINGULAR fallback and call sites must select the
  // `_other` variant explicitly when `count !== 1`. The `_one` and `_other`
  // suffixes mirror the canonical i18next contract so a future migration
  // to a CLDR-aware translator only flips on the rule executor — no copy
  // moves required. See also `controlCenter.warehouseLines.supersessionBanner.title*`
  // earlier in this file (same pattern).
  'warehouseLines.supersessionBanner.affectedCases': '{{count}} in-flight loan is pinned to the previous version',
  'warehouseLines.supersessionBanner.affectedCases_one': '{{count}} in-flight loan is pinned to the previous version',
  'warehouseLines.supersessionBanner.affectedCases_other':
    '{{count}} in-flight loans are pinned to the previous version',
  'warehouseLines.supersessionBanner.action': 'Review affected loans',

  // ── Command palette commands (Issue #1645 FD3) ───────────────────────────
  'warehouseLines.commandPalette.list.label': 'Warehouse lines',
  'warehouseLines.commandPalette.list.description': 'Manage capital providers and warehouse-line profiles',
  'warehouseLines.commandPalette.exceptions.label': 'Warehouse-line exceptions',
  'warehouseLines.commandPalette.exceptions.description': 'Review pending warehouse-line exception requests',
  'warehouseLines.commandPalette.sizerPolicyByLine.label': 'Sizer policy by line',
  'warehouseLines.commandPalette.sizerPolicyByLine.description':
    'Inspect the effective rule set for a specific warehouse line',

  // ── SCD2 createNewVersion flow (Issue #1645 FS2) ─────────────────────────
  'controlCenter.warehouseLines.profileEditor.createNewVersionTitle': 'Create new version',
  'controlCenter.warehouseLines.profileEditor.createNewVersionNotice':
    'Saving will create a new DRAFT version forked from v{{version}}. The current ACTIVE version stays live until you publish the new draft.',
  'controlCenter.warehouseLines.profiles.toast.newVersionCreated': 'New draft version created',
  // Defensive validators on the editor save path. These should never
  // surface in normal use — the parent page is responsible for opening
  // the editor with the right mode + profile combination — but we
  // surface them rather than silently routing to the wrong mutation
  // (the exact regression FS2 closes; see the file-level docstring on
  // LoanTypeProfileEditor.tsx).
  'controlCenter.warehouseLines.profileEditor.validateMissingProfile':
    'Cannot save: no profile in scope for this mode.',
  'controlCenter.warehouseLines.profileEditor.validateUnknownMode': 'Cannot save: editor is in an unknown mode.',

  // ── Sizer-policy "By line" view (Issue #1645 FD4) ────────────────────────
  'controlCenter.sizerPolicy.tab.byLine': 'By line',
  'controlCenter.sizerPolicy.byLine.title': 'Effective rule set by warehouse line',
  'controlCenter.sizerPolicy.byLine.description':
    'Inspect the credit-box rules, required docs, global rules, and pricing adjustments for a single warehouse line. Edits open in the warehouse-line management surface.',
  'controlCenter.sizerPolicy.byLine.linePickerLabel': 'Warehouse line',
  'controlCenter.sizerPolicy.byLine.noLines': 'No warehouse lines configured for this business yet.',
  'controlCenter.sizerPolicy.byLine.noLinesCta': 'Add a warehouse line',
  'controlCenter.sizerPolicy.byLine.noProfiles': 'No active loan-type profiles published on this line yet.',
  'controlCenter.sizerPolicy.byLine.profilesSection': 'Loan-type profiles',
  'controlCenter.sizerPolicy.byLine.creditBoxSection': 'Credit-box rules',
  'controlCenter.sizerPolicy.byLine.requiredDocsSection': 'Required documents',
  'controlCenter.sizerPolicy.byLine.globalRulesSection': 'Global rules (line-wide)',
  'controlCenter.sizerPolicy.byLine.pricingAdjustmentsSection': 'Pricing adjustments',
  'controlCenter.sizerPolicy.byLine.editLineLink': 'Edit on warehouse-line page',
  'controlCenter.sizerPolicy.byLine.editProfileLink': 'Edit profile',
  'controlCenter.sizerPolicy.byLine.versionLabel': 'Version {{version}}',
  'controlCenter.sizerPolicy.byLine.empty.creditBox': 'No credit-box rules on the active version.',
  'controlCenter.sizerPolicy.byLine.empty.requiredDocs': 'No required documents on the active version.',
  'controlCenter.sizerPolicy.byLine.empty.globalRules': 'No line-wide global rules.',
  'controlCenter.sizerPolicy.byLine.empty.pricingAdjustments': 'No pricing adjustments on the active version.',

  // =========================================================================
  // Loan-case stage indicator + stage-map drawer (UX v3 refinement)
  // Compact replacement for the horizontal phased stepper that scales to 20+
  // production stages without consuming a full row.
  // =========================================================================
  'cases.stageIndicator.counter': 'Stage {{current}}/{{total}}',
  'cases.stageIndicator.next': 'Next',
  'cases.stageIndicator.metCounter': '{{met}}/{{total}} met',
  // PR #1741: shared-types translate() doesn't apply ICU plural rules — callers
  // must select _one vs _other manually based on count. The non-suffixed key
  // is intentionally absent so a stale call falls through to the dev warning.
  'cases.stageIndicator.blockerCount_one': '{{count}} blocker',
  'cases.stageIndicator.blockerCount_other': '{{count}} blockers',
  'cases.stageIndicator.stageMap': 'Stage map',
  'cases.stageIndicator.mapSubtitle': 'Stage {{current}} of {{total}}',
  'cases.stageIndicator.unphased': 'Stages',
  'cases.stageIndicator.reqCount': '{{count}} reqs',
  'cases.stageIndicator.here': 'here',
  'cases.stageIndicator.cannotAdvance': 'Cannot advance to',
  'cases.stageIndicator.blockersIntro': '{{count}} required field(s) still need to be filled in:',
  'cases.stageIndicator.askAstor': 'Ask Astor to fix',
  'cases.stageIndicator.progressAriaLabel': 'Stage {{current}} of {{total}}',
  'cases.stageIndicator.blockersAriaLabel': '{{count}} blockers stop advancing to {{next}}',
  'cases.stageIndicator.allMetAriaLabel': 'All requirements met for {{next}}',
  'cases.stageIndicator.requirementsLoadError': "Couldn't load requirements",
  // Fallback label rendered in the stage indicator when the stage-list query
  // fails (permissions / seed gap) so the page chrome still shows a "currently
  // here" pill instead of the entire stage row disappearing.
  'cases.stageIndicator.currentStageFallback': 'Current stage',
  // Phase label fallback for stages that don't match any `LOAN_LIFECYCLE_PHASES`
  // entry — surfaces as a section header in the Stage Map drawer.
  'cases.loanPhases.other': 'Other',

  // =========================================================================
  // StageIndicator20 — v3 dedicated 20-dot lifecycle strip (Slice A1).
  // Compact horizontal dot row of all stages with hover tooltip + blocker
  // alert + "Open stage map" trigger. Distinct from `StageIndicator` (the
  // pill-pair compact row); used on loan-detail headers where space allows.
  // =========================================================================
  'cases.stageIndicator20.openStageMap': 'Open stage map',
  'cases.stageIndicator20.stageOf': 'Stage {{current}} of {{total}}',
  'cases.stageIndicator20.blockerAlert': '{{count}} required field(s) for {{stage}}',
  'cases.stageIndicator20.blockerAriaLabel': '{{stage}} is blocked by {{count}} missing field(s)',
  'cases.stageIndicator20.tooltipPast': '{{number}}. {{name}} · done',
  'cases.stageIndicator20.tooltipCurrent': '{{number}}. {{name}} · current',
  'cases.stageIndicator20.tooltipFuture': '{{number}}. {{name}}',
  'cases.stageIndicator20.progressAriaLabel': 'Stage {{current}} of {{total}}',
  'cases.stageMapDrawer.title': 'Stage map',
  'cases.stageMapDrawer.subtitle': 'Stage {{current}} of {{total}}',
  'cases.stageMapDrawer.close': 'Close stage map',

  // -------------------------------------------------------------------------
  // Stage map phase headings (Slice A2 — v3 20-stage lifecycle).
  // Surfaced both inline in the StageMapDrawer phase sections and as the
  // `phase` chip on the StageIndicator20 dot tooltips.
  // -------------------------------------------------------------------------
  'cases.stageMap.phase.setup': 'Setup',
  'cases.stageMap.phase.underwriting': 'Underwriting',
  'cases.stageMap.phase.approval': 'Approval',
  'cases.stageMap.phase.closing': 'Closing',
  'cases.stageMap.phase.servicing': 'Servicing',

  // -------------------------------------------------------------------------
  // Stage indicator 20 — page-level wrapper copy (Slice A2). The primitive
  // owns its own copy under `cases.stageIndicator20.*` above; these are the
  // page-wrapper strings that flow into the blocker tooltip + degraded
  // fallback row used by the loan-case detail page wrapper.
  // -------------------------------------------------------------------------
  'cases.stage20.blockerStageFallback': 'next stage',
  'cases.stage20.reqsLoadError': "Couldn't load entry requirements — try refreshing.",

  // =========================================================================
  // LiveSizingSlider — sizing slider with cap marker and Astor `ai` pin
  // (Slice A1). Used on the loan-detail Sizing tab.
  // =========================================================================
  'sizing.slider.capLabel': 'cap',
  'sizing.slider.aiLabel': 'ai',
  'sizing.slider.valueAriaLabel': '{{label}} value',

  // -------------------------------------------------------------------------
  // Sizing tab — Live sizing rail (Slice A2). Four-slider strip showing
  // Amount / Rate / Points / Term against the leading capital line's caps
  // and Astor's recommended values.
  // -------------------------------------------------------------------------
  'cases.sizing.live.title': 'Live sizing',
  'cases.sizing.live.subtitle': '{{provider}} — {{line}}',
  'cases.sizing.live.amount': 'Amount',
  'cases.sizing.live.rate': 'Interest rate',
  'cases.sizing.live.points': 'Points',
  'cases.sizing.live.term': 'Term',
  'cases.sizing.live.emptyHint':
    'Run sizing to see the leading capital line as live sliders with Astor’s recommendation.',

  // =========================================================================
  // CitationPill / ToolTag — Astor output inline references (Slice A1).
  // =========================================================================
  'astor.citationPill.sourceAriaLabel': 'Source citation: {{label}}',
  'astor.citationPill.toolAriaLabel': 'Tool reference: {{label}}',
  'astor.toolTag.ariaLabel': 'Tool: {{label}}',

  // =========================================================================
  // AstorReasoningBlock — collapsible chain-of-thought disclosure with left
  // mint bar (Slice A1). Builds on AstorReasoningTrace for the tool list.
  // =========================================================================
  'astor.reasoningBlock.summaryLabel': 'Astor reasoning',
  'astor.reasoningBlock.expand': 'Show full reasoning',
  'astor.reasoningBlock.collapse': 'Hide reasoning',
  'astor.reasoningBlock.confidence': 'conf {{value}}',
  'astor.reasoningBlock.tools': 'Tools used',

  // =========================================================================
  // DealSheetThumbnail — rotated term-sheet card mockup used as hero element
  // on white-labeled marketing sites (Slice A1).
  // =========================================================================
  'website.dealSheet.label': 'Term sheet',
  'website.dealSheet.borrower': 'Borrower',
  'website.dealSheet.property': 'Subject property',
  'website.dealSheet.loanAmount': 'Loan amount',
  'website.dealSheet.loanType': 'Loan type',
  'website.dealSheet.rate': 'Rate',
  'website.dealSheet.points': 'Points',
  'website.dealSheet.term': 'Term',
  'website.dealSheet.monthlyInt': 'Monthly int.',
  'website.dealSheet.ltc': 'LTC',
  'website.dealSheet.ltarv': 'LTARV',
  'website.dealSheet.fico': 'FICO',
  'website.dealSheet.authorizedUnderwritten': 'Authorized · Underwritten',
  'website.dealSheet.stampApproved': 'Approved',
  'website.dealSheet.stampPending': 'Pending',
  'website.dealSheet.cornerSent24h': 'Sent in 24 hrs',

  // =========================================================================
  // v4 editorial conversion pages — Home / QuoteTool / Products / ProductDetail
  // (Slice B3). Generic copy only: page content is brand- and CMS-driven, so
  // these strings cover chrome (CTAs, eyebrows, section labels, form chrome,
  // FAQ scaffolding, trust strips). Per CLAUDE.md §11d nothing here may carry
  // a tenant brand name — actual brand names flow from `BrandConfig`.
  // =========================================================================

  // Shared eyebrows / chapter labels reused across all 4 surfaces
  'website.conversion.chapter01': '§ 01',
  'website.conversion.chapter02': '§ 02',
  'website.conversion.chapter03': '§ 03',
  'website.conversion.chapter04': '§ 04',

  // Home — funded-deal feed, manifesto, stats, pre-qual CTA strip
  'website.conversion.home.eyebrow': 'PRIVATE LENDING · BUILT BY OPERATORS',
  'website.conversion.home.dealsEyebrow': 'RECENT CLOSINGS',
  'website.conversion.home.dealsHeadline': "We close what we quote. Here's the last",
  'website.conversion.home.dealsItalic': 'thirty days.',
  'website.conversion.home.dealsSub':
    'Every deal we fund. Property type, market, leverage, days from inquiry to wire. No marketing-edited timelines — actual data, refreshed daily.',
  'website.conversion.home.statsFunded': 'Funded · 30 days',
  'website.conversion.home.statsDealCount': 'Loans closed',
  'website.conversion.home.statsAvgDays': 'Avg days to fund',
  'website.conversion.home.statsAvgSize': 'Avg loan size',
  'website.conversion.home.seeAllDeals': 'See every deal closed',
  'website.conversion.home.manifestoEyebrow': 'WHY WE EXIST',
  'website.conversion.home.trustEyebrow': 'WHAT WE FUND',
  'website.conversion.home.trustHeadline': 'Four programs. Sized between',
  'website.conversion.home.trustItalic': '$500K and $10M.',
  'website.conversion.home.preQualEyebrow': '§ 05 · THE NEXT STEP',
  'website.conversion.home.preQualHeadline': 'Bring us your deal.',
  'website.conversion.home.preQualItalic': "We'll quote it in a day.",
  'website.conversion.home.preQualSub':
    'Submit your numbers, get a term sheet within 24 hours, close in 7–10 days. No commitment, no hard credit pull, no broker chain.',
  'website.conversion.home.quoteCta': 'Quote my deal',
  'website.conversion.home.orCall': 'or call',
  'website.conversion.home.lockTerms': 'Lock these terms',
  'website.conversion.home.dealSheetCorner': 'SENT IN 24 HRS',
  'website.conversion.home.dealSheetStamp': 'APPROVED · 7 DAYS',
  'website.conversion.home.dealSheetCaption':
    'Full term sheet — locked numbers, draw schedule, fee breakdown — in your inbox inside 24 hours.',
  // Sample deal defaults — rendered on the hero deal-sheet thumbnail when
  // the CMS doesn't supply a sampleDeal override. Intentionally generic
  // (no city/state anchor, no real-looking entity name) so they read as
  // illustrative on every white-label tenant, not as another tenant's deal.
  'website.conversion.home.sampleDeal.borrowerName': 'Sample Holdings LLC',
  'website.conversion.home.sampleDeal.borrowerMeta': 'EIN ••-•••3344 · LLC',
  'website.conversion.home.sampleDeal.propertyAddress': 'Subject Property · Sample Address',
  'website.conversion.home.sampleDeal.propertyMeta': 'SFR · 3bd/2ba · 1,640 sqft',
  'website.conversion.home.sampleDeal.signature': 'A. Diaz',
  'website.conversion.home.sampleDeal.loanType': 'Bridge / F&F',
  'website.conversion.home.leadCaptureSuccess': "Quote sent — we'll be in touch within 24 hours.",
  'website.conversion.home.leadCaptureError':
    'Something went wrong sending your quote. Please try again or call us directly.',

  // § 05 BY THE NUMBERS section (fund-report aesthetic, 6 metrics)
  'website.conversion.home.byNumbersChapter': '§ 05',
  'website.conversion.home.byNumbersEyebrow': 'BY THE NUMBERS',
  'website.conversion.home.byNumbersHeadline': 'How we measure ourselves.',
  'website.conversion.home.byNumbersMetric.avgSize.label': 'Average loan size',
  'website.conversion.home.byNumbersMetric.avgSize.sub': 'last 30 days · $500k – $10M range',
  'website.conversion.home.byNumbersMetric.borrowerNps.label': 'Borrower NPS',
  'website.conversion.home.byNumbersMetric.borrowerNps.sub': 'last 12 months',
  'website.conversion.home.byNumbersMetric.medianDaysToFund.label': 'Median days from inquiry to wire',
  'website.conversion.home.byNumbersMetric.medianDaysToFund.sub': 'last 30 days',
  'website.conversion.home.byNumbersMetric.noRepricingRate.label': 'Loans never re-priced post term sheet',
  'website.conversion.home.byNumbersMetric.noRepricingRate.sub': 'lifetime · industry avg 71%',
  'website.conversion.home.byNumbersMetric.onTimeRate.label': 'On-time funding rate',
  'website.conversion.home.byNumbersMetric.onTimeRate.sub': 'last 12 months',
  'website.conversion.home.byNumbersMetric.repeatBorrowerShare.label': 'Repeat borrower share',
  'website.conversion.home.byNumbersMetric.repeatBorrowerShare.sub': 'last 12 months',

  // § Calculator section header — sits above the inline calculator
  'website.conversion.home.calcSectionSampleLabel': 'SAMPLE · FIX & FLIP · OAKLAND, CA',
  'website.conversion.home.calcSectionTitle': 'Your deal · our quote.',

  // § Hero — dealsheet caption strong intro (rendered bold beside dealSheetCaption)
  'website.conversion.home.dealSheetCaptionIntro': 'This is what you get.',

  // § 03 WHAT WE FUND — 4-product breakdown
  'website.conversion.home.fundHeadline': 'Four programs. Sized between',
  'website.conversion.home.fundItalic': '$500k and $10M.',
  // 4 default products — designer-spec values. CMS overrides via the
  // `loanProducts` prop on `<Home>`; these defaults render when none provided.
  'website.conversion.home.fundProduct.bridge.body':
    'Short-term capital for acquisitions, refinances, and cash-out on stabilized properties. Multi-family, mixed-use, and small commercial. 12-month standard term with no prepayment penalty — exit when the take-out is ready.',
  'website.conversion.home.fundProduct.bridge.ltv': '80% LTV',
  'website.conversion.home.fundProduct.bridge.name': 'Bridge',
  'website.conversion.home.fundProduct.bridge.rate': '8.50 – 11.99%',
  'website.conversion.home.fundProduct.bridge.term': '12 months',
  'website.conversion.home.fundProduct.construction.body':
    'Staged financing for new residential construction. We fund land acquisition (up to 60%) and 100% of vertical construction cost. Draw-based releases tied to milestone inspections — no surprises, no delays.',
  'website.conversion.home.fundProduct.construction.ltv': '90% LTC · 70% ARV',
  'website.conversion.home.fundProduct.construction.name': 'Ground-Up Construction',
  'website.conversion.home.fundProduct.construction.rate': '8.99 – 10.99%',
  'website.conversion.home.fundProduct.construction.term': '12 – 18 months',
  'website.conversion.home.fundProduct.dscr.body':
    "Long-term financing for income-producing rentals. We qualify on the property's cash flow at 1.0x DSCR minimum — no personal income docs. Up to 10 years interest-only, then 30-year amortization. Built for portfolio investors.",
  'website.conversion.home.fundProduct.dscr.ltv': '80% LTV',
  'website.conversion.home.fundProduct.dscr.name': 'DSCR Bridge Plus',
  'website.conversion.home.fundProduct.dscr.rate': 'from 6.00%',
  'website.conversion.home.fundProduct.dscr.term': 'up to 30 years',
  'website.conversion.home.fundProduct.fixFlip.body':
    'Short-term capital for acquiring and renovating residential properties. We fund up to 100% of your rehab budget — reimbursed in 72-hour draws as work completes. No prepayment penalty if you exit faster than projected.',
  'website.conversion.home.fundProduct.fixFlip.ltv': '90% LTV · 75% ARV',
  'website.conversion.home.fundProduct.fixFlip.name': 'Fix & Flip',
  'website.conversion.home.fundProduct.fixFlip.rate': '8.50 – 10.99%',
  'website.conversion.home.fundProduct.fixFlip.term': '12 – 24 months',
  'website.conversion.home.fundProductMetadata.leverage': 'LEVERAGE',
  'website.conversion.home.fundProductMetadata.rate': 'RATE',
  'website.conversion.home.fundProductMetadata.term': 'TERM',
  'website.conversion.home.fundReadTermSheet': 'Read the term sheet',

  // § 01 manifesto — signature + caption beneath the founder body (dark navy bg)
  'website.conversion.home.manifestoSignature': '— David & Roger',
  'website.conversion.home.manifestoSignatureCaption': 'FT. MYERS · MAY 2026',

  // § Operator signature line — between calculator and the manifesto, links to /leadership
  'website.conversion.home.operatorSignature.copy':
    " review every deal above $1M personally. They've operated, financed, and exited deals like yours.",
  'website.conversion.home.operatorSignature.cta': 'Meet the operators',
  // Joiner for the two operator names in the signature line. Locale-specific.
  'website.conversion.home.operatorSignature.namesJoiner': ' & ',

  // § 04 OPERATORS section (founder portraits)
  'website.conversion.home.operatorsChapter': '§ 04',
  'website.conversion.home.operatorsEyebrow': 'OPERATORS',
  'website.conversion.home.operatorsHeadline': "The people you'll",
  'website.conversion.home.operatorsItalic': 'actually talk to.',
  'website.conversion.home.operatorsPortraitInitialsLabel': 'PORTRAIT · {{initials}}',
  // Brand-neutral tagline fallback per operator slot (slot 0 / slot 1).
  // The CMS-driven LeadershipBio.tagline overrides these when set.
  'website.conversion.home.operatorsTaglineDefault.0': 'The builder.',
  'website.conversion.home.operatorsTaglineDefault.1': 'The structurer.',

  // § Pre-qual / final CTA — updated chapter number (v1 key kept for back-compat)
  'website.conversion.home.preQualEyebrowV2': '§ 06 · THE NEXT STEP',

  // Quote tool — focused lead capture
  'website.conversion.quoteTool.eyebrow': 'THE QUOTE TOOL · NO CREDIT PULL',
  'website.conversion.quoteTool.headline': 'Get a real quote in',
  'website.conversion.quoteTool.italic': '24 hours.',
  'website.conversion.quoteTool.sub':
    'The same underwriting math we use internally. Type your numbers, see real rates, leverage, and projected profit — instantly.',
  'website.conversion.quoteTool.trustPenciled': 'Investors quoted this year',
  'website.conversion.quoteTool.trustMedian': 'Median time to term sheet',
  'website.conversion.quoteTool.trustNoCreditPull': 'No hard credit pull until close',
  'website.conversion.quoteTool.faqEyebrow': 'THE FINE PRINT',
  'website.conversion.quoteTool.faqHeadline': 'What this quote is —',
  'website.conversion.quoteTool.faqItalic': "and isn't.",

  // QuoteTool v3 extension (#1860) — 5 sliders + loan-type tabs + email gate
  'website.conversion.quoteTool.dealInputsLabel': 'Deal inputs',
  'website.conversion.quoteTool.loanTypeTabsAriaLabel': 'Loan program',
  'website.conversion.quoteTool.loanTypes.fixAndFlip': 'Fix & Flip',
  'website.conversion.quoteTool.loanTypes.construction': 'Construction',
  'website.conversion.quoteTool.loanTypes.bridge': 'Bridge',
  'website.conversion.quoteTool.loanTypes.dscr': 'DSCR',
  'website.conversion.quoteTool.fields.purchase': 'Purchase price',
  'website.conversion.quoteTool.fields.rehab': 'Rehab budget',
  'website.conversion.quoteTool.fields.arv': 'After-repair value',
  'website.conversion.quoteTool.fields.holdingPeriod': 'Holding period',
  'website.conversion.quoteTool.fields.sponsorExperience': 'Sponsor experience',
  'website.conversion.quoteTool.ranges.purchase': '$500K – $10M',
  'website.conversion.quoteTool.ranges.rehab': '$0 – $2M',
  'website.conversion.quoteTool.ranges.arv': '$500K – $15M',
  'website.conversion.quoteTool.ranges.holdingPeriod': '6 – 24 months',
  'website.conversion.quoteTool.ranges.sponsorExperience': 'First-time → 20+ exits',
  'website.conversion.quoteTool.values.months': '{{months}} months',
  'website.conversion.quoteTool.values.exits': '{{exits}}+ exits',
  'website.conversion.quoteTool.values.firstTime': 'First-time',
  'website.conversion.quoteTool.liveWithinBox': 'Live · within credit box',
  'website.conversion.quoteTool.liveOutsideBox': 'Outside credit box · request manual review',
  'website.conversion.quoteTool.loanAmountLabel': 'loan amount',
  'website.conversion.quoteTool.atRate': 'at {{rate}} for {{months}} months · {{points}} points',
  'website.conversion.quoteTool.metrics.ltc': 'LTC',
  'website.conversion.quoteTool.metrics.ltarv': 'LTARV',
  'website.conversion.quoteTool.metrics.monthlyInterest': 'Monthly interest',
  'website.conversion.quoteTool.metrics.cashToClose': 'Cash to close',
  'website.conversion.quoteTool.metrics.projectedSale': 'Projected sale',
  'website.conversion.quoteTool.metrics.projectedProfit': 'Projected profit',
  'website.conversion.quoteTool.metricSubs.ltcCap': 'cap {{cap}}',
  'website.conversion.quoteTool.metricSubs.ltarvCap': 'cap {{cap}}',
  'website.conversion.quoteTool.metricSubs.totalInterest': '~{{total}} total',
  'website.conversion.quoteTool.metricSubs.cashToClose': 'down payment + closing',
  'website.conversion.quoteTool.metricSubs.projectedSale': 'net of selling costs',
  'website.conversion.quoteTool.metricSubs.projectedProfit': 'before tax',
  'website.conversion.quoteTool.emailCard.headline': 'Get your full deal sheet.',
  'website.conversion.quoteTool.emailCard.sub':
    'Locked terms, full amortization, draw schedule, fee breakdown. Delivered in 24 hours. No credit pull.',
  'website.conversion.quoteTool.emailCard.emailLabel': 'Email',
  'website.conversion.quoteTool.emailCard.emailPlaceholder': 'your@email.com',
  'website.conversion.quoteTool.emailCard.submitCta': 'Send me terms',
  'website.conversion.quoteTool.emailCard.submitting': 'Sending…',
  'website.conversion.quoteTool.emailCard.disclosure': 'NO SPAM · NO CREDIT PULL · UNSUBSCRIBE ANYTIME',
  'website.conversion.quoteTool.estimateFootnote':
    'Estimates based on typical {{program}} terms — actual rate and structure depend on full underwriting.',
  'website.conversion.quoteTool.validation.emailRequired': 'A valid email address is required',
  'website.conversion.quoteTool.submitSuccess': "Deal sheet on the way — we'll be in touch within 24 hours.",
  'website.conversion.quoteTool.submitError':
    'Something went wrong sending your deal sheet. Please try again or call us directly.',
  'website.conversion.quoteTool.trustStrip.dealsPenciledCount': '14,200+ deals',
  'website.conversion.quoteTool.trustStrip.dealsPenciledSuffix': 'penciled this year',
  'website.conversion.quoteTool.trustStrip.medianPrefix': 'Median time to term sheet',
  'website.conversion.quoteTool.trustStrip.medianValue': '22 hours',
  'website.conversion.quoteTool.trustStrip.noCreditPull': 'No hard credit pull until close',

  // Products — editorial program list
  'website.conversion.products.eyebrow': 'LOAN PROGRAMS · $500K – $10M · NATIONWIDE',
  'website.conversion.products.headline': 'Four programs.',
  'website.conversion.products.italic': 'Built by operators.',
  'website.conversion.products.headlineTail': '',
  'website.conversion.products.sub':
    'Every program is structured around how investors actually run deals. Fast closings, transparent pricing, draw schedules tied to milestones — not arbitrary calendar dates.',
  'website.conversion.products.listEyebrow': 'WHAT WE FUND',
  'website.conversion.products.rateLabel': 'RATE',
  'website.conversion.products.leverageLabel': 'LEVERAGE',
  'website.conversion.products.termLabel': 'TERM',
  'website.conversion.products.applyCta': 'Apply now',
  'website.conversion.products.readSheet': 'Read the term sheet',
  'website.conversion.products.quoteDeal': 'Quote a deal',

  // Product detail — single program surface
  'website.conversion.productDetail.whoEyebrow': 'WHO THIS IS FOR',
  'website.conversion.productDetail.whoHeadline': 'Built for the',
  'website.conversion.productDetail.whoItalic': 'operator borrower.',
  'website.conversion.productDetail.termsEyebrow': 'RATES & TERMS',
  'website.conversion.productDetail.termsHeadline': 'What you can expect',
  'website.conversion.productDetail.termsItalic': 'when we quote.',
  'website.conversion.productDetail.eligibilityEyebrow': 'ELIGIBILITY',
  'website.conversion.productDetail.eligibilityHeadline': 'A short list of',
  'website.conversion.productDetail.eligibilityItalic': 'requirements.',
  'website.conversion.productDetail.faqEyebrow': 'COMMON QUESTIONS',
  'website.conversion.productDetail.faqHeadline': 'Questions we',
  'website.conversion.productDetail.faqItalic': 'hear most.',
  'website.conversion.productDetail.quoteCta': 'Quote this deal',
  'website.conversion.productDetail.ctaHeadline': 'Ready to size your',
  'website.conversion.productDetail.ctaItalic': 'deal?',

  // Generic / shared chrome
  'website.conversion.common.dotSeparator': '·',
  'website.conversion.common.arrow': '→',
  'website.conversion.common.requestQuote': 'Request a quote',
  'website.conversion.common.faqExpand': 'Expand answer',
  'website.conversion.common.faqCollapse': 'Collapse answer',
  'website.conversion.common.ratePlaceholder': 'Rate',
  'website.conversion.common.ltvPlaceholder': 'LTV',
  'website.conversion.common.pointsPlaceholder': 'Points',
  'website.conversion.common.termPlaceholder': 'Term',

  // =========================================================================
  // Website v4 — Coastal Inquiry page (closes Issue #1865)
  //
  // REMOVED in the v3 design pass (PR #1874): the hand-rolled §01/§02/§03
  // Inquiry form was replaced with the shared embed `IntakePageByPage`
  // wizard so partner-apply + /embed/intake + standalone tenant /intake
  // all run one flow. ~70 keys here (eyebrow / sections / fields /
  // loanTypes / propertyTypes / markets / validation) became orphan
  // references. The embed flow's own copy lives under
  // `website.embed.intake.*` and `website.embed.poweredBy` etc. — no
  // re-mapping needed because the embed components own their strings.
  //
  // =========================================================================

  // =========================================================================
  // Website v4 — Narrative pages (About / Leadership / Markets / Partners).
  // Generic, brand-neutral copy. All tenant-specific content (founder names,
  // region body text, partner program details) flows from CMS props. These
  // keys carry the chrome — eyebrows, section titles, stat labels, CTA labels.
  // Slice B4 of /fiops-design-pass run 20260516-design-handoff-v3 (Issue #1819).
  // =========================================================================

  // About — page chrome
  'website.narrative.about.eyebrow': 'ABOUT',
  'website.narrative.about.pullQuoteEyebrow': 'IN OUR WORDS',
  'website.narrative.about.ctaEyebrow': 'WORK WITH US',
  'website.narrative.about.ctaHeadline': 'Bring us your deal.',
  'website.narrative.about.ctaItalic': 'your deal.',
  'website.narrative.about.ctaSub':
    'A loan officer reviews every inquiry within 4 business hours and follows up with a locked term sheet inside 24 hours.',
  'website.narrative.about.ctaQuoteLabel': 'Quote a deal',
  'website.narrative.about.ctaContactLabel': 'Talk to a loan officer',

  // Leadership — page chrome
  'website.narrative.leadership.eyebrow': 'LEADERSHIP',
  'website.narrative.leadership.headline': 'The people you will',
  'website.narrative.leadership.italic': 'actually talk to.',
  'website.narrative.leadership.sub':
    'Operators who review every deal personally — and who pick up the phone when you call.',
  'website.narrative.leadership.bioChapter': 'BIO',
  'website.narrative.leadership.linkedinLabel': 'Connect on LinkedIn',
  'website.narrative.leadership.portraitFallback': 'Portrait',

  // Markets — page chrome
  'website.narrative.markets.eyebrow': 'MARKETS',
  'website.narrative.markets.headline': 'Where we',
  'website.narrative.markets.italic': 'lend.',
  'website.narrative.markets.sub':
    'We lend nationwide on residential and small-commercial real estate. These are the markets where our team has the deepest operating experience.',
  'website.narrative.markets.statFunded': 'Funded',
  'website.narrative.markets.statDeals': 'Closed deals',
  'website.narrative.markets.statDaysToClose': 'Avg days to close',
  'website.narrative.markets.quoteLink': 'Quote a deal',
  'website.narrative.markets.sectionChapter': '§ 01',
  'website.narrative.markets.sectionEyebrow': 'FEATURED MARKETS',
  'website.narrative.markets.keyMarketsLabel': 'Key markets',
  'website.narrative.markets.countiesLabel': 'Counties',
  'website.narrative.markets.ctaPrefix': 'Quote a deal in',

  // Partners — page chrome
  'website.narrative.partners.eyebrow': 'PARTNERS',
  'website.narrative.partners.flowChapter': 'HOW IT WORKS',
  'website.narrative.partners.flowOutboundEyebrow': 'OUTBOUND · YOU → US',
  'website.narrative.partners.flowOutboundHeadline': 'Send us your borrowers.',
  'website.narrative.partners.flowOutboundItalic': 'your borrowers.',
  'website.narrative.partners.flowOutboundBody':
    'When your borrower closes with us, you earn a referral commission. Paid within 30 days of funding. No volume minimums.',
  'website.narrative.partners.flowInboundEyebrow': 'INBOUND · US → YOU',
  'website.narrative.partners.flowInboundHeadline': 'We send you ours.',
  'website.narrative.partners.flowInboundItalic': 'you ours.',
  'website.narrative.partners.flowInboundBody':
    'When we see a deal that fits your box better — specialty programs, jumbo, niche products — we refer it. Attribution flows to the right team automatically.',
  'website.narrative.partners.flowBridgeLabel': 'BIDIRECTIONAL',
  'website.narrative.partners.qualifiesChapter': 'WHO QUALIFIES',
  'website.narrative.partners.qualifiesHeadline': 'Built for serious',
  'website.narrative.partners.qualifiesItalic': 'broker shops.',
  'website.narrative.partners.benefitsLabel': 'WHAT YOU GET',
  'website.narrative.partners.requirementsLabel': 'WHAT WE EXPECT',
  // Screen-reader aria-labels for the attribution table rows (conversion
  // column + earnings column). Visible cells render the numeric values
  // themselves; these labels give assistive tech a semantic anchor
  // instead of a raw number.
  'website.narrative.partners.attributionRowConvLabel': 'Conversion rate',
  'website.narrative.partners.attributionRowEarnLabel': 'Partner earnings',
  'website.narrative.partners.applyChapter': 'GET STARTED',
  'website.narrative.partners.applyHeadline': 'Apply to become',
  'website.narrative.partners.applyItalic': 'a partner.',
  'website.narrative.partners.applySub':
    "Tell us about your shop. We'll review your application and set up the bidirectional referral links if it's a fit.",
  'website.narrative.partners.applyHref': '/embed/partner-apply',

  // =========================================================================
  // v4 ThemedHeader / ThemedFooter — default nav & link labels for white-labeled
  // lender pages. Tenants can override via component props; these keys back
  // the defaults so they can still be localized per tenant.
  // =========================================================================
  'website.themedHeader.nav.capital': 'Capital',
  'website.themedHeader.nav.programs': 'Loan programs',
  'website.themedHeader.nav.deals': 'Recent deals',
  'website.themedHeader.nav.operators': 'Operators',
  'website.themedHeader.nav.brokers': 'Brokers',
  'website.themedHeader.cta.quoteDeal': 'Quote a deal',

  'website.themedFooter.columns.quickLinks.title': 'Quick Links',
  'website.themedFooter.columns.quickLinks.home': 'Home',
  'website.themedFooter.columns.quickLinks.programs': 'Loan Programs',
  'website.themedFooter.columns.quickLinks.about': 'About',
  'website.themedFooter.columns.quickLinks.geography': 'Geographic Focus',
  'website.themedFooter.columns.programs.title': 'Loan Programs',
  'website.themedFooter.columns.programs.fixflip': 'Fix & Flip',
  'website.themedFooter.columns.programs.construction': 'Construction',
  'website.themedFooter.columns.programs.bridge': 'Bridge',
  'website.themedFooter.columns.programs.dscr': 'DSCR',
  'website.themedFooter.columns.tools.title': 'Tools',
  'website.themedFooter.columns.tools.quoteDeal': 'Quote a deal',
  'website.themedFooter.columns.tools.calculator': 'Calculator',
  'website.themedFooter.columns.tools.prequal': 'Pre-Qualification',
  'website.themedFooter.columns.tools.portal': 'Borrower Portal',
  'website.themedFooter.columns.tools.borrowerPortal': 'Borrower Portal',
  'website.themedFooter.columns.tools.partnerPortal': 'Partner Portal',

  // Review summary row label (parallels website.embed.intake.section.* keys).
  'website.embed.intake.review.row.loanType': 'Loan type',

  // =========================================================================
  // Borrower Card — Overview right-rail tile rendering consumer identity,
  // FICO MID with gradient score bar, prior-projects history, and Astor
  // confidence rating. See LoanBorrowerCard.tsx.
  // =========================================================================
  'cases.borrowerCard.title': 'Borrower',
  'cases.borrowerCard.openProfile': 'Open profile →',
  'cases.borrowerCard.ficoMid': 'FICO MID',
  'cases.borrowerCard.priorProjects': 'PRIOR PROJECTS',
  'cases.borrowerCard.avgHistoricalLtv': 'AVG HISTORICAL LTV',
  'cases.borrowerCard.verifiedSince': 'VERIFIED SINCE',
  'cases.borrowerCard.astorConfidence': 'ASTOR CONFIDENCE',
  'cases.borrowerCard.band': 'band',
  'cases.borrowerCard.derog': 'derog',
  'cases.borrowerCard.fico.poor': 'poor',
  'cases.borrowerCard.fico.fair': 'fair',
  'cases.borrowerCard.fico.good': 'good',
  'cases.borrowerCard.fico.veryGood': 'very good',
  'cases.borrowerCard.fico.exceptional': 'exceptional',
  'cases.borrowerCard.confidence.high': 'high',
  'cases.borrowerCard.confidence.medium': 'medium',
  'cases.borrowerCard.confidence.low': 'low',
  'cases.borrowerCard.creditPullSource': '↳ credit.pull · {{recency}}',
  // BorrowerCard tier display (1Sharpe warehouse-line stack — PR2 wires real
  // data; PR1 ships the scaffold).
  'cases.borrowerCard.tierAriaLabel': 'Borrower tier: {{tier}}',
  'cases.borrowerCard.tierStatus.caseByCase': 'case-by-case',
  'cases.borrowerCard.tierStatus.ineligible': 'ineligible',

  // Brief view popover (TopBar overlay #10a — Section 10 design)
  'briefView.title': 'Brief view',
  'briefView.live': 'live',
  'briefView.intro.morning': 'Today: ',
  'briefView.kpi.toActOn': 'To act on',
  'briefView.kpi.toActOnSub': 'by {{cutoff}}',
  'briefView.kpi.astorHandled': 'Astor handled',
  'briefView.kpi.astorHandledSub': 'since {{startTime}}',
  'briefView.kpi.slaRisk': 'SLA risk',
  'briefView.kpi.slaRiskSubAllClear': 'all clear',
  'briefView.kpi.slaRiskSubBreach': '{{count}} at risk',
  'briefView.priorities.header': 'Your {{count}} priorities',
  'briefView.openFull': 'Open full briefing',
  'briefView.empty': 'Nothing urgent right now.',
  // Default sublabel copy used by the Header (10-business-portal) when the
  // real `toActOn` cutoff and `astorHandled` start-of-day signals haven't
  // been wired up yet — see TODOs in Header.tsx.
  'briefView.kpi.toActOnCutoffDefault': 'EOD',
  'briefView.kpi.astorHandledStartDefault': 'today',
  // Body sentence rendered AFTER the mint-bold "Today: " lead-in
  // (briefView.intro.morning).
  'briefView.intro.bodyDefault':
    'No urgent gates right now — Astor is handling routine work. Open the full briefing for details.',
  'briefView.intro.bodyWithItems':
    '{{count}} item(s) need your attention — Astor is handling routine work in the background.',

  // Astor quick-ask popover (TopBar overlay #10b)
  'astorQuickAsk.title': 'Ask Astor',
  'astorQuickAsk.context': 'context: this page',
  'astorQuickAsk.online': 'online',
  'astorQuickAsk.placeholder': 'Ask anything about this case…',
  'astorQuickAsk.suggestionsHeader': 'Suggestions for this page',
  'astorQuickAsk.recentConversations': '{{count}} recent conversations',
  'astorQuickAsk.openAstor': 'Open Astor',
  'astorQuickAsk.empty': 'No suggestions for this surface yet.',
  // Default suggestion strings rendered by the Header (10-business-portal).
  // Each row is icon + localized text; when clicked, the text is stashed
  // in `sessionStorage('astor.prefill')` and we navigate to /astor.
  'astorQuickAsk.suggestions.summarizeMemo': 'Summarize the credit memo',
  'astorQuickAsk.suggestions.compareFunded': 'How does this loan compare to last 10 funded?',
  'astorQuickAsk.suggestions.whatChanged': 'What changed since yesterday?',
  'astorQuickAsk.suggestions.draftReply': 'Draft a reply',

  // AI Briefing rail (Home page right rail — Section 01)
  'aiBriefing.title': 'AI Briefing',
  'aiBriefing.collapse': 'Collapse',
  'aiBriefing.expand': 'Open AI Briefing',
  'aiBriefing.snapshot.header': "Today's snapshot",
  'aiBriefing.snapshot.activeLoans': 'Active loans',
  'aiBriefing.snapshot.activeLoansSub': '{{deltaSigned}} wk',
  'aiBriefing.snapshot.slaRisk': 'At SLA risk',
  'aiBriefing.snapshot.slaRiskSubPercent': '{{percent}}%',
  'aiBriefing.snapshot.unreadMessages': 'Unread msgs',
  'aiBriefing.snapshot.unreadMessagesSub': 'cleared {{time}}',
  'aiBriefing.snapshot.prioritySignals': 'Priority signals',
  'aiBriefing.snapshot.prioritySignalsSub': '{{newCount}} new',
  'aiBriefing.read.header': "Astor's read",
  'aiBriefing.read.confidence': 'conf {{percent}}%',
  'aiBriefing.read.readTime': 'read {{seconds}}s',
  'aiBriefing.read.urgentNarrative':
    'Pipeline is healthy — but {{count}} cases drifted toward policy gates overnight. Open them to keep on schedule.',
  'aiBriefing.read.allClearNarrative': 'Pipeline is healthy. {{activeLoans}} active loans, all on track.',
  'aiBriefing.updates.header': 'Latest updates',
  'aiBriefing.updates.viewAll': 'View all updates',
  'aiBriefing.updates.channel': 'Channel: {{channel}}',
  'aiBriefing.empty': 'No briefing available — Astor is still gathering signal.',

  // AstorsReadCard primitive (reused across Home, Borrower Detail, Watchlist footer)
  'astorRead.defaultLabel': 'ASTOR · read',
  'astorRead.summaryLabel': 'ASTOR · summary',
  'astorRead.trendLabel': 'ASTOR · trend',
  'astorRead.confidence': 'conf {{percent}}%',
  'astorRead.toolPrefix': '↳',

  // =========================================================================
  // Astor live toast (bottom-left persistent notification)
  // =========================================================================
  'astor.live.label': 'ASTOR · live',
  'astor.live.open': 'Open',
  'astor.live.dismiss': 'Dismiss',

  // Intake submissions — Astor pre-screen banner (UX v3 #1739)
  'intakeSubmissions.astorBanner.headline': '{{ready}} ready to advance · {{review}} need a closer look',
  'intakeSubmissions.astorBanner.reasoning':
    'I pre-screened the intake queue against your active policies and document checklists. Ready-to-advance items pass cleanly; the rest have edge cases that benefit from a human call.',
  'intakeSubmissions.astorBanner.reviewAll': 'Review submitted',

  // Watchlist — Astor signal footer (UX v3 #1739)
  'watchlist.astorFooter.intro':
    "I'm tracking {{total}} items on your watchlist. {{signals}} have signals worth your attention right now.",
  'watchlist.astorFooter.briefMe': 'Brief me',
  'watchlist.astorFooter.allQuiet': "I'm tracking {{total}} items on your watchlist. All quiet right now.",
  // Signal-aware narrative (#1868). `{{summary}}` is a comma-joined list
  // of the top 3 signals (each rendered as a short fragment with the pin
  // label + signal classification), built client-side from the live
  // signal stream so the narrative reflects what the user is seeing.
  'watchlist.astorFooter.signalsNarrative': "I'm tracking {{total}} items. {{summary}}.",

  // Scorecard — Astor explainer footer (UX v3 #1739)
  'scorecard.astorExplainer.intro':
    "I noticed you've missed the funded-loans target {{missed}} of the last {{total}} weeks. Want me to drill into the bottleneck — stage drift, conversion, or pipeline volume?",
  'scorecard.astorExplainer.drillIn': 'Drill in',
  'scorecard.astorExplainer.onTrack':
    "You're hitting your funded-loans target. Pipeline conversion is healthy across the last {{total}} weeks.",

  // Control Center — Astor Autonomy page (UX v3 #1739, net-new)
  'controlCenter.autonomy.title': 'Astor autonomy',
  'controlCenter.autonomy.description':
    'Choose how much Astor does on its own — per agent and per action. Changes apply to new work; in-flight runs respect the level they started at.',
  'controlCenter.autonomy.l1.name': 'Observe',
  'controlCenter.autonomy.l1.sub': 'Astor suggests · you act on every step',
  'controlCenter.autonomy.l2.name': 'Supervised',
  'controlCenter.autonomy.l2.sub': 'Astor recommends · you approve before each action',
  'controlCenter.autonomy.l3.name': 'Trusted',
  'controlCenter.autonomy.l3.sub': 'Astor acts within guardrails · escalates on policy edges',
  'controlCenter.autonomy.l4.name': 'Full',
  'controlCenter.autonomy.l4.sub': 'Astor acts autonomously · escalates exceptions only',
  'controlCenter.autonomy.perAgent.title': 'Per-agent overrides',
  'controlCenter.autonomy.perAgent.subtitle':
    'Set autonomy per workflow agent. Each agent defaults to the master level if unset.',
  'controlCenter.autonomy.col.agent': 'Agent',
  'controlCenter.autonomy.col.description': 'Description',
  'controlCenter.autonomy.col.level': 'Level',
  'controlCenter.autonomy.col.calls7d': 'Calls (7d)',
  'controlCenter.autonomy.col.actions': '',
  'controlCenter.autonomy.tune': 'Tune',
  'controlCenter.autonomy.auditLog': 'Audit log',
  // (Removed: camelCase agent description keys never referenced by any
  // code path — the autonomy page renders the DB description on every row.
  // The snake_case BIC-persona keys below ARE used as fallbacks when a row
  // has no description set.)
  // BIC persona keys (real agents in `bic_agents`) — fallback descriptions
  // when the row description isn't already localized. Code matches
  // BicAgentPersonaCode in shared-types.
  'controlCenter.autonomy.agents.borrower_concierge':
    'Helps borrowers navigate the application — onboarding, document upload, status updates, FAQ',
  'controlCenter.autonomy.agents.loan_officer_assistant':
    'Helps LO manage their pipeline — deal coaching, comp generation, suggested next actions',
  'controlCenter.autonomy.agents.underwriter_assistant':
    'Helps UW review deals — policy compliance, risk-flag triage, second-opinion analysis',
  'controlCenter.autonomy.agents.servicing_specialist':
    'Handles post-funded operations — payment processing, modification handling, payoff calc',
  'controlCenter.autonomy.agents.admin_operator':
    'Operational queries, KPI tracking, exception management, approval workflow oversight',

  // Autonomy page — empty / inherit / loading states (UX v3 #1739)
  'controlCenter.autonomy.empty': 'No agents seeded for this business yet.',
  'controlCenter.autonomy.inherit': 'Inherit from master',
  'controlCenter.autonomy.saving': 'Saving…',
  'controlCenter.autonomy.saveError': 'Could not save autonomy level. Please try again.',

  // AstorReasoningTrace — tool-call list header (UX v3 #1739)
  'astor.reasoningTrace.header': 'REASONING TRACE',

  // Borrowers list — UX v3 KPI strip (#1739)
  // PR #1741 audit: helper copy reflects the loaded page, not server-side
  // aggregates. The active/with-loans counters iterate the visible borrowers
  // only; copy says "of loaded" so the percent isn't read as a global figure.
  'borrowers.kpi.total': 'Total borrowers',
  'borrowers.kpi.totalSub': 'lifetime',
  'borrowers.kpi.active': 'Active',
  'borrowers.kpi.activeSub': '{{percent}}% of loaded',
  'borrowers.kpi.recent': 'New this month',
  'borrowers.kpi.recentSub': 'added in last 30d',
  'borrowers.kpi.withLoans': 'With active loans',
  'borrowers.kpi.withLoansSub': '{{percent}}% of loaded active',

  // Partners list — UX v3 KPI strip (#1739)
  'partners.kpi.active': 'Active partners',
  // PR #1741 audit: `activeCount` is page-local (no server aggregate yet);
  // helper drops the misleading `of {{total}}` interpolation — that read
  // as a fraction of the global total when it was actually page-local.
  'partners.kpi.activeSub': 'on loaded page',
  'partners.kpi.pending': 'Pending invites',
  'partners.kpi.pendingSub': 'awaiting response',
  'partners.kpi.referrals': 'MTD referrals',
  'partners.kpi.referralsSub': '{{converted}} converted',
  'partners.kpi.topPartner': 'Top partner',
  'partners.kpi.topPartnerSub': '{{conversions}} this month',
  // v3 design — closed loans + funded volume tiles. Both render '—' until a
  // server-side aggregate endpoint exists (relationships endpoint doesn't
  // populate per-row stats — see existing `Stats not available` comment).
  'partners.kpi.closedYoy.label': 'Closed YoY',
  'partners.kpi.closedYoy.sub': 'vs last YTD',
  'partners.kpi.fundedVolume.label': 'Funded volume',
  'partners.kpi.fundedVolume.sub': 'this YTD',

  // Astor chat — reasoning trace surface inside AstorThread (UX v3 #1739)
  'astor.chat.toolTrace.header': 'Tools used',
  'astor.chat.toolTrace.empty': 'No tools used in this response',
  'astor.chat.toolTrace.toggleShow': 'Show reasoning',
  'astor.chat.toolTrace.toggleHide': 'Hide reasoning',
  'astor.chat.toolTrace.totalDuration': 'reasoned in {{duration}}',

  // Astor conversation list — left rail sidebar (UX v3 #1749 — AstorActive
  // and AstorEmpty in docs/ux-v3-1749/pages/home-astor.jsx). Last-7-days vs
  // Older grouping + search + New chat CTA.
  'astor.conversationList.newChat': 'New chat',
  'astor.conversationList.searchPlaceholder': 'Search conversations…',
  'astor.conversationList.empty': 'No conversations yet',
  'astor.conversationList.emptySearch': 'No conversations found',
  'astor.conversationList.emptyHint': 'Start chatting with an agent below',
  'astor.conversationList.untitled': 'Untitled',
  'astor.conversationList.agents': 'Agents',
  'astor.conversationList.createAgent': 'Create agent',
  'astor.conversationList.deleteConversation': 'Delete conversation',
  'astor.conversationList.deleteConfirm': 'Delete this conversation?',
  'astor.conversationList.expandSidebar': 'Expand sidebar',
  'astor.conversationList.collapseSidebar': 'Collapse sidebar',
  'astor.conversationList.group.today': 'Today',
  'astor.conversationList.group.yesterday': 'Yesterday',
  'astor.conversationList.group.last7Days': 'Last 7 days',
  'astor.conversationList.group.older': 'Older',

  // Inbox Approvals — Astor verify column (UX v3 #1739)
  'approvals.astorColumn.header': 'Astor read',
  'approvals.astorColumn.approve': 'Approve',
  'approvals.astorColumn.review': 'Review',
  'approvals.astorColumn.hold': 'Hold',
  'approvals.astorColumn.unknown': '—',
  'approvals.astorColumn.confidence': 'conf {{percent}}%',
  'approvals.astorColumn.pending': 'analyzing…',

  // Borrower detail — UX v3 rich profile card (#1739)
  'borrowers.detail.card.ficoBand': 'FICO band',
  'borrowers.detail.card.ficoPoor': 'Poor',
  'borrowers.detail.card.ficoFair': 'Fair',
  'borrowers.detail.card.ficoGood': 'Good',
  'borrowers.detail.card.ficoExcellent': 'Excellent',
  'borrowers.detail.card.priorProjects': 'Prior projects',
  'borrowers.detail.card.priorProjectsSub': '{{closed}} closed · {{derog}} derog',
  'borrowers.detail.card.avgHistLtv': 'Avg historical LTV',
  'borrowers.detail.card.verifiedSince': 'Verified since',
  'borrowers.detail.card.astorConfidence': 'Astor confidence',
  'borrowers.detail.card.astorConfidenceHigh': 'High',
  'borrowers.detail.card.astorConfidenceMedium': 'Medium',
  'borrowers.detail.card.astorConfidenceLow': 'Low',
  'borrowers.detail.card.creditPullLabel': 'credit.pull · {{age}}',
  'borrowers.detail.card.noFicoData': 'No FICO on file',
  'borrowers.detail.card.openFullProfile': 'Open profile',

  // Borrower detail — Astor's read narrative (#1739)
  // Composed at runtime from real consumer + case data; clauses are skipped when
  // their source signal is missing rather than rendering "$0" or "0 projects".
  'borrowers.detail.astorRead.repeatBorrower': 'Repeat borrower with {{closedCount}} closed loans.',
  'borrowers.detail.astorRead.firstTime': 'First-time borrower on the platform.',
  'borrowers.detail.astorRead.exposure': 'Current exposure: {{amount}} across {{activeCount}} active loans.',
  'borrowers.detail.astorRead.verifiedSince': 'Identity verified {{when}}.',
  'borrowers.detail.astorRead.noSignal': 'Not enough activity yet for Astor to form a read.',

  // Borrower detail — Applications tab v3 (#1749)
  // Lifetime application list with status pills + amount + dates. KPI tiles
  // up top summarize total / open / converted counts derived from the same
  // case list so the surface stays single-fetch.
  'borrowers.applicationsTab.empty.title': 'No applications yet',
  'borrowers.applicationsTab.empty.description':
    'When this borrower submits an application or you create one on their behalf, it will appear here.',
  'borrowers.applicationsTab.kpi.total': 'Total',
  'borrowers.applicationsTab.kpi.totalHelper': 'Applications',
  'borrowers.applicationsTab.kpi.open': 'Open',
  'borrowers.applicationsTab.kpi.openHelper': 'In progress / review',
  'borrowers.applicationsTab.kpi.openHelperSampled': 'In progress / review · last {{sample}}',
  'borrowers.applicationsTab.kpi.converted': 'Converted',
  'borrowers.applicationsTab.kpi.convertedHelper': 'Approved or closed',
  'borrowers.applicationsTab.kpi.convertedHelperSampled': 'Approved or closed · last {{sample}}',
  'borrowers.applicationsTab.row.amount': 'Amount',
  'borrowers.applicationsTab.row.amountUnknown': '—',
  'borrowers.applicationsTab.row.submitted': 'Submitted',
  'borrowers.applicationsTab.row.updated': 'Updated',
  'borrowers.applicationsTab.row.untitled': 'Untitled application',
  'borrowers.applicationsTab.row.viewLoan': 'View loan',
  'borrowers.applicationsTab.status.new': 'New',
  'borrowers.applicationsTab.status.in_progress': 'In progress',
  'borrowers.applicationsTab.status.pending_review': 'Pending review',
  'borrowers.applicationsTab.status.under_review': 'Under review',
  'borrowers.applicationsTab.status.pending_approval': 'Pending approval',
  'borrowers.applicationsTab.status.approved': 'Approved',
  'borrowers.applicationsTab.status.rejected': 'Rejected',
  'borrowers.applicationsTab.status.closed': 'Closed',
  'borrowers.applicationsTab.status.cancelled': 'Cancelled',
  'borrowers.applicationsTab.status.archived': 'Archived',
  'borrowers.applicationsTab.truncatedNotice':
    'Showing the {{shown}} most recent of {{total}} applications. Detailed amounts load for the visible rows.',

  // Borrower detail — Entities tab v3 (#1749).
  // 3-column LLC / Personal card grid with EIN + Articles / Op-agreement on-file flags.
  // The entityType.<TYPE> subkeys mirror the BorrowingEntityType enum values from
  // shared-types — when a value isn't translated, the component falls back to
  // `entityType.toUpperCase()` (see BorrowerEntitiesTab.tsx:99-101).
  'borrowers.entitiesTab.dash': '—',
  'borrowers.entitiesTab.individual': 'Personal',
  'borrowers.entitiesTab.cardAriaLabel': 'Entity: {{name}}',
  'borrowers.entitiesTab.ein': 'EIN',
  'borrowers.entitiesTab.formed': 'Formed',
  'borrowers.entitiesTab.articles': 'Articles',
  'borrowers.entitiesTab.opAgreement': 'Op agreement',
  'borrowers.entitiesTab.onFile': 'on file',
  'borrowers.entitiesTab.empty.title': 'No entities yet',
  'borrowers.entitiesTab.empty.description':
    'When this borrower forms an LLC, partnership, or other entity for a loan, it will appear here.',
  'borrowers.entitiesTab.entityType.LLC': 'LLC',
  'borrowers.entitiesTab.entityType.CORPORATION': 'Corporation',
  'borrowers.entitiesTab.entityType.PARTNERSHIP': 'Partnership',
  'borrowers.entitiesTab.entityType.TRUST': 'Trust',
  'borrowers.entitiesTab.entityType.SOLE_PROPRIETORSHIP': 'Sole prop',
  'borrowers.entitiesTab.entityType.S_CORPORATION': 'S Corp',
  'borrowers.entitiesTab.entityType.C_CORPORATION': 'C Corp',
  'borrowers.entitiesTab.entityType.LP': 'LP',
  'borrowers.entitiesTab.entityType.LLP': 'LLP',
  'borrowers.entitiesTab.entityType.NONPROFIT': 'Nonprofit',
  'borrowers.entitiesTab.entityType.INDIVIDUAL': 'Personal',

  // Borrower detail — Documents tab v3 (#1749)
  // Single-surface category-aware document list with status chips, expiring
  // highlighting, and an Astor-verify affordance. The MetricGrid strip up top
  // breaks the total set into Total / Expiring / Flagged so the underwriter
  // can see at a glance which buckets need attention. Empty / case-docs /
  // truncated copy reuses the existing `entityHub.documents.*` keys.
  'borrowers.documentsTab.title': 'Documents',
  'borrowers.documentsTab.onFile': '{{count}} on file',
  'borrowers.documentsTab.astorVerify': 'Astor verify',
  'borrowers.documentsTab.upload': 'Upload',
  'borrowers.documentsTab.rowActions': 'Document actions',
  'borrowers.documentsTab.expiringBanner':
    '{{count}} document(s) expire within 30 days. Request re-uploads before they go stale.',
  'borrowers.documentsTab.kpi.total': 'Total',
  'borrowers.documentsTab.kpi.totalHelper': 'On file',
  'borrowers.documentsTab.kpi.expiring': 'Expiring',
  'borrowers.documentsTab.kpi.expiringHelper': 'Within 30 days',
  'borrowers.documentsTab.kpi.flagged': 'Flagged',
  'borrowers.documentsTab.kpi.flaggedHelper': 'Re-upload requested',
  'borrowers.documentsTab.column.document': 'Document',
  'borrowers.documentsTab.column.category': 'Category',
  'borrowers.documentsTab.column.status': 'Status',
  'borrowers.documentsTab.column.uploaded': 'Uploaded',
  'borrowers.documentsTab.column.expires': 'Expires',
  'borrowers.documentsTab.status.verified': 'verified',
  'borrowers.documentsTab.status.pending': 'pending',
  'borrowers.documentsTab.status.expiring': 'expiring',
  'borrowers.documentsTab.status.flagged': 'flagged',

  // Borrower detail — Activity tab v3 (#1749)
  // Composed surface: 4 KPI tiles + 30-day engagement chart + unified timeline.
  // Tiles without a backend source render disabled placeholders with the
  // `notYetWired` subtitle so reviewers can tell the slot is intentional.
  'borrowers.activityTab.kpi.avgReplyTime': 'Avg reply time',
  'borrowers.activityTab.kpi.logins30d': 'Logins (30d)',
  'borrowers.activityTab.kpi.logins30dSub': 'sign-in audit events',
  'borrowers.activityTab.kpi.docsRequested': 'Docs requested',
  'borrowers.activityTab.kpi.nps': 'NPS',
  'borrowers.activityTab.kpi.notYetWired': 'TODO #1749 — source pending',
  'borrowers.activityTab.engagement.title': 'LAST 30 DAYS',
  'borrowers.activityTab.engagement.subtitle': '{{total}} audit events in the window',

  // E-Sign queue — UX v3 SignerStrip + KPIs (#1739)
  'esign.signerStrip.signed': 'Signed',
  'esign.signerStrip.pending': 'Sent',
  'esign.signerStrip.waiting': 'Waiting',
  'esign.signerStrip.stalled': 'Stalled {{hours}}h',
  'esign.signerStrip.nudge': 'Nudge',
  'esign.signerStrip.viewPacket': 'View packet',
  'esign.kpi.inFlight': 'In flight',
  'esign.kpi.inFlightSub': '{{awaiting}} awaiting signers',
  'esign.kpi.stalled': 'Stalled > 48h',
  'esign.kpi.stalledSub': '{{label}}',
  'esign.kpi.completed7d': 'Completed (7d)',
  'esign.kpi.completed7dSub': '{{percent}}% on-time',
  'esign.kpi.avgCycle': 'Avg cycle',
  'esign.kpi.avgCycleSub': '{{delta}} vs. goal',
  'esign.kpi.none': '—',
  // Inbox › E-Sign Queue (UX v3 #1749) — stalled-envelope warning banner
  'inbox.eSign.stalledBanner':
    '{{count}} envelope(s) have been awaiting a signer for more than 48 hours. Nudge the signer or void the packet to unblock.',

  // Reports / EsigHistory — UX v3 KPI strip incl. hash chain (#1739)
  'reports.esig.kpi.sent': 'Sent (30d)',
  'reports.esig.kpi.sentSub': '{{inFlight}} in flight',
  'reports.esig.kpi.completed': 'Completed',
  'reports.esig.kpi.completedSub': '{{percent}}% on-time',
  'reports.esig.kpi.avgCycle': 'Avg cycle',
  'reports.esig.kpi.avgCycleSub': '{{delta}} vs. goal',
  'reports.esig.kpi.stalled': 'Stalled',
  'reports.esig.kpi.stalledSub': 'oldest {{age}}',
  'reports.esig.kpi.hashChain': 'Hash chain',
  'reports.esig.kpi.hashChainSub': 'audit clean · 30d',
  'reports.esig.kpi.hashChainValue': '✓ intact',

  // Reports / DocApprovals — UX v3 KPI strip + Astor verify column (#1739)
  'reports.docApprovals.kpi.awaiting': 'Awaiting review',
  'reports.docApprovals.kpi.awaitingSub': '{{atRisk}} SLA at risk',
  'reports.docApprovals.kpi.approved': 'Approved (30d)',
  'reports.docApprovals.kpi.approvedSub': '{{percent}}% on-time',
  'reports.docApprovals.kpi.rejected': 'Rejected (30d)',
  'reports.docApprovals.kpi.rejectedSub': '{{resubmitted}} resubmitted',
  'reports.docApprovals.kpi.astorVerified': 'Astor auto-verified',
  'reports.docApprovals.kpi.astorVerifiedSub': 'of submissions',
  'reports.docApprovals.column.astor': 'Astor verify',
  'reports.docApprovals.astor.verified': 'Verified',
  'reports.docApprovals.astor.flagged': 'Flagged',
  'reports.docApprovals.astor.missing': 'Missing',
  'reports.docApprovals.astor.pending': 'analyzing…',

  // Scorecard 6-tile KPI strip (v3 design — values render as em-dash until scorecard.summary RPC lands)
  'reports.scorecard.kpi.fundedMtd': 'Funded MTD',
  'reports.scorecard.kpi.fundedMtdSub': 'This month to date',
  'reports.scorecard.kpi.loansFunded': 'Loans funded',
  'reports.scorecard.kpi.loansFundedSub': 'This period',
  'reports.scorecard.kpi.newApps': 'New apps',
  'reports.scorecard.kpi.newAppsSub': 'This period',
  'reports.scorecard.kpi.pipelineConv': 'Pipeline conv.',
  'reports.scorecard.kpi.pipelineConvSub': 'vs. prior period',
  'reports.scorecard.kpi.avgTimeToClose': 'Avg time to close',
  'reports.scorecard.kpi.avgTimeToCloseSub': 'Days · trailing 30d',
  'reports.scorecard.kpi.delinquency30': 'Delinquency 30+',
  'reports.scorecard.kpi.delinquency30Sub': 'Active loans',

  // Loan Case Tasks — UX v3 Astor's plan side card (#1739)
  'cases.tasks.astorPlan.title': "Astor's plan",
  'cases.tasks.astorPlan.intro':
    "I've run the deterministic checks for this stage. Here's what would move this case forward fastest:",
  'cases.tasks.astorPlan.suggestedHeader': 'Suggested next 60 minutes',
  'cases.tasks.astorPlan.approve': 'Approve at recommended terms',
  'cases.tasks.astorPlan.callBorrower': 'Call borrower',
  'cases.tasks.astorPlan.counter': 'Counter terms',
  'cases.tasks.astorPlan.predicted': 'Predicted time to next stage:',
  'cases.tasks.astorPlan.predictedValue': '~{{hours}}h',
  'cases.tasks.astorPlan.predictedFallback': 'depends on borrower response',

  // Loan Case Property Analysis — comp map (#1739)
  'cases.propertyAnalysis.compMap.title': 'Comparable sales',
  'cases.propertyAnalysis.compMap.radius': '0.5mi radius',
  'cases.propertyAnalysis.compMap.subjectLabel': 'Subject',
  'cases.propertyAnalysis.compMap.autoPulled': 'auto-pulled',
  'cases.propertyAnalysis.compMap.compsCount_one': '{{count}} comp',
  'cases.propertyAnalysis.compMap.compsCount_other': '{{count}} comps',
  'cases.propertyAnalysis.compMap.compsCount': '{{count}} comps',
  'cases.propertyAnalysis.compMap.empty': 'No comparables on file yet',

  // Loan Case Property Analysis — Astor adjustments (#1739)
  'cases.propertyAnalysis.adjustments.title': "Astor's adjustments",
  'cases.propertyAnalysis.adjustments.autoChip': '+ auto',
  'cases.propertyAnalysis.adjustments.colAttribute': 'Attribute',
  'cases.propertyAnalysis.adjustments.colDelta': 'Per-unit',
  'cases.propertyAnalysis.adjustments.colNet': 'Net effect',
  'cases.propertyAnalysis.adjustments.row.sqft': 'SqFt delta',
  'cases.propertyAnalysis.adjustments.row.bedBath': 'Bed/bath parity',
  'cases.propertyAnalysis.adjustments.row.lot': 'Lot size',
  'cases.propertyAnalysis.adjustments.row.time': 'Time',
  'cases.propertyAnalysis.adjustments.row.condition': 'Condition (planned)',
  // PR #1741: dropped the `{{deals}}` interpolation — the placeholder `274 OMX`
  // shipped before any real calibrated-deal count exists. Re-introduce a typed
  // count interpolation when the MSA-calibrated coefficients endpoint lands.
  'cases.propertyAnalysis.adjustments.footer': 'Adjustments learned from bridge deals in {{msa}}.',
  'cases.propertyAnalysis.adjustments.footerFallback':
    'Adjustments are model defaults. Local-MSA training kicks in once {{count}} closed comps are on file.',

  // Partner Detail — Astor trend card (#1739)
  'partners.detail.astorTrend.label': 'ASTOR · trend',
  'partners.detail.astorTrend.up':
    'Conversion is up {{pct}}% MoM. Average loan size trending up — likely time to revisit pricing tier with this partner.',
  'partners.detail.astorTrend.flat':
    'Conversion is steady at {{pct}}%. No re-pricing signal this month — keep the relationship warm.',
  'partners.detail.astorTrend.down':
    'Conversion is down {{pct}}% MoM. Worth a check-in — pricing pressure or a competitor in their funnel?',
  'partners.detail.astorTrend.noData': 'Not enough recent activity to compute a trend yet.',

  // Loan Case — Property Analysis tab (Issue #1749, v3 redesign)
  'cases.propertyAnalysisTab.subject.title': 'Subject property',
  'cases.propertyAnalysisTab.subject.subtitle': 'Recorded facts at intake',
  'cases.propertyAnalysisTab.subject.empty': '—',
  'cases.propertyAnalysisTab.subject.address': 'Address',
  'cases.propertyAnalysisTab.subject.propertyType': 'Property type',
  'cases.propertyAnalysisTab.subject.beds': 'Beds',
  'cases.propertyAnalysisTab.subject.baths': 'Baths',
  'cases.propertyAnalysisTab.subject.sqft': 'GLA',
  'cases.propertyAnalysisTab.subject.sqftValue': '{{value}} ft²',
  'cases.propertyAnalysisTab.subject.lotSize': 'Lot size',
  'cases.propertyAnalysisTab.subject.lotSizeValue': '{{value}} ft²',
  'cases.propertyAnalysisTab.subject.yearBuilt': 'Year built',
  'cases.propertyAnalysisTab.subject.neighborhood': 'Neighborhood',
  'cases.propertyAnalysisTab.subject.purchasePrice': 'Purchase price',
  'cases.propertyAnalysisTab.subject.condition': 'Condition / quality',
  'cases.propertyAnalysisTab.reconciliation.title': 'Value reconciliation',
  'cases.propertyAnalysisTab.reconciliation.subtitle': 'Three independent paths cross-checked against the engine blend',
  'cases.propertyAnalysisTab.reconciliation.empty':
    'Run the property triage to surface a reconciled value across the three paths.',
  'cases.propertyAnalysisTab.reconciliation.reconciledValue': 'Reconciled',
  'cases.propertyAnalysisTab.reconciliation.reconciledHelper': 'Engine weighted blend',
  'cases.propertyAnalysisTab.reconciliation.avmValue': 'AVM',
  'cases.propertyAnalysisTab.reconciliation.avmHelper': 'Model · {{source}}',
  'cases.propertyAnalysisTab.reconciliation.avmPending': 'AVM signal not yet wired',
  'cases.propertyAnalysisTab.reconciliation.compsValue': 'Comp-weighted',
  'cases.propertyAnalysisTab.reconciliation.compsHelper': 'Sum of weighted adjusted comps',
  'cases.propertyAnalysisTab.reconciliation.purchaseValue': 'Purchase',
  'cases.propertyAnalysisTab.reconciliation.purchaseHelper': 'Borrower stated',
  'cases.propertyAnalysisTab.reconciliation.deltaAbove': '+{{pct}}% vs reconciled',
  'cases.propertyAnalysisTab.reconciliation.deltaBelow': '{{pct}}% vs reconciled',
  'cases.propertyAnalysisTab.reconciliation.deltaFlat': 'In line with reconciled',

  // Property Analysis tab — comparables delta chips (#1855)
  'cases.propertyAnalysisTab.comps.deltaAbove': '+{{pct}}% above subject',
  'cases.propertyAnalysisTab.comps.deltaBelow': '{{pct}}% below subject',
  'cases.propertyAnalysisTab.comps.deltaFlat': 'Matches subject',
  'cases.propertyAnalysisTab.comps.deltaTooltip':
    "Delta is the comparable's adjusted sale price relative to the subject property's reconciled value.",

  // Loan Case — Sizing tab (Issue #1749, v3 redesign)
  'cases.sizingTab.policyGates.title': 'Policy gates',
  'cases.sizingTab.policyGates.subtitle': 'Live across {{count}} capital lines',
  'cases.sizingTab.policyGates.evaluated': '{{count}} evaluated',
  'cases.sizingTab.policyGates.qualifying': '{{count}} qualify',
  'cases.sizingTab.policyGates.exception': '{{count}} need exception',
  'cases.sizingTab.policyGates.blocked': '{{count}} blocked',
  'cases.sizingTab.policyGates.duration': 'Sized in {{ms}}ms',
  'cases.sizingTab.policyGates.emptyHint': 'Run the sizer to see live policy gates across capital lines.',

  'cases.sizingTab.leader.headline': 'Leading capital line',
  'cases.sizingTab.leader.subheadline': '{{provider}} — {{line}}',
  'cases.sizingTab.leader.maxLoan': 'Max loan',
  'cases.sizingTab.leader.advanceRate': 'Advance rate',
  'cases.sizingTab.leader.rate': 'Rate',
  'cases.sizingTab.leader.points': 'Points',
  'cases.sizingTab.leader.term': 'Term',
  'cases.sizingTab.leader.termMonths': '{{count}} mo',
  'cases.sizingTab.leader.capMarker.qualifies': 'Within policy',
  'cases.sizingTab.leader.capMarker.exception': 'Needs exception',
  'cases.sizingTab.leader.capMarker.blocked': 'No qualifier yet',

  'cases.sizingTab.astor.recommendApprove': 'Astor recommends pinning {{provider}} at {{maxLoan}}.',
  'cases.sizingTab.astor.recommendApproveReasoning':
    '{{line}} qualified within policy at {{advanceRate}} advance · {{rate}} rate · {{points}} points. {{otherCount}} other lines also evaluated.',
  'cases.sizingTab.astor.recommendException': 'Top qualifier needs an exception on {{provider}}.',
  'cases.sizingTab.astor.recommendExceptionReasoning':
    '{{line}} would advance {{maxLoan}} once an exception is approved on {{ruleCount}} blocking rule(s). Review the comparison table for cleaner alternatives.',
  'cases.sizingTab.astor.recommendBlocked': 'No capital line qualifies for this deal yet.',
  'cases.sizingTab.astor.recommendBlockedReasoning':
    '{{count}} line(s) evaluated, all blocked. Adjust inputs or request a policy exception. Officer review required.',
  'cases.sizingTab.astor.pendingHeadline': 'Astor is still sizing this deal.',
  'cases.sizingTab.astor.pendingReasoning':
    'The engine is fanning out across the capital-line policy graph. This usually takes a few seconds.',
  'cases.sizingTab.astor.pinAction': 'Pin top result',
  'cases.sizingTab.astor.compareAction': 'Compare all',

  // Loan Case Notes — v3 timeline (Issue #1749)
  'cases.notesTab.title': 'Internal notes',
  'cases.notesTab.subtitle':
    'Team-only notes and system updates. Use @mentions to flag a teammate; borrowers never see this.',
  'cases.notesTab.composePlaceholder': 'Write a note. Mention a teammate with @handle.',
  'cases.notesTab.composeHint': 'Press ⌘/Ctrl + Enter to add. Mentions notify the named teammate.',
  'cases.notesTab.empty': 'No internal notes yet. Add one to track team communication.',
  'cases.notesTab.unknownMention': 'Mention not matched to a teammate',

  // Loan Case Application tab — intake snapshot + change history (#1758)
  'cases.applicationTab.title': 'Application',
  'cases.applicationTab.subtitle': 'What the borrower submitted on intake.',
  'cases.applicationTab.noData': 'No intake submission attached to this loan yet.',
  'cases.applicationTab.viewIntakeSubmission': 'View intake submission',
  'cases.applicationTab.astorRead.summary': 'Astor read the intake submitted via {{channel}}.',
  'cases.applicationTab.astorRead.noSignal': 'Astor will summarize the intake once a submission is attached.',
  'cases.applicationTab.snapshot.title': 'Applicant snapshot',
  'cases.applicationTab.snapshot.applicantName': 'Applicant name',
  'cases.applicationTab.snapshot.applicantEmail': 'Email',
  'cases.applicationTab.snapshot.applicantPhone': 'Phone',
  'cases.applicationTab.snapshot.submittedAt': 'Submitted',
  'cases.applicationTab.snapshot.intakeType': 'Intake type',
  'cases.applicationTab.snapshot.loanAmount': 'Loan amount',
  'cases.applicationTab.snapshot.propertyAddress': 'Property address',
  'cases.applicationTab.snapshot.referralCode': 'Referral code',
  'cases.applicationTab.snapshot.completionTime': 'Completion time',
  'cases.applicationTab.snapshot.completionTimeValue': '{{minutes}} min',
  'cases.applicationTab.intake.title': 'Intake answers',
  'cases.applicationTab.intake.empty': 'No intake answers captured.',
  'cases.applicationTab.history.title': 'Change history',
  'cases.applicationTab.history.unavailable': 'Application change history is coming soon.',

  // Loan Case Parties tab — borrowers, entities, guarantors, partner (#1758)
  'cases.partiesTab.title': 'Parties',
  'cases.partiesTab.subtitle': 'Everyone on the deal — borrowers, entities, guarantors, and the referring partner.',
  'cases.partiesTab.empty': 'No parties on this loan yet.',
  'cases.partiesTab.section.borrower': 'Borrower',
  'cases.partiesTab.section.entities': 'Entities',
  'cases.partiesTab.section.guarantors': 'Guarantors',
  'cases.partiesTab.section.partner': 'Partner',
  'cases.partiesTab.addParty': 'Add party',
  'cases.partiesTab.addBorrowingEntity': 'Add entity',
  'cases.partiesTab.addGuarantor': 'Add guarantor',
  'cases.partiesTab.menu.addBorrower': 'Add borrower',
  'cases.partiesTab.menu.addEntity': 'Add entity',
  'cases.partiesTab.menu.addGuarantor': 'Add guarantor',
  'cases.partiesTab.menu.linkPartner': 'Link partner',
  'cases.partiesTab.linkPartner': 'Link partner',
  'cases.partiesTab.unlinkPartner': 'Unlink partner',
  'cases.partiesTab.unlinkPartnerConfirm': 'Unlink this partner from the loan?',
  'cases.partiesTab.searchPartners': 'Search partners…',
  'cases.partiesTab.selectPartner': 'Select a partner',
  'cases.partiesTab.partnerLinked': 'Partner linked',
  'cases.partiesTab.partnerUnlinked': 'Partner unlinked',
  'cases.partiesTab.partnerRole': 'Partner role',
  'cases.partiesTab.noActivePartners': 'No active partners available.',
  'cases.partiesTab.pocBadge': 'Point of contact',
  'cases.partiesTab.pocTooltip': 'Primary point of contact for this loan.',
  'cases.partiesTab.makePoc': 'Make point of contact',
  'cases.partiesTab.noContact': 'No contact info on file.',
  'cases.partiesTab.contactEmail': 'Email {{name}}',
  'cases.partiesTab.contactPhone': 'Call {{name}}',
  'cases.partiesTab.singleBorrowerTooltip': 'Only one primary borrower per loan.',
  'cases.partiesTab.entity': 'Entity',
  'cases.partiesTab.role.label': 'Role',
  'cases.partiesTab.ownership.label': 'Ownership',
  'cases.partiesTab.ownershipInterest': 'Ownership interest',
  'cases.partiesTab.guarantee.label': 'Guarantee',
  'cases.partiesTab.guaranteeAmount': 'Guarantee amount',
  'cases.partiesTab.guaranteePercentage': 'Guarantee %',
  'cases.partiesTab.guaranteeType': 'Guarantee type',
  'cases.partiesTab.guarantorType': 'Guarantor type',
  'cases.partiesTab.removeEntity': 'Remove entity',
  'cases.partiesTab.removeGuarantor': 'Remove guarantor',

  // Borrower slide-over — 340px right rail (Issue #1749)
  'borrowers.slideover.ariaLabel': 'Borrower details',
  'borrowers.slideover.close': 'Close',
  'borrowers.slideover.expand': 'Open full profile',
  'borrowers.slideover.edit': 'Edit borrower',
  'borrowers.slideover.sendMessage': 'Send message',
  'borrowers.slideover.more': 'More actions',
  'borrowers.slideover.empty': 'Select a borrower to see their details here.',
  'borrowers.slideover.status': 'Status',
  'borrowers.slideover.ficoBand': 'FICO band',
  'borrowers.slideover.ficoBand.tier.exceptional': 'Exceptional',
  'borrowers.slideover.ficoBand.tier.veryGood': 'Very good',
  'borrowers.slideover.ficoBand.tier.good': 'Good',
  'borrowers.slideover.ficoBand.tier.fair': 'Fair',
  'borrowers.slideover.ficoBand.tier.poor': 'Poor',
  'borrowers.slideover.ficoBand.unavailable': 'FICO not on file',
  'borrowers.slideover.ficoBand.range300': '300',
  'borrowers.slideover.ficoBand.range580': '580',
  'borrowers.slideover.ficoBand.range670': '670',
  'borrowers.slideover.ficoBand.range740': '740',
  'borrowers.slideover.ficoBand.range850': '850',
  'borrowers.slideover.contact': 'Contact',
  'borrowers.slideover.contact.email': 'Email',
  'borrowers.slideover.contact.phone': 'Phone',
  'borrowers.slideover.contact.entity': 'Entity',
  'borrowers.slideover.contact.location': 'Location',
  'borrowers.slideover.contact.unspecified': 'Not on file',
  'borrowers.slideover.activity': 'Activity',
  'borrowers.slideover.activity.lastLogin': 'Last login',
  'borrowers.slideover.activity.lastLoginNever': 'Never',
  'borrowers.slideover.activity.joined': 'Joined',
  'borrowers.slideover.activity.loans': 'Loans',
  'borrowers.slideover.activity.loansFormat': '{{count}} on file',
  'borrowers.slideover.activity.consumerId': 'Borrower ID',
  'borrowers.slideover.astor.narrativeFallback':
    'Astor will surface a summary once activity is recorded for this borrower.',
  'borrowers.slideover.action.message': 'Message',
  'borrowers.slideover.action.sendInvite': 'Send invite',

  // Borrower notifications panel — alternate right rail on borrowers list (Issue #1749)
  'borrowers.notificationsPanel.ariaLabel': 'Borrower notifications',
  'borrowers.notificationsPanel.title': 'Notifications',
  'borrowers.notificationsPanel.markAllRead': 'Mark all as read',
  'borrowers.notificationsPanel.close': 'Close notifications',
  'borrowers.notificationsPanel.viewAll': 'View all notifications',
  'borrowers.notificationsPanel.group.borrowerContext': 'For this borrower',
  'borrowers.notificationsPanel.group.workspace': 'Workspace updates',
  'borrowers.notificationsPanel.empty.title': 'You are all caught up',
  'borrowers.notificationsPanel.empty.description':
    'New activity related to your borrowers will appear here as it happens.',
  'borrowers.notificationsPanel.loading': 'Loading notifications…',
  'borrowers.notificationsPanel.error': 'Could not load notifications. Try again shortly.',

  // Borrowers list — right-side ContextRail (Issue #1769 wave 2).
  // Owned by the Borrowers list page rail (BorrowerListContextRail);
  // do NOT mix with the borrower-detail page rail (BorrowerContextRail —
  // uses `contextRail.*` keys).
  'borrowersList.rail.ariaLabel': 'Borrower context panel',
  'borrowersList.rail.tab.details': 'Details',
  'borrowersList.rail.tab.astor': 'Astor',
  'borrowersList.rail.empty.details': 'No borrower selected',
  'borrowersList.rail.empty.activity': 'No borrower selected',
  'borrowersList.rail.empty.messages': 'No borrower selected',
  'borrowersList.rail.empty.astor': 'No borrower selected',
  'borrowersList.rail.empty.hint': 'Click a borrower row in the table to load their context here.',
  'borrowersList.rail.section.status': 'Status',
  'borrowersList.rail.section.ficoBand': 'FICO band',
  'borrowersList.rail.section.contact': 'Contact',
  'borrowersList.rail.section.activity': 'Activity',
  'borrowersList.rail.fico.unavailable': 'FICO not on file yet.',
  'borrowersList.rail.attr.email': 'Email',
  'borrowersList.rail.attr.phone': 'Phone',
  'borrowersList.rail.attr.lastLogin': 'Last login',
  'borrowersList.rail.attr.joined': 'Joined',
  'borrowersList.rail.attr.loans': 'Loans',
  'borrowersList.rail.attr.consumerId': 'Consumer ID',
  'borrowersList.rail.astor.summary': 'Astor · summary',
  'borrowersList.rail.astor.summaryEmpty': 'No Astor summary yet for this borrower.',
  'borrowersList.rail.astor.summaryDetailedEmpty':
    'Astor will surface a summary here once this borrower has loan activity.',
  'borrowersList.rail.astor.suggestedActions': 'Suggested actions',
  'borrowersList.rail.astor.action.message': 'Send message',
  'borrowersList.rail.astor.action.invite': 'Invite to portal',
  'borrowersList.rail.astor.action.refer': 'Refer to lender',
  'borrowersList.rail.astor.action.openProfile': 'Open full profile',
  'borrowersList.rail.action.message': 'Message',
  'borrowersList.rail.action.openProfile': 'Open profile',
  'borrowersList.rail.messages.empty': 'No messages yet.',
  'borrowersList.rail.messages.openComposer': 'Open message composer',
  'borrowersList.column.borrower': 'Borrower',
  'borrowersList.column.email': 'Email',
  'borrowersList.column.phone': 'Phone',
  'borrowersList.column.status': 'Status',
  'borrowersList.column.lastActivity': 'Last activity',
  'borrowersList.column.joined': 'Joined',
  'borrowersList.menu.viewProfile': 'View profile',
  'borrowersList.menu.sendMessage': 'Send message',
  'borrowersList.menu.deactivate': 'Deactivate',
  'borrowersList.empty.title': 'No borrowers found',
  'borrowersList.empty.searchHint': 'Try adjusting your search criteria',
  'borrowersList.empty.firstHint': 'Add your first borrower to get started',
  'borrowersList.error.load': 'Failed to load borrowers',
  'borrowersList.action.ariaLabel': 'Borrower action panel',

  // Partner slide-over — 340px right rail (Issue #1749)
  'partners.slideover.ariaLabel': 'Partner details',
  'partners.slideover.close': 'Close',
  'partners.slideover.expand': 'Open full profile',
  'partners.slideover.empty': 'Select a partner to see their details here.',
  'partners.slideover.type.whiteLabel': 'White-label partner',
  'partners.slideover.type.wholesale': 'Wholesale partner',
  'partners.slideover.type.referral': 'Referral partner',
  'partners.slideover.type.broker': 'Broker',
  'partners.slideover.type.unspecified': 'Partner',
  'partners.slideover.status.active': 'Active',
  'partners.slideover.status.pending': 'Pending verification',
  'partners.slideover.status.inactive': 'Inactive',
  'partners.slideover.status.suspended': 'Suspended',
  'partners.slideover.about': 'About',
  'partners.slideover.about.contactName': 'Contact name',
  'partners.slideover.about.companyName': 'Company',
  'partners.slideover.about.joined': 'Joined',
  'partners.slideover.about.commissionRate': 'Commission rate',
  'partners.slideover.about.unspecified': 'Not on file',
  'partners.slideover.performance': 'Performance · 30 days',
  'partners.slideover.performance.referrals': 'Referrals',
  'partners.slideover.performance.conversions': 'Conversions',
  'partners.slideover.performance.conversionsFormat': '{{count}} · {{percent}}%',
  'partners.slideover.performance.commissions': 'Commissions',
  'partners.slideover.performance.earnings': 'Earnings',
  'partners.slideover.warehouseLine': 'Warehouse line',
  'partners.slideover.warehouseLine.unavailable': 'No warehouse line linked yet.',
  'partners.slideover.warehouseLine.capacityFormat': 'Capacity {{capacity}} · utilized {{utilized}}',
  'partners.slideover.action.openProfile': 'Open profile',
  'partners.slideover.action.message': 'Message',

  // ============================================================================
  // Control Center → Intake Templates designer (Issue #1749 — v3 surface)
  // The borrower-facing form builder host. Mostly wraps the
  // IntakeTemplateEditor primitive + the version/list shell.
  // ============================================================================
  'controlCenter.intakeTemplatesDesigner.title': 'Intake templates',
  'controlCenter.intakeTemplatesDesigner.subtitle':
    'The forms borrowers see when they apply. Configure fields, docs, and which loan products use each template.',

  // KPI strip
  'controlCenter.intakeTemplatesDesigner.kpi.active.label': 'Active templates',
  'controlCenter.intakeTemplatesDesigner.kpi.active.subAvailable': 'of {{total}} available',
  'controlCenter.intakeTemplatesDesigner.kpi.active.subWithCustom': 'of {{total}} available · {{custom}} custom',
  'controlCenter.intakeTemplatesDesigner.kpi.default.label': 'Default template',
  'controlCenter.intakeTemplatesDesigner.kpi.default.value.none': 'None',
  'controlCenter.intakeTemplatesDesigner.kpi.default.sub': 'Used when no loan type specified',
  'controlCenter.intakeTemplatesDesigner.kpi.consumerLink.label': 'Consumer application link',
  'controlCenter.intakeTemplatesDesigner.kpi.consumerLink.copy': 'Copy generic link',

  // Supported investment types panel
  'controlCenter.intakeTemplatesDesigner.investmentTypes.title': 'Supported investment types',
  'controlCenter.intakeTemplatesDesigner.investmentTypes.subtitle':
    'Toggle which investment types borrowers can choose in the application wizard.',
  'controlCenter.intakeTemplatesDesigner.investmentTypes.minOneRequired':
    'At least one investment type must remain enabled.',
  'controlCenter.intakeTemplatesDesigner.investmentTypes.notSure.title': '"Not sure" option',
  'controlCenter.intakeTemplatesDesigner.investmentTypes.notSure.description':
    'When enabled, borrowers can select "Not sure — I\'d like to speak with an advisor".',

  // Table + toolbar
  'controlCenter.intakeTemplatesDesigner.search.placeholder': 'Search templates…',
  'controlCenter.intakeTemplatesDesigner.create': 'Create template',
  'controlCenter.intakeTemplatesDesigner.columns.active': 'Active',
  'controlCenter.intakeTemplatesDesigner.columns.template': 'Template',
  'controlCenter.intakeTemplatesDesigner.columns.type': 'Type',
  'controlCenter.intakeTemplatesDesigner.columns.source': 'Source',
  'controlCenter.intakeTemplatesDesigner.columns.steps': 'Steps',
  'controlCenter.intakeTemplatesDesigner.columns.usedBy': 'Used by',
  'controlCenter.intakeTemplatesDesigner.columns.version': 'Version',
  'controlCenter.intakeTemplatesDesigner.columns.default': 'Default',

  // Row cells
  'controlCenter.intakeTemplatesDesigner.source.system': 'System',
  'controlCenter.intakeTemplatesDesigner.source.custom': 'Custom',
  'controlCenter.intakeTemplatesDesigner.source.basedOn': 'Based on: {{name}}',
  'controlCenter.intakeTemplatesDesigner.steps.count': '{{count}} steps',
  'controlCenter.intakeTemplatesDesigner.usedBy.none': 'None',
  'controlCenter.intakeTemplatesDesigner.usedBy.products': '{{count}} loan product(s)',
  'controlCenter.intakeTemplatesDesigner.default.current': 'Current default',
  'controlCenter.intakeTemplatesDesigner.default.set': 'Set as default',

  // Row actions
  'controlCenter.intakeTemplatesDesigner.action.edit': 'Edit',
  'controlCenter.intakeTemplatesDesigner.action.customize': 'Customize (creates a copy)',
  'controlCenter.intakeTemplatesDesigner.action.preview': 'Preview as borrower',
  'controlCenter.intakeTemplatesDesigner.action.copyLink': 'Copy share link',

  // Empty / loading / help
  'controlCenter.intakeTemplatesDesigner.empty.title': 'No templates match your search',
  'controlCenter.intakeTemplatesDesigner.empty.description':
    'Intake templates define the loan application wizard your borrowers see. Create one or adjust your search.',
  'controlCenter.intakeTemplatesDesigner.help':
    'Active templates are available for use; the default is used when borrowers visit /apply/loan without a specific type.',

  // ============================================================================
  // COMMISSIONS DASHBOARD (V2 — Issue #1803, v3-design-pass-5 PR-2)
  // ============================================================================
  // Page header
  'commissions.dashboard.title': 'Commissions',
  'commissions.dashboard.subtitle': 'Money flow across ALL partners — inbound + outbound',

  // Flow-of-money visualization
  'commissions.dashboard.flow.youEarned': 'YOU EARNED',
  'commissions.dashboard.flow.youPaid': 'YOU PAID',
  'commissions.dashboard.flow.net': 'NET',
  'commissions.dashboard.flow.earnedSub': '↗ from {{partners}} partners · {{loans}} funded loans',
  'commissions.dashboard.flow.paidSub': '↙ to {{partners}} partners · {{loans}} funded loans',
  'commissions.dashboard.flow.netInflow': 'net inflow this period',
  'commissions.dashboard.flow.netOutflow': 'net outflow this period',
  'commissions.dashboard.flow.pendingPayout': 'PENDING PAYOUT',
  'commissions.dashboard.flow.pendingPayoutSub': 'across {{loans}} funded loans',
  'commissions.dashboard.flow.ytdNet': 'YTD NET',
  'commissions.dashboard.flow.ytdNetSub': '{{months}} months',
  'commissions.dashboard.flow.topNetContributor': 'TOP NET CONTRIBUTOR',
  'commissions.dashboard.flow.ytd': 'YTD',

  // Tabs
  'commissions.dashboard.tab.all': 'All events',
  'commissions.dashboard.tab.earned': 'You earned',
  'commissions.dashboard.tab.paid': 'You paid',
  'commissions.dashboard.tab.byPartner': 'By partner',
  'commissions.dashboard.tab.byEmployee': 'By your employee',
  'commissions.dashboard.tab.settings': 'Settings',

  // Filter row
  'commissions.dashboard.filter.searchPlaceholder': 'Search by loan, partner, employee…',
  'commissions.dashboard.filter.direction': 'Direction',
  'commissions.dashboard.filter.directionAll': 'All directions',
  'commissions.dashboard.filter.directionEarned': 'You earned',
  'commissions.dashboard.filter.directionPaid': 'You paid',
  'commissions.dashboard.filter.status': 'Status',
  'commissions.dashboard.filter.statusAll': 'All statuses',
  'commissions.dashboard.filter.partner': 'Partner',
  'commissions.dashboard.filter.partnerAll': 'All partners',
  'commissions.dashboard.filter.date': 'Date',
  'commissions.dashboard.filter.dateMtd': 'Month to date',
  'commissions.dashboard.filter.dateQtd': 'Quarter to date',
  'commissions.dashboard.filter.dateYtd': 'Year to date',
  'commissions.dashboard.filter.dateAll': 'All time',

  // Actions
  'commissions.dashboard.action.exportReport': 'Export Report',
  'commissions.dashboard.action.export': 'Export',

  // Table headers
  'commissions.dashboard.table.header.direction': 'Direction',
  'commissions.dashboard.table.header.loan': 'Loan',
  'commissions.dashboard.table.header.partner': 'Partner',
  'commissions.dashboard.table.header.attribution': 'Attribution',
  'commissions.dashboard.table.header.amount': 'Amount',
  'commissions.dashboard.table.header.status': 'Status',
  'commissions.dashboard.table.header.funded': 'Funded',

  // Direction chips
  'commissions.dashboard.directionChip.earned': '↗ EARNED',
  'commissions.dashboard.directionChip.paid': '↙ PAID',

  // Attribution
  'commissions.dashboard.attribution.your': 'your:',
  'commissions.dashboard.attribution.their': 'their:',
  'commissions.dashboard.attribution.none': '—',

  // Status labels
  'commissions.dashboard.status.pending': 'pending',
  'commissions.dashboard.status.approved': 'approved',
  'commissions.dashboard.status.paid': 'paid',
  'commissions.dashboard.status.cancelled': 'cancelled',

  // Empty + Astor callout
  'commissions.dashboard.empty': 'No commission events match your filters.',
  'commissions.dashboard.astorCallout.label': 'Astor:',
  'commissions.dashboard.astorCallout.body':
    'Looking for internal employee commissions (paid to your team for processing loans)? Those live on each employee’s profile → Compensation tab, not here. This page is partner-to-partner only.',

  // Placeholder notice (mock data warning until commissions.listAll lands — Issue #1803)
  'commissions.dashboard.placeholderNotice':
    'Showing placeholder data. Live commission totals connect once the backend procedure ships.',

  // Placeholder views for unimplemented tabs
  'commissions.dashboard.tabPlaceholder.byPartner': 'Partner-grouped view coming with the live data feed.',
  'commissions.dashboard.tabPlaceholder.byEmployee': 'Per-employee attribution view coming with the live data feed.',
  'commissions.dashboard.tabPlaceholder.settings':
    'Commission tier and split-rule settings will live here. Backend wiring pending.',

  // ============================================================================
  // APPROVALS V2 (v3 design-pass-5 PR-2)
  // ============================================================================

  // Page header
  'approvalsV2.header.title': 'Approvals',
  'approvalsV2.header.subtitle': 'Everything that needs a human decision, grouped by what it is',
  'approvalsV2.header.export': 'Export',
  'approvalsV2.header.askAstor': 'Ask Astor',

  // KPI strip (4 tiles above the AstorBlock).
  // `awaitingYou` wires to the live `waitingForMe` row count; the other three
  // render an em-dash placeholder until backend Astor analytics lands
  // (TODO #1818-followup).
  'approvalsV2.kpi.awaitingYou.label': 'Awaiting you',
  'approvalsV2.kpi.awaitingYou.sub': 'across all categories',
  'approvalsV2.kpi.astorPrescreened.label': 'Astor pre-screened',
  'approvalsV2.kpi.astorPrescreened.sub': 'verdict + reasoning attached',
  'approvalsV2.kpi.avgTimeToDecide.label': 'Avg time to decide',
  'approvalsV2.kpi.avgTimeToDecide.sub': 'rolling 30d',
  'approvalsV2.kpi.astorAgreement.label': 'Astor agreement',
  'approvalsV2.kpi.astorAgreement.sub': 'your decision vs. Astor verdict',

  // Astor block (shown when something is past SLA)
  'approvalsV2.astorBlock.headline': 'Astor sees {{relative}} that this {{id}} is past SLA — triage it now?',
  'approvalsV2.astorBlock.action': 'Triage {{id}}',

  // Category tiles (5 buckets)
  'approvalsV2.category.EXCEPTION.label': 'EXCEPTION',
  'approvalsV2.category.EXCEPTION.sub': 'human override required',
  'approvalsV2.category.EXCEPTION.description': 'Policy / underwriting exceptions waiting on a decision',
  'approvalsV2.category.PRICING.label': 'PRICING',
  'approvalsV2.category.PRICING.sub': 'rate / fee adjustments',
  'approvalsV2.category.PRICING.description': 'Rate, fee, or pricing-tier changes outside the standard sheet',
  'approvalsV2.category.TERM_SHEET.label': 'TERM SHEET',
  'approvalsV2.category.TERM_SHEET.sub': 'awaiting your sign-off',
  'approvalsV2.category.TERM_SHEET.description': 'Term sheet drafts ready for your approval',
  'approvalsV2.category.DOCS.label': 'DOCS',
  'approvalsV2.category.DOCS.sub': 'document review queue',
  'approvalsV2.category.DOCS.description': 'Borrower / property documents queued for review',
  'approvalsV2.category.ESIGN.label': 'E-SIGN',
  'approvalsV2.category.ESIGN.sub': 'awaiting signature countersign',
  'approvalsV2.category.ESIGN.description': 'E-signature packets waiting on a counterparty',

  // Filter bar
  'approvalsV2.filters.searchPlaceholder': 'Search by ID, workflow, requestor…',
  'approvalsV2.filters.searchAriaLabel': 'Search approvals',
  'approvalsV2.filters.category.all': 'All categories',
  'approvalsV2.filters.category.ariaLabel': 'Filter by category',
  'approvalsV2.filters.assigned.all': 'All assignees',
  'approvalsV2.filters.assigned.me': 'Assigned to me',
  'approvalsV2.filters.assigned.ariaLabel': 'Filter by assignee',
  'approvalsV2.filters.pipelineStage.all': 'All pipeline stages',
  'approvalsV2.filters.pipelineStage.ariaLabel': 'Filter by pipeline stage',
  'approvalsV2.filters.astorRead.all': 'All Astor reads',
  'approvalsV2.filters.astorRead.ariaLabel': 'Filter by Astor verdict',
  'approvalsV2.filters.groupByCategory': 'Group by category',

  // Astor verdict labels
  'approvalsV2.astorVerdict.approve': 'Approve',
  'approvalsV2.astorVerdict.review': 'Review',
  'approvalsV2.astorVerdict.hold': 'Hold',

  // Column headers
  'approvalsV2.columns.idSla': 'ID / SLA',
  'approvalsV2.columns.whatLoan': 'What & loan',
  'approvalsV2.columns.pipelineStage': 'Pipeline stage',
  'approvalsV2.columns.amount': 'Amount',
  'approvalsV2.columns.astorRead': 'Astor read',
  'approvalsV2.columns.assigned': 'Assigned',
  'approvalsV2.columns.action': 'Action',

  // Row cells
  'approvalsV2.row.urgentSla': 'URGENT {{hours}}',
  'approvalsV2.row.overdueSla': 'OVERDUE {{hours}}',
  'approvalsV2.row.sla': 'SLA {{hours}}',
  'approvalsV2.row.stagePending': 'Pending',
  'approvalsV2.row.amountPending': '—',
  'approvalsV2.row.astorConfidence': '{{percent}}%',
  'approvalsV2.row.astorAnalyzing': 'Astor analyzing…',
  'approvalsV2.row.escalationPath': '→ {{target}}',
  'approvalsV2.row.escalationFallback': 'Underwriter pool',
  'approvalsV2.row.review': 'Review',
  'approvalsV2.row.quickApprove': 'Quick approve',

  // Tile sub-stat
  'approvalsV2.tile.slaAtRisk': '{{count}} SLA at risk',

  // ============================================================================
  // ASTOR AUTONOMY (v3 design-pass-5 PR-2 — Issue #1806)
  // ============================================================================

  // Read-only preview notice (controls disabled until #1806 lands)
  'astorAutonomy.readOnlyNotice':
    'Read-only preview. Master autonomy and per-agent overrides are gated until the backend procedures land.',

  // Empty-state messaging while business context resolves (PR-1808 round 2)
  'commissions.dashboard.emptyState.noBusiness': 'Select a business to view its commissions activity.',

  // ============================================================================
  // ENUM LOCALIZATION
  // Use `enums.<EnumName>.<VALUE>` per `.claude/rules/portals.md` § Feature-key
  // namespacing. Mirrors the PascalCase TS enum identifier exactly.
  // ============================================================================

  // UserRole — used wherever an internal role code is shown to a user
  // (escalation paths, approver pools, assignment chips).
  'enums.UserRole.super_admin': 'Super admin',
  'enums.UserRole.platform_admin': 'Platform admin',
  'enums.UserRole.business_admin': 'Admin',
  'enums.UserRole.business_user': 'Member',
  'enums.UserRole.business_viewer': 'Viewer',
  'enums.UserRole.consumer': 'Borrower',
  'enums.UserRole.loan_officer': 'Loan officer',
  'enums.UserRole.processor': 'Processor',
  'enums.UserRole.closer': 'Closer',
  'enums.UserRole.underwriter': 'Underwriter',
  'enums.UserRole.senior_underwriter': 'Senior underwriter',
  'enums.UserRole.analyst': 'Analyst',
  'enums.UserRole.ops_manager': 'Ops manager',
  'enums.UserRole.portfolio_manager': 'Portfolio manager',
  'enums.UserRole.fund_manager': 'Fund manager',
  'enums.UserRole.principal': 'Principal',
  'enums.UserRole.draw_inspector': 'Draw inspector',
  'enums.UserRole.servicing': 'Servicing',

  // ============================================================================
  // EMPTY STATES — v3 design-pass-6 (Borrowers / Partners / Commissions)
  // ============================================================================

  // --- EmptyBorrowers (loans/borrowers/_components/EmptyBorrowers.tsx) -------
  'dashboard.emptyStates.borrowers.title': 'No borrowers yet.',
  'dashboard.emptyStates.borrowers.subtitle':
    'Once you start originating loans, every borrower you onboard appears here with their FICO band, prior projects, and lifetime LTV.',
  'dashboard.emptyStates.borrowers.primaryCta': 'Send application invite',
  'dashboard.emptyStates.borrowers.secondaryCta': 'Add manually',

  // --- EmptyPartners (partners/_components/EmptyPartners.tsx) ---------------
  // Bidirectional explainer
  'dashboard.emptyStates.partners.explainer.title': 'How partnerships work in Fiops',
  'dashboard.emptyStates.partners.explainer.bidirectionalBadge': 'Bidirectional',
  'dashboard.emptyStates.partners.explainer.outbound.eyebrow': 'OUTBOUND · YOU SEND',
  'dashboard.emptyStates.partners.explainer.outbound.body': 'You refer borrowers to their products.',
  'dashboard.emptyStates.partners.explainer.outbound.emphasis': 'You earn when their loan funds.',
  'dashboard.emptyStates.partners.explainer.inbound.eyebrow': 'INBOUND · THEY SEND',
  'dashboard.emptyStates.partners.explainer.inbound.body': 'They refer borrowers to your products.',
  'dashboard.emptyStates.partners.explainer.inbound.emphasis': 'You pay when the loan funds.',
  // Standard empty CTA
  'dashboard.emptyStates.partners.title': 'No partners yet.',
  'dashboard.emptyStates.partners.subtitle':
    'Invite a partner and Fiops will set up the bidirectional referral links automatically — with per-employee attribution so commissions go to the right person.',
  'dashboard.emptyStates.partners.primaryCta': 'Invite a partner',
  'dashboard.emptyStates.partners.secondaryCta': 'Import from CSV',
  // Astor offer strip
  'dashboard.emptyStates.partners.astorOffer.label': 'Astor:',
  'dashboard.emptyStates.partners.astorOffer.body':
    'Want me to draft an invitation email for a partner you have in mind? Tell me their name and what kind of partnership.',
  'dashboard.emptyStates.partners.astorOffer.cta': 'Draft for me',

  // --- EmptyCommissions (commissions/_components/EmptyCommissions.tsx) ------
  'dashboard.emptyStates.commissions.title': 'No commission events yet.',
  'dashboard.emptyStates.commissions.subtitle':
    "Commissions show up here when a referred loan funds. Until then, you can see what you're projected to earn.",
  'dashboard.emptyStates.commissions.primaryCta': 'View partners',
  'dashboard.emptyStates.commissions.secondaryCta': 'How attribution works',
  // Projected from pipeline
  'dashboard.emptyStates.commissions.projected.heading': 'Projected from active pipeline',
  'dashboard.emptyStates.commissions.projected.astorBadge': 'Astor',
  'dashboard.emptyStates.commissions.projected.basedOn': '· based on {{count}} referred loans in flight',
  // EARN tile
  'dashboard.emptyStates.commissions.projected.earnLabel': "YOU'LL EARN",
  'dashboard.emptyStates.commissions.projected.earnSub': 'across {{count}} outbound loans · est. fund {{window}}',
  'dashboard.emptyStates.commissions.projected.earnSubEmpty': 'no outbound loans in flight yet',
  // PAY tile
  'dashboard.emptyStates.commissions.projected.payLabel': "YOU'LL PAY",
  'dashboard.emptyStates.commissions.projected.paySub': 'across {{count}} inbound loans · est. fund {{window}}',
  'dashboard.emptyStates.commissions.projected.paySubEmpty': 'no inbound loans in flight yet',
  // NET tile
  'dashboard.emptyStates.commissions.projected.netLabel': 'PROJECTED NET',
  'dashboard.emptyStates.commissions.projected.netSubNegative': 'this period · pay > earn',
  'dashboard.emptyStates.commissions.projected.netSubPositive': 'this period · earn > pay',
  'dashboard.emptyStates.commissions.projected.netSubEmpty': 'will appear once loans are in flight',

  // Astor verdict — "unknown" addition (approve/review/hold defined above with the PR-2 cluster)
  'approvalsV2.astorVerdict.unknown': 'Unknown',

  // ============================================================================
  // Empty Inbox (v3 design pass — celebrated zero state)
  // ============================================================================
  // Header chip
  'dashboard.emptyStates.inbox.chip.allCaught': 'All caught up',
  // Hero
  'dashboard.emptyStates.inbox.hero.title': 'Inbox zero.',
  // `subtitle` is the telemetry-aware copy — only shown when callers pass
  // real `clearedCount` / `avgTimeMinutes` props. `subtitleNoTelemetry` is
  // the brand-new-tenant fallback so we never fabricate activity attributed
  // to "your team" (Copilot review on PR #1809).
  'dashboard.emptyStates.inbox.hero.subtitle': 'All {{count}} items cleared today · median response time {{minutes}}.',
  'dashboard.emptyStates.inbox.hero.subtitleNoTelemetry':
    'No notifications yet · this is where Astor will surface what needs your attention.',
  'dashboard.emptyStates.inbox.hero.responseTime': '{{minutes}} minutes',
  // Stat strip
  'dashboard.emptyStates.inbox.stats.cleared': 'CLEARED',
  'dashboard.emptyStates.inbox.stats.byAstor': 'BY ASTOR',
  'dashboard.emptyStates.inbox.stats.byYou': 'BY YOU',
  'dashboard.emptyStates.inbox.stats.avgTime': 'AVG TIME',
  // On the horizon
  'dashboard.emptyStates.inbox.horizon.title': 'On the horizon',
  'dashboard.emptyStates.inbox.horizon.eSignExpiring': 'E-Sign packet expiring · Hillside Dr',
  'dashboard.emptyStates.inbox.horizon.eSignWhen': 'in 18h',
  'dashboard.emptyStates.inbox.horizon.bpoDue': 'BPO due back · 4218 Vermont Pl',
  'dashboard.emptyStates.inbox.horizon.bpoWhen': 'tomorrow',
  'dashboard.emptyStates.inbox.horizon.callScheduled': 'Marcus Reyes call scheduled',
  'dashboard.emptyStates.inbox.horizon.callWhen': 'Tue 10am',

  // ============================================================================
  // Empty Approvals (v3 design pass — all clear)
  // ============================================================================
  // Chip
  'dashboard.emptyStates.approvals.chip.allClear': 'All clear',
  // Hero — `subtitle` is the telemetry-aware copy (only rendered when
  // callers pass `weeklyKpis`). `subtitleNoTelemetry` is the brand-new-
  // tenant fallback so we never fabricate weekly activity attributed to
  // "your team" (Copilot review on PR #1809).
  'dashboard.emptyStates.approvals.hero.title': 'Nothing awaiting approval.',
  'dashboard.emptyStates.approvals.hero.subtitle':
    'Your team has cleared {{decisions}} approvals this week with a median decision time of {{medianTime}}.',
  'dashboard.emptyStates.approvals.hero.subtitleNoTelemetry':
    'No approvals in flight. New decisions will land here as Astor surfaces them.',
  // KPI overline
  'dashboard.emptyStates.approvals.kpi.overline': 'This week · your team',
  // KPI tile labels + supporting subtext (values come from `weeklyKpis`
  // prop at render time — no hardcoded fabricated numbers).
  'dashboard.emptyStates.approvals.kpi.decisions.label': 'Decisions',
  'dashboard.emptyStates.approvals.kpi.decisions.sub': 'all approved',
  'dashboard.emptyStates.approvals.kpi.medianTime.label': 'Median time',
  'dashboard.emptyStates.approvals.kpi.medianTime.sub': '-22% vs. goal',
  'dashboard.emptyStates.approvals.kpi.astorAgree.label': 'Astor agree',
  'dashboard.emptyStates.approvals.kpi.astorAgree.sub': 'last 30d',
  'dashboard.emptyStates.approvals.kpi.inCommittee.label': 'In committee',
  'dashboard.emptyStates.approvals.kpi.inCommittee.sub': 'schedule Wed 2pm',

  // ============================================================================
  // Empty Watchlist (v3 design pass — with Astor suggestions)
  // ============================================================================
  // Section overline
  'dashboard.emptyStates.watchlist.suggestions.overline': 'Astor suggests watching',
  'dashboard.emptyStates.watchlist.suggestions.count': '{{count}} picks',
  // Pick 1 — Borrower
  'dashboard.emptyStates.watchlist.picks.borrower.type': 'Borrower',
  'dashboard.emptyStates.watchlist.picks.borrower.name': 'Marcus Reyes',
  'dashboard.emptyStates.watchlist.picks.borrower.reason': 'Active in 2 loans · highest exposure',
  // Pick 2 — Loan
  'dashboard.emptyStates.watchlist.picks.loan.type': 'Loan',
  'dashboard.emptyStates.watchlist.picks.loan.name': '4218 Vermont Pl',
  'dashboard.emptyStates.watchlist.picks.loan.reason': 'LTC drifting toward cap',
  // Pick 3 — Partner
  'dashboard.emptyStates.watchlist.picks.partner.type': 'Partner',
  'dashboard.emptyStates.watchlist.picks.partner.name': 'Riverstone Capital',
  'dashboard.emptyStates.watchlist.picks.partner.reason': '12 active referrals · top conversion',
  // CTA
  'dashboard.emptyStates.watchlist.picks.addToWatchlist': 'Add to watchlist',

  // ============================================================================
  // Empty Astor (v3 design pass — prompts grid by category)
  // ============================================================================
  // Capability strip (persistent header above hero)
  'dashboard.emptyStates.astor.header.assistantChip': 'Fiops Assistant · v4.2',
  'dashboard.emptyStates.astor.header.capability': 'Pipeline-aware. Knows your policy. Cites every claim.',
  'dashboard.emptyStates.astor.header.clearContext': 'Clear context',
  // Hero
  'dashboard.emptyStates.astor.hero.title': 'What can I help you with?',
  'dashboard.emptyStates.astor.hero.subtitle':
    'I know your pipeline, policy, warehouse lines, and 12 months of decision history. Ask in plain English — or pick a starting point.',
  // Categories — 6 prompt cards
  'dashboard.emptyStates.astor.prompts.pipeline.category': 'PIPELINE',
  'dashboard.emptyStates.astor.prompts.pipeline.label': "What's at SLA risk today?",
  'dashboard.emptyStates.astor.prompts.pipeline.sub': 'Ranked by hours to breach',
  'dashboard.emptyStates.astor.prompts.reporting.category': 'REPORTING',
  'dashboard.emptyStates.astor.prompts.reporting.label': 'Funded loans this month',
  'dashboard.emptyStates.astor.prompts.reporting.sub': 'Volume + by originator',
  'dashboard.emptyStates.astor.prompts.decisions.category': 'DECISIONS',
  'dashboard.emptyStates.astor.prompts.decisions.label': "What's awaiting my approval?",
  'dashboard.emptyStates.astor.prompts.decisions.sub': '6 items · 3 ready, 3 wait',
  'dashboard.emptyStates.astor.prompts.drafting.category': 'DRAFTING',
  'dashboard.emptyStates.astor.prompts.drafting.label': 'Draft a credit memo',
  'dashboard.emptyStates.astor.prompts.drafting.sub': 'Pick a loan to start from',
  'dashboard.emptyStates.astor.prompts.search.category': 'SEARCH',
  'dashboard.emptyStates.astor.prompts.search.label': 'Find loans with FICO < 720',
  'dashboard.emptyStates.astor.prompts.search.sub': 'Across all pipelines',
  'dashboard.emptyStates.astor.prompts.partners.category': 'PARTNERS',
  'dashboard.emptyStates.astor.prompts.partners.label': 'Top originators this quarter',
  'dashboard.emptyStates.astor.prompts.partners.sub': 'By volume + conversion',
  // Input bar
  'dashboard.emptyStates.astor.input.placeholder': 'Ask Astor… (/find · @loan · @borrower)',
  'dashboard.emptyStates.astor.input.ariaLabel': 'Ask Astor a question',
  'dashboard.emptyStates.astor.input.modelChip': 'haiku-4.5',
  'dashboard.emptyStates.astor.input.send': 'Send',
  // ============================================================================
  // V3 DESIGN PASS — SCENARIO VARIANTS (4 state-driven Overview renders)
  // Source: docs/ux-v3-1769/project/pages/states-scenarios.jsx
  // ============================================================================
  // -- scnDeclined: Application declined (intake submission, status='rejected')
  'cases.scenarios.declined.banner.title': 'Declined — does not meet credit box',
  'cases.scenarios.declined.banner.primaryReason': 'Primary reason:',
  'cases.scenarios.declined.banner.fallbackBody':
    'Borrower does not meet underwriting criteria. Review failed gates below for the specific policy thresholds.',
  'cases.scenarios.declined.banner.letterSent': 'Decline letter sent',
  'cases.scenarios.declined.banner.fallbackSentMeta': '— borrower acknowledged read.',
  'cases.scenarios.declined.astor.title': 'Astor pre-screen (at submission)',
  'cases.scenarios.declined.astor.confidenceChip': 'Decline · conf {{conf}}%',
  'cases.scenarios.declined.astor.predictedAtIntake': 'predicted at intake',
  'cases.scenarios.declined.astor.fallbackBody':
    'Astor flagged this submission as decline-likely at intake. Key signals: FICO below floor, limited prior experience, and reserves below the program minimum. Human review confirmed.',
  'cases.scenarios.declined.gates.title': 'Failed policy gates',
  'cases.scenarios.declined.gates.needPrefix': 'need',
  'cases.scenarios.declined.gates.severity.hard': 'hard fail',
  'cases.scenarios.declined.gates.severity.soft': 'near miss',
  'cases.scenarios.declined.revisit.title': 'Astor: when to revisit',
  'cases.scenarios.declined.revisit.body':
    "Borrower could be a fit in the future if they meet the conditions below. I'll watch for any of these signals and ping you.",
  'cases.scenarios.declined.revisit.fallback.fico': 'FICO improves to 700+ · monitor 90d',
  'cases.scenarios.declined.revisit.fallback.jv': 'Listed as JV partner on a closed flip',
  'cases.scenarios.declined.revisit.fallback.referral': 'Refers a stronger borrower from their network',
  'cases.scenarios.declined.revisit.watch': 'Watch this borrower',
  'cases.scenarios.declined.timeline.title': 'Timeline',
  // -- scnFunded: Funded loan (loanStage in [FUNDED, SERVICING])
  'cases.scenarios.funded.banner.title': 'Loan funded successfully',
  'cases.scenarios.funded.banner.fallbackBody': 'Loan funded and title confirmed receipt. Servicing active.',
  'cases.scenarios.funded.banner.origFee': 'ORIGINATION FEE',
  'cases.scenarios.funded.banner.monthlyPi': 'MONTHLY P&I',
  'cases.scenarios.funded.banner.maturity': 'MATURITY',
  'cases.scenarios.funded.banner.daysToClose': 'DAYS TO CLOSE',
  'cases.scenarios.funded.servicing.title': 'Servicing snapshot',
  'cases.scenarios.funded.servicing.unpaidPrincipal': 'UNPAID PRINCIPAL',
  'cases.scenarios.funded.servicing.accruedInterest': 'ACCRUED INTEREST',
  'cases.scenarios.funded.servicing.paymentsMade': 'PAYMENTS MADE',
  'cases.scenarios.funded.servicing.dpd': 'DPD',
  'cases.scenarios.funded.servicing.scheduleLabel': 'Payment schedule',
  // a11y labels for the payment-schedule heatmap. Each cell announces
  // its month index + state to screen readers (Copilot PR #1810). The
  // visible cell is purely visual; the aria-label carries the meaning.
  'cases.scenarios.funded.servicing.scheduleAriaLabel': 'Payment schedule heatmap, 18 months',
  'cases.scenarios.funded.servicing.scheduleCell.paid': 'Month {{month}}: paid',
  'cases.scenarios.funded.servicing.scheduleCell.current': 'Month {{month}}: current',
  'cases.scenarios.funded.servicing.scheduleCell.future': 'Month {{month}}: future',
  'cases.scenarios.funded.servicing.fallback.start': 'Mar 26',
  'cases.scenarios.funded.servicing.fallback.next': '↑ next: May 1',
  'cases.scenarios.funded.servicing.fallback.end': 'Sep 27',
  'cases.scenarios.funded.rehab.title': 'Rehab progress · borrower-reported',
  'cases.scenarios.funded.rehab.drawsProgress': 'Construction draws · {{done}} of {{total}} complete',
  'cases.scenarios.funded.rehab.requestDraw': 'Request draw',
  'cases.scenarios.funded.astor.title': 'Astor watching',
  'cases.scenarios.funded.astor.body':
    "Loan is on track. I'm monitoring rehab pace, comp drift, and any signals from the borrower's other holdings.",
  'cases.scenarios.funded.astor.fallback.rehabPace': 'Rehab pace · on schedule',
  'cases.scenarios.funded.astor.fallback.arv': 'ARV trending · stable',
  'cases.scenarios.funded.astor.fallback.borrowerOther': 'Borrower other loans · current',
  'cases.scenarios.funded.yield.title': 'Yield to date',
  'cases.scenarios.funded.yield.metaFallback': 'interest + origination · 60 days held',
  'cases.scenarios.funded.yield.annualizedLabel': 'ANNUALIZED',
  'cases.scenarios.funded.yield.vsPortfolioLabel': 'VS. PORTFOLIO',
  // -- scnBlocked: Blocked on borrower (metadata.borrowerBlocked)
  // Plural variants: shared-types `translate()` has no ICU plural rules
  // (see `1-shared-types/src/i18n/utils.ts`), so the call site picks
  // `_one` vs `_other` manually based on count. Convention matches
  // `cases.stageIndicator.blockerCount_one` / `_other` (Copilot PR #1810).
  'cases.scenarios.blocked.banner.title_one': 'Waiting on borrower for {{count}} document · stale {{days}} days',
  'cases.scenarios.blocked.banner.title_other': 'Waiting on borrower for {{count}} documents · stale {{days}} days',
  'cases.scenarios.blocked.banner.fallbackBody':
    "Sent the request via email. Borrower opened but hasn't replied. Astor recommends a polite nudge with a deadline.",
  'cases.scenarios.blocked.banner.sendNudge': 'Send nudge now',
  'cases.scenarios.blocked.banner.draftCustom': 'Draft custom message',
  'cases.scenarios.blocked.banner.schedule': 'Schedule for tomorrow 9am',
  'cases.scenarios.blocked.outstanding.title_one': 'Outstanding requests · {{count}} item',
  'cases.scenarios.blocked.outstanding.title_other': 'Outstanding requests · {{count}} items',
  'cases.scenarios.blocked.priority.high': 'high',
  'cases.scenarios.blocked.priority.med': 'med',
  'cases.scenarios.blocked.priority.low': 'low',
  'cases.scenarios.blocked.nudge.title': "Astor's suggested nudge",
  'cases.scenarios.blocked.nudge.draftLabel': 'DRAFT · awaiting your send',
  'cases.scenarios.blocked.nudge.fallback.line1':
    'Hi — quick check-in. We still need a few items to keep your loan on track for committee this week.',
  'cases.scenarios.blocked.nudge.fallback.line2':
    '· Interior photos · Itemized contractor SOW · Updated rehab schedule',
  'cases.scenarios.blocked.nudge.fallback.line3':
    "If you can get these to me by Thursday EOD, we'll have you in committee Tuesday. Reply with any roadblocks I can help unblock.",
  'cases.scenarios.blocked.nudge.deadlinePrefix': 'Deadline:',
  'cases.scenarios.blocked.nudge.sendAsIs': 'Send as-is',
  'cases.scenarios.blocked.responsiveness.title': 'Borrower responsiveness',
  'cases.scenarios.blocked.responsiveness.avgReply': 'AVG REPLY',
  'cases.scenarios.blocked.responsiveness.last30d': 'LAST 30D',
  'cases.scenarios.blocked.responsiveness.staleNow': 'STALE NOW',
  'cases.scenarios.blocked.responsiveness.opened': 'OPENED',
  'cases.scenarios.blocked.responsiveness.fallbackNote':
    'Unusual silence — typically responds within hours. Last seen on platform earlier this week.',
  // -- scnPastSla: Approvals queue with past-SLA rows
  'cases.scenarios.pastSla.header.pastSla': '{{count}} past SLA',
  'cases.scenarios.pastSla.header.atRisk': '{{count}} at risk',
  'cases.scenarios.pastSla.header.hotSubtitle':
    'Queue is hot today. Astor has triaged — the top items need a human call before noon.',
  // Plural variants — call site picks `_one` vs `_other` manually because
  // shared-types `translate()` has no ICU plural rules (Copilot PR #1810).
  'cases.scenarios.pastSla.astorBlock.headline_one': '{{count}} approval breached SLA overnight — clear {{id}} first.',
  'cases.scenarios.pastSla.astorBlock.headline_other':
    '{{count}} approvals breached SLA overnight — fastest path is to clear {{id}} first.',
  'cases.scenarios.pastSla.astorBlock.bodyLead': '{{id}} has been waiting',
  // `relativeOver` interpolates `formatRelativeTime()` output (e.g. "about
  // 2 hours ago") and produced grammatically wrong "about 2 hours ago past
  // SLA". `durationOver` takes a duration string without the "ago" suffix
  // (e.g. "2h", "3d 4h") composed at the call site (Copilot PR #1810).
  'cases.scenarios.pastSla.astorBlock.relativeOver': '{{relative}} past SLA',
  'cases.scenarios.pastSla.astorBlock.durationOver': '{{duration}} past SLA',
  'cases.scenarios.pastSla.astorBlock.bodyTail':
    "— the borrower is on the partner's hot list and a delay risks them taking the deal elsewhere. I've prepared a 1-click approve with experience exception.",
  'cases.scenarios.pastSla.astorBlock.confidence': 'conf {{conf}}%',
  'cases.scenarios.pastSla.astorBlock.seeAll': 'See all {{count}}',
  'cases.scenarios.pastSla.astorBlock.action': 'Approve {{id}} →',

  // HOME — BUSINESS ADMIN / OWNER VARIANT
  // ============================================================================
  // Role-aware command-center home for BUSINESS_ADMIN / PRINCIPAL users.
  // Renders KPI strip + team/partner panels + scorecard + delegations.
  'dashboard.role.admin.badge.role': 'Business Admin · Owner',
  'dashboard.role.admin.context.activeLoans': '{{count}} active loans',
  'dashboard.role.admin.context.teamMembers': '{{count}} team members',
  'dashboard.role.admin.context.fundedMtd': '{{value}} funded MTD',

  // Astor headline / body / actions
  'dashboard.role.admin.astor.headline':
    'On pace to hit your $10M funded-MTD target — but UW throughput is the bottleneck.',
  'dashboard.role.admin.astor.confidenceLabel': 'Astor confidence',
  'dashboard.role.admin.astor.bodyPrefix': "You'll likely close",
  'dashboard.role.admin.astor.bodyMiddle':
    'by month-end at current velocity. UW Review is the slowest stage — averaging',
  'dashboard.role.admin.astor.bodySuffix':
    'vs. 4.2d on your best weeks. Rachel handles 2.8 cases/day; team avg is 1.4. Hiring or reassigning could unlock $4M/mo.',
  'dashboard.role.admin.astor.action.seePlan': 'See plan',
  'dashboard.role.admin.astor.action.openReport': 'Open bottleneck report',

  // KPI tiles
  'dashboard.role.admin.kpi.fundedMtd.label': 'Funded MTD',
  'dashboard.role.admin.kpi.fundedMtd.sub': 'of $10M · 84%',
  'dashboard.role.admin.kpi.activePipeline.label': 'Active pipeline',
  'dashboard.role.admin.kpi.activePipeline.sub': '74 loans',
  'dashboard.role.admin.kpi.timeToClose.label': 'Avg time to close',
  'dashboard.role.admin.kpi.timeToClose.sub': '-3d vs. 30d goal',
  'dashboard.role.admin.kpi.delinquency.label': 'Delinquency 30+',
  'dashboard.role.admin.kpi.delinquency.sub': 'goal ≤ 2%',
  'dashboard.role.admin.kpi.netCommission.label': 'Net commission MTD',
  'dashboard.role.admin.kpi.netCommission.sub': 'earned > paid',
  'dashboard.role.admin.kpi.uwCapacity.label': 'UW capacity used',
  'dashboard.role.admin.kpi.uwCapacity.sub': 'bottleneck warning',

  // Team throughput panel
  'dashboard.role.admin.panel.teamThroughput.title': 'Team throughput · this week',
  'dashboard.role.admin.team.rachel.name': 'Rachel Vasquez',
  'dashboard.role.admin.team.rachel.role': 'Senior UW',
  'dashboard.role.admin.team.dev.name': 'Dev Martinez',
  'dashboard.role.admin.team.dev.role': 'UW',
  'dashboard.role.admin.team.sara.name': 'Sara Klein',
  'dashboard.role.admin.team.sara.role': 'Loan Officer',
  'dashboard.role.admin.team.anna.name': 'Anna Park',
  'dashboard.role.admin.team.anna.role': 'Processor',
  'dashboard.role.admin.team.carlos.name': 'Carlos Diaz',
  'dashboard.role.admin.team.carlos.role': 'Closing Coord.',
  'dashboard.role.admin.team.casesCount': '{{count}} cases',
  'dashboard.role.admin.team.note.overCapacity': 'over capacity',
  'dashboard.role.admin.team.note.hasRoom': 'has room',

  // Top partners panel
  'dashboard.role.admin.panel.topPartners.title': 'Top partners · MTD net',
  'dashboard.role.admin.partners.riverstone.name': 'Riverstone Capital',
  'dashboard.role.admin.partners.riverstone.type': 'White-label · bidirectional',
  'dashboard.role.admin.partners.premier.name': 'Premier Realty Partners',
  'dashboard.role.admin.partners.premier.type': 'White-label',
  'dashboard.role.admin.partners.gulf.name': 'Gulf Coast Lending',
  'dashboard.role.admin.partners.gulf.type': 'Wholesale',
  'dashboard.role.admin.partners.sunshine.name': 'Sunshine Mortgage',
  'dashboard.role.admin.partners.sunshine.type': 'Referral',
  'dashboard.role.admin.partners.earnedPaid': '↗ {{earned}} · ↙ {{paid}}',

  // Scorecard panel
  'dashboard.role.admin.panel.scorecard.title': 'Scorecard · last 8 weeks',
  'dashboard.role.admin.panel.scorecard.openReport': 'Open report →',
  'dashboard.role.admin.panel.scorecard.weeklyHeader': 'Weekly funded loans',
  'dashboard.role.admin.panel.scorecard.date.mar23': 'Mar 23',
  'dashboard.role.admin.panel.scorecard.date.apr13': 'Apr 13',
  'dashboard.role.admin.panel.scorecard.date.may4': 'May 4',
  'dashboard.role.admin.panel.scorecard.date.may11': 'May 11',
  'dashboard.role.admin.panel.scorecard.summaryPrefix': 'Hit your',
  'dashboard.role.admin.panel.scorecard.goalHighlight': '5/wk goal',
  'dashboard.role.admin.panel.scorecard.summarySuffix':
    "1 of 8 weeks. Astor's read: it's not intake (averaging 25/wk vs. 20 goal) — it's UW throughput.",

  // What needs you panel
  'dashboard.role.admin.panel.needsYou.title': 'What needs you · this week',
  'dashboard.role.admin.delegation.exception.title': 'Approve 1 exception over policy',
  'dashboard.role.admin.delegation.exception.sub': 'EXC-128 Marlin Ct · over your delegation limit',
  'dashboard.role.admin.delegation.invites.title': 'Approve 2 team-member invitations',
  'dashboard.role.admin.delegation.invites.sub': 'Pending 5 days',
  'dashboard.role.admin.delegation.warehouse.title': 'Sign Q3 warehouse renewal',
  'dashboard.role.admin.delegation.warehouse.sub': 'Riverstone Bridge IV · expires Jun 30',
  'dashboard.role.admin.delegation.autonomy.title': 'Review Astor autonomy upgrade',
  'dashboard.role.admin.delegation.autonomy.sub': 'memo.drafter ready to graduate L2 → L3',
  'dashboard.role.admin.delegation.cta.review': 'Review',
  'dashboard.role.admin.delegation.cta.approve': 'Approve',
  'dashboard.role.admin.delegation.cta.open': 'Open',
  'dashboard.role.admin.delegation.cta.decide': 'Decide',

  // ============================================================================
  // HOME — UNDERWRITER VARIANT
  // ============================================================================
  // Role-aware home for UNDERWRITER / SENIOR_UNDERWRITER users.
  'dashboard.role.underwriter.badge.role': 'Senior Underwriter',
  'dashboard.role.underwriter.context.activeCases': '{{count}} active cases',
  'dashboard.role.underwriter.context.pastSla': '{{count}} past SLA',

  // Astor headline / body / actions
  'dashboard.role.underwriter.astor.headline': 'EXC-142 is 11h past SLA — clear it first.',
  'dashboard.role.underwriter.astor.confidenceLabel': 'Astor confidence',
  'dashboard.role.underwriter.astor.body':
    'Vermont Pl is on the Riverstone hot list and risking attrition. Memo is ready, comps are tight, and the policy exception is well-supported. The other two cases in your queue (Castro St, Marlin Ct) can wait until after.',
  'dashboard.role.underwriter.astor.action.seeMemo': 'See memo',
  'dashboard.role.underwriter.astor.action.approve': 'Approve at $725k',

  // KPI tiles
  'dashboard.role.underwriter.kpi.activeCases.label': 'My active cases',
  'dashboard.role.underwriter.kpi.activeCases.sub': '3 in UW, 4 in Closing',
  'dashboard.role.underwriter.kpi.medianDecision.label': 'Median decision time',
  'dashboard.role.underwriter.kpi.medianDecision.sub': '-22% vs. team avg',
  'dashboard.role.underwriter.kpi.astorAgreement.label': 'Astor agreement · 30d',
  'dashboard.role.underwriter.kpi.astorAgreement.sub': 'when you accept',
  'dashboard.role.underwriter.kpi.exceptions.label': 'Exceptions opened',
  'dashboard.role.underwriter.kpi.exceptions.sub': '2 approved · 1 pending',
  'dashboard.role.underwriter.kpi.pastSla.label': 'Past SLA',
  'dashboard.role.underwriter.kpi.pastSla.sub': 'EXC-142 · -11h',

  // My cases panel
  'dashboard.role.underwriter.panel.myCases.title': 'My cases · ordered by urgency',
  'dashboard.role.underwriter.cases.vermont.name': '4218 Vermont Pl',
  'dashboard.role.underwriter.cases.vermont.astor': 'Approve at $725k',
  'dashboard.role.underwriter.cases.castro.name': 'Castro St',
  'dashboard.role.underwriter.cases.castro.astor': 'Counter at $385k',
  'dashboard.role.underwriter.cases.marlin.name': 'Marlin Ct',
  'dashboard.role.underwriter.cases.marlin.astor': 'Waiting on borrower docs',
  'dashboard.role.underwriter.cases.hillside.name': 'Hillside Dr',
  'dashboard.role.underwriter.cases.hillside.astor': 'Send to title — clear to close',
  'dashboard.role.underwriter.cases.stage.uwReview': 'UW Review',
  'dashboard.role.underwriter.cases.stage.creditReview': 'Credit Review',
  'dashboard.role.underwriter.cases.stage.appraisal': 'Appraisal / BPO',
  'dashboard.role.underwriter.cases.stage.docPrep': 'Doc Prep',
  'dashboard.role.underwriter.cases.sla.minus11h': '-11h',
  'dashboard.role.underwriter.cases.sla.4h': '4h',
  'dashboard.role.underwriter.cases.sla.paused': 'paused',
  'dashboard.role.underwriter.cases.sla.2d': '2d',
  'dashboard.role.underwriter.cases.slaPrefix': 'SLA {{value}}',

  // Your week panel
  'dashboard.role.underwriter.panel.yourWeek.title': 'Your week',
  'dashboard.role.underwriter.weekStat.decisions': 'Decisions',
  'dashboard.role.underwriter.weekStat.approve': 'Approve',
  'dashboard.role.underwriter.weekStat.counter': 'Counter',
  'dashboard.role.underwriter.weekStat.decline': 'Decline',
  'dashboard.role.underwriter.weekStat.summaryPrefix': "You're decisioning",
  'dashboard.role.underwriter.weekStat.summaryHighlight': '2.8 cases / day',
  'dashboard.role.underwriter.weekStat.summarySuffix': 'on average — top quartile on the team.',

  // ============================================================================
  // HOME — LOAN OFFICER VARIANT
  // ============================================================================
  // Role-aware home for LOAN_OFFICER users.
  'dashboard.role.loanOfficer.badge.role': 'Loan Officer',
  'dashboard.role.loanOfficer.context.borrowers': '{{count}} borrowers in pipeline',
  'dashboard.role.loanOfficer.context.awaitingReply': '{{count}} awaiting your reply',

  // Astor headline / body / actions
  'dashboard.role.loanOfficer.astor.headline': '3 borrowers are waiting for you — start with {{name}}.',
  'dashboard.role.loanOfficer.astor.confidenceLabel': 'Astor confidence',
  'dashboard.role.loanOfficer.astor.body':
    "Marcus replied 8m ago confirming his call. Diane has questions about your rate. Pat hasn't heard back in 5 days and is going cold — Astor can draft a re-engagement message.",
  'dashboard.role.loanOfficer.astor.action.seeAll': 'See all 3',
  'dashboard.role.loanOfficer.astor.action.reply': 'Reply to {{name}}',

  // KPI tiles
  'dashboard.role.loanOfficer.kpi.pipelineValue.label': 'My pipeline · value',
  'dashboard.role.loanOfficer.kpi.pipelineValue.sub': '14 deals · 3 stages',
  'dashboard.role.loanOfficer.kpi.convRate.label': 'Conv. rate · 30d',
  'dashboard.role.loanOfficer.kpi.convRate.sub': '+8pp · top quartile',
  'dashboard.role.loanOfficer.kpi.responseTime.label': 'Avg response · me',
  'dashboard.role.loanOfficer.kpi.responseTime.sub': 'goal ≤ 1h',
  'dashboard.role.loanOfficer.kpi.commissions.label': 'Commissions MTD',
  'dashboard.role.loanOfficer.kpi.commissions.sub': 'paid + pending',
  'dashboard.role.loanOfficer.kpi.goingCold.label': 'Going cold',
  'dashboard.role.loanOfficer.kpi.goingCold.sub': 'no reply 5+ days',

  // Awaiting reply panel
  'dashboard.role.loanOfficer.panel.awaitingReply.title': 'Awaiting your reply',
  'dashboard.role.loanOfficer.replies.hotLabel': 'hot',
  'dashboard.role.loanOfficer.replies.marcus.name': 'Marcus Reyes',
  'dashboard.role.loanOfficer.replies.marcus.deal': '4218 Vermont Pl · $725k',
  'dashboard.role.loanOfficer.replies.marcus.quote': '"Can do Tuesday 10am PT. Sending updated rehab…"',
  'dashboard.role.loanOfficer.replies.marcus.age': '8m',
  'dashboard.role.loanOfficer.replies.diane.name': 'Diane Ruiz',
  'dashboard.role.loanOfficer.replies.diane.deal': 'Hillside Dr · $1.85M',
  'dashboard.role.loanOfficer.replies.diane.quote': 'Questions about rate-lock policy',
  'dashboard.role.loanOfficer.replies.diane.age': '1h',
  'dashboard.role.loanOfficer.replies.james.name': 'James Holt',
  'dashboard.role.loanOfficer.replies.james.deal': 'Bridge intake',
  'dashboard.role.loanOfficer.replies.james.quote': 'Asked for term sheet by Friday',
  'dashboard.role.loanOfficer.replies.james.age': '3h',

  // Pipeline by stage panel
  'dashboard.role.loanOfficer.panel.pipelineByStage.title': 'My pipeline · by stage',
  'dashboard.role.loanOfficer.stages.intake': 'Intake',
  'dashboard.role.loanOfficer.stages.termSheet': 'Term Sheet',
  'dashboard.role.loanOfficer.stages.underwriting': 'Underwriting',
  'dashboard.role.loanOfficer.stages.closing': 'Closing',

  // =====================================================================
  // Borrower Portal Redesign (Fiops Borrower Portal — Astor-first IA)
  // Added for the design pass landing the consumer portal redesign.
  // =====================================================================

  // Borrower portal — Home
  'consumer.home.greeting.date': '{{date}}',
  'consumer.home.greeting.title': 'Good morning, {{firstName}}',
  'consumer.home.greeting.activeLoans': '{{n}} active loans',
  'consumer.home.greeting.totalExposure': '{{amount}} total exposure',
  'consumer.home.greeting.nextClose': 'Next close in {{days}} days',
  'consumer.home.actions.uploadDocs': 'Upload documents',
  'consumer.home.actions.newLoan': 'New loan',
  'consumer.home.briefing.label': 'Astor briefing',
  'consumer.home.briefing.timestamp': 'Updated {{when}}',
  'consumer.home.briefing.refresh': 'Refresh',
  'consumer.home.briefing.openAstor': 'Open Astor',
  'consumer.home.briefing.showOvernight': 'Show overnight updates',
  'consumer.home.briefing.hideOvernight': 'Hide overnight updates',
  'consumer.home.kpi.activeLoans': 'Active loans',
  'consumer.home.kpi.activeLoansSubtitle': '{{n}} need action',
  'consumer.home.kpi.totalExposure': 'Total exposure',
  'consumer.home.kpi.totalExposureSub': 'across {{n}} entities',
  'consumer.home.kpi.nextClose': 'Next close',
  'consumer.home.kpi.nextCloseSub': '{{days}} days',
  'consumer.home.kpi.offersAwaiting': 'Offers awaiting',
  'consumer.home.kpi.offersAwaitingSub': '{{n}} pending response',
  'consumer.home.loans.title': 'Active loans',
  'consumer.home.loans.astorRanked': 'Auto-ranked by Astor',
  'consumer.home.loans.viewAll': 'View all',
  'consumer.home.loanRow.nextLabel': 'Next: {{label}} · due {{due}} · {{who}}',
  'consumer.home.insights.title': 'Portfolio insights',
  'consumer.home.insights.viewAll': 'View all',
  'consumer.home.insights.refiEyebrow': 'Refi opportunity',
  'consumer.home.insights.refiAction': 'See refi options',
  'consumer.home.insights.expiringEyebrow': 'Expiring soon',
  'consumer.home.insights.renewAction': 'Start renewal',
  'consumer.home.insights.closingEyebrow': 'Closing checklist',
  'consumer.home.insights.checklistAction': 'Open checklist',
  'consumer.home.tasks.title': 'Priority tasks',
  'consumer.home.tasks.viewAll': 'View all',
  'consumer.home.threads.title': 'Recent threads',
  'consumer.home.threads.open': 'Open',
  'consumer.home.threads.tools': '{{n}} tools',
  'consumer.home.threads.citations': '{{n}} citations',

  // Borrower portal — Astor (full page + side panel)
  'consumer.astor.sidebar.title': 'Astor',
  'consumer.astor.sidebar.liveIndicator': 'live',
  'consumer.astor.sidebar.newConversation': 'New conversation',
  'consumer.astor.sidebar.searchPlaceholder': 'Search threads',
  'consumer.astor.sidebar.recentHeader': 'Recent',
  'consumer.astor.thread.tools': '{{n}} tools · {{citations}} citations',
  'consumer.astor.header.export': 'Export',
  'consumer.astor.trace.label': 'Reasoning trace',
  'consumer.astor.trace.showAll': 'Show all',
  'consumer.astor.sources.title': 'Sources',
  'consumer.astor.reply.regenerate': 'Regenerate',
  'consumer.astor.composer.placeholder': 'Ask Astor anything about your portfolio…',
  'consumer.astor.composer.send': 'Send',
  'consumer.astorPanel.title': 'Astor',
  'consumer.astorPanel.actions.openFull': 'Open full page',
  'consumer.astorPanel.actions.close': 'Close',
  'consumer.astorPanel.tabs.ask': 'Ask',
  'consumer.astorPanel.tabs.briefing': 'Briefing',
  'consumer.astorPanel.tabs.threads': 'Threads',
  'consumer.astorPanel.greeting': 'Hi {{firstName}}, how can I help?',
  'consumer.astorPanel.promptHint': 'I know your portfolio. Try one of these:',
  'consumer.astorPanel.composerPlaceholder': 'Ask Astor…',
  'consumer.astorPanel.disclaimer': 'Astor can make mistakes.',
  'consumer.astorPanel.disclaimerLink': 'Learn more',
  'consumer.astorPanel.briefing.snapshotHeading': "Today's snapshot",
  'consumer.astorPanel.briefing.activeLoans': 'Active loans',
  'consumer.astorPanel.briefing.pendingActions': 'Pending actions',
  'consumer.astorPanel.briefing.astorReadHeading': "Astor's read",
  'consumer.astorPanel.briefing.confidenceChip': 'conf {{pct}}%',
  'consumer.astorPanel.briefing.readTimeChip': 'read · {{seconds}}s',
  'consumer.astorPanel.briefing.latestUpdatesHeading': 'Latest updates',
  'consumer.astorPanel.threads.heading': 'Pick up where you left off',

  // Borrower portal — Loans (list + detail + new wizard)
  'consumer.loans.title': 'Loans',
  'consumer.loans.subtitle': 'All your loans — active, in flight, and closed',
  'consumer.loans.actions.filter': 'Filter',
  'consumer.loans.actions.newLoan': 'New loan',
  'consumer.loans.kpi.inProgress': 'In progress',
  'consumer.loans.kpi.underReview': 'Under review',
  'consumer.loans.kpi.offersClosing': 'Offers / closing',
  'consumer.loans.kpi.fundedActive': 'Funded / active',
  'consumer.loans.kpi.totalExposure': 'Total exposure',
  'consumer.loans.filter.all': 'All',
  'consumer.loans.filter.inProgress': 'In progress',
  'consumer.loans.filter.underReview': 'Under review',
  'consumer.loans.filter.approved': 'Approved',
  'consumer.loans.filter.closed': 'Closed',
  'consumer.loans.search.placeholder': 'Search loans by number, property, or lender',
  'consumer.loans.astorSorted': 'Astor sorted',
  'consumer.loans.sort.newestFirst': 'Newest first',
  'consumer.loans.col.id': 'Application #',
  'consumer.loans.col.type': 'Type',
  'consumer.loans.col.amount': 'Amount',
  'consumer.loans.col.status': 'Status',
  'consumer.loans.col.submitted': 'Submitted',
  'consumer.loans.col.astorNextStep': 'Astor next-step',
  'consumer.loans.col.lastUpdated': 'Last updated',
  'consumer.loans.stage.inProgress': 'In progress',
  'consumer.loanDetail.title': 'Document Request',
  // W2: "New" mint chip beside the priority chip on early-stage loans
  'consumer.loanDetail.badge.new': 'New',
  'consumer.loanDetail.subtitle': 'Loan number: {{id}}',
  'consumer.loanDetail.breadcrumb.home': 'Home',
  'consumer.loanDetail.breadcrumb.loans': 'Loans',
  'consumer.loanDetail.actions.documents': 'Documents',
  'consumer.loanDetail.actions.messages': 'Messages',
  'consumer.loanDetail.actions.withdraw': 'Withdraw',
  'consumer.loanDetail.actions.review': 'Review',
  'consumer.loanDetail.astorBlock.title': "I've pre-staged 4 documents for this request",
  'consumer.loanDetail.astorBlock.body': 'Review and send when you are ready, or see what changed.',
  'consumer.loanDetail.astorBlock.reviewSend': 'Review & send',
  'consumer.loanDetail.astorBlock.seeChanges': 'See changes',
  'consumer.loanDetail.astorSuggestions': 'Astor has {{n}} suggestions',
  'consumer.loanDetail.stepper.heading': 'Application status',
  'consumer.loanDetail.stepper.inProgress': 'In progress',
  'consumer.loanDetail.stepper.applied': 'Applied',
  'consumer.loanDetail.stepper.underReview': 'Under review',
  'consumer.loanDetail.stepper.offerReceived': 'Offer received',
  'consumer.loanDetail.stepper.offerAccepted': 'Offer accepted',
  'consumer.loanDetail.stepper.closing': 'Closing',
  'consumer.loanDetail.stepper.funded': 'Funded',
  'consumer.loanDetail.stepper.active': 'Active',
  'consumer.loanDetail.tabs.application': 'Application',
  'consumer.loanDetail.tabs.property': 'My property',
  'consumer.loanDetail.tabs.dataroom': 'Data room',
  'consumer.loanDetail.tabs.activity': 'Activity',
  'consumer.loanDetail.tabs.messages': 'Messages',
  'consumer.loanDetail.tabs.parties': 'Parties',
  'consumer.loanDetail.application.detailsTitle': 'Loan details',
  'consumer.loanDetail.application.submissionTitle': 'Your submission',
  'consumer.loanDetail.application.submissionSubtitle': 'Astor pre-filled {{filled}} of {{total}} fields',
  'consumer.loanDetail.application.astorPrefilled': 'Astor pre-filled',
  'consumer.loanDetail.application.field.amount': 'Loan amount',
  'consumer.loanDetail.application.field.type': 'Loan type',
  'consumer.loanDetail.application.field.term': 'Term',
  'consumer.loanDetail.application.field.purpose': 'Purpose',
  'consumer.loanDetail.application.field.property': 'Property',
  'consumer.loanDetail.application.field.entity': 'Borrowing entity',
  'consumer.loanDetail.property.profileTitle': 'Property profile',
  'consumer.loanDetail.property.marketTitle': 'Market intel',
  'consumer.loanDetail.property.astorReadLabel': "Astor's read",
  'consumer.loanDetail.property.field.address': 'Address',
  'consumer.loanDetail.property.field.type': 'Type',
  'consumer.loanDetail.property.field.units': 'Units',
  'consumer.loanDetail.property.field.sqft': 'Square feet',
  'consumer.loanDetail.property.field.yearBuilt': 'Year built',
  'consumer.loanDetail.property.field.appraisedValue': 'Appraised value',
  'consumer.loanDetail.property.field.purchasePrice': 'Purchase price',
  'consumer.loanDetail.property.stat.medianRent': 'Median rent',
  'consumer.loanDetail.property.stat.daysOnMarket': 'Days on market',
  'consumer.loanDetail.property.stat.priceTrend': 'Price trend',
  'consumer.loanDetail.property.stat.rentTrend': 'Rent trend',
  'consumer.loanDetail.property.stat.compsCount': 'Comparable sales',
  'consumer.loanDetail.dataRoom.linkedTitle': 'Linked documents',
  'consumer.loanDetail.dataRoom.upload': 'Upload',
  'consumer.loanDetail.dataRoom.predictedTitle': 'Astor predicted next requests',
  'consumer.loanDetail.dataRoom.predictedSubtitle': 'Pre-stage these now to keep the loan moving',
  'consumer.loanDetail.dataRoom.confidence': 'conf {{pct}}%',
  'consumer.loanDetail.dataRoom.preStage': 'Pre-stage',
  'consumer.loanDetail.activity.title': 'Activity',
  'consumer.loanDetail.activity.todayEvening': 'Today · Evening',
  'consumer.loanDetail.activity.todayAfternoon': 'Today · Afternoon',
  'consumer.loanDetail.activity.astorPrestaged': 'Astor pre-staged {{n}} documents',
  'consumer.loanDetail.messages.title': 'Messages',
  'consumer.loanDetail.messages.draftLabel': 'Draft',
  'consumer.loanDetail.messages.lenderLabel': '{{lender}}',
  'consumer.loanDetail.messages.composePlaceholder': 'Write a message…',
  'consumer.loanDetail.messages.sendAsIs': 'Send as-is',
  'consumer.loanDetail.messages.edit': 'Edit',
  'consumer.loanDetail.messages.tryAnotherTone': 'Try another tone',
  'consumer.loanDetail.parties.title': 'Parties',
  'consumer.loanDetail.parties.borrower': 'Borrower',
  'consumer.loanDetail.parties.coBorrower': 'Co-borrower',
  'consumer.loanDetail.parties.loanOfficer': 'Loan officer',
  'consumer.loanDetail.parties.processor': 'Processor',
  'consumer.loanDetail.parties.title.title': 'Title',
  'consumer.loanDetail.parties.insurance': 'Insurance',
  'consumer.newLoan.title': 'New loan',
  'consumer.newLoan.subtitle': 'Astor pre-fills what it knows — you confirm and add the rest',
  'consumer.newLoan.step.loanBasics': 'Loan basics',
  'consumer.newLoan.step.property': 'Property',
  'consumer.newLoan.step.entity': 'Entity',
  'consumer.newLoan.step.financials': 'Financials',
  'consumer.newLoan.step.trackRecord': 'Track record',
  'consumer.newLoan.step.review': 'Review',
  'consumer.newLoan.step.entityTitle': 'Borrowing entity',
  'consumer.newLoan.astor.prefillBanner':
    'I pre-filled **{{filled}} of {{total}} fields** from your **last {{loans}} loans** and **{{docs}} uploaded documents**',
  'consumer.newLoan.astor.prefillSub': 'Confirm what is right and fix what is not — Astor will learn from your edits.',
  'consumer.newLoan.astor.confidence': 'conf {{pct}}%',
  'consumer.newLoan.astor.seeSources': 'See sources',
  'consumer.newLoan.astor.sourceEyebrow': 'What Astor used to pre-fill this',
  'consumer.newLoan.astor.fieldChip': 'Astor',
  'consumer.newLoan.stepOf': 'Step {{current}} of {{total}}',
  'consumer.newLoan.field.entityName': 'Entity name',
  'consumer.newLoan.field.entityType': 'Entity type',
  'consumer.newLoan.field.stateOfFormation': 'State of formation',
  'consumer.newLoan.field.ein': 'EIN',
  'consumer.newLoan.field.formedOn': 'Formed on',
  'consumer.newLoan.field.owners': 'Owners',
  'consumer.newLoan.field.operatingAgreement': 'Operating agreement',
  'consumer.newLoan.field.authorizedSigner': 'Authorized signer',
  'consumer.newLoan.field.selectPlaceholder': 'Select…',
  'consumer.newLoan.field.needsYou': 'Needs you',
  'consumer.newLoan.actions.back': 'Back',
  'consumer.newLoan.actions.saveDraft': 'Save draft',
  'consumer.newLoan.actions.continue': 'Continue',

  // Borrower portal — Offers
  'consumer.offers.pageTitle': 'Offers',
  'consumer.offers.pageSubtitle': 'Term sheets and offers from lenders — decide and counter',
  'consumer.offers.kpi.totalOffers': 'Total offers',
  'consumer.offers.kpi.pendingReview': 'Pending review',
  'consumer.offers.kpi.pendingSub': 'Awaiting your response',
  'consumer.offers.kpi.bestSavings': 'Best monthly savings',
  'consumer.offers.kpi.bestSavingsSub': '{{winner}} vs. current',
  'consumer.offers.kpi.decideBy': 'Decide by',
  'consumer.offers.kpi.decideByDate': '{{date}} · {{daysLeft}} days',
  'consumer.offers.viewCompare': 'Compare',
  'consumer.offers.viewList': 'List',
  'consumer.offers.astorBanner.eyebrow': 'Astor · explains in plain English',
  'consumer.offers.astorBanner.confidence': 'conf {{pct}}%',
  'consumer.offers.astorBanner.tools': '{{tools}} tools · {{citations}} citations',
  'consumer.offers.astorBanner.reasoning': 'Show reasoning trace',
  'consumer.offers.astorBanner.body':
    '{{winnerName}} saves you {{savings}}/month with a break-even at month {{breakEven}}. {{recommendation}}',
  'consumer.offers.compare.title': 'Side-by-side',
  'consumer.offers.compare.lender': 'Lender',
  'consumer.offers.compare.amount': 'Loan amount',
  'consumer.offers.compare.rate': 'Rate',
  'consumer.offers.compare.term': 'Term',
  'consumer.offers.compare.monthly': 'Monthly payment',
  'consumer.offers.compare.sectionPros': 'Pros & Cons',
  'consumer.offers.compare.highlights': 'Highlights',
  'consumer.offers.compare.cautions': 'Cautions',
  'consumer.offers.compare.sectionCosts': 'Costs',
  'consumer.offers.compare.origination': 'Origination',
  'consumer.offers.compare.totalFees': 'Total fees',
  'consumer.offers.compare.sectionFit': 'Fit',
  'consumer.offers.compare.dscr': 'DSCR',
  'consumer.offers.compare.ltv': 'LTV',
  'consumer.offers.compare.deadline': 'Deadline',
  'consumer.offers.compare.emptyState': 'Compare view shows when you have 2 or more active offers.',
  'consumer.offers.astorPick': 'Astor pick',
  'consumer.offers.accept': 'Accept',
  'consumer.offers.acceptNamed': 'Accept {{lender}}',
  'consumer.offers.counter': 'Counter',
  'consumer.offers.askAstor': 'Ask',
  'consumer.offers.decideby': 'Decide by',
  'consumer.offers.fixed': 'fixed',
  'consumer.offers.perMonth': '/mo',
  'consumer.offers.years': 'years',
  'consumer.offers.list.title': 'All offers',
  'consumer.offers.confirmAccept.title': 'Confirm offer acceptance',
  'consumer.offers.confirmAccept.body':
    'You are accepting the offer from {{lender}}. This will notify the lender and move your loan to the next stage.',
  'consumer.offers.confirmAccept.cta': 'Accept this offer',
  'consumer.offers.astorExplain.title': "Astor's read",
  'consumer.offers.astorExplain.reasoning': 'Show reasoning trace',
  'consumer.offers.breakEven.title': 'Break-even',
  'consumer.offers.breakEven.atMonth': 'Month {{n}}',
  'consumer.offers.noOffers': 'No offers yet',
  'consumer.offers.noOffersDescription': 'Offers from lenders will appear here once your loan reaches the offer stage.',

  // Borrower portal — Sign queue + Documents
  'consumer.signQueue.pageTitle': 'Sign queue',
  'consumer.signQueue.pageSubtitle': 'Documents awaiting your signature — with plain-English summaries',
  'consumer.signQueue.urgentStripDeadline': 'Deadline {{date}}',
  'consumer.signQueue.urgentStripDetail': '{{count}} urgent documents tied to {{loan}}',
  // Urgent banner: loan-context line beneath the deadline (W7 design polish)
  'consumer.signQueue.urgentStripLoanContext': '{{loan}} · {{count}} documents to sign',
  'consumer.signQueue.urgentStripDocCount': '{{count}} documents to sign',
  'consumer.signQueue.signAllNow': 'Sign all now',
  'consumer.signQueue.tabPending': 'Pending',
  'consumer.signQueue.tabSigned': 'Signed',
  'consumer.signQueue.chipUrgent': 'Urgent',
  'consumer.signQueue.actionReview': 'Review',
  'consumer.signQueue.actionSign': 'Sign',
  'consumer.signQueue.actionDownload': 'Download',
  'consumer.signQueue.rowSentBy': 'Sent by {{sender}} · {{when}}',
  'consumer.signQueue.rowSignedAt': 'Signed {{when}}',
  'consumer.signQueue.rowPages': '{{count}} pages',
  'consumer.signQueue.emptyPendingTitle': 'Nothing to sign right now',
  'consumer.signQueue.emptyPendingSubtitle': 'New requests will land here the moment a lender sends them.',
  'consumer.signQueue.emptySignedTitle': 'No signed documents yet',
  'consumer.signQueue.astorWalkTitle': "I'll walk you through the Note before you sign",
  'consumer.signQueue.astorWalkBody':
    'Astor reads each document, flags risky clauses, and answers questions in plain English — so you sign with confidence.',
  'consumer.signQueue.astorWalkCta': 'Walk me through it',
  'consumer.documents.pageTitle': 'Documents',
  'consumer.documents.pageSubtitle': 'Upload documents anytime — Astor links them to the right loan automatically',
  'consumer.documents.uploadButton': 'Upload',
  'consumer.documents.astorSmartLinkTitle': '{{count}} unlinked documents — I can link them to the right loan',
  'consumer.documents.astorSmartLinkDetail':
    'Astor matches each document to a loan using filename, content, and parties.',
  'consumer.documents.astorSmartLinkCta': 'Review & link',
  'consumer.documents.dropzoneTitle': 'Drop files anywhere or click to browse',
  'consumer.documents.dropzoneSubtitle': 'PDF, XLSX, DOCX, JPG, PNG up to 50 MB',
  'consumer.documents.sectionTitle': 'All documents',
  'consumer.documents.filterAll': 'All',
  'consumer.documents.filterVerified': 'Verified',
  'consumer.documents.filterPending': 'Pending',
  'consumer.documents.filterExpired': 'Expired',
  'consumer.documents.searchPlaceholder': 'Search by name',
  'consumer.documents.statusVerified': 'verified',
  'consumer.documents.statusPendingReview': 'pending review',
  'consumer.documents.statusExpired': 'expired',
  'consumer.documents.astorQaTitle': 'Chat with your documents',
  'consumer.documents.astorQaSubtitle': "Ask Astor anything about any document you've uploaded — citations included",
  'consumer.documents.astorQaCta': 'Try it',
  'consumer.documents.astorQaPrompt1': 'Summarize my latest appraisal',
  'consumer.documents.astorQaPrompt2': 'What is the prepayment penalty in my Note?',
  'consumer.documents.astorQaPrompt3': 'Compare my last two rent rolls',
  'consumer.documents.astorQaPrompt4': 'When does my insurance expire?',

  // Borrower portal — Reference (Entities + Guarantees + Watchlist)
  'consumer.entities.pageTitle': 'Entities',
  'consumer.entities.pageSubtitle': 'Borrowing entities tied to your loans',
  'consumer.entities.addEntity': 'Add entity',
  'consumer.entities.sectionTitle': 'All entities',
  'consumer.entities.kpiActiveEntities': 'Active entities',
  'consumer.entities.kpiActiveLoans': 'Active loans',
  'consumer.entities.kpiStates': 'States',
  'consumer.entities.kpiNeedAttention': 'Need attention',
  'consumer.entities.kpiNeedAttentionSub': 'EIN doc missing for {{count}}',
  'consumer.entities.colLegalName': 'Legal name',
  'consumer.entities.colType': 'Type',
  'consumer.entities.colEinState': 'EIN · State',
  'consumer.entities.colOwners': 'Owners',
  'consumer.entities.colActiveLoans': 'Active loans',
  'consumer.entities.rowFormedSub': 'Formed {{state}} · since {{year}}',
  'consumer.entities.loanCount': '{{count}} loans',
  'consumer.guarantees.pageTitle': 'Guarantees',
  'consumer.guarantees.pageSubtitle': 'Personal and limited guarantees across your portfolio',
  'consumer.guarantees.kpiTotalExposure': 'Total exposure',
  'consumer.guarantees.kpiActiveGuarantees': 'Active guarantees',
  'consumer.guarantees.kpiActiveGuaranteesSub': 'across your portfolio',
  'consumer.guarantees.kpiActiveGuaranteesMeta': 'Personal + limited',
  'consumer.guarantees.kpiExpiring': 'Expiring (12mo)',
  'consumer.guarantees.kpiNoExpirations': 'No upcoming expirations',
  'consumer.guarantees.tabActive': 'Active obligations',
  'consumer.guarantees.tabHistory': 'History',
  'consumer.guarantees.colBorrower': 'Borrower',
  'consumer.guarantees.colLoan': 'Loan #',
  'consumer.guarantees.colAmount': 'Amount',
  'consumer.guarantees.colType': 'Type',
  'consumer.guarantees.colExpiry': 'Expiry',
  'consumer.guarantees.emptyHistoryTitle': 'No guarantee history',
  'consumer.guarantees.emptyHistorySub': 'Released guarantees will appear here.',
  'consumer.guarantees.emptyActiveTitle': 'No guarantee obligations',
  'consumer.guarantees.emptyActiveSub': 'You do not currently guarantee any loans.',
  'consumer.watchlist.pageTitle': 'Watchlist',
  'consumer.watchlist.pageSubtitle': 'Loans, rates, and signals you want to keep close',
  'consumer.watchlist.addToWatchlist': 'Add to watchlist',
  'consumer.watchlist.sectionPinned': 'Pinned',
  'consumer.watchlist.watchingSince': 'Watching since {{date}}',
  'consumer.watchlist.astorSuggestionsTitle': 'Things you might want to watch',
  'consumer.watchlist.astorSuggestionsSubtitle': 'Based on your portfolio and activity',
  'consumer.watchlist.suggestionAdd': '+ Add',
  // R5: three distinct Astor-flavoured suggestion tiles (portfolio-specific
  // reasons in the subtitle) until astor.watchlist.suggestions endpoint lands
  'consumer.watchlist.suggestion.rateIndex.title': 'Rate index alert',
  'consumer.watchlist.suggestion.rateIndex.sub': '10Y treasury moved 25bps — your refi window may have opened',
  'consumer.watchlist.suggestion.lenderCounter.title': 'Lender counter-offer watch',
  'consumer.watchlist.suggestion.lenderCounter.sub':
    'Westside Capital DSCR loans now pricing 8.0% — your last offer was 8.5%',
  'consumer.watchlist.suggestion.compsTracker.title': 'Comps tracker',
  'consumer.watchlist.suggestion.compsTracker.sub':
    '3 new sales near your property in the last 30 days — your ARV may have changed',

  // Borrower portal — Profile
  'consumer.profile.pageTitle': 'Profile',
  'consumer.profile.pageSubtitle': 'Your account, financial profile, and notification preferences',
  'consumer.profile.tabs.profile': 'Profile',
  'consumer.profile.tabs.financial': 'Financial profile',
  'consumer.profile.tabs.notifications': 'Notifications',
  'consumer.profile.tabs.astor': 'Astor preferences',
  'consumer.profile.verifiedChip': 'Verified',
  // Detailed verified chip — surfaces WHAT was verified, not just the binary
  'consumer.profile.verifiedChipDetail': 'Verified · ID + Address',
  'consumer.profile.fields.fullName': 'Full name',
  'consumer.profile.fields.email': 'Email',
  'consumer.profile.fields.phone': 'Phone',
  'consumer.profile.actions.editProfile': 'Edit profile',
  'consumer.profile.actions.changePassword': 'Change password',
  'consumer.profile.actions.signOutEverywhere': 'Sign out everywhere',
  'consumer.profile.account.heading': 'Account',
  'consumer.profile.account.twoFactorAuth': 'Two-factor auth',
  'consumer.profile.account.twoFactorValue': 'On · Authenticator',
  'consumer.profile.account.loginSessions': 'Login sessions',
  'consumer.profile.account.loginSessionsValue': '{{count}} active',
  'consumer.profile.account.memberSince': 'Member since',
  'consumer.profile.quickActions.heading': 'Quick actions',
  'consumer.profile.quickActions.downloadData': 'Download account data',
  'consumer.profile.quickActions.connectPlaid': 'Connect Plaid · banks',
  'consumer.profile.quickActions.banksLinked': '{{count}} banks linked',
  'consumer.profile.quickActions.addCoBorrower': 'Add co-borrower',
  'consumer.profile.financial.subTabEmployment': 'Employment',
  'consumer.profile.financial.subTabAssets': 'Assets',
  'consumer.profile.financial.subTabLiabilities': 'Liabilities',
  'consumer.profile.financial.employmentHint': 'Keep this current so lenders can quote accurately.',
  'consumer.profile.financial.employmentStatus': 'Employment status',
  'consumer.profile.financial.monthlyIncome': 'Monthly income',
  'consumer.profile.financial.yearsInRole': 'Years in role',
  'consumer.profile.financial.annualGrossIncome': 'Annual gross income',
  'consumer.profile.financial.summaryHeading': 'Financial summary',
  'consumer.profile.financial.totalAssets': 'Total assets',
  'consumer.profile.financial.totalDebt': 'Total debt',
  'consumer.profile.financial.monthlyPayments': 'Monthly payments',
  'consumer.profile.financial.netWorth': 'Net worth',
  'consumer.profile.financial.netWorthFormula': 'Net worth = total assets − total debt',
  'consumer.profile.notifications.channelsHeading': 'Channels',
  'consumer.profile.notifications.channelEmail': 'Email',
  'consumer.profile.notifications.channelSms': 'SMS',
  'consumer.profile.notifications.channelPush': 'Push',
  'consumer.profile.notifications.channelInApp': 'In-app',
  'consumer.profile.notifications.devicesEnrolled': '{{count}} devices enrolled',
  'consumer.profile.notifications.inAppAlways': 'Always on',
  'consumer.profile.notifications.categoriesHeading': 'Categories',
  'consumer.profile.notifications.categoriesSubtitle': 'Pick what you want to hear about.',
  'consumer.profile.notifications.catApplicationUpdates': 'Application updates',
  'consumer.profile.notifications.catApplicationUpdatesSub': 'Stage changes, approvals, denials',
  'consumer.profile.notifications.catDocRequests': 'Document requests',
  'consumer.profile.notifications.catDocRequestsSub': 'When a lender asks for something new',
  'consumer.profile.notifications.catMessages': 'Messages',
  'consumer.profile.notifications.catMessagesSub': 'New replies from your lender or Astor',
  'consumer.profile.notifications.catProposals': 'Offers',
  'consumer.profile.notifications.catProposalsSub': 'New term sheets and offer changes',
  'consumer.profile.notifications.catDeadlines': 'Deadlines',
  'consumer.profile.notifications.catDeadlinesSub': 'Upcoming signature and document deadlines',
  'consumer.profile.notifications.catAstorInsights': 'Astor insights',
  'consumer.profile.notifications.catAstorInsightsSub': 'Refi opportunities, market shifts, risk signals',
  'consumer.profile.notifications.quietHoursHeading': 'Quiet hours',
  'consumer.profile.notifications.quietHoursStart': 'Start',
  'consumer.profile.notifications.quietHoursEnd': 'End',
  'consumer.profile.notifications.quietHoursDisclaimer':
    'Critical alerts (signature deadlines, account security) will still come through.',
  'consumer.profile.astorPrefs.heading': 'How Astor works for you',
  'consumer.profile.astorPrefs.subtitle': 'Turn assistance up or down for each capability.',
  'consumer.profile.astorPrefs.autoFill': 'Auto-fill',
  'consumer.profile.astorPrefs.autoFillSub': 'Pre-fill loan applications from your prior loans and uploaded docs',
  'consumer.profile.astorPrefs.draftReplies': 'Draft replies',
  'consumer.profile.astorPrefs.draftRepliesSub': 'Suggest replies to lender messages — you review before sending',
  'consumer.profile.astorPrefs.prestage': 'Pre-stage documents',
  'consumer.profile.astorPrefs.prestageSub': 'Pull documents Astor predicts a lender will ask for',
  'consumer.profile.astorPrefs.negotiation': 'Negotiation coaching',
  'consumer.profile.astorPrefs.negotiationSub': 'Suggest counters on term sheets and explain trade-offs',
  'consumer.profile.astorPrefs.refi': 'Surface refi opportunities',
  'consumer.profile.astorPrefs.refiSub': 'Alert you when a refi could save money or improve terms',
  'consumer.profile.astorPrefs.privacyHeading': 'What Astor can see',
  'consumer.profile.astorPrefs.privacyBody':
    'Astor reads your loans, documents, and messages to help you. You control what it remembers and when.',
  'consumer.profile.astorPrefs.viewAuditLog': 'View audit log',
  'consumer.profile.astorPrefs.clearHistory': 'Clear conversation history',

  // =====================================================================
  // Borrower Portal — fill-in keys for the v3 design pass
  // (placeholders, fallbacks, sign-queue error states, missing KPI labels)
  // =====================================================================

  // AstorBriefing placeholders (until tRPC briefing endpoint ships)
  'consumer.home.briefing.placeholderHeadline': '3 things need you before Friday',
  'consumer.home.briefing.placeholderBullet1': 'Upload your 2025 W-2 so your active loan can move to underwriting',
  'consumer.home.briefing.placeholderBullet2': 'Your DSCR refi quote came back at 7.85% — review before end of day',
  'consumer.home.briefing.placeholderBullet3':
    'Astor staged your 2024 entity returns from a prior loan — confirm to reuse',

  // AstorBriefing overnight log (toggle text + 3 mock entries until briefing
  // endpoint emits real overnight items)
  'consumer.home.briefing.showOvernightCount': 'Show overnight ({{count}})',
  'consumer.home.briefing.hideOvernightCount': 'Hide overnight',
  'consumer.home.briefing.overnightEntry1': 'Westside Capital replied on the DSCR refi quote',
  'consumer.home.briefing.overnightEntry2': 'Vermont property tax notice scheduled for Friday',
  'consumer.home.briefing.overnightEntry3': 'Insurance COI uploaded to the Marlin Ct data room',

  // Greeting header — time-of-day variant + stats bar + fallback first name
  'consumer.home.greeting.morning': 'Good morning, {{firstName}}.',
  'consumer.home.greeting.afternoon': 'Good afternoon, {{firstName}}.',
  'consumer.home.greeting.evening': 'Good evening, {{firstName}}.',
  'consumer.home.greeting.statActiveLoans': '{{count}} active loans',
  'consumer.home.greeting.statTotalExposure': '{{amount}} total exposure',
  'consumer.home.greeting.statNextClose': 'Next close in {{days}} days',
  'consumer.home.greeting.defaultFirstName': 'there',

  // KPI tiles (additional)
  'consumer.home.kpi.activeApplications': 'Active applications',
  'consumer.home.kpi.activeApplicationsSub': 'Submitted, awaiting decision',
  'consumer.home.kpi.pendingActions': 'Pending actions',
  'consumer.home.kpi.pendingActionsSub': 'Need your attention',
  'consumer.home.kpi.documents': 'Documents',
  'consumer.home.kpi.documentsSub': 'Across all loans',
  'consumer.home.kpi.nextCloseNone': 'No closes scheduled',

  // Active loans panel — fallbacks when a row field is missing
  'consumer.home.loans.empty': 'No active loans yet',
  'consumer.home.loans.defaultType': 'Loan',
  'consumer.home.loans.nextDueDefault': 'when ready',
  'consumer.home.loans.nextWhoDefault': 'your team',

  // Documents — predicted-docs block
  'consumer.documents.predictedDocsTitle': 'Documents Astor predicts you will need',
  'consumer.documents.predictedPreStage': 'Pre-stage',

  // Sign queue — error state
  'consumer.signQueue.errorTitle': 'Could not load your sign queue',
  'consumer.signQueue.retry': 'Try again',

  // Dead-code cleanup: the `consumer.briefing.*` namespace previously added
  // here duplicated the wired `consumer.home.briefing.*` keys above and was
  // never referenced by any consumer of `AstorBriefing.tsx`. Removed so the
  // namespace has a single source of truth.

  // ============================================================
  // Topbar overlay primitives (Brief / AstorQuickAsk / Notifications)
  // Added in PR #1825 — the three sliding panels anchored to the
  // business-portal topbar (commit bd295aba8). Keyed under `overlays.*`.
  // ============================================================

  // Brief view — executive 30-second case summary
  'overlays.brief.title': 'Brief',
  'overlays.brief.close': 'Close brief',
  'overlays.brief.live': 'LIVE',
  'overlays.brief.liveAtAria': 'Last refreshed {time}',
  'overlays.brief.kpisLabel': 'Key indicators',
  'overlays.brief.signals.header': 'Signals',
  'overlays.brief.signals.empty': 'No active signals',
  'overlays.brief.cases.header': 'Cases needing attention',
  'overlays.brief.cases.empty': 'Everything is on track',
  'overlays.brief.quickActions.header': 'Quick actions',

  // Astor quick-ask overlay — page-aware AI suggestions
  'overlays.astor.title': 'Ask Astor',
  'overlays.astor.close': 'Close Astor',
  'overlays.astor.placeholder': 'Ask anything about this page…',
  'overlays.astor.send': 'Send',
  'overlays.astor.empty': 'Astor has nothing for this view yet — ask a question.',
  'overlays.astor.hint': 'Astor sees this page and your recent activity.',
  'overlays.astor.suggestionsHeader': 'Page-aware suggestions',

  // Notifications overlay — filter tabs + grouping
  'overlays.notifications.title': 'Notifications',
  'overlays.notifications.close': 'Close notifications',
  'overlays.notifications.markAllRead': 'Mark all read',
  'overlays.notifications.empty': "You're caught up.",
  'overlays.notifications.loading': 'Loading…',
  'overlays.notifications.unreadChip': '{count} unread',
  'overlays.notifications.tabs.all': 'All',
  'overlays.notifications.tabs.unread': 'Unread',
  'overlays.notifications.tabs.mentions': 'Mentions',
  'overlays.notifications.tabs.approvals': 'Approvals',
  'overlays.notifications.tabs.tasks': 'Tasks',
  'overlays.notifications.bucket.today': 'Today',
  'overlays.notifications.bucket.yesterday': 'Yesterday',
  'overlays.notifications.bucket.thisWeek': 'This week',
  'overlays.notifications.bucket.earlier': 'Earlier',
};
